import { Component } from '@angular/core';

import Wiz from 'src/wiz';
let wiz = new Wiz('/wiz').app('page.access');
import { OnInit, OnDestroy } from '@angular/core';
import { Service } from "src/libs/portal/season/service";
import { io } from 'socket.io-client';

interface PlannerRequestState {
    status: 'idle' | 'loading' | 'ready' | 'error';
    lastPrompt: string;
    errorMessage: string;
}

@Component({
    standalone: false,
    selector: 'wiz-page-access',
    templateUrl: './view.html',
    styleUrls: ['./view.scss']
})
export class PageAccessComponent implements OnInit, OnDestroy {
    constructor(public service: Service) {
        this.removeLegacyExampleContent();
    }


    public departureOpen: boolean = false;
    public departureBusy: boolean = false;
    public departureEditing: boolean = false;
    public departureError: string = '';
    public departureData: any = null;
    public departureProfile: any = {};
    public departureCourseId: string = '';
    public departurePostId: string = '';
    public departureTripId: string = '';
    public departureGroups: string[] = ['미리 확인할 때', '폰 켠 김에', '짐 쌀 때'];
    private departureTimer: any = null;

    public myTripOpen: boolean = false;
    public myTripBusy: boolean = false;
    public myTripError: string = '';
    public myTrip: any = null;
    public travelModeChoice: string = '';
    public companionSearchOffered: boolean = false;

    public soloDepartureCourse() {
        const course = this.mapCoursePickerOpen
            ? this.mapExecutableCourses.find((item: any) => String(item.id || '') === this.stagedExecutionCourseId)
            : this.executionCourse;
        if (!course || !course.id || !this.myTrip) return null;
        return String(this.myTrip.course_id || '') === String(course.id || '') ? course : null;
    }

    public async openDeparturePreparation(course: any, dm: any = null, trip: any = null) {
        if (!this.isLoggedIn()) { this.goLogin(); return; }
        this.closeDeparturePreparation();
        this.departureCourseId = String(dm ? dm.preparation.courseId : (trip ? trip.course_id : course.id) || '');
        this.departurePostId = String(dm ? dm.companionPostId || '' : '');
        this.departureTripId = String(trip ? trip.id || '' : '');
        this.departureOpen = true;
        this.departureData = null;
        this.departureProfile = {};
        await this.departureRequest('load');
        if (!this.departureOpen) return;
        this.departureTimer = window.setInterval(() => {
            if (!this.departureBusy && !this.departureEditing) this.departureRequest('load', {}, true);
        }, 15000);
    }

    public closeDeparturePreparation() {
        this.departureOpen = false;
        if (this.departureTimer) window.clearInterval(this.departureTimer);
        this.departureTimer = null;
    }

    public departureItems(group: string) {
        return (this.departureData && this.departureData.items || []).filter((item: any) => item.group === group);
    }

    public departureCompleted() {
        return (this.departureData && this.departureData.items || []).filter((item: any) => item.done).length;
    }

    public async departureRequest(action: string, extra: any = {}, quiet: boolean = false) {
        if (this.departureBusy) return;
        this.departureBusy = true;
        if (!quiet) this.departureError = '';
        if (!quiet) await this.service.render();
        const courseId = this.departureCourseId;
        const postId = this.departurePostId;
        try {
            const response: any = await wiz.call('departure_preparation', {
                action, course_id: courseId, post_id: postId, trip_id: this.departureTripId, ...extra
            });
            if (!this.departureOpen || courseId !== this.departureCourseId || postId !== this.departurePostId) return;
            if (response.code !== 200) {
                this.departureError = response.data && response.data.message || '준비표를 불러오지 못했습니다. 다시 시도해주세요.';
            } else {
                this.departureData = response.data.preparation;
                if (!quiet || !this.departureEditing) {
                    this.departureProfile = { ...this.departureData.profile };
                    const trip = this.departureData.trip || {};
                    for (const key of ['start', 'end', 'country']) {
                        if (trip[key]) this.departureProfile[key] = trip[key];
                    }
                }
                if (!quiet) this.departureEditing = !this.departureData.items.length;
            }
        } catch (error) {
            this.departureError = '서버 연결을 확인하고 다시 시도해주세요.';
        } finally {
            this.departureBusy = false;
            await this.service.render();
        }
    }

    public async generateDeparturePreparation() {
        await this.departureRequest('generate', { profile: JSON.stringify(this.departureProfile) });
    }

    public async checkDepartureItem(item: any) {
        await this.departureRequest('check', { item_id: item.id, done: String(!item.done) });
    }

    public stagedExecutionCourse() {
        return this.mapExecutableCourses.find((item: any) => String(item.id || '') === this.stagedExecutionCourseId) || null;
    }

    public async chooseTravelMode(mode: string) {
        if (['solo', 'companion'].indexOf(mode) < 0 || this.myTripBusy || this.mapRouteLoading) return;
        this.travelModeChoice = mode;
        this.companionSearchOffered = false;
        this.myTripError = '';
        if (mode === 'solo') await this.saveSoloTrip();
        else {
            this.myTripBusy = true;
            await this.service.render();
            try { await this.continueWithCompanion(); }
            finally { this.myTripBusy = false; }
        }
        await this.service.render();
    }

    public async saveSoloTrip() {
        const course = this.stagedExecutionCourse();
        if (!course || this.myTripBusy) return;
        this.myTripBusy = true;
        this.myTripError = '';
        await this.service.render();
        try {
            const request = { action: 'save', course_id: course.id };
            let response: any = await wiz.call('personal_trip', request);
            // A bundle refresh can briefly return an API route miss.
            if (response && response.code === 404) {
                response = await wiz.call('personal_trip', request);
            }
            if (response && response.code === 200 && response.data && response.data.trip) {
                this.myTrip = response.data.trip;
                this.myTripOpen = false;
                await this.selectExecutionCourse(course);
            } else {
                this.myTripError = response && response.data && response.data.message || '내 여행을 저장하지 못했습니다.';
            }
        } catch (e) {
            this.myTripError = '서버 연결을 확인하고 다시 시도해주세요.';
        } finally {
            this.myTripBusy = false;
            await this.service.render();
        }
    }

    public async loadMyTrip(quiet: boolean = true) {
        if (!this.isLoggedIn() || this.myTripBusy) return;
        this.myTripBusy = true;
        if (!quiet) this.myTripError = '';
        try {
            const response: any = await wiz.call('personal_trip', { action: 'load' });
            if (response && response.code === 200) {
                this.myTrip = response.data && response.data.trip || null;
            }
        } catch (e) {
            if (!quiet) this.myTripError = '내 여행을 불러오지 못했습니다.';
        } finally {
            this.myTripBusy = false;
        }
    }

    public async openMyTrip() {
        await this.loadMyTrip(false);
        if (!this.myTrip) {
            this.mapCoursePickerOpen = true;
            await this.loadMapExecutionCourses(true);
            await this.service.render();
            return;
        }
        this.myTripOpen = true;
        await this.service.render();
    }

    public async closeMyTrip() {
        this.myTripOpen = false;
        await this.service.render();
    }

    public async openMyTripDeparture() {
        if (!this.myTrip) return;
        this.myTripOpen = false;
        await this.openDeparturePreparation(null, null, this.myTrip);
    }

    public async startOrResumeMyTrip() {
        if (!this.myTrip) return;
        if (this.executionCourse && String(this.executionCourse.id || '') === String(this.myTrip.course_id || '')) {
            this.myTripOpen = false;
            await this.service.render();
            return;
        }
        await this.loadMapExecutionCourses(true);
        const course = this.mapExecutableCourses.find((item: any) => String(item.id || '') === String(this.myTrip.course_id || ''));
        if (!course) { this.myTripError = '여행 코스를 불러오지 못했습니다.'; await this.service.render(); return; }
        this.travelModeChoice = 'solo';
        this.myTripOpen = false;
        await this.selectExecutionCourse(course);
    }

    public async continueWithCompanion() {
        const staged = this.stagedExecutionCourse();
        const courseId = String(staged && staged.id || '');
        if (!staged) return;
        const loaded = await this.loadDirectChatRooms(false);
        if (!loaded) {
            this.myTripError = '동행 방을 확인하지 못했습니다. 다시 시도해주세요.';
            await this.service.render();
            return;
        }
        const postIds = staged && staged.companion_post_ids || [];
        const room = this.directChats.find((chat: any) => chat.category === 'companion' && (
            postIds.indexOf(String(chat.companionPostId || '')) >= 0
            || String(chat.preparation && chat.preparation.courseId || '') === courseId
        ));
        if (room) {
            this.mapCoursePickerOpen = false;
            await this.setTab('chat');
            await this.setChatContentTab('dm');
            await this.selectDirectChat(room);
            return;
        }
        this.companionSearchOffered = true;
        await this.service.render();
    }

    public async confirmCompanionSearch() {
        if (!this.companionSearchOffered || this.myTripBusy) return;
        const staged = this.stagedExecutionCourse();
        if (!staged) return;
        const courseId = String(staged.id || '');
        const source = String(staged.source || 'mine');
        this.companionSearchOffered = false;
        this.mapCoursePickerOpen = false;
        await this.openCompanionMode('pretrip');
        await this.openCompanionCoursePicker();
        await this.setCompanionCoursePickerTab(source === 'saved' ? 'saved' : 'mine');
        const course = this.companionPickerCourses().find((item: any) => String(item && item.id || '') === courseId);
        if (course) await this.selectCompanionCourse(course);
    }

    public myTripStatusLabel() {
        if (!this.myTrip) return '';
        if (this.myTrip.status === 'active') return '여행 중';
        if (this.myTrip.status === 'completed') return '여행 완료';
        return '출발 전';
    }

    public myTripActionLabel() {
        if (this.executionCourse && this.myTrip
            && String(this.executionCourse.id || '') === String(this.myTrip.course_id || '')) return '여행 이어가기';
        return this.myTrip && this.myTrip.status === 'active' ? '지도에서 이어가기' : '지도에서 여행 시작';
    }

    public behavioralSignals: any = { enabled: false, followed_authors: [], executed_courses: [], terms: [] };
    public replanBusy: boolean = false;
    public replanProposal: any = null;
    public replanToken: string = '';
    public replanMessage: string = '';
    public replanWeatherLabel: string = '';
    public replanFixedIds: string[] = [];
    public replanClosedIds: string[] = [];
    public replanDelayMinutes: number = 0;
    public travelReviewOpen: boolean = false;
    public travelReviewCourseId: string = '';
    public travelReviewData: any = null;
    public travelReviewError: string = '';
    public travelReviewBusy: boolean = false;
    public travelReviewTarget: string = '';
    public travelReviewRating: number = 5;
    public travelReviewComment: string = '';
    public travelerTrust: any = null;

    private applyBehavioralSignals(data: any) {
        this.behavioralSignals = data && data.behavioral_signals
            ? data.behavioral_signals : { enabled: false, followed_authors: [], executed_courses: [], terms: [] };
    }

    private async refreshBehavioralSignals() {
        if (!this.isLoggedIn()) return;
        try {
            const response: any = await wiz.call('travel_growth', { action: 'signals' });
            if (response && response.code === 200) this.applyBehavioralSignals(response.data);
        } catch (e) { }
    }

    private async recordExecutionStart(courseId: string) {
        try {
            let response: any = await wiz.call('travel_growth', { action: 'start', course_id: courseId });
            if (response && response.code === 200) this.applyBehavioralSignals(response.data);
        } catch (e) { }
    }

    public toggleReplanPlace(id: string, type: string) {
        let list = type === 'fixed' ? this.replanFixedIds : this.replanClosedIds;
        let index = list.indexOf(id);
        if (index >= 0) list.splice(index, 1); else list.push(id);
    }

    public async previewTravelReplan() {
        if (this.replanBusy || !this.executionCourse) return;
        this.replanBusy = true;
        this.replanProposal = null;
        this.replanMessage = '';
        this.replanWeatherLabel = '';
        const courseId = this.activeExecutionCourseId;
        try {
            const next = this.executionNextSpot();
            let rain = false;
            if (next && next.lat !== null && next.lng !== null) {
                const controller = new AbortController();
                const timer = setTimeout(() => controller.abort(), 8000);
                try {
                    const query = new URLSearchParams({ latitude: String(next.lat), longitude: String(next.lng),
                        current: 'weather_code', daily: 'precipitation_probability_max', timezone: 'auto', forecast_days: '1' });
                    const weatherResponse = await fetch('https://api.open-meteo.com/v1/forecast?' + query.toString(), { signal: controller.signal });
                    if (!weatherResponse.ok) throw new Error('날씨 조회 실패');
                    const weather = await weatherResponse.json();
                    const code = Number((weather.current || {}).weather_code);
                    const probability = Number(((weather.daily || {}).precipitation_probability_max || [])[0]);
                    if (!Number.isFinite(code) || !Number.isFinite(probability)) throw new Error('날씨 데이터 없음');
                    rain = code >= 51 || probability >= 60;
                    this.replanWeatherLabel = '다음 장소의 오늘 강수 확률 ' + probability + '%';
                } catch (e) {
                    this.replanWeatherLabel = '날씨를 확인하지 못해 휴무·지연 조건만 사용합니다.';
                } finally { clearTimeout(timer); }
            } else this.replanWeatherLabel = '다음 장소의 좌표가 없어 날씨를 확인하지 못했습니다.';
            let delay = Math.max(0, Math.min(240, Number(this.replanDelayMinutes) || 0));
            if (next && next.date === new Date().toLocaleDateString('sv-SE') && /^\d{2}:\d{2}$/.test(next.visit_time || '')) {
                const scheduled = new Date(next.date + 'T' + next.visit_time + ':00');
                delay = Math.max(delay, Math.min(240, Math.max(0, Math.floor((Date.now() - scheduled.getTime()) / 60000))));
            }
            const response: any = await wiz.call('travel_growth', { action: 'replan', course_id: courseId,
                fixed_ids: JSON.stringify(this.replanFixedIds), closed_ids: JSON.stringify(this.replanClosedIds),
                rain: String(rain), delay: String(Math.floor(delay)) });
            if (!response || response.code !== 200) throw new Error((response && response.data && response.data.message) || '재계획을 만들지 못했습니다.');
            if (courseId !== this.activeExecutionCourseId) return;
            this.replanProposal = response.data.proposal;
            this.replanToken = response.data.token;
            this.replanMessage = this.replanProposal.changes.length ? '변경 내용을 확인한 뒤 적용해주세요.' : '변경할 조건이나 확인 가능한 대안이 없습니다.';
        } catch (e) {
            this.replanMessage = e instanceof Error ? e.message : '재계획 중 오류가 발생했습니다.';
        } finally { this.replanBusy = false; await this.service.render(); }
    }

    public async applyTravelReplan(reset: boolean = false) {
        if (this.replanBusy || !this.executionCourse) return;
        this.replanBusy = true;
        const courseId = this.activeExecutionCourseId;
        try {
            const response: any = await wiz.call('travel_growth', { action: reset ? 'reset_replan' : 'apply_replan',
                course_id: courseId, token: this.replanToken });
            if (!response || response.code !== 200) throw new Error((response && response.data && response.data.message) || '일정을 적용하지 못했습니다.');
            if (courseId !== this.activeExecutionCourseId) return;
            if (this.executionNavigationActive) await this.toggleExecutionNavigation();
            this.applyExecutionCourse(response.data.execution);
            this.replanProposal = null;
            this.replanFixedIds = this.replanFixedIds.filter((id: string) => this.executionPlaces.some((p: any) => p.id === id));
            this.replanClosedIds = [];
            this.replanMessage = reset ? '원래 코스로 복원했습니다.' : '남은 일정에 적용했습니다. 새 경로를 확인해주세요.';
            this.scheduleNaverMapRender();
        } catch (e) { this.replanMessage = e instanceof Error ? e.message : '적용 실패'; }
        finally { this.replanBusy = false; await this.service.render(); }
    }

    public async openTravelReviews(course: any) {
        if (!course) return;
        this.travelReviewOpen = true;
        this.travelReviewCourseId = String(course.id || '');
        this.travelReviewData = null;
        this.travelReviewError = '';
        this.travelReviewTarget = '';
        this.travelReviewComment = '';
        this.travelerTrust = null;
        await this.loadTravelReviews();
    }

    private async loadTravelReviews() {
        this.travelReviewBusy = true;
        const courseId = this.travelReviewCourseId;
        try {
            const response: any = await wiz.call('travel_growth', { action: 'reviews', course_id: courseId });
            if (!response || response.code !== 200) throw new Error((response && response.data && response.data.message) || '후기를 불러오지 못했습니다.');
            if (this.travelReviewCourseId !== courseId) return;
            this.travelReviewData = response.data;
            this.travelReviewTarget = response.data.targets[0] ? response.data.targets[0].id : '';
            const authorId = this.courseAuthorId(this.allCourses().find((c: any) => String(c.id) === courseId));
            if (authorId) {
                const trust: any = await wiz.call('travel_growth', { action: 'trust', target_id: authorId });
                if (trust && trust.code === 200 && this.travelReviewCourseId === courseId) this.travelerTrust = trust.data.trust;
            }
        } catch (e) { this.travelReviewError = e instanceof Error ? e.message : '후기 조회 실패'; }
        finally { this.travelReviewBusy = false; await this.service.render(); }
    }

    public async submitTravelReview() {
        if (this.travelReviewBusy || !this.travelReviewTarget) return;
        this.travelReviewBusy = true;
        this.travelReviewError = '';
        try {
            const response: any = await wiz.call('travel_growth', { action: 'review_submit', course_id: this.travelReviewCourseId,
                target_id: this.travelReviewTarget, rating: this.travelReviewRating, comment: this.travelReviewComment });
            if (!response || response.code !== 200) throw new Error((response && response.data && response.data.message) || '후기 저장 실패');
            this.travelReviewData = response.data;
            this.travelReviewComment = '';
            await this.showSaveHint('확정 동행·완료 기록과 함께 후기를 저장했습니다.');
        } catch (e) { this.travelReviewError = e instanceof Error ? e.message : '후기 저장 실패'; }
        finally { this.travelReviewBusy = false; await this.service.render(); }
    }

    private removeLegacyExampleContent() {
        this.myActivityItems = this.myActivityItems.map((item: any) => ({ ...item, count: 0 }));
        this.myProfileFollowers = [];
        this.myProfileFollowing = [];
        this.myProfilePosts = [];
        this.plannerCompanionCards = [];
        this.communityPosts = [];
        this.reviewPosts = [];
        this.directChats = [];
        this.activeDirectChatId = '';
        this.selectedTogetherCompanionId = '';
        this.togetherCompanions = [];
        this.togetherMeetingPoint = { name: '', time: '', note: '', x: 50, y: 50 };
        this.courses = [];
        this.mapSpotMap = { default: [] };
    }

    public activeTab: string = 'home';
    public featuredCourseDetailOpen: boolean = false;
    public selectedFeaturedCourse: any = null;
    public weatherLoading: boolean = false;
    public weatherError: string = '';
    public weatherLocationLabel: string = '서울';
    public weatherUpdatedLabel: string = '';
    public weatherCurrent: any = null;
    public weatherForecast: any[] = [];
    public weatherCalendarCells: any[] = [];
    public weatherCalendarMonth: Date = new Date();
    public selectedWeatherDate: string = '';
    public selectedWeatherRegion: string = 'seoul';
    public weatherCoordinates: any = { latitude: 37.5665, longitude: 126.9780 };
    public weatherWeekdays: string[] = ['일', '월', '화', '수', '목', '금', '토'];
    public weatherRegions: any[] = [
        { value: 'current', label: '현재 위치', latitude: null, longitude: null },
        { value: 'seoul', label: '서울', latitude: 37.5665, longitude: 126.9780 },
        { value: 'incheon', label: '인천', latitude: 37.4563, longitude: 126.7052 },
        { value: 'busan', label: '부산', latitude: 35.1796, longitude: 129.0756 },
        { value: 'daegu', label: '대구', latitude: 35.8714, longitude: 128.6014 },
        { value: 'daejeon', label: '대전', latitude: 36.3504, longitude: 127.3845 },
        { value: 'gwangju', label: '광주', latitude: 35.1595, longitude: 126.8526 },
        { value: 'ulsan', label: '울산', latitude: 35.5384, longitude: 129.3114 },
        { value: 'sejong', label: '세종', latitude: 36.4800, longitude: 127.2890 },
        { value: 'suwon', label: '수원', latitude: 37.2636, longitude: 127.0286 },
        { value: 'seongnam', label: '성남', latitude: 37.4449, longitude: 127.1389 },
        { value: 'goyang', label: '고양', latitude: 37.6584, longitude: 126.8320 },
        { value: 'yongin', label: '용인', latitude: 37.2411, longitude: 127.1776 },
        { value: 'paju', label: '파주', latitude: 37.7599, longitude: 126.7800 },
        { value: 'gapyeong', label: '가평', latitude: 37.8315, longitude: 127.5090 },
        { value: 'yangpyeong', label: '양평', latitude: 37.4912, longitude: 127.4876 },
        { value: 'chuncheon', label: '춘천', latitude: 37.8813, longitude: 127.7298 },
        { value: 'gangneung', label: '강릉', latitude: 37.7519, longitude: 128.8761 },
        { value: 'sokcho', label: '속초', latitude: 38.2070, longitude: 128.5918 },
        { value: 'yangyang', label: '양양', latitude: 38.0754, longitude: 128.6190 },
        { value: 'wonju', label: '원주', latitude: 37.3422, longitude: 127.9202 },
        { value: 'pyeongchang', label: '평창', latitude: 37.3705, longitude: 128.3903 },
        { value: 'cheongju', label: '청주', latitude: 36.6424, longitude: 127.4890 },
        { value: 'cheonan', label: '천안', latitude: 36.8151, longitude: 127.1139 },
        { value: 'gongju', label: '공주', latitude: 36.4465, longitude: 127.1190 },
        { value: 'buyeo', label: '부여', latitude: 36.2757, longitude: 126.9098 },
        { value: 'taean', label: '태안', latitude: 36.7456, longitude: 126.2980 },
        { value: 'boryeong', label: '보령', latitude: 36.3332, longitude: 126.6128 },
        { value: 'jeonju', label: '전주', latitude: 35.8242, longitude: 127.1480 },
        { value: 'gunsan', label: '군산', latitude: 35.9677, longitude: 126.7366 },
        { value: 'iksan', label: '익산', latitude: 35.9483, longitude: 126.9576 },
        { value: 'yeosu', label: '여수', latitude: 34.7604, longitude: 127.6622 },
        { value: 'suncheon', label: '순천', latitude: 34.9506, longitude: 127.4872 },
        { value: 'mokpo', label: '목포', latitude: 34.8118, longitude: 126.3922 },
        { value: 'damyang', label: '담양', latitude: 35.3211, longitude: 126.9882 },
        { value: 'gyeongju', label: '경주', latitude: 35.8562, longitude: 129.2247 },
        { value: 'pohang', label: '포항', latitude: 36.0190, longitude: 129.3435 },
        { value: 'andong', label: '안동', latitude: 36.5684, longitude: 128.7294 },
        { value: 'changwon', label: '창원', latitude: 35.2279, longitude: 128.6811 },
        { value: 'jinju', label: '진주', latitude: 35.1800, longitude: 128.1076 },
        { value: 'tongyeong', label: '통영', latitude: 34.8544, longitude: 128.4332 },
        { value: 'geoje', label: '거제', latitude: 34.8806, longitude: 128.6211 },
        { value: 'gimhae', label: '김해', latitude: 35.2285, longitude: 128.8894 },
        { value: 'jeju', label: '제주', latitude: 33.4996, longitude: 126.5312 },
        { value: 'seogwipo', label: '서귀포', latitude: 33.2541, longitude: 126.5601 },
        { value: 'ulleungdo', label: '울릉도', latitude: 37.4845, longitude: 130.9057 }
    ];
    public notifications: any[] = [];
    public notificationsLoading: boolean = false;
    public homeContentTab: string = 'feed';
    public companionMode: string = 'pretrip';
    public companionCourseFilterId: string = '';
    public companionCourseFilterTitle: string = '';
    public expandedCompanionPostId: string = '';
    public chatContentTab: string = 'chat';
    public mapContentTab: string = 'map';
    public savedContentTab: string = 'courses';
    public savedPlaceType: string = 'all';
    public query: string = '';
    public draft: string = '';
    public directDraft: string = '';
    public communitySearchQuery: string = '';
    public courseComposerOpen: boolean = false;
    public courseBuilderMode: string = '';
    public courseBuilderStep: string = 'mode';
    public courseAiMood: string = '';
    public courseComposerPlannerEdit: boolean = false;
    public courseBuilderDayIndex: number = 0;
    public courseBuilderDays: any[] = [];
    public courseScheduleFormOpen: boolean = false;
    public courseScheduleDraft: any = { type: 'flight', title: '', time: '', detail: '' };
    public courseScheduleDetailOpen: boolean = false;
    public courseScheduleDetail: any = {};
    public courseAiRebuilding: boolean = false;
    public courseDraftArchiveOpen: boolean = false;
    public courseDraftArchiveSummary: any = null;
    public communityComposerOpen: boolean = false;
    public communityPublishSubmitting: boolean = false;
    public communitySortDropdownOpen: boolean = false;
    public selectedCommunityTopic: string = 'recommend';
    public selectedCommunitySort: string = 'recommended';
    public expandedCommunityPostId: string = '';
    public activeCommunityPost: any = null;
    public communityComments: any[] = [];
    public communityCommentDraft: string = '';
    public communityCommentsLoading: boolean = false;
    public communityArchiveOpen: boolean = false;
    public communityArchivePosts: any[] = [];
    public communityArchiveLoading: boolean = false;
    public communityDeleteSubmittingId: string = '';
    public communityDetailMenuOpen: boolean = false;
    public communityReportSubmittingId: string = '';
    public communityReportedPostIds: string[] = [];
    public courseDraft: any = {
        title: '',
        region: '',
        regions: [],
        schedule: '',
        scheduleDate: '',
        scheduleEndDate: '',
        places: '',
        photo: '',
        photoName: '',
        description: '',
        category: '여행',
        companionType: '',
        isPublic: true,
        companionEnabled: false,
        companionDate: '',
        companionTime: '',
        companionCapacity: 2,
        companionCost: '',
        companionBudgetStyle: '',
        companionPace: '',
        companionMood: '',
        companionFlexible: '',
        companionMeetingPoint: '',
        companionSmoking: '',
        companionDrinking: '',
        companionIntro: ''
    };
    public courseCollaborationItems: any[] = [];
    public courseCollaborationDraft: any = { title: '', detail: '' };
    public courseCollaborationLoading: boolean = false;
    public courseCollaborationSaving: boolean = false;
    public courseCollaborationIsOwner: boolean = false;
    public courseDraftSavedAt: string = '';
    public courseBuilderPlaces: any[] = [];
    public coursePlaceSearchQuery: string = '';
    public coursePlaceSearchResults: any[] = [];
    public coursePlaceSearchSource: string = '';
    public coursePlaceSearchOpen: boolean = false;
    public coursePlaceSearching: boolean = false;
    public courseRegionSearchOpen: boolean = false;
    public courseRegionInput: string = '';
    public courseBuilderError: string = '';
    public coursePublishModalOpen: boolean = false;
    public coursePublishSubmitting: boolean = false;
    public courseMySaveSubmitting: boolean = false;
    public companionCoursePickerOpen: boolean = false;
    public companionCoursePickerTab: string = 'mine';
    public companionSelectedCourse: any = null;
    public companionRecruitSubmitting: boolean = false;
    public companionRecruitDraft: any = {
        capacity: '',
        cost: '',
        pace: '',
        budgetStyle: '',
        smoking: '',
        drinking: '',
        mood: '',
        flexible: '',
        meetingPoint: '',
        intro: ''
    };
    public companionMeetingSearchResults: any[] = [];
    public companionMeetingSearching: boolean = false;
    public companionMeetingSearchError: string = '';
    private companionMeetingSearchTimer: any = null;
    private companionMeetingSearchVersion: number = 0;
    public coursePlaceDetailOpen: boolean = false;
    public coursePlaceDetailLoading: boolean = false;
    public coursePlaceDetailError: string = '';
    public coursePlaceDetail: any = {};
    public coursePlaceDetailTab: string = 'overview';
    public courseDragIndex: number = -1;
    public coursePlaceCategoryChoices: string[] = ['식당', '카페', '숙박', '관광명소', '공원', '쇼핑', '문화·전시', '액티비티', '기타'];
    public courseNaverReady: boolean = false;
    public courseDateCalendarMonth: Date = new Date();
    private courseDateDragActive: boolean = false;
    private courseDateDragStartKey: string = '';
    private courseBuilderOriginalDays: any[] = [];
    public communityDraft: any = {
        kind: 'post',
        topic: 'recommend',
        title: '',
        body: '',
        hasDestination: true,
        destination: '',
        photo: '',
        photoName: '',
        place: '',
        pollOptions: [],
        pollInput: '',
        tags: [],
        tagInput: ''
    };
    public isChatSending: boolean = false;
    private chatSendInvocationCount: number = 0;
    private chatInFlightMessageIds: any = {};
    public chatDrawerOpen: boolean = false;
    public chatHistoryLoading: boolean = false;
    public chatDeletingThreadId: string = '';
    public chatDeleteConfirmThreadId: string = '';
    public chatThreads: any[] = [];
    public activeChatThreadId: string = '';
    public plannerCourseConfirming: boolean = false;
    public selectedKeyword: string = '여행';
    public activeFilterKey: string = '';
    public filterOverviewOpen: boolean = false;
    public authModalOpen: boolean = false;
    public authMode: string = 'login';
    public authSubmitting: boolean = false;
    public socialProviders: any = {
        kakao: false,
        apple: false,
        google: false
    };
    public authServerError: string = '';
    public pendingSaveCourse: any = null;
    public saveHintVisible: boolean = false;
    public saveHintMessage: string = '로그인해야 코스를 저장할 수 있어요.';
    public courseDraftSavedToastVisible: boolean = false;
    public placeCourses: any[] = [];
    public placeThemeSections: any[] = [];
    public activePlaceThemeKey: string = '';
    public placeCoursesLoading: boolean = false;
    public recentPlaces: any[] = [];
    public savedPlaceItems: any[] = [];
    public savedPlaceFilters: any[] = [
        { key: 'all', label: '전체', icon: 'fa-layer-group' },
        { key: 'cafe', label: '카페', icon: 'fa-mug-saucer' },
        { key: 'stay', label: '숙소', icon: 'fa-bed' },
        { key: 'food', label: '맛집', icon: 'fa-utensils' },
        { key: 'walk', label: '산책', icon: 'fa-person-walking' },
        { key: 'landmark', label: '명소', icon: 'fa-location-dot' },
        { key: 'drive', label: '드라이브', icon: 'fa-car-side' }
    ];
    public myPageReady: boolean = false;
    public myProfileOpen: boolean = false;
    public myProfileTab: string = 'myCourses';
    public myCourseViewMode: string = 'list';
    public myProfileEditOpen: boolean = false;
    public myProfileEditing: boolean = false;
    public myProfileSaving: boolean = false;
    public myCompanionEditOpen: boolean = false;
    public myCompanionEditSaving: boolean = false;
    public selectedMyCompanionPost: any = null;
    public myCompanionEditDraft: any = {};
    public myResumeOpen: boolean = false;
    public myResumePreviewOpen: boolean = false;
    public travelResumeStep: number = 1;
    public travelResumeReturnToSettings: boolean = false;
    public myFeedComposerOpen: boolean = false;
    public myArchiveOpen: boolean = false;
    public myActivityOpen: boolean = false;
    public mySettingsDetail: string = '';
    public settingsDetailLoading: boolean = false;
    public settingsDetailSaving: boolean = false;
    public accountCenterProfile: any = { email: '', nickname: '', mobile: '', role: 'user' };
    public accountPreferences: any = {
        allowMarketing: false,
        showActivity: true,
        personalizedRecommendations: true,
        travelPreferences: ''
    };
    public accountPassword: any = { current: '', next: '', confirm: '' };
    public professionalSettings: any = { accountType: 'traveler', insightsEnabled: true, creatorTools: false };
    public billingSettings: any = { invoiceEmail: '', companyName: '', businessNumber: '', monthlyBudget: 0, paymentStatus: 'not_configured' };
    public profileInsights: any[] = [];
    public myActivityFilter: string = 'likes';
    public myActivityGroups: any = { likes: [], comments: [], reposts: [], tags: [] };
    public myFeedDetailOpen: boolean = false;
    public myCourseDetailOpen: boolean = false;
    private courseDetailReturnContext: string = '';
    public profileCourseMapOpen: boolean = false;
    public profileCourseMapLoading: boolean = false;
    public profileCourseMapError: string = '';
    public profileCourseStaticMapUrl: string = '';
    public profileCourseDynamicMapEnabled: boolean = false;
    public profileCourseInlineMapLoading: boolean = false;
    public profileCourseInlineMapError: string = '';
    public myCourseDeleteSubmittingId: string = '';
    public myCourseDeleteConfirmOpen: boolean = false;
    public myCourseArchiveSubmittingId: string = '';
    public newFeedStep: string = 'select';
    public newFeedPhotoIndex: number = 0;
    public profileFeedDetailPhotoIndex: number = 0;
    public profileCourseDetailPhotoIndex: number = 0;
    public profileCourseDetailLoading: boolean = false;
    public selectedMyProfilePost: any = null;
    public selectedMyProfileCourse: any = null;
    public feedComposerReturnCourse: any = null;
    public myProfileEdit: any = {
        photo: '',
        nickname: '',
        region: '',
        intro: ''
    };
    private myProfileEditSnapshot: any = null;
    public travelResume: any = {
        schemaVersion: 2,
        photo: '',
        fullName: '',
        age: '',
        gender: '',
        region: '',
        companionUses: 0,
        interests: '',
        smoking: '',
        drinking: '',
        travelExperience: '',
        intro: '',
        safetyRecordAccepted: false,
        safetyRecordAcceptedAt: '',
        safetyRecordConsentVersion: ''
    };
    public travelIdentity: any = {
        loading: false,
        verifying: false,
        verified: false,
        configured: true,
        name: '',
        age: 0,
        gender: '',
        verifiedAt: '',
        safetyRecordAccepted: false,
        safetyRecordAcceptedAt: '',
        error: ''
    };
    public resumeRegionOptions: string[] = [
        '서울', '경기', '인천', '부산', '대구', '대전', '광주', '울산', '세종',
        '강원', '충북', '충남', '전북', '전남', '경북', '경남', '제주'
    ];
    public resumeInterestOptions: string[] = ['바다', '맛집', '전시', '산책', '사진', '카페'];
    public newFeedPost: any = {
        photo: '',
        photos: [],
        courseId: '',
        courseTitle: '',
        title: '',
        location: '',
        tags: '',
        caption: '',
        icon: 'fa-camera',
        tone: 'feed-rose'
    };
    public feedIconOptions: any[] = [
        { value: 'fa-camera', label: '사진' },
        { value: 'fa-mug-saucer', label: '카페' },
        { value: 'fa-water', label: '바다' },
        { value: 'fa-bed', label: '숙소' },
        { value: 'fa-map-location-dot', label: '장소' }
    ];
    public feedToneOptions: any[] = [
        { value: 'feed-rose', label: '레드' },
        { value: 'feed-blue', label: '코랄' },
        { value: 'feed-green', label: '민트' },
        { value: 'feed-sun', label: '옐로우' }
    ];
    public myActivityItems: any[] = [
        { key: 'likes', icon: 'fa-heart', label: '좋아요', text: '내가 좋아요를 누른 피드와 코스 반응을 관리해요.', count: 0 },
        { key: 'comments', icon: 'fa-comment', label: '댓글', text: '내가 남긴 댓글과 답글 기록을 확인해요.', count: 0 },
        { key: 'reposts', icon: 'fa-repeat', label: '리포스트', text: '다시 공유했거나 리그램한 피드를 모아봐요.', count: 0 },
        { key: 'tags', icon: 'fa-tag', label: '태그', text: '내가 태그된 여행 피드와 장소를 관리해요.', count: 0 }
    ];
    public myProfileFollowers: any[] = [
        { name: '서울 산책러', handle: '@seoul.walk', icon: 'fa-person-walking', note: '성수와 한강 코스를 자주 저장해요.', following: false },
        { name: '부산 바다친구', handle: '@busan.wave', icon: 'fa-water', note: '해운대 1박 코스를 팔로우 중', following: false },
        { name: '감성숙소 큐레이터', handle: '@stay.note', icon: 'fa-bed', note: '숙소 중심 여행 피드를 모아요.', following: false }
    ];
    public myProfileFollowing: any[] = [];
    public myProfilePosts: any[] = [
        {
            id: 'feed-seongsu',
            title: '성수 감성 데이트',
            location: '서울 성수',
            caption: '서울숲 산책과 카페를 천천히 이어 본 코스',
            icon: 'fa-mug-saucer',
            tone: 'feed-rose',
            likes: 128,
            regrams: 8,
            regrammed: true
        },
        {
            id: 'feed-busan',
            title: '해운대 바다 코스',
            location: '부산 해운대',
            caption: '오션 카페에서 시작해 해변길로 마무리',
            icon: 'fa-water',
            tone: 'feed-blue',
            likes: 96,
            regrams: 5,
            regrammed: false
        },
        {
            id: 'feed-jeju',
            title: '애월 감성숙소',
            location: '제주 애월',
            caption: '노을 시간에 맞춰 저장해 둔 스테이 루트',
            icon: 'fa-bed',
            tone: 'feed-green',
            likes: 84,
            regrams: 4,
            regrammed: false
        },
        {
            id: 'feed-iksun',
            title: '익선동 골목 기록',
            location: '서울 종로',
            caption: '한옥 골목, 디저트, 사진 스폿을 묶은 피드',
            icon: 'fa-camera',
            tone: 'feed-sun',
            likes: 73,
            regrams: 6,
            regrammed: true
        }
    ];
    public authForm: any = {
        email: '',
        password: '',
        passwordConfirm: '',
        nickname: ''
    };
    public authErrors: any = {};
    public messages: any[] = [
        {
            role: 'assistant',
            text: '가고 싶은 지역과 날짜를 알려주세요. 출발 장소와 이동수단, 이용시간을 확인해 코스 초안을 준비할게요.',
            time: '오전 10:30',
            seed: true
        }
    ];
    public chatVisibleMessageLimit: number = 40;
    private readonly chatMessagePageSize: number = 40;
    public suggestions: string[] = [
        '이번 주말 서울 데이트 코스',
        '20만원 예산 제주 1박 코스',
        '혼자 가기 좋은 바다 코스',
        '부모님과 당일치기 코스',
        '덜 걷는 카페 중심 코스',
        '동행 모집까지 가능한 코스'
    ];
    public plannerQuickStartReplies: any[] = [
        { label: '오늘', prompt: '오늘', initial: true },
        { label: '내일', prompt: '내일', initial: true },
        { label: '이번 주말', prompt: '이번 주말', initial: true },
        { label: '다음 주', prompt: '다음 주', initial: true }
    ];

    public plannerCourseReady: boolean = false;
    public plannerCourseVisible: boolean = false;
    public plannerCourseExpanded: boolean = false;
    public isPlannerGenerating: boolean = false;
    public plannerTravelState: any = {};
    // 서버가 소유하는 여행 도메인 상태와 화면의 일시적인 요청 상태를 분리한다.
    public plannerRequestState: PlannerRequestState = {
        status: 'idle',
        lastPrompt: '',
        errorMessage: ''
    };
    public plannerStage: string = 'collecting';
    public plannerAction: string = 'ask_clarification';
    public plannerMissingSlots: string[] = [];
    public plannerWarnings: string[] = [];
    public plannerSuggestedReplies: any[] = [];
    public plannerDestinationCandidates: any[] = [];
    public plannerFailureStage: string = '';
    public plannerProgressSteps: string[] = ['여행 조건 정리 중', '장소 검색 중', '동선 계산 중', '일정 구성 중'];
    public plannerGenerationStep: number = 0;
    private plannerProgressTimer: any = null;
    public plannerCourseBaseTitle: string = '내 부산 여행';
    public plannerCourseDayIndex: number = 0;
    public plannerCourseDays: any[] = [];
    public plannerQuality: any = {};
    public plannerTravelerStyle: string = '취향 중심';
    private plannerTouchStartX: number = 0;
    private travelConditionDragActive: boolean = false;
    private travelConditionDragStartX: number = 0;
    private travelConditionDragScrollLeft: number = 0;
    public plannerCourseTitle: string = '내 부산 여행 1일차 코스';
    public plannerCourseRegion: string = '부산 해운대구';
    public plannerCourseSchedule: string = '2박 3일';
    public plannerMapLabel: string = '해운대구 동선';
    public plannerMapExpanded: boolean = false;
    public plannerTotalTime: string = '약 7시간';
    public plannerDistance: string = '약 12.6km';
    public plannerReturnTime: string = '20:20';
    public plannerMapRoutePoints: string = '';
    public plannerMapAreas: any[] = [
        { label: '해운대구', className: 'area-haeundae' },
        { label: '수영구', className: 'area-suyoung' }
    ];
    public plannerStops: any[] = [];
    public plannerRouteSource: string = '기본 동선';
    private plannerNaverRouteToken: number = 0;
    private plannerRouteSummary: any = {};
    private plannerNaverRoutePath: any[] = [];
    public plannerCompanionCards: any[] = [];

    public flowSteps: any[] = [
        { icon: 'fa-wand-magic-sparkles', label: 'AI 계획', text: '취향과 예산으로 일정 생성' },
        { icon: 'fa-pen-to-square', label: '코스 제작', text: '장소, 사진, 설명을 묶어 공유' },
        { icon: 'fa-map-location-dot', label: '여행 실행', text: '지도 경로와 친구 위치 확인' },
        { icon: 'fa-user-group', label: '동행 성사', text: '신청 수락 후 1:1 채팅' },
        { icon: 'fa-star', label: '후기 기록', text: '여행 완료 후 리뷰 작성' }
    ];

    public communityTopics: any[] = [
        { key: 'recommend', label: '추천', icon: 'fa-thumbs-up' },
        { key: 'vote', label: '인기 투표', icon: 'fa-square-poll-vertical' },
        { key: 'life', label: '생활정보', icon: 'fa-circle-info' },
        { key: 'question', label: '질문', icon: 'fa-circle-question' },
        { key: 'review', label: '후기', icon: 'fa-star' }
    ];
    public communitySortOptions: any[] = [
        { key: 'recommended', label: '추천' },
        { key: 'latest', label: '최신순' }
    ];

    // 추후 커뮤니티 API 연동 예정: 현재는 UI 확인용 로컬 데이터.
    public communityPosts: any[] = [
        {
            id: 'community-seoul-daytrip',
            kind: 'post',
            topic: 'recommend',
            title: '서울 근교에서 조용했던 당일치기 코스',
            summary: '양평 산책로와 작은 카페를 묶으니 이동 부담이 적고 오후 일정으로 좋았어요.',
            category: '추천',
            destination: '경기 양평',
            place: '두물머리 산책로',
            author: '서울 산책러',
            likes: 128,
            comments: 18,
            votes: 0,
            createdAt: 5,
            createdLabel: '5분 전',
            tags: ['당일치기', '카페', '조용한코스']
        },
        {
            id: 'community-gangneung-family',
            kind: 'post',
            topic: 'recommend',
            title: '부모님과 강릉 가면 편했던 동선',
            summary: '시장, 바다 전망 카페, 짧은 산책로 순서로 잡으니 오래 걷지 않아도 만족도가 높았어요.',
            category: '추천',
            destination: '강원 강릉',
            place: '안목해변, 중앙시장',
            author: '가족여행 메모',
            likes: 88,
            comments: 13,
            votes: 0,
            createdAt: 12,
            createdLabel: '12분 전',
            tags: ['가족', '강릉', '부모님']
        },
        {
            id: 'community-busan-rain-date',
            kind: 'post',
            topic: 'life',
            title: '비 오는 날 부산 실내 데이트 추천',
            summary: '전시, 서점, 오션뷰 카페 순서로 움직이면 날씨 영향이 적었습니다.',
            category: '생활정보',
            destination: '부산 해운대',
            place: '해운대 실내 전시',
            author: '부산 바다친구',
            likes: 94,
            comments: 11,
            votes: 0,
            createdAt: 24,
            createdLabel: '24분 전',
            tags: ['비오는날', '데이트', '실내']
        },
        {
            id: 'community-jeju-cafe-list',
            kind: 'post',
            topic: 'review',
            title: '혼자 제주 갈 때 저장해둘 카페 리스트',
            summary: '애월과 협재 쪽은 오전 방문이 한산했고, 노트북 작업하기 좋은 곳도 있었어요.',
            category: '후기',
            destination: '제주 애월',
            place: '애월 해안 카페',
            author: '제주 애월 기록',
            likes: 76,
            comments: 9,
            votes: 0,
            createdAt: 56,
            createdLabel: '56분 전',
            tags: ['혼행', '카페', '노트북']
        },
        {
            id: 'community-budget-vote',
            kind: 'post',
            topic: 'vote',
            title: '주말 1박 여행 예산 어느 정도가 적당해요?',
            summary: '숙소와 교통비를 포함한 현실적인 예산을 골라주세요.',
            category: '인기 투표',
            destination: '',
            place: '',
            author: 'GACHI',
            likes: 63,
            comments: 7,
            votes: 146,
            createdAt: 78,
            createdLabel: '1시간 전',
            poll: {
                title: '1인 기준 예산',
                options: ['10만원 이하', '10-20만원', '20만원 이상'],
                counts: {
                    '10만원 이하': 28,
                    '10-20만원': 84,
                    '20만원 이상': 34
                }
            },
            tags: ['예산', '1박2일', '투표']
        },
        {
            id: 'community-jejudo-question',
            kind: 'question',
            topic: 'question',
            title: '제주 서쪽 혼자 여행이면 차 없이 가능할까요?',
            summary: '애월, 협재 쪽으로 2박을 잡으려는데 버스만으로 동선이 괜찮은지 궁금합니다.',
            category: '질문',
            destination: '제주 서쪽',
            place: '애월, 협재',
            author: '혼행 준비중',
            likes: 41,
            comments: 14,
            votes: 0,
            createdAt: 118,
            createdLabel: '2시간 전',
            tags: ['제주', '혼행', '대중교통']
        }
    ];

    public companionPosts: any[] = [];

    public reviewPosts: any[] = [
        {
            id: 'review-seongsu',
            title: 'AI가 잡아준 성수 반나절 코스',
            course: '성수 감성 데이트 루트',
            author: '민지',
            rating: '4.8',
            summary: '동선이 짧아서 카페 대기 시간이 있어도 일정이 무너지지 않았어요.',
            tags: ['AI 일정', '데이트', '서울']
        },
        {
            id: 'review-busan',
            title: '위치 공유 덕분에 해운대에서 안 헤맸어요',
            course: '해운대 바다 1박2일 코스',
            author: '준호',
            rating: '4.9',
            summary: '친구 위치가 바로 보여서 각자 움직이다 다시 만나기 편했습니다.',
            tags: ['같이 지도', '부산', '1박2일']
        },
        {
            id: 'review-jeju',
            title: '동행 성사 후 채팅으로 일정 조율',
            course: '제주 애월 감성숙소 스테이',
            author: '하나',
            rating: '4.7',
            summary: '수락된 사람끼리만 채팅이 열려서 부담 없이 세부 일정을 맞췄어요.',
            tags: ['동행', '제주', '후기']
        }
    ];

    public directChats: any[] = [];
    public directChatFilters: any[] = [
        { key: 'all', label: '전체' },
        { key: 'unread', label: '안읽음' },
        { key: 'companion', label: '동행모임' }
    ];
    public directChatFilter: string = 'all';
    public activeDirectChatId: string = '';
    public directRoomOpen: boolean = false;
    public directActionMenuOpen: boolean = false;
    public directLedgerOpen: boolean = false;
    public directLedgerTab: string = 'history';
    public directLedgerExpenseFormOpen: boolean = false;
    public directLedgerExpenseDraft: any = { title: '', category: '식사', amount: null, payer: 'me' };
    public directLedgerExpenseCategories: string[] = ['식사', '카페', '숙소', '교통', '관광', '기타'];
    public directLedgerReceiptScanning: boolean = false;
    public directLedgerReceiptPreview: string = '';
    public directLedgerReceiptStatus: string = '';
    public directLedgerReceiptError: string = '';
    public directLedgerExpenses: any[] = [
        { id: 'stay', icon: 'fa-bed', category: '숙소', title: '오션뷰 게스트하우스', payer: 'other', amount: 98000, tone: 'stay' },
        { id: 'meal', icon: 'fa-utensils', category: '식사', title: '해변 브런치', payer: 'me', amount: 46000, tone: 'meal' },
        { id: 'transport', icon: 'fa-taxi', category: '교통', title: '공항에서 숙소 이동', payer: 'me', amount: 24000, tone: 'transport' },
        { id: 'cafe', icon: 'fa-mug-hot', category: '카페', title: '애월 바다 카페', payer: 'me', amount: 18500, tone: 'cafe' }
    ];
    public directMessageSending: boolean = false;
    private directChatSocket: any = null;
    private directChatPollTimer: any = null;
    private directChatRoomRefreshTimer: any = null;

    public keywordTags: string[] = [
        '여행',
        '데이트',
        '1박2일',
        '당일치기',
        '감성숙소',
        '카페',
        '맛집',
        '볼거리',
        '쇼핑'
    ];

    public homeRegionTabs: any[] = [
        { label: '전체', value: '' },
        { label: '서울', value: '서울' },
        { label: '경기', value: '경기' },
        { label: '인천', value: '인천' },
        { label: '부산', value: '부산' },
        { label: '대구', value: '대구' },
        { label: '대전', value: '대전' },
        { label: '광주', value: '광주' },
        { label: '울산', value: '울산' },
        { label: '제주', value: '제주' },
        { label: '강릉', value: '강릉' },
        { label: '속초', value: '속초' },
        { label: '춘천', value: '춘천' },
        { label: '전주', value: '전주' },
        { label: '여수', value: '여수' },
        { label: '목포', value: '목포' },
        { label: '경주', value: '경주' },
        { label: '통영', value: '통영' },
        { label: '가평', value: '가평' }
    ];

    public selectedFilters: any = {
        companion: '',
        schedule: '',
        location: ''
    };

    public conditionFilters: any[] = [
        {
            key: 'companion',
            label: '누구랑',
            icon: 'fa-user-group',
            defaultValue: '누구랑',
            options: ['연인', '친구', '가족', '혼자']
        },
        {
            key: 'schedule',
            label: '언제',
            icon: 'fa-calendar-days',
            defaultValue: '날짜'
        },
        {
            key: 'location',
            label: '위치',
            icon: 'fa-location-dot',
            defaultValue: '지역'
        }
    ];

    public weekdays: string[] = ['일', '월', '화', '수', '목', '금', '토'];
    public calendarMonthLabel: string = '';
    public calendarWeeks: any[] = [];
    public calendarDays: any[] = [];
    public scheduleRange: any = { start: '', end: '' };
    public draftScheduleRange: any = { start: '', end: '' };
    public todayKey: string = '';
    public selectedLocationRegion: string = '서울';
    public locationGroups: any[] = [
        {
            name: '서울',
            areas: [
                { label: '전체', value: '서울' },
                { label: '성수', value: '성수' },
                { label: '종로', value: '종로' },
                { label: '익선동', value: '익선동' },
                { label: '한강', value: '한강' },
                { label: '홍대', value: '홍대' },
                { label: '강남', value: '강남' },
                { label: '잠실', value: '잠실' },
                { label: '이태원', value: '이태원' },
                { label: '북촌', value: '북촌' }
            ]
        },
        {
            name: '경기',
            areas: [
                { label: '전체', value: '경기' },
                { label: '수원', value: '수원' },
                { label: '가평', value: '가평' },
                { label: '양평', value: '양평' },
                { label: '파주', value: '파주' },
                { label: '용인', value: '용인' },
                { label: '고양', value: '고양' },
                { label: '성남', value: '성남' },
                { label: '화성', value: '화성' },
                { label: '남양주', value: '남양주' },
                { label: '포천', value: '포천' }
            ]
        },
        {
            name: '인천',
            areas: [
                { label: '전체', value: '인천' },
                { label: '송도', value: '송도' },
                { label: '월미도', value: '월미도' },
                { label: '강화', value: '강화' },
                { label: '영종', value: '영종' },
                { label: '을왕리', value: '을왕리' }
            ]
        },
        {
            name: '강원',
            areas: [
                { label: '전체', value: '강원' },
                { label: '강릉', value: '강릉' },
                { label: '속초', value: '속초' },
                { label: '춘천', value: '춘천' },
                { label: '양양', value: '양양' },
                { label: '평창', value: '평창' },
                { label: '원주', value: '원주' },
                { label: '동해', value: '동해' },
                { label: '삼척', value: '삼척' },
                { label: '홍천', value: '홍천' }
            ]
        },
        {
            name: '충청',
            areas: [
                { label: '전체', value: '충청' },
                { label: '대전', value: '대전' },
                { label: '세종', value: '세종' },
                { label: '천안', value: '천안' },
                { label: '아산', value: '아산' },
                { label: '공주', value: '공주' },
                { label: '부여', value: '부여' },
                { label: '태안', value: '태안' },
                { label: '청주', value: '청주' },
                { label: '충주', value: '충주' },
                { label: '제천', value: '제천' },
                { label: '단양', value: '단양' },
                { label: '보령', value: '보령' },
                { label: '서산', value: '서산' }
            ]
        },
        {
            name: '전라',
            areas: [
                { label: '전체', value: '전라' },
                { label: '전주', value: '전주' },
                { label: '군산', value: '군산' },
                { label: '목포', value: '목포' },
                { label: '여수', value: '여수' },
                { label: '순천', value: '순천' },
                { label: '광주', value: '광주' },
                { label: '담양', value: '담양' },
                { label: '남원', value: '남원' },
                { label: '고흥', value: '고흥' }
            ]
        },
        {
            name: '경상',
            areas: [
                { label: '전체', value: '경상' },
                { label: '대구', value: '대구' },
                { label: '경주', value: '경주' },
                { label: '포항', value: '포항' },
                { label: '안동', value: '안동' },
                { label: '통영', value: '통영' },
                { label: '거제', value: '거제' },
                { label: '울산', value: '울산' },
                { label: '진주', value: '진주' },
                { label: '창원', value: '창원' },
                { label: '남해', value: '남해' }
            ]
        },
        {
            name: '부산',
            areas: [
                { label: '전체', value: '부산' },
                { label: '해운대', value: '해운대' },
                { label: '광안리', value: '광안리' },
                { label: '영도', value: '영도' },
                { label: '서면', value: '서면' },
                { label: '기장', value: '기장' },
                { label: '송정', value: '송정' },
                { label: '남포동', value: '남포동' }
            ]
        },
        {
            name: '제주',
            areas: [
                { label: '전체', value: '제주' },
                { label: '애월', value: '애월' },
                { label: '협재', value: '협재' },
                { label: '서귀포', value: '서귀포' },
                { label: '성산', value: '성산' },
                { label: '중문', value: '중문' },
                { label: '제주시', value: '제주시' },
                { label: '우도', value: '우도' }
            ]
        }
    ];

    private calendarMonth: Date = new Date();
    private scheduleDragStartKey: string = '';
    private schedulePointerActive: boolean = false;
    private ignoreNextScheduleClick: boolean = false;
    private pinchStartDistance: number = 0;
    private pinchStartZoom: number = 2;
    private naverMap: any = null;
    private naverMapElement: any = null;
    private naverMarkers: any[] = [];
    private naverRouteLine: any = null;
    private naverCompareLine: any = null;
    private naverUserMarker: any = null;
    private naverSearchMarker: any = null;
    private naverSearchCoordinate: any = null;
    private naverDirectionsService: any = null;
    private naverDirectionsRenderer: any = null;
    private naverMapsLoader: any = null;
    private naverMapsConfigLoader: any = null;
    private naverMapsAdapter: any = null;
    private naverRouteToken: number = 0;
    private naverBoundsToken: number = 0;
    private executionMapZoomAdjustment: number = 0;
    private naverUserCoordinate: any = null;
    private naverCenterOnUser: boolean = false;
    private courseNaverMap: any = null;
    private courseNaverMapElement: any = null;
    private courseNaverMarkers: any[] = [];
    private courseNaverInfoWindows: any[] = [];
    private courseNaverRouteLine: any = null;
    private courseNaverMapClickListener: any = null;
    private profileCourseNaverMap: any = null;
    private profileCourseNaverMapElement: any = null;
    private profileCourseNaverMarkers: any[] = [];
    private profileCourseNaverInfoWindows: any[] = [];
    private profileCourseNaverRouteLine: any = null;
    private profileCourseInlineNaverMap: any = null;
    private profileCourseInlineNaverMapElement: any = null;
    private profileCourseInlineNaverMarkers: any[] = [];
    private profileCourseInlineNaverRouteLine: any = null;
    private profileCourseInlineMapRenderToken: number = 0;
    private profileCourseResolvedCoordinates: any = {};
    private plannerNaverMap: any = null;
    private plannerNaverMapElement: any = null;
    private plannerNaverMarkers: any[] = [];
    private plannerNaverRouteLine: any = null;
    private plannerExpandedNaverMap: any = null;
    private plannerExpandedNaverMapElement: any = null;
    private plannerExpandedNaverMarkers: any[] = [];
    private plannerExpandedNaverRouteLine: any = null;
    private saveHintTimer: any = null;
    private courseDraftSavedToastTimer: any = null;
    private pendingChatPrompt: string = '';
    private placeCoursesRequestToken: number = 0;
    private placeSearchTimer: any = null;
    private coursePlaceSearchTimer: any = null;
    private coursePlaceSearchRequestToken: number = 0;
    private savedCourseIds: any[] = [];
    private courseDraftStorageBaseKey: string = 'tour-on-course-draft';
    private courseDraftArchiveChecked: boolean = false;
    private recentPlacesStorageBaseKey: string = 'tour-on-recent-places';
    private savedPlacesStorageBaseKey: string = 'gachi-saved-places';
    private travelResumeStorageBaseKey: string = 'tour-on-travel-resume';
    private identityReturnId: string = '';
    private portOneSdkLoader: any = null;
    private myProfileEditStorageBaseKey: string = 'gachi-profile-edit';
    private myCourseViewModeStorageBaseKey: string = 'gachi-my-course-view-mode';
    private companionPostsStorageBaseKey: string = 'gachi-companion-posts';
    private savedCompanionPostsStorageBaseKey: string = 'gachi-saved-companion-posts';
    private savedCompanionPostIds: string[] = [];
    private communityPostsStorageKey: string = 'gachi-community-posts';
    private communityActorStorageKey: string = 'gachi-community-actor';
    private communityLikeStorageKey: string = 'gachi-community-liked';
    private communityVoteStorageKey: string = 'gachi-community-voted';

    public tabs: any[] = [
        { key: 'home', label: '홈', icon: 'fa-house', iconStyle: 'fa-solid' },
        { key: 'map', label: '지도', icon: 'fa-map-location-dot', iconStyle: 'fa-solid' },
        { key: 'chat', label: '채팅', icon: 'fa-comment-dots', iconStyle: 'fa-regular' },
        { key: 'saved', label: '찜', icon: 'fa-bookmark', iconStyle: 'fa-regular' },
        { key: 'my', label: '마이', icon: 'fa-user', iconStyle: 'fa-regular' }
    ];

    public zenlyLayerTab: string = 'signals';
    public zenlyHeatmapLoading: boolean = false;
    public zenlySignalsLoading: boolean = false;
    public zenlySignalSubmitting: boolean = false;
    public zenlySignalComposerOpen: boolean = false;
    public selectedPresencePlaceId: string = '';
    public selectedZenlySignalId: string = '';
    public selectedZenlyFallbackCard: any = null;
    public zenlyHeatmap: any = {
        region: '성수',
        regionTotal: 0,
        banner: '지금 성수에 0명이 있어요',
        privacy: '개인 식별자 없이 장소별 시간대 집계만 사용합니다.',
        places: []
    };
    public zenlyPresenceHourly: any[] = [];
    public zenlySignals: any[] = [];
    public zenlyNearbyTravelerCount: number = 0;
    private zenlySignalPresenceTimer: any = null;
    public zenlySignalDurations: any[] = [
        { value: 30, label: '30분' },
        { value: 60, label: '1시간' },
        { value: 180, label: '3시간' }
    ];
    public zenlySignalMoodTags: string[] = ['조용히', '활발하게', '카페', '맛집', '사진', '산책', '전시', '야경'];
    public zenlySignalDraft: any = {
        locationMode: 'current',
        placeQuery: '',
        place: null,
        message: '',
        duration: 60,
        tags: []
    };
    public zenlyPlaceSearchResults: any[] = [];
    public togetherTripStarted: boolean = false;
    public togetherTripEnded: boolean = false;
    public togetherSafetyOpen: boolean = false;
    public togetherInfoFocus: string = 'companions';
    public togetherMapZoom: number = 1;
    public togetherMapPan: any = { x: 0, y: 0 };
    public togetherMapDragging: boolean = false;
    public togetherMapInteracted: boolean = false;
    private togetherMapPointers: any = {};
    private togetherMapLastPoint: any = null;
    private togetherMapPinchDistance: number = 0;
    private togetherMapPinchZoom: number = 1;
    private togetherMapPinchCenter: any = null;
    private togetherMapRenderFrame: any = null;
    public togetherLocationSharingActive: boolean = false;
    public togetherShareDuration: any = 60;
    public togetherShareStartedAt: number = 0;
    private togetherShareTimer: any = null;
    public togetherMeetingAppointment: any = null;
    public togetherMeetingChatMessages: any[] = [];
    public togetherMeetingChatDraft: string = '';
    public togetherMeetingChatOpen: boolean = false;
    public togetherMeetingChatLoading: boolean = false;
    public togetherMeetingChatSending: boolean = false;
    public togetherMeetingSendFailed: boolean = false;
    public togetherMeetingPeerTyping: boolean = false;
    public togetherMeetingUnread: number = 0;
    public togetherRealtimeStatus: string = 'connecting';
    private togetherMeetingExpiryTimer: any = null;
    private togetherMeetingPollTimer: any = null;
    private togetherMeetingTypingTimer: any = null;
    private togetherMeetingTypingActive: boolean = false;
    private togetherMeetingPeerTypingTimer: any = null;
    private togetherLocationWatchId: any = null;
    private togetherLocationPollTimer: any = null;
    private togetherLocationLastSentAt: number = 0;
    private togetherLocationSending: boolean = false;
    private togetherVisibilityHandler: any = null;
    private togetherMapCenterCoordinate: any = null;
    private togetherOwnCoordinate: any = null;
    public selectedTogetherCompanionId: string = 'together-minji';
    public togetherShareDurations: any[] = [
        { value: 30, label: '30분' },
        { value: 60, label: '1시간' },
        { value: 'trip', label: '여행 종료까지' }
    ];
    public togetherPrivateZones: any = {
        home: true,
        stay: true
    };
    public togetherMeetingPoint: any = {
        name: '제주공항 렌터카 셔틀 승강장',
        time: '오전 10:00',
        note: '도착하면 준비방에 알려주세요',
        x: 47,
        y: 57
    };
    public togetherCompanions: any[] = [
        {
            id: 'together-minji',
            name: '민지',
            initial: '민',
            color: '#f97373',
            status: '다음 목적지로 이동 중 · 3분 전',
            x: 58,
            y: 38,
            matched: true,
            mutualConsent: true,
            sharing: true,
            blocked: false,
            reported: false
        },
        {
            id: 'together-junho',
            name: '준호',
            initial: '준',
            color: '#5b8def',
            status: '약속 장소 근처 · 5분 전',
            x: 34,
            y: 64,
            matched: true,
            mutualConsent: false,
            sharing: true,
            blocked: false,
            reported: false
        },
        {
            id: 'together-soyeon',
            name: '소연',
            initial: '소',
            color: '#8b6edb',
            status: '합류 수락을 기다리는 중',
            x: 72,
            y: 61,
            matched: false,
            mutualConsent: false,
            sharing: false,
            blocked: false,
            reported: false
        }
    ];

    public recommendations: any[] = [
        {
            id: 'featured-seoul-riverside',
            title: '서울숲부터 한강까지 감성 산책',
            location: '서울 성동',
            summary: '서울숲 · 성수 카페거리 · 뚝섬한강공원을 잇는 반나절 코스',
            duration: '4시간',
            distance: '4.2km',
            rating: '4.8',
            price: '1인 3만원대',
            tone: 'tone-green',
            icon: 'fa-tree',
            image: 'https://tong.visitkorea.or.kr/cms/resource_photo/99/3580599_image2_1.jpg',
            image_broken: false,
            saved: false,
            places: [
                { name: '서울숲', order: 1, time: '11:00', category: '공원·산책', memo: '숲길과 호수 주변을 천천히 걸으며 여행을 시작해요.', move: '도보 15분' },
                { name: '성수 카페거리', order: 2, time: '13:00', category: '카페·거리', memo: '골목의 전시 공간과 취향에 맞는 카페를 함께 둘러봐요.', move: '도보 20분' },
                { name: '뚝섬한강공원', order: 3, time: '15:30', category: '한강·노을', memo: '강변에 앉아 노을을 감상하며 반나절 코스를 마무리해요.' }
            ],
            tags: ['여행', '연인', '친구', '혼자', '오늘', '이번주말', '당일치기', '서울', '성동', '성수', '산책', '카페', '데이트']
        },
        {
            id: 'featured-busan-ocean',
            title: '해운대 바다 하루 코스',
            location: '부산 해운대',
            summary: '해운대해수욕장 · 동백섬 · 더베이101 야경을 연결한 바다 코스',
            duration: '6시간',
            distance: '5.1km',
            rating: '4.9',
            price: '1인 5만원대',
            tone: 'tone-blue',
            icon: 'fa-water',
            image: '/assets/places/haeundae-beach.jpg',
            image_broken: false,
            saved: false,
            places: [
                { name: '해운대해수욕장', order: 1, time: '12:00', category: '해변', memo: '백사장과 해안 풍경을 여유롭게 즐겨요.', move: '도보 18분' },
                { name: '동백섬', order: 2, time: '14:00', category: '산책·전망', memo: '해안 산책로를 따라 누리마루 전망까지 걸어봐요.', move: '도보 12분' },
                { name: '더베이101', order: 3, time: '18:30', category: '야경·맛집', memo: '마린시티 야경과 함께 저녁 식사를 즐겨요.' }
            ],
            tags: ['여행', '연인', '친구', '가족', '오늘', '이번주말', '당일치기', '부산', '해운대', '바다', '야경', '데이트']
        },
        {
            id: 'featured-jeju-aewol',
            title: '제주 애월 노을 드라이브',
            location: '제주 애월',
            summary: '한담해안산책로 · 애월 카페 · 곽지해수욕장을 도는 여유 코스',
            duration: '5시간',
            distance: '12.8km',
            rating: '4.8',
            price: '1인 6만원대',
            tone: 'tone-sun',
            icon: 'fa-car-side',
            image: 'https://tong.visitkorea.or.kr/cms/resource/34/3090534_image2_1.JPG',
            image_broken: false,
            saved: false,
            places: [
                { name: '한담해안산책로', order: 1, time: '11:00', category: '해안 산책', memo: '바다 바로 옆 산책로에서 애월의 풍경을 만나요.', move: '도보 10분' },
                { name: '애월 카페거리', order: 2, time: '13:00', category: '카페', memo: '오션뷰 카페에서 충분히 쉬어가요.', move: '차량 12분' },
                { name: '곽지해수욕장', order: 3, time: '16:30', category: '해변·노을', memo: '넓은 해변에서 노을을 보며 드라이브를 마무리해요.' }
            ],
            tags: ['여행', '연인', '친구', '가족', '이번주말', '당일치기', '1박2일', '제주', '애월', '바다', '드라이브', '카페']
        },
        {
            id: 'featured-gangneung-coffee',
            title: '강릉 바다와 커피 미식 코스',
            location: '강원 강릉',
            summary: '안목해변 · 커피거리 · 강릉중앙시장을 즐기는 당일 코스',
            duration: '6시간',
            distance: '7.6km',
            rating: '4.7',
            price: '1인 4만원대',
            tone: 'tone-rose',
            icon: 'fa-mug-saucer',
            image: 'https://tong.visitkorea.or.kr/cms/resource/35/3018735_image2_1.jpg',
            image_broken: false,
            saved: false,
            places: [
                { name: '안목해변', order: 1, time: '10:30', category: '해변', memo: '아침 바다를 보며 가볍게 산책해요.', move: '도보 5분' },
                { name: '강릉 커피거리', order: 2, time: '12:00', category: '카페', memo: '취향에 맞는 로스터리에서 커피와 디저트를 즐겨요.', move: '차량 15분' },
                { name: '강릉중앙시장', order: 3, time: '15:00', category: '시장·맛집', memo: '강릉 대표 간식과 로컬 먹거리를 맛봐요.' }
            ],
            tags: ['여행', '연인', '친구', '가족', '혼자', '이번주말', '당일치기', '강원', '강릉', '바다', '카페', '맛집']
        },
        {
            id: 'featured-gyeongju-history',
            title: '경주 낮부터 야경까지 역사 여행',
            location: '경북 경주',
            summary: '대릉원 · 황리단길 · 동궁과 월지를 차례로 만나는 하루 코스',
            duration: '7시간',
            distance: '5.8km',
            rating: '4.9',
            price: '1인 4만원대',
            tone: 'tone-orange',
            icon: 'fa-landmark',
            image: 'https://tong.visitkorea.or.kr/cms/resource/91/2792391_image2_1.jpg',
            image_broken: false,
            saved: false,
            places: [
                { name: '대릉원', order: 1, time: '11:00', category: '역사·산책', memo: '고분과 숲길을 따라 경주의 역사를 천천히 만나봐요.', move: '도보 8분' },
                { name: '황리단길', order: 2, time: '13:00', category: '거리·맛집', memo: '한옥 골목에서 식사와 소품숍을 함께 즐겨요.', move: '차량 8분' },
                { name: '동궁과 월지', order: 3, time: '18:30', category: '유적·야경', memo: '수면에 비친 궁궐 야경으로 하루를 마무리해요.' }
            ],
            tags: ['여행', '연인', '친구', '가족', '혼자', '이번주말', '당일치기', '경북', '경주', '역사', '야경', '맛집']
        }
    ];

    public courses: any[] = [
        {
            id: 'list-iksun',
            title: '익선동 한옥 골목 맛집 투어',
            location: '서울 종로',
            summary: '식사, 디저트, 사진 스폿을 짧게 연결',
            duration: '2시간 30분',
            distance: '2.1km',
            icon: 'fa-utensils',
            saved: false,
            tags: ['여행', '연인', '친구', '가족', '오늘', '이번주말', '서울', '맛집', '데이트', '당일치기']
        },
        {
            id: 'list-gangneung',
            title: '강릉 커피 거리 당일치기',
            location: '강릉 안목',
            summary: '바다 전망 카페와 중앙시장 간식 루트',
            duration: '5시간',
            distance: '6.4km',
            icon: 'fa-cookie-bite',
            saved: false,
            tags: ['여행', '연인', '친구', '가족', '당일치기', '이번주말', '강릉', '바다', '맛집']
        },
        {
            id: 'list-jeonju',
            title: '전주 한옥마을 1박2일 미식 루트',
            location: '전주 완산',
            summary: '한옥 숙소와 시장 간식, 야간 산책을 담은 코스',
            duration: '1박2일',
            distance: '5.7km',
            icon: 'fa-landmark',
            saved: false,
            tags: ['여행', '연인', '친구', '가족', '1박2일', '전주', '전북', '감성숙소', '맛집']
        },
        {
            id: 'list-gyeongju',
            title: '경주 황리단길 감성숙소 코스',
            location: '경주 황남',
            summary: '고분 산책, 감성 숙소, 늦은 저녁 카페까지 연결',
            duration: '1박2일',
            distance: '4.3km',
            icon: 'fa-moon',
            saved: false,
            tags: ['여행', '연인', '친구', '1박2일', '경주', '경북', '감성숙소', '데이트']
        },
        {
            id: 'list-yeosu',
            title: '여수 밤바다 낭만 코스',
            location: '여수 돌산',
            summary: '해상 케이블카와 포차 거리, 야경 산책 루트',
            duration: '1박2일',
            distance: '6.1km',
            icon: 'fa-moon',
            saved: false,
            tags: ['여행', '연인', '친구', '1박2일', '여수', '전남', '데이트', '바다']
        },
        {
            id: 'list-sokcho',
            title: '속초 바다 산책 당일치기',
            location: '속초 청초호',
            summary: '혼자 걷기 좋은 호수길과 시장 먹거리 추천',
            duration: '당일치기',
            distance: '4.8km',
            icon: 'fa-person-walking',
            saved: false,
            tags: ['여행', '혼자', '친구', '오늘', '당일치기', '속초', '강원', '바다', '산책']
        },
        {
            id: 'list-gongju-buyeo',
            title: '공주·부여 역사 드라이브',
            location: '충남 공주',
            summary: '공산성, 백제문화단지, 한적한 카페를 잇는 코스',
            duration: '당일치기',
            distance: '8.2km',
            icon: 'fa-route',
            saved: false,
            tags: ['여행', '가족', '친구', '당일치기', '충남', '공주', '부여', '드라이브']
        },
        {
            id: 'list-tongyeong',
            title: '통영 바다 전망 미식 코스',
            location: '통영 동피랑',
            summary: '벽화마을 산책과 해산물 맛집, 케이블카 전망',
            duration: '당일치기',
            distance: '5.5km',
            icon: 'fa-fish',
            saved: false,
            tags: ['여행', '친구', '가족', '당일치기', '통영', '경남', '바다', '맛집']
        }
    ];

    public mapCategories: any[] = [
        { key: 'cafe', label: '카페', icon: 'fa-mug-saucer' },
        { key: 'food', label: '맛집', icon: 'fa-utensils' },
        { key: 'walk', label: '산책', icon: 'fa-person-walking' },
        { key: 'landmark', label: '명소', icon: 'fa-location-dot' }
    ];

    public activeMapCategories: any = {
        cafe: true,
        food: true,
        walk: true,
        landmark: true
    };

    public mapTravelModes: any[] = [
        { key: 'transit', label: '대중교통', icon: 'fa-bus-simple' },
        { key: 'walk', label: '도보', icon: 'fa-person-walking' },
        { key: 'car', label: '차량', icon: 'fa-car-side' }
    ];

    public moveFilterOptions: any[] = [
        { key: '', label: '전체', icon: 'fa-layer-group' },
        { key: 'walk', label: '도보', icon: 'fa-person-walking' },
        { key: 'car', label: '차량', icon: 'fa-car-side' }
    ];

    public travelMode: string = 'transit';
    public selectedMoveFilter: string = '';
    public selectedMapSpotId: string = '';
    public mapZoom: number = 2;
    public userPosition: any = { x: 82, y: 18 };
    public naverMapReady: boolean = false;
    public naverMapsClientId: string = '';
    public mapCoursePickerOpen: boolean = false;
    public mapCourseSearchQuery: string = '';
    public stagedExecutionCourseId: string = '';
    public mapCoursesLoading: boolean = false;
    public mapExecutableCourses: any[] = [];
    public activeExecutionCourseId: string = '';
    public executionCourse: any = null;
    public executionPlaces: any[] = [];
    public executionTotalMinutes: number = 0;
    public executionTotalDistanceMeters: number = 0;
    public executionRouteSummary: string = '';
    public executionRouteError: string = '';
    public executionNavigationActive: boolean = false;
    public executionNavigationUsingLiveLocation: boolean = false;
    public executionNavigationStatus: string = '';
    public executionNavigationSteps: any[] = [];
    public executionNavigationStepIndex: number = 0;
    public executionNavigationNextDistance: string = '';
    public executionNavigationRemainingMeters: number | null = null;
    public executionNavigationRemainingSeconds: number = 0;
    public executionNavigationSpeedKmh: number = 0;
    public executionVoiceGuidanceEnabled: boolean = true;
    public mapRouteOverviewActive: boolean = false;
    public mapRouteSheetExpanded: boolean = false;
    public mapRouteLoading: boolean = false;
    public mapDepartureLoading: boolean = false;
    public mapStartAddress: string = '';
    public mapStartStatus: string = '현재 위치';
    public mapSearchFocused: boolean = false;
    public mapSearchRemoteSuggestions: any[] = [];
    public mapSearchNaverSuggestions: any[] = [];
    public mapSearchSuggestionLoading: boolean = false;
    public mapSelectedPlace: any = null;
    public mapPlaceRouteOpen: boolean = false;
    public mapPlaceRouteSearchTarget: string = '';
    public mapPlaceRouteOrigin: any = null;
    public mapPlaceRouteDestination: any = null;
    public mapPlaceRouteTravelMode: string = 'transit';
    public mapPlaceRouteLoading: boolean = false;
    public mapPlaceRouteSummary: string = '';
    public mapPlaceRouteSteps: any[] = [];
    public mapPlaceRouteAlternatives: any[] = [];
    public mapPlaceRouteSelectedIndex: number = 0;
    public mapPlaceRouteError: string = '';
    public mapPlaceGuidanceExpanded: boolean = false;
    public mapPlaceNavigationActive: boolean = false;
    public mapPlaceRouteRefreshLoading: boolean = false;
    public mapPlaceRouteUpdatedAt: string = '';
    public mapPlaceRouteProvider: string = 'estimate';
    public mapPlaceRouteSheetCollapsed: boolean = false;
    public mapPlaceRouteSheetDragging: boolean = false;
    public mapPlaceTravelModes: any[] = [
        { key: 'transit', label: '대중교통', icon: 'fa-bus-simple' },
        { key: 'walking', label: '도보', icon: 'fa-person-walking' },
        { key: 'driving', label: '차량', icon: 'fa-car-side' }
    ];
    public mapGpsDenied: boolean = false;
    private mapStartCoordinate: any = null;
    private mapStartRequiresGps: boolean = false;
    private executionLiveOrigin: any = null;
    private executionNavigationLastCoordinate: any = null;
    private executionNavigationLastRefreshAt: number = 0;
    private executionNavigationLastSpokenKey: string = '';
    private executionNavigationPath: any[] = [];
    private executionNavigationPathMeters: number = 0;
    private executionNavigationFilteredCoordinate: any = null;
    private executionNavigationHeading: number | null = null;
    private executionNavigationOffRouteSince: number = 0;
    private executionNavigationLastRerouteAt: number = 0;
    private executionNavigationLastPositionAt: number = 0;
    private executionNavigationLastPositionCoordinate: any = null;
    private executionWakeLock: any = null;
    private mapGeoWatchId: any = null;
    private pendingMobileCourseId: string = '';
    private mobileDeepLinkListenerBound: boolean = false;
    private readonly savedCourseOfflineStorageKey: string = 'gachi-offline-saved-courses-data';
    private ownedCourseRows: any[] = [];
    private mapPlaceNavigationWatchId: any = null;
    private mapPlaceNavigationLastRefreshAt: number = 0;
    private mapPlaceNavigationLastCoordinate: any = null;
    private mapPlaceNaverRoutes: any[] = [];
    private mapPlaceRouteSheetDragStartY: number = 0;
    private mapPlaceRouteSheetDragBaseY: number = 0;
    private mapPlaceRouteSheetDragOffsetY: number = 0;
    private mapPlaceRouteSheetDragHeight: number = 0;
    private mapPlaceRouteSheetDragLastY: number = 0;
    private mapPlaceRouteSheetDragLastAt: number = 0;
    private mapPlaceRouteSheetElement: any = null;
    private mapPlaceRouteSheetSettleTimer: any = null;
    private mapSearchTimer: any = null;
    private pendingMapSearchSuggestion: any = null;

    public mapCenters: any = {
        default: { lat: 37.5447, lng: 127.0557, zoom: 15 },
        서울: { lat: 37.5447, lng: 127.0557, zoom: 15 },
        성수: { lat: 37.5447, lng: 127.0557, zoom: 15 },
        종로: { lat: 37.5741, lng: 126.9861, zoom: 15 },
        익선동: { lat: 37.5741, lng: 126.9895, zoom: 16 },
        한강: { lat: 37.5288, lng: 126.9347, zoom: 15 },
        홍대: { lat: 37.5563, lng: 126.9236, zoom: 15 },
        강남: { lat: 37.4979, lng: 127.0276, zoom: 15 },
        잠실: { lat: 37.5133, lng: 127.1002, zoom: 15 },
        이태원: { lat: 37.5345, lng: 126.9946, zoom: 15 },
        경기: { lat: 37.2869, lng: 127.0117, zoom: 14 },
        수원: { lat: 37.2877, lng: 127.0117, zoom: 15 },
        가평: { lat: 37.8315, lng: 127.5098, zoom: 14 },
        인천: { lat: 37.3925, lng: 126.6349, zoom: 14 },
        송도: { lat: 37.3925, lng: 126.6349, zoom: 15 },
        을왕리: { lat: 37.4470, lng: 126.3715, zoom: 15 },
        부산: { lat: 35.1587, lng: 129.1604, zoom: 15 },
        해운대: { lat: 35.1587, lng: 129.1604, zoom: 15 },
        강원: { lat: 37.7711, lng: 128.9476, zoom: 14 },
        강릉: { lat: 37.7711, lng: 128.9476, zoom: 15 },
        속초: { lat: 38.1904, lng: 128.5989, zoom: 15 },
        춘천: { lat: 37.8813, lng: 127.7298, zoom: 14 },
        원주: { lat: 37.3422, lng: 127.9202, zoom: 14 },
        충청: { lat: 36.4615, lng: 127.1247, zoom: 14 },
        공주: { lat: 36.4615, lng: 127.1247, zoom: 15 },
        태안: { lat: 36.7456, lng: 126.2981, zoom: 14 },
        대전: { lat: 36.3504, lng: 127.3845, zoom: 14 },
        세종: { lat: 36.4801, lng: 127.2890, zoom: 14 },
        청주: { lat: 36.6424, lng: 127.4890, zoom: 14 },
        전라: { lat: 35.8143, lng: 127.1537, zoom: 15 },
        전주: { lat: 35.8143, lng: 127.1537, zoom: 15 },
        여수: { lat: 34.7406, lng: 127.7354, zoom: 14 },
        광주: { lat: 35.1595, lng: 126.8526, zoom: 14 },
        목포: { lat: 34.8118, lng: 126.3922, zoom: 14 },
        경상: { lat: 35.8352, lng: 129.2133, zoom: 15 },
        경주: { lat: 35.8352, lng: 129.2133, zoom: 15 },
        통영: { lat: 34.8454, lng: 128.4270, zoom: 15 },
        대구: { lat: 35.8714, lng: 128.6014, zoom: 14 },
        울산: { lat: 35.5384, lng: 129.3114, zoom: 14 },
        창원: { lat: 35.2285, lng: 128.6811, zoom: 14 },
        제주: { lat: 33.4634, lng: 126.3090, zoom: 14 },
        애월: { lat: 33.4634, lng: 126.3090, zoom: 15 },
        제주시: { lat: 33.4996, lng: 126.5312, zoom: 14 },
        서귀포: { lat: 33.2541, lng: 126.5601, zoom: 14 }
    };

    public mapSpotMap: any = {
        default: [
            { id: 'default-cafe', order: 1, name: '로컬 카페 거리', kind: '카페', category: 'cafe', icon: 'fa-mug-saucer', x: 27, y: 31, rating: '4.7', hours: '10:00-22:00', description: '창가 좌석과 디저트가 좋은 첫 목적지입니다.', times: { walk: '도보 8분', car: '차량 3분' }, photoClass: 'photo-cafe', visited: false },
            { id: 'default-walk', order: 2, name: '대표 산책로', kind: '산책', category: 'walk', icon: 'fa-person-walking', x: 47, y: 48, rating: '4.6', hours: '상시', description: '짧게 걷기 좋고 중간 휴식 지점이 많습니다.', times: { walk: '도보 12분', car: '차량 5분' }, photoClass: 'photo-walk', visited: false },
            { id: 'default-food', order: 3, name: '저녁 맛집 골목', kind: '맛집', category: 'food', icon: 'fa-utensils', x: 68, y: 63, rating: '4.8', hours: '11:30-21:30', description: '식사 선택지가 몰려 있어 마무리 동선에 적합합니다.', times: { walk: '도보 18분', car: '차량 8분' }, photoClass: 'photo-food', visited: false },
            { id: 'default-landmark', order: 4, name: '전망 포인트', kind: '명소', category: 'landmark', icon: 'fa-camera', x: 39, y: 72, rating: '4.5', hours: '상시', description: '사진을 남기기 좋은 코스의 마지막 지점입니다.', times: { walk: '도보 10분', car: '차량 4분' }, photoClass: 'photo-landmark', visited: false }
        ],
        서울: [
            { id: 'seoul-cafe', order: 1, name: '서울숲 카페거리', kind: '카페', category: 'cafe', icon: 'fa-mug-saucer', x: 26, y: 34, rating: '4.8', hours: '10:00-22:00', description: '성수 골목 초입의 브런치와 커피 스폿입니다.', times: { walk: '도보 7분', car: '차량 3분' }, photoClass: 'photo-cafe', visited: true },
            { id: 'seoul-walk', order: 2, name: '서울숲 산책로', kind: '산책', category: 'walk', icon: 'fa-tree', x: 44, y: 49, rating: '4.7', hours: '05:00-22:00', description: '그늘길과 벤치가 이어지는 코스 중심 지점입니다.', times: { walk: '도보 9분', car: '차량 4분' }, photoClass: 'photo-walk', visited: false },
            { id: 'seoul-food', order: 3, name: '성수 맛집 골목', kind: '맛집', category: 'food', icon: 'fa-utensils', x: 63, y: 61, rating: '4.6', hours: '11:30-21:30', description: '예약 없이 들르기 좋은 식당이 밀집한 구간입니다.', times: { walk: '도보 13분', car: '차량 6분' }, photoClass: 'photo-food', visited: false },
            { id: 'seoul-landmark', order: 4, name: '뚝섬 전망데크', kind: '명소', category: 'landmark', icon: 'fa-camera', x: 76, y: 39, rating: '4.5', hours: '상시', description: '한강 방향으로 열리는 사진 포인트입니다.', times: { walk: '도보 15분', car: '차량 8분' }, photoClass: 'photo-landmark', visited: false }
        ],
        부산: [
            { id: 'busan-cafe', order: 1, name: '해운대 오션 카페', kind: '카페', category: 'cafe', icon: 'fa-mug-saucer', x: 24, y: 38, rating: '4.6', hours: '09:00-22:00', description: '바다를 보며 출발하기 좋은 카페입니다.', times: { walk: '도보 5분', car: '차량 2분' }, photoClass: 'photo-cafe', visited: false },
            { id: 'busan-walk', order: 2, name: '해운대 해변길', kind: '산책', category: 'walk', icon: 'fa-water', x: 47, y: 55, rating: '4.9', hours: '상시', description: '바다 옆으로 길게 걷는 대표 산책 구간입니다.', times: { walk: '도보 8분', car: '차량 4분' }, photoClass: 'photo-walk', visited: false },
            { id: 'busan-food', order: 3, name: '미포 식당가', kind: '맛집', category: 'food', icon: 'fa-fish', x: 68, y: 43, rating: '4.7', hours: '11:00-22:00', description: '해산물과 로컬 식당을 함께 고르기 좋습니다.', times: { walk: '도보 16분', car: '차량 7분' }, photoClass: 'photo-food', visited: false },
            { id: 'busan-landmark', order: 4, name: '달맞이 전망길', kind: '명소', category: 'landmark', icon: 'fa-camera', x: 77, y: 68, rating: '4.8', hours: '상시', description: '노을과 야경을 모두 보기 좋은 언덕길입니다.', times: { walk: '도보 24분', car: '차량 9분' }, photoClass: 'photo-landmark', visited: false }
        ],
        제주: [
            { id: 'jeju-cafe', order: 1, name: '애월 브런치 카페', kind: '카페', category: 'cafe', icon: 'fa-mug-saucer', x: 25, y: 37, rating: '4.7', hours: '09:30-19:00', description: '바다 뷰와 가벼운 식사를 함께 잡는 지점입니다.', times: { walk: '도보 10분', car: '차량 4분' }, photoClass: 'photo-cafe', visited: false },
            { id: 'jeju-walk', order: 2, name: '곽지 해변 산책', kind: '산책', category: 'walk', icon: 'fa-water', x: 48, y: 56, rating: '4.8', hours: '상시', description: '모래사장과 산책길을 짧게 잇는 구간입니다.', times: { walk: '도보 14분', car: '차량 6분' }, photoClass: 'photo-walk', visited: false },
            { id: 'jeju-food', order: 3, name: '로컬 해산물 식당', kind: '맛집', category: 'food', icon: 'fa-utensils', x: 67, y: 65, rating: '4.6', hours: '11:00-20:30', description: '늦은 점심이나 저녁으로 이동하기 좋은 식당입니다.', times: { walk: '도보 22분', car: '차량 8분' }, photoClass: 'photo-food', visited: false },
            { id: 'jeju-landmark', order: 4, name: '애월 해안도로', kind: '명소', category: 'landmark', icon: 'fa-camera', x: 74, y: 34, rating: '4.9', hours: '상시', description: '드라이브와 사진 촬영을 함께 잡는 대표 포인트입니다.', times: { walk: '도보 28분', car: '차량 7분' }, photoClass: 'photo-landmark', visited: false }
        ],
        경기: [
            { id: 'gyeonggi-cafe', order: 1, name: '행궁동 카페', kind: '카페', category: 'cafe', icon: 'fa-mug-saucer', x: 29, y: 34, rating: '4.6', hours: '10:00-21:00', description: '골목 초입에서 쉬어가기 좋은 카페입니다.', times: { walk: '도보 8분', car: '차량 4분' }, photoClass: 'photo-cafe', visited: false },
            { id: 'gyeonggi-walk', order: 2, name: '수원 화성 산책', kind: '산책', category: 'walk', icon: 'fa-person-walking', x: 46, y: 53, rating: '4.8', hours: '상시', description: '성곽을 따라 천천히 도는 대표 산책 코스입니다.', times: { walk: '도보 12분', car: '차량 5분' }, photoClass: 'photo-walk', visited: false },
            { id: 'gyeonggi-food', order: 3, name: '통닭거리 맛집', kind: '맛집', category: 'food', icon: 'fa-utensils', x: 66, y: 66, rating: '4.5', hours: '11:00-22:00', description: '식사 선택지가 많아 동선 마무리에 적합합니다.', times: { walk: '도보 16분', car: '차량 7분' }, photoClass: 'photo-food', visited: false },
            { id: 'gyeonggi-landmark', order: 4, name: '방화수류정', kind: '명소', category: 'landmark', icon: 'fa-camera', x: 75, y: 39, rating: '4.7', hours: '상시', description: '수변과 성곽을 함께 담기 좋은 포인트입니다.', times: { walk: '도보 14분', car: '차량 6분' }, photoClass: 'photo-landmark', visited: false }
        ],
        강원: [
            { id: 'gangwon-cafe', order: 1, name: '안목 커피 거리', kind: '카페', category: 'cafe', icon: 'fa-mug-saucer', x: 27, y: 36, rating: '4.6', hours: '09:00-22:00', description: '바다 앞 커피집을 고르기 좋은 거리입니다.', times: { walk: '도보 8분', car: '차량 3분' }, photoClass: 'photo-cafe', visited: false },
            { id: 'gangwon-walk', order: 2, name: '해변 산책로', kind: '산책', category: 'walk', icon: 'fa-water', x: 48, y: 55, rating: '4.7', hours: '상시', description: '바닷바람을 맞으며 짧게 걷는 구간입니다.', times: { walk: '도보 11분', car: '차량 4분' }, photoClass: 'photo-walk', visited: false },
            { id: 'gangwon-food', order: 3, name: '중앙시장 먹거리', kind: '맛집', category: 'food', icon: 'fa-utensils', x: 68, y: 62, rating: '4.5', hours: '10:00-21:00', description: '간식과 식사를 한 번에 해결하기 좋은 지점입니다.', times: { walk: '도보 20분', car: '차량 12분' }, photoClass: 'photo-food', visited: false },
            { id: 'gangwon-landmark', order: 4, name: '서피비치 포인트', kind: '명소', category: 'landmark', icon: 'fa-camera', x: 76, y: 35, rating: '4.8', hours: '상시', description: '사진과 노을을 함께 담기 좋은 해변 포인트입니다.', times: { walk: '도보 30분', car: '차량 18분' }, photoClass: 'photo-landmark', visited: false }
        ],
        충청: [
            { id: 'chungcheong-cafe', order: 1, name: '공산성 카페', kind: '카페', category: 'cafe', icon: 'fa-mug-saucer', x: 27, y: 35, rating: '4.5', hours: '10:00-20:00', description: '산책 전후로 쉬어가기 좋은 카페입니다.', times: { walk: '도보 8분', car: '차량 4분' }, photoClass: 'photo-cafe', visited: false },
            { id: 'chungcheong-walk', order: 2, name: '공산성 산책길', kind: '산책', category: 'walk', icon: 'fa-person-walking', x: 47, y: 51, rating: '4.8', hours: '09:00-18:00', description: '성곽과 강변을 함께 보는 핵심 구간입니다.', times: { walk: '도보 14분', car: '차량 6분' }, photoClass: 'photo-walk', visited: false },
            { id: 'chungcheong-food', order: 3, name: '로컬 한식 골목', kind: '맛집', category: 'food', icon: 'fa-utensils', x: 66, y: 65, rating: '4.4', hours: '11:00-21:00', description: '가족 식사로 고르기 쉬운 식당이 모여 있습니다.', times: { walk: '도보 18분', car: '차량 8분' }, photoClass: 'photo-food', visited: false },
            { id: 'chungcheong-landmark', order: 4, name: '백제 전망 포인트', kind: '명소', category: 'landmark', icon: 'fa-camera', x: 75, y: 38, rating: '4.6', hours: '상시', description: '역사 코스의 사진 지점으로 쓰기 좋습니다.', times: { walk: '도보 24분', car: '차량 12분' }, photoClass: 'photo-landmark', visited: false }
        ],
        전라: [
            { id: 'jeolla-cafe', order: 1, name: '한옥마을 찻집', kind: '카페', category: 'cafe', icon: 'fa-mug-saucer', x: 26, y: 34, rating: '4.5', hours: '10:00-21:00', description: '한옥 골목 안에서 쉬어가기 좋은 찻집입니다.', times: { walk: '도보 6분', car: '차량 3분' }, photoClass: 'photo-cafe', visited: false },
            { id: 'jeolla-walk', order: 2, name: '전주 한옥마을', kind: '산책', category: 'walk', icon: 'fa-person-walking', x: 47, y: 52, rating: '4.7', hours: '상시', description: '골목과 전통 건물을 함께 둘러보는 구간입니다.', times: { walk: '도보 10분', car: '차량 4분' }, photoClass: 'photo-walk', visited: false },
            { id: 'jeolla-food', order: 3, name: '남부시장 맛집', kind: '맛집', category: 'food', icon: 'fa-utensils', x: 68, y: 64, rating: '4.6', hours: '11:00-22:00', description: '전주식 식사와 간식을 같이 고르기 좋습니다.', times: { walk: '도보 14분', car: '차량 6분' }, photoClass: 'photo-food', visited: false },
            { id: 'jeolla-landmark', order: 4, name: '오목대 전망', kind: '명소', category: 'landmark', icon: 'fa-camera', x: 76, y: 39, rating: '4.6', hours: '상시', description: '한옥 지붕 풍경을 내려다보는 사진 포인트입니다.', times: { walk: '도보 12분', car: '차량 5분' }, photoClass: 'photo-landmark', visited: false }
        ],
        경상: [
            { id: 'gyeongsang-cafe', order: 1, name: '황리단길 카페', kind: '카페', category: 'cafe', icon: 'fa-mug-saucer', x: 27, y: 34, rating: '4.6', hours: '10:00-21:00', description: '고분 산책 전후로 쉬어가기 좋은 지점입니다.', times: { walk: '도보 7분', car: '차량 3분' }, photoClass: 'photo-cafe', visited: false },
            { id: 'gyeongsang-walk', order: 2, name: '대릉원 산책로', kind: '산책', category: 'walk', icon: 'fa-person-walking', x: 46, y: 52, rating: '4.8', hours: '09:00-22:00', description: '고분과 나무길이 이어지는 대표 산책 구간입니다.', times: { walk: '도보 12분', car: '차량 5분' }, photoClass: 'photo-walk', visited: false },
            { id: 'gyeongsang-food', order: 3, name: '로컬 미식 골목', kind: '맛집', category: 'food', icon: 'fa-utensils', x: 67, y: 65, rating: '4.5', hours: '11:00-21:30', description: '저녁 식사로 이어가기 좋은 식당 구간입니다.', times: { walk: '도보 16분', car: '차량 7분' }, photoClass: 'photo-food', visited: false },
            { id: 'gyeongsang-landmark', order: 4, name: '첨성대 야경', kind: '명소', category: 'landmark', icon: 'fa-camera', x: 75, y: 38, rating: '4.7', hours: '상시', description: '해가 진 뒤에도 코스 만족도가 높은 명소입니다.', times: { walk: '도보 18분', car: '차량 8분' }, photoClass: 'photo-landmark', visited: false }
        ]
    };

    public async ngOnInit() {
        try {
            this.naverMapsClientId = this.resolveNaverMapsClientId() || this.naverMapsClientId;
            this.prepareCalendar();
            this.restoreIncomingState();
            this.bindMobileDeepLinks();
            this.restoreNotificationReadState();
            await this.service.init();
            this.myProfileOpen = this.activeTab === 'my' && this.isLoggedIn();
            this.myPageReady = true;
            if (this.isLoggedIn()) {
                await wiz.call('track_activity', {});
                await this.loadNotifications(false);
            }
            this.restoreCompanionPostsCache();
            await this.syncOwnedCompanionPosts();
            await this.loadCompanionPosts(false);
            this.loadRecentPlaces();
            this.loadSavedPlaces();
            this.loadSavedCompanionPosts();
            this.loadTravelResume();
            await this.syncTravelResumeFromServer(true);
            this.hydrateCompanionPreparationRooms();
            this.loadMyProfileEdit();
            await this.restoreIdentityReturn();
            this.loadMyCourseViewMode();
            this.restoreCommunityPostsCache();
            await this.loadCommunityPosts(false);
            await this.loadPublicCourses(false);
            if (this.isLoggedIn()) await this.loadMyProfileFollowing(false);
            if (this.routeLoginForMap()) return;
            if (this.routeRootHomeIfEmptyFilters()) return;
            await this.restoreSavedCourses();
            await this.openPendingMobileCourse();
            await this.service.render();
            if (this.activeTab === 'home') this.refreshHomePlacesInBackground();
            if (this.isLoggedIn()) {
                await this.loadAccountSettings();
                await this.loadChatThreads(false);
                await this.loadDirectChatRooms(true);
                this.startDirectChatRealtime();
            }
            if (this.pendingChatPrompt) {
                let prompt = this.pendingChatPrompt;
                this.pendingChatPrompt = '';
                await this.sendChatPrompt(prompt);
            }
            if (this.activeTab === 'map' && this.mapContentTab === 'map') await this.prepareMapExecution();
            if (this.activeTab === 'map' && this.mapContentTab === 'zenly') await this.loadTogetherMapData(false);
        } catch (e) {
            this.myPageReady = true;
            this.activeTab = 'home';
            this.homeContentTab = 'feed';
            this.filterOverviewOpen = false;
            this.courseComposerOpen = false;
            try { await this.service.render(); } catch (renderError) { }
        }
    }

    public ngOnDestroy() {
        this.closeDeparturePreparation();
        this.resetCompanionMeetingSearch();
        if (this.zenlySignalPresenceTimer) window.clearTimeout(this.zenlySignalPresenceTimer);
        this.stopDirectChatRealtime();
        this.stopDirectChatRoomRefresh();
        this.stopTogetherLocationTracking(true);
        this.clearTogetherMeetingTimers();
        this.releaseExecutionWakeLock();
        if (this.mapGeoWatchId && typeof navigator !== 'undefined' && navigator.geolocation) {
            try { navigator.geolocation.clearWatch(this.mapGeoWatchId); } catch (e) { }
        }
    }

    public weatherHeaderEmoji() {
        return this.weatherCurrent ? this.weatherEmoji(this.weatherCurrent.code) : '🌤️';
    }

    public async openWeather() {
        this.activeTab = 'weather';
        await this.service.render();
        if (!this.weatherForecast.length) await this.loadWeather(false);
    }

    public async refreshWeather() {
        await this.loadWeather(this.selectedWeatherRegion === 'current');
    }

    public async onWeatherRegionChange(value: string) {
        let region = this.weatherRegions.find((item: any) => item.value === value) || this.weatherRegions[1];
        this.selectedWeatherRegion = region.value;
        if (region.value === 'current') {
            await this.loadWeather(true);
            return;
        }
        this.weatherCoordinates = { latitude: region.latitude, longitude: region.longitude };
        this.weatherLocationLabel = region.label;
        try { localStorage.setItem('gachi-weather-region', region.value); } catch (e) { }
        await this.loadWeather(false);
    }

    public async openNotifications() {
        this.activeTab = 'notifications';
        await this.loadNotifications(false);
        await this.service.render();
    }

    public async closeUtilityPanel() {
        await this.setTab('home');
    }

    public unreadNotificationCount() {
        return this.notifications.filter((item: any) => !item.read).length;
    }

    public async markNotificationRead(item: any) {
        if (!item) return;
        if (!item.read && this.isLoggedIn()) {
            try {
                const response: any = await wiz.call('notifications', { action: 'read', notification_id: item.id });
                if (response && response.code === 200 && response.data && Array.isArray(response.data.notifications)) {
                    this.applyNotifications(response.data.notifications);
                } else {
                    item.read = true;
                }
            } catch (e) { item.read = true; }
        }
        await this.service.render();
        let deepLink = String(item.deepLink || item.deep_link || '').trim();
        if (deepLink) this.service.href(deepLink);
    }

    public async markAllNotificationsRead() {
        if (this.isLoggedIn()) {
            try {
                const response: any = await wiz.call('notifications', { action: 'read_all' });
                if (response && response.code === 200 && response.data && Array.isArray(response.data.notifications)) {
                    this.applyNotifications(response.data.notifications);
                } else {
                    this.notifications.forEach((item: any) => item.read = true);
                }
            } catch (e) { this.notifications.forEach((item: any) => item.read = true); }
        }
        await this.service.render();
    }

    private notificationVisual(eventType: string) {
        if (eventType.indexOf('course') > -1) return { icon: 'fa-route', tone: 'course' };
        if (eventType.indexOf('signal') > -1 || eventType.indexOf('meeting') > -1 || eventType.indexOf('companion') > -1) return { icon: 'fa-user-group', tone: 'companion' };
        if (eventType.indexOf('weather') > -1) return { icon: 'fa-cloud-sun', tone: 'weather' };
        return { icon: 'fa-bell', tone: 'course' };
    }

    private notificationTimeLabel(value: any) {
        let date = new Date(String(value || ''));
        if (isNaN(date.getTime())) return '';
        let seconds = Math.max(0, Math.floor((Date.now() - date.getTime()) / 1000));
        if (seconds < 60) return '방금 전';
        if (seconds < 3600) return `${Math.floor(seconds / 60)}분 전`;
        if (seconds < 86400) return `${Math.floor(seconds / 3600)}시간 전`;
        if (seconds < 604800) return `${Math.floor(seconds / 86400)}일 전`;
        return date.toLocaleDateString('ko-KR');
    }

    private applyNotifications(rows: any[] = []) {
        this.notifications = (rows || []).map((row: any) => {
            let eventType = String(row.event_type || row.eventType || 'general');
            let visual = this.notificationVisual(eventType);
            return {
                id: String(row.id || ''),
                title: String(row.title || 'GACHI 알림'),
                message: String(row.message || row.body || ''),
                deepLink: String(row.deep_link || row.deepLink || ''),
                time: this.notificationTimeLabel(row.created_at || row.createdAt),
                read: !!row.read,
                icon: visual.icon,
                tone: visual.tone
            };
        });
    }

    private async loadNotifications(showLoading: boolean = true) {
        if (!this.isLoggedIn()) {
            this.notifications = [];
            return false;
        }
        this.notificationsLoading = showLoading;
        try {
            const response: any = await wiz.call('notifications', { action: 'load' });
            if (response && response.code === 200 && response.data && Array.isArray(response.data.notifications)) {
                this.applyNotifications(response.data.notifications);
                this.notificationsLoading = false;
                return true;
            }
        } catch (e) { }
        this.notificationsLoading = false;
        return false;
    }

    private restoreNotificationReadState() {
        try {
            let stored = JSON.parse(localStorage.getItem('gachi-notification-read') || '[]');
            if (Array.isArray(stored)) this.notifications.forEach((item: any) => item.read = stored.indexOf(item.id) >= 0);
            let regionValue = localStorage.getItem('gachi-weather-region') || 'seoul';
            let region = this.weatherRegions.find((item: any) => item.value === regionValue);
            if (region && region.value !== 'current') {
                this.selectedWeatherRegion = region.value;
                this.weatherLocationLabel = region.label;
                this.weatherCoordinates = { latitude: region.latitude, longitude: region.longitude };
            }
        } catch (e) { }
    }

    private persistNotificationReadState() {
        try {
            let readIds = this.notifications.filter((item: any) => item.read).map((item: any) => item.id);
            localStorage.setItem('gachi-notification-read', JSON.stringify(readIds));
        } catch (e) { }
    }

    private async loadWeather(requestLocation: boolean) {
        if (this.weatherLoading) return;
        this.weatherLoading = true;
        this.weatherError = '';
        await this.service.render();

        if (requestLocation && navigator.geolocation) {
            try {
                let position: any = await new Promise((resolve, reject) => {
                    navigator.geolocation.getCurrentPosition(resolve, reject, {
                        enableHighAccuracy: false,
                        timeout: 5000,
                        maximumAge: 10 * 60 * 1000
                    });
                });
                this.weatherCoordinates = {
                    latitude: Number(position.coords.latitude),
                    longitude: Number(position.coords.longitude)
                };
                this.selectedWeatherRegion = 'current';
                this.weatherLocationLabel = '현재 위치';
            } catch (e) {
                let fallback = this.weatherRegions[1];
                this.selectedWeatherRegion = fallback.value;
                this.weatherLocationLabel = fallback.label;
                this.weatherCoordinates = { latitude: fallback.latitude, longitude: fallback.longitude };
            }
        }

        let latitude = Number(this.weatherCoordinates.latitude || 37.5665);
        let longitude = Number(this.weatherCoordinates.longitude || 126.9780);
        let query = new URLSearchParams({
            latitude: String(latitude),
            longitude: String(longitude),
            current: 'temperature_2m,apparent_temperature,relative_humidity_2m,weather_code,wind_speed_10m',
            daily: 'weather_code,temperature_2m_max,temperature_2m_min,apparent_temperature_max,apparent_temperature_min,precipitation_probability_max,wind_speed_10m_max',
            timezone: 'auto',
            forecast_days: '16'
        });

        try {
            let response = await fetch('https://api.open-meteo.com/v1/forecast?' + query.toString(), { cache: 'no-store' });
            if (!response.ok) throw new Error('weather request failed');
            let data: any = await response.json();
            let current = data.current || {};
            let daily = data.daily || {};
            this.weatherCurrent = {
                temperature: current.temperature_2m,
                apparentTemperature: current.apparent_temperature,
                humidity: current.relative_humidity_2m,
                code: current.weather_code,
                windSpeed: current.wind_speed_10m
            };
            this.weatherForecast = (daily.time || []).map((date: string, index: number) => ({
                date,
                code: (daily.weather_code || [])[index],
                max: (daily.temperature_2m_max || [])[index],
                min: (daily.temperature_2m_min || [])[index],
                apparentMax: (daily.apparent_temperature_max || [])[index],
                apparentMin: (daily.apparent_temperature_min || [])[index],
                rainProbability: Math.round(Number((daily.precipitation_probability_max || [])[index] || 0)),
                windSpeed: (daily.wind_speed_10m_max || [])[index]
            }));
            if (!this.weatherForecast.some((day: any) => day.date === this.selectedWeatherDate)) {
                this.selectedWeatherDate = this.weatherForecast.length ? this.weatherForecast[0].date : '';
            }
            if (this.selectedWeatherDate) {
                let selectedDate = new Date(this.selectedWeatherDate + 'T00:00:00');
                this.weatherCalendarMonth = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1);
            }
            this.buildWeatherCalendar();
            this.weatherUpdatedLabel = '방금 업데이트 · ' + this.weatherTimeLabel(new Date());
        } catch (e) {
            this.weatherError = '날씨 정보를 불러오지 못했어요. 잠시 후 다시 시도해 주세요.';
        } finally {
            this.weatherLoading = false;
            await this.service.render();
        }
    }

    private buildWeatherCalendar() {
        let year = this.weatherCalendarMonth.getFullYear();
        let month = this.weatherCalendarMonth.getMonth();
        let firstWeekday = new Date(year, month, 1).getDay();
        let daysInMonth = new Date(year, month + 1, 0).getDate();
        let forecastByDate: any = {};
        this.weatherForecast.forEach((day: any) => forecastByDate[day.date] = day);
        let cells: any[] = [];
        for (let index = 0; index < firstWeekday; index++) cells.push({ blank: true });
        for (let day = 1; day <= daysInMonth; day++) {
            let key = year + '-' + String(month + 1).padStart(2, '0') + '-' + String(day).padStart(2, '0');
            cells.push({
                blank: false,
                day,
                key,
                forecast: forecastByDate[key] || null,
                isToday: this.weatherForecast.length > 0 && key === this.weatherForecast[0].date
            });
        }
        this.weatherCalendarCells = cells;
    }

    public async moveWeatherMonth(offset: number) {
        this.weatherCalendarMonth = new Date(
            this.weatherCalendarMonth.getFullYear(),
            this.weatherCalendarMonth.getMonth() + offset,
            1
        );
        this.buildWeatherCalendar();
        await this.service.render();
    }

    public weatherMonthLabel() {
        return this.weatherCalendarMonth.getFullYear() + '년 ' + (this.weatherCalendarMonth.getMonth() + 1) + '월';
    }

    public async selectWeatherDate(day: any) {
        if (!day) return;
        this.selectedWeatherDate = day.date;
        await this.service.render();
    }

    public selectedWeatherForecast() {
        return this.weatherForecast.find((day: any) => day.date === this.selectedWeatherDate) || null;
    }

    public isSelectedWeatherToday() {
        return this.weatherForecast.length > 0 && this.selectedWeatherDate === this.weatherForecast[0].date;
    }

    public weatherCellAriaLabel(cell: any) {
        if (!cell || !cell.forecast) return cell && cell.day ? cell.day + '일 예보 없음' : '';
        return cell.day + '일 ' + this.weatherLabel(cell.forecast.code) + ', 최고 ' +
            this.weatherTemperature(cell.forecast.max) + '도, 최저 ' + this.weatherTemperature(cell.forecast.min) + '도';
    }

    public weatherDetailDateLabel(date: string) {
        let parsed = new Date(date + 'T00:00:00');
        return (parsed.getMonth() + 1) + '월 ' + parsed.getDate() + '일 ' +
            ['일', '월', '화', '수', '목', '금', '토'][parsed.getDay()] + '요일';
    }

    public weatherTemperature(value: any) {
        let number = Number(value);
        return isFinite(number) ? Math.round(number) : '-';
    }

    public weatherTimeLabel(date: Date) {
        return date.toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' });
    }

    public weatherEmoji(code: any) {
        let value = Number(code);
        if (value === 0) return '☀️';
        if ([1, 2].indexOf(value) >= 0) return '🌤️';
        if (value === 3) return '☁️';
        if ([45, 48].indexOf(value) >= 0) return '🌫️';
        if ([51, 53, 55, 56, 57].indexOf(value) >= 0) return '🌦️';
        if ([61, 63, 65, 66, 67, 80, 81, 82].indexOf(value) >= 0) return '🌧️';
        if ([71, 73, 75, 77, 85, 86].indexOf(value) >= 0) return '🌨️';
        if ([95, 96, 99].indexOf(value) >= 0) return '⛈️';
        return '🌤️';
    }

    public weatherLabel(code: any) {
        let value = Number(code);
        if (value === 0) return '맑음';
        if ([1, 2].indexOf(value) >= 0) return '대체로 맑음';
        if (value === 3) return '흐림';
        if ([45, 48].indexOf(value) >= 0) return '안개';
        if ([51, 53, 55, 56, 57].indexOf(value) >= 0) return '이슬비';
        if ([61, 63, 65, 66, 67, 80, 81, 82].indexOf(value) >= 0) return '비';
        if ([71, 73, 75, 77, 85, 86].indexOf(value) >= 0) return '눈';
        if ([95, 96, 99].indexOf(value) >= 0) return '뇌우';
        return '날씨 확인 중';
    }

    public async setTab(tab: string) {
        if (tab === 'zenly' || tab === 'together') {
            tab = 'map';
            this.mapContentTab = 'zenly';
        }
        if (tab === 'map' && !this.isLoggedIn()) {
            this.goMapLogin();
            return;
        }

        this.activeTab = tab;
        if (tab !== 'map') this.togetherMeetingChatOpen = false;
        this.activeFilterKey = '';
        this.filterOverviewOpen = false;
        this.selectedMapSpotId = '';
        this.myProfileOpen = tab === 'my' && this.isLoggedIn();
        this.myProfileEditOpen = false;
        this.myResumeOpen = false;
        this.myResumePreviewOpen = false;
        this.myFeedComposerOpen = false;
        this.myArchiveOpen = false;
        this.myActivityOpen = false;
        this.mySettingsDetail = '';
        this.myFeedDetailOpen = false;
        this.myCourseDetailOpen = false;
        this.profileFeedDetailPhotoIndex = 0;
        this.profileCourseDetailPhotoIndex = 0;
        this.selectedMyProfilePost = null;
        this.selectedMyProfileCourse = null;
        this.persistAccessState();
        this.replaceAccessUrl();
        if (tab === 'my' && this.isLoggedIn()) await this.loadMyProfileFollowing(false);
        await this.service.render();
        if (tab === 'home') this.refreshHomePlacesInBackground();
        if (tab === 'chat') {
            await this.loadChatThreads(false);
            await this.loadDirectChatRooms(true);
            if (!this.directChatSocket || !this.directChatPollTimer) this.startDirectChatRealtime();
        }
        if (tab === 'map' && this.mapContentTab === 'map') await this.prepareMapExecution();
        if (tab === 'map' && this.mapContentTab === 'zenly') await this.loadTogetherMapData(false);
    }

    public async selectTab(tab: string) {
        if (tab === 'chat' && ['chat', 'dm'].indexOf(this.chatContentTab) < 0) this.chatContentTab = 'chat';
        await this.setTab(tab);
        if (tab === 'chat') this.resetChatContentScroll();
    }

    public async setHomeContentTab(tab: string) {
        if (['feed', 'courses', 'companion', 'community'].indexOf(tab) < 0) tab = 'feed';
        this.companionCourseFilterId = '';
        this.companionCourseFilterTitle = '';
        this.homeContentTab = tab;
        await this.service.render();
        this.resetHomeContentScroll();
    }

    public async setCompanionMode(mode: string) {
        if (['pretrip', 'instant'].indexOf(mode) < 0) return;
        this.companionMode = mode;
        this.persistAccessState();
        this.replaceAccessUrl();
        await this.service.render();
    }

    public async openCompanionMode(mode: string) {
        if (['pretrip', 'instant'].indexOf(mode) < 0) mode = 'pretrip';
        this.activeTab = 'home';
        this.homeContentTab = 'companion';
        this.companionMode = mode;
        this.companionCourseFilterId = '';
        this.companionCourseFilterTitle = '';
        this.persistAccessState();
        this.replaceAccessUrl();
        await this.service.render();
        this.resetHomeContentScroll();
    }

    public async openInstantCompanion() {
        await this.setTab('zenly');
        if (this.activeTab !== 'map' || this.mapContentTab !== 'zenly') return;
        if (!this.isTogetherMapActive()) await this.startTogetherTrip();
        if (!this.isTogetherMapActive()) return;
        await this.setZenlyLayerTab('signals');
        if (!this.zenlySignalComposerOpen) await this.openTogetherSignalComposer();
    }

    public async setChatContentTab(tab: string) {
        if (['chat', 'dm'].indexOf(tab) < 0) return;
        let changed = this.chatContentTab !== tab;
        this.chatContentTab = tab;
        if (tab !== 'chat') this.chatDrawerOpen = false;
        if (changed) {
            this.directRoomOpen = false;
            this.directActionMenuOpen = false;
            this.directLedgerOpen = false;
            this.directLedgerTab = 'history';
        }
        await this.service.render();
        if (tab === 'dm' && this.isLoggedIn()) {
            await this.loadDirectChatRooms(true);
            if (!this.directChatSocket || !this.directChatPollTimer) this.startDirectChatRealtime();
        }
        this.resetChatContentScroll();
    }

    public async setMapContentTab(tab: string) {
        tab = this.normalizeMapContentTab(tab);
        if (!this.isSupportedMapContentTab(tab)) return;
        this.mapContentTab = tab;
        if (tab !== 'zenly') this.togetherMeetingChatOpen = false;
        this.selectedMapSpotId = '';
        this.persistAccessState();
        this.replaceAccessUrl();
        await this.service.render();
        if (tab === 'map') await this.prepareMapExecution();
        if (tab === 'zenly') {
            this.bindZenlyMarkerDelegation();
            await this.loadTogetherMapData(false);
        }
    }

    public togetherConfirmedTrip() {
        if (this.executionCourse) {
            return {
                courseId: this.executionCourse.id || this.activeExecutionCourseId,
                courseConfirmed: true,
                route: this.executionCourse.title || this.executionCourseTitle(),
                title: this.executionCourse.title || this.executionCourseTitle(),
                date: '오늘',
                time: '현재 코스 실행 중',
                meetingPoint: this.togetherMeetingPoint.name,
                routeStops: this.executionPlaces.map((place: any) => place.name)
            };
        }
        return this.companionPosts.find((post: any) => {
            return this.isConfirmedCompanionPost(post) && post.status === 'matched';
        }) || null;
    }

    public hasConfirmedTogetherTrip() {
        return !!this.togetherConfirmedTrip();
    }

    public isTogetherMapActive() {
        return !!this.executionCourse || this.togetherTripStarted || this.hasActiveTogetherMeetingChat();
    }

    public togetherTripTitle() {
        let trip = this.togetherConfirmedTrip();
        if (!trip && this.hasActiveTogetherMeetingChat()) {
            return String(this.togetherMeetingAppointment.title || '주변 즉석 만남');
        }
        return String(trip && (trip.route || trip.title) ? (trip.route || trip.title) : '확정된 여행');
    }

    public togetherTripSchedule() {
        let trip = this.togetherConfirmedTrip();
        let schedule = [trip && trip.date, trip && trip.time].filter((value: any) => !!value).join(' · ');
        if (!schedule && this.hasActiveTogetherMeetingChat()) return this.togetherMeetingRemainingLabel();
        return schedule || '여행 종료 시 자동으로 닫혀요';
    }

    public togetherNextDestination() {
        let next = this.executionPlaces.find((place: any) => place && !place.visited) || this.executionPlaces[0];
        if (next) {
            let eta = next.times && next.times[this.travelMode] ? next.times[this.travelMode] : '도착 시간 계산 중';
            return {
                name: next.name || '다음 목적지',
                eta,
                detail: next.categoryLabel || next.kind || next.location || '코스 목적지'
            };
        }
        let trip = this.togetherConfirmedTrip();
        let stops = this.companionRouteStops(trip);
        return {
            name: stops[1] || stops[0] || '다음 목적지 확인 중',
            eta: '약 18분 뒤',
            detail: stops.length > 1 ? `${stops[0]}에서 이동` : '준비방 코스 기준'
        };
    }

    public async loadTogetherMapData(showLoading: boolean = false) {
        this.zenlyLayerTab = 'signals';
        if (!this.isTogetherMapActive()) {
            await this.loadTogetherMeeting(false, false);
            if (this.hasActiveTogetherMeetingChat()) {
                this.togetherTripStarted = true;
                this.togetherTripEnded = false;
            } else {
                this.zenlySignals = [];
                this.selectedZenlySignalId = '';
                return;
            }
        }
        await this.loadZenlySignals(showLoading);
        if (this.zenlySignalPresenceTimer) window.clearTimeout(this.zenlySignalPresenceTimer);
        this.zenlySignalPresenceTimer = window.setTimeout(async () => {
            this.zenlySignalPresenceTimer = null;
            if (this.activeTab === 'map' && this.mapContentTab === 'zenly' && !document.hidden && this.isTogetherMapActive()) {
                await this.loadTogetherMapData(false);
            }
        }, 15000);
        await this.loadTogetherMeeting(false, false);
        this.syncTogetherCompanionsFromTrip();
        await this.refreshTogetherLocations(false);
        this.bindZenlyMarkerDelegation();
        await this.service.render();
    }

    public async startTogetherTrip() {
        if (!this.hasConfirmedTogetherTrip()) {
            await this.showSaveHint('확정된 동행 여행이 있어야 같이 지도를 열 수 있어요.');
            return;
        }
        this.togetherTripStarted = true;
        this.togetherTripEnded = false;
        this.togetherLocationSharingActive = false;
        this.togetherShareStartedAt = 0;
        this.clearTogetherShareTimer();
        this.togetherMapCenterCoordinate = null;
        this.togetherOwnCoordinate = null;
        this.togetherInfoFocus = 'companions';
        this.resetTogetherMapView();
        this.zenlyLayerTab = 'signals';
        await this.activateTogetherTripMeeting();
        await this.loadTogetherMapData(false);
        await this.service.render();
        await this.showSaveHint('같이 지도가 열렸어요. 정확한 위치는 공유 시간을 고른 뒤 공개돼요.');
    }

    public togetherMapWorldTransform() {
        return `translate3d(${this.togetherMapPan.x}px, ${this.togetherMapPan.y}px, 0) scale(${this.togetherMapZoom})`;
    }

    public togetherMapBackgroundPosition() {
        return `${Math.round(this.togetherMapPan.x)}px ${Math.round(this.togetherMapPan.y)}px`;
    }

    public togetherMapBackgroundSize() {
        let zoom = this.clampTogetherMapZoom(this.togetherMapZoom);
        let grid = Math.max(24, Math.round(42 * zoom));
        return `${grid}px ${grid}px, ${grid}px ${grid}px, ${Math.round(420 * zoom)}px ${Math.round(360 * zoom)}px, ${Math.round(520 * zoom)}px ${Math.round(440 * zoom)}px`;
    }

    public togetherMapMarkerScale() {
        return String(1 / Math.max(1, this.togetherMapZoom));
    }

    public togetherMapZoomLabel() {
        return `${Math.round(this.togetherMapZoom * 100)}%`;
    }

    public startTogetherMapPan(event: any) {
        if (!event || (event.pointerType === 'mouse' && event.button !== 0)) return;
        let target = event.target;
        if (target && target.closest && target.closest('button, input, textarea, form, a')) return;
        if (event.preventDefault) event.preventDefault();
        this.togetherMapInteracted = true;
        this.togetherMapPointers[String(event.pointerId)] = { x: event.clientX, y: event.clientY };
        if (event.currentTarget && event.currentTarget.setPointerCapture) {
            try { event.currentTarget.setPointerCapture(event.pointerId); } catch (e) { }
        }
        let points = this.togetherMapPointerList();
        if (points.length === 1) {
            this.togetherMapDragging = true;
            this.togetherMapLastPoint = points[0];
        } else if (points.length >= 2) {
            this.togetherMapDragging = true;
            this.togetherMapPinchDistance = this.togetherMapDistance(points[0], points[1]);
            this.togetherMapPinchZoom = this.togetherMapZoom;
            this.togetherMapPinchCenter = this.togetherMapCenter(points[0], points[1]);
        }
        this.scheduleTogetherMapRender();
    }

    public moveTogetherMapPan(event: any) {
        let key = String(event && event.pointerId);
        if (!event || !this.togetherMapPointers[key]) return;
        if (event.preventDefault) event.preventDefault();
        this.togetherMapPointers[key] = { x: event.clientX, y: event.clientY };
        let points = this.togetherMapPointerList();
        if (points.length >= 2) {
            let distance = Math.max(1, this.togetherMapDistance(points[0], points[1]));
            let center = this.togetherMapCenter(points[0], points[1]);
            if (!this.togetherMapPinchDistance) {
                this.togetherMapPinchDistance = distance;
                this.togetherMapPinchZoom = this.togetherMapZoom;
                this.togetherMapPinchCenter = center;
            }
            this.togetherMapZoom = this.clampTogetherMapZoom(this.togetherMapPinchZoom * distance / this.togetherMapPinchDistance);
            if (this.togetherMapPinchCenter) {
                this.togetherMapPan.x += center.x - this.togetherMapPinchCenter.x;
                this.togetherMapPan.y += center.y - this.togetherMapPinchCenter.y;
            }
            this.togetherMapPinchCenter = center;
        } else if (points.length === 1 && this.togetherMapLastPoint) {
            this.togetherMapPan.x += points[0].x - this.togetherMapLastPoint.x;
            this.togetherMapPan.y += points[0].y - this.togetherMapLastPoint.y;
            this.togetherMapLastPoint = points[0];
        }
        this.clampTogetherMapPan(event.currentTarget);
        this.scheduleTogetherMapRender();
    }

    public endTogetherMapPan(event: any) {
        if (!event) return;
        delete this.togetherMapPointers[String(event.pointerId)];
        if (event.currentTarget && event.currentTarget.releasePointerCapture) {
            try {
                if (!event.currentTarget.hasPointerCapture || event.currentTarget.hasPointerCapture(event.pointerId)) {
                    event.currentTarget.releasePointerCapture(event.pointerId);
                }
            } catch (e) { }
        }
        let points = this.togetherMapPointerList();
        this.togetherMapPinchDistance = 0;
        this.togetherMapPinchCenter = null;
        this.togetherMapLastPoint = points.length ? points[0] : null;
        this.togetherMapDragging = points.length > 0;
        this.scheduleTogetherMapRender();
    }

    public async zoomTogetherMap(step: number, event?: any) {
        if (event && event.stopPropagation) event.stopPropagation();
        this.togetherMapInteracted = true;
        this.togetherMapZoom = this.clampTogetherMapZoom(this.togetherMapZoom + Number(step || 0));
        this.clampTogetherMapPan();
        await this.service.render();
    }

    public zoomTogetherMapWheel(event: any) {
        if (!event) return;
        if (event.preventDefault) event.preventDefault();
        this.togetherMapInteracted = true;
        this.togetherMapZoom = this.clampTogetherMapZoom(this.togetherMapZoom + (event.deltaY < 0 ? 0.15 : -0.15));
        this.clampTogetherMapPan(event.currentTarget);
        this.scheduleTogetherMapRender();
    }

    public async recenterTogetherMap(event?: any) {
        if (event && event.stopPropagation) event.stopPropagation();
        if (this.togetherOwnCoordinate) this.togetherMapCenterCoordinate = { ...this.togetherOwnCoordinate };
        this.resetTogetherMapView();
        this.reprojectTogetherLocations();
        this.togetherMapInteracted = true;
        await this.service.render();
    }

    private resetTogetherMapView() {
        this.togetherMapZoom = 1;
        this.togetherMapPan = { x: 0, y: 0 };
        this.togetherMapDragging = false;
        this.togetherMapPointers = {};
        this.togetherMapLastPoint = null;
        this.togetherMapPinchDistance = 0;
        this.togetherMapPinchCenter = null;
    }

    private togetherMapPointerList() {
        return Object.keys(this.togetherMapPointers).map((key: string) => this.togetherMapPointers[key]);
    }

    private togetherMapDistance(first: any, second: any) {
        return Math.hypot(Number(second.x) - Number(first.x), Number(second.y) - Number(first.y));
    }

    private togetherMapCenter(first: any, second: any) {
        return { x: (Number(first.x) + Number(second.x)) / 2, y: (Number(first.y) + Number(second.y)) / 2 };
    }

    private clampTogetherMapZoom(value: number) {
        return Math.min(3, Math.max(0.6, Math.round(value * 100) / 100));
    }

    private clampTogetherMapPan(viewport?: any) {
        let width = viewport && viewport.clientWidth ? viewport.clientWidth : 390;
        let height = viewport && viewport.clientHeight ? viewport.clientHeight : 650;
        let scaleRoom = Math.max(0, this.togetherMapZoom - 1);
        let maxX = width * 0.82 + width * scaleRoom * 0.5;
        let maxY = height * 0.72 + height * scaleRoom * 0.5;
        this.togetherMapPan.x = Math.min(maxX, Math.max(-maxX, this.togetherMapPan.x));
        this.togetherMapPan.y = Math.min(maxY, Math.max(-maxY, this.togetherMapPan.y));
    }

    private scheduleTogetherMapRender() {
        if (this.togetherMapRenderFrame || typeof window === 'undefined') return;
        this.togetherMapRenderFrame = window.requestAnimationFrame(async () => {
            this.togetherMapRenderFrame = null;
            await this.service.render();
        });
    }

    public async setTogetherInfoFocus(focus: string, event?: any) {
        if (event && event.stopPropagation) event.stopPropagation();
        if (['companions', 'route', 'meeting', 'signals'].indexOf(focus) < 0) return;
        this.togetherMeetingChatOpen = false;
        this.togetherInfoFocus = focus;
        if (focus === 'signals') this.zenlyLayerTab = 'signals';
        await this.service.render();
    }

    public async openTogetherSafety(event?: any) {
        if (event && event.stopPropagation) event.stopPropagation();
        this.zenlySignalComposerOpen = false;
        this.togetherMeetingChatOpen = false;
        this.togetherSafetyOpen = true;
        await this.service.render();
    }

    public async closeTogetherSafety() {
        this.togetherSafetyOpen = false;
        await this.service.render();
    }

    public async closeTogetherOverlays() {
        this.togetherSafetyOpen = false;
        this.zenlySignalComposerOpen = false;
        this.togetherMeetingChatOpen = false;
        await this.service.render();
    }

    public async setTogetherShareDuration(duration: any) {
        if ([30, 60, 'trip'].indexOf(duration) < 0) return;
        this.togetherShareDuration = duration;
        if (this.togetherLocationSharingActive) this.scheduleTogetherShareExpiry();
        await this.service.render();
    }

    public async startTogetherLocationShare() {
        if (!this.isTogetherMapActive()) {
            await this.showSaveHint('여행을 시작한 뒤 위치를 공유할 수 있어요.');
            return;
        }
        let meeting = this.togetherMeetingAppointment;
        if (!meeting || !meeting.id || !meeting.serverBacked) {
            await this.showSaveHint('동행 약속을 확인한 뒤 위치를 공개할 수 있어요.');
            return;
        }
        let coordinate = await this.requestTogetherGpsCoordinate();
        if (!coordinate) {
            await this.showSaveHint('위치 권한을 허용해야 동행자와 위치를 공유할 수 있어요.');
            return;
        }
        try {
            const response: any = await wiz.call('zenly_location_share_start', {
                meeting_id: meeting.id,
                duration: this.togetherShareDuration,
                home_enabled: this.togetherPrivateZones.home,
                stay_enabled: this.togetherPrivateZones.stay
            });
            if (!response || response.code !== 200 || !response.data) {
                await this.showSaveHint(this.responseMessage(response && response.data, '위치 공유를 시작하지 못했어요.'));
                return;
            }
            this.togetherLocationSharingActive = true;
            this.togetherShareStartedAt = Date.now();
            this.togetherOwnCoordinate = coordinate;
            if (!this.togetherMapCenterCoordinate) this.togetherMapCenterCoordinate = coordinate;
            this.applyTogetherLocationPayload(response.data);
            this.scheduleTogetherShareExpiry();
            this.startTogetherLocationTracking();
            await this.sendTogetherLocationUpdate(coordinate, true);
            this.togetherSafetyOpen = false;
            await this.service.render();
            await this.showSaveHint(`${this.togetherShareStatusLabel()} · 보호 구역에서는 서버가 위치를 자동으로 흐리게 표시해요.`);
        } catch (e) {
            await this.showSaveHint('위치 공유 서버에 연결하지 못했어요.');
        }
    }

    public togetherShareStatusLabel() {
        if (!this.togetherLocationSharingActive) return '공유 꺼짐';
        if (this.togetherShareDuration === 'trip') return '여행 종료까지 공개 중';
        if (Number(this.togetherShareDuration) === 30) return '30분 공개 중';
        return '1시간 공개 중';
    }

    public async endTogetherLocationShare() {
        let meeting = this.togetherMeetingAppointment;
        if (meeting && meeting.id && meeting.serverBacked && this.isLoggedIn()) {
            try {
                await wiz.call('zenly_location_share_stop', { meeting_id: meeting.id });
            } catch (e) { }
        }
        this.togetherLocationSharingActive = false;
        this.togetherShareStartedAt = 0;
        this.clearTogetherShareTimer();
        this.stopTogetherLocationTracking(false);
        this.togetherSafetyOpen = false;
        await this.service.render();
        await this.showSaveHint('위치 공유를 종료했어요. 동행자에게는 대략적인 위치만 보여요.');
    }

    public async toggleTogetherPrivateZone(key: string) {
        if (['home', 'stay'].indexOf(key) < 0) return;
        let enabled = !this.togetherPrivateZones[key];
        let coordinate: any = null;
        if (enabled) {
            coordinate = await this.requestTogetherGpsCoordinate();
            if (!coordinate) {
                await this.showSaveHint('현재 위치를 확인해야 비공개 구역으로 저장할 수 있어요.');
                return;
            }
        }
        let meeting = this.togetherMeetingAppointment;
        if (meeting && meeting.id && meeting.serverBacked && this.isLoggedIn()) {
            try {
                const response: any = await wiz.call('zenly_location_private_zone', {
                    meeting_id: meeting.id,
                    zone: key,
                    enabled,
                    lat: coordinate ? coordinate.lat : '',
                    lng: coordinate ? coordinate.lng : ''
                });
                if (!response || response.code !== 200) {
                    await this.showSaveHint(this.responseMessage(response && response.data, '비공개 구역을 저장하지 못했어요.'));
                    return;
                }
                if (response.data && response.data.consent) this.applyTogetherLocationConsent(response.data.consent);
            } catch (e) {
                await this.showSaveHint('비공개 구역을 저장하지 못했어요.');
                return;
            }
        }
        this.togetherPrivateZones[key] = enabled;
        await this.service.render();
        await this.showSaveHint(enabled
            ? `현재 위치를 ${key === 'home' ? '집' : '숙소'} 비공개 구역으로 저장했어요.`
            : `${key === 'home' ? '집' : '숙소'} 비공개 구역을 해제했어요.`);
    }

    private requestTogetherGpsCoordinate(): Promise<any> {
        return new Promise((resolve) => {
            if (typeof navigator === 'undefined' || !navigator.geolocation) {
                resolve(null);
                return;
            }
            navigator.geolocation.getCurrentPosition(
                (position: any) => resolve({
                    lat: Number(position.coords.latitude),
                    lng: Number(position.coords.longitude),
                    accuracy: Number(position.coords.accuracy || 0)
                }),
                () => resolve(null),
                { enableHighAccuracy: true, timeout: 10000, maximumAge: 15000 }
            );
        });
    }

    private applyTogetherLocationConsent(consent: any) {
        if (!consent) return;
        this.togetherLocationSharingActive = consent.active === true;
        if (consent.duration) {
            this.togetherShareDuration = consent.duration === 'trip' ? 'trip' : Number(consent.duration);
        }
        this.togetherPrivateZones.home = consent.homeEnabled !== false;
        this.togetherPrivateZones.stay = consent.stayEnabled !== false;
    }

    private applyTogetherLocationPayload(data: any) {
        if (!data) return;
        if (data.consent) this.applyTogetherLocationConsent(data.consent);
        let positions = Array.isArray(data.positions) ? data.positions : [];
        let received = new Set<string>();
        positions.forEach((position: any, index: number) => {
            let userKey = String(position && position.userKey || '');
            if (!userKey) return;
            received.add(userKey);
            let companion = this.togetherCompanions.find((item: any) => String(item && item.id || '') === userKey);
            if (!companion && this.togetherMeetingAppointment
                && String(this.togetherMeetingAppointment.peerUserKey || '') === userKey) {
                companion = this.togetherCompanions.find((item: any) => item && item.name === this.togetherMeetingAppointment.peerName);
            }
            if (!companion) {
                let name = String(position.name || '동행자');
                companion = {
                    id: userKey,
                    name,
                    initial: name.slice(0, 1),
                    color: ['#f97373', '#5b8def', '#8b6edb'][index % 3],
                    matched: true,
                    mutualConsent: true,
                    blocked: false,
                    reported: false
                };
                this.togetherCompanions = [...this.togetherCompanions, companion];
            }
            companion.id = userKey;
            companion.lat = Number(position.lat);
            companion.lng = Number(position.lng);
            companion.precise = position.precise === true;
            companion.privateZone = position.privateZone === true;
            companion.rangeLabel = String(position.rangeLabel || '약 500m 범위 · 보호됨');
            companion.status = `${position.updatedLabel || '위치 확인 중'} · ${companion.rangeLabel}`;
            companion.sharing = true;
            let projected = this.projectTogetherCoordinate(companion.lat, companion.lng);
            companion.x = projected.x;
            companion.y = projected.y;
        });
        this.togetherCompanions.forEach((companion: any) => {
            if (companion && companion.lat !== undefined && !received.has(String(companion.id || ''))) {
                companion.sharing = false;
                companion.precise = false;
                companion.status = '위치 공유 꺼짐';
            }
        });
    }

    private projectTogetherCoordinate(lat: any, lng: any) {
        let latitude = Number(lat);
        let longitude = Number(lng);
        let center = this.togetherMapCenterCoordinate || this.togetherOwnCoordinate || { lat: latitude, lng: longitude };
        if (!isFinite(latitude) || !isFinite(longitude) || !center) return { x: 50, y: 50 };
        let centerLat = Number(center.lat);
        let centerLng = Number(center.lng);
        let lngSpan = 0.06 * Math.max(0.35, Math.cos(centerLat * Math.PI / 180));
        let latSpan = 0.045;
        return {
            x: Math.max(-80, Math.min(180, 50 + ((longitude - centerLng) / lngSpan) * 100)),
            y: Math.max(-80, Math.min(180, 50 - ((latitude - centerLat) / latSpan) * 100))
        };
    }

    private reprojectTogetherLocations() {
        if (this.togetherOwnCoordinate) this.userPosition = this.projectTogetherCoordinate(this.togetherOwnCoordinate.lat, this.togetherOwnCoordinate.lng);
        this.togetherCompanions.forEach((companion: any) => {
            if (!companion || !isFinite(Number(companion.lat)) || !isFinite(Number(companion.lng))) return;
            let point = this.projectTogetherCoordinate(companion.lat, companion.lng);
            companion.x = point.x;
            companion.y = point.y;
        });
    }

    private startTogetherLocationTracking() {
        this.stopTogetherLocationTracking(false);
        if (typeof navigator === 'undefined' || !navigator.geolocation || !this.togetherLocationSharingActive) return;
        if (!this.togetherVisibilityHandler && typeof document !== 'undefined') {
            this.togetherVisibilityHandler = async () => {
                if (document.visibilityState !== 'visible') return;
                await this.refreshTogetherLocations(false);
                if (this.togetherLocationSharingActive) {
                    let coordinate = await this.requestTogetherGpsCoordinate();
                    if (coordinate) await this.sendTogetherLocationUpdate(coordinate, true);
                }
                await this.service.render();
            };
            document.addEventListener('visibilitychange', this.togetherVisibilityHandler);
        }
        this.togetherLocationWatchId = navigator.geolocation.watchPosition(
            async (position: any) => {
                let coordinate = {
                    lat: Number(position.coords.latitude),
                    lng: Number(position.coords.longitude),
                    accuracy: Number(position.coords.accuracy || 0)
                };
                this.togetherOwnCoordinate = coordinate;
                if (!this.togetherMapCenterCoordinate) this.togetherMapCenterCoordinate = coordinate;
                this.userPosition = this.projectTogetherCoordinate(coordinate.lat, coordinate.lng);
                await this.sendTogetherLocationUpdate(coordinate, false);
            },
            async () => {
                this.togetherRealtimeStatus = 'gps-error';
                await this.service.render();
            },
            { enableHighAccuracy: true, timeout: 12000, maximumAge: 5000 }
        );
        this.scheduleTogetherLocationPoll();
    }

    private stopTogetherLocationTracking(stopPolling: boolean = false) {
        if (this.togetherLocationWatchId && typeof navigator !== 'undefined' && navigator.geolocation) {
            try { navigator.geolocation.clearWatch(this.togetherLocationWatchId); } catch (e) { }
        }
        this.togetherLocationWatchId = null;
        this.togetherLocationSending = false;
        if (stopPolling && this.togetherLocationPollTimer && typeof window !== 'undefined') {
            window.clearTimeout(this.togetherLocationPollTimer);
            this.togetherLocationPollTimer = null;
        }
        if (stopPolling && this.togetherVisibilityHandler && typeof document !== 'undefined') {
            document.removeEventListener('visibilitychange', this.togetherVisibilityHandler);
            this.togetherVisibilityHandler = null;
        }
    }

    private async sendTogetherLocationUpdate(coordinate: any, force: boolean = false) {
        let meeting = this.togetherMeetingAppointment;
        if (!meeting || !meeting.id || !this.togetherLocationSharingActive || this.togetherLocationSending) return;
        if (!force && Date.now() - this.togetherLocationLastSentAt < 4000) return;
        this.togetherLocationSending = true;
        try {
            const response: any = await wiz.call('zenly_location_update', {
                meeting_id: meeting.id,
                lat: coordinate.lat,
                lng: coordinate.lng,
                accuracy: coordinate.accuracy || 0
            });
            if (response && response.code === 200 && response.data) {
                this.togetherLocationLastSentAt = Date.now();
                this.applyTogetherLocationPayload(response.data);
                this.togetherRealtimeStatus = 'connected';
                await this.service.render();
            } else if (response && [403, 410].indexOf(Number(response.code)) > -1) {
                this.togetherLocationSharingActive = false;
                this.stopTogetherLocationTracking(false);
            }
        } catch (e) {
            this.togetherRealtimeStatus = 'reconnecting';
        } finally {
            this.togetherLocationSending = false;
        }
    }

    private async refreshTogetherLocations(shouldRender: boolean = true) {
        let meeting = this.togetherMeetingAppointment;
        if (!meeting || !meeting.id || !meeting.serverBacked || !this.isLoggedIn()) return;
        try {
            const response: any = await wiz.call('zenly_location_snapshot', { meeting_id: meeting.id });
            if (response && response.code === 200 && response.data) {
                this.applyTogetherLocationPayload(response.data);
                if (this.togetherLocationSharingActive && !this.togetherLocationWatchId) this.startTogetherLocationTracking();
            }
        } catch (e) { }
        if (shouldRender) await this.service.render();
    }

    private scheduleTogetherLocationPoll() {
        if (this.togetherLocationPollTimer && typeof window !== 'undefined') window.clearTimeout(this.togetherLocationPollTimer);
        this.togetherLocationPollTimer = null;
        if (!this.hasActiveTogetherMeetingChat() || typeof window === 'undefined') return;
        this.togetherLocationPollTimer = window.setTimeout(async () => {
            this.togetherLocationPollTimer = null;
            await this.refreshTogetherLocations(this.activeTab === 'map' && this.mapContentTab === 'zenly');
            if (this.hasActiveTogetherMeetingChat()) this.scheduleTogetherLocationPoll();
        }, 10000);
    }

    public togetherVisibleCompanions() {
        return this.togetherCompanions.filter((companion: any) => companion && !companion.blocked);
    }

    private syncTogetherCompanionsFromTrip() {
        let trip = this.togetherConfirmedTrip();
        let tripCourseId = String(trip && trip.courseId || '');
        let tripPostId = String(trip && trip.id || '');
        let post = this.companionPosts.find((item: any) => {
            if (!item || item.status !== 'matched') return false;
            if (tripPostId && String(item.id || '') === tripPostId) return true;
            return !!tripCourseId && String(item.courseId || '') === tripCourseId;
        }) || null;
        let candidates: any[] = [];

        if (post) {
            let applications = this.companionApplications(post);
            if (post.owned === true) {
                candidates = applications
                    .filter((application: any) => application && application.status === 'accepted')
                    .map((application: any) => ({
                        id: String(application.applicantKey || application.id || ''),
                        name: this.companionApplicationName(application)
                    }));
            } else {
                let currentApplication = this.currentCompanionApplication(post);
                if (currentApplication && currentApplication.status === 'accepted') {
                    candidates = [{
                        id: `host-${String(post.id || '')}`,
                        name: String(post.host || '동행 작성자')
                    }];
                }
            }
        }

        if (!candidates.length && this.hasActiveTogetherMeetingChat()) {
            let peerName = String(this.togetherMeetingAppointment && this.togetherMeetingAppointment.peerName || '').trim();
            if (peerName && peerName !== '동행자') {
                candidates = [{
                    id: `meeting-${String(this.togetherMeetingAppointment.id || peerName)}`,
                    name: peerName
                }];
            }
        }

        let positions = [
            { x: 58, y: 38 },
            { x: 34, y: 64 },
            { x: 72, y: 61 },
            { x: 43, y: 29 },
            { x: 66, y: 48 }
        ];
        let colors = ['#f97373', '#5b8def', '#8b6edb', '#16a085', '#f59e0b'];
        let previous: any = {};
        this.togetherCompanions.forEach((companion: any) => {
            if (companion && companion.id) previous[String(companion.id)] = companion;
        });
        let seen: any = {};
        this.togetherCompanions = candidates.filter((candidate: any) => {
            let id = String(candidate && candidate.id || '');
            if (!id || seen[id]) return false;
            seen[id] = true;
            return true;
        }).map((candidate: any, index: number) => {
            let id = String(candidate.id);
            let saved = previous[id] || {};
            let position = positions[index % positions.length];
            let name = String(candidate.name || '동행자').trim() || '동행자';
            return {
                ...saved,
                id,
                name,
                initial: name.slice(0, 1),
                color: saved.color || colors[index % colors.length],
                status: '같이 여행 중',
                x: Number.isFinite(Number(saved.x)) ? Number(saved.x) : position.x,
                y: Number.isFinite(Number(saved.y)) ? Number(saved.y) : position.y,
                matched: true,
                mutualConsent: true,
                sharing: saved.sharing !== false,
                blocked: saved.blocked === true,
                reported: saved.reported === true
            };
        });
        if (!this.togetherCompanions.some((companion: any) => companion.id === this.selectedTogetherCompanionId)) {
            this.selectedTogetherCompanionId = this.togetherCompanions[0] ? this.togetherCompanions[0].id : '';
        }
    }

    public selectedTogetherCompanion() {
        let visible = this.togetherVisibleCompanions();
        return visible.find((companion: any) => companion.id === this.selectedTogetherCompanionId) || visible[0] || null;
    }

    public isTogetherCompanionSelected(companion: any) {
        return !!companion && companion.id === this.selectedTogetherCompanionId;
    }

    public canShowTogetherPreciseLocation(companion: any) {
        return !!companion
            && companion.precise === true
            && companion.matched === true
            && companion.mutualConsent === true
            && companion.sharing === true
            && !companion.blocked;
    }

    public togetherCompanionPrecisionLabel(companion: any) {
        if (companion && companion.rangeLabel) return companion.rangeLabel;
        if (this.canShowTogetherPreciseLocation(companion)) return '실시간 위치 · 상호 수락';
        if (companion && companion.matched) return '약 500m 범위 · 정확한 위치 잠김';
        return '약 1km 범위 · 수락 전';
    }

    public async selectTogetherCompanion(companion: any, event?: any) {
        if (event && event.stopPropagation) event.stopPropagation();
        if (!companion || companion.blocked) return;
        this.selectedTogetherCompanionId = companion.id;
        this.togetherInfoFocus = 'companions';
        this.selectedZenlySignalId = '';
        await this.service.render();
    }

    public async blockTogetherCompanion(companion: any, event?: any) {
        if (event && event.stopPropagation) event.stopPropagation();
        if (!companion) return;
        let meeting = this.togetherMeetingAppointment;
        let userId = String(companion.id || meeting && meeting.peerUserKey || '');
        if (meeting && meeting.id && userId && this.isLoggedIn()) {
            try {
                const response: any = await wiz.call('zenly_together_block', {
                    meeting_id: meeting.id,
                    user_id: userId
                });
                if (!response || response.code !== 200) {
                    await this.showSaveHint(this.responseMessage(response && response.data, '동행자를 차단하지 못했어요.'));
                    return;
                }
            } catch (e) {
                await this.showSaveHint('동행자를 차단하지 못했어요.');
                return;
            }
        }
        companion.blocked = true;
        let next = this.togetherVisibleCompanions()[0];
        this.selectedTogetherCompanionId = next ? next.id : '';
        await this.service.render();
        await this.showSaveHint(`${companion.name}님을 차단했어요. 위치와 신호가 더 이상 보이지 않아요.`);
    }

    public async reportTogetherCompanion(companion: any, event?: any) {
        if (event && event.stopPropagation) event.stopPropagation();
        if (!companion) return;
        let meeting = this.togetherMeetingAppointment;
        let userId = String(companion.id || meeting && meeting.peerUserKey || '');
        if (meeting && meeting.id && userId && this.isLoggedIn()) {
            try {
                const response: any = await wiz.call('zenly_together_report', {
                    meeting_id: meeting.id,
                    user_id: userId,
                    reason: '같이 지도 안전 신고'
                });
                if (!response || response.code !== 200) {
                    await this.showSaveHint(this.responseMessage(response && response.data, '신고를 접수하지 못했어요.'));
                    return;
                }
            } catch (e) {
                await this.showSaveHint('신고를 접수하지 못했어요.');
                return;
            }
        }
        companion.reported = true;
        await this.service.render();
        await this.showSaveHint(`${companion.name}님에 대한 신고를 접수했어요.`);
    }

    public async openTogetherMeetingChat(event?: any) {
        if (event && event.stopPropagation) event.stopPropagation();
        if (!this.hasActiveTogetherMeetingChat()) {
            await this.showSaveHint('진행 중인 약속이 생기면 약속 채팅이 열려요.');
            return;
        }
        this.togetherInfoFocus = 'meeting';
        this.togetherSafetyOpen = false;
        this.zenlySignalComposerOpen = false;
        this.togetherMeetingChatOpen = true;
        this.togetherMeetingUnread = 0;
        await this.refreshTogetherMeetingMessages(false);
        await this.markTogetherMeetingMessagesRead(false);
        await this.service.render();
        this.scrollTogetherMeetingChat();
    }

    public async closeTogetherMeetingChat(event?: any) {
        if (event && event.stopPropagation) event.stopPropagation();
        this.togetherMeetingChatOpen = false;
        await this.service.render();
    }

    public hasActiveTogetherMeetingChat() {
        let meeting = this.togetherMeetingAppointment;
        if (!meeting || meeting.active === false || meeting.status !== 'active') return false;
        let endsAt = Number(meeting.endsAtEpoch || 0);
        return !endsAt || endsAt > Date.now();
    }

    public togetherMeetingRemainingLabel() {
        if (!this.hasActiveTogetherMeetingChat()) return '종료됨';
        let endsAt = Number(this.togetherMeetingAppointment && this.togetherMeetingAppointment.endsAtEpoch || 0);
        if (!endsAt) return '약속 진행 중';
        let minutes = Math.max(1, Math.ceil((endsAt - Date.now()) / 60000));
        if (minutes < 60) return `${minutes}분 남음`;
        let hours = Math.floor(minutes / 60);
        let rest = minutes % 60;
        return rest ? `${hours}시간 ${rest}분 남음` : `${hours}시간 남음`;
    }

    private togetherMeetingCompanionPost() {
        let trip = this.togetherConfirmedTrip();
        if (!trip) return null;
        let tripPostId = String(trip.id || '');
        let tripCourseId = String(trip.courseId || '');
        return this.companionPosts.find((post: any) => {
            if (!post || post.status !== 'matched' || !this.isConfirmedCompanionPost(post)) return false;
            if (tripPostId && String(post.id || '') === tripPostId) return true;
            return !!tripCourseId && String(post.courseId || '') === tripCourseId;
        }) || null;
    }

    private togetherTripEndsAtIso() {
        let trip: any = this.togetherConfirmedTrip() || {};
        let explicit = [trip.endsAt, trip.endAt, trip.endDateTime, trip.tripEndsAt].find((value: any) => !!value);
        if (explicit) {
            let parsed = new Date(explicit);
            if (!isNaN(parsed.getTime()) && parsed.getTime() > Date.now() + (30 * 60 * 1000)) {
                return parsed.toISOString();
            }
        }
        let durationMinutes = Math.max(180, Number(this.executionTotalMinutes || trip.durationMinutes || 0));
        let date = String(trip.date || '').trim();
        let time = String(trip.time || '').trim();
        if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
            let normalizedTime = /^\d{1,2}:\d{2}$/.test(time) ? time.padStart(5, '0') : '10:00';
            let start = new Date(`${date}T${normalizedTime}:00`);
            if (!isNaN(start.getTime())) {
                let endsAt = new Date(start.getTime() + durationMinutes * 60 * 1000);
                if (endsAt.getTime() > Date.now() + (30 * 60 * 1000)) return endsAt.toISOString();
            }
        }
        return new Date(Date.now() + durationMinutes * 60 * 1000).toISOString();
    }

    private async activateTogetherTripMeeting() {
        let trip = this.togetherConfirmedTrip();
        if (!trip) return;
        this.clearTogetherMeetingState(false);
        let locationLabel = String(trip.meetingPoint || this.togetherMeetingPoint.name || '준비방에서 정한 약속 장소');
        this.togetherMeetingPoint = {
            ...this.togetherMeetingPoint,
            name: locationLabel,
            time: String(trip.time || '약속 진행 중'),
            note: '확정된 동행자와 실시간으로 대화할 수 있어요.'
        };
        let post = this.togetherMeetingCompanionPost();
        if (!post || !this.isLoggedIn()) return;
        try {
            const response: any = await wiz.call('zenly_trip_meeting_start', {
                post_id: post.id,
                duration_minutes: Math.max(180, Number(this.executionTotalMinutes || 0)),
                ends_at: this.togetherTripEndsAtIso()
            });
            if (response && response.code === 200 && response.data && response.data.meeting) {
                this.applyTogetherMeetingPayload(response.data.meeting, response.data.messages || []);
            }
        } catch (e) { }
    }

    private applyTogetherMeetingPayload(meeting: any, messages: any[] = []) {
        if (!meeting || meeting.status !== 'active') {
            if (this.togetherMeetingAppointment && this.togetherMeetingAppointment.serverBacked) {
                this.clearTogetherMeetingState(true);
            }
            return;
        }
        this.clearTogetherMeetingTimers();
        this.togetherMeetingAppointment = {
            ...meeting,
            active: true,
            serverBacked: true,
            endsAtEpoch: Number(meeting.endsAtEpoch || 0)
        };
        this.togetherMeetingChatMessages = Array.isArray(messages) ? messages.map((message: any, index: number) => ({
            id: message.id || `meeting-message-${index}`,
            role: ['me', 'other', 'system'].indexOf(message.role) > -1 ? message.role : 'other',
            senderName: message.senderName || (message.role === 'me' ? '나' : '동행자'),
            text: String(message.text || ''),
            timeLabel: message.timeLabel || '방금',
            read: !!message.read,
            readAt: String(message.readAt || '')
        })) : [];
        this.togetherMeetingPoint = {
            ...this.togetherMeetingPoint,
            name: meeting.locationLabel || '서로 정한 약속 장소',
            time: '약속 진행 중',
            note: `${meeting.peerName || '동행자'}님과 · ${this.togetherMeetingRemainingLabel()}`
        };
        this.scheduleTogetherMeetingExpiry();
        this.scheduleTogetherMeetingPoll();
        this.scheduleTogetherLocationPoll();
    }

    public async loadTogetherMeeting(showLoading: boolean = false, shouldRender: boolean = true) {
        if (!this.isLoggedIn()) return;
        if (showLoading) {
            this.togetherMeetingChatLoading = true;
            if (shouldRender) await this.service.render();
        }
        try {
            const response: any = await wiz.call('zenly_meeting_active', {});
            if (response && response.code === 200 && response.data) {
                if (response.data.meeting) {
                    this.applyTogetherMeetingPayload(response.data.meeting, response.data.messages || []);
                } else if (this.togetherMeetingAppointment && this.togetherMeetingAppointment.serverBacked) {
                    this.clearTogetherMeetingState(true);
                }
            }
        } catch (e) { }
        this.togetherMeetingChatLoading = false;
        if (shouldRender) await this.service.render();
    }

    public async refreshTogetherMeetingMessages(shouldRender: boolean = true) {
        let meeting = this.togetherMeetingAppointment;
        if (!meeting || !meeting.serverBacked || !meeting.id || !this.isLoggedIn()) return;
        try {
            const response: any = await wiz.call('zenly_meeting_messages', { meeting_id: meeting.id });
            if (response && response.code === 200 && response.data && response.data.meeting) {
                this.applyTogetherMeetingPayload(response.data.meeting, response.data.messages || []);
                if (this.togetherMeetingChatOpen) await this.markTogetherMeetingMessagesRead(false);
            } else if (response && [404, 410].indexOf(Number(response.code)) > -1) {
                this.clearTogetherMeetingState(true);
            }
        } catch (e) { }
        if (shouldRender) {
            await this.service.render();
            this.scrollTogetherMeetingChat();
        }
    }

    public async sendTogetherMeetingMessage(event?: any) {
        if (event && event.preventDefault) event.preventDefault();
        if (event && event.stopPropagation) event.stopPropagation();
        let text = String(this.togetherMeetingChatDraft || '').trim().slice(0, 80);
        if (!text || !this.hasActiveTogetherMeetingChat() || this.togetherMeetingChatSending) return;
        let meeting = this.togetherMeetingAppointment;
        this.togetherMeetingChatSending = true;
        this.togetherMeetingSendFailed = false;
        if (meeting.serverBacked) {
            try {
                const response: any = await wiz.call('zenly_meeting_message_send', {
                    meeting_id: meeting.id,
                    message: text
                });
                if (response && response.code === 200 && response.data && response.data.meeting) {
                    this.togetherMeetingChatDraft = '';
                    this.applyTogetherMeetingPayload(response.data.meeting, response.data.messages || []);
                } else if (response && [404, 410].indexOf(Number(response.code)) > -1) {
                    this.clearTogetherMeetingState(true);
                    await this.showSaveHint('약속이 끝나 채팅도 닫혔어요.');
                } else {
                    this.togetherMeetingSendFailed = true;
                    await this.showSaveHint(this.responseMessage(response && response.data, '메시지를 보내지 못했어요.'));
                }
            } catch (e) {
                this.togetherMeetingSendFailed = true;
                await this.showSaveHint('메시지를 보내지 못했어요.');
            }
        } else {
            this.togetherMeetingChatMessages = [...this.togetherMeetingChatMessages, {
                id: `local-meeting-${Date.now()}`,
                role: 'me',
                senderName: '나',
                text,
                timeLabel: this.currentChatTimeLabel()
            }];
            this.togetherMeetingChatDraft = '';
        }
        this.togetherMeetingChatSending = false;
        await this.service.render();
        this.scrollTogetherMeetingChat();
    }

    public async onTogetherMeetingTyping() {
        let meeting = this.togetherMeetingAppointment;
        if (!meeting || !meeting.id || !meeting.serverBacked || !this.isLoggedIn()) return;
        if (this.togetherMeetingTypingTimer && typeof window !== 'undefined') window.clearTimeout(this.togetherMeetingTypingTimer);
        if (!this.togetherMeetingTypingActive) {
            this.togetherMeetingTypingActive = true;
            try {
                await wiz.call('zenly_meeting_typing', { meeting_id: meeting.id, typing: true });
            } catch (e) { }
        }
        if (typeof window !== 'undefined') {
            this.togetherMeetingTypingTimer = window.setTimeout(async () => {
                this.togetherMeetingTypingTimer = null;
                this.togetherMeetingTypingActive = false;
                try { await wiz.call('zenly_meeting_typing', { meeting_id: meeting.id, typing: false }); } catch (e) { }
            }, 1400);
        }
    }

    private async markTogetherMeetingMessagesRead(shouldRender: boolean = true) {
        let meeting = this.togetherMeetingAppointment;
        if (!meeting || !meeting.id || !meeting.serverBacked || !this.isLoggedIn()) return;
        if (!this.togetherMeetingChatMessages.some((message: any) => message && message.role === 'other')) return;
        try {
            await wiz.call('zenly_meeting_messages_read', { meeting_id: meeting.id });
            this.togetherMeetingUnread = 0;
        } catch (e) { }
        if (shouldRender) await this.service.render();
    }

    public togetherRealtimeStatusLabel() {
        if (this.togetherRealtimeStatus === 'connected') return '연결됨';
        if (this.togetherRealtimeStatus === 'reconnecting') return '재연결 중';
        if (this.togetherRealtimeStatus === 'offline') return '연결 끊김';
        if (this.togetherRealtimeStatus === 'gps-error') return 'GPS 확인 필요';
        return '연결 중';
    }

    public async endTogetherMeeting(event?: any) {
        if (event && event.stopPropagation) event.stopPropagation();
        if (!this.togetherMeetingAppointment) return;
        await this.closeTogetherMeeting(true);
        await this.service.render();
        await this.showSaveHint('약속을 종료해 약속 채팅도 함께 사라졌어요.');
    }

    private async closeTogetherMeeting(notifyServer: boolean = true) {
        let meeting = this.togetherMeetingAppointment;
        if (notifyServer && meeting && meeting.serverBacked && meeting.id && this.isLoggedIn()) {
            try {
                await wiz.call('zenly_meeting_end', { meeting_id: meeting.id });
            } catch (e) { }
        }
        this.clearTogetherMeetingState(true);
    }

    private clearTogetherMeetingState(markEnded: boolean = true) {
        this.clearTogetherMeetingTimers();
        this.togetherMeetingAppointment = null;
        this.togetherMeetingChatMessages = [];
        this.togetherMeetingChatDraft = '';
        this.togetherMeetingChatOpen = false;
        this.togetherMeetingChatLoading = false;
        this.togetherMeetingChatSending = false;
        this.togetherMeetingSendFailed = false;
        this.togetherMeetingPeerTyping = false;
        this.togetherMeetingUnread = 0;
        this.stopTogetherLocationTracking();
        if (markEnded) {
            this.togetherMeetingPoint = {
                ...this.togetherMeetingPoint,
                time: '종료됨',
                note: '약속 채팅이 자동으로 닫혔어요.'
            };
        }
    }

    private scheduleTogetherMeetingExpiry() {
        if (this.togetherMeetingExpiryTimer && typeof window !== 'undefined') {
            window.clearTimeout(this.togetherMeetingExpiryTimer);
        }
        this.togetherMeetingExpiryTimer = null;
        if (!this.hasActiveTogetherMeetingChat() || typeof window === 'undefined') return;
        let endsAt = Number(this.togetherMeetingAppointment.endsAtEpoch || 0);
        if (!endsAt) return;
        let delay = Math.max(0, endsAt - Date.now());
        this.togetherMeetingExpiryTimer = window.setTimeout(async () => {
            this.togetherMeetingExpiryTimer = null;
            this.clearTogetherMeetingState(true);
            try {
                await this.service.render();
                await this.showSaveHint('약속이 끝나 약속 채팅이 자동으로 사라졌어요.');
            } catch (e) { }
        }, delay);
    }

    private scheduleTogetherMeetingPoll() {
        if (this.togetherMeetingPollTimer && typeof window !== 'undefined') {
            window.clearTimeout(this.togetherMeetingPollTimer);
        }
        this.togetherMeetingPollTimer = null;
        if (!this.hasActiveTogetherMeetingChat()
            || !this.togetherMeetingAppointment.serverBacked
            || typeof window === 'undefined') return;
        this.togetherMeetingPollTimer = window.setTimeout(async () => {
            this.togetherMeetingPollTimer = null;
            await this.refreshTogetherMeetingMessages(this.togetherMeetingChatOpen);
            if (this.hasActiveTogetherMeetingChat()) this.scheduleTogetherMeetingPoll();
        }, 7000);
    }

    private clearTogetherMeetingTimers() {
        if (typeof window !== 'undefined') {
            if (this.togetherMeetingExpiryTimer) window.clearTimeout(this.togetherMeetingExpiryTimer);
            if (this.togetherMeetingPollTimer) window.clearTimeout(this.togetherMeetingPollTimer);
            if (this.togetherMeetingTypingTimer) window.clearTimeout(this.togetherMeetingTypingTimer);
            if (this.togetherMeetingPeerTypingTimer) window.clearTimeout(this.togetherMeetingPeerTypingTimer);
        }
        this.togetherMeetingExpiryTimer = null;
        this.togetherMeetingPollTimer = null;
        this.togetherMeetingTypingTimer = null;
        this.togetherMeetingPeerTypingTimer = null;
        this.togetherMeetingTypingActive = false;
    }

    private scrollTogetherMeetingChat() {
        if (typeof window === 'undefined' || typeof document === 'undefined') return;
        window.setTimeout(() => {
            let list: any = document.querySelector('.access-shell .together-meeting-chat-list');
            if (list) list.scrollTop = list.scrollHeight;
        }, 0);
    }

    public togetherSignalRadius(signal: any) {
        let fuzzy = Number(signal && signal.fuzzyRadius ? signal.fuzzyRadius : 500);
        if (!isFinite(fuzzy) || fuzzy <= 0) fuzzy = 500;
        return Math.max(58, Math.min(108, Math.round(fuzzy / 7)));
    }

    public async selectTogetherSignal(signal: any, event?: any) {
        if (event && event.stopPropagation) event.stopPropagation();
        this.togetherMeetingChatOpen = false;
        this.togetherInfoFocus = 'signals';
        await this.selectZenlySignal(signal, event);
    }

    public async openTogetherSignalComposer(event?: any) {
        if (event && event.stopPropagation) event.stopPropagation();
        if (!this.isTogetherMapActive()) {
            await this.showSaveHint('여행을 시작한 뒤 주변 합류 신호를 보낼 수 있어요.');
            return;
        }
        this.togetherInfoFocus = 'signals';
        this.zenlyLayerTab = 'signals';
        this.togetherSafetyOpen = false;
        this.togetherMeetingChatOpen = false;
        if (!this.zenlySignalComposerOpen) await this.toggleZenlySignalComposer();
    }

    public async useTogetherSignalExample(message: string) {
        this.zenlySignalDraft.message = String(message || '').slice(0, 50);
        await this.service.render();
    }

    public async endTogetherTrip() {
        this.togetherLocationSharingActive = false;
        this.togetherShareStartedAt = 0;
        this.clearTogetherShareTimer();
        this.togetherTripStarted = false;
        this.togetherTripEnded = true;
        this.togetherSafetyOpen = false;
        this.zenlySignalComposerOpen = false;
        this.selectedZenlySignalId = '';
        this.zenlySignals = [];
        if (this.executionCourse) {
            await this.endExecutionCourse();
        } else {
            await this.closeTogetherMeeting(true);
        }
        await this.service.render();
        await this.showSaveHint('여행을 종료했어요. 위치 공유와 약속 채팅이 모두 자동으로 닫혔어요.');
    }

    private scheduleTogetherShareExpiry() {
        this.clearTogetherShareTimer();
        if (!this.togetherLocationSharingActive || this.togetherShareDuration === 'trip' || typeof window === 'undefined') return;
        let minutes = Number(this.togetherShareDuration);
        if (!isFinite(minutes) || minutes <= 0) return;
        this.togetherShareTimer = window.setTimeout(async () => {
            this.togetherShareTimer = null;
            try {
                await this.endTogetherLocationShare();
                await this.showSaveHint('선택한 공개 시간이 끝나 위치 공유가 자동으로 해제됐어요.');
            } catch (e) { }
        }, minutes * 60 * 1000);
    }

    private clearTogetherShareTimer() {
        if (this.togetherShareTimer && typeof window !== 'undefined') {
            window.clearTimeout(this.togetherShareTimer);
        }
        this.togetherShareTimer = null;
    }

    public activeDirectChat() {
        return this.directChats.find((chat: any) => chat.id === this.activeDirectChatId) || this.directChats[0] || { messages: [] };
    }

    public filteredDirectChats() {
        return this.directChats
            .filter((chat: any) => this.directChatMatchesFilter(chat, this.directChatFilter))
            .sort((a: any, b: any) => this.directChatSortTime(b) - this.directChatSortTime(a));
    }

    public directChatListTime(chat: any) {
        let raw = String(chat && chat.lastMessageAt || '').trim();
        if (!raw && chat && Array.isArray(chat.messages) && chat.messages.length > 0) {
            raw = String(chat.messages[chat.messages.length - 1].createdAt || '').trim();
        }
        if (!raw) return String(chat && chat.time || '방금');
        let sentAt = this.directChatDate(raw);
        if (!isFinite(sentAt.getTime())) return String(chat && chat.time || '방금');
        let now = new Date();
        let today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
        let sentDay = new Date(sentAt.getFullYear(), sentAt.getMonth(), sentAt.getDate()).getTime();
        let dayGap = Math.round((today - sentDay) / 86400000);
        if (dayGap <= 0) {
            return `${String(sentAt.getHours()).padStart(2, '0')}:${String(sentAt.getMinutes()).padStart(2, '0')}`;
        }
        if (dayGap === 1) return '어제';
        if (sentAt.getFullYear() === now.getFullYear()) return `${sentAt.getMonth() + 1}월 ${sentAt.getDate()}일`;
        return `${sentAt.getFullYear()}.${String(sentAt.getMonth() + 1).padStart(2, '0')}.${String(sentAt.getDate()).padStart(2, '0')}`;
    }

    private directChatSortTime(chat: any) {
        let raw = String(chat && chat.lastMessageAt || '').trim();
        if (!raw && chat && Array.isArray(chat.messages) && chat.messages.length > 0) {
            raw = String(chat.messages[chat.messages.length - 1].createdAt || '').trim();
        }
        let timestamp = raw ? this.directChatDate(raw).getTime() : 0;
        return isFinite(timestamp) ? timestamp : 0;
    }

    private directChatDate(raw: string) {
        let value = String(raw || '').trim();
        if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(value) && !/(Z|[+-]\d{2}:?\d{2})$/i.test(value)) {
            value += 'Z';
        }
        return new Date(value);
    }

    public directChatFilterCount(key: string) {
        return this.directChats.filter((chat: any) => this.directChatMatchesFilter(chat, key)).length;
    }

    public async setDirectChatFilter(key: string) {
        if (!this.directChatFilters.some((filter: any) => filter.key === key)) return;
        this.directChatFilter = key;
        this.directRoomOpen = false;
        this.directActionMenuOpen = false;
        this.directLedgerOpen = false;
        await this.service.render();
    }

    public async selectDirectChat(chat: any) {
        if (!chat) return;
        await this.loadDirectChatRooms(false);
        chat = this.directChats.find((item: any) => item && item.id === chat.id) || chat;
        this.activeDirectChatId = chat.id;
        chat.unread = 0;
        this.directRoomOpen = true;
        this.directActionMenuOpen = false;
        this.directLedgerOpen = false;
        this.directLedgerTab = 'history';
        await this.service.render();
        if (!this.directChatSocket || !this.directChatPollTimer) this.startDirectChatRealtime();
        this.startDirectChatRoomRefresh();
        await this.markDirectMessagesRead(chat, true);
        this.resetChatContentScroll();
    }

    public async closeDirectChatRoom() {
        if (this.directLedgerOpen) {
            this.directLedgerOpen = false;
            this.directLedgerTab = 'history';
            await this.service.render();
            return;
        }
        this.stopDirectChatRoomRefresh();
        this.directRoomOpen = false;
        this.directActionMenuOpen = false;
        this.directDraft = '';
        await this.service.render();
    }

    public async toggleDirectActionMenu() {
        this.directActionMenuOpen = !this.directActionMenuOpen;
        await this.service.render();
    }

    public async toggleDirectLedger() {
        this.directLedgerOpen = !this.directLedgerOpen;
        this.directLedgerTab = 'history';
        this.directLedgerExpenseFormOpen = false;
        this.directActionMenuOpen = false;
        await this.service.render();
    }

    public async setDirectLedgerTab(tab: string) {
        if (tab !== 'history' && tab !== 'settlement') return;
        this.directLedgerTab = tab;
        await this.service.render();
    }

    public directLedgerTotal() {
        return this.directLedgerExpenses.reduce((sum: number, expense: any) => sum + Number(expense.amount || 0), 0);
    }

    public directLedgerPaidBy(payer: string) {
        return this.directLedgerExpenses
            .filter((expense: any) => expense.payer === payer)
            .reduce((sum: number, expense: any) => sum + Number(expense.amount || 0), 0);
    }

    public directLedgerShare() {
        return Math.round(this.directLedgerTotal() / 2);
    }

    public directLedgerBalance() {
        return Math.abs(this.directLedgerPaidBy('me') - this.directLedgerShare());
    }

    public formatDirectLedgerAmount(amount: number) {
        return Math.max(0, Number(amount || 0)).toLocaleString('ko-KR');
    }

    public async openDirectLedgerExpenseForm() {
        this.directLedgerExpenseDraft = { title: '', category: '식사', amount: null, payer: 'me' };
        this.directLedgerReceiptPreview = '';
        this.directLedgerReceiptStatus = '';
        this.directLedgerReceiptError = '';
        this.directLedgerExpenseFormOpen = true;
        await this.service.render();
    }

    public async closeDirectLedgerExpenseForm() {
        if (this.directLedgerReceiptScanning) return;
        this.directLedgerExpenseFormOpen = false;
        await this.service.render();
    }

    public async handleDirectLedgerReceiptCapture(event: any) {
        let input = event && event.target ? event.target : null;
        let file = input && input.files && input.files[0] ? input.files[0] : null;
        if (!file || this.directLedgerReceiptScanning) return;
        if (!/^image\/(jpe?g|png|webp)$/i.test(String(file.type || ''))) {
            this.directLedgerReceiptError = 'JPG, PNG, WEBP 영수증 이미지만 사용할 수 있어요.';
            if (input) input.value = '';
            await this.service.render();
            return;
        }
        if (Number(file.size || 0) > 12 * 1024 * 1024) {
            this.directLedgerReceiptError = '영수증 사진은 12MB 이하로 촬영해주세요.';
            if (input) input.value = '';
            await this.service.render();
            return;
        }

        this.directLedgerReceiptScanning = true;
        this.directLedgerReceiptStatus = '영수증에서 결제 정보를 읽고 있어요.';
        this.directLedgerReceiptError = '';
        await this.service.render();
        try {
            let image = await this.compressDirectLedgerReceipt(file);
            this.directLedgerReceiptPreview = image;
            await this.service.render();
            let response: any = await wiz.call('receipt_scan', { image });
            let receipt = response && response.data ? response.data.receipt : null;
            if (!response || response.code !== 200 || !receipt || !Number(receipt.amount || 0)) {
                throw new Error(String(response && response.data && response.data.message || '영수증 정보를 인식하지 못했어요.'));
            }
            let category = String(receipt.category || '기타');
            this.directLedgerExpenseDraft.title = String(receipt.merchant || receipt.item || '영수증 결제').trim();
            this.directLedgerExpenseDraft.amount = Math.round(Number(receipt.amount || 0));
            this.directLedgerExpenseDraft.category = this.directLedgerExpenseCategories.indexOf(category) >= 0 ? category : '기타';
            this.directLedgerReceiptStatus = `자동 입력 완료 · ${this.formatDirectLedgerAmount(receipt.amount)}원`;
        } catch (error) {
            this.directLedgerReceiptError = String(error && error.message || '영수증을 읽지 못했어요. 직접 입력해주세요.');
            this.directLedgerReceiptStatus = '';
        } finally {
            this.directLedgerReceiptScanning = false;
            if (input) input.value = '';
            await this.service.render();
        }
    }

    private compressDirectLedgerReceipt(file: any): Promise<string> {
        return new Promise((resolve, reject) => {
            if (typeof FileReader === 'undefined' || typeof Image === 'undefined') {
                reject(new Error('이 기기에서는 영수증 촬영을 사용할 수 없어요.'));
                return;
            }
            let reader = new FileReader();
            reader.onerror = () => reject(new Error('영수증 사진을 불러오지 못했어요.'));
            reader.onload = () => {
                let image = new Image();
                image.onerror = () => reject(new Error('영수증 사진 형식을 확인해주세요.'));
                image.onload = () => {
                    let maxSide = 1600;
                    let scale = Math.min(1, maxSide / Math.max(image.width || 1, image.height || 1));
                    let canvas = document.createElement('canvas');
                    canvas.width = Math.max(1, Math.round(image.width * scale));
                    canvas.height = Math.max(1, Math.round(image.height * scale));
                    let context = canvas.getContext('2d');
                    if (!context) {
                        reject(new Error('영수증 사진을 처리하지 못했어요.'));
                        return;
                    }
                    context.fillStyle = '#ffffff';
                    context.fillRect(0, 0, canvas.width, canvas.height);
                    context.drawImage(image, 0, 0, canvas.width, canvas.height);
                    resolve(canvas.toDataURL('image/jpeg', 0.82));
                };
                image.src = String(reader.result || '');
            };
            reader.readAsDataURL(file);
        });
    }

    public canAddDirectLedgerExpense() {
        let title = String(this.directLedgerExpenseDraft && this.directLedgerExpenseDraft.title || '').trim();
        let amount = Number(this.directLedgerExpenseDraft && this.directLedgerExpenseDraft.amount || 0);
        return title.length > 0 && isFinite(amount) && amount > 0;
    }

    public async addDirectLedgerExpense() {
        if (!this.canAddDirectLedgerExpense()) return;
        let draft = this.directLedgerExpenseDraft;
        let presentation = this.directLedgerCategoryPresentation(draft.category);
        this.directLedgerExpenses = [{
            id: `expense-${Date.now()}`,
            icon: presentation.icon,
            category: String(draft.category || '기타'),
            title: String(draft.title || '').trim(),
            payer: draft.payer === 'other' ? 'other' : 'me',
            amount: Math.round(Number(draft.amount || 0)),
            tone: presentation.tone
        }, ...this.directLedgerExpenses];
        this.directLedgerExpenseFormOpen = false;
        await this.service.render();
    }

    private directLedgerCategoryPresentation(category: string) {
        let presentations: any = {
            '식사': { icon: 'fa-utensils', tone: 'meal' },
            '카페': { icon: 'fa-mug-hot', tone: 'cafe' },
            '숙소': { icon: 'fa-bed', tone: 'stay' },
            '교통': { icon: 'fa-taxi', tone: 'transport' },
            '관광': { icon: 'fa-camera', tone: 'activity' },
            '기타': { icon: 'fa-receipt', tone: 'etc' }
        };
        return presentations[category] || presentations['기타'];
    }

    public directLedgerSettlementCopy(chat: any) {
        let balance = this.directLedgerBalance();
        if (balance === 0) return '서로 주고받을 금액 없이 정산이 완료됐어요.';
        if (this.directLedgerPaidBy('me') < this.directLedgerShare()) {
            return `내가 ${chat && chat.name || '상대방'}님에게 ${this.formatDirectLedgerAmount(balance)}원 보내기`;
        }
        return `${chat && chat.name || '상대방'}님이 나에게 ${this.formatDirectLedgerAmount(balance)}원 보내기`;
    }

    public directLedgerSettlementAction() {
        if (this.directLedgerBalance() === 0) return 'complete';
        return this.directLedgerPaidBy('me') < this.directLedgerShare() ? 'send' : 'request';
    }

    public directLedgerSettlementShareLabel() {
        let action = this.directLedgerSettlementAction();
        if (action === 'send') return '채팅으로 보내기';
        if (action === 'request') return '채팅으로 요청하기';
        return '정산 완료 공유';
    }

    public isDirectSettlementMessage(message: any) {
        return String(message && message.text || '').trim().startsWith('[여행 가계부 정산]');
    }

    public directSettlementMessageTotal(message: any) {
        let detail = this.directSettlementMessageDetail(message);
        return detail.total || '0원';
    }

    public directSettlementMessageShare(message: any) {
        let detail = this.directSettlementMessageDetail(message);
        return detail.share || '0원';
    }

    public directSettlementMessageRequest(message: any) {
        let detail = this.directSettlementMessageDetail(message);
        let action = this.directSettlementMessageAction(message);
        if (action === 'complete') return detail.request || '주고받을 금액 없이 정산이 완료됐어요.';
        let amountMatch = String(detail.request || '').match(/([\d,]+원)/);
        let amount = amountMatch ? amountMatch[1] : '';
        let chat = this.activeDirectChat();
        let target = `${chat && chat.name || '상대방'}님에게`;
        return `${target} ${amount ? amount + ' ' : ''}${action === 'send' ? '보내기' : '요청하기'}`;
    }

    public directSettlementMessageAction(message: any) {
        let action = this.directSettlementMessageDetail(message).action;
        if (message && message.role !== 'me') {
            if (action === 'send') return 'request';
            if (action === 'request') return 'send';
        }
        return action;
    }

    public directSettlementMessageActionLabel(message: any) {
        let action = this.directSettlementMessageAction(message);
        if (action === 'send') return '보내기';
        if (action === 'request') return '요청하기';
        return '정산 완료';
    }

    public directSettlementMessageActionGuide(message: any) {
        let action = this.directSettlementMessageAction(message);
        if (action === 'send') return '내가 보내야 할 금액이에요';
        if (action === 'request') return '내가 받아야 할 금액이에요';
        return '추가로 주고받을 금액이 없어요';
    }

    private directSettlementMessageDetail(message: any) {
        let lines = String(message && message.text || '')
            .split('\n')
            .map((line: string) => line.trim())
            .filter((line: string) => !!line);
        let amountLine = lines[1] || '';
        let totalMatch = amountLine.match(/총 지출\s+([\d,]+원)/);
        let shareMatch = amountLine.match(/1인\s+([\d,]+원)/);
        let actionLine = lines.find((line: string) => line.indexOf('[정산 액션]') === 0) || '';
        let request = lines.slice(2).filter((line: string) => line.indexOf('[정산 액션]') !== 0).join(' ');
        let action = actionLine.indexOf('send') >= 0
            ? 'send'
            : (actionLine.indexOf('request') >= 0
                ? 'request'
                : (request.indexOf('내가 ') === 0
                    ? 'send'
                    : (request.indexOf('님이 나에게') >= 0 ? 'request' : 'complete')));
        return {
            total: totalMatch ? totalMatch[1] : '',
            share: shareMatch ? shareMatch[1] : '',
            request,
            action
        };
    }

    public async shareDirectLedgerSettlement() {
        let chat = this.activeDirectChat();
        if (!chat || !chat.id || this.directMessageSending) return;
        this.directDraft = [
            '[여행 가계부 정산]',
            `총 지출 ${this.formatDirectLedgerAmount(this.directLedgerTotal())}원 · 1인 ${this.formatDirectLedgerAmount(this.directLedgerShare())}원`,
            this.directLedgerSettlementCopy(chat),
            `[정산 액션] ${this.directLedgerSettlementAction()}`
        ].join('\n');
        let sent = await this.sendDirectMessage();
        if (!sent) return;
        this.directLedgerOpen = false;
        this.directLedgerExpenseFormOpen = false;
        await this.service.render();
        this.scrollDirectMessages();
    }

    public directMuteActionLabel(chat: any) {
        return chat && chat.muted ? '알람 켜기' : '알람 끄기';
    }

    public directBlockActionLabel(chat: any) {
        return chat && chat.blocked ? '차단 해제' : '상대방 차단';
    }

    public async handleDirectChatAction(action: string) {
        let chat = this.activeDirectChat();
        if (!chat || !chat.id) return;

        this.directActionMenuOpen = false;

        if (action === 'block') {
            chat.blocked = !chat.blocked;
            await this.showSaveHint(chat.blocked ? `${chat.name}님을 차단했어요.` : `${chat.name}님 차단을 해제했어요.`);
            return;
        }

        if (action === 'report') {
            await this.showSaveHint(`${chat.name}님 신고 접수를 준비했어요.`);
            return;
        }

        if (action === 'fraud') {
            await this.showSaveHint('사기 이력이 조회되지 않았어요.');
            return;
        }

        if (action === 'mute') {
            chat.muted = !chat.muted;
            await this.showSaveHint(chat.muted ? '채팅방 알림을 껐어요.' : '채팅방 알림을 켰어요.');
            return;
        }

        if (action === 'leave') {
            let name = chat.name || '1:1';
            this.directChats = this.directChats.filter((item: any) => item.id !== chat.id);
            this.activeDirectChatId = this.directChats.length > 0 ? this.directChats[0].id : '';
            this.directRoomOpen = false;
            this.directLedgerOpen = false;
            this.directDraft = '';
            await this.showSaveHint(`${name} 채팅방에서 나왔어요.`);
        }
    }

    public canSendDirectMessage() {
        let chat = this.activeDirectChat();
        return !this.directMessageSending
            && !!(chat && chat.id && !chat.blocked)
            && String(this.directDraft || '').trim().length > 0;
    }

    public async sendDirectMessage(): Promise<boolean> {
        let text = String(this.directDraft || '').trim();
        if (!text) return false;

        if (!this.isLoggedIn()) {
            await this.openAuthModal('login');
            return false;
        }

        let chat = this.activeDirectChat();
        if (chat.blocked) {
            await this.showSaveHint('차단한 상대에게는 메시지를 보낼 수 없어요.');
            return false;
        }
        let postId = String(chat.companionPostId || '').trim();
        if (!postId) {
            await this.showSaveHint('동행 준비방 정보를 찾지 못했어요.');
            return false;
        }
        this.directMessageSending = true;
        await this.service.render();
        try {
            let response: any = await wiz.call('save_course', {
                community_action: 'direct_message_send',
                post_id: postId,
                message: text
            });
            if (!response || response.code !== 200 || !response.data || !response.data.message) {
                await this.showSaveHint(String(response && response.data && response.data.message || '메시지를 보내지 못했어요.'));
                return false;
            }
            this.appendDirectMessage(chat, response.data.message, false);
            this.directDraft = '';
            await this.loadDirectChatRooms(true);
            this.scrollDirectMessages();
            return true;
        } catch (e) {
            await this.showSaveHint('메시지 서버에 연결하지 못했어요. 잠시 후 다시 시도해주세요.');
            return false;
        } finally {
            this.directMessageSending = false;
            await this.service.render();
        }
    }

    private appendDirectMessage(chat: any, message: any, incoming: boolean) {
        if (!chat || !message || !message.id) return;
        if (!Array.isArray(chat.messages)) chat.messages = [];
        if (chat.messages.some((item: any) => item && item.id === message.id)) return;
        let currentUserId = this.currentCompanionApplicantKey();
        let normalized = {
            ...message,
            role: message.role || (String(message.senderKey || '') === currentUserId ? 'me' : 'other')
        };
        chat.messages.push(normalized);
        chat.preview = normalized.text;
        chat.time = normalized.time || '방금';
        chat.lastMessageAt = normalized.createdAt || new Date().toISOString();
        if (incoming && normalized.role !== 'me' && chat.id !== this.activeDirectChatId) {
            chat.unread = Math.max(0, Number(chat.unread || 0)) + 1;
        } else if (chat.id === this.activeDirectChatId) {
            chat.unread = 0;
        }
    }

    private mergeDirectMessageReadState(existing: any, messages: any[]) {
        let previousById = new Map<string, any>();
        (existing && Array.isArray(existing.messages) ? existing.messages : []).forEach((message: any) => {
            if (message && message.id) previousById.set(String(message.id), message);
        });
        return (Array.isArray(messages) ? messages : []).map((message: any) => {
            let previous = message && message.id ? previousById.get(String(message.id)) : null;
            if (!previous || previous.read !== true || message.read === true) return message;
            return {
                ...message,
                read: true,
                readAt: String(previous.readAt || message.readAt || '')
            };
        });
    }

    private async loadDirectChatRooms(render: boolean = true) {
        if (!this.isLoggedIn()) return false;
        try {
            let response: any = await wiz.call('saved_courses', { community_action: 'direct_chat_rooms' });
            if (!response || response.code !== 200 || !response.data || !Array.isArray(response.data.rooms)) return false;
            let remoteRooms = response.data.rooms.map((room: any) => {
                let existing = this.directChats.find((item: any) => item && item.id === room.id);
                let post = this.companionPosts.find((item: any) => item && String(item.id || '') === String(room.companionPostId || ''));
                let preparation = post
                    ? this.companionPreparationFromPost(post)
                    : (existing && existing.preparation ? existing.preparation : null);
                if (preparation && existing && existing.preparation) {
                    preparation = {
                        ...preparation,
                        collapsed: typeof existing.preparation.collapsed === 'boolean'
                            ? existing.preparation.collapsed
                            : preparation.collapsed,
                        packingItems: Array.isArray(existing.preparation.packingItems)
                            ? existing.preparation.packingItems
                            : preparation.packingItems
                    };
                }
                return {
                    ...(existing || {}),
                    ...room,
                    preparation,
                    muted: !!(existing && existing.muted),
                    blocked: !!(existing && existing.blocked),
                    unread: Number(room.unread || 0),
                    messages: this.mergeDirectMessageReadState(existing, room.messages)
                };
            });
            let otherChats = this.directChats.filter((chat: any) => chat && chat.category !== 'companion');
            this.directChats = [...remoteRooms, ...otherChats];
            if (this.activeDirectChatId && !this.directChats.some((chat: any) => chat.id === this.activeDirectChatId)) {
                this.activeDirectChatId = '';
                this.directRoomOpen = false;
            }
            if (render) await this.service.render();
            let active = this.activeDirectChat();
            if (this.directRoomOpen && active && Number(active.unread || 0) > 0) {
                await this.markDirectMessagesRead(active, render);
            }
            return true;
        } catch (e) { }
        return false;
    }

    private async markDirectMessagesRead(chat: any, render: boolean = true) {
        let postId = String(chat && chat.companionPostId || '').trim();
        if (!postId || !this.isLoggedIn()) return false;
        try {
            let response: any = await wiz.call('save_course', {
                community_action: 'direct_message_read',
                post_id: postId
            });
            if (!response || response.code !== 200) return false;
            chat.unread = 0;
            if (render) await this.service.render();
            return true;
        } catch (e) { }
        return false;
    }

    private startDirectChatRealtime() {
        this.stopDirectChatRealtime();
        if (typeof window === 'undefined' || !this.isLoggedIn()) return;
        let auth = this.service && this.service.auth ? this.service.auth : null;
        let token = String(auth && auth.token || '').trim();
        let socket = io('/wiz/app/main/page.access', {
            transports: ['polling'],
            upgrade: false,
            reconnection: true,
            timeout: 10000
        });
        socket.on('connect', async () => {
            this.togetherRealtimeStatus = 'connected';
            socket.emit('join', { token });
            await this.refreshTogetherLocations(false);
            await this.service.render();
        });
        socket.on('disconnect', async () => {
            this.togetherRealtimeStatus = 'offline';
            await this.service.render();
        });
        socket.on('connect_error', async () => {
            this.togetherRealtimeStatus = 'reconnecting';
            await this.service.render();
        });
        if (socket.io && socket.io.on) {
            socket.io.on('reconnect_attempt', async () => {
                this.togetherRealtimeStatus = 'reconnecting';
                await this.service.render();
            });
        }
        socket.on('direct_message', async (message: any) => {
            let roomId = `dm-${String(message && message.postId || '')}`;
            let chat = this.directChats.find((item: any) => item && item.id === roomId);
            if (!chat) {
                await this.loadDirectChatRooms(false);
                chat = this.directChats.find((item: any) => item && item.id === roomId);
            }
            if (!chat) return;
            this.appendDirectMessage(chat, message, true);
            await this.service.render();
            if (chat.id === this.activeDirectChatId && this.directRoomOpen) {
                await this.markDirectMessagesRead(chat, true);
                this.scrollDirectMessages();
            }
        });
        socket.on('direct_message_read', async (event: any) => {
            let roomId = `dm-${String(event && event.postId || '')}`;
            let chat = this.directChats.find((item: any) => item && item.id === roomId);
            if (!chat || !Array.isArray(event && event.messageIds)) return;
            let messageIds = new Set(event.messageIds.map((id: any) => String(id || '')));
            let currentUserId = this.currentCompanionApplicantKey();
            (chat.messages || []).forEach((message: any) => {
                let isMine = message && (
                    message.role === 'me'
                    || String(message.senderKey || '') === currentUserId
                );
                if (isMine && messageIds.has(String(message.id || ''))) {
                    message.read = true;
                    message.readAt = String(event.readAt || '');
                }
            });
            await this.service.render();
        });
        socket.on('together_meeting_message', async (message: any) => {
            await this.receiveTogetherMeetingMessage(message);
        });
        socket.on('together_meeting_ended', async (event: any) => {
            let meetingId = String(event && event.meetingId || '');
            let current = this.togetherMeetingAppointment;
            if (!current || String(current.id || '') !== meetingId) return;
            let endedByMe = String(event && event.endedBy || '') === this.currentCompanionApplicantKey();
            this.clearTogetherMeetingState(true);
            await this.service.render();
            if (!endedByMe) await this.showSaveHint('동행자가 약속을 종료해 채팅도 닫혔어요.');
        });
        socket.on('together_meeting_read', async (event: any) => {
            let meeting = this.togetherMeetingAppointment;
            if (!meeting || String(meeting.id || '') !== String(event && event.meetingId || '')) return;
            let ids = new Set((Array.isArray(event && event.messageIds) ? event.messageIds : []).map((id: any) => String(id || '')));
            this.togetherMeetingChatMessages.forEach((message: any) => {
                if (message && message.role === 'me' && ids.has(String(message.id || ''))) {
                    message.read = true;
                    message.readAt = String(event.readAt || '');
                }
            });
            await this.service.render();
        });
        socket.on('together_meeting_typing', async (event: any) => {
            let meeting = this.togetherMeetingAppointment;
            if (!meeting || String(meeting.id || '') !== String(event && event.meetingId || '')) return;
            if (String(event.userKey || '') === this.currentCompanionApplicantKey()) return;
            this.togetherMeetingPeerTyping = event.typing === true;
            if (this.togetherMeetingPeerTypingTimer && typeof window !== 'undefined') window.clearTimeout(this.togetherMeetingPeerTypingTimer);
            if (this.togetherMeetingPeerTyping && typeof window !== 'undefined') {
                this.togetherMeetingPeerTypingTimer = window.setTimeout(async () => {
                    this.togetherMeetingPeerTyping = false;
                    await this.service.render();
                }, 2200);
            }
            await this.service.render();
        });
        socket.on('together_location_update', async (event: any) => {
            if (this.isCurrentTogetherMeetingEvent(event)) await this.refreshTogetherLocations(true);
        });
        socket.on('together_location_consent', async (event: any) => {
            if (this.isCurrentTogetherMeetingEvent(event)) await this.refreshTogetherLocations(true);
        });
        socket.on('together_participant_blocked', async (event: any) => {
            if (!this.isCurrentTogetherMeetingEvent(event)) return;
            let myKey = this.currentCompanionApplicantKey();
            if (String(event.blockedUserKey || '') === myKey || String(event.blockerUserKey || '') === myKey) {
                this.togetherCompanions = [];
                await this.endTogetherLocationShare();
                await this.service.render();
            }
        });
        this.directChatSocket = socket;
        this.directChatPollTimer = window.setInterval(() => {
            this.loadDirectChatRooms(true).catch(() => false);
        }, 2000);
    }

    private isCurrentTogetherMeetingEvent(event: any) {
        let meeting = this.togetherMeetingAppointment;
        return !!meeting && String(meeting.id || '') === String(event && event.meetingId || '');
    }

    private stopDirectChatRealtime() {
        if (this.directChatSocket) {
            this.directChatSocket.close();
            this.directChatSocket = null;
        }
        if (this.directChatPollTimer && typeof window !== 'undefined') {
            window.clearInterval(this.directChatPollTimer);
            this.directChatPollTimer = null;
        }
    }

    private async receiveTogetherMeetingMessage(message: any) {
        let meetingId = String(message && message.meetingId || '');
        if (!meetingId || !message || !message.id) return;
        let meeting = this.togetherMeetingAppointment;
        if (!meeting || String(meeting.id || '') !== meetingId) {
            await this.loadTogetherMeeting(false, false);
            meeting = this.togetherMeetingAppointment;
        }
        if (!meeting || String(meeting.id || '') !== meetingId || !this.hasActiveTogetherMeetingChat()) return;
        if (this.togetherMeetingChatMessages.some((item: any) => String(item && item.id || '') === String(message.id))) return;
        let isMine = String(message.senderKey || '') === this.currentCompanionApplicantKey();
        this.togetherMeetingChatMessages = [...this.togetherMeetingChatMessages, {
            id: String(message.id),
            role: isMine ? 'me' : 'other',
            senderName: isMine ? '나' : String(message.senderName || meeting.peerName || '동행자'),
            text: String(message.text || ''),
            timeLabel: String(message.timeLabel || '방금'),
            read: false,
            readAt: ''
        }];
        if (!isMine) {
            if (this.togetherMeetingChatOpen) {
                await this.markTogetherMeetingMessagesRead(false);
            } else {
                this.togetherMeetingUnread += 1;
                await this.showSaveHint(`${message.senderName || meeting.peerName || '동행자'}님이 약속 메시지를 보냈어요.`);
            }
        }
        await this.service.render();
        if (this.togetherMeetingChatOpen) this.scrollTogetherMeetingChat();
    }

    private startDirectChatRoomRefresh() {
        this.stopDirectChatRoomRefresh();
        if (typeof window === 'undefined') return;
        let refresh = async () => {
            if (this.activeTab !== 'chat' || this.chatContentTab !== 'dm' || !this.directRoomOpen) {
                this.stopDirectChatRoomRefresh();
                return;
            }
            await this.loadDirectChatRooms(true);
            this.directChatRoomRefreshTimer = window.setTimeout(refresh, 1500);
        };
        this.directChatRoomRefreshTimer = window.setTimeout(refresh, 750);
    }

    private stopDirectChatRoomRefresh() {
        if (this.directChatRoomRefreshTimer && typeof window !== 'undefined') {
            window.clearTimeout(this.directChatRoomRefreshTimer);
        }
        this.directChatRoomRefreshTimer = null;
    }

    public handleDirectComposerFocus() {
        this.scrollDirectMessages();
    }

    public async startDirectChat() {
        if (!this.isLoggedIn()) {
            await this.openAuthModal('login');
            return;
        }

        await this.showSaveHint('1:1 채팅은 동행 신청이 수락된 사용자끼리만 열립니다.');
    }

    public async setCommunityTopic(topic: string) {
        if (!this.communityTopics.some((item: any) => item.key === topic)) return;
        this.selectedCommunityTopic = topic;
        this.communitySortDropdownOpen = false;
        await this.service.render();
        this.resetChatContentScroll();
    }

    public async toggleCommunitySortDropdown() {
        this.communitySortDropdownOpen = !this.communitySortDropdownOpen;
        await this.service.render();
    }

    public async setCommunitySort(sort: string) {
        if (!this.communitySortOptions.some((item: any) => item.key === sort)) return;
        this.selectedCommunitySort = sort;
        if (sort === 'recommended') this.selectedCommunityTopic = 'recommend';
        this.communitySortDropdownOpen = false;
        await this.service.render();
    }

    public communitySortLabel() {
        let option = this.communitySortOptions.find((item: any) => item.key === this.selectedCommunitySort);
        return option ? option.label : '추천';
    }

    public communityFilterTopics() {
        return this.communityTopics.filter((topic: any) => topic.key !== 'recommend');
    }

    public communityTopicLabel() {
        if (this.selectedCommunityTopic === 'recommend') return this.communitySortLabel();
        let topic = this.communityTopics.find((item: any) => item.key === this.selectedCommunityTopic);
        return topic ? topic.label : '추천';
    }

    public filteredCommunityPosts() {
        let posts = this.communityPosts.filter((post: any) => {
            if (!this.selectedCommunityTopic) return true;
            if (this.selectedCommunityTopic === 'recommend') return true;
            return post.topic === this.selectedCommunityTopic;
        });
        let search = String(this.communitySearchQuery || '').trim().toLowerCase();
        if (search) {
            posts = posts.filter((post: any) => this.communityPostSearchText(post).indexOf(search) > -1);
        }

        return [...posts].sort((a: any, b: any) => {
            if (this.selectedCommunitySort === 'latest') {
                return Number(a.createdAt || 0) - Number(b.createdAt || 0);
            }
            let aScore = Number(a.likes || 0) + Number(a.comments || 0) * 3 + Number(a.votes || 0);
            let bScore = Number(b.likes || 0) + Number(b.comments || 0) * 3 + Number(b.votes || 0);
            return bScore - aScore;
        });
    }

    public async handleCommunitySearchInput() {
        this.expandedCommunityPostId = '';
        await this.service.render();
    }

    public async clearCommunitySearch() {
        this.communitySearchQuery = '';
        this.expandedCommunityPostId = '';
        await this.service.render();
    }

    private communityPostSearchText(post: any) {
        return [
            post && post.title,
            post && post.summary,
            post && post.category,
            post && post.author,
            post && post.destination,
            post && post.place,
            ...(post && Array.isArray(post.tags) ? post.tags : [])
        ].map((value: any) => String(value || '').toLowerCase()).join(' ');
    }

    private async loadCommunityPosts(showLoading: boolean = false) {
        try {
            const { code, data } = await this.requestCommunityPosts();
            if (code !== 200) {
                let cached = this.readCommunityPostsCache();
                if (cached.length > 0) this.communityPosts = this.mergeCommunityPosts(cached);
                if (showLoading) await this.service.render();
                return false;
            }
            let posts = data && Array.isArray(data.posts)
                ? data.posts.filter((post: any) => post && post.kind !== 'companion' && post.topic !== 'companion')
                : [];
            this.communityPosts = this.mergeCommunityPosts(posts);
            this.persistCommunityPostsCache();
            if (showLoading) await this.service.render();
            return true;
        } catch (e) { }
        return false;
    }

    private async requestCommunityPosts() {
        try {
            let response: any = await wiz.call('saved_courses', {
                community_action: 'list',
                actor_key: this.communityActorKey()
            });
            if (response && response.code === 200) return response;
        } catch (e) { }
        return { code: 500, data: { posts: [] } };
    }

    private mergeCommunityPosts(posts: any[] = []) {
        let seen: any = {};
        return [...posts, ...this.communityPosts].filter((post: any) => {
            let id = String(post && post.id ? post.id : '');
            if (!id || seen[id]) return false;
            seen[id] = true;
            return true;
        });
    }

    private applyCommunityPostUpdate(post: any) {
        if (!post || !post.id) return null;
        this.communityPosts = this.mergeCommunityPosts([post]);
        if (this.activeCommunityPost && this.activeCommunityPost.id === post.id) {
            this.activeCommunityPost = post;
        }
        this.persistCommunityPostsCache();
        return post;
    }

    private async saveCommunityPost(post: any) {
        if (!post || !post.id) return { post: null, remote: false };
        let payload = JSON.stringify(post);
        try {
            let response: any = await wiz.call('save_course', {
                community_action: 'post',
                actor_key: this.communityActorKey(),
                post: payload
            });
            if (!response || response.code !== 200) {
                response = await wiz.call('save_community_post', { post: payload });
            }
            if (response && response.code === 200 && response.data && response.data.post) {
                this.applyCommunityPostUpdate(response.data.post);
                return { post: response.data.post, remote: true };
            }
        } catch (e) { }
        this.applyCommunityPostUpdate(post);
        return { post, remote: false };
    }

    private async requestCommunityPostAction(action: string, post: any, data: any = {}) {
        let postId = String(post && post.id ? post.id : '');
        if (!postId) return { code: 400, data: {} };
        try {
            let response: any = await wiz.call('save_course', {
                community_action: action,
                post_id: postId,
                actor_key: this.communityActorKey(),
                ...data
            });
            if (response && response.code === 200) return response;
        } catch (e) { }
        return { code: 500, data: {} };
    }

    private async loadCommunityComments(post: any, showLoading: boolean = true) {
        if (!post || !post.id) {
            this.communityComments = [];
            return;
        }
        this.communityCommentsLoading = true;
        if (showLoading) await this.service.render();
        try {
            let response: any = await wiz.call('saved_courses', {
                community_action: 'comments',
                post_id: post.id
            });
            if (response && response.code === 200 && response.data && Array.isArray(response.data.comments)) {
                this.communityComments = response.data.comments;
            } else {
                this.communityComments = [];
            }
        } catch (e) {
            this.communityComments = [];
        }
        this.communityCommentsLoading = false;
        if (showLoading) await this.service.render();
    }

    private async requestMyCommunityPosts() {
        try {
            let response: any = await wiz.call('saved_courses', {
                community_action: 'mine',
                actor_key: this.communityActorKey()
            });
            if (response && response.code === 200) return response;
        } catch (e) { }
        return { code: 500, data: { posts: [] } };
    }

    public async openCommunityArchive() {
        this.communityArchiveOpen = true;
        await this.loadCommunityArchivePosts(true);
    }

    public async closeCommunityArchive() {
        if (this.communityDeleteSubmittingId) return;
        this.communityArchiveOpen = false;
        await this.service.render();
    }

    public async loadCommunityArchivePosts(showLoading: boolean = true) {
        this.communityArchiveLoading = true;
        if (showLoading) await this.service.render();
        let response = await this.requestMyCommunityPosts();
        let posts = response && response.code === 200 && response.data && Array.isArray(response.data.posts)
            ? response.data.posts
            : [];
        this.communityArchivePosts = posts;
        posts.forEach((post: any) => this.applyCommunityPostUpdate(post));
        this.communityArchiveLoading = false;
        if (showLoading) await this.service.render();
    }

    public isOwnCommunityPost(post: any) {
        if (!post || !post.id) return false;
        return post.owned === true;
    }

    public async openArchivedCommunityPost(post: any) {
        if (!post || !post.id) return;
        this.communityArchiveOpen = false;
        this.activeTab = 'home';
        this.homeContentTab = 'community';
        this.applyCommunityPostUpdate(post);
        await this.openCommunityPost(post);
    }

    public async deleteCommunityPost(event: any, post: any) {
        if (event && event.stopPropagation) event.stopPropagation();
        if (!post || !post.id || this.communityDeleteSubmittingId) return;
        if (!this.isOwnCommunityPost(post)) {
            await this.showSaveHint('내가 쓴 글만 삭제할 수 있어요.');
            return;
        }
        this.communityDetailMenuOpen = false;
        if (typeof window !== 'undefined' && !window.confirm('이 게시글을 삭제할까요? 삭제한 글은 복구할 수 없습니다.')) return;
        this.communityDeleteSubmittingId = post.id;
        await this.service.render();
        let response = await this.requestCommunityPostAction('delete', post);
        if (response && response.code === 200) {
            this.communityPosts = this.communityPosts.filter((item: any) => item && item.id !== post.id);
            this.communityArchivePosts = this.communityArchivePosts.filter((item: any) => item && item.id !== post.id);
            if (this.activeCommunityPost && this.activeCommunityPost.id === post.id) {
                this.activeCommunityPost = null;
                this.communityComments = [];
                this.communityCommentDraft = '';
            }
            this.persistCommunityPostsCache();
            await this.showSaveHint('내가 쓴 글을 삭제했어요.');
        } else {
            let message = response && response.data && response.data.message
                ? response.data.message
                : '내가 쓴 글만 삭제할 수 있어요.';
            await this.showSaveHint(message);
        }
        this.communityDeleteSubmittingId = '';
        await this.service.render();
    }

    public async toggleCommunityDetailMenu(event?: any) {
        if (event && event.stopPropagation) event.stopPropagation();
        this.communityDetailMenuOpen = !this.communityDetailMenuOpen;
        await this.service.render();
    }

    public hasReportedCommunityPost(post: any) {
        return !!post && this.communityReportedPostIds.indexOf(String(post.id || '')) >= 0;
    }

    public async reportCommunityPost(event: any, post: any) {
        if (event && event.stopPropagation) event.stopPropagation();
        if (!post || !post.id || this.isOwnCommunityPost(post) || this.communityReportSubmittingId) return;
        this.communityDetailMenuOpen = false;
        if (this.hasReportedCommunityPost(post)) {
            await this.showSaveHint('이미 신고한 게시글이에요.');
            return;
        }
        if (typeof window !== 'undefined' && !window.confirm('부적절하거나 문제가 있는 게시글로 신고할까요?')) return;

        this.communityReportSubmittingId = post.id;
        await this.service.render();
        let response = await this.requestCommunityPostAction('report', post, { reason: '부적절한 내용' });
        if (response && response.code === 200) {
            if (this.communityReportedPostIds.indexOf(post.id) < 0) this.communityReportedPostIds.push(post.id);
            let already = !!(response.data && response.data.already);
            await this.showSaveHint(already ? '이미 신고한 게시글이에요.' : '신고가 접수되었어요. 운영팀이 확인할게요.');
        } else {
            await this.showSaveHint('신고를 접수하지 못했어요. 잠시 후 다시 시도해주세요.');
        }
        this.communityReportSubmittingId = '';
        await this.service.render();
    }

    private restoreCommunityPostsCache() {
        let cached = this.readCommunityPostsCache();
        if (cached.length > 0) this.communityPosts = this.mergeCommunityPosts(cached);
    }

    private readCommunityPostsCache() {
        if (typeof window === 'undefined' || !window.localStorage) return [];
        try {
            let raw = window.localStorage.getItem(this.communityPostsStorageKey) || '[]';
            let posts = JSON.parse(raw);
            return Array.isArray(posts)
                ? posts.map((post: any) => ({ ...post, owned: false }))
                : [];
        } catch (e) { }
        return [];
    }

    private persistCommunityPostsCache(posts: any[] = this.communityPosts) {
        if (typeof window === 'undefined' || !window.localStorage) return;
        try {
            let cached = posts
                .filter((post: any) => this.shouldPersistCommunityPost(post))
                .map((post: any) => {
                    let cachePost = { ...post };
                    delete cachePost.owned;
                    return cachePost;
                })
                .slice(0, 50);
            window.localStorage.setItem(this.communityPostsStorageKey, JSON.stringify(cached));
        } catch (e) { }
    }

    private shouldPersistCommunityPost(post: any) {
        let id = String(post && post.id ? post.id : '');
        return id.indexOf('community-draft-') === 0 || id.indexOf('community-user-') === 0 || id.indexOf('community-db-') === 0;
    }

    private communityActorKey() {
        if (typeof window === 'undefined' || !window.localStorage) return 'server-render';
        try {
            let current = window.localStorage.getItem(this.communityActorStorageKey);
            if (current) return current;
            let next = `actor-${Date.now()}-${Math.random().toString(16).slice(2)}`;
            window.localStorage.setItem(this.communityActorStorageKey, next);
            return next;
        } catch (e) { }
        return 'volatile-actor';
    }

    private readCommunityActionMap(storageKey: string) {
        if (typeof window === 'undefined' || !window.localStorage) return {};
        try {
            let raw = window.localStorage.getItem(storageKey) || '{}';
            let value = JSON.parse(raw);
            return value && typeof value === 'object' ? value : {};
        } catch (e) { }
        return {};
    }

    private writeCommunityActionMap(storageKey: string, value: any) {
        if (typeof window === 'undefined' || !window.localStorage) return;
        try {
            window.localStorage.setItem(storageKey, JSON.stringify(value || {}));
        } catch (e) { }
    }

    public hasLikedCommunityPost(post: any) {
        let id = String(post && post.id ? post.id : '');
        if (!id) return false;
        return !!this.readCommunityActionMap(this.communityLikeStorageKey)[id];
    }

    private markCommunityPostLiked(post: any) {
        let id = String(post && post.id ? post.id : '');
        if (!id) return;
        let map = this.readCommunityActionMap(this.communityLikeStorageKey);
        map[id] = true;
        this.writeCommunityActionMap(this.communityLikeStorageKey, map);
    }

    public hasVotedCommunityPoll(post: any) {
        let id = String(post && post.id ? post.id : '');
        if (!id) return false;
        return !!this.readCommunityActionMap(this.communityVoteStorageKey)[id];
    }

    public communityPollOptionSelected(post: any, option: string) {
        let id = String(post && post.id ? post.id : '');
        if (!id) return false;
        return this.readCommunityActionMap(this.communityVoteStorageKey)[id] === option;
    }

    private markCommunityPollVoted(post: any, option: string) {
        let id = String(post && post.id ? post.id : '');
        if (!id) return;
        let map = this.readCommunityActionMap(this.communityVoteStorageKey);
        map[id] = option;
        this.writeCommunityActionMap(this.communityVoteStorageKey, map);
    }

    public communityPostLocationLabel(post: any) {
        if (!post) return '';
        return [post.destination, post.place]
            .map((value: any) => String(value || '').trim())
            .filter((value: string) => !!value)
            .join(' · ');
    }

    public communityPostViews(post: any) {
        if (!post) return 0;
        return Math.max(0, Number(post.views || 0));
    }

    public isCommunityPostExpanded(post: any) {
        return !!post && this.expandedCommunityPostId === post.id;
    }

    public communityPostCanExpand(post: any) {
        if (!post) return false;
        return !!post.poll || !!post.photo || String(post.title || '').length > 18 || String(post.summary || '').length > 38;
    }

    public async openCommunityPost(post: any, commentMode: boolean = false) {
        if (!post || !post.id) return;
        this.communityDetailMenuOpen = false;
        this.activeCommunityPost = post;
        this.expandedCommunityPostId = '';
        post.views = Number(post.views || 0) + 1;
        this.applyCommunityPostUpdate(post);
        let response = await this.requestCommunityPostAction('view', post);
        if (response && response.code === 200 && response.data && response.data.post) {
            this.applyCommunityPostUpdate(response.data.post);
        } else if (String(post.id || '').indexOf('community-draft-') === 0) {
            await this.saveCommunityPost(post);
        }
        if (commentMode) this.communityCommentDraft = this.communityCommentDraft || '';
        await this.loadCommunityComments(this.activeCommunityPost, false);
        await this.service.render();
    }

    public async closeCommunityPost() {
        this.communityDetailMenuOpen = false;
        this.activeCommunityPost = null;
        this.communityComments = [];
        this.communityCommentDraft = '';
        await this.service.render();
    }

    public async toggleCommunityPost(post: any) {
        await this.openCommunityPost(post);
    }

    public async handleCommunityPostKeydown(event: any, post: any) {
        let key = event ? String(event.key || event.code || '') : '';
        if (key !== 'Enter' && key !== ' ' && key !== 'Spacebar') return;
        if (event.preventDefault) event.preventDefault();
        await this.openCommunityPost(post);
    }

    public async likeCommunityPost(event: any, post: any) {
        if (event && event.stopPropagation) event.stopPropagation();
        if (!post || !post.id) return;
        if (this.hasLikedCommunityPost(post)) {
            await this.showSaveHint('좋아요는 한 번만 누를 수 있어요.');
            return;
        }
        this.markCommunityPostLiked(post);
        post.likes = Number(post.likes || 0) + 1;
        this.applyCommunityPostUpdate(post);
        await this.service.render();
        let response = await this.requestCommunityPostAction('like', post);
        if (response && response.code === 200 && response.data && response.data.post) {
            this.applyCommunityPostUpdate(response.data.post);
            if (response.data.already) await this.showSaveHint('이미 좋아요를 눌렀어요.');
        } else if (String(post.id || '').indexOf('community-draft-') === 0) {
            await this.saveCommunityPost(post);
        }
        await this.service.render();
    }

    public async submitCommunityComment() {
        let post = this.activeCommunityPost;
        let body = String(this.communityCommentDraft || '').trim();
        if (!post || !post.id) return;
        if (!body) {
            await this.showSaveHint('댓글을 입력해주세요.');
            return;
        }
        let localComment = {
            id: `community-comment-${Date.now()}`,
            postId: post.id,
            author: this.myDisplayName(),
            body,
            createdAt: 0,
            createdLabel: '방금'
        };
        this.communityCommentDraft = '';
        this.communityComments = [...this.communityComments, localComment];
        post.comments = Number(post.comments || 0) + 1;
        this.applyCommunityPostUpdate(post);
        await this.service.render();

        let response = await this.requestCommunityPostAction('comment', post, {
            body,
            author: this.myDisplayName()
        });
        if (response && response.code === 200 && response.data) {
            if (response.data.post) this.applyCommunityPostUpdate(response.data.post);
            if (response.data.comment) {
                this.communityComments = [
                    ...this.communityComments.filter((comment: any) => comment.id !== localComment.id),
                    response.data.comment
                ];
            }
        } else {
            await this.showSaveHint('서버 연결이 불안정해 댓글을 기기에 먼저 표시했어요.');
        }
        await this.service.render();
    }

    public async writeCommunityPost() {
        await this.openCommunityComposer('post');
    }

    public async confirmPlannerCourse() {
        if (this.plannerCourseConfirming) return;
        let allStops = this.plannerAllStops();
        if (!this.plannerCourseReady || allStops.length === 0) {
            await this.showSaveHint('먼저 채팅으로 여행 조건을 알려주세요.');
            return;
        }
        if (!this.isLoggedIn()) {
            await this.openAuthModal('login');
            return;
        }

        this.plannerCourseConfirming = true;
        await this.service.render();
        let routePayload = this.plannerCourseRoutePayload(allStops);
        let dayCount = Math.max(1, Array.isArray(this.plannerCourseDays) ? this.plannerCourseDays.length : 1);
        let payload = {
            title: this.plannerCourseSaveTitle(),
            region: this.plannerCourseRegion,
            category: '여행',
            description: `AI 대화를 기반으로 만든 ${this.plannerCourseRegion} 코스입니다.`,
            cover_image: allStops[0] && allStops[0].image ? allStops[0].image : '/assets/places/haeundae-beach.jpg',
            image: allStops[0] && allStops[0].image ? allStops[0].image : '/assets/places/haeundae-beach.jpg',
            duration_type: dayCount > 1 ? 'overnight' : 'hours',
            duration_value: dayCount > 1 ? this.plannerCourseSchedule : String(Math.max(1, Math.ceil(allStops.length * 1.5))),
            companion_type: (this.plannerTravelState.companions || []).indexOf('혼자') >= 0 ? 'solo' : '',
            is_public: false,
            is_featured: false,
            places: routePayload.places,
            tags: this.uniqueTags(['여행', this.plannerCourseRegion, this.plannerCourseSchedule, 'AI플래너', ...allStops.map((stop: any) => stop.name)])
        };

        this.courseDraft = {
            ...this.courseDraft,
            title: payload.title,
            region: this.plannerCourseRegion,
            schedule: this.plannerCourseSchedule,
            places: this.plannerCoursePlacesText(),
            photo: allStops[0] && allStops[0].image ? allStops[0].image : '/assets/places/haeundae-beach.jpg',
            photoName: 'AI 플래너 코스',
            description: payload.description,
            category: '여행',
            companionEnabled: true,
            companionDate: '일정 협의',
            companionTime: '10:00 출발',
            companionCapacity: 4,
            companionCost: '1인 약 8만원',
            companionBudgetStyle: 'medium',
            companionPace: 'balanced',
            companionMood: '사진, 맛집, 편안한 대화',
            companionFlexible: '카페 순서, 종료 시간',
            companionMeetingPoint: `${this.plannerCourseRegion} 첫 장소`,
            companionSmoking: 'non',
            companionDrinking: 'light',
            companionIntro: `AI가 만든 ${this.plannerCourseRegion} 코스를 함께 걸을 동행을 모집합니다.`
        };

        let createResponse: any;
        try {
            createResponse = await wiz.call('create_builder_course', { data: JSON.stringify(payload) });
        } catch (e) {
            createResponse = { code: 500, data: {} };
        }

        if (!createResponse || Number(createResponse.code || 0) !== 200) {
            this.plannerCourseConfirming = false;
            await this.showSaveHint(this.responseMessage(createResponse && createResponse.data, '코스를 저장하지 못했어요. 잠시 후 다시 시도해주세요.'));
            return;
        }

        let savedCourse = this.courseRowToCard(createResponse.data && createResponse.data.row, payload);
        this.rememberOwnedCourseRow(createResponse.data && createResponse.data.row);
        savedCourse.route = routePayload.route;
        savedCourse.distance = this.plannerDistance;
        this.courses = [
            savedCourse,
            ...this.courses.filter((course: any) => course && course.id !== savedCourse.id)
        ];
        this.recommendations = [
            savedCourse,
            ...this.recommendations.filter((course: any) => course && course.id !== savedCourse.id)
        ];
        await this.loadChatThreads(false);
        await this.startNewChat();
        this.plannerCourseConfirming = false;
        await this.showSaveHint('내 코스에 한 번만 저장하고, 현재 대화는 보관함으로 옮겼어요.');
    }

    public isPlannerCourseConfirmed() {
        return this.plannerCompanionCards.length > 0
            && this.plannerCompanionCards.every((card: any) => !!card.courseId && card.courseConfirmed === true);
    }

    public plannerCompanionStatus(card: any) {
        if (!card || card.courseConfirmed !== true) return '코스 확정 후 모집을 확인할 수 있어요';
        return `모집중 신청 ${card && card.applicants ? card.applicants : 0}/${card && card.capacity ? card.capacity : 1}명`;
    }

    public refreshPlannerPreviewFromPrompt(prompt: string) {
        // 실제 장소 검색 도구 결과가 없으면 임의 코스를 만들지 않습니다.
        this.resetPlannerPreview();
        return;

        let text = String(prompt || '').toLowerCase();

        if (!this.plannerCourseReady) {
            this.plannerCourseBaseTitle = text.indexOf('바다') > -1 ? '내 부산 바다 여행' : '내 부산 여행';
        } else if (text.indexOf('바다') > -1) {
            this.plannerCourseBaseTitle = '내 부산 바다 여행';
        }
        this.plannerCourseRegion = '부산 해운대구';
        if (text.indexOf('2박') > -1) this.plannerCourseSchedule = '2박 3일';
        if (text.indexOf('1박') > -1) this.plannerCourseSchedule = '1박 2일';
        if (text.indexOf('당일') > -1) this.plannerCourseSchedule = '당일';
        if (text.indexOf('야경') > -1 || text.indexOf('저녁') > -1) {
            this.plannerMapLabel = '해운대구-수영구 동선';
        } else if (!this.plannerCourseReady) {
            this.plannerMapLabel = '해운대구 동선';
        }

        let nextStops = this.plannerCourseReady ? this.plannerStops.map((stop: any) => ({ ...stop })) : [];
        let templates = this.busanPlannerStopTemplates();
        let shouldFillDefault = !this.plannerCourseReady && !this.promptHasPlannerKeyword(text);

        if (shouldFillDefault || text.indexOf('바다') > -1 || text.indexOf('해변') > -1 || text.indexOf('부산') > -1) {
            this.ensurePlannerStop(nextStops, templates.beach);
            this.ensurePlannerStop(nextStops, templates.coast);
        }
        if (shouldFillDefault || text.indexOf('맛집') > -1 || text.indexOf('점심') > -1 || text.indexOf('먹') > -1 || text.indexOf('식사') > -1) {
            this.ensurePlannerStop(nextStops, templates.food);
        }
        if (shouldFillDefault || text.indexOf('카페') > -1 || text.indexOf('디저트') > -1 || text.indexOf('감성') > -1) {
            this.ensurePlannerStop(nextStops, templates.cafe);
        }
        if (text.indexOf('야경') > -1 || text.indexOf('저녁') > -1 || text.indexOf('밤') > -1 || text.indexOf('night') > -1) {
            this.ensurePlannerStop(nextStops, templates.night);
        }

        if (nextStops.length === 0) {
            this.ensurePlannerStop(nextStops, templates.beach);
            this.ensurePlannerStop(nextStops, templates.food);
            this.ensurePlannerStop(nextStops, templates.cafe);
        }

        let previousDay = this.plannerCourseDayIndex;
        this.plannerCourseDays = this.buildPlannerCourseDays(this.normalizePlannerStops(nextStops));
        this.applyPlannerCourseDay(Math.min(previousDay, this.plannerCourseDays.length - 1));
        this.plannerCourseReady = true;
        this.plannerCourseVisible = true;
        this.refreshPlannerRouteWithGoogle();

        let regionKey = String(this.plannerCourseRegion || 'travel').replace(/\s+/g, '-');
        this.plannerCompanionCards = [
            {
                id: `planner-mate-${regionKey}-primary`,
                courseId: '',
                courseConfirmed: false,
                title: `${this.plannerCourseRegion} 코스 같이 걸을 동행을 찾아요`,
                route: this.plannerCourseTitle,
                routeStops: this.plannerStops.map((stop: any) => stop.name),
                location: this.plannerCourseRegion,
                date: this.plannerCourseSchedule,
                time: '10:00 출발',
                capacity: 4,
                applicants: 3,
                estimatedCost: '1인 약 8만원',
                budgetStyle: 'medium',
                pace: 'balanced',
                moodTags: ['사진', '맛집', '편안한 대화'],
                flexibility: ['카페 순서', '종료 시간'],
                intro: 'AI가 짠 동선을 따라 사진과 맛집을 함께 즐길 동행을 찾아요.',
                host: 'GACHI',
                status: 'open',
                saved: false,
                image: this.plannerStops[0] && this.plannerStops[0].image ? this.plannerStops[0].image : '/assets/places/haeundae-beach.jpg'
            },
            {
                id: `planner-mate-${regionKey}-slow`,
                courseId: '',
                courseConfirmed: false,
                title: '여유롭게 쉬어가는 여행 메이트 구해요',
                route: this.plannerCourseTitle,
                routeStops: this.plannerStops.map((stop: any) => stop.name),
                location: this.plannerCourseRegion,
                date: this.plannerCourseSchedule,
                time: '13:00 출발',
                capacity: 3,
                applicants: 1,
                estimatedCost: '1인 약 6만원',
                budgetStyle: 'medium',
                pace: 'slow',
                moodTags: ['카페', '산책', '조용히'],
                flexibility: ['장소 체류 시간'],
                intro: '카페와 산책 시간을 넉넉하게 잡고 천천히 이동할 분을 찾아요.',
                host: 'GACHI',
                status: 'open',
                saved: false,
                image: this.plannerStops[this.plannerStops.length - 1] && this.plannerStops[this.plannerStops.length - 1].image ? this.plannerStops[this.plannerStops.length - 1].image : '/assets/places/busan-cafe.jpg'
            }
        ];
    }

    private busanPlannerStopTemplates() {
        return {
            beach: {
                key: 'beach',
                time: '10:00',
                name: '해운대 해변',
                area: '해운대구 우동',
                tag: '바다 산책',
                move: '도보 10분',
                image: '/assets/places/haeundae-beach.jpg',
                icon: 'fa-water',
                lat: 35.1587,
                lng: 129.1604,
                mapLeft: '78%',
                mapTop: '24%'
            },
            food: {
                key: 'food',
                time: '12:30',
                name: '해운대 밀면 점심',
                area: '해운대구 중동',
                tag: '점심 식사',
                move: '도보 10분',
                image: '/assets/places/busan-milmyeon.jpg',
                icon: 'fa-utensils',
                lat: 35.1629,
                lng: 129.1638,
                mapLeft: '64%',
                mapTop: '45%'
            },
            coast: {
                key: 'coast',
                time: '15:00',
                name: '동백섬 해안 산책로',
                area: '해운대구 우동',
                tag: '해안 산책',
                move: '도보 15분',
                image: '/assets/places/dongbaekseom.jpg',
                icon: 'fa-person-walking',
                lat: 35.1537,
                lng: 129.1518,
                mapLeft: '53%',
                mapTop: '61%'
            },
            cafe: {
                key: 'cafe',
                time: '17:00',
                name: '부산 감성 카페',
                area: '해운대구 마린시티',
                tag: '카페 · 디저트',
                move: '차량 18분',
                image: '/assets/places/busan-cafe.jpg',
                icon: 'fa-mug-saucer',
                lat: 35.1554,
                lng: 129.1458,
                mapLeft: '34%',
                mapTop: '68%'
            },
            night: {
                key: 'night',
                time: '19:30',
                name: '광안대교 야경',
                area: '수영구 광안동',
                tag: '야경 감상',
                move: '차량 22분',
                image: '/assets/places/gwangan-night.jpg',
                icon: 'fa-moon',
                lat: 35.1531,
                lng: 129.1187,
                mapLeft: '24%',
                mapTop: '34%'
            }
        };
    }

    private promptHasPlannerKeyword(text: string) {
        return ['바다', '해변', '부산', '맛집', '점심', '먹', '식사', '카페', '디저트', '감성', '야경', '저녁', '밤', 'night']
            .some((keyword: string) => text.indexOf(keyword) > -1);
    }

    private plannerContextCanGenerateCourse(text: string) {
        let context = String(text || '').toLowerCase();
        let regionKeywords = this.courseKnownRegions()
            .reduce((keywords: string[], option: any) => {
                let label = String(option && option.label ? option.label : '').trim().toLowerCase();
                let area = String(option && option.area ? option.area : '').trim().toLowerCase();
                if (label) keywords.push(label);
                area.split(/\s+/).filter((item: string) => item.length >= 2).forEach((item: string) => keywords.push(item));
                return keywords;
            }, [])
            .concat([
                '경주', '춘천', '속초', '양양', '통영', '거제', '포항', '울산', '대전', '광주', '세종',
                '충주', '제천', '안동', '군산', '목포', '순천', '남해', '하동', '평창'
            ]);
        let intentKeywords = [
            '여행', '코스', '일정', '추천', '데이트', '가볼', '관광', '맛집', '카페',
            '당일', '반나절', '1박', '2박', '3박', '동선'
        ];
        let hasRegion = regionKeywords.some((keyword: string) => context.indexOf(keyword) > -1);
        let hasIntent = intentKeywords.some((keyword: string) => context.indexOf(keyword) > -1);
        return hasRegion && hasIntent;
    }

    private plannerPromptExplicitlyRequestsCourse(text: string) {
        let prompt = String(text || '').trim().toLowerCase();
        if (!prompt) return false;
        let courseTarget = '(?:코스|일정|동선|여행 계획|여행계획)';
        let createAction = '(?:만들(?:어|어줘|어 줘|어달라|어 달라)?|짜(?:줘| 줘|달라| 달라)?|계획(?:해줘|해 줘|해달라|해 달라)?|구성(?:해줘|해 줘|해달라|해 달라)?)';
        return new RegExp(`${courseTarget}.{0,16}${createAction}`).test(prompt)
            || new RegExp(`${createAction}.{0,16}${courseTarget}`).test(prompt);
    }

    private plannerConversationCanGenerateCourse(prompt: string) {
        let recentPrompts = this.messages
            .filter((message: any) => message && message.role === 'user' && !message.loading)
            .slice(-6)
            .map((message: any) => String(message.text || '').trim());
        let current = String(prompt || '').trim();
        if (current && recentPrompts.indexOf(current) === -1) recentPrompts.push(current);
        const state = this.plannerTravelState || {};
        const region = String(state.region || state.destination || '');
        const validMode = ['도보', '대중교통', '자동차'].indexOf(String(state.transport || '')) > -1;
        const validTime = /^(?:[01]\d|2[0-3]):[0-5]\d$/;
        const validWindow = Number(state.days || 1) > 1 || (
            validTime.test(String(state.arrival_time || ''))
            && validTime.test(String(state.departure_time || ''))
            && String(state.arrival_time) < String(state.departure_time)
        );
        if ((this.plannerMissingSlots || []).length || !region || /^\d{4}[-./]/.test(region)
            || !state.start_location || state.start_location === region || !validMode || !validWindow) return false;
        return ['ready_to_generate', 'error'].indexOf(this.plannerStage) > -1
            && this.plannerPromptExplicitlyRequestsCourse(current)
            && this.plannerContextCanGenerateCourse(recentPrompts.join(' '));
    }

    private ensurePlannerStop(stops: any[], stop: any) {
        if (!stop || stops.some((item: any) => item.key === stop.key)) return;
        stops.push({ ...stop });
    }

    private normalizePlannerStops(stops: any[]) {
        let order = ['beach', 'food', 'coast', 'cafe', 'night'];
        return stops
            .filter((stop: any) => stop && stop.key)
            .sort((a: any, b: any) => order.indexOf(a.key) - order.indexOf(b.key))
            .map((stop: any, index: number) => ({
                ...stop,
                status: index < 2 ? 'active' : 'upcoming'
            }));
    }

    private buildPlannerCourseDays(dayOneStops: any[]) {
        let firstDay = dayOneStops.length > 0 ? dayOneStops : this.normalizePlannerStops([
            this.busanPlannerStopTemplates().beach,
            this.busanPlannerStopTemplates().food,
            this.busanPlannerStopTemplates().coast,
            this.busanPlannerStopTemplates().cafe
        ]);
        let days = [
            { label: '1일차', stops: firstDay }
        ];

        if (this.plannerCourseSchedule.indexOf('1박') > -1 || this.plannerCourseSchedule.indexOf('2박') > -1) {
            days.push({
                label: '2일차',
                stops: this.normalizePlannerStops(this.busanPlannerDayTwoStops())
            });
        }

        return days;
    }

    private busanPlannerDayTwoStops() {
        return [
            {
                key: 'day2-breakfast',
                time: '09:30',
                name: '숙소 근처 브런치',
                area: '해운대구 중동',
                tag: '아침 식사',
                move: '도보 12분',
                image: '/assets/places/busan-milmyeon.jpg',
                icon: 'fa-utensils',
                lat: 35.1634,
                lng: 129.164,
                mapLeft: '72%',
                mapTop: '28%'
            },
            {
                key: 'day2-blue-line',
                time: '11:30',
                name: '해운대 블루라인파크',
                area: '해운대구 송정동',
                tag: '해안 열차',
                move: '차량 18분',
                image: '/assets/places/dongbaekseom.jpg',
                icon: 'fa-train',
                lat: 35.1612,
                lng: 129.1716,
                mapLeft: '66%',
                mapTop: '48%'
            },
            {
                key: 'day2-cheongsapo',
                time: '14:30',
                name: '청사포 조개구이',
                area: '해운대구 청사포',
                tag: '점심 식사',
                move: '도보 8분',
                image: '/assets/places/haeundae-beach.jpg',
                icon: 'fa-fish',
                lat: 35.16,
                lng: 129.1917,
                mapLeft: '58%',
                mapTop: '66%'
            },
            {
                key: 'day2-haeridan',
                time: '17:00',
                name: '해리단길 카페',
                area: '해운대구 우동',
                tag: '카페 · 쇼핑',
                move: '차량 14분',
                image: '/assets/places/busan-cafe.jpg',
                icon: 'fa-mug-saucer',
                lat: 35.1636,
                lng: 129.1587,
                mapLeft: '36%',
                mapTop: '72%'
            }
        ];
    }

    private applyPlannerCourseDay(index: number) {
        if (!Array.isArray(this.plannerCourseDays) || this.plannerCourseDays.length === 0) {
            this.plannerStops = [];
            this.refreshPlannerStats();
            return;
        }

        this.plannerCourseDayIndex = Math.max(0, Math.min(index, this.plannerCourseDays.length - 1));
        let day = this.plannerCourseDays[this.plannerCourseDayIndex];
        this.plannerStops = ((day && day.stops) || []).map((stop: any) => ({ ...stop }));
        this.plannerNaverRoutePath = this.plannerStops
            .filter((stop: any) => this.isFiniteNumber(stop.lat) && this.isFiniteNumber(stop.lng))
            .map((stop: any) => ({ lat: Number(stop.lat), lng: Number(stop.lng) }));
        this.plannerCourseTitle = `${this.plannerCourseRegion} ${this.plannerCourseDayIndex + 1}일차`;
        this.refreshPlannerStats();
        if (day && Number(day.totalMoveMinutes || 0) > 0) {
            let visitMinutes = this.plannerStops.reduce((sum: number, stop: any) => sum + Number(stop.durationMinutes || 0), 0);
            this.plannerTotalTime = this.formatPlannerMinutes(Number(day.totalMoveMinutes || 0) + visitMinutes);
        }
        if (day && Number(day.totalDistanceMeters || 0) > 0) {
            this.plannerDistance = this.formatPlannerDistance(Number(day.totalDistanceMeters), false);
        }
        if (day && day.endTime) this.plannerReturnTime = String(day.endTime);
    }

    public plannerActiveDay() {
        return this.plannerCourseDays[this.plannerCourseDayIndex] || {};
    }

    public plannerReturnLabel() {
        let plan = this.plannerActiveDay().returnPlan || {};
        return String(plan.label || '숙소 복귀');
    }

    public plannerReturnNote() {
        let plan = this.plannerActiveDay().returnPlan || {};
        return String(plan.note || '숙소 또는 귀환 동선으로 자연스럽게 마무리해요');
    }

    public plannerQualityScore() {
        return Math.max(0, Math.min(100, Number(this.plannerQuality.score || this.plannerActiveDay().qualityScore || 0)));
    }

    public plannerEvidenceItems() {
        if (!this.plannerCourseReady) return [];
        let quality = this.plannerQuality || {};
        let checks = quality.checks || {};
        let items = [
            `장소 ${this.plannerStops.length}곳을 장소 ID·좌표 데이터 기준으로 구성`,
            `이동 동선 검증 ${checks.simple_route_ok === true ? '통과' : '보완 필요'}`
        ];
        if (Number(quality.condition_fulfillment_rate || 0) > 0) {
            items.push(`요청 조건 반영률 ${Math.round(Number(quality.condition_fulfillment_rate))}%`);
        }
        if (Number(quality.place_data_completeness_rate || 0) > 0) {
            items.push(`장소 데이터 완성도 ${Math.round(Number(quality.place_data_completeness_rate))}%`);
        }
        return items;
    }

    public plannerQualityNotice() {
        let checks = (this.plannerQuality && this.plannerQuality.checks) || {};
        if (checks.opening_hours_ok === false) return '영업시간은 방문 전에 다시 확인해주세요.';
        if (checks.simple_route_ok === false) return '일부 구간은 예상 이동시간을 사용했어요. 지도에서 최종 경로를 확인해주세요.';
        return 'AI는 조건을 정리하고, 장소와 동선은 저장된 데이터와 경로 검증 결과를 사용했어요.';
    }

    public async retryPlannerRequest() {
        let prompt = String(this.plannerRequestState.lastPrompt || '').trim();
        if (!prompt || this.isChatSending) return;
        await this.sendChatPrompt(prompt, this.createChatMessageId('retry'));
    }

    public plannerRouteLegs() {
        return Array.isArray(this.plannerRouteSummary && this.plannerRouteSummary.legs)
            ? this.plannerRouteSummary.legs
            : [];
    }

    public plannerStars(value: any) {
        let rating = Math.max(0, Math.min(5, Number(value || 0)));
        return '★★★★★'.slice(0, Math.round(rating)) + '☆☆☆☆☆'.slice(0, 5 - Math.round(rating));
    }

    public plannerReviewLabel(value: any) {
        let count = Math.max(0, Number(value || 0));
        return count > 0 ? `리뷰 ${count.toLocaleString()}개` : '리뷰 정보 없음';
    }

    public plannerCostLabel(value: any) {
        let cost = Math.max(0, Number(value || 0));
        return cost > 0 ? `예상 ${Math.round(cost).toLocaleString()}원` : '무료 또는 현장 확인';
    }

    public plannerStopHasImage(stop: any, index: number) {
        if (!stop || stop.imageBroken || !String(stop.image || '').trim()) return false;
        let source = String(stop.image || '').trim();
        let firstMatch = (this.plannerStops || []).findIndex((item: any) => String(item && item.image || '').trim() === source);
        return firstMatch < 0 || firstMatch === index;
    }

    public markPlannerStopImageBroken(stop: any) {
        if (stop) stop.imageBroken = true;
    }

    public plannerStopVisualIcon(stop: any) {
        let category = `${stop && stop.tag || ''} ${(stop && stop.tags || []).join(' ')} ${stop && stop.name || ''}`;
        if (/카페|커피|디저트|베이커리/.test(category)) return 'fa-mug-hot';
        if (/식사|맛집|음식|레스토랑|시장/.test(category)) return 'fa-utensils';
        if (/바다|해변|호수|공원|자연|산책/.test(category)) return 'fa-water';
        if (/야경|전망|사진/.test(category)) return 'fa-camera-retro';
        if (/문화|박물관|미술관|체험/.test(category)) return 'fa-landmark';
        return stop && stop.icon ? stop.icon : 'fa-location-dot';
    }

    public plannerStopVisualClass(stop: any, index: number) {
        let category = `${stop && stop.tag || ''} ${(stop && stop.tags || []).join(' ')} ${stop && stop.name || ''}`;
        if (/카페|커피|디저트|베이커리/.test(category)) return 'visual-cafe';
        if (/식사|맛집|음식|레스토랑|시장/.test(category)) return 'visual-food';
        if (/바다|해변|호수|공원|자연|산책/.test(category)) return 'visual-nature';
        if (/야경|전망|사진/.test(category)) return 'visual-night';
        return `visual-place visual-place-${index % 4}`;
    }

    public hasPlannerMultipleDays() {
        return Array.isArray(this.plannerCourseDays) && this.plannerCourseDays.length > 1;
    }

    public plannerActiveDayLabel() {
        return `${this.plannerCourseDayIndex + 1}일차`;
    }

    public plannerActiveDayDateLabel() {
        let label = this.plannerActiveDayLabel();
        let activeDay = this.plannerActiveDay();
        let value = String(activeDay && activeDay.date || '').trim();
        if (!value) {
            let startDate = String(this.plannerTravelState && this.plannerTravelState.start_date || '').trim();
            let parts = startDate.match(/^(\d{4})-(\d{2})-(\d{2})$/);
            if (parts) {
                let fallback = new Date(Number(parts[1]), Number(parts[2]) - 1, Number(parts[3]) + this.plannerCourseDayIndex);
                value = this.formatIsoDate(fallback);
            }
        }
        if (!value) return label;
        let parts = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
        if (!parts) return `${label} · ${value}`;
        let date = new Date(Number(parts[1]), Number(parts[2]) - 1, Number(parts[3]));
        let weekday = ['일', '월', '화', '수', '목', '금', '토'][date.getDay()];
        return `${label} · ${Number(parts[2])}월 ${Number(parts[3])}일 (${weekday})`;
    }

    public plannerNextDayButtonLabel() {
        if (!this.hasPlannerMultipleDays()) return '';
        let next = (this.plannerCourseDayIndex + 1) % this.plannerCourseDays.length;
        return `${next + 1}일차 코스 보기`;
    }

    public async showNextPlannerDay() {
        if (!this.hasPlannerMultipleDays()) return;
        this.applyPlannerCourseDay((this.plannerCourseDayIndex + 1) % this.plannerCourseDays.length);
        this.refreshPlannerRouteWithGoogle();
        await this.service.render();
    }

    public async closePlannerCoursePreview() {
        if (!this.plannerCourseReady) return;
        if (this.plannerCourseExpanded) {
            this.plannerCourseExpanded = false;
            await this.service.render();
            this.scrollToPlannerPreview();
            return;
        }
        this.plannerCourseVisible = false;
        this.plannerMapExpanded = false;
        await this.service.render();
        this.scrollToLatest();
    }

    public async openPlannerCoursePreview() {
        if (!this.plannerCourseReady) return;
        this.plannerCourseVisible = true;
        this.plannerCourseExpanded = false;
        await this.service.render();
        this.scrollToPlannerPreview();
    }

    public async togglePlannerCourseExpanded() {
        if (!this.plannerCourseReady || !this.plannerCourseVisible) return;
        this.plannerCourseExpanded = !this.plannerCourseExpanded;
        await this.service.render();
        if (!this.plannerCourseExpanded) this.scrollToPlannerPreview();
    }

    public handlePlannerTouchStart(event: any) {
        let touch = event && event.touches && event.touches[0] ? event.touches[0] : null;
        this.plannerTouchStartX = touch ? Number(touch.clientX || 0) : 0;
    }

    public async handlePlannerTouchEnd(event: any) {
        if (!this.hasPlannerMultipleDays() || !this.plannerTouchStartX) return;
        let touch = event && event.changedTouches && event.changedTouches[0] ? event.changedTouches[0] : null;
        let endX = touch ? Number(touch.clientX || 0) : this.plannerTouchStartX;
        let delta = endX - this.plannerTouchStartX;
        this.plannerTouchStartX = 0;
        if (Math.abs(delta) < 42) return;
        if (delta < 0) {
            await this.showNextPlannerDay();
            return;
        }
        this.applyPlannerCourseDay((this.plannerCourseDayIndex - 1 + this.plannerCourseDays.length) % this.plannerCourseDays.length);
        this.refreshPlannerRouteWithGoogle();
        await this.service.render();
    }

    private refreshPlannerRouteWithGoogle() {
        if (!this.plannerCourseReady || !Array.isArray(this.plannerStops) || this.plannerStops.length < 2) {
            this.schedulePlannerNaverMapRender();
            return;
        }

        let routeStops = this.plannerStops
            .map((stop: any, index: number) => ({
                stop,
                index,
                position: { lat: Number(stop.lat), lng: Number(stop.lng) }
            }))
            .filter((item: any) => this.isFiniteNumber(item.stop.lat) && this.isFiniteNumber(item.stop.lng));
        this.plannerNaverRoutePath = routeStops.map((item: any) => item.position);
        this.schedulePlannerNaverMapRender();
        if (routeStops.length < 2) return;

        let token = ++this.plannerNaverRouteToken;
        this.plannerRouteSource = '네이버 지도 교통편 확인 중';
        this.loadNaverMapsScript().then(async (google: any) => {
            if (token !== this.plannerNaverRouteToken) return;
            let service = this.isNaverMapsReady(google) ? new google.maps.DirectionsService() : null;
            let resolvedLegs: any[] = [];

            for (let index = 0; index < routeStops.length - 1; index++) {
                if (token !== this.plannerNaverRouteToken) return;
                let from = routeStops[index];
                let to = routeStops[index + 1];
                let preferredMode = this.plannerRouteTravelModeForLeg(from.stop, to.stop);
                let naverLeg = service
                    ? await this.requestPlannerNaverLeg(service, google, from.position, to.position, preferredMode)
                    : null;
                resolvedLegs.push(naverLeg
                    ? {
                        ...naverLeg,
                        fromIndex: from.index,
                        toIndex: to.index,
                        from: from.stop,
                        to: to.stop,
                        source: 'naver_maps'
                    }
                    : {
                        ...this.estimatedPlannerLeg(from.stop, to.stop, preferredMode),
                        fromIndex: from.index,
                        toIndex: to.index,
                        from: from.stop,
                        to: to.stop,
                        source: 'distance_estimate'
                    });
            }

            if (token !== this.plannerNaverRouteToken) return;
            this.applyPlannerResolvedLegs(routeStops, resolvedLegs);
            await this.service.render();
            this.schedulePlannerNaverMapRender();
        }).catch(async () => {
            if (token !== this.plannerNaverRouteToken) return;
            let estimatedLegs = routeStops.slice(0, -1).map((from: any, index: number) => {
                let to = routeStops[index + 1];
                let mode = this.plannerRouteTravelModeForLeg(from.stop, to.stop);
                return {
                    ...this.estimatedPlannerLeg(from.stop, to.stop, mode),
                    fromIndex: from.index,
                    toIndex: to.index,
                    from: from.stop,
                    to: to.stop,
                    source: 'distance_estimate'
                };
            });
            this.applyPlannerResolvedLegs(routeStops, estimatedLegs);
            await this.service.render();
            this.schedulePlannerNaverMapRender();
        });
    }

    private async requestPlannerNaverLeg(service: any, google: any, origin: any, destination: any, mode: string): Promise<any> {
        let request = (routeMode: string) => new Promise((resolve) => {
            service.route({
                origin,
                destination,
                travelMode: google.maps.TravelMode[routeMode],
                provideRouteAlternatives: false
            }, (result: any, status: string) => {
                let route = result && result.routes && result.routes[0] ? result.routes[0] : null;
                let leg = route && route.legs && route.legs[0] ? route.legs[0] : null;
                if (status !== 'OK' || !route || !leg) {
                    resolve(null);
                    return;
                }
                resolve({
                    mode: routeMode,
                    seconds: Number(leg.duration && leg.duration.value ? leg.duration.value : 0),
                    meters: Number(leg.distance && leg.distance.value ? leg.distance.value : 0),
                    durationText: leg.duration && leg.duration.text ? leg.duration.text : '',
                    distanceText: leg.distance && leg.distance.text ? leg.distance.text : '',
                    path: this.plannerPathFromNaverRoute(route)
                });
            });
        });

        let result: any = await request(mode);
        if (!result && mode !== 'DRIVING') result = await request('DRIVING');
        return result;
    }

    private plannerPathFromNaverRoute(route: any) {
        return ((route && route.overview_path) || []).map((point: any) => ({
            lat: typeof point.lat === 'function' ? point.lat() : Number(point.lat),
            lng: typeof point.lng === 'function' ? point.lng() : Number(point.lng)
        })).filter((point: any) => this.isFiniteNumber(point.lat) && this.isFiniteNumber(point.lng));
    }

    private estimatedPlannerLeg(from: any, to: any, mode: string) {
        let distance = this.distanceKm(from && from.lat, from && from.lng, to && to.lat, to && to.lng);
        let kilometers = distance === null ? 1 : Math.max(0.1, distance);
        let speed = mode === 'WALKING' ? 4.2 : (mode === 'TRANSIT' ? 18 : 24);
        let overhead = mode === 'WALKING' ? 0 : (mode === 'TRANSIT' ? 8 : 4);
        let minutes = Math.max(2, Math.round((kilometers / speed) * 60 + overhead));
        return {
            mode,
            seconds: minutes * 60,
            meters: Math.round(kilometers * 1000),
            durationText: `${minutes}분`,
            distanceText: this.formatPlannerDistance(kilometers * 1000, false),
            path: [
                { lat: Number(from.lat), lng: Number(from.lng) },
                { lat: Number(to.lat), lng: Number(to.lng) }
            ]
        };
    }

    private applyPlannerResolvedLegs(routeStops: any[], legs: any[]) {
        let totalSeconds = 0;
        let totalMeters = 0;
        let routePath: any[] = [];
        let naverLegCount = 0;

        legs.forEach((leg: any) => {
            let seconds = Math.max(0, Number(leg && leg.seconds ? leg.seconds : 0));
            let meters = Math.max(0, Number(leg && leg.meters ? leg.meters : 0));
            let stop = this.plannerStops[leg.fromIndex];
            totalSeconds += seconds;
            totalMeters += meters;
            if (leg.source === 'naver_maps') naverLegCount++;
            if (stop) {
                stop.move = `${this.plannerRouteModeLabel(leg.mode)} ${this.formatPlannerLegDuration(seconds)}`;
                stop.naverDistance = this.formatPlannerDistance(meters, false);
            }
            (leg.path || []).forEach((point: any) => {
                let previous = routePath[routePath.length - 1];
                if (!previous || previous.lat !== point.lat || previous.lng !== point.lng) routePath.push(point);
            });
        });

        let itinerarySeconds = this.applyPlannerArrivalTimes(routeStops, legs);
        if (totalMeters > 0) this.plannerDistance = this.formatPlannerDistance(totalMeters);
        if (itinerarySeconds > 0) this.plannerTotalTime = this.formatPlannerDuration(itinerarySeconds);
        this.plannerNaverRoutePath = routePath.length > 1
            ? routePath
            : routeStops.map((item: any) => item.position);
        this.plannerRouteSource = naverLegCount === legs.length
            ? '네이버 지도 자동차 경로 기준'
            : (naverLegCount > 0 ? '네이버 지도 · 일부 거리 추정' : '거리·교통편 예상');
        this.plannerMapLabel = '실제 장소 동선';
        this.plannerRouteSummary = {
            source: naverLegCount === legs.length ? 'naver_maps' : 'mixed_route',
            travel_mode: 'mixed',
            total_seconds: totalSeconds,
            itinerary_seconds: itinerarySeconds,
            total_meters: totalMeters,
            legs: legs.map((leg: any, index: number) => ({
                order: index + 1,
                from: leg.from && leg.from.name ? leg.from.name : '',
                to: leg.to && leg.to.name ? leg.to.name : '',
                mode: String(leg.mode || '').toLowerCase(),
                source: leg.source,
                duration_text: leg.durationText,
                duration_seconds: leg.seconds,
                distance_text: leg.distanceText,
                distance_meters: leg.meters
            }))
        };
        this.syncPlannerActiveDayStops();
    }

    private applyPlannerArrivalTimes(routeStops: any[], legs: any[]) {
        if (!routeStops.length) return 0;
        let startMinutes = this.plannerMinutesFromTime(routeStops[0].stop.time, 10 * 60);
        let currentMinutes = startMinutes;

        routeStops.forEach((item: any, index: number) => {
            if (index > 0 && item.stop.timeLocked) {
                let lockedMinutes = this.plannerMinutesFromTime(item.stop.time, currentMinutes);
                if (lockedMinutes >= currentMinutes) currentMinutes = lockedMinutes;
            }
            item.stop.time = this.timeFromMinutes(currentMinutes);
            if (index >= routeStops.length - 1) return;
            let leg = legs[index] || {};
            let travelMinutes = Math.max(1, Math.round(Number(leg.seconds || 0) / 60));
            currentMinutes = this.roundPlannerMinutes(currentMinutes + this.plannerStopDurationMinutes(item.stop) + travelMinutes);
        });

        let endMinutes = currentMinutes + this.plannerStopDurationMinutes(routeStops[routeStops.length - 1].stop);
        this.plannerReturnTime = this.timeFromMinutes(endMinutes);
        return Math.max(60, (endMinutes - startMinutes) * 60);
    }

    private plannerMinutesFromTime(value: any, fallback: number) {
        let match = String(value || '').match(/^(\d{1,2}):(\d{2})$/);
        if (!match) return fallback;
        return Math.max(0, Math.min(1439, Number(match[1]) * 60 + Number(match[2])));
    }

    private roundPlannerMinutes(value: number) {
        return Math.round(value / 5) * 5;
    }

    private plannerStopDurationMinutes(stop: any) {
        let text = `${stop && stop.name ? stop.name : ''} ${stop && stop.tag ? stop.tag : ''}`;
        if (/숙소|체크인/.test(text)) return 30;
        if (/아침|점심|저녁|식사|맛집|브런치/.test(text)) return 70;
        if (/카페|디저트/.test(text)) return 60;
        if (/열차|레포츠|액티비티/.test(text)) return 90;
        if (/쇼핑/.test(text)) return 75;
        if (/산책|해변|바다|공원|문화|전시|관광/.test(text)) return 90;
        return 75;
    }

    private plannerRouteTravelModeForLeg(from: any, to: any) {
        let move = String(from && from.move ? from.move : '');
        if (/지하철|버스|대중교통|열차/.test(move)) return 'TRANSIT';
        if (/차량|자동차|택시|렌터카/.test(move)) return 'DRIVING';
        if (/도보|걷기/.test(move)) return 'WALKING';
        let distance = this.distanceKm(from && from.lat, from && from.lng, to && to.lat, to && to.lng);
        return distance !== null && distance <= 1.4 ? 'WALKING' : 'TRANSIT';
    }

    private plannerRouteModeLabel(mode: string) {
        if (mode === 'DRIVING') return '차량';
        if (mode === 'TRANSIT') return '대중교통';
        return '도보';
    }

    private syncPlannerActiveDayStops() {
        if (!this.plannerCourseDays[this.plannerCourseDayIndex]) return;
        this.plannerCourseDays[this.plannerCourseDayIndex].stops = this.plannerStops.map((stop: any) => ({ ...stop }));
    }

    public async openPlannerMap() {
        this.plannerMapExpanded = true;
        await this.service.render();
        this.schedulePlannerNaverMapRender();
    }

    public async closePlannerMap() {
        if (!this.plannerMapExpanded) return;
        this.plannerMapExpanded = false;
        await this.service.render();
    }

    public fitPlannerMap() {
        let google: any = this.naverMapsAdapter;
        let map = this.plannerExpandedNaverMap;
        if (!map || !google || !google.maps || typeof google.maps.LatLngBounds !== 'function') return;
        let path = this.plannerNaverRoutePath.length > 1
            ? this.plannerNaverRoutePath
            : this.plannerStops.filter((stop: any) => this.isFiniteNumber(stop.lat) && this.isFiniteNumber(stop.lng));
        if (!path.length) return;
        let bounds = new google.maps.LatLngBounds();
        path.forEach((point: any) => bounds.extend({ lat: Number(point.lat), lng: Number(point.lng) }));
        map.fitBounds(bounds, 42);
    }

    private schedulePlannerNaverMapRender() {
        if (typeof window === 'undefined') return;
        let root: any = window as any;
        let frame = root.requestAnimationFrame || ((callback: any) => setTimeout(callback, 16));
        frame(() => this.renderPlannerNaverMaps());
    }

    private async renderPlannerNaverMaps() {
        if (!this.plannerCourseReady || typeof document === 'undefined') return;
        let google = await this.loadNaverMapsScript();
        if (!this.isNaverMapsReady(google)) return;
        this.renderPlannerNaverMapTarget(google, '.planner-naver-map', false);
        if (this.plannerMapExpanded) this.renderPlannerNaverMapTarget(google, '.planner-naver-map-expanded', true);
    }

    private renderPlannerNaverMapTarget(google: any, selector: string, expanded: boolean) {
        let element: any = document.querySelector(`.access-shell ${selector}`);
        if (!element || !element.offsetWidth || !element.offsetHeight) return;
        let mapStops = (this.plannerStops || [])
            .map((stop: any, index: number) => ({ stop, index }))
            .filter((item: any) => this.isFiniteNumber(item.stop.lat) && this.isFiniteNumber(item.stop.lng));
        let points = mapStops.map((item: any) => ({ lat: Number(item.stop.lat), lng: Number(item.stop.lng) }));
        if (!points.length) return;

        let map = expanded ? this.plannerExpandedNaverMap : this.plannerNaverMap;
        let mapElement = expanded ? this.plannerExpandedNaverMapElement : this.plannerNaverMapElement;
        if (!map || mapElement !== element) {
            map = new google.maps.Map(element, {
                center: points[0],
                zoom: points.length > 1 ? 13 : 15,
                clickableIcons: false,
                disableDefaultUI: true,
                gestureHandling: expanded ? 'greedy' : 'none',
                keyboardShortcuts: expanded,
                zoomControl: expanded,
                streetViewControl: false,
                mapTypeControl: false,
                fullscreenControl: false
            });
            if (expanded) {
                this.plannerExpandedNaverMap = map;
                this.plannerExpandedNaverMapElement = element;
            } else {
                this.plannerNaverMap = map;
                this.plannerNaverMapElement = element;
            }
        }

        let markers = expanded ? this.plannerExpandedNaverMarkers : this.plannerNaverMarkers;
        markers.forEach((marker: any) => marker.setMap(null));
        markers = points.map((position: any, index: number) => {
            let stop = mapStops[index].stop || {};
            let marker = new google.maps.Marker({
                map,
                position,
                title: stop.name || `${mapStops[index].index + 1}번 장소`,
                label: {
                    text: String(mapStops[index].index + 1),
                    color: '#ffffff',
                    fontSize: expanded ? '11px' : '8px',
                    fontWeight: '800'
                },
                icon: {
                    path: google.maps.SymbolPath.CIRCLE,
                    scale: expanded ? 12 : 9,
                    fillColor: '#F20D19',
                    fillOpacity: 1,
                    strokeColor: '#ffffff',
                    strokeWeight: expanded ? 3 : 2
                }
            });
            if (expanded && typeof google.maps.InfoWindow === 'function' && marker.addListener) {
                let next = this.plannerRouteLegs()[index] || {};
                let tooltip = new google.maps.InfoWindow({
                    content: `<div class="planner-map-tooltip"><strong>${this.escapeMapText(stop.name || '')}</strong><small>${this.escapeMapText(stop.timePeriod || stop.time || '')}${next.duration_text ? ` · ${this.escapeMapText(next.duration_text)}` : ''}</small></div>`
                });
                marker.addListener('click', () => tooltip.open({ map, anchor: marker }));
            }
            return marker;
        });
        if (expanded) this.plannerExpandedNaverMarkers = markers;
        else this.plannerNaverMarkers = markers;

        let previousLine = expanded ? this.plannerExpandedNaverRouteLine : this.plannerNaverRouteLine;
        if (previousLine) previousLine.setMap(null);
        let path = this.plannerNaverRoutePath.length > 1 ? this.plannerNaverRoutePath : points;
        let routeLine = new google.maps.Polyline({
            map,
            path,
            strokeColor: '#F20D19',
            strokeOpacity: 0.9,
            strokeWeight: expanded ? 5 : 3,
            geodesic: false
        });
        if (expanded) this.plannerExpandedNaverRouteLine = routeLine;
        else this.plannerNaverRouteLine = routeLine;

        let bounds = new google.maps.LatLngBounds();
        path.forEach((point: any) => bounds.extend(point));
        points.forEach((point: any) => bounds.extend(point));
        if (points.length > 1) map.fitBounds(bounds, expanded ? 42 : 10);
        else {
            map.setCenter(points[0]);
            map.setZoom(15);
        }
    }

    private formatPlannerDuration(seconds: any) {
        let minutes = Math.max(1, Math.round(Number(seconds || 0) / 60));
        let hours = Math.floor(minutes / 60);
        let remain = minutes % 60;
        if (hours > 0 && remain > 0) return `약 ${hours}시간 ${remain}분`;
        if (hours > 0) return `약 ${hours}시간`;
        return `약 ${minutes}분`;
    }

    private escapeMapText(value: any) {
        return String(value || '').replace(/[&<>"']/g, (char: string) => ({
            '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
        } as any)[char] || char);
    }

    private formatPlannerLegDuration(seconds: any) {
        let minutes = Math.max(1, Math.round(Number(seconds || 0) / 60));
        let hours = Math.floor(minutes / 60);
        let remain = minutes % 60;
        if (hours > 0 && remain > 0) return `${hours}시간 ${remain}분`;
        if (hours > 0) return `${hours}시간`;
        return `${minutes}분`;
    }

    private formatPlannerDistance(meters: any, approximate: boolean = true) {
        let value = Math.max(0, Number(meters || 0));
        let prefix = approximate ? '약 ' : '';
        if (value < 1000) return `${prefix}${Math.round(value)}m`;
        return `${prefix}${(value / 1000).toFixed(1)}km`;
    }

    private plannerAllStops() {
        if (!Array.isArray(this.plannerCourseDays) || this.plannerCourseDays.length === 0) return this.plannerStops || [];
        return this.plannerCourseDays.reduce((items: any[], day: any) => {
            return items.concat(((day && day.stops) || []).map((stop: any) => ({ ...stop, dayLabel: day.label || '' })));
        }, []);
    }

    private plannerCoursePlacesText() {
        if (!Array.isArray(this.plannerCourseDays) || this.plannerCourseDays.length === 0) {
            return (this.plannerStops || []).map((stop: any) => stop.name).join('\n');
        }
        return this.plannerCourseDays.map((day: any) => {
            let stops = ((day && day.stops) || []).map((stop: any) => `${stop.time || ''} ${stop.name}`).join('\n');
            return `${day.label || '일정'}\n${stops}`;
        }).join('\n\n');
    }

    private plannerCourseSaveTitle() {
        if (this.hasPlannerMultipleDays()) return `${this.plannerCourseBaseTitle} ${this.plannerCourseSchedule} 코스`;
        return this.plannerCourseTitle;
    }

    private plannerCourseSaveId(stops: any[]) {
        let threadId = String(this.activeChatThreadId || '').trim();
        if (threadId) return `planner-course-${threadId}`.slice(0, 64);
        let source = [
            this.plannerCourseRegion,
            this.plannerCourseSchedule,
            ...stops.map((stop: any) => stop && (stop.placeId || stop.key || stop.name) ? (stop.placeId || stop.key || stop.name) : '')
        ].join('|');
        let hash = 0;
        for (let index = 0; index < source.length; index++) hash = ((hash << 5) - hash + source.charCodeAt(index)) | 0;
        return `planner-course-${Math.abs(hash).toString(36)}`;
    }

    private plannerCourseToSavedCard(stops: any[], courseId: string = '') {
        let title = this.plannerCourseSaveTitle();
        let routePayload = this.plannerCourseRoutePayload(stops);
        return {
            id: courseId || this.plannerCourseSaveId(stops),
            title,
            location: this.plannerCourseRegion,
            summary: `${this.plannerCoursePlacesText().replace(/\n/g, ' · ').slice(0, 90)}${this.plannerCoursePlacesText().length > 90 ? '...' : ''}`,
            duration: this.plannerCourseSchedule,
            distance: this.plannerDistance,
            rating: '4.9',
            price: '',
            tone: 'tone-blue',
            icon: 'fa-route',
            image: stops[0] && stops[0].image ? stops[0].image : '/assets/places/haeundae-beach.jpg',
            saved: false,
            mine: true,
            source: 'mine',
            places: routePayload.places,
            route: routePayload.route,
            tags: this.uniqueTags(['여행', this.plannerCourseRegion, this.plannerCourseSchedule, 'AI플래너', ...stops.map((stop: any) => stop.name)])
        };
    }

    private departurePlannerDayDate(dayIndex: number) {
        const start = String(this.plannerTravelState.start_date || '');
        if (!/^\d{4}-\d{2}-\d{2}$/.test(start)) return '';
        const date = new Date(start + 'T12:00:00');
        date.setDate(date.getDate() + dayIndex);
        return this.formatIsoDate(date);
    }

    private plannerCourseRoutePayload(stops: any[]) {
        let days = Array.isArray(this.plannerCourseDays) && this.plannerCourseDays.length > 0
            ? this.plannerCourseDays
            : [{ label: '1일차', stops: stops || [] }];
        let places: any[] = [];
        days.forEach((day: any, dayIndex: number) => {
            ((day && day.stops) || []).forEach((stop: any, index: number) => {
                places.push({
                    place_id: stop.placeId || stop.key || '',
                    provider_place_id: stop.providerPlaceId || '',
                    place_provider: stop.placeProvider || '',
                    day: dayIndex + 1,
                    day_label: day && day.label ? day.label : `${dayIndex + 1}일차`,
                    date: day.date || this.departurePlannerDayDate(dayIndex),
                    order: index + 1,
                    order_index: places.length + 1,
                    time: stop.time || '',
                    visit_time: stop.time || '',
                    name: stop.name || '',
                    area: stop.area || '',
                    address: stop.address || stop.area || '',
                    tag: stop.tag || '',
                    category: stop.category || stop.tag || '',
                    item_type: 'place',
                    memo: stop.tag || '',
                    move: stop.move || '',
                    distance: stop.naverDistance || '',
                    image: stop.image || '',
                    lat: this.isFiniteNumber(stop.lat) ? Number(stop.lat) : null,
                    lng: this.isFiniteNumber(stop.lng) ? Number(stop.lng) : null,
                    latitude: this.isFiniteNumber(stop.lat) ? Number(stop.lat) : null,
                    longitude: this.isFiniteNumber(stop.lng) ? Number(stop.lng) : null
                });
            });
        });
        return {
            places,
            route: {
                source: 'mine',
                title: this.plannerCourseSaveTitle(),
                region: this.plannerCourseRegion,
                schedule: this.plannerCourseSchedule,
                start_date: this.plannerTravelState.start_date || '',
                end_date: this.plannerTravelState.end_date || '',
                active_day: this.plannerCourseDayIndex + 1,
                total_time: this.plannerTotalTime,
                total_distance: this.plannerDistance,
                route_source: this.plannerRouteSource,
                map_label: this.plannerMapLabel,
                google: this.plannerRouteSummary || {},
                days: days.map((day: any, index: number) => ({
                    day: index + 1,
                    label: day && day.label ? day.label : `${index + 1}일차`,
                    stop_count: ((day && day.stops) || []).length
                }))
            }
        };
    }

    private refreshPlannerStats() {
        let hasNight = this.plannerStops.some((stop: any) => stop.key === 'night');
        let hasCafe = this.plannerStops.some((stop: any) => stop.key === 'cafe');
        let count = this.plannerStops.length;

        this.plannerTotalTime = hasNight ? '약 9시간 30분' : (hasCafe ? '약 7시간 20분' : '약 5시간 40분');
        this.plannerDistance = hasNight ? '약 18.4km' : (count >= 4 ? '약 12.6km' : '약 8.2km');
        this.plannerReturnTime = hasNight ? '21:00' : (hasCafe ? '20:20' : '18:30');
        this.plannerMapRoutePoints = this.plannerStops
            .map((stop: any) => `${String(stop.mapLeft || '50%').replace('%', '')},${String(stop.mapTop || '50%').replace('%', '')}`)
            .join(' ');
        this.schedulePlannerNaverMapRender();
    }

    private resetPlannerPreview() {
        this.plannerCourseReady = false;
        this.plannerCourseVisible = false;
        this.plannerCourseExpanded = false;
        this.isPlannerGenerating = false;
        this.plannerCourseBaseTitle = '내 부산 여행';
        this.plannerCourseDayIndex = 0;
        this.plannerCourseDays = [];
        this.plannerQuality = {};
        this.plannerTravelerStyle = '취향 중심';
        this.plannerNaverRouteToken++;
        this.plannerMapExpanded = false;
        this.plannerNaverRoutePath = [];
        this.plannerRouteSource = '기본 동선';
        this.plannerRouteSummary = {};
        this.plannerCourseTitle = '내 부산 여행 1일차 코스';
        this.plannerCourseRegion = '부산 해운대구';
        this.plannerCourseSchedule = '2박 3일';
        this.plannerMapLabel = '해운대구 동선';
        this.plannerTotalTime = '약 7시간';
        this.plannerDistance = '약 12.6km';
        this.plannerReturnTime = '20:20';
        this.plannerMapRoutePoints = '';
        this.plannerStops = [];
    }

    private resetPlannerConversationState() {
        this.stopPlannerProgress();
        this.plannerTravelState = {};
        this.plannerRequestState = { status: 'idle', lastPrompt: '', errorMessage: '' };
        this.plannerStage = 'collecting';
        this.plannerAction = 'ask_clarification';
        this.plannerMissingSlots = [];
        this.plannerWarnings = [];
        this.plannerSuggestedReplies = [];
        this.plannerDestinationCandidates = [];
        this.plannerFailureStage = '';
        this.plannerGenerationStep = 0;
    }

    public plannerTravelSummaryItems() {
        let state = this.plannerTravelState || {};
        let values: string[] = [];
        const areaKey = (value: any) => String(value || '').replace(/서울특별시|서울시/g, '서울').replace(/\s+/g, '').replace(/^서울(?=[가-힣]+구$)/, '').trim();
        let region = String(state.region || '');
        if (region) values.push(region);
        const start = String(state.start_location || '');
        if (start && areaKey(start) !== areaKey(region)) values.push(`시작 ${start}`);
        let date = this.plannerSummaryDateParts(state.start_date);
        if (date) values.push(`${date.month}.${date.day}` + (Number(state.days) > 1 ? ` · ${Number(state.days) - 1}박 ${state.days}일` : ' · 당일'));
        else if (state.days) values.push(Number(state.days) === 1 ? '당일' : `${Number(state.days) - 1}박 ${state.days}일`);
        if (Array.isArray(state.companions) && state.companions.length) values.push(state.companions.join('·'));
        if (state.event_selection && state.event_selection.name) values.push(String(state.event_selection.name));
        if (state.transport) values.push(String(state.transport));
        // Optional conditions stay in state for editing without crowding the summary.
        values = values.filter((value, index, rows) => rows.findIndex(item => areaKey(item) === areaKey(value)) === index);
        return values;
    }

    public startTravelConditionDrag(event: any) {
        let target = event && event.currentTarget as HTMLElement;
        if (!target || event.pointerType === 'touch' || event.button !== 0) return;
        this.travelConditionDragActive = true;
        this.travelConditionDragStartX = Number(event.clientX || 0);
        this.travelConditionDragScrollLeft = target.scrollLeft;
        target.classList.add('dragging');
        if (typeof target.setPointerCapture === 'function') target.setPointerCapture(event.pointerId);
    }

    public moveTravelConditionDrag(event: any) {
        if (!this.travelConditionDragActive || event.pointerType === 'touch') return;
        let target = event && event.currentTarget as HTMLElement;
        if (!target) return;
        let delta = Number(event.clientX || 0) - this.travelConditionDragStartX;
        if (Math.abs(delta) > 2 && typeof event.preventDefault === 'function') event.preventDefault();
        target.scrollLeft = this.travelConditionDragScrollLeft - delta;
    }

    public endTravelConditionDrag(event: any) {
        if (!this.travelConditionDragActive || event.pointerType === 'touch') return;
        let target = event && event.currentTarget as HTMLElement;
        this.travelConditionDragActive = false;
        if (target) {
            target.classList.remove('dragging');
            if (
                typeof target.releasePointerCapture === 'function'
                && typeof target.hasPointerCapture === 'function'
                && target.hasPointerCapture(event.pointerId)
            ) target.releasePointerCapture(event.pointerId);
        }
    }

    private plannerSummaryDateParts(value: any) {
        let match = String(value || '').trim().match(/^(\d{4})-(\d{2})-(\d{2})$/);
        if (!match) return null;
        return {
            year: Number(match[1]),
            month: Number(match[2]),
            day: Number(match[3])
        };
    }

    public hasPlannerTravelSummary() {
        return this.plannerTravelSummaryItems().length > 0;
    }

    public plannerStageLabel() {
        let labels: any = {
            collecting: '여행 조건 확인 중',
            collecting_destination_preferences: '여행지 추천 준비 중',
            destination_candidates_ready: '여행지 선택 필요',
            destination_selected: '여행지 선택 완료',
            ready_to_generate: '코스 생성 전 확인',
            checking_feasibility: '후보·동선 확인 중',
            generating: '후보·동선 확인 중',
            draft_ready: '코스 생성 가능',
            editing_conditions: '수정할 조건 선택',
            revising: '코스 수정 중',
            completed: '코스 확정',
            error: '확인 필요'
        };
        return labels[this.plannerStage] || '여행 조건 확인 중';
    }

    public plannerFailureLabel() {
        // Missing travel conditions are already explained by the assistant's
        // next question. Showing a second generic error here can contradict
        // that question (for example, asking for a start point while warning
        // that destination and date are missing).
        if (this.plannerFailureStage === 'travel_conditions'
            && (this.plannerStage === 'collecting' || this.plannerAction === 'ask_clarification')) return '';
        let labels: any = {
            travel_conditions: '여행 조건을 조금 더 알려주세요.',
            place_search: '장소 수를 줄이거나 주변 지역으로 넓혀볼까요?',
            directions: '가까운 장소 중심으로 동선을 바꿔볼까요?',
            response_format: '여행 조건은 보관했어요. 대화를 이어가 주세요.',
            model_config: '여행 조건은 보관했어요. 잠시 후 이어서 진행해주세요.',
            revision_target: '수정할 날짜나 장소를 더 구체적으로 알려주세요.'
        };
        return labels[this.plannerFailureStage] || '';
    }

    public async generatePlannerFromSummary() {
        if (this.isChatSending) return;
        await this.sendChatPrompt('이 조건으로 코스 만들어줘');
    }

    public async editPlannerConditions() {
        if (this.isChatSending) return;
        await this.sendChatPrompt('조건 수정');
    }

    public async selectPlannerSuggestedReply(reply: any) {
        if (this.isChatSending || !reply) return;
        let prompt = typeof reply === 'string' ? reply : String(reply.prompt || reply.label || '');
        if (!prompt.trim()) return;
        await this.sendChatPrompt(prompt.trim());
    }

    public showPlannerQuickStartReplies() {
        let hasUserMessage = (this.messages || []).some((message: any) => {
            return message && message.role === 'user' && !message.loading;
        });
        return !hasUserMessage
            && !this.isPlannerGenerating
            && !this.plannerCourseReady
            && !this.hasPlannerTravelSummary();
    }

    public plannerComposerQuickReplies() {
        if (this.plannerCourseReady) return [];
        if (this.plannerSuggestedReplies.length) return this.plannerSuggestedReplies;
        return this.showPlannerQuickStartReplies() ? this.plannerQuickStartReplies : [];
    }

    public async selectPlannerComposerQuickReply(reply: any) {
        if (this.isChatSending || !reply) return;
        let prompt = typeof reply === 'string' ? reply : String(reply.prompt || reply.label || '');
        if (!prompt.trim()) return;
        await this.sendChatPrompt(prompt.trim(), this.createChatMessageId('quick'));
    }

    private plannerRequiredConditionsMissing() {
        const state = this.plannerTravelState || {};
        const location = (value: any) => {
            if (typeof value !== 'string' || !value.trim()) return false;
            const text = value.trim();
            return !/^(?:오늘|내일|모레|당일|미정|모름|없음|어디든|도보|대중교통|자동차)$/.test(text)
                && !/^(?:오전|오후)?[\d\s./:~T+Z\-년월일박시분]+(?:동안|일정)?$/.test(text);
        };
        const missing: string[] = [];
        const region = location(state.region) ? state.region : state.destination;
        if (!location(region)) missing.push('region');
        const days = Number(state.days);
        const date = (value: any) => typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value);
        if (!(Number.isInteger(days) && days >= 1 && days <= 14) && !(date(state.start_date) && date(state.end_date))) missing.push('days');
        if (!location(state.start_location) || state.start_location === region) missing.push('start_location');
        if (['도보', '대중교통', '자동차'].indexOf(String(state.transport || '')) < 0) missing.push('transport');
        const clock = /^(?:[01]\d|2[0-3]):[0-5]\d$/;
        if (!(days > 1) && !(clock.test(String(state.arrival_time || ''))
            && clock.test(String(state.departure_time || ''))
            && String(state.arrival_time) < String(state.departure_time))) missing.push('time_window');
        return missing;
    }

    public plannerCanOfferCourse() {
        return this.plannerStage === 'ready_to_generate'
            && !(this.plannerMissingSlots || []).length
            && !this.plannerRequiredConditionsMissing().length;
    }

    private plannerRequiredConditionQuestion() {
        const questions: any = {
            region: '어느 지역으로 여행할 예정인가요?',
            days: '여행은 며칠 일정으로 생각하고 있나요?',
            start_location: '여행지에서 어느 역이나 장소를 시작점으로 할까요?',
            transport: '도보, 대중교통, 자동차 중 어떤 이동수단을 이용할까요?',
            time_window: '몇 시부터 몇 시까지 둘러볼까요?'
        };
        return questions[(this.plannerMissingSlots || [])[0]] || '여행 조건을 조금 더 알려주세요.';
    }

    private applyPlannerContract(payload: any) {
        payload = payload || {};
        if (payload.travel_state && typeof payload.travel_state === 'object') this.plannerTravelState = { ...payload.travel_state };
        this.plannerStage = String(payload.stage || this.plannerTravelState.conversation_stage || 'collecting');
        this.plannerAction = String(payload.action || 'answer_only');
        this.plannerMissingSlots = Array.isArray(payload.missing_slots) ? payload.missing_slots : [];
        this.plannerWarnings = Array.isArray(payload.warnings) ? payload.warnings : [];
        this.plannerSuggestedReplies = Array.isArray(payload.suggested_replies) ? payload.suggested_replies : [];
        this.plannerFailureStage = String(payload.failure_stage || '');
        if (this.plannerStage === 'ready_to_generate') {
            this.plannerMissingSlots = Array.from(new Set([
                ...this.plannerRequiredConditionsMissing(), ...this.plannerMissingSlots
            ]));
            if (this.plannerMissingSlots.length || payload.can_generate === false) {
                this.plannerStage = 'collecting';
                this.plannerAction = 'ask_clarification';
                this.plannerTravelState.conversation_stage = 'collecting';
                this.plannerTravelState.conditions_confirmed = false;
                this.plannerTravelState.pending_slot = this.plannerMissingSlots[0] || '';
                if (this.plannerMissingSlots.indexOf('region') >= 0) {
                    this.plannerTravelState.region = '';
                    this.plannerTravelState.destination = null;
                }
                this.plannerSuggestedReplies = [];
                payload.message = this.plannerRequiredConditionQuestion();
                payload.reply = payload.message;
            }
        }
        this.plannerDestinationCandidates = Array.isArray(payload.destination_candidates)
            ? payload.destination_candidates
            : (Array.isArray(this.plannerTravelState.destination_candidates) ? this.plannerTravelState.destination_candidates : []);
        if (Array.isArray(payload.progress_steps) && payload.progress_steps.length) this.plannerProgressSteps = payload.progress_steps;
        if (payload.itinerary_draft && Array.isArray(payload.itinerary_draft.days) && payload.itinerary_draft.days.length) {
            this.applyPlannerDraft(payload.itinerary_draft);
        }
    }

    public async selectPlannerDestination(candidate: any) {
        if (this.isChatSending || !candidate || !candidate.name) return;
        await this.sendChatPrompt(`${candidate.name}${this.destinationParticle(candidate.name)} 일정 만들어줘`);
    }

    public destinationSelectionLabel(name: string) {
        return `${name || ''}${this.destinationParticle(name)} 만들기`;
    }

    private destinationParticle(name: string) {
        let value = String(name || '').trim();
        if (!value) return '로';
        let code = value.charCodeAt(value.length - 1);
        let jongseong = code >= 0xac00 && code <= 0xd7a3 ? (code - 0xac00) % 28 : 0;
        return jongseong === 0 || jongseong === 8 ? '로' : '으로';
    }

    public handleChatAvatarError(event: any) {
        let target = event && event.target ? event.target : null;
        if (target && target.style) target.style.display = 'none';
    }

    private applyPlannerDraft(draft: any) {
        let days = Array.isArray(draft.days) ? draft.days : [];
        this.plannerQuality = draft.quality && typeof draft.quality === 'object' ? draft.quality : {};
        this.plannerTravelerStyle = String(draft.traveler_style || '취향 중심');
        this.plannerCourseRegion = String(draft.region || this.plannerTravelState.region || '여행');
        this.plannerCourseSchedule = days.length <= 1 ? '당일' : `${days.length - 1}박 ${days.length}일`;
        this.plannerCourseBaseTitle = String(draft.title || `${this.plannerCourseRegion} 여행`).replace(/\s*코스\s*$/, '');
        this.plannerMapLabel = `${this.plannerCourseRegion} 동선`;
        this.plannerCourseDays = days.map((day: any, dayIndex: number) => {
            let places = Array.isArray(day.places) ? day.places : [];
            let stops = places.map((place: any, index: number) => ({
                key: String(place.place_id || `day-${dayIndex}-${index}`),
                placeId: String(place.place_id || ''),
                time: String(place.time || ''),
                name: String(place.name || ''),
                area: String(place.address || this.plannerCourseRegion),
                tag: String(place.activity || place.category || '여행지'),
                category: String(place.category || ''),
                move: '일정 종료',
                image: String(place.thumbnail || ''),
                icon: this.plannerIconForCategory(String(place.category || '')),
                lat: this.toPlannerNumber(place.lat),
                lng: this.toPlannerNumber(place.lng),
                durationMinutes: Number(place.duration_minutes || 0),
                durationLabel: String(place.duration_label || ''),
                timePeriod: String(place.time_period || ''),
                timePeriodIcon: String(place.time_period_icon || 'fa-location-dot'),
                rating: Number(place.rating || 0),
                reviewCount: Number(place.review_count || 0),
                openingStatus: String(place.opening_status || '영업시간 확인 필요'),
                tags: Array.isArray(place.tags) ? place.tags : [],
                representativeMenu: String(place.representative_menu || ''),
                estimatedCost: Number(place.estimated_cost || 0),
                status: index < 2 ? 'active' : 'upcoming',
                mapLeft: '',
                mapTop: ''
            }));
            places.forEach((place: any, index: number) => {
                if (index <= 0 || !stops[index - 1]) return;
                let move = place.move_from_previous || {};
                let minutes = Number(move.duration_minutes || 0);
                stops[index - 1].move = `${this.plannerModeLabel(move.mode || '')} ${minutes > 0 ? `${minutes}분` : '시간 확인 필요'}`;
            });
            stops = this.applyPlannerMapPositions(stops);
            return {
                label: day.label || `${dayIndex + 1}일차`,
                date: String(day.date || ''),
                stops,
                totalMoveMinutes: Number(day.total_move_minutes || 0),
                totalDistanceMeters: Number(day.total_distance_meters || 0),
                totalStayMinutes: Number(day.total_stay_minutes || 0),
                expectedCost: Number(day.expected_cost || 0),
                expectedCostLabel: String(day.expected_cost_label || ''),
                expectedMoveTime: String(day.expected_move_time || ''),
                endTime: day.end_time || '',
                returnPlan: day.return_plan && typeof day.return_plan === 'object' ? day.return_plan : {},
                theme: String(day.theme || ''),
                todayRecommendation: String(day.today_recommendation || ''),
                recommendationReason: String(day.recommendation_reason || ''),
                caution: String(day.caution || ''),
                weather: String(day.weather || ''),
                description: Array.isArray(day.description) ? day.description : [],
                qualityScore: Number(day.quality_score || 0)
            };
        });
        this.plannerCourseDayIndex = 0;
        this.plannerCourseReady = this.plannerCourseDays.length > 0;
        this.plannerCourseVisible = this.plannerCourseReady;
        this.plannerCourseExpanded = false;
        this.plannerRouteSource = '내부 장소·이동시간 기반';
        this.applyPlannerCourseDay(0);
        this.plannerCompanionCards = [];
        this.refreshPlannerRouteWithGoogle();
    }

    private startPlannerProgress() {
        this.stopPlannerProgress();
        this.plannerGenerationStep = 0;
        if (typeof window === 'undefined') return;
        this.plannerProgressTimer = window.setInterval(() => {
            this.plannerGenerationStep = Math.min(this.plannerGenerationStep + 1, this.plannerProgressSteps.length - 1);
            this.service.render();
        }, 650);
    }

    private stopPlannerProgress() {
        if (this.plannerProgressTimer && typeof window !== 'undefined') window.clearInterval(this.plannerProgressTimer);
        this.plannerProgressTimer = null;
    }

    private formatPlannerMinutes(value: number) {
        let minutes = Math.max(0, Math.round(Number(value || 0)));
        let hours = Math.floor(minutes / 60);
        let remain = minutes % 60;
        return hours > 0 ? `약 ${hours}시간${remain ? ` ${remain}분` : ''}` : `약 ${remain}분`;
    }

    private rebuildPlannerPreviewFromMessages() {
        this.resetPlannerPreview();
    }

    private plannerReplyForPrompt(prompt: string) {
        let text = String(prompt || '');
        if (this.plannerCourseReady && (text.indexOf('야경') > -1 || text.indexOf('저녁') > -1 || text.indexOf('밤') > -1)) {
            return '좋아요! 저녁 야경 스팟까지 반영해서 기존 코스를 업데이트할게요. 잠시만 기다려주세요.';
        }
        return '좋아요! 바다, 맛집, 감성 카페를 중심으로 코스를 만들어볼게요. 잠시만 기다려주세요.';
    }

    private applyPlannerPreviewFromAiPayload(prompt: string, payload: any) {
        let toolLogs = payload && Array.isArray(payload.tool_logs) ? payload.tool_logs : [];
        let stops = this.plannerStopsFromToolLogs(toolLogs);
        if (stops.length < 2) {
            return false;
        }

        this.applyPlannerDirectionsFromToolLogs(stops, toolLogs);
        stops = this.applyPlannerMapPositions(stops).map((stop: any, index: number) => ({
            ...stop,
            status: index < 2 ? 'active' : 'upcoming'
        }));

        this.plannerCourseRegion = this.plannerRegionFromStops(stops, prompt);
        this.plannerCourseSchedule = this.plannerScheduleFromPrompt(prompt);
        this.plannerCourseBaseTitle = `${this.plannerCourseRegion} AI 여행`;
        this.plannerMapLabel = `${this.plannerCourseRegion} 동선`;
        this.plannerCourseDayIndex = 0;
        this.plannerCourseDays = [{ label: '1일차', stops }];
        this.plannerCourseReady = true;
        this.plannerCourseVisible = true;
        this.applyPlannerCourseDay(0);
        this.plannerRouteSource = 'AI 실제 장소 검색 기반';
        this.plannerCompanionCards = [];
        this.refreshPlannerRouteWithGoogle();
        return true;
    }

    private plannerStopsFromToolLogs(toolLogs: any[]) {
        let rows: any[] = [];
        let seen: any = {};
        (toolLogs || []).forEach((log: any) => {
            let call = log && log.functionCall ? log.functionCall : {};
            if (call.name !== 'place_search') return;
            let response = log && log.functionResponse ? log.functionResponse : {};
            let results = Array.isArray(response.results) ? response.results : [];
            results.forEach((place: any) => {
                let key = String((place && (place.place_id || place.id || place.name)) || '').trim();
                if (!key || seen[key] || rows.length >= 5) return;
                seen[key] = true;
                rows.push(place);
            });
        });

        return rows.map((place: any, index: number) => {
            let category = this.cleanPlannerText(place.category || '', 40);
            let address = this.cleanPlannerText(place.address || place.area || '', 120);
            return {
                key: this.cleanPlannerText(place.place_id || place.id || place.name, 80) || `ai-place-${index}`,
                placeId: this.cleanPlannerText(place.place_id || place.id || '', 80),
                time: this.plannerTimeForIndex(index),
                name: this.cleanPlannerText(place.name || place.title || '', 80),
                area: this.plannerAreaFromAddress(address),
                tag: this.plannerActivityLabel(category, index),
                move: '이동 확인 중',
                image: this.cleanPlannerText(place.thumbnail || place.image || '', 300),
                icon: this.plannerIconForCategory(category),
                lat: this.toPlannerNumber(place.lat || place.latitude),
                lng: this.toPlannerNumber(place.lng || place.longitude),
                mapLeft: '',
                mapTop: ''
            };
        }).filter((stop: any) => !!stop.name);
    }

    private applyPlannerDirectionsFromToolLogs(stops: any[], toolLogs: any[]) {
        let directions = (toolLogs || []).filter((log: any) => {
            return log && log.functionCall && log.functionCall.name === 'directions_lookup';
        });
        directions.forEach((log: any, index: number) => {
            if (index >= stops.length - 1) return;
            let response = log.functionResponse || {};
            let minutes = Number(response.duration_minutes || 0);
            let meters = Number(response.distance_meters || 0);
            let mode = this.plannerModeLabel(response.mode || (log.functionCall.arguments || {}).mode || '');
            stops[index].move = `${mode} ${minutes > 0 ? `${minutes}분` : '시간 확인 필요'}`;
            if (meters > 0) stops[index].naverDistance = this.formatPlannerDistance(meters, false);
        });
    }

    private applyPlannerMapPositions(stops: any[]) {
        let coords = (stops || []).filter((stop: any) => this.isFiniteNumber(stop.lat) && this.isFiniteNumber(stop.lng));
        if (coords.length < 2) {
            return (stops || []).map((stop: any, index: number) => ({
                ...stop,
                ...this.fallbackPlannerMapPosition(index, stops.length)
            }));
        }

        let minLat = Math.min(...coords.map((stop: any) => Number(stop.lat)));
        let maxLat = Math.max(...coords.map((stop: any) => Number(stop.lat)));
        let minLng = Math.min(...coords.map((stop: any) => Number(stop.lng)));
        let maxLng = Math.max(...coords.map((stop: any) => Number(stop.lng)));
        let latSpan = maxLat - minLat || 0.01;
        let lngSpan = maxLng - minLng || 0.01;

        return (stops || []).map((stop: any, index: number) => {
            if (!this.isFiniteNumber(stop.lat) || !this.isFiniteNumber(stop.lng)) {
                return { ...stop, ...this.fallbackPlannerMapPosition(index, stops.length) };
            }
            let left = 18 + ((Number(stop.lng) - minLng) / lngSpan) * 64;
            let top = 18 + (1 - ((Number(stop.lat) - minLat) / latSpan)) * 64;
            return {
                ...stop,
                mapLeft: `${Math.max(12, Math.min(88, left)).toFixed(0)}%`,
                mapTop: `${Math.max(12, Math.min(88, top)).toFixed(0)}%`
            };
        });
    }

    private fallbackPlannerMapPosition(index: number, total: number) {
        let ratio = total > 1 ? index / (total - 1) : 0;
        return {
            mapLeft: `${Math.round(76 - ratio * 52)}%`,
            mapTop: `${Math.round(22 + ratio * 58)}%`
        };
    }

    private generatedPlannerCompanionCards() {
        let first = this.plannerStops[0] || {};
        let last = this.plannerStops[this.plannerStops.length - 1] || first;
        let regionKey = String(this.plannerCourseRegion || 'ai').replace(/\s+/g, '-');
        return [
            {
                id: `planner-mate-${regionKey}-ai`,
                courseId: '',
                courseConfirmed: false,
                title: `${this.plannerCourseRegion} AI 코스 같이 갈 동행을 찾아요`,
                route: this.plannerCourseTitle,
                routeStops: this.plannerStops.map((stop: any) => stop.name),
                location: this.plannerCourseRegion,
                date: this.plannerCourseSchedule,
                time: '10:00 출발',
                capacity: 4,
                applicants: 2,
                estimatedCost: '1인 약 8만원',
                budgetStyle: 'medium',
                pace: 'balanced',
                moodTags: ['사진', '맛집', '편안한 대화'],
                flexibility: ['카페 순서', '종료 시간'],
                intro: '대화로 만든 동선을 함께 다듬고 걸을 동행을 찾아요.',
                host: 'GACHI',
                status: 'open',
                saved: false,
                image: first.image || '/assets/places/haeundae-beach.jpg'
            },
            {
                id: `planner-mate-${regionKey}-slow-ai`,
                courseId: '',
                courseConfirmed: false,
                title: '여유롭게 쉬어가는 여행 메이트 구해요',
                route: this.plannerCourseTitle,
                routeStops: this.plannerStops.map((stop: any) => stop.name),
                location: this.plannerCourseRegion,
                date: this.plannerCourseSchedule,
                time: '13:00 출발',
                capacity: 3,
                applicants: 1,
                estimatedCost: '1인 약 6만원',
                budgetStyle: 'medium',
                pace: 'slow',
                moodTags: ['카페', '산책', '조용히'],
                flexibility: ['장소 체류 시간'],
                intro: '장소마다 충분히 머물며 사진과 식사를 즐길 분을 찾아요.',
                host: 'GACHI',
                status: 'open',
                saved: false,
                image: last.image || '/assets/places/busan-cafe.jpg'
            }
        ];
    }

    private plannerRegionFromStops(stops: any[], prompt: string) {
        let promptText = String(prompt || '');
        let known = this.courseKnownRegions().find((option: any) => {
            let area = String(option && option.area ? option.area : '').trim();
            return area && promptText.indexOf(area.split(' ')[0]) > -1;
        });
        if (known && known.area) return known.area;

        let area = this.cleanPlannerText(stops && stops[0] ? stops[0].area : '', 60);
        if (!area) return 'AI 추천 지역';
        let parts = area.replace(/광역시|특별시|특별자치도|특별자치시/g, '').split(/\s+/).filter((item: string) => !!item);
        return parts.slice(0, 2).join(' ') || area;
    }

    private plannerAreaFromAddress(address: string) {
        let text = this.cleanPlannerText(address, 120);
        if (!text) return '위치 확인 필요';
        return text.split(/\s+/).filter((item: string) => !!item).slice(0, 3).join(' ');
    }

    private plannerScheduleFromPrompt(prompt: string) {
        let text = String(prompt || '');
        if (text.indexOf('2박') > -1) return '2박 3일';
        if (text.indexOf('1박') > -1) return '1박 2일';
        if (text.indexOf('당일') > -1) return '당일';
        if (text.indexOf('반나절') > -1 || text.indexOf('오후') > -1) return '반나절';
        return this.plannerCourseSchedule || '일정 협의';
    }

    private plannerTimeForIndex(index: number) {
        return ['10:00', '12:30', '15:00', '17:00', '19:30'][index] || '시간 협의';
    }

    private plannerActivityLabel(category: string, index: number) {
        let text = String(category || '');
        if (text.indexOf('카페') > -1) return '카페 · 디저트';
        if (text.indexOf('음식') > -1 || text.indexOf('맛집') > -1) return index <= 1 ? '점심 식사' : '저녁 식사';
        if (text.indexOf('숙박') > -1) return '숙소 체크인';
        if (text.indexOf('쇼핑') > -1) return '쇼핑';
        if (text.indexOf('문화') > -1) return '문화 산책';
        if (text.indexOf('레포츠') > -1) return '액티비티';
        return index === 0 ? '여행 시작' : '추천 방문';
    }

    private plannerIconForCategory(category: string) {
        let text = String(category || '');
        if (text.indexOf('카페') > -1) return 'fa-mug-saucer';
        if (text.indexOf('음식') > -1 || text.indexOf('맛집') > -1) return 'fa-utensils';
        if (text.indexOf('숙박') > -1) return 'fa-bed';
        if (text.indexOf('쇼핑') > -1) return 'fa-bag-shopping';
        if (text.indexOf('문화') > -1) return 'fa-landmark';
        if (text.indexOf('레포츠') > -1) return 'fa-person-running';
        return 'fa-location-dot';
    }

    private defaultPlannerImage(category: string, index: number) {
        let text = String(category || '');
        if (text.indexOf('카페') > -1) return '/assets/places/busan-cafe.jpg';
        if (text.indexOf('음식') > -1 || text.indexOf('맛집') > -1) return '/assets/places/busan-milmyeon.jpg';
        if (text.indexOf('야경') > -1) return '/assets/places/gwangan-night.jpg';
        return ['/assets/places/haeundae-beach.jpg', '/assets/places/dongbaekseom.jpg', '/assets/bg-blue.jpg'][index % 3];
    }

    public plannerModeLabel(mode: string) {
        let text = String(mode || '').toLowerCase();
        if (text === 'driving' || text === 'car') return '차량';
        if (text === 'transit') return '대중교통';
        return '도보';
    }

    private toPlannerNumber(value: any) {
        if (value === null || typeof value === 'undefined' || value === '') return null;
        let number = Number(value);
        return isFinite(number) ? number : null;
    }

    private cleanPlannerText(value: any, limit: number = 120) {
        let text = String(value || '').replace(/<[^>]*>/g, ' ').replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim();
        return text.length > limit ? text.slice(0, limit).trim() : text;
    }

    public async openCommunityComposer(kind: string = 'post') {
        let nextTopic = kind === 'question' ? 'question' : this.selectedCommunityTopic;
        if (nextTopic === 'question') nextTopic = 'recommend';
        this.communityDraft = {
            ...this.defaultCommunityDraft(),
            kind: kind === 'question' ? 'question' : 'post',
            topic: kind === 'question' ? 'question' : nextTopic
        };
        this.communityPublishSubmitting = false;
        this.communityComposerOpen = true;
        await this.service.render();
    }

    public async closeCommunityComposer() {
        if (this.communityPublishSubmitting) return;
        this.communityComposerOpen = false;
        await this.service.render();
    }

    public async setCommunityDraftKind(kind: string) {
        this.communityDraft.kind = kind === 'question' ? 'question' : 'post';
        if (this.communityDraft.kind === 'question') this.communityDraft.topic = 'question';
        if (this.communityDraft.kind === 'post') {
            if (this.communityDraft.topic === 'question') this.communityDraft.topic = 'recommend';
            this.communityDraft.pollOptions = [];
            this.communityDraft.pollInput = '';
        }
        await this.service.render();
    }

    public async setCommunityDraftDestination(enabled: boolean) {
        this.communityDraft.hasDestination = enabled;
        if (!enabled) {
            this.communityDraft.destination = '';
            this.communityDraft.place = '';
        }
        await this.service.render();
    }

    public communityDraftTags() {
        return this.normalizeCommunityDraftTags(this.communityDraft ? this.communityDraft.tags : []);
    }

    public async handleCommunityTagKeydown(event: any) {
        let key = event && event.key ? String(event.key) : '';
        if (key !== 'Enter' && key !== ',') return;
        await this.addCommunityTagFromInput(event);
    }

    public async addCommunityTagFromInput(event?: any) {
        if (event && event.preventDefault) event.preventDefault();
        let input = String(this.communityDraft.tagInput || '');
        let nextTags = input
            .split(/[,\s]+/)
            .map((tag: string) => this.normalizeCommunityTag(tag))
            .filter((tag: string) => !!tag);
        if (nextTags.length === 0) return;

        this.communityDraft.tags = this.uniqueTags([
            ...this.communityDraftTags(),
            ...nextTags
        ]);
        this.communityDraft.tagInput = '';
        await this.service.render();
    }

    public async removeCommunityTag(tag: string) {
        let target = this.normalizeCommunityTag(tag);
        this.communityDraft.tags = this.communityDraftTags().filter((item: string) => item !== target);
        await this.service.render();
    }

    public communityDraftPollOptions() {
        return this.normalizeDraftList(this.communityDraft ? this.communityDraft.pollOptions : []);
    }

    public async handleCommunityPollKeydown(event: any) {
        let key = event && event.key ? String(event.key) : '';
        if (key !== 'Enter') return;
        await this.addCommunityPollOption(event);
    }

    public async addCommunityPollOption(event?: any) {
        if (event && event.preventDefault) event.preventDefault();
        let option = String(this.communityDraft.pollInput || '').trim();
        if (!option) return;

        let options = this.communityDraftPollOptions();
        if (options.indexOf(option) < 0) options.push(option);
        this.communityDraft.pollOptions = options;
        this.communityDraft.pollInput = '';
        await this.service.render();
    }

    public async removeCommunityPollOption(option: string) {
        this.communityDraft.pollOptions = this.communityDraftPollOptions().filter((item: string) => item !== option);
        await this.service.render();
    }

    public async handleCommunityPhotoUpload(event: any) {
        let input = event && event.target ? event.target : null;
        let file = input && input.files && input.files[0] ? input.files[0] : null;
        if (!file) return;

        if (file.type && !/^image\//.test(file.type)) {
            await this.showSaveHint('이미지 파일만 등록할 수 있어요.');
            if (input) input.value = '';
            return;
        }

        let reader = new FileReader();
        reader.onload = () => {
            this.communityDraft.photo = String(reader.result || '');
            this.communityDraft.photoName = String(file.name || '첨부 사진');
            this.service.render();
        };
        reader.readAsDataURL(file);
        if (input) input.value = '';
    }

    public async clearCommunityPhoto() {
        this.communityDraft.photo = '';
        this.communityDraft.photoName = '';
        await this.service.render();
    }

    public communityPhotoIsImage(value: any) {
        let source = String(value || '').trim();
        if (!source) return false;
        if (/^data:image\//i.test(source)) return true;
        return /^https?:\/\/.+\.(png|jpe?g|gif|webp|avif)(\?.*)?$/i.test(source);
    }

    public communityPhotoLabel(post: any) {
        if (!post) return '첨부 사진';
        return String(post.photoName || '').trim() || '첨부 사진';
    }

    public async publishCommunityPost() {
        if (this.communityPublishSubmitting) return;
        this.communityPublishSubmitting = true;
        await this.service.render();
        try {
            let title = String(this.communityDraft.title || '').trim();
            let body = String(this.communityDraft.body || '').trim();
            if (!title) {
                await this.showSaveHint('글 제목을 입력해주세요.');
                return;
            }
            if (!body) {
                await this.showSaveHint('본문을 입력해주세요.');
                return;
            }

            let isQuestion = this.communityDraft.kind === 'question';
            let topic = String(this.communityDraft.topic || 'recommend');
            if (isQuestion) topic = 'question';
            if (!isQuestion && topic === 'question') topic = 'recommend';
            let topicMeta = this.communityTopics.find((item: any) => item.key === topic) || this.communityTopics[0];
            let destination = this.communityDraft.hasDestination ? String(this.communityDraft.destination || '').trim() : '';
            let place = this.communityDraft.hasDestination ? String(this.communityDraft.place || '').trim() : '';
            let pollOptions = isQuestion ? this.communityDraftPollOptions() : [];
            let pendingPollOption = String(this.communityDraft.pollInput || '').trim();
            if (isQuestion && pendingPollOption && pollOptions.indexOf(pendingPollOption) < 0) {
                pollOptions = [...pollOptions, pendingPollOption];
            }
            let pendingTags = String(this.communityDraft.tagInput || '')
                .split(/[,\s]+/)
                .map((tag: string) => this.normalizeCommunityTag(tag))
                .filter((tag: string) => !!tag);
            let tags = this.uniqueTags([
                topicMeta.label,
                destination,
                place,
                ...this.communityDraftTags(),
                ...pendingTags
            ]).filter((tag: string) => !!tag);
            let now = Date.now();
            let post: any = {
                id: `community-draft-${now}`,
                kind: isQuestion ? 'question' : 'post',
                topic,
                title,
                summary: body,
                category: isQuestion ? '질문' : topicMeta.label,
                destination,
                place,
                photo: String(this.communityDraft.photo || '').trim(),
                photoName: String(this.communityDraft.photoName || '').trim(),
                author: this.myDisplayName(),
                likes: 0,
                comments: isQuestion ? 1 : 0,
                views: 1,
                votes: 0,
                createdAt: 0,
                createdLabel: '방금',
                tags
            };

            if (pollOptions.length > 0) {
                let counts: any = {};
                pollOptions.forEach((option: string) => counts[option] = 0);
                if (!isQuestion) {
                    post.topic = 'vote';
                    post.category = '인기 투표';
                }
                post.poll = {
                    title: isQuestion ? '질문 투표' : '투표',
                    options: pollOptions,
                    counts
                };
            }

            let saveResult = await this.saveCommunityPost(post);
            if (!saveResult || !saveResult.post) {
                await this.showSaveHint('글 저장에 실패했어요. 다시 시도해주세요.');
                return;
            }
            let savedPost = saveResult.post;
            if (saveResult.remote) await this.loadCommunityPosts(false);
            this.selectedCommunityTopic = 'recommend';
            this.selectedCommunitySort = 'latest';
            this.expandedCommunityPostId = savedPost.id;
            this.communityDraft = this.defaultCommunityDraft();
            this.communityComposerOpen = false;
            await this.showSaveHint(saveResult.remote
                ? (isQuestion ? '질문글이 등록됐어요.' : '커뮤니티 글이 등록됐어요.')
                : '서버 연결이 불안정해 이 기기에 먼저 보관했어요.');
        } finally {
            this.communityPublishSubmitting = false;
            await this.service.render();
        }
    }

    public communityPollPercent(post: any, option: string) {
        if (!post || !post.poll || !post.poll.counts) return 0;
        let total = Number(post.votes || 0);
        if (total <= 0) return 0;
        return Math.round((Number(post.poll.counts[option] || 0) / total) * 100);
    }

    public async voteCommunityPoll(post: any, option: string) {
        if (!post || !post.poll || !option) return;
        if (this.hasVotedCommunityPoll(post)) {
            await this.showSaveHint('투표는 한 번만 할 수 있어요.');
            return;
        }
        this.markCommunityPollVoted(post, option);
        if (!post.poll.counts) post.poll.counts = {};
        post.poll.counts[option] = Number(post.poll.counts[option] || 0) + 1;
        post.votes = Number(post.votes || 0) + 1;
        this.applyCommunityPostUpdate(post);
        await this.service.render();
        let response = await this.requestCommunityPostAction('vote', post, { option });
        if (response && response.code === 200 && response.data) {
            if (response.data.selectedOption) this.markCommunityPollVoted(post, response.data.selectedOption);
            if (response.data.post) this.applyCommunityPostUpdate(response.data.post);
            await this.showSaveHint(response.data.already ? '이미 투표했어요.' : '투표가 반영됐어요.');
        } else if (String(post.id || '').indexOf('community-draft-') === 0) {
            await this.saveCommunityPost(post);
            await this.showSaveHint('투표가 반영됐어요.');
        } else {
            await this.showSaveHint('서버 연결이 불안정해 투표를 기기에 먼저 표시했어요.');
        }
    }

    public async openAiPlanner() {
        this.activeTab = 'chat';
        this.chatContentTab = 'chat';
        this.draft = '';
        this.persistAccessState();
        this.replaceAccessUrl();
        await this.service.render();
    }

    public async openCompanionCoursePicker() {
        if (!this.isLoggedIn()) {
            await this.openAuthModal('login');
            return;
        }
        this.courseComposerOpen = false;
        this.coursePublishModalOpen = false;
        await this.restoreSavedCourses();
        this.companionCoursePickerOpen = true;
        this.myCompanionEditOpen = false;
        this.selectedMyCompanionPost = null;
        this.companionCoursePickerTab = 'mine';
        this.companionSelectedCourse = null;
        this.companionRecruitDraft = this.defaultCompanionRecruitDraft();
        await this.service.render();
    }

    public async closeCompanionCoursePicker() {
        if (this.companionRecruitSubmitting) return;
        this.resetCompanionMeetingSearch();
        this.companionCoursePickerOpen = false;
        this.companionSelectedCourse = null;
        this.companionRecruitDraft = this.defaultCompanionRecruitDraft();
        this.myCompanionEditOpen = false;
        this.myCompanionEditSaving = false;
        this.selectedMyCompanionPost = null;
        await this.service.render();
    }

    public async setCompanionCoursePickerTab(tab: string) {
        if (['mine', 'saved'].indexOf(tab) < 0) return;
        this.companionCoursePickerTab = tab;
        await this.service.render();
    }

    public companionPickerCourses() {
        if (this.companionCoursePickerTab === 'saved') return this.savedCourses();
        return this.myProfileCourses().filter((course: any) => course && !course.isCourseDraft && course.source !== 'draft');
    }

    public async selectCompanionCourse(course: any) {
        if (!course || !course.id) return;
        this.companionSelectedCourse = course;
        this.companionRecruitDraft = this.defaultCompanionRecruitDraft();
        await this.service.render();
    }

    public async changeCompanionCourse() {
        this.companionSelectedCourse = null;
        this.companionRecruitDraft = this.defaultCompanionRecruitDraft();
        await this.service.render();
    }


    private companionCalendarDate(value: any) {
        let date = String(value || '').trim();
        return /^\d{4}-\d{2}-\d{2}$/.test(date) && Number.isFinite(Date.parse(date)) ? date : '';
    }

    private validateCompanionSchedule() {
        let draft = this.companionRecruitDraft;
        return (!draft.startDate || !!this.companionCalendarDate(draft.startDate))
            && (!draft.endDate || (!!draft.startDate && !!this.companionCalendarDate(draft.endDate) && draft.endDate >= draft.startDate));
    }

    private companionSchedulePayload(previousDate: string = '', previousTime: string = '') {
        let draft = this.companionRecruitDraft;
        let date = draft.startDate
            ? draft.startDate + (draft.endDate && draft.endDate !== draft.startDate ? ' ~ ' + draft.endDate : '')
            : previousDate;
        return { startDate: draft.startDate || '', endDate: draft.endDate || '', date, time: previousTime };
    }

    public resetCompanionMeetingSearch() {
        if (this.companionMeetingSearchTimer) clearTimeout(this.companionMeetingSearchTimer);
        this.companionMeetingSearchTimer = null;
        this.companionMeetingSearchVersion++;
        this.companionMeetingSearchResults = [];
        this.companionMeetingSearching = false;
        this.companionMeetingSearchError = '';
    }

    public async onCompanionMeetingSearchInput() {
        this.resetCompanionMeetingSearch();
        this.companionRecruitDraft.meetingCoordinate = null;
        let query = String(this.companionRecruitDraft.meetingPoint || '').trim();
        if (!query) {
            await this.service.render();
            return;
        }
        this.companionMeetingSearchResults = this.companionCourseStops(this.companionSelectedCourse)
            .filter((place: any) => String(place.name || '').toLowerCase().includes(query.toLowerCase())).slice(0, 8);
        this.companionMeetingSearching = true;
        this.companionMeetingSearchTimer = setTimeout(() => this.searchCompanionMeetingPlaces(), 250);
        await this.service.render();
    }

    public async searchCompanionMeetingPlaces() {
        if (this.companionMeetingSearchTimer) clearTimeout(this.companionMeetingSearchTimer);
        this.companionMeetingSearchTimer = null;
        let query = String(this.companionRecruitDraft.meetingPoint || '').trim();
        if (!query || this.companionRecruitSubmitting) return;
        let version = ++this.companionMeetingSearchVersion;
        this.companionMeetingSearching = true;
        this.companionMeetingSearchError = '';
        await this.service.render();
        let center = this.companionCourseStops(this.companionSelectedCourse)
            .find((place: any) => this.isFiniteNumber(place.lat) && this.isFiniteNumber(place.lng)) || this.currentMapCenter();
        try {
            let response: any = await wiz.call('search_course_places', { keyword: query, lat: center.lat, lng: center.lng, region: '', limit: 8 });
            if (version !== this.companionMeetingSearchVersion) return;
            if (!response || response.code !== 200) throw new Error('place search failed');
            let rows = response.data && Array.isArray(response.data.rows) ? response.data.rows : [];
            let local = this.companionCourseStops(this.companionSelectedCourse)
                .filter((place: any) => String(place.name || '').toLowerCase().includes(query.toLowerCase()));
            let seen = new Set<string>();
            this.companionMeetingSearchResults = [...local, ...rows].filter((place: any) => {
                if (!place || !(place.name || place.title)) return false;
                let key = [place.name || place.title, place.address || '', place.lat, place.lng].join('|');
                if (seen.has(key)) return false;
                seen.add(key);
                return true;
            }).slice(0, 8);
            if (!this.companionMeetingSearchResults.length) this.companionMeetingSearchError = '검색 결과가 없어요. 다른 장소명이나 주소를 입력해주세요.';
        } catch (e) {
            if (version !== this.companionMeetingSearchVersion) return;
            this.companionMeetingSearchError = '장소를 검색하지 못했어요. 잠시 후 다시 입력해주세요.';
        }
        this.companionMeetingSearching = false;
        await this.service.render();
    }

    public async selectCompanionMeetingPlace(place: any) {
        if (!place || this.companionRecruitSubmitting) return;
        this.resetCompanionMeetingSearch();
        this.companionRecruitDraft.meetingPoint = String(place.name || place.title || '').trim();
        this.companionRecruitDraft.meetingCoordinate = this.isFiniteNumber(place.lat) && this.isFiniteNumber(place.lng)
            ? { lat: Number(place.lat), lng: Number(place.lng) } : null;
        await this.service.render();
    }

    public async clearCompanionMeetingPoint() {
        this.resetCompanionMeetingSearch();
        this.companionRecruitDraft.meetingPoint = '';
        this.companionRecruitDraft.meetingCoordinate = null;
        await this.service.render();
    }

    public companionCourseIsMine(course: any) {
        return !!course && (!!course.mine || String(course.source || '') === 'mine');
    }

    public companionCourseAuthor(course: any) {
        if (this.companionCourseIsMine(course)) return this.myDisplayName();
        let route = course && course.route && typeof course.route === 'object' ? course.route : {};
        let candidates = [
            route.original_author,
            route.originalAuthor,
            route.author,
            course && course.originalAuthor,
            course && course.author_name,
            course && course.author,
            course && course.creator_name,
            course && course.creator
        ];
        for (let value of candidates) {
            let author = String(value || '').trim();
            if (author) return author;
        }
        return '작성자 정보 미제공';
    }

    public companionCourseSourceLabel(course: any) {
        if (!course) return '';
        if (this.companionCourseIsMine(course)) return '출처 · 내가 만든 코스';
        return `출처 · 저장한 코스 / 원작자: ${this.companionCourseAuthor(course)} / 원본 ID: ${course.id}`;
    }

    public companionCourseStops(course: any) {
        if (!course) return [];
        let stops = this.profileCourseStops(course).filter((item: any) => {
            return item && String(item.name || '').trim() && String(item.item_type || item.itemType || 'place') === 'place';
        });
        let seen: any = {};
        return stops.filter((item: any) => {
            let name = String(item.name || '').trim();
            if (!name || seen[name]) return false;
            seen[name] = true;
            return true;
        });
    }

    public companionCourseRoutePreview(course: any) {
        let names = this.companionCourseStops(course).map((item: any) => item.name).slice(0, 4);
        return names.length ? names.join(' → ') : this.profileCourseSummary(course);
    }

    public async submitLoadedCourseCompanion() {
        if (this.companionRecruitSubmitting) return;
        let course = this.companionSelectedCourse;
        if (!course || !course.id) {
            await this.showSaveHint('동행에 연결할 코스를 먼저 선택해주세요.');
            return;
        }
        let capacity = Number(this.companionRecruitDraft.capacity);
        if (!Number.isInteger(capacity) || capacity < 1 || capacity > 8) {
            await this.showSaveHint('모집 인원은 1명 이상 8명 이하로 입력해주세요.');
            return;
        }

        this.companionRecruitSubmitting = true;
        await this.service.render();
        let stops = this.companionCourseStops(course);
        let firstStop = stops[0] || {};
        let courseRoute = course && course.route && typeof course.route === 'object' ? course.route : {};
        let date = String(course.date || course.scheduleDate || courseRoute.start_date || courseRoute.schedule_date || '').trim() || '코스 일정 기준';
        let time = String(firstStop.visit_time || firstStop.visitTime || firstStop.time || '').trim();
        let sourceLabel = this.companionCourseSourceLabel(course);
        let post: any = {
            id: `mate-loaded-${course.id}-${Date.now()}`,
            courseId: course.id,
            originalCourseId: course.id,
            courseConfirmed: true,
            courseSource: this.companionCourseIsMine(course) ? 'mine' : 'saved',
            courseSourceLabel: sourceLabel,
            courseAuthor: this.companionCourseAuthor(course),
            title: `${this.profileCourseTitle(course)} 동행 모집`,
            route: this.profileCourseTitle(course),
            routeStops: stops.map((item: any) => item.name),
            location: this.profileCourseRegion(course),
            date,
            time,
            ...this.companionSchedulePayload(date, time),
            capacity,
            applicants: 0,
            estimatedCost: String(this.companionRecruitDraft.cost || '비용 협의').trim(),
            budgetStyle: String(this.companionRecruitDraft.budgetStyle || '').trim(),
            pace: String(this.companionRecruitDraft.pace || '').trim(),
            moodTags: this.splitList(this.companionRecruitDraft.mood || ''),
            flexibility: this.splitList(this.companionRecruitDraft.flexible || ''),
            interestTags: this.uniqueTags(stops.map((item: any) => item.category || item.name)).slice(0, 6),
            smoking: String(this.companionRecruitDraft.smoking || '').trim(),
            drinking: String(this.companionRecruitDraft.drinking || '').trim(),
            verificationRequired: true,
            hostHistory: `동행 ${this.travelResumeCompanionUses()}회 · 후기 확인`,
            meetingPoint: String(this.companionRecruitDraft.meetingPoint || '').trim(),
            meetingCoordinate: this.companionRecruitDraft.meetingPoint && this.companionRecruitDraft.meetingCoordinate
                ? { ...this.companionRecruitDraft.meetingCoordinate } : null,
            packingItems: ['신분증', '보조배터리', '개인 준비물'],
            intro: String(this.companionRecruitDraft.intro || '확정한 코스를 함께 여행할 동행을 모집합니다.').trim(),
            host: this.myDisplayName(),
            owned: true,
            image: this.companionCourseCoverForPost(course),
            status: 'open',
            saved: false,
            applications: []
        };
        let savedPost = await this.saveCompanionPostRemote(post);
        if (!savedPost) {
            this.companionRecruitSubmitting = false;
            await this.showSaveHint('모집글을 공유하지 못했습니다. 잠시 후 다시 시도해주세요.');
            return;
        }
        post = savedPost;
        this.companionPosts = [post, ...this.companionPosts.filter((item: any) => item && item.id !== post.id)];
        this.persistCompanionPostsCache();
        if (!this.matchesSelectedLocation(post)) this.selectedFilters.location = '';
        this.activeTab = 'home';
        this.homeContentTab = 'companion';
        this.companionMode = 'pretrip';
        this.expandedCompanionPostId = '';
        this.companionCoursePickerOpen = false;
        this.companionSelectedCourse = null;
        this.companionRecruitDraft = this.defaultCompanionRecruitDraft();
        this.companionRecruitSubmitting = false;
        this.persistAccessState();
        this.replaceAccessUrl();
        await this.showSaveHint('확정 코스에 동행 모집을 올렸어요.');
        this.resetHomeContentScroll();
    }

    public async submitCompanionRecruit() {
        if (!this.validateCompanionSchedule()) {
            await this.showSaveHint('시작일을 선택하고 종료일이 시작일 이후인지 확인해주세요.');
            return;
        }
        if (this.myCompanionEditOpen) {
            await this.saveMyCompanionEdit();
            return;
        }
        await this.submitLoadedCourseCompanion();
    }

    private async loadCompanionPosts(showLoading: boolean = false) {
        let sharedPosts: any[] = [];
        let loaded = false;
        try {
            let response: any = await wiz.call('saved_courses', {
                community_action: 'list',
                actor_key: this.communityActorKey()
            });
            if (response && response.code === 200 && response.data && Array.isArray(response.data.posts)) {
                loaded = true;
                sharedPosts = response.data.posts
                    .filter((post: any) => post && post.topic === 'companion')
                    .map((post: any) => this.companionPostFromCommunity(post))
                    .filter((post: any) => !!post);
            }
        } catch (e) { }

        try {
            let legacyResponse: any = await wiz.call('saved_courses', {
                community_action: 'companions',
                actor_key: this.communityActorKey()
            });
            if (legacyResponse && legacyResponse.code === 200 && legacyResponse.data && Array.isArray(legacyResponse.data.posts)) {
                loaded = true;
                sharedPosts = [...sharedPosts, ...legacyResponse.data.posts.filter((post: any) => {
                    return post && this.isConfirmedCompanionPost(post);
                })];
            }
        } catch (e) { }

        let seen: any = {};
        this.companionPosts = [...sharedPosts, ...this.companionPosts].filter((post: any) => {
            let id = String((post && post.id) || '');
            if (!id || seen[id]) return false;
            seen[id] = true;
            return true;
        });
        this.applySavedCompanionPostIds();
        if (this.isLoggedIn()) await this.loadCompanionApplicationsForPosts();
        if (showLoading) await this.service.render();
        return loaded;
    }

    private async loadCompanionApplicationsForPosts() {
        for (let post of this.companionPosts.slice(0, 50)) {
            let postId = String((post && post.id) || '').trim();
            if (!postId) continue;
            try {
                let response: any = await wiz.call('saved_courses', {
                    community_action: 'companion_applications',
                    post_id: postId
                });
                if (response && response.code === 200 && response.data && Array.isArray(response.data.applications)) {
                    post.applications = response.data.applications;
                    if (response.data.matched === true || post.applications.some((item: any) => item && item.status === 'accepted')) post.status = 'matched';
                    else if (post.applications.length > 0) post.status = 'requested';
                    post.applicants = post.applications.length;
                }
            } catch (e) { }
        }
        this.syncTogetherCompanionsFromTrip();
    }

    private companionPostFromCommunity(post: any) {
        let wrapper = post && post.poll && typeof post.poll === 'object' ? post.poll : {};
        let payload = wrapper && wrapper.companion && typeof wrapper.companion === 'object' ? wrapper.companion : null;
        if (!payload) return null;
        return {
            ...payload,
            id: String(post.id || payload.id || ''),
            courseId: String(payload.courseId || post.place || ''),
            courseConfirmed: true,
            owned: post.owned === true,
            createdLabel: post.createdLabel || payload.createdLabel || '방금'
        };
    }

    private async saveCompanionPostRemote(post: any) {
        if (!post || !post.id || !post.courseId) return null;
        let storedPost = { ...post };
        delete storedPost.owned;
        let communityPost = {
            id: String(post.id),
            kind: 'post',
            topic: 'companion',
            title: String(post.title || '동행 모집'),
            summary: String(post.intro || ''),
            category: '동행 모집',
            destination: String(post.location || ''),
            place: String(post.courseId),
            photo: String(post.image || ''),
            author: String(post.host || this.myDisplayName() || '여행자'),
            tags: Array.isArray(post.interestTags) ? post.interestTags : [],
            poll: { companion: storedPost }
        };
        try {
            let response: any = await wiz.call('save_course', {
                community_action: 'post',
                actor_key: this.communityActorKey(),
                post: JSON.stringify(communityPost)
            });
            if (!response || response.code !== 200 || !response.data || !response.data.post) return null;
            return this.companionPostFromCommunity(response.data.post);
        } catch (e) { }
        return null;
    }

    private async syncOwnedCompanionPosts() {
        if (!this.isLoggedIn()) return;
        let ownedPosts = this.companionPosts.filter((post: any) => {
            return post && post.owned === true && this.isConfirmedCompanionPost(post);
        }).slice(0, 50);
        for (let post of ownedPosts) {
            await this.saveCompanionPostRemote(post);
        }
    }

    private defaultCompanionRecruitDraft() {
        return {
            capacity: '',
            cost: '',
            pace: '',
            budgetStyle: '',
            smoking: '',
            drinking: '',
            mood: '',
            flexible: '',
            meetingPoint: '',
            meetingCoordinate: null,
            startDate: '',
            endDate: '',
            time: '',
            intro: ''
        };
    }

    private restoreCompanionPostsCache() {
        this.expandedCompanionPostId = '';
        if (typeof window === 'undefined' || !window.localStorage) return;
        try {
            let currentKey = this.companionPostsStorageKey();
            let keys = [currentKey];
            if (this.isAdmin()) {
                for (let index = 0; index < window.localStorage.length; index++) {
                    let key = String(window.localStorage.key(index) || '');
                    if (key === this.companionPostsStorageBaseKey || key.indexOf(`${this.companionPostsStorageBaseKey}:`) === 0) {
                        if (keys.indexOf(key) === -1) keys.push(key);
                    }
                }
            }
            let saved = keys.reduce((posts: any[], key: string) => {
                try {
                    let rows = JSON.parse(window.localStorage.getItem(key) || '[]');
                    if (!Array.isArray(rows)) return posts;
                    return posts.concat(rows.filter((post: any) => key === currentKey || post.owned === true));
                } catch (e) { }
                return posts;
            }, []);
            let seen: any = {};
            let restored = saved.filter((post: any) => {
                return post && typeof post === 'object'
                    && !!String(post.id || '').trim()
                    && !!String(post.courseId || '').trim()
                    && post.courseConfirmed === true
                    && !seen[String(post.id)]
                    && (seen[String(post.id)] = true);
            });
            let restoredIds: any = {};
            restored.forEach((post: any) => restoredIds[String(post.id)] = true);
            this.companionPosts = [
                ...restored,
                ...this.companionPosts.filter((post: any) => post && !restoredIds[String(post.id || '')])
            ];
        } catch (e) { }
    }

    private loadSavedCompanionPosts() {
        if (typeof window === 'undefined' || !window.localStorage) return;
        try {
            let raw = window.localStorage.getItem(this.savedCompanionPostsStorageKey()) || '[]';
            let ids = JSON.parse(raw);
            this.savedCompanionPostIds = Array.isArray(ids)
                ? ids.map((id: any) => String(id || '').trim()).filter((id: string) => !!id)
                : [];
        } catch (e) {
            this.savedCompanionPostIds = [];
        }
        this.applySavedCompanionPostIds();
    }

    private persistSavedCompanionPosts() {
        if (typeof window === 'undefined' || !window.localStorage) return;
        try {
            window.localStorage.setItem(this.savedCompanionPostsStorageKey(), JSON.stringify(this.savedCompanionPostIds));
        } catch (e) { }
    }

    private applySavedCompanionPostIds() {
        this.companionPosts.forEach((post: any) => {
            post.saved = !!post && this.savedCompanionPostIds.indexOf(String(post.id || '')) > -1;
        });
    }

    private persistCompanionPostsCache() {
        if (typeof window === 'undefined' || !window.localStorage) return;
        try {
            let posts = this.companionPosts
                .filter((post: any) => post && post.owned === true && post.courseConfirmed === true)
                .slice(0, 50);
            window.localStorage.setItem(this.companionPostsStorageKey(), JSON.stringify(posts));
        } catch (e) { }
    }

    private companionPostsStorageKey() {
        return this.userScopedStorageKey(this.companionPostsStorageBaseKey);
    }

    private savedCompanionPostsStorageKey() {
        return this.userScopedStorageKey(this.savedCompanionPostsStorageBaseKey);
    }

    public async openCourseComposer() {
        this.resetCourseComposerDraftState();
        this.refreshCourseDraftArchiveSummary();
        this.activeFilterKey = '';
        this.filterOverviewOpen = false;
        this.courseBuilderMode = 'manual';
        this.courseBuilderStep = 'info';
        this.courseDraftArchiveOpen = false;
        this.clearCourseNaverOverlays();
        this.courseComposerOpen = true;
        await this.service.render();
    }

    public async openPlannerCourseEditor() {
        if (!this.plannerCourseReady || !Array.isArray(this.plannerCourseDays) || this.plannerCourseDays.length === 0) {
            await this.showSaveHint('먼저 AI와 코스를 만들어주세요.');
            return;
        }

        this.resetCourseComposerDraftState();
        this.courseComposerPlannerEdit = true;
        this.courseBuilderMode = 'manual';
        this.courseBuilderStep = 'places';
        this.courseBuilderDayIndex = Math.max(0, Math.min(this.plannerCourseDayIndex, this.plannerCourseDays.length - 1));
        this.courseBuilderDays = this.plannerCourseDays.map((day: any, dayIndex: number) => ({
            label: day && day.label ? day.label : `${dayIndex + 1}일차`,
            places: ((day && day.stops) || []).map((stop: any, index: number) => this.plannerStopToBuilderPlace(stop, dayIndex, index))
        }));
        this.courseBuilderOriginalDays = this.cloneCourseBuilderDays(this.courseBuilderDays);
        this.courseBuilderPlaces = this.cloneCourseBuilderPlaces(this.courseBuilderDays[this.courseBuilderDayIndex].places);
        let allPlaces = this.courseBuilderDays.reduce((items: any[], day: any) => items.concat(day.places || []), []);
        this.courseDraft = {
            ...this.defaultCourseDraft(),
            title: this.plannerCourseBaseTitle || this.plannerCourseTitle,
            region: this.plannerCourseRegion,
            schedule: this.plannerCourseSchedule,
            places: allPlaces.map((place: any) => place.name).join('\n'),
            photo: allPlaces[0] && allPlaces[0].image ? allPlaces[0].image : '',
            photoName: 'AI 플래너 코스',
            description: `AI 대화로 만든 ${this.plannerCourseSchedule} 코스를 수정합니다.`,
            category: '여행',
            companionType: 'friend',
            isPublic: false
        };
        this.activeFilterKey = '';
        this.filterOverviewOpen = false;
        this.coursePublishModalOpen = false;
        this.coursePlaceSearchOpen = false;
        this.courseBuilderError = '';
        this.clearCourseNaverOverlays();
        this.courseComposerOpen = true;
        await this.service.render();
        this.scheduleCourseMapRender();
    }

    private plannerStopToBuilderPlace(stop: any, dayIndex: number, index: number) {
        return {
            placeId: String(stop && (stop.placeId || stop.key) ? (stop.placeId || stop.key) : `planner-${dayIndex}-${index}`).replace(/^place-/, ''),
            originalKey: stop && stop.key ? stop.key : '',
            name: stop && stop.name ? stop.name : '장소',
            lat: this.safeNumber(stop && stop.lat),
            lng: this.safeNumber(stop && stop.lng),
            order: index + 1,
            visitTime: stop && stop.time ? stop.time : this.plannerTimeForIndex(index),
            memo: stop && stop.tag ? stop.tag : '',
            area: stop && stop.area ? stop.area : '',
            address: stop && stop.address ? stop.address : '',
            category: '',
            categorySelected: false,
            image: stop && stop.image ? stop.image : '',
            move: stop && stop.move ? stop.move : '이동 확인 중',
            manualTime: false
        };
    }

    private cloneCourseBuilderPlaces(places: any[]) {
        return (places || []).map((place: any) => ({ ...place }));
    }

    private cloneCourseBuilderDays(days: any[]) {
        return (days || []).map((day: any) => ({
            label: day.label,
            date: day.date || '',
            dateLabel: day.dateLabel || '',
            places: this.cloneCourseBuilderPlaces(day.places)
        }));
    }

    public async setCourseBuilderDay(index: number) {
        if (index < 0 || index >= this.courseBuilderDays.length || index === this.courseBuilderDayIndex) return;
        this.syncCurrentCourseBuilderDay();
        this.courseBuilderDayIndex = index;
        this.courseBuilderPlaces = this.cloneCourseBuilderPlaces(this.courseBuilderDays[index].places);
        this.coursePlaceSearchQuery = '';
        this.coursePlaceSearchResults = [];
        this.coursePlaceSearchOpen = false;
        this.courseBuilderError = '';
        this.clearCourseNaverOverlays();
        await this.service.render();
        this.scheduleCourseMapRender();
    }

    public courseBuilderDayCount() {
        let start = String(this.courseDraft.scheduleDate || '').trim();
        let end = String(this.courseDraft.scheduleEndDate || start).trim();
        let startDate = start ? new Date(`${start}T00:00:00`) : null;
        let endDate = end ? new Date(`${end}T00:00:00`) : null;
        if (startDate && endDate && !isNaN(startDate.getTime()) && !isNaN(endDate.getTime())) {
            return Math.max(1, Math.min(31, Math.round((endDate.getTime() - startDate.getTime()) / 86400000) + 1));
        }
        return Math.max(1, this.courseBuilderDays.length);
    }

    public isLastCourseBuilderDay() {
        return this.courseBuilderDayIndex >= this.courseBuilderDayCount() - 1;
    }

    public nextCourseBuilderDayLabel() {
        let next = this.courseBuilderDays[this.courseBuilderDayIndex + 1];
        return next && next.label ? `${next.label} 구상하기` : `${this.courseBuilderDayIndex + 2}일차 구상하기`;
    }

    public async continueToNextCourseBuilderDay() {
        if (this.isLastCourseBuilderDay()) return;
        if (!this.courseBuilderDays[this.courseBuilderDayIndex + 1]) this.ensureCourseBuilderDaysFromDates();
        if (!this.courseBuilderDays[this.courseBuilderDayIndex + 1]) {
            this.courseBuilderError = '다음 날짜 정보를 불러오지 못했습니다. 기본 정보에서 날짜를 다시 확인해주세요.';
            await this.service.render();
            return;
        }
        await this.setCourseBuilderDay(this.courseBuilderDayIndex + 1);
    }

    private syncCurrentCourseBuilderDay() {
        if (!this.courseBuilderDays[this.courseBuilderDayIndex]) return;
        this.courseBuilderDays[this.courseBuilderDayIndex].places = this.cloneCourseBuilderPlaces(this.courseBuilderPlaces);
    }

    private ensureCourseBuilderDaysFromDates() {
        this.syncCurrentCourseBuilderDay();
        let start = String(this.courseDraft.scheduleDate || '').trim();
        let end = String(this.courseDraft.scheduleEndDate || start).trim();
        let startDate = start ? new Date(`${start}T00:00:00`) : null;
        let endDate = end ? new Date(`${end}T00:00:00`) : null;
        if (!startDate || !endDate || isNaN(startDate.getTime()) || isNaN(endDate.getTime())) return;
        let dayCount = Math.max(1, Math.min(31, Math.round((endDate.getTime() - startDate.getTime()) / 86400000) + 1));
        let previous = this.cloneCourseBuilderDays(this.courseBuilderDays);
        let fallbackPlaces = previous.length === 0 ? this.cloneCourseBuilderPlaces(this.courseBuilderPlaces) : [];
        this.courseBuilderDays = Array.from({ length: dayCount }).map((_: any, index: number) => {
            let date = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate() + index);
            let key = this.formatIsoDate(date);
            let matched = previous.find((day: any) => day.date === key) || previous[index];
            return {
                label: `${index + 1}일차`,
                date: key,
                dateLabel: this.formatCourseDateLabel(key),
                places: matched ? this.cloneCourseBuilderPlaces(matched.places) : (index === 0 ? fallbackPlaces : [])
            };
        });
        this.courseBuilderDayIndex = Math.max(0, Math.min(this.courseBuilderDayIndex, this.courseBuilderDays.length - 1));
        this.courseBuilderPlaces = this.cloneCourseBuilderPlaces(this.courseBuilderDays[this.courseBuilderDayIndex].places);
    }

    public async closeCourseComposer() {
        this.courseComposerOpen = false;
        this.courseComposerPlannerEdit = false;
        this.courseBuilderDayIndex = 0;
        this.courseBuilderDays = [];
        this.courseScheduleFormOpen = false;
        this.courseScheduleDraft = { type: 'flight', title: '', time: '', detail: '' };
        this.courseBuilderOriginalDays = [];
        this.courseAiRebuilding = false;
        this.courseBuilderMode = '';
        this.courseBuilderStep = 'mode';
        this.coursePublishModalOpen = false;
        this.coursePlaceDetailOpen = false;
        this.coursePlaceSearchOpen = false;
        this.clearCourseNaverOverlays();
        await this.service.render();
    }

    public async openCourseDraftArchive() {
        this.refreshCourseDraftArchiveSummary();
        this.courseDraftArchiveOpen = true;
        this.filterOverviewOpen = false;
        await this.service.render();
    }

    public async closeCourseDraftArchive() {
        this.courseDraftArchiveOpen = false;
        await this.service.render();
    }

    public async loadSavedCourseDraft() {
        if (!this.restoreCourseDraft(true)) {
            this.courseDraftArchiveOpen = false;
            await this.showSaveHint('저장된 코스가 없어요.');
            return;
        }

        this.activeFilterKey = '';
        this.filterOverviewOpen = false;
        this.courseDraftArchiveOpen = false;
        this.courseComposerOpen = true;
        this.coursePublishModalOpen = false;
        this.coursePlaceSearchOpen = false;
        this.courseBuilderError = '';
        this.clearCourseNaverOverlays();
        await this.service.render();
        if (this.courseBuilderStep === 'places') {
            this.scheduleCourseMapRender();
            if (!this.coursePlaceSearchResults.length) await this.searchCoursePlaces(false);
        }
    }

    public async selectCourseBuilderMode(mode: string) {
        if (mode !== 'manual') return;
        this.courseBuilderMode = 'manual';
        this.courseBuilderStep = 'info';
        this.courseBuilderError = '';
        this.coursePlaceSearchOpen = false;
        this.clearCourseNaverOverlays();
        await this.service.render();
    }

    public async resetCourseBuilderMode() {
        this.courseBuilderMode = 'manual';
        this.courseBuilderStep = 'info';
        this.courseBuilderError = '';
        this.coursePlaceSearchOpen = false;
        this.clearCourseNaverOverlays();
        await this.service.render();
    }

    public courseBuilderStepOneLabel() {
        return '기본 정보';
    }

    public async setCourseType(type: string) {
        if (type !== '여행' && type !== '데이트') return;
        this.courseDraft.category = type;
        if (type === '데이트' && !String(this.courseDraft.companionType || '').trim()) {
            this.courseDraft.companionType = 'couple';
        }
        this.courseBuilderError = '';
        await this.service.render();
    }

    public courseDateCalendarMonthLabel() {
        return `${this.courseDateCalendarMonth.getFullYear()}년 ${this.courseDateCalendarMonth.getMonth() + 1}월`;
    }

    public courseDateCalendarDays() {
        let year = this.courseDateCalendarMonth.getFullYear();
        let month = this.courseDateCalendarMonth.getMonth();
        let firstDate = new Date(year, month, 1);
        let startDate = new Date(year, month, 1 - firstDate.getDay());
        let todayKey = this.formatIsoDate(new Date());
        let selectedStart = String(this.courseDraft.scheduleDate || '');
        let selectedEnd = String(this.courseDraft.scheduleEndDate || selectedStart || '');
        let days: any[] = [];
        for (let i = 0; i < 42; i++) {
            let date = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate() + i);
            let key = this.formatIsoDate(date);
            let inRange = !!selectedStart && !!selectedEnd && key >= selectedStart && key <= selectedEnd;
            days.push({
                key,
                label: date.getDate(),
                inMonth: date.getMonth() === month,
                isToday: key === todayKey,
                inRange,
                rangeStart: key === selectedStart,
                rangeEnd: key === selectedEnd,
                selected: key === selectedStart || key === selectedEnd,
                ariaLabel: `${date.getFullYear()}년 ${date.getMonth() + 1}월 ${date.getDate()}일`
            });
        }
        return days;
    }

    public async changeCourseDateMonth(direction: number) {
        this.courseDateCalendarMonth = new Date(this.courseDateCalendarMonth.getFullYear(), this.courseDateCalendarMonth.getMonth() + direction, 1);
        await this.service.render();
    }

    public async setCourseDraftDate(value: string) {
        if (!value) return;
        this.setCourseDraftDateRange(value, value);
        let date = new Date(`${value}T00:00:00`);
        if (!isNaN(date.getTime())) this.courseDateCalendarMonth = new Date(date.getFullYear(), date.getMonth(), 1);
        this.courseBuilderError = '';
        await this.service.render();
    }

    public async startCourseDateDrag(day: any, event: any) {
        if (!day) return;
        if (event && event.preventDefault) event.preventDefault();
        this.courseDateDragActive = true;
        this.courseDateDragStartKey = day.key;
        this.setCourseDraftDateRange(day.key, day.key);
        await this.service.render();
    }

    public async previewCourseDatePointer(event: any) {
        if (!this.courseDateDragActive) return;
        if (event && event.preventDefault) event.preventDefault();
        let key = this.courseDateKeyFromPointer(event);
        if (!key || key === this.courseDraft.scheduleEndDate) return;
        this.setCourseDraftDateRange(this.courseDateDragStartKey || key, key);
        await this.service.render();
    }

    public finishCourseDateDrag() {
        this.courseDateDragActive = false;
        this.courseDateDragStartKey = '';
    }

    public openCourseRegionSearch() {
        this.courseRegionSearchOpen = true;
    }

    public onCourseRegionInput(event: any) {
        if (event && event.target) this.courseRegionInput = event.target.value || '';
        this.courseRegionSearchOpen = true;
    }

    public courseRegionMatches() {
        let query = String(this.courseRegionInput || '').trim().toLowerCase();
        let initials = this.koreanInitials(query);
        let useInitialSearch = this.isKoreanInitialQuery(query);
        let options = this.courseKnownRegions();
        let selected = this.courseRegions().map((region: string) => region.toLowerCase());
        options = options.filter((option: any) => selected.indexOf(String(option.label || '').toLowerCase()) === -1);
        if (!query) return options.slice(0, 6);
        return options.filter((option: any) => {
            let label = String(option.label || '').toLowerCase();
            let area = String(option.area || '').toLowerCase();
            let index = `${label} ${area}`.indexOf(query) > -1;
            let initialIndex = this.koreanInitials(`${option.label} ${option.area}`).indexOf(initials || query) > -1;
            return index || (useInitialSearch && initialIndex);
        }).slice(0, 8);
    }

    public async selectCourseRegionSuggestion(option: any) {
        if (!option) return;
        this.commitCourseRegion(String(option.label || option.area || '').trim());
        this.courseRegionInput = '';
        this.courseRegionSearchOpen = false;
        this.courseBuilderError = '';
        this.coursePlaceSearchResults = [];
        await this.service.render();
        if (this.courseBuilderStep === 'places') this.scheduleCourseMapRender();
    }

    public courseRegions() {
        let regions = Array.isArray(this.courseDraft.regions) ? this.courseDraft.regions : [];
        regions = regions.map((region: any) => String(region || '').trim()).filter((region: string) => !!region);
        if (!regions.length) {
            let legacy = String(this.courseDraft.region || '').trim();
            if (legacy) regions = legacy.split(/\s*(?:,|·|→)\s*/).filter((region: string) => !!region);
        }
        return regions.filter((region: string, index: number) => regions.indexOf(region) === index);
    }

    public async addCourseRegionInput() {
        let region = String(this.courseRegionInput || '').trim();
        if (!region) return;
        let matched = this.courseKnownRegions().find((option: any) => {
            let label = String(option.label || '').toLowerCase();
            let area = String(option.area || '').toLowerCase();
            return label === region.toLowerCase() || area === region.toLowerCase();
        });
        this.commitCourseRegion(matched ? matched.label : region);
        this.courseRegionInput = '';
        this.courseRegionSearchOpen = false;
        this.courseBuilderError = '';
        this.coursePlaceSearchResults = [];
        await this.service.render();
    }

    public async removeCourseRegion(region: string) {
        let regions = this.courseRegions().filter((item: string) => item !== region);
        this.courseDraft.regions = regions;
        this.courseDraft.region = regions.join(', ');
        this.coursePlaceSearchResults = [];
        this.courseBuilderError = '';
        await this.service.render();
        if (this.courseBuilderStep === 'places') {
            this.scheduleCourseMapRender();
            await this.searchCoursePlaces(false);
        }
    }

    private commitCourseRegion(region: string) {
        if (!region) return;
        let regions = this.courseRegions();
        if (regions.indexOf(region) === -1) regions.push(region);
        this.courseDraft.regions = regions;
        this.courseDraft.region = regions.join(', ');
    }

    private commitPendingCourseRegion() {
        let region = String(this.courseRegionInput || '').trim();
        if (!region) return;
        this.commitCourseRegion(region);
        this.courseRegionInput = '';
        this.courseRegionSearchOpen = false;
    }

    public async onCourseCoverFile(event: any) {
        let file = event && event.target && event.target.files ? event.target.files[0] : null;
        if (!file || typeof FileReader === 'undefined') return;
        let reader = new FileReader();
        reader.onload = async () => {
            this.courseDraft.photo = String(reader.result || '');
            this.courseDraft.photoName = file.name || '대표 이미지';
            await this.service.render();
        };
        reader.readAsDataURL(file);
    }

    private courseDateKeyFromPointer(event: any) {
        if (!event || typeof document === 'undefined') return '';
        let target = document.elementFromPoint(event.clientX, event.clientY);
        while (target) {
            if (target.getAttribute) {
                let key = target.getAttribute('data-course-date-key');
                if (key) return key;
            }
            target = target.parentElement;
        }
        return '';
    }

    private setCourseDraftDateRange(start: string, end: string) {
        let range = [start, end].sort();
        this.courseDraft.scheduleDate = range[0];
        this.courseDraft.scheduleEndDate = range[1];
        this.courseDraft.schedule = range[0] === range[1]
            ? this.formatCourseDateLabel(range[0])
            : `${this.formatCourseDateLabel(range[0])} - ${this.formatCourseDateLabel(range[1])}`;
    }

    private formatIsoDate(date: Date) {
        let year = date.getFullYear();
        let month = String(date.getMonth() + 1).padStart(2, '0');
        let day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    }

    private formatCourseDateLabel(value: string) {
        let date = new Date(`${value}T00:00:00`);
        if (isNaN(date.getTime())) return value;
        let weekday = ['일', '월', '화', '수', '목', '금', '토'][date.getDay()];
        return `${date.getMonth() + 1}.${date.getDate()} ${weekday}`;
    }

    private koreanInitials(value: string) {
        const initials = 'ㄱㄲㄴㄷㄸㄹㅁㅂㅃㅅㅆㅇㅈㅉㅊㅋㅌㅍㅎ';
        return String(value || '').split('').map((char: string) => {
            let code = char.charCodeAt(0) - 44032;
            if (code < 0 || code > 11171) return char.toLowerCase();
            return initials[Math.floor(code / 588)] || char;
        }).join('');
    }

    private isKoreanInitialQuery(value: string) {
        return /^[ㄱ-ㅎ]+$/.test(String(value || '').trim());
    }

    private courseKnownRegions() {
        return [
            { label: '수원', area: '경기 수원', lat: 37.2636, lng: 127.0286 },
            { label: '수지', area: '경기 용인 수지', lat: 37.3221, lng: 127.0977 },
            { label: '서울 성수', area: '서울 성동구 성수동', lat: 37.5446, lng: 127.0557 },
            { label: '서울 홍대', area: '서울 마포구', lat: 37.5563, lng: 126.9236 },
            { label: '서울 잠실', area: '서울 송파구', lat: 37.5133, lng: 127.1002 },
            { label: '판교', area: '경기 성남 분당', lat: 37.3947, lng: 127.1112 },
            { label: '부산', area: '부산 해운대', lat: 35.1631, lng: 129.1635 },
            { label: '제주', area: '제주 제주시', lat: 33.4996, lng: 126.5312 },
            { label: '강릉', area: '강원 강릉', lat: 37.7519, lng: 128.8761 },
            { label: '여수', area: '전남 여수', lat: 34.7604, lng: 127.6622 },
            { label: '전주', area: '전북 전주', lat: 35.8242, lng: 127.1480 },
            { label: '인천', area: '인천 송도', lat: 37.3895, lng: 126.6450 },
            { label: '대구', area: '대구 중구', lat: 35.8714, lng: 128.6014 }
        ];
    }

    public courseInfoStepComplete() {
        return !!String(this.courseDraft.title || '').trim()
            && (this.courseRegions().length > 0 || !!String(this.courseRegionInput || '').trim())
            && !!String(this.courseDraft.schedule || '').trim();
    }

    public async setCourseBuilderStep(step: string) {
        if (step === 'info') {
            await this.backToCourseInfo();
            return;
        }
        if (step === 'places' && this.courseBuilderMode === 'ai' && this.courseBuilderPlaces.length === 0) {
            this.courseBuilderError = 'AI 추천 코스를 선택해주세요.';
            await this.service.render();
            return;
        }
        if (step === 'places') await this.continueCourseInfo();
    }

    public async continueCourseInfo() {
        this.commitPendingCourseRegion();
        if (!this.courseInfoStepComplete()) {
            this.courseBuilderError = '코스 제목, 지역, 일정을 입력해주세요.';
            await this.service.render();
            return;
        }

        this.ensureCourseDraftMeta();
        this.ensureCourseBuilderDaysFromDates();
        this.courseBuilderError = '';
        this.courseBuilderStep = 'places';
        await this.service.render();
        this.scheduleCourseMapRender();
        if (!this.coursePlaceSearchResults.length) await this.searchCoursePlaces(false);
    }

    public async backToCourseInfo() {
        this.courseBuilderStep = this.courseBuilderMode === 'ai' ? 'ai' : 'info';
        this.coursePublishModalOpen = false;
        this.coursePlaceSearchOpen = false;
        this.courseBuilderError = '';
        this.clearCourseNaverOverlays();
        await this.service.render();
    }

    public courseAiReady() {
        return (this.courseRegions().length > 0 || !!String(this.courseRegionInput || '').trim())
            && !!String(this.courseDraft.schedule || '').trim();
    }

    public courseAiOptions() {
        return [];

        let region = String(this.courseDraft.region || '서울 성수').trim();
        let schedule = String(this.courseDraft.schedule || '반나절').trim();
        let companion = this.companionTypeLabel();
        let mood = String(this.courseAiMood || '취향 맞춤').trim();
        return [
            {
                title: `${region} 감성 산책 코스`,
                summary: `${schedule} · ${companion} · ${mood}`,
                category: '산책',
                places: ['동네 카페', '전시 공간', '산책로']
            },
            {
                title: `${region} 맛집 중심 코스`,
                summary: `${schedule} · 이동 적게 · 식사와 카페 중심`,
                category: '맛집',
                places: ['브런치 맛집', '소품샵', '디저트 카페']
            },
            {
                title: `${region} 여유 코스`,
                summary: `${schedule} · ${companion} · 사진 찍기 좋은 동선`,
                category: '여행',
                places: ['핫플 거리', '편집샵', '야경 스팟']
            }
        ];
    }

    public async applyAiCourseOption(option: any) {
        this.commitPendingCourseRegion();
        if (!this.courseAiReady()) {
            this.courseBuilderError = '어디로, 언제 갈지 입력해주세요.';
            await this.service.render();
            return;
        }

        let region = String(this.courseDraft.region || '').trim();
        this.courseBuilderMode = 'ai';
        this.courseDraft.title = option.title || this.courseDraft.title || `${region} AI 추천 코스`;
        this.courseDraft.category = option.category || this.courseDraft.category || '여행';
        this.courseDraft.description = option.summary || this.courseDraft.description || '';
        let rows: any[] = [];
        try {
            let center = this.courseSearchCenter();
            let result = await wiz.call('search_course_places', {
                lat: center.lat,
                lng: center.lng,
                keyword: option.category || '',
                region,
                limit: 3
            });
            if (result.code === 200) rows = (result.data && result.data.rows) || [];
        } catch (e) { }
        rows = rows.filter((place: any) => !!String(place.place_id || place.id || '').replace(/^place-/, ''));

        if (rows.length === 0) {
            this.courseBuilderError = '실제 장소를 찾지 못해 코스를 만들지 않았어요. 직접 장소를 검색해주세요.';
            await this.service.render();
            return;
        }

        this.courseBuilderPlaces = rows.slice(0, 3).map((place: any, index: number) => ({
                placeId: String(place.place_id || place.id || '').replace(/^place-/, ''),
                name: place.name || place.title || '장소',
                lat: this.safeNumber(place.lat || place.latitude),
                lng: this.safeNumber(place.lng || place.longitude),
                order: index + 1,
                visitTime: this.visitTimeForIndex(index),
                memo: '',
                area: place.area || region,
                address: place.address || '',
                category: '',
                categorySelected: false,
                image: place.image || '',
                manualTime: false
            }));
        this.normalizeCourseBuilderOrder(true);
        this.applyCourseDraftFromPlaces();
        this.courseBuilderError = '';
        this.courseBuilderStep = 'places';
        await this.service.render();
        this.scheduleCourseMapRender();
    }

    public async toggleCourseCompanion() {
        this.courseDraft.companionEnabled = !this.courseDraft.companionEnabled;
        await this.service.render();
    }

    public async saveCourseDraft() {
        let saved = this.persistCourseDraft();
        if (!saved) {
            await this.showSaveHint('임시 저장 공간이 부족해 저장하지 못했어요.');
            return;
        }
        await this.showCourseDraftSavedToast();
    }

    private async showCourseDraftSavedToast() {
        this.courseDraftSavedToastVisible = true;
        if (this.courseDraftSavedToastTimer) clearTimeout(this.courseDraftSavedToastTimer);
        if (typeof window !== 'undefined') {
            this.courseDraftSavedToastTimer = window.setTimeout(async () => {
                this.courseDraftSavedToastVisible = false;
                await this.service.render();
            }, 2000);
        }
        await this.service.render();
    }

    public async saveCourseToMyCourses() {
        if (this.courseMySaveSubmitting) return;
        let title = String(this.courseDraft.title || '').trim();
        if (!title) {
            await this.showSaveHint('코스 제목을 입력해주세요.');
            return;
        }
        if (this.allCourseLocationPlaces().length === 0) {
            this.courseBuilderStep = 'places';
            await this.showSaveHint('내 코스에 저장할 장소를 1개 이상 담아주세요.');
            await this.focusCoursePlaceSearch();
            return;
        }
        let emptyDay = this.courseBuilderEmptyLocationDay();
        if (emptyDay) {
            await this.moveToEmptyCourseBuilderDay(emptyDay, '내 코스에 저장하기 전에');
            return;
        }

        this.ensureCourseDraftMeta();
        this.persistCourseDraft();
        this.courseMySaveSubmitting = true;
        await this.service.render();

        let payload = {
            ...this.courseBuilderPayload(),
            is_public: false,
            is_featured: false
        };
        let result = await wiz.call('create_builder_course', { data: JSON.stringify(payload) });
        if (result.code !== 200) {
            this.courseMySaveSubmitting = false;
            if (result.code === 401) {
                await this.openAuthModal('login');
                return;
            }
            await this.showSaveHint(this.responseMessage(result.data, '내 코스 저장에 실패했습니다.'));
            return;
        }

        let course = this.courseRowToCard(result.data && result.data.row ? result.data.row : null, payload);
        this.rememberOwnedCourseRow(result.data && result.data.row);
        this.courseMySaveSubmitting = false;

        course.source = 'mine';
        course.mine = true;
        course.saved = false;
        this.courses = [course, ...this.courses.filter((item: any) => item && item.id !== course.id)];
        this.clearCourseDraftStorage();
        this.courseComposerOpen = false;
        this.courseDraftArchiveOpen = false;
        this.courseBuilderMode = '';
        this.courseBuilderStep = 'mode';
        this.activeTab = 'my';
        this.myProfileTab = 'myCourses';
        this.persistAccessState();
        this.replaceAccessUrl();
        await this.showSaveHint('내 코스에 저장했어요.');
    }

    public hasSavedCourseDraft() {
        this.ensureCourseDraftArchiveSummary();
        return !!this.courseDraftArchiveSummary;
    }

    public savedCourseDraftTitle() {
        this.ensureCourseDraftArchiveSummary();
        let saved = this.courseDraftArchiveSummary;
        let title = saved ? String(saved.title || '').trim() : '';
        if (title) return title;
        let region = saved ? String(saved.region || '').trim() : '';
        return region ? `${region} 새 코스` : '저장된 새 코스';
    }

    public savedCourseDraftMeta() {
        this.ensureCourseDraftArchiveSummary();
        let saved = this.courseDraftArchiveSummary;
        if (!saved) return '임시저장한 코스가 없습니다.';
        let parts = [saved.region, saved.schedule].map((value: any) => String(value || '').trim()).filter((value: string) => !!value);
        if (saved.placeCount > 0) parts.push(`${saved.placeCount}곳`);
        return parts.length ? parts.join(' · ') : '기본 정보 작성 중';
    }

    public savedCourseDraftTime() {
        this.ensureCourseDraftArchiveSummary();
        let saved = this.courseDraftArchiveSummary;
        if (!saved) return '';
        let date = new Date(saved.savedAt);
        if (Number.isNaN(date.getTime())) return '';
        return `${date.getMonth() + 1}.${date.getDate()} ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
    }

    public async publishCourse() {
        await this.openCoursePublishModal();
    }

    public async openCoursePublishModal() {
        let title = String(this.courseDraft.title || '').trim();
        if (!title) {
            await this.showSaveHint('코스 제목을 입력해주세요.');
            return;
        }
        if (this.allCourseLocationPlaces().length === 0) {
            this.courseBuilderStep = 'places';
            await this.showSaveHint('첫 장소를 검색해서 담아보세요.');
            await this.focusCoursePlaceSearch();
            return;
        }
        let emptyDay = this.courseBuilderEmptyLocationDay();
        if (emptyDay) {
            await this.moveToEmptyCourseBuilderDay(emptyDay, '게시하기 전에');
            return;
        }

        this.ensureCourseDraftMeta();
        this.syncCurrentCourseBuilderDay();
        this.coursePublishModalOpen = true;
        await this.service.render();
    }

    public async closeCoursePublishModal() {
        this.coursePublishModalOpen = false;
        await this.service.render();
    }

    public async submitCoursePublish() {
        if (this.coursePublishSubmitting) return;

        let title = String(this.courseDraft.title || '').trim();
        if (!title) {
            this.courseBuilderError = '코스 제목을 입력해주세요.';
            await this.service.render();
            return;
        }
        if (this.allCourseLocationPlaces().length === 0) {
            this.courseBuilderError = '코스에 담을 장소를 1개 이상 추가해주세요.';
            await this.service.render();
            return;
        }
        let emptyDay = this.courseBuilderEmptyLocationDay();
        if (emptyDay) {
            this.coursePublishModalOpen = false;
            await this.moveToEmptyCourseBuilderDay(emptyDay, '게시하기 전에');
            return;
        }
        if (this.courseDraft.companionEnabled) {
            let capacity = Number(this.courseDraft.companionCapacity);
            if (!Number.isInteger(capacity) || capacity < 1 || capacity > 8) {
                this.courseBuilderError = '동행 모집 인원을 1명 이상 8명 이하로 입력해주세요.';
                await this.service.render();
                return;
            }
        }

        this.courseBuilderError = '';
        this.coursePublishSubmitting = true;
        await this.service.render();

        let payload = this.courseBuilderPayload();
        let result = await wiz.call('create_builder_course', { data: JSON.stringify(payload) });
        this.coursePublishSubmitting = false;

        if (result.code !== 200) {
            if (result.code === 401) {
                this.coursePublishModalOpen = false;
                await this.openAuthModal('login');
                return;
            }
            this.courseBuilderError = this.responseMessage(result.data, '코스 게시에 실패했습니다.');
            await this.service.render();
            return;
        }

        let row = result.data && result.data.row ? result.data.row : null;
        let course = this.courseRowToCard(row, payload);
        this.rememberOwnedCourseRow(row);
        this.courses = [course, ...this.courses.filter((item: any) => item.id !== course.id)];
        this.recommendations = [course, ...this.recommendations.filter((item: any) => item.id !== course.id)];

        let companionShareFailed = false;
        if (this.courseDraft.companionEnabled) {
            let allItems = this.allCourseBuilderItems();
            let firstItem = allItems[0] || {};
            let companionPost: any = {
                id: `mate-${course.id}`,
                courseId: course.id,
                courseConfirmed: true,
                title: `${course.title} 동행 모집`,
                route: course.title,
                routeStops: allItems.map((place: any) => place && place.name ? place.name : '').filter((name: string) => !!name),
                location: course.location,
                date: String(this.courseDraft.schedule || '일정 협의').trim(),
                time: String(firstItem.visitTime || '시간 협의').trim(),
                capacity: Number(this.courseDraft.companionCapacity || 2),
                applicants: 0,
                estimatedCost: String(this.courseDraft.companionCost || '비용 협의').trim(),
                budgetStyle: String(this.courseDraft.companionBudgetStyle || '').trim(),
                pace: String(this.courseDraft.companionPace || '').trim(),
                moodTags: this.splitList(this.courseDraft.companionMood || ''),
                flexibility: this.splitList(this.courseDraft.companionFlexible || ''),
                interestTags: this.uniqueTags([
                    ...allItems.map((place: any) => place && (place.category || place.kind) ? (place.category || place.kind) : ''),
                    ...this.courseDraftPlaces()
                ]).slice(0, 6),
                smoking: String(this.courseDraft.companionSmoking || '').trim(),
                drinking: String(this.courseDraft.companionDrinking || '').trim(),
                verificationRequired: true,
                hostHistory: `동행 ${this.travelResumeCompanionUses()}회 · 후기 확인`,
                meetingPoint: String(this.courseDraft.companionMeetingPoint || '').trim(),
                packingItems: ['신분증', '보조배터리', '개인 준비물'],
                intro: String(this.courseDraft.companionIntro || '함께 여행할 동행을 모집합니다.').trim(),
                host: this.myDisplayName(),
                owned: true,
                image: this.courseDraft.photoName ? String(course.cover_image || course.image || '').trim() : '',
                status: 'open',
                saved: false,
                applications: []
            };
            let savedCompanionPost = await this.saveCompanionPostRemote(companionPost);
            if (savedCompanionPost) {
                companionPost = savedCompanionPost;
                this.companionPosts = [
                    companionPost,
                    ...this.companionPosts.filter((item: any) => item && item.id !== companionPost.id)
                ];
                this.persistCompanionPostsCache();
            } else {
                companionShareFailed = true;
                this.courseBuilderError = '코스는 게시됐지만 동행 모집글 공유에 실패했습니다. 코스에서 다시 모집해주세요.';
            }
        }

        this.courseDraft = this.defaultCourseDraft();
        this.courseBuilderPlaces = [];
        this.coursePlaceSearchQuery = '';
        this.coursePlaceSearchResults = [];
        this.courseDraftSavedAt = '';
        this.courseBuilderMode = '';
        this.courseBuilderStep = 'mode';
        this.courseAiMood = '';
        this.clearCourseDraftStorage();
        this.coursePublishModalOpen = false;
        this.courseComposerOpen = false;
        this.activeTab = 'home';
        this.homeContentTab = 'courses';
        this.persistAccessState();
        this.replaceAccessUrl();
        await this.showSaveHint(companionShareFailed
            ? '코스는 게시됐지만 동행 모집글은 공유하지 못했어요.'
            : '코스가 게시됐어요.');
    }

    public coursePublishDayRows() {
        let days = this.courseBuilderDays.length > 0
            ? this.courseBuilderDays
            : [{ label: '1일차', dateLabel: '', places: this.courseBuilderPlaces }];
        return days.map((day: any, index: number) => ({
            label: day && day.label ? day.label : `${index + 1}일차`,
            dateLabel: String(day && day.dateLabel || '').trim(),
            placeCount: (day && Array.isArray(day.places) ? day.places : [])
                .filter((item: any) => !this.isCourseScheduleItem(item)).length
        }));
    }

    private courseBuilderEmptyLocationDay() {
        this.syncCurrentCourseBuilderDay();
        if (this.courseBuilderDays.length <= 1) return null;
        return this.courseBuilderDays.find((day: any) => {
            let items = day && Array.isArray(day.places) ? day.places : [];
            return !items.some((item: any) => !this.isCourseScheduleItem(item));
        }) || null;
    }

    private async moveToEmptyCourseBuilderDay(day: any, prefix: string) {
        let index = this.courseBuilderDays.indexOf(day);
        this.coursePublishModalOpen = false;
        this.courseBuilderStep = 'places';
        if (index >= 0) {
            this.courseBuilderDayIndex = index;
            this.courseBuilderPlaces = this.cloneCourseBuilderPlaces(day.places || []);
        }
        this.courseBuilderError = `${prefix} ${day.label || '해당 일차'}에 장소를 1개 이상 추가해주세요.`;
        this.clearCourseNaverOverlays();
        await this.service.render();
        this.scheduleCourseMapRender();
        await this.focusCoursePlaceSearch();
    }

    public async onCoursePlaceSearchInput() {
        this.coursePlaceSearchOpen = true;
        if (this.coursePlaceSearchTimer) clearTimeout(this.coursePlaceSearchTimer);
        this.coursePlaceSearchTimer = setTimeout(() => {
            this.searchCoursePlaces(false);
        }, 220);
        await this.service.render();
    }

    public async openCoursePlaceSearch() {
        this.coursePlaceSearchOpen = true;
        if (!this.coursePlaceSearchResults.length) await this.searchCoursePlaces(false);
        await this.service.render();
    }

    public async closeCoursePlaceSearch() {
        if (!this.coursePlaceSearchOpen) return;
        this.coursePlaceSearchOpen = false;
        this.coursePlaceSearching = false;
        this.coursePlaceSearchRequestToken += 1;
        if (this.coursePlaceSearchTimer) {
            clearTimeout(this.coursePlaceSearchTimer);
            this.coursePlaceSearchTimer = null;
        }
        await this.service.render();
    }

    public async onCourseComposerClick(event: any) {
        if (!this.coursePlaceSearchOpen) return;
        let target = event && event.target;
        if (target && target.closest) {
            if (target.closest('.course-search-shell')) return;
            if (target.closest('.course-place-search-trigger')) return;
        }
        await this.closeCoursePlaceSearch();
    }

    public async searchCoursePlaces(showLoading: boolean = true) {
        let requestToken = ++this.coursePlaceSearchRequestToken;
        if (showLoading) {
            this.coursePlaceSearching = true;
            await this.service.render();
        }
        this.commitPendingCourseRegion();
        let rows: any[] = [];
        let source = '';
        try { rows = await this.searchStoredCoursePlaces(); } catch (e) { rows = []; }
        source = rows.length ? 'stored' : '';
        if (requestToken !== this.coursePlaceSearchRequestToken) return;
        this.coursePlaceSearching = false;
        this.coursePlaceSearchSource = source;
        this.coursePlaceSearchResults = rows;
        await this.service.render();
        this.scheduleCourseMapRender();
    }

    private async searchStoredCoursePlaces() {
        let targets = this.courseSearchRegions();
        let batches = await Promise.all(targets.map(async (target: any) => {
            let result = await wiz.call('search_course_places', {
                lat: target.lat,
                lng: target.lng,
                keyword: this.coursePlaceSearchQuery,
                region: target.label,
                limit: 8
            });
            let data = result && result.data ? result.data : {};
            let clientId = String(data.naver_maps_client_id || data.naverMapsClientId || '').trim();
            if (clientId) this.naverMapsClientId = clientId;
            return result.code === 200 ? (data.rows || []) : [];
        }));
        return this.interleaveCoursePlaceBatches(batches, 12);
    }

    private courseSearchRegions() {
        let selected = this.courseRegions();
        let known = this.courseKnownRegions();
        let targets = selected.map((region: string) => {
            let normalized = region.toLowerCase();
            let match = known.find((option: any) => {
                let label = String(option.label || '').toLowerCase();
                let area = String(option.area || '').toLowerCase();
                return label === normalized || area === normalized || label.indexOf(normalized) > -1 || normalized.indexOf(label) > -1;
            });
            let center = match || this.courseRegionCenter(region) || this.courseSearchCenter();
            return { label: region, lat: Number(center.lat), lng: Number(center.lng) };
        });
        if (targets.length) return targets;
        let center = this.courseSearchCenter();
        return [{ label: this.courseRegionLabel(), lat: Number(center.lat), lng: Number(center.lng) }];
    }

    private inferCoursePlaceCategory(place: any) {
        let types = Array.isArray(place && place.types)
            ? place.types.map((value: any) => String(value || '').toLowerCase())
            : [];
        let text = [place && place.category, place && place.kind, place && place.name, place && place.title]
            .map((value: any) => String(value || '').toLowerCase())
            .join(' ');
        let hasType = (...values: string[]) => values.some((value: string) => types.indexOf(value) > -1);
        let hasText = (pattern: RegExp) => pattern.test(text);

        if (hasType('cafe', 'bakery') || hasText(/카페|커피|베이커리|디저트|찻집|cafe|bakery/)) return '카페';
        if (hasType('restaurant', 'food', 'meal_delivery', 'meal_takeaway', 'bar') || hasText(/식당|맛집|음식점|레스토랑|국밥|횟집|카레|치킨|피자|고기|분식|restaurant|food/)) return '식당';
        if (hasType('lodging', 'hotel', 'motel', 'campground', 'rv_park') || hasText(/숙소|숙박|호텔|모텔|리조트|펜션|게스트하우스|캠핑|lodging|hotel|motel/)) return '숙박';
        if (hasType('park') || hasText(/공원|수목원|산책로|숲길|park/)) return '공원';
        if (hasType('shopping_mall', 'store', 'department_store', 'market') || hasText(/쇼핑|백화점|아울렛|시장|상점|스토어|편집숍|shopping|store|market/)) return '쇼핑';
        if (hasType('museum', 'art_gallery', 'library', 'movie_theater') || hasText(/박물관|미술관|전시|갤러리|공연장|극장|문화관|museum|gallery/)) return '문화·전시';
        if (hasType('amusement_park', 'aquarium', 'zoo', 'bowling_alley', 'spa', 'gym') || hasText(/체험|액티비티|테마파크|놀이공원|수족관|동물원|스파|activity|amusement|aquarium/)) return '액티비티';
        if (hasType('tourist_attraction', 'point_of_interest', 'natural_feature', 'place_of_worship') || hasText(/관광|명소|전망대|해변|폭포|오름|사찰|성당|궁|유적|attraction|landmark/)) return '관광명소';
        return '기타';
    }

    private interleaveCoursePlaceBatches(batches: any[][], limit: number) {
        let rows: any[] = [];
        let seen: any = {};
        let max = Math.max(0, ...batches.map((batch: any[]) => batch.length));
        for (let index = 0; index < max && rows.length < limit; index++) {
            batches.forEach((batch: any[]) => {
                let row = batch[index];
                let key = row ? String(row.place_id || row.id || `${row.name}-${row.address}`) : '';
                if (!row || !key || seen[key] || rows.length >= limit) return;
                seen[key] = true;
                rows.push(row);
            });
        }
        return rows;
    }

    public async addCoursePlace(place: any) {
        if (!place) return;
        let placeId = String(place.place_id || place.id || '').replace(/^place-/, '');
        if (!placeId || this.courseBuilderPlaces.some((item: any) => item.placeId === placeId)) {
            this.coursePlaceSearchOpen = false;
            await this.service.render();
            return;
        }

        let inferredCategory = this.inferCoursePlaceCategory(place);
        let next = {
            placeId,
            providerPlaceId: place.provider_place_id || '',
            placeProvider: place.place_provider || place.source || '',
            source: place.source || 'stored',
            name: place.name || place.title || '장소',
            lat: this.safeNumber(place.lat || place.latitude),
            lng: this.safeNumber(place.lng || place.longitude),
            order: this.courseBuilderPlaces.length + 1,
            visitTime: this.nextCourseVisitTime(),
            memo: '',
            area: place.area || '',
            address: place.address || '',
            category: inferredCategory,
            categorySelected: true,
            categorySource: 'auto',
            image: place.image || '',
            rating: place.rating || null,
            manualTime: false
        };

        this.courseBuilderPlaces = [...this.courseBuilderPlaces, next];
        this.normalizeCourseBuilderOrder(true);
        this.applyCourseDraftFromPlaces();
        this.coursePlaceSearchOpen = false;
        if (!this.courseDraft.region) this.courseDraft.region = this.courseRegionLabel();
        if (!this.courseDraft.photo && next.image) this.courseDraft.photo = next.image;
        await this.service.render();
        this.scheduleCourseMapRender();
    }

    public async openCourseScheduleForm() {
        this.courseScheduleDraft = { type: 'flight', title: '', time: '', detail: '' };
        this.courseScheduleFormOpen = true;
        await this.service.render();
    }

    public async closeCourseScheduleForm() {
        this.courseScheduleFormOpen = false;
        await this.service.render();
    }

    public isCourseScheduleItem(place: any) {
        if (!place) return false;
        if (place.isSchedule === true) return true;
        let type = String(place.itemType || place.item_type || '').trim();
        return ['flight', 'train', 'bus', 'ferry', 'lodging', 'meeting', 'memo'].indexOf(type) > -1;
    }

    public courseScheduleTypeLabel(type: string) {
        let labels: any = { flight: '항공편', train: '기차', bus: '버스', ferry: '선박', lodging: '숙박', meeting: '약속', memo: '기타 일정' };
        return labels[String(type || '')] || '기타 일정';
    }

    public courseScheduleIcon(type: string) {
        let icons: any = { flight: 'fa-plane', train: 'fa-train', bus: 'fa-bus', ferry: 'fa-ship', lodging: 'fa-bed', meeting: 'fa-people-arrows', memo: 'fa-calendar-check' };
        return icons[String(type || '')] || 'fa-calendar-check';
    }

    public async openCourseScheduleDetail(place: any) {
        if (!this.isCourseScheduleItem(place)) return;
        let type = String(place.itemType || place.item_type || 'memo').trim();
        this.courseScheduleDetail = {
            name: String(place.name || '일정').trim(),
            type,
            typeLabel: this.courseScheduleTypeLabel(type),
            icon: this.courseScheduleIcon(type),
            time: this.courseVisitTimeLabel(place.visitTime || place.visit_time || ''),
            detail: String(place.memo || '등록된 상세 정보가 없습니다.').trim(),
            location: String(place.area || place.address || '').trim()
        };
        this.courseScheduleDetailOpen = true;
        await this.service.render();
    }

    public async closeCourseScheduleDetail() {
        this.courseScheduleDetailOpen = false;
        this.courseScheduleDetail = {};
        await this.service.render();
    }

    public courseFlightAirport() {
        let region = String(this.courseRegions().join(' ') || this.courseDraft.region || '').trim();
        if (!/제주/.test(region)) return null;
        return {
            name: '제주국제공항',
            mapLabel: '제주공항',
            address: '제주특별자치도 제주시 공항로 2',
            lat: 33.510413,
            lng: 126.491353
        };
    }

    public async addCourseScheduleItem() {
        let draft = this.courseScheduleDraft || {};
        let type = String(draft.type || 'memo').trim();
        let title = String(draft.title || '').trim();
        if (!title) {
            this.courseBuilderError = '추가할 일정 제목을 입력해주세요.';
            await this.service.render();
            return;
        }
        let airport = type === 'flight' ? this.courseFlightAirport() : null;
        let item = {
            placeId: `schedule-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
            source: 'schedule',
            itemType: type,
            isSchedule: true,
            name: title,
            lat: airport ? airport.lat : null,
            lng: airport ? airport.lng : null,
            order: this.courseBuilderPlaces.length + 1,
            visitTime: String(draft.time || '').trim() || this.nextCourseVisitTime(),
            memo: String(draft.detail || '').trim(),
            area: airport ? airport.mapLabel : this.courseScheduleTypeLabel(type),
            address: airport ? airport.address : '',
            mapLabel: airport ? airport.mapLabel : '',
            showOnMap: !!airport,
            category: this.courseScheduleTypeLabel(type),
            categorySelected: true,
            image: '',
            manualTime: true
        };
        this.courseBuilderPlaces = [...this.courseBuilderPlaces, item];
        this.normalizeCourseBuilderOrder(false);
        this.applyCourseDraftFromPlaces();
        this.courseScheduleFormOpen = false;
        this.courseBuilderError = '';
        await this.service.render();
        this.scheduleCourseMapRender();
    }

    public async removeCoursePlace(index: number) {
        if (index < 0 || index >= this.courseBuilderPlaces.length) return;
        this.courseBuilderPlaces.splice(index, 1);
        this.normalizeCourseBuilderOrder(true);
        this.applyCourseDraftFromPlaces();
        await this.service.render();
        this.scheduleCourseMapRender();
    }

    public coursePlaceDragStart(index: number) {
        this.courseDragIndex = index;
    }

    public coursePlaceDragOver(event: any) {
        if (event && event.preventDefault) event.preventDefault();
    }

    public async coursePlaceDrop(index: number) {
        if (this.courseDragIndex < 0 || this.courseDragIndex === index) return;
        let item = this.courseBuilderPlaces.splice(this.courseDragIndex, 1)[0];
        this.courseBuilderPlaces.splice(index, 0, item);
        this.courseDragIndex = -1;
        this.normalizeCourseBuilderOrder(true);
        this.applyCourseDraftFromPlaces();
        await this.service.render();
        this.scheduleCourseMapRender();
    }

    public async updateCourseVisitTime(place: any) {
        if (place) place.manualTime = true;
        this.syncCurrentCourseBuilderDay();
        await this.service.render();
    }

    public courseVisitTimeLabel(value: string) {
        let match = String(value || '').trim().match(/^(\d{1,2}):(\d{2})$/);
        if (!match) return '시간 선택';
        let hour = Math.max(0, Math.min(23, Number(match[1])));
        let minute = match[2];
        let period = hour < 12 ? '오전' : '오후';
        let displayHour = hour % 12 || 12;
        return `${period} ${displayHour}:${minute}`;
    }

    public async openCoursePlaceDetail(place: any, event?: any) {
        let target = event && event.target;
        if (target && target.closest && target.closest('.timeline-time, .timeline-actions, input, select, button')) return;
        if (!place) return;
        let name = String(place.name || place.title || '').trim();
        if (!name) return;
        this.coursePlaceDetail = {
            name,
            address: String(place.address || place.area || this.courseRegionLabel() || '').trim(),
            category: String(place.category || '').trim(),
            image: String(place.image || '').trim(),
            rating: place.rating || null,
            reviewCount: place.reviewCount || place.review_count || place.user_ratings_total || null,
            price: String(place.price || place.price_level || '가격 정보 없음'),
            hours: String(place.hours || place.opening_hours || place.openingHours || '영업시간 정보 없음'),
            businessStatus: String(place.businessStatus || place.business_status || '').trim(),
            phone: String(place.phone || place.tel || place.telephone || '').trim(),
            description: String(place.description || place.memo || place.overview || '').trim(),
            time: String(place.time || place.visit_time || place.visitTime || '').trim(),
            reviews: Array.isArray(place.reviews) ? place.reviews : [],
            weekdayHours: Array.isArray(place.weekdayHours)
                ? place.weekdayHours
                : (Array.isArray(place.weekday_hours) ? place.weekday_hours : []),
            menuCategories: [],
            menuItems: [],
            menuLoaded: false,
            menuLoading: false,
            menuError: ''
        };
        this.coursePlaceDetailTab = 'overview';
        this.coursePlaceDetailOpen = true;
        this.coursePlaceDetailLoading = true;
        this.coursePlaceDetailError = '';
        await this.service.render();

        this.coursePlaceDetailLoading = false;
        await this.service.render();
    }

    public async closeCoursePlaceDetail() {
        this.coursePlaceDetailOpen = false;
        this.coursePlaceDetailLoading = false;
        this.coursePlaceDetailError = '';
        await this.service.render();
    }

    public async setCoursePlaceDetailTab(tab: string) {
        if (['overview', 'menu', 'reviews', 'info'].indexOf(tab) < 0) return;
        this.coursePlaceDetailTab = tab;
        await this.service.render();
        if (tab !== 'menu' || this.coursePlaceDetail.menuLoaded || this.coursePlaceDetail.menuLoading) return;
        this.coursePlaceDetail.menuLoading = true;
        this.coursePlaceDetail.menuError = '';
        await this.service.render();
        try {
            let response = await wiz.call('naver_place_menu', {
                name: this.coursePlaceDetail.name || '',
                address: this.coursePlaceDetail.address || ''
            });
            let data = response && response.data ? response.data : {};
            this.coursePlaceDetail.menuItems = Array.isArray(data.menus) ? data.menus : [];
            this.coursePlaceDetail.naverPlaceName = String(data.place_name || '');
            if (!this.coursePlaceDetail.menuItems.length) {
                this.coursePlaceDetail.menuError = String(data.message || '네이버에 등록된 메뉴를 찾지 못했어요.');
            }
        } catch (e) {
            this.coursePlaceDetail.menuItems = [];
            this.coursePlaceDetail.menuError = '네이버 메뉴 정보를 불러오지 못했어요.';
        }
        this.coursePlaceDetail.menuLoaded = true;
        this.coursePlaceDetail.menuLoading = false;
        if (this.coursePlaceDetailOpen) await this.service.render();
    }

    public async updateCoursePlaceCategory(place: any) {
        if (!place) return;
        place.category = String(place.category || '').trim();
        place.categorySelected = !!place.category;
        this.applyCourseDraftFromPlaces();
        this.persistCourseDraft();
        await this.service.render();
    }

    public async completePlannerCourseEdit() {
        if (!this.courseComposerPlannerEdit || this.courseAiRebuilding) return;
        this.syncCurrentCourseBuilderDay();
        let emptyDay = this.courseBuilderDays.find((day: any) => !Array.isArray(day.places) || day.places.length === 0);
        if (emptyDay) {
            this.courseBuilderError = `${emptyDay.label || '일정'}에 장소를 1개 이상 남겨주세요.`;
            await this.service.render();
            return;
        }

        let originalDays = this.cloneCourseBuilderDays(this.courseBuilderOriginalDays);
        let editedBuilderDays = this.cloneCourseBuilderDays(this.courseBuilderDays);
        let prompt = this.plannerCourseEditPrompt(originalDays, editedBuilderDays);
        let editedPlannerDays = editedBuilderDays.map((day: any, dayIndex: number) => {
            let stops = (day.places || []).map((place: any, index: number) => ({
                key: place.originalKey || place.placeId || `edited-${dayIndex}-${index}`,
                placeId: place.placeId || '',
                time: place.visitTime || this.plannerTimeForIndex(index),
                timeLocked: !!place.manualTime,
                name: place.name || '장소',
                area: place.area || place.address || this.plannerCourseRegion,
                tag: place.memo || place.category || '추천 방문',
                move: '이동 확인 중',
                image: place.image || '',
                icon: this.plannerIconForCategory(place.category || ''),
                lat: this.safeNumber(place.lat),
                lng: this.safeNumber(place.lng),
                mapLeft: '',
                mapTop: ''
            }));
            stops = this.applyPlannerMapPositions(stops).map((stop: any, index: number) => ({
                ...stop,
                status: index < 2 ? 'active' : 'upcoming'
            }));
            return {
                label: day.label || `${dayIndex + 1}일차`,
                stops
            };
        });

        let activeDay = Math.max(0, Math.min(this.courseBuilderDayIndex, editedPlannerDays.length - 1));
        let history = this.messages
            .filter((message: any) => !message.loading && !message.seed)
            .slice(-12)
            .map((message: any) => ({ role: message.role, text: String(message.text || '') }));
        let summary = editedBuilderDays.map((day: any) => `${day.label} ${(day.places || []).length}곳`).join(', ');
        let requestTime = this.currentChatTimeLabel();
        this.messages.push({
            role: 'user',
            text: `코스 수정 완료: ${summary}. 수정한 일정으로 다시 계산해줘.`,
            time: requestTime
        });
        let reply: any = {
            role: 'assistant',
            text: '수정한 장소와 시간을 확인하고 동선과 이동시간을 다시 계산하고 있어요.',
            time: requestTime,
            loading: true
        };
        this.messages.push(reply);

        this.courseAiRebuilding = true;
        this.isPlannerGenerating = true;
        this.isChatSending = true;
        this.plannerCourseBaseTitle = String(this.courseDraft.title || this.plannerCourseBaseTitle || '내 여행').trim();
        this.plannerCourseRegion = String(this.courseDraft.region || this.plannerCourseRegion).trim();
        this.plannerCourseDays = editedPlannerDays;
        this.plannerCourseReady = true;
        this.plannerCourseVisible = true;
        this.applyPlannerCourseDay(activeDay);
        this.plannerRouteSource = 'AI 수정 내용 분석 중';

        this.courseComposerOpen = false;
        this.courseComposerPlannerEdit = false;
        this.courseBuilderMode = '';
        this.courseBuilderStep = 'mode';
        this.coursePlaceSearchOpen = false;
        this.clearCourseNaverOverlays();
        await this.service.render();
        this.refreshPlannerRouteWithGoogle();
        this.scrollToPlannerPreview();

        try {
            let response: any = await wiz.call('chat_send', {
                prompt,
                history: JSON.stringify(history),
                thread_id: this.activeChatThreadId
            });
            let code = Number(response && response.code ? response.code : 0);
            let data = response && response.data ? response.data : response;
            if (code === 200 && data) {
                reply.text = data.reply || '수정한 코스를 기준으로 동선과 시간을 다시 정리했어요.';
                this.activeChatThreadId = data.thread_id || this.activeChatThreadId;
            } else {
                reply.text = this.responseMessage(data || response, '수정한 일정은 반영했고, 지도 이동시간을 기준으로 코스를 다시 계산했어요.');
            }
        } catch (e) {
            reply.text = '수정한 일정은 반영했어요. AI 응답은 지연됐지만 지도 이동시간을 기준으로 코스를 다시 계산했습니다.';
        }

        reply.loading = false;
        this.courseAiRebuilding = false;
        this.isPlannerGenerating = false;
        this.isChatSending = false;
        this.courseBuilderDays = [];
        this.courseBuilderOriginalDays = [];
        this.courseBuilderPlaces = [];
        await this.service.render();
        this.scrollToLatest();
        this.scrollToPlannerPreview();
        await this.showSaveHint('수정한 코스로 다시 만들었어요.');
    }

    private plannerCourseEditPrompt(originalDays: any[], editedDays: any[]) {
        let changes: string[] = [];
        editedDays.forEach((day: any, dayIndex: number) => {
            let original = originalDays[dayIndex] || { places: [] };
            let beforePlaces = (original.places || []).map((place: any) => `${place.visitTime || ''} ${place.name || ''} ${place.memo || ''}`.trim());
            let afterPlaces = (day.places || []).map((place: any) => `${place.visitTime || ''} ${place.name || ''} ${place.memo || ''}`.trim());
            if (beforePlaces.join('|') !== afterPlaces.join('|')) {
                changes.push(`${day.label}: ${beforePlaces.join(' → ') || '없음'} => ${afterPlaces.join(' → ')}`);
            }
        });
        let schedule = editedDays.map((day: any) => {
            let places = (day.places || []).map((place: any) => {
                let coordinate = this.isFiniteNumber(place.lat) && this.isFiniteNumber(place.lng)
                    ? `${Number(place.lat).toFixed(5)},${Number(place.lng).toFixed(5)}`
                    : '좌표 없음';
                return `- ${place.visitTime || '시간 미정'} ${place.name} (${place.area || place.address || '위치 미정'}, ${coordinate}) / ${place.memo || place.category || '활동 미정'}`;
            }).join('\n');
            return `[${day.label}]\n${places}`;
        }).join('\n');
        return [
            '사용자가 기존 AI 여행 코스를 직접 수정했습니다.',
            `변경점: ${changes.join(' / ') || '장소별 세부 정보 확인'}`,
            schedule,
            '사용자가 선택한 장소와 순서를 우선 유지하세요. 변경된 장소 사이의 교통수단과 이동시간을 다시 확인하고, 수동으로 바꾼 방문시간은 가능한 한 유지해서 코스를 짧게 다시 설명해주세요.'
        ].join('\n').slice(0, 1950);
    }

    public async focusCoursePlaceSearch() {
        if (this.courseBuilderStep !== 'places') {
            if (!this.courseBuilderMode) this.courseBuilderMode = 'manual';
            this.courseBuilderStep = 'places';
            await this.service.render();
            this.scheduleCourseMapRender();
        }
        this.coursePlaceSearchOpen = true;
        await this.service.render();
        if (typeof document === 'undefined') return;
        let input: any = document.querySelector('.course-place-search-input');
        if (input && input.focus) input.focus();
    }

    public courseDraftPlaces() {
        if (this.courseBuilderDays.length > 0) {
            this.syncCurrentCourseBuilderDay();
            return this.courseBuilderDays.reduce((items: string[], day: any) => {
                return items.concat((day.places || []).map((place: any) => place.name));
            }, []).slice(0, 12);
        }
        if (this.courseBuilderPlaces.length > 0) {
            return this.courseBuilderPlaces.map((place: any) => place.name).slice(0, 8);
        }
        return this.splitList(this.courseDraft.places).slice(0, 8);
    }

    private allCourseBuilderItems() {
        this.syncCurrentCourseBuilderDay();
        if (this.courseBuilderDays.length > 0) {
            return this.courseBuilderDays.reduce((items: any[], day: any) => items.concat(day.places || []), []);
        }
        return this.courseBuilderPlaces;
    }

    private allCourseLocationPlaces() {
        return this.allCourseBuilderItems().filter((item: any) => !this.isCourseScheduleItem(item));
    }

    public courseRegionLabel() {
        let selected = this.courseRegions();
        if (selected.length) return selected.join(' · ');
        let counts: any = {};
        this.courseBuilderPlaces.forEach((place: any) => {
            let area = String(place.area || place.address || '').trim();
            if (!area) return;
            let label = area.split(/\s+/).slice(0, 2).join(' ');
            if (!label) return;
            counts[label] = (counts[label] || 0) + 1;
        });
        let best = Object.keys(counts).sort((a: string, b: string) => counts[b] - counts[a])[0];
        return best || String(this.courseDraft.region || this.selectedFilters.location || '서울 성수동').trim();
    }

    public courseWalkBadge() {
        let minutes = this.courseWalkingMinutes();
        let places = this.courseBuilderPlaces.filter((place: any) => !this.isCourseScheduleItem(place));
        if (places.length < 2) return '도보 총 0분';
        return `도보 총 ${minutes}분${this.hasFullCourseCoordinates() ? '' : ' 예상'}`;
    }

    public courseSegmentTravel(index: number) {
        if (index < 0 || index >= this.courseBuilderPlaces.length - 1) return null;
        let prev = this.courseBuilderPlaces[index];
        let next = this.courseBuilderPlaces[index + 1];
        let distance = this.distanceKm(prev.lat, prev.lng, next.lat, next.lng);
        if (distance === null) {
            return { distance: '거리 확인 중', drive: '확인 중', walk: '확인 중' };
        }
        let distanceLabel = distance < 1
            ? `${Math.max(1, Math.round(distance * 1000))}m`
            : `${distance.toFixed(distance < 10 ? 1 : 0)}km`;
        let driveMinutes = Math.max(2, Math.round((distance / 32) * 60));
        let walkMinutes = Math.max(2, Math.round((distance / 4.2) * 60));
        return {
            distance: `약 ${distanceLabel}`,
            drive: `${driveMinutes}분`,
            walk: `${walkMinutes}분`
        };
    }

    public courseTimelineMapSpots() {
        let places = this.courseBuilderPlaces.filter((place: any) => {
            let type = String(place.itemType || place.item_type || '').trim();
            return !this.isCourseScheduleItem(place) || (type === 'flight' && this.isFiniteNumber(place.lat) && this.isFiniteNumber(place.lng));
        });
        if (places.length === 0) return [];
        let coordinatePlaces = places.filter((place: any) => this.isFiniteNumber(place.lat) && this.isFiniteNumber(place.lng));
        if (coordinatePlaces.length === places.length) {
            let lats = places.map((place: any) => Number(place.lat));
            let lngs = places.map((place: any) => Number(place.lng));
            let minLat = Math.min(...lats);
            let maxLat = Math.max(...lats);
            let minLng = Math.min(...lngs);
            let maxLng = Math.max(...lngs);
            let latRange = Math.max(0.0001, maxLat - minLat);
            let lngRange = Math.max(0.0001, maxLng - minLng);
            return places.map((place: any) => ({
                name: place.mapLabel || (place.itemType === 'flight' ? place.area : '') || place.name,
                x: places.length === 1 ? 50 : 14 + ((Number(place.lng) - minLng) / lngRange * 72),
                y: places.length === 1 ? 50 : 82 - ((Number(place.lat) - minLat) / latRange * 64)
            }));
        }
        return places.map((place: any, index: number) => this.fallbackCourseDraftSpot(place.mapLabel || place.area || place.name, index, places.length));
    }

    public courseTimelineRoutePoints() {
        return this.courseTimelineMapSpots().map((spot: any) => `${spot.x},${spot.y}`).join(' ');
    }

    public courseDraftMapLabel() {
        return this.courseRegionLabel();
    }

    public courseDraftMapSpots() {
        return this.courseTimelineMapSpots();
    }

    public courseDraftRoutePoints() {
        return this.courseTimelineRoutePoints();
    }

    public companionTypeLabel() {
        let labels: any = { couple: '연인', friend: '친구', family: '가족', solo: '혼자' };
        return labels[this.courseDraft.companionType] || '미선택';
    }

    public visibilityLabel() {
        return this.courseDraft.isPublic ? '커뮤니티에 게시' : '나만 보기';
    }

    public async setCourseCompanionType(type: string) {
        if (['couple', 'friend', 'family', 'solo'].indexOf(type) < 0) return;
        this.courseDraft.companionType = type;
        await this.service.render();
    }

    public async toggleCourseVisibility() {
        this.courseDraft.isPublic = !this.courseDraft.isPublic;
        await this.service.render();
    }

    private courseBuilderPayload() {
        this.syncCurrentCourseBuilderDay();
        let region = String(this.courseRegions().join(', ') || this.courseDraft.region || this.courseRegionLabel() || '국내').trim();
        let title = String(this.courseDraft.title || '').trim();
        let description = String(this.courseDraft.description || '').trim();
        let days = this.courseBuilderDays.length > 0
            ? this.courseBuilderDays
            : [{ label: '1일차', date: this.courseDraft.scheduleDate || '', places: this.courseBuilderPlaces }];
        let places: any[] = [];
        days.forEach((day: any, dayIndex: number) => {
            (day.places || []).forEach((place: any, index: number) => places.push({
                place_id: place.placeId,
                provider_place_id: place.providerPlaceId || '',
                place_provider: place.placeProvider || place.source || '',
                name: place.name || '',
                area: place.area || '',
                address: place.address || '',
                category: place.categorySelected ? (place.category || '') : '',
                image: place.image || '',
                latitude: place.lat,
                longitude: place.lng,
                rating: place.rating,
                day: dayIndex + 1,
                day_label: day.label || `${dayIndex + 1}일차`,
                date: day.date || '',
                item_type: this.isCourseScheduleItem(place) ? (place.itemType || place.item_type || 'memo') : 'place',
                order_index: places.length + 1,
                visit_time: place.visitTime || '',
                memo: this.courseBuilderStoredPlaceMemo(place, day, dayIndex, index)
            }));
        });
        let dayCount = Math.max(1, days.length);
        let locationPlaces = places.filter((place: any) => place.item_type === 'place');

        let courseType = String(this.courseDraft.category || '').trim() === '데이트' ? '데이트' : '여행';
        return {
            title,
            region,
            category: courseType,
            description: description || places.map((place: any) => place.name).join(', '),
            cover_image: String(this.courseDraft.photo || '').trim(),
            image: String(this.courseDraft.photo || '').trim(),
            duration_type: dayCount > 1 ? 'overnight' : 'hours',
            duration_value: dayCount > 1 ? `${dayCount - 1}박 ${dayCount}일` : String(Math.max(1, Math.ceil((this.courseWalkingMinutes() + locationPlaces.length * 55) / 60))),
            companion_type: String(this.courseDraft.companionType || ''),
            is_public: !!this.courseDraft.isPublic,
            is_featured: !!this.courseDraft.isPublic,
            places,
            tags: this.uniqueTags([courseType, region, title, this.companionTypeLabel(), ...this.courseDraftPlaces()])
        };
    }

    private courseBuilderStoredPlaceMemo(place: any, day: any, dayIndex: number, index: number) {
        let dayNumber = dayIndex + 1;
        let meta = {
            item_type: this.isCourseScheduleItem(place) ? (place.itemType || place.item_type || 'memo') : 'place',
            day: dayNumber,
            day_label: day && day.label ? day.label : `${dayNumber}일차`,
            date: String(day && day.date || ''),
            name: String(place && place.name || ''),
            area: String(place && place.area || ''),
            address: String(place && place.address || ''),
            category: place && place.categorySelected ? String(place.category || '') : '',
            image: String(place && place.image || ''),
            latitude: place && place.lat,
            longitude: place && place.lng,
            memo: String(place && place.memo || ''),
            source_order: index + 1
        };
        return `__gachi_item__${JSON.stringify(meta)}`;
    }

    private courseRowToCard(row: any, fallback: any) {
        row = row || {};
        fallback = fallback || {};
        let id = row.id || `draft-course-${Date.now()}`;
        let region = row.region || fallback.region || this.courseRegionLabel();
        let courseType = row.category || fallback.category || '여행';
        let duration = row.duration || `${fallback.duration_value || '4'}시간`;
        let places = Array.isArray(row.places) && row.places.length > 0
            ? row.places
            : (Array.isArray(fallback.places) ? fallback.places : []);
        let rawTags = Array.isArray(row.tags) ? row.tags : (Array.isArray(fallback.tags) ? fallback.tags : []);
        let archived = !!row.archived || rawTags.indexOf('__gachi_archived__') > -1;
        return {
            id,
            title: row.title || fallback.title,
            location: region,
            summary: row.description || fallback.description || '직접 만든 여행 코스입니다.',
            description: row.description || fallback.description || '',
            duration,
            duration_type: row.duration_type || fallback.duration_type || (String(duration).indexOf('박') > -1 ? 'overnight' : 'hours'),
            duration_value: row.duration_value || fallback.duration_value || duration,
            distance: this.courseWalkBadge(),
            category: courseType,
            companion_type: row.companion_type || fallback.companion_type || '',
            is_public: typeof row.is_public !== 'undefined' ? !!row.is_public : !!fallback.is_public,
            is_featured: typeof row.is_featured !== 'undefined' ? !!row.is_featured : !!fallback.is_featured,
            is_hidden: typeof row.is_hidden !== 'undefined' ? !!row.is_hidden : !!fallback.is_hidden,
            icon: 'fa-route',
            tone: 'tone-rose',
            image: row.cover_image || row.image || fallback.cover_image || '',
            cover_image: row.cover_image || row.image || fallback.cover_image || '',
            saved: false,
            mine: true,
            source: 'mine',
            archived,
            places,
            route: row.route || fallback.route || {},
            authorId: row.author_id || fallback.author_id || row.user_id || fallback.user_id || '',
            authorName: row.author_name || fallback.author_name || '',
            authorHandle: row.author_handle || fallback.author_handle || '',
            authorInitial: row.author_initial || fallback.author_initial || '',
            authorFollowing: !!(row.author_following || fallback.author_following),
            tags: this.uniqueTags([courseType, region, row.title || fallback.title, duration, ...rawTags.filter((tag: any) => tag !== '__gachi_archived__')])
        };
    }

    private ensureCourseDraftMeta() {
        if (!String(this.courseDraft.region || '').trim()) this.courseDraft.region = this.courseRegionLabel();
        if (!String(this.courseDraft.category || '').trim()) this.courseDraft.category = '여행';
        if (typeof this.courseDraft.isPublic === 'undefined') this.courseDraft.isPublic = true;
        if (!String(this.courseDraft.photo || '').trim()) {
            let cover = this.allCourseBuilderItems().find((place: any) => !!place.image);
            if (cover) this.courseDraft.photo = cover.image;
        }
    }

    private normalizeCourseBuilderOrder(recalculateTimes: boolean = false) {
        this.courseBuilderPlaces = this.courseBuilderPlaces.map((place: any, index: number) => {
            place.order = index + 1;
            if (place.categorySelected !== true || String(place.category || '').trim() === '지도 위치') {
                place.category = '';
                place.categorySelected = false;
            }
            if (recalculateTimes && !place.manualTime) place.visitTime = this.visitTimeForIndex(index);
            return place;
        });
    }

    private applyCourseDraftFromPlaces() {
        if (this.courseBuilderDays.length > 0) {
            this.syncCurrentCourseBuilderDay();
            this.courseDraft.places = this.courseBuilderDays
                .reduce((items: string[], day: any) => items.concat((day.places || []).map((place: any) => place.name)), [])
                .join('\n');
            return;
        }
        this.courseDraft.places = this.courseBuilderPlaces.map((place: any) => place.name).join('\n');
        if (this.courseBuilderPlaces.length > 0 && this.courseRegions().length === 0) this.courseDraft.region = this.courseRegionLabel();
    }

    private nextCourseVisitTime() {
        return this.visitTimeForIndex(this.courseBuilderPlaces.length);
    }

    private visitTimeForIndex(index: number) {
        let start = 14 * 60;
        if (index > 0) {
            let previous = this.courseBuilderPlaces[index - 1];
            start = this.minutesFromTime(previous && previous.visitTime ? previous.visitTime : this.visitTimeForIndex(index - 1)) + 90;
        }
        return this.timeFromMinutes(start);
    }

    private minutesFromTime(value: string) {
        let parts = String(value || '14:00').split(':');
        let hours = Number(parts[0] || 14);
        let minutes = Number(parts[1] || 0);
        if (!isFinite(hours) || !isFinite(minutes)) return 14 * 60;
        return (hours * 60) + minutes;
    }

    private timeFromMinutes(value: number) {
        value = ((Math.round(value) % 1440) + 1440) % 1440;
        let hours = Math.floor(value / 60);
        let minutes = value % 60;
        return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
    }

    private courseWalkingMinutes() {
        let total = 0;
        let places = this.courseBuilderPlaces.filter((place: any) => !place.isSchedule);
        for (let i = 1; i < places.length; i++) {
            let prev = places[i - 1];
            let current = places[i];
            let distance = this.distanceKm(prev.lat, prev.lng, current.lat, current.lng);
            total += distance === null ? 12 : Math.max(2, Math.round((distance / 4.2) * 60));
        }
        return Math.round(total);
    }

    private hasFullCourseCoordinates() {
        let places = this.courseBuilderPlaces.filter((place: any) => !place.isSchedule);
        return places.length > 0 && places.every((place: any) => this.isFiniteNumber(place.lat) && this.isFiniteNumber(place.lng));
    }

    private distanceKm(lat1: any, lng1: any, lat2: any, lng2: any) {
        lat1 = this.safeNumber(lat1);
        lng1 = this.safeNumber(lng1);
        lat2 = this.safeNumber(lat2);
        lng2 = this.safeNumber(lng2);
        if ([lat1, lng1, lat2, lng2].some((value: any) => value === null)) return null;
        let radius = 6371;
        let dlat = (lat2 - lat1) * Math.PI / 180;
        let dlng = (lng2 - lng1) * Math.PI / 180;
        let a = Math.sin(dlat / 2) ** 2 + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dlng / 2) ** 2;
        return radius * (2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a)));
    }

    private safeNumber(value: any) {
        if (value === null || typeof value === 'undefined' || value === '') return null;
        let number = Number(value);
        return isFinite(number) ? number : null;
    }

    private isFiniteNumber(value: any) {
        return value !== null && typeof value !== 'undefined' && isFinite(Number(value));
    }

    private courseSearchCenter() {
        let first = this.courseBuilderPlaces.find((place: any) => this.isFiniteNumber(place.lat) && this.isFiniteNumber(place.lng));
        if (first) return { lat: first.lat, lng: first.lng };
        let regionCenter = this.courseRegionCenter();
        if (regionCenter) return regionCenter;
        let center = this.currentMapCenter();
        return { lat: center.lat || 37.5446, lng: center.lng || 127.0557 };
    }

    private courseRegionCenter(regionValue: string = '') {
        let region = String(regionValue || this.courseRegions()[0] || this.courseDraft.region || '').trim();
        if (!region) return null;
        let normalized = region.toLowerCase();
        let initials = this.koreanInitials(normalized);
        let useInitialSearch = this.isKoreanInitialQuery(normalized);
        let match = this.courseKnownRegions().find((option: any) => {
            let label = String(option.label || '').toLowerCase();
            let area = String(option.area || '').toLowerCase();
            return label.indexOf(normalized) > -1
                || area.indexOf(normalized) > -1
                || normalized.indexOf(label) > -1
                || (useInitialSearch && this.koreanInitials(`${option.label} ${option.area}`).indexOf(initials) > -1);
        });
        return match ? { lat: match.lat, lng: match.lng } : null;
    }

    public async requestCompanion(post: any) {
        if (!post) return;
        if (this.isOwnCompanionPost(post)) {
            await this.showSaveHint('내가 올린 동행 모집에는 신청할 수 없어요.');
            return;
        }
        if (!this.isConfirmedCompanionPost(post)) {
            await this.showSaveHint('확정된 코스가 연결된 모집에만 신청할 수 있어요.');
            return;
        }
        if (!this.isLoggedIn()) {
            await this.openAuthModal('login');
            return;
        }
        if (post.status === 'matched') {
            await this.openCompanionPreparationRoom(post);
            return;
        }
        if (this.currentCompanionApplication(post)) {
            await this.showSaveHint('신청 완료 상태입니다. 작성자 수락을 기다려주세요.');
            return;
        }

        if (!this.hasCompletedTravelResume()) {
            await this.promptTravelResumeCompletion();
            return;
        }

        if (!(await this.confirmCompanionSafetyRecord())) return;

        let application = this.createCompanionApplication(post);
        post.applicationSubmitting = true;
        await this.service.render();
        let submission = await this.submitCompanionApplicationRemote(post, application);
        post.applicationSubmitting = false;
        if (!submission.application) {
            await this.service.render();
            await this.showSaveHint(submission.message || '동행 신청 기록을 안전하게 저장하지 못했어요. 잠시 후 다시 시도해주세요.');
            return;
        }
        application = submission.application;
        if (!Array.isArray(post.applications)) post.applications = [];
        post.applications = [
            application,
            ...post.applications.filter((item: any) => item && item.applicantKey !== application.applicantKey)
        ];
        post.status = 'requested';
        post.applicants = Math.max(Number(post.applicants || 0) + 1, post.applications.length);
        await this.showSaveHint('여행 이력서를 제출하고 동행 신청을 보냈어요.');
    }

    public async acceptCompanionApplication(post: any, application?: any) {
        if (!post || post.status !== 'requested') return;
        if (!application && Array.isArray(post.applications)) application = post.applications[0];
        if (application) {
            let applications = await this.acceptCompanionApplicationRemote(post, application);
            if (!applications) {
                await this.showSaveHint('신청자 선택을 저장하지 못했어요. 잠시 후 다시 시도해주세요.');
                return;
            }
            post.applications = applications;
            application = applications.find((item: any) => item && item.id === application.id) || application;
            post.selectedApplicationId = application.id;
        }
        post.status = 'matched';
        await this.openCompanionPreparationRoom(post, application);
        await this.showSaveHint('신청자를 선택하고 동행 준비방을 만들었어요.');
    }

    private async confirmCompanionSafetyRecord() {
        return await this.service.modal.show({
            title: '동행 신청 안전 기록 동의',
            message: '신청 계정, 신청 시각, 접속 IP·브라우저, 이력서 사본을 180일 보관하고 적법한 수사기관 요청이 있을 때만 제공할 수 있습니다. 이는 통신사·정부 본인인증이 아닙니다.',
            action: '동의하고 신청',
            actionBtn: 'primary',
            status: 'info'
        });
    }

    private async submitCompanionApplicationRemote(post: any, application: any) {
        try {
            let response: any = await wiz.call('save_course', {
                community_action: 'companion_apply',
                post_id: String(post && post.id || ''),
                application: JSON.stringify(application)
            });
            if (response && response.code === 200 && response.data && response.data.application) {
                return { application: response.data.application, message: '' };
            }
            return {
                application: null,
                message: String(response && response.data && response.data.message || '동행 신청을 저장하지 못했어요.')
            };
        } catch (e) { }
        return { application: null, message: '서버 연결을 확인하지 못했어요. 잠시 후 다시 시도해주세요.' };
    }

    private async acceptCompanionApplicationRemote(post: any, application: any) {
        try {
            let response: any = await wiz.call('save_course', {
                community_action: 'companion_accept',
                post_id: String(post && post.id || ''),
                application_id: String(application && application.id || '')
            });
            if (response && response.code === 200 && response.data && Array.isArray(response.data.applications)) {
                return response.data.applications;
            }
        } catch (e) { }
        return null;
    }

    public async toggleCompanionSave(post: any, event?: any) {
        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }
        if (!post) return;
        if (!this.isLoggedIn()) {
            await this.openAuthModal('login');
            return;
        }
        let postId = String(post.id || '').trim();
        if (!postId) return;
        let saved = this.savedCompanionPostIds.indexOf(postId) > -1;
        this.savedCompanionPostIds = saved
            ? this.savedCompanionPostIds.filter((id: string) => id !== postId)
            : [postId, ...this.savedCompanionPostIds.filter((id: string) => id !== postId)];
        this.applySavedCompanionPostIds();
        this.persistSavedCompanionPosts();
        if (post.owned === true) this.persistCompanionPostsCache();
        await this.showSaveHint(saved ? '동행 찜을 취소했어요.' : '동행을 찜했어요.');
    }

    public companionActionLabel(post: any) {
        if (!post) return '신청';
        if (post.applicationSubmitting) return '신청 저장 중';
        if (this.isOwnCompanionPost(post)) return '내 모집글';
        if (post.status === 'matched') return '준비방 열기';
        if (this.currentCompanionApplication(post)) return '수락 대기';
        return '이력서로 신청';
    }

    public isCompanionActionDisabled(post: any) {
        return !!(post && post.applicationSubmitting)
            || this.isOwnCompanionPost(post)
            || (!!this.currentCompanionApplication(post) && post.status !== 'matched');
    }

    public isOwnCompanionPost(post: any) {
        if (!post) return false;
        if (post.owned === true) return true;
        let host = String(post.host || '').trim();
        return !!host && host === String(this.myDisplayName() || '').trim() && String(post.id || '').indexOf('mate-') === 0;
    }

    public isCompanionExpanded(post: any) {
        return !!post && String(post.id || '') === this.expandedCompanionPostId;
    }

    public async toggleCompanionDetail(post: any) {
        if (!post || !post.id) return;
        this.expandedCompanionPostId = this.isCompanionExpanded(post) ? '' : String(post.id);
        if (this.expandedCompanionPostId && post.authorId) {
            post.trustLoading = true;
            post.trustError = '';
            try {
                const response: any = await wiz.call('travel_growth', { action: 'trust', target_id: post.authorId });
                if (!response || response.code !== 200) throw new Error('후기 근거를 조회하지 못했습니다.');
                post.travelTrust = response.data.trust;
            } catch (e) { post.trustError = '동행 후기 근거를 조회하지 못했습니다. 다시 상세보기를 눌러주세요.'; }
            finally { post.trustLoading = false; }
        }
        await this.service.render();
    }

    public async openCompanionCourseStory(post: any) {
        if (!post || !post.courseId) return;
        let ids = [post.courseId, post.originalCourseId].map((value: any) => String(value || '').trim()).filter((value: string) => !!value);
        let course = this.allCourses().find((item: any) => item && ids.indexOf(String(item.id || '').trim()) > -1);
        if (!course && post.courseSnapshot && typeof post.courseSnapshot === 'object') course = post.courseSnapshot;
        if (!course) {
            let stops = Array.isArray(post.routeStops) ? post.routeStops : [];
            course = {
                id: String(post.courseId),
                title: String(post.route || post.title || '동행 코스'),
                location: String(post.location || ''),
                summary: String(post.intro || '동행 모집에 연결된 확정 코스입니다.'),
                duration: String(post.date || '일정 협의'),
                image: String(post.image || ''),
                source: String(post.courseSource || ''),
                mine: post.courseSource === 'mine',
                places: stops.map((stop: any, index: number) => {
                    if (stop && typeof stop === 'object') return stop;
                    return {
                        id: `${post.courseId}-stop-${index + 1}`,
                        name: String(stop || `장소 ${index + 1}`),
                        time: index === 0 ? String(post.time || '') : '',
                        order: index + 1,
                        item_type: 'place'
                    };
                })
            };
        }

        this.activeTab = 'my';
        this.myProfileOpen = true;
        this.persistAccessState();
        this.replaceAccessUrl();
        await this.openMyCourseDetail(course, 'companion');
    }

    public companionCover(post: any) {
        if (!post || post.image_broken) return '';
        return String((post && (post.image || post.coverImage || post.cover_image)) || '').trim();
    }

    public markCompanionCoverBroken(post: any) {
        if (!post) return;
        post.image_broken = true;
        if (post.owned === true) this.persistCompanionPostsCache();
    }

    private companionCourseCoverForPost(course: any) {
        if (!course || course.image_broken) return '';
        return String(course.cover_image || '').trim();
    }

    public companionStatusLabel(post: any) {
        if (!post) return '';
        if (post.status === 'matched') return '준비방 개설';
        if (post.status === 'requested') return '신청 검토 중';
        return '모집 중';
    }

    public companionApplications(post: any) {
        if (!post || !Array.isArray(post.applications)) return [];
        return post.applications;
    }

    public currentCompanionApplication(post: any) {
        if (!this.isLoggedIn()) return null;
        let applicantKey = this.currentCompanionApplicantKey();
        return this.companionApplications(post).find((application: any) => {
            return application && application.applicantKey === applicantKey && application.status !== 'declined';
        }) || null;
    }

    public companionApplicantCount(post: any) {
        return Math.max(Number(post && post.applicants ? post.applicants : 0), this.companionApplications(post).length);
    }

    public companionApplicationPhoto(application: any) {
        let resume = this.companionApplicationResume(application);
        return String(resume.photo || '').trim();
    }

    public companionApplicationName(application: any) {
        let resume = this.companionApplicationResume(application);
        return String(application && application.applicantNickname || resume.nickname || resume.name || '신청자').trim();
    }

    public companionApplicationMeta(application: any) {
        let resume = this.companionApplicationResume(application);
        let uses = this.normalizePositiveNumber(resume.companionUses);
        return [
            String(resume.region || '').trim(),
            `${uses}회 동행`,
            resume.reviewScore ? `후기 ${Number(resume.reviewScore).toFixed(1)}` : ''
        ].filter((item: string) => !!item).join(' · ');
    }

    public companionApplicationIntro(application: any) {
        let resume = this.companionApplicationResume(application);
        return String(resume.intro || '').trim();
    }

    public companionApplicationExperience(application: any) {
        let resume = this.companionApplicationResume(application);
        return String(resume.travelExperience || '').trim();
    }

    public companionApplicationStatusLabel(application: any) {
        if (application && application.status === 'accepted') return '선택됨';
        if (application && application.status === 'declined') return '마감';
        return '검토 중';
    }

    public isConfirmedCompanionPost(post: any) {
        return !!post && !!String(post.courseId || '').trim() && post.courseConfirmed === true;
    }

    public companionRouteStops(post: any) {
        if (!post) return [];
        let stops = Array.isArray(post.routeStops) ? post.routeStops : this.splitList(post.routeStops || '');
        return stops.map((stop: any) => String(stop && stop.name ? stop.name : stop || '').trim()).filter((stop: string) => !!stop);
    }

    public companionRouteText(post: any) {
        let stops = this.companionRouteStops(post);
        return stops.length > 0 ? stops.join(' → ') : String(post && post.route ? post.route : '동선 확인 필요');
    }

    public companionMoodTags(post: any) {
        return this.splitList(post && post.moodTags ? post.moodTags : '').slice(0, 4);
    }

    public companionFlexibility(post: any) {
        return this.splitList(post && post.flexibility ? post.flexibility : '').slice(0, 3);
    }

    public companionCardFacts(post: any) {
        return [
            { icon: 'fa-calendar-day', label: '날짜와 시간', value: [post && post.date, post && post.time].filter((value: any) => !!value).join(' · ') || '일정 협의' },
            { icon: 'fa-won-sign', label: '예상 비용', value: String(post && post.estimatedCost ? post.estimatedCost : '비용 협의') },
            { icon: 'fa-person-walking', label: '여행 속도', value: this.travelPaceLabel(post && post.pace) || '보통' },
            { icon: 'fa-user-group', label: '모집 인원', value: `${Math.max(1, Number(post && post.capacity ? post.capacity : 1))}명 · 신청 ${this.companionApplicantCount(post)}명` }
        ];
    }

    public companionFitCriteria(post: any) {
        let interests = this.splitList(post && post.interestTags ? post.interestTags : '').slice(0, 3).join(' · ');
        return [
            { icon: 'fa-calendar-check', label: '날짜', value: `${post && post.date ? post.date : '협의'} 가능` },
            { icon: 'fa-person-walking', label: '속도', value: this.travelPaceLabel(post && post.pace) || '보통' },
            { icon: 'fa-wallet', label: '예산', value: this.budgetStyleLabel(post && post.budgetStyle) || '협의' },
            { icon: 'fa-location-dot', label: '관심 장소', value: interests || '코스 장소' },
            { icon: 'fa-ban-smoking', label: '흡연 여부·음주 성향', value: `${this.smokingStyleLabel(post && post.smoking)} · ${this.drinkingStyleLabel(post && post.drinking)}` },
            { icon: 'fa-star', label: '이력·후기', value: String(post && post.hostHistory ? post.hostHistory : '후기 확인') },
            { icon: 'fa-shield-halved', label: '안전 확인', value: post && post.verificationRequired ? '본인인증 또는 안전 기록' : '선택' }
        ];
    }

    public companionApplicationFitItems(post: any, application: any) {
        let resume = this.companionApplicationResume(application);
        let postInterests = this.splitList(post && post.interestTags ? post.interestTags : '').map((value: string) => value.toLowerCase());
        let resumeInterests = this.splitList(resume.interests || '').map((value: string) => value.toLowerCase());
        let overlap = resumeInterests.filter((value: string) => {
            return postInterests.some((target: string) => target.indexOf(value) > -1 || value.indexOf(target) > -1);
        });
        let smokingMatched = !post || !post.smoking || String(resume.smoking || '') === String(post.smoking || '');
        let drinkingMatched = !post || !post.drinking || String(resume.drinking || '') === String(post.drinking || '');
        let historyMatched = this.normalizePositiveNumber(resume.companionUses) > 0 || String(resume.travelExperience || '').trim().length > 20;
        return [
            { label: '날짜 겹침', value: resume.availabilityConfirmed === false ? '재확인' : '가능', matched: resume.availabilityConfirmed !== false },
            { label: '관심 장소', value: overlap.length > 0 ? `${overlap.length}곳 겹침` : '겹침 없음', matched: overlap.length > 0 },
            { label: '흡연 여부·음주 성향', value: smokingMatched && drinkingMatched ? '조건 일치' : '조율 필요', matched: smokingMatched && drinkingMatched },
            { label: '여행 이력·후기', value: historyMatched ? `${this.normalizePositiveNumber(resume.companionUses)}회 · ${resume.reviewScore ? Number(resume.reviewScore).toFixed(1) : '후기 확인'}` : '첫 동행', matched: historyMatched },
            {
                label: '안전 확인',
                value: resume.identityVerified ? '본인 인증 완료' : (application && application.safetyRecord && application.safetyRecord.recorded ? '안전 기록 저장' : '미완료'),
                matched: !!resume.identityVerified || !!(application && application.safetyRecord && application.safetyRecord.recorded)
            }
        ];
    }

    public companionApplicationFitScore(post: any, application: any) {
        let items = this.companionApplicationFitItems(post, application);
        let matched = items.filter((item: any) => item.matched).length;
        return Math.round((matched / Math.max(1, items.length)) * 100);
    }

    public async openCompanionPreparationRoom(post: any, application?: any) {
        if (!post || post.status !== 'matched') return;
        await this.loadDirectChatRooms(false);
        let chat = this.ensureDirectChatForCompanion(post, application);
        if (!chat) return;
        this.activeTab = 'chat';
        this.chatContentTab = 'dm';
        this.activeDirectChatId = chat.id;
        this.directRoomOpen = true;
        this.directActionMenuOpen = false;
        this.persistAccessState();
        this.replaceAccessUrl();
        await this.service.render();
        this.resetChatContentScroll();
    }

    public async toggleCompanionPackingItem(chat: any, item: any) {
        if (!chat || !chat.preparation || !item) return;
        item.done = !item.done;
        await this.service.render();
    }

    public async toggleCompanionPreparationRoom(chat: any) {
        if (!chat || !chat.preparation) return;
        chat.preparation.collapsed = !chat.preparation.collapsed;
        await this.service.render();
    }

    public async openCompanionTimedMap() {
        await this.openInstantCompanion();
    }

    public travelPaceLabel(value: any) {
        let labels: any = { slow: '여유롭게', balanced: '보통', fast: '빠르게' };
        return labels[String(value || '')] || '';
    }

    public budgetStyleLabel(value: any) {
        let labels: any = { low: '알뜰형', medium: '균형형', high: '경험 우선' };
        return labels[String(value || '')] || '';
    }

    public smokingStyleLabel(value: any) {
        let labels: any = { non: '비흡연', smoking: '흡연', flexible: '상관없음' };
        return labels[String(value || '')] || '협의';
    }

    public drinkingStyleLabel(value: any) {
        let labels: any = { none: '음주 안 함', light: '가볍게', social: '즐기는 편', flexible: '상관없음' };
        return labels[String(value || '')] || '협의';
    }

    public latestCourses() {
        return this.recommendations.filter((course: any) => {
            return course && course.serverPublicCourse === true;
        }).slice(0, 5);
    }

    public feedCourses() {
        return this.filteredRecommendations().filter((course: any) => {
            return course && course.serverPublicCourse === true;
        });
    }

    public courseAuthorId(course: any) {
        return String((course && (course.authorId || course.author_id)) || '').trim();
    }

    public courseAuthorName(course: any) {
        return String((course && (course.authorName || course.author_name)) || 'GACHI 크리에이터').trim();
    }

    public courseAuthorHandle(course: any) {
        let value = String((course && (course.authorHandle || course.author_handle)) || '').trim();
        return value || `@${this.courseAuthorName(course).replace(/\s+/g, '').toLowerCase()}`;
    }

    public courseAuthorInitial(course: any) {
        return String((course && (course.authorInitial || course.author_initial)) || this.courseAuthorName(course).charAt(0) || 'G');
    }

    public courseAuthorCanFollow(course: any) {
        let authorId = this.courseAuthorId(course);
        let session = this.service && this.service.auth ? (this.service.auth.session || {}) : {};
        return !!authorId && authorId !== String(session.id || '');
    }

    public isCourseAuthorFollowing(course: any) {
        return !!(course && (course.authorFollowing || course.author_following));
    }

    public async toggleCourseAuthorFollow(course: any, event?: any) {
        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }
        if (!this.isLoggedIn()) {
            await this.openAuthModal('login');
            return;
        }
        let authorId = this.courseAuthorId(course);
        if (!authorId || !this.courseAuthorCanFollow(course)) return;
        let following = !this.isCourseAuthorFollowing(course);
        let response: any = null;
        try {
            response = await wiz.call('saved_courses', {
                community_action: 'author_follow',
                author_id: authorId,
                following
            });
        } catch (e) { }
        if (!response || response.code !== 200) {
            await this.showSaveHint(response && response.data && response.data.message ? response.data.message : '팔로우 상태를 변경하지 못했습니다.');
            return;
        }
        this.allCourses().forEach((item: any) => {
            if (this.courseAuthorId(item) === authorId) {
                item.authorFollowing = following;
                item.author_following = following;
            }
        });
        if (this.selectedFeaturedCourse && this.courseAuthorId(this.selectedFeaturedCourse) === authorId) {
            this.selectedFeaturedCourse.authorFollowing = following;
            this.selectedFeaturedCourse.author_following = following;
        }
        if (response.data && Array.isArray(response.data.following_authors)) {
            this.applyMyProfileFollowing(response.data.following_authors);
        }
        await this.showSaveHint(following ? `${this.courseAuthorName(course)}님을 팔로우했어요.` : '팔로우를 취소했어요.');
    }

    public savedPlaces() {
        return this.savedPlaceItems;
    }

    public isPlaceSaved(place: any) {
        let placeId = this.savedPlaceIdentity(place);
        return !!placeId && this.savedPlaceItems.some((item: any) => this.savedPlaceIdentity(item) === placeId);
    }

    public async togglePlaceSave(place: any, event?: any) {
        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }
        if (!place) return;
        if (!this.isLoggedIn()) {
            await this.openAuthModal('login');
            return;
        }

        let placeId = this.savedPlaceIdentity(place);
        if (!placeId) {
            await this.showSaveHint('저장할 장소 정보가 부족해요.');
            return;
        }

        let wasSaved = this.isPlaceSaved(place);
        if (wasSaved) {
            this.savedPlaceItems = this.savedPlaceItems.filter((item: any) => this.savedPlaceIdentity(item) !== placeId);
        } else {
            let savedPlace = this.savedPlaceFromSource(place, placeId);
            this.savedPlaceItems = [
                savedPlace,
                ...this.savedPlaceItems.filter((item: any) => this.savedPlaceIdentity(item) !== placeId)
            ].slice(0, 100);
        }
        this.persistSavedPlaces();
        await this.showSaveHint(wasSaved ? '장소 찜을 취소했어요.' : '장소를 찜했어요.');
    }

    public filteredSavedPlaces() {
        let places = this.savedPlaces();
        if (this.savedPlaceType === 'all') return places;
        return places.filter((place: any) => this.savedPlaceTypeKey(place) === this.savedPlaceType);
    }

    public savedPlaceFilterCount(key: string) {
        if (key === 'all') return this.savedPlaces().length;
        return this.savedPlaces().filter((place: any) => this.savedPlaceTypeKey(place) === key).length;
    }

    public async removeSavedPlace(place: any, event?: any) {
        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }

        await this.togglePlaceSave(place);
    }

    public async setSavedPlaceType(key: string) {
        if (!this.savedPlaceFilters.some((filter: any) => filter.key === key)) return;
        this.savedPlaceType = key;
        await this.service.render();
    }

    public activeSavedPlaceTypeLabel() {
        let filter = this.savedPlaceFilters.find((item: any) => item.key === this.savedPlaceType);
        return filter ? filter.label : '전체';
    }

    public savedPlaceEmptyText() {
        if (this.savedPlaceType === 'all') return '저장한 장소가 없습니다.';
        return `저장된 ${this.activeSavedPlaceTypeLabel()} 장소가 없습니다.`;
    }

    public savedPlaceKindLabel(place: any) {
        let filter = this.savedPlaceFilters.find((item: any) => item.key === this.savedPlaceTypeKey(place));
        return filter ? filter.label : (place && place.kind ? place.kind : '장소');
    }

    private savedPlaceTypeKey(place: any) {
        let raw = [
            place && place.category,
            place && place.kind,
            place && place.name,
            place && place.description
        ].join(' ').toLowerCase();

        if (raw.indexOf('cafe') > -1 || raw.indexOf('카페') > -1 || raw.indexOf('커피') > -1) return 'cafe';
        if (raw.indexOf('stay') > -1 || raw.indexOf('hotel') > -1 || raw.indexOf('숙소') > -1 || raw.indexOf('호텔') > -1 || raw.indexOf('스테이') > -1) return 'stay';
        if (raw.indexOf('food') > -1 || raw.indexOf('맛집') > -1 || raw.indexOf('식당') > -1 || raw.indexOf('미식') > -1) return 'food';
        if (raw.indexOf('walk') > -1 || raw.indexOf('산책') > -1 || raw.indexOf('숲') > -1) return 'walk';
        if (raw.indexOf('drive') > -1 || raw.indexOf('드라이브') > -1 || raw.indexOf('도로') > -1) return 'drive';
        return 'landmark';
    }

    private executionCategoryIcon(category: string) {
        let icons: any = {
            cafe: 'fa-mug-saucer',
            food: 'fa-utensils',
            walk: 'fa-person-walking',
            stay: 'fa-bed',
            shopping: 'fa-bag-shopping',
            landmark: 'fa-location-dot',
            drive: 'fa-car-side'
        };
        return icons[category] || 'fa-location-dot';
    }

    private executionPhotoClass(category: string) {
        if (category === 'cafe') return 'photo-cafe';
        if (category === 'food') return 'photo-food';
        if (category === 'walk') return 'photo-walk';
        return 'photo-landmark';
    }

    public savedCompanionPosts() {
        return this.companionPosts.filter((post: any) => {
            return this.isConfirmedCompanionPost(post) && !!post.saved;
        });
    }

    public async setSavedContentTab(tab: string) {
        if (['courses', 'places', 'companions'].indexOf(tab) < 0) return;
        this.savedContentTab = tab;
        await this.service.render();
    }

    public homeRegionLabel() {
        return this.selectedFilters.location || '모든 지역';
    }

    public isHomeRegionActive(region: any) {
        return this.selectedFilters.location === region.value;
    }

    public async selectHomeRegion(region: any) {
        let value = region && typeof region.value !== 'undefined' ? region.value : '';
        this.selectedFilters.location = value;
        this.activeFilterKey = '';
        this.filterOverviewOpen = false;
        this.syncLocationRegion();
        this.persistAccessState();
        this.replaceAccessUrl();
        await this.loadFilterPlaces();
        await this.service.render();
    }

    public async clearHomeRegionFilter() {
        this.resetHomeFiltersToRoot();
    }

    public resetHomeFiltersToRoot() {
        this.selectedFilters = {
            companion: '',
            schedule: '',
            location: ''
        };
        this.scheduleRange = { start: '', end: '' };
        this.draftScheduleRange = { start: '', end: '' };
        this.query = '';
        this.selectedKeyword = '여행';
        this.activeFilterKey = '';
        this.filterOverviewOpen = false;
        this.travelMode = 'walk';
        this.selectedMoveFilter = '';
        this.placeCourses = [];
        this.placeThemeSections = [];
        this.activePlaceThemeKey = '';
        this.clearStoredAccessState();
        this.service.href('/');
    }

    public filteredCompanionPosts() {
        let posts = this.companionPosts.filter((post: any) => {
            return this.isConfirmedCompanionPost(post);
        });
        if (!this.companionCourseFilterId) return posts;
        return posts.filter((post: any) => {
            return [post && post.courseId, post && post.originalCourseId]
                .map((value: any) => String(value || '').trim())
                .indexOf(this.companionCourseFilterId) > -1;
        });
    }

    public courseCompanionPosts(course: any) {
        let ids = [
            course && course.id,
            course && course.originalCourseId,
            course && course.original_course_id
        ].map((value: any) => String(value || '').trim()).filter((value: string) => !!value);
        if (ids.length === 0) return [];
        return this.companionPosts.filter((post: any) => {
            if (!this.isConfirmedCompanionPost(post)) return false;
            let postIds = [post && post.courseId, post && post.originalCourseId]
                .map((value: any) => String(value || '').trim());
            return postIds.some((id: string) => ids.indexOf(id) > -1);
        });
    }

    public courseCompanionCount(course: any) {
        return this.courseCompanionPosts(course).length;
    }

    public async openCourseCompanions(course: any, event?: any) {
        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }
        let posts = this.courseCompanionPosts(course);
        if (posts.length === 0) return;
        let courseIds = [course && course.id, course && course.originalCourseId, course && course.original_course_id]
            .map((value: any) => String(value || '').trim()).filter((value: string) => !!value);
        let matchedCourseId = [posts[0] && posts[0].courseId, posts[0] && posts[0].originalCourseId]
            .map((value: any) => String(value || '').trim())
            .find((id: string) => courseIds.indexOf(id) > -1) || courseIds[0] || '';
        this.activeTab = 'home';
        this.homeContentTab = 'companion';
        this.companionMode = 'pretrip';
        this.companionCourseFilterId = matchedCourseId;
        this.companionCourseFilterTitle = String((course && course.title) || '선택한 코스').trim();
        this.expandedCompanionPostId = String((posts[0] && posts[0].id) || '');
        this.persistAccessState();
        this.replaceAccessUrl();
        await this.service.render();
        this.resetHomeContentScroll();
    }

    public async clearCompanionCourseFilter() {
        this.companionCourseFilterId = '';
        this.companionCourseFilterTitle = '';
        this.expandedCompanionPostId = '';
        await this.service.render();
    }

    public filteredReviewPosts() {
        return this.reviewPosts.filter((review: any) => this.matchesSelectedLocation(review));
    }

    public async selectSuggestion(value: string) {
        if (this.isChatSending) return;
        this.draft = value;
        await this.send();
    }

    public canSendChat() {
        return !this.isChatSending && String(this.draft || '').trim().length > 0;
    }

    public async send() {
        if (this.isChatSending) return;

        let prompt = String(this.draft || '').trim();
        if (!prompt) return;

        let clientMessageId = this.createChatMessageId('client');
        this.draft = '';
        await this.sendChatPrompt(prompt, clientMessageId);
    }

    public async handleEnter(event: any) {
        if (event.shiftKey) return;
        event.preventDefault();
        event.stopPropagation();
        await this.send();
    }

    private createChatMessageId(prefix: string) {
        let randomId = '';
        try {
            let cryptoApi: any = typeof window !== 'undefined' ? (window as any).crypto : null;
            randomId = cryptoApi && typeof cryptoApi.randomUUID === 'function'
                ? cryptoApi.randomUUID().replace(/-/g, '')
                : '';
        } catch (e) { }
        if (!randomId) randomId = `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`;
        return `${prefix}-${randomId}`;
    }

    private appendChatMessageOnce(message: any, path: string) {
        let id = String(message && message.id || '').trim();
        let beforeLength = this.messages.length;
        if (id && this.messages.some((item: any) => String(item && item.id || '') === id)) {
            this.logChatMessageEvent('append_blocked_duplicate', {
                message_id: id,
                append_path: path,
                before_length: beforeLength,
                after_length: beforeLength
            });
            return false;
        }
        this.messages.push(message);
        this.logChatMessageEvent('message_appended', {
            message_id: id,
            append_path: path,
            before_length: beforeLength,
            after_length: this.messages.length
        });
        return true;
    }

    public visibleChatMessages() {
        let rows = Array.isArray(this.messages) ? this.messages : [];
        let start = Math.max(0, rows.length - this.chatVisibleMessageLimit);
        return rows.slice(start);
    }

    public hiddenChatMessageCount() {
        return Math.max(0, (this.messages || []).length - this.chatVisibleMessageLimit);
    }

    public async showOlderChatMessages() {
        let scroller: any = typeof document !== 'undefined'
            ? document.querySelector('.access-shell .ai-planner-layout')
            : null;
        let previousHeight = scroller ? Number(scroller.scrollHeight || 0) : 0;
        let previousTop = scroller ? Number(scroller.scrollTop || 0) : 0;
        this.chatVisibleMessageLimit += this.chatMessagePageSize;
        await this.service.render();
        if (scroller) scroller.scrollTop = previousTop + Math.max(0, Number(scroller.scrollHeight || 0) - previousHeight);
    }

    public trackChatMessage(index: number, message: any) {
        return String(
            message && (message.id || message.requestId || message.request_id)
            || `${message && message.role || ''}:${message && message.time || ''}:${message && message.text || ''}:${index}`
        );
    }

    private dedupeChatMessages(messages: any[]) {
        let seen: any = {};
        return (messages || []).filter((message: any) => {
            let id = String(message && (message.id || message.request_id) || '').trim();
            let fallback = `${message && message.role || ''}:${message && message.created || ''}:${message && message.text || ''}`;
            let key = id || fallback;
            if (seen[key]) return false;
            seen[key] = true;
            return true;
        });
    }

    private logChatMessageEvent(event: string, detail: any = {}) {
        if (typeof console === 'undefined' || !console.info) return;
        console.info('[gachi_chat]', {
            event,
            conversation_id: this.activeChatThreadId || '',
            ...detail
        });
    }

    public formatMessage(value: any) {
        let escaped = String(value || '').replace(/[&<>"']/g, (char: string) => {
            let map: any = {
                '&': '&amp;',
                '<': '&lt;',
                '>': '&gt;',
                '"': '&quot;',
                "'": '&#39;'
            };
            return map[char] || char;
        });
        return escaped
            .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
            .replace(/\n/g, '<br>');
    }

    public async toggleChatDrawer() {
        this.chatDrawerOpen = !this.chatDrawerOpen;
        if (this.chatDrawerOpen) await this.loadChatThreads(false);
        await this.service.render();
    }

    public async startNewChat() {
        this.activeChatThreadId = '';
        this.draft = '';
        this.messages = [this.defaultChatMessage()];
        this.chatVisibleMessageLimit = this.chatMessagePageSize;
        this.resetPlannerPreview();
        this.resetPlannerConversationState();
        this.chatDrawerOpen = false;
        await this.service.render();
    }

    public async openChatThread(thread: any) {
        if (!thread || !thread.id || !this.isLoggedIn()) return;

        this.chatHistoryLoading = true;
        await this.service.render();

        const { code, data } = await wiz.call('chat_thread', {
            thread_id: thread.id
        });

        this.chatHistoryLoading = false;
        if (code === 200 && data && data.thread) {
            this.activeChatThreadId = data.thread.id || '';
            this.chatVisibleMessageLimit = this.chatMessagePageSize;
            this.messages = this.dedupeChatMessages([
                this.defaultChatMessage(),
                ...(data.thread.messages || [])
            ]);
            this.resetPlannerPreview();
            this.applyPlannerContract({
                stage: data.thread.travel_state && data.thread.travel_state.conversation_stage,
                travel_state: data.thread.travel_state || {},
                itinerary_draft: data.thread.itinerary_draft || {},
                missing_slots: [],
                action: 'answer_only',
                warnings: [],
                failure_stage: ''
            });
            this.chatDrawerOpen = false;
            await this.service.render();
            this.scrollToLatest();
            return;
        }

        if (code === 401 && this.service.auth) this.service.auth.clearLocalSession();
        await this.service.render();
    }

    public async requestDeleteChatThread(thread: any) {
        if (!thread || !thread.id || !this.isLoggedIn() || this.chatDeletingThreadId) return;
        this.chatDeleteConfirmThreadId = String(thread.id);
        await this.service.render();
    }

    public async cancelDeleteChatThread() {
        if (this.chatDeletingThreadId) return;
        this.chatDeleteConfirmThreadId = '';
        await this.service.render();
    }

    public async deleteChatThread(thread: any) {
        if (!thread || !thread.id || !this.isLoggedIn() || this.chatDeletingThreadId) return;

        let threadId = String(thread.id);
        if (this.chatDeleteConfirmThreadId !== threadId) {
            await this.requestDeleteChatThread(thread);
            return;
        }
        this.chatDeletingThreadId = threadId;
        await this.service.render();
        let response: any;
        try {
            response = await wiz.call('chat_thread_delete', { thread_id: threadId });
        } catch (e) {
            response = { code: 500, data: {} };
        }

        if (response && response.code === 200) {
            this.chatThreads = this.chatThreads.filter((item: any) => String(item && item.id || '') !== threadId);
            this.chatDeleteConfirmThreadId = '';
            if (String(this.activeChatThreadId || '') === threadId) {
                this.activeChatThreadId = '';
                this.draft = '';
                this.messages = [this.defaultChatMessage()];
                this.chatVisibleMessageLimit = this.chatMessagePageSize;
                this.resetPlannerPreview();
                this.resetPlannerConversationState();
            }
            await this.showSaveHint('AI 채팅을 삭제했어요.');
        } else {
            let message = response && response.data && response.data.message
                ? response.data.message
                : 'AI 채팅을 삭제하지 못했어요.';
            await this.showSaveHint(message);
        }
        this.chatDeletingThreadId = '';
        await this.service.render();
    }

    public async toggleKeyword(tag: string) {
        this.selectedKeyword = this.selectedKeyword === tag ? '' : tag;
        this.persistAccessState();
        this.replaceAccessUrl();
        await this.loadFilterPlaces();
        await this.service.render();
    }

    public async openFilterOverview() {
        this.activeFilterKey = '';
        this.filterOverviewOpen = !this.filterOverviewOpen;
        await this.service.render();
    }

    public async closeFilterOverview() {
        this.filterOverviewOpen = false;
        await this.service.render();
    }

    public shouldShowFilterBack(filter: any) {
        return !!filter && ['location', 'companion'].indexOf(filter.key) > -1;
    }

    public async backToFilterOverview() {
        this.activeFilterKey = '';
        this.filterOverviewOpen = true;
        await this.service.render();
    }

    public async openFilterSheetFromOverview(key: string) {
        this.filterOverviewOpen = false;
        this.activeFilterKey = key;
        if (key === 'location') this.syncLocationRegion();
        if (key === 'schedule') this.buildCalendar();
        await this.service.render();
    }

    public async selectMoveFilter(mode: string) {
        if (!this.isSupportedMoveFilter(mode)) return;

        this.selectedMoveFilter = this.selectedMoveFilter === mode && mode ? '' : mode;
        if (this.selectedMoveFilter && this.isSupportedTravelMode(this.selectedMoveFilter)) {
            this.travelMode = this.selectedMoveFilter;
        }

        this.persistAccessState();
        this.replaceAccessUrl();
        await this.loadFilterPlaces();
        await this.service.render();
        this.scheduleNaverMapRender();
    }

    public async openFilterSheet(key: string) {
        if (this.activeFilterKey === key) {
            this.activeFilterKey = '';
            await this.service.render();
            return;
        }

        this.filterOverviewOpen = false;
        this.activeFilterKey = key;
        if (key === 'location') this.syncLocationRegion();
        if (key === 'schedule') this.buildCalendar();
        await this.service.render();
    }

    public async closeFilterSheet() {
        this.activeFilterKey = '';
        await this.service.render();
    }

    public async selectCondition(key: string, value: string) {
        this.selectedFilters[key] = this.selectedFilters[key] === value ? '' : value;
        this.logFilterEvent(key, this.selectedFilters[key]);
        this.activeFilterKey = '';
        this.filterOverviewOpen = false;
        this.persistAccessState();
        this.replaceAccessUrl();
        await this.loadFilterPlaces();
        await this.service.render();
    }

    public hasActiveFilters() {
        return ['companion', 'schedule', 'location'].some((key: string) => !!this.selectedFilters[key]) || !!this.selectedMoveFilter;
    }

    public async clearAllFilters() {
        if (this.activeTab === 'home') {
            this.resetHomeFiltersToRoot();
            return;
        }

        this.selectedFilters = {
            companion: '',
            schedule: '',
            location: ''
        };
        this.scheduleRange = { start: '', end: '' };
        this.draftScheduleRange = { start: '', end: '' };
        this.activeFilterKey = '';
        this.filterOverviewOpen = false;
        this.travelMode = 'walk';
        this.selectedMoveFilter = '';
        this.placeCourses = [];
        this.placeThemeSections = [];
        this.activePlaceThemeKey = '';
        this.syncLocationRegion();
        this.persistAccessState();
        this.replaceAccessUrl();
        await this.loadFilterPlaces();
        await this.service.render();
    }

    public async clearSearch() {
        this.query = '';
        this.persistAccessState();
        this.replaceAccessUrl();
        await this.loadFilterPlaces();
        await this.service.render();
    }

    public async handleSearchInput() {
        this.persistAccessState();
        this.replaceAccessUrl();
        this.scheduleFilterPlaceLoad();
        await this.service.render();
    }

    public markCourseImageBroken(course: any) {
        if (!course) return;
        course.image_broken = true;
    }

    public async selectPlaceTheme(theme: any) {
        if (!theme || !theme.key) return;
        this.activePlaceThemeKey = theme.key;
        await this.service.render();
    }

    public visiblePlaceThemes() {
        return this.placeThemeSections.filter((theme: any) => this.filteredThemeCourses(theme).length > 0);
    }

    public activePlaceTheme() {
        let themes = this.visiblePlaceThemes();
        if (themes.length === 0) return null;
        return themes.find((theme: any) => theme.key === this.activePlaceThemeKey) || themes[0];
    }

    public isPlaceThemeActive(theme: any) {
        let active = this.activePlaceTheme();
        return !!active && !!theme && active.key === theme.key;
    }

    public themeFilteredCount(theme: any) {
        return this.filteredThemeCourses(theme).length;
    }

    public filteredThemePlaceCourses() {
        let theme = this.activePlaceTheme();
        return theme ? this.filteredThemeCourses(theme) : [];
    }

    public activePlaceThemeCountLabel() {
        if (this.placeCoursesLoading) return '로딩';
        return `${this.filteredThemePlaceCourses().length}개`;
    }

    public async setZenlyLayerTab(tab: string) {
        if (['density', 'signals'].indexOf(tab) < 0) return;
        this.zenlyLayerTab = tab;
        this.selectedPresencePlaceId = '';
        this.selectedZenlySignalId = '';
        this.selectedZenlyFallbackCard = null;
        await this.service.render();
        this.bindZenlyMarkerDelegation();
        if (tab === 'density') await this.loadZenlyHeatmap(false);
        if (tab === 'signals') await this.loadZenlySignals(false);
    }

    public zenlyPresencePlaces() {
        let places = this.zenlyHeatmap && Array.isArray(this.zenlyHeatmap.places) ? this.zenlyHeatmap.places : [];
        return places;
    }

    public selectedPresencePlace() {
        let places = this.zenlyPresencePlaces();
        return places.find((place: any) => place.place_id === this.selectedPresencePlaceId) || places[0] || {};
    }

    public async selectPresencePlace(place: any, event?: any) {
        if (event) event.stopPropagation();
        if (event && ['mousedown', 'touchstart'].indexOf(event.type) > -1 && event.preventDefault) event.preventDefault();
        if (!place) return;
        this.selectedPresencePlaceId = place.place_id;
        this.selectedZenlySignalId = '';
        this.selectedZenlyFallbackCard = {
            id: place.place_id,
            name: place.name || '장소',
            category: place.category || '장소',
            count: Math.max(0, Number(place.count || 0))
        };
        await this.service.render();
        this.bindZenlyMarkerDelegation();
        this.loadPresenceHourly(place.place_id, false);
    }

    public presenceBarHeight(count: any) {
        let max = Math.max(1, ...this.zenlyPresenceHourly.map((item: any) => Number(item.count || 0)));
        let value = Math.max(0, Number(count || 0));
        return `${Math.max(12, Math.round((value / max) * 58))}px`;
    }

    public selectedZenlySignal() {
        return this.zenlySignals.find((signal: any) => signal.id === this.selectedZenlySignalId) || this.zenlySignals[0] || {};
    }

    public isZenlyPresenceSelected(place: any) {
        return !!place && !!this.selectedPresencePlaceId && String(this.selectedPresencePlaceId) === String(place.place_id);
    }

    public isZenlySignalSelected(signal: any) {
        return !!signal && !!this.selectedZenlySignalId && String(this.selectedZenlySignalId) === String(signal.id);
    }

    public async selectZenlySignal(signal: any, event?: any) {
        if (event) event.stopPropagation();
        if (event && ['mousedown', 'touchstart'].indexOf(event.type) > -1 && event.preventDefault) event.preventDefault();
        if (!signal) return;
        this.selectedZenlySignalId = signal.id;
        this.selectedPresencePlaceId = '';
        this.selectedZenlyFallbackCard = {
            id: signal.id,
            name: signal.name || signal.message || '동행 신호',
            category: signal.category || '신호',
            count: Math.max(0, Number(signal.count || 0))
        };
        await this.service.render();
        this.bindZenlyMarkerDelegation();
    }

    public async closeZenlyMiniCard(event?: any) {
        let target = event && event.target ? event.target : null;
        if (target && target.closest && target.closest('.zenly-pin-marker, .zenly-summary-bar')) return;
        if (!this.selectedPresencePlaceId && !this.selectedZenlySignalId) return;
        this.selectedPresencePlaceId = '';
        this.selectedZenlySignalId = '';
        this.selectedZenlyFallbackCard = null;
        await this.service.render();
        this.bindZenlyMarkerDelegation();
    }

    public async handleZenlyMapPointer(event: any) {
        let target = event && event.target ? event.target : null;
        let marker = target && target.closest ? target.closest('.zenly-pin-marker') : null;
        if (!marker) return;
        if (event.stopPropagation) event.stopPropagation();
        if (event.preventDefault) event.preventDefault();

        let placeId = marker.getAttribute('data-place-id') || '';
        if (placeId) {
            await this.selectZenlyMarkerFallback(marker, placeId, 'density');
            return;
        }

        let signalId = marker.getAttribute('data-signal-id') || '';
        if (signalId) {
            await this.selectZenlyMarkerFallback(marker, signalId, 'signals');
        }
    }

    private async selectZenlyMarkerFallback(marker: any, id: string, type: string) {
        let card = this.zenlyFallbackCardFromMarker(marker, id, type);
        if (type === 'signals') {
            this.selectedZenlySignalId = id;
            this.selectedPresencePlaceId = '';
        } else {
            this.selectedPresencePlaceId = id;
            this.selectedZenlySignalId = '';
        }
        this.selectedZenlyFallbackCard = card;
        await this.service.render();
        this.bindZenlyMarkerDelegation();
    }

    private zenlyFallbackCardFromMarker(marker: any, id: string, type: string) {
        let label = String(marker && marker.getAttribute ? marker.getAttribute('aria-label') || '' : '').trim();
        let countMatch = label.match(/지금\s*([0-9]+)\s*명/);
        let count = countMatch ? Number(countMatch[1]) : 0;
        let name = label.replace(/\s*지금\s*[0-9]+\s*명.*$/, '').trim();
        if (!name) name = type === 'signals' ? '동행 신호' : '장소';
        return {
            id,
            name,
            category: type === 'signals' ? '신호' : '장소',
            count: Math.max(0, Number(count || 0))
        };
    }

    private bindZenlyMarkerDelegation() {
        if (typeof window === 'undefined' || typeof document === 'undefined') return;
        window.setTimeout(() => {
            let map: any = document.querySelector('.access-shell .zenly-live-map');
            if (!map || map.__gachiZenlyDelegationBound) return;
            map.__gachiZenlyDelegationBound = true;

            let selectFromEvent = (event: any) => {
                let target = event && event.target ? event.target : null;
                let marker = target && target.closest ? target.closest('.zenly-pin-marker') : null;
                if (!marker) return;
                if (event.preventDefault) event.preventDefault();
                if (event.stopPropagation) event.stopPropagation();

                let placeId = marker.getAttribute('data-place-id') || '';
                if (placeId) {
                    this.selectZenlyMarkerFallback(marker, placeId, 'density');
                    return;
                }

                let signalId = marker.getAttribute('data-signal-id') || '';
                if (signalId) {
                    this.selectZenlyMarkerFallback(marker, signalId, 'signals');
                }
            };

            map.addEventListener('click', selectFromEvent, true);
            map.addEventListener('mousedown', selectFromEvent, true);
            map.addEventListener('touchstart', selectFromEvent, { capture: true, passive: false });
        }, 0);
    }

    public zenlySignalMarkers() {
        if (this.zenlySignals && this.zenlySignals.length > 0) {
            return this.zenlySignals.map((signal: any, index: number) => ({
                ...signal,
                id: signal.id || `signal-${index}`,
                name: signal.placeName || signal.locationName || signal.message || '동행 신호',
                category: '신호',
                count: Math.max(1, Number(signal.responseCount || (signal.responses ? signal.responses.length : 0) || 1)),
                displayPosition: signal.displayPosition || this.seedSignalPosition(index)
            }));
        }
        return [];
    }

    public zenlyMarkerLevel(item: any) {
        let count = Math.max(0, Number(item && item.count ? item.count : 0));
        if (count >= 15) return 'high';
        if (count >= 7) return 'medium';
        return 'low';
    }

    public zenlyTotalCount() {
        let total = Math.max(0, Number(this.zenlyHeatmap && this.zenlyHeatmap.regionTotal ? this.zenlyHeatmap.regionTotal : 0));
        if (total > 0) return total;
        return this.zenlyPresencePlaces().reduce((sum: number, place: any) => {
            return sum + Math.max(0, Number(place && place.count ? place.count : 0));
        }, 0);
    }

    public zenlySummaryRegion() {
        let label = this.zenlyRegionLabel();
        if (!label) return '서울';
        if (label.indexOf('서울') > -1 || label.indexOf('성수') > -1) return '서울';
        return label;
    }

    public zenlySummaryText() {
        return `지금 ${this.zenlySummaryRegion()}에 ${this.zenlyTotalCount()}명이 있어요`;
    }

    public zenlySelectedCard() {
        if (this.zenlyLayerTab === 'density' && this.selectedPresencePlaceId) {
            let place = this.zenlyPresencePlaces().find((item: any) => item.place_id === this.selectedPresencePlaceId);
            if (!place && this.selectedZenlyFallbackCard && String(this.selectedZenlyFallbackCard.id) === String(this.selectedPresencePlaceId)) {
                return this.selectedZenlyFallbackCard;
            }
            if (!place) return null;
            return {
                id: place.place_id,
                name: place.name,
                category: place.category || '장소',
                count: Math.max(0, Number(place.count || 0))
            };
        }

        if (this.zenlyLayerTab === 'signals' && this.selectedZenlySignalId) {
            let signal = this.zenlySignalMarkers().find((item: any) => item.id === this.selectedZenlySignalId);
            if (!signal && this.selectedZenlyFallbackCard && String(this.selectedZenlyFallbackCard.id) === String(this.selectedZenlySignalId)) {
                return this.selectedZenlyFallbackCard;
            }
            if (!signal) return null;
            return {
                ...signal,
                id: signal.id,
                name: signal.name || signal.message || '동행 신호',
                category: signal.category || '신호',
                count: Math.max(0, Number(signal.count || 0))
            };
        }

        return null;
    }

    public zenlyCardIcon(card: any) {
        let category = String(card && card.category ? card.category : '').toLowerCase();
        if (category.indexOf('카페') > -1) return 'fa-mug-saucer';
        if (category.indexOf('맛') > -1) return 'fa-utensils';
        if (category.indexOf('산책') > -1) return 'fa-person-walking';
        if (category.indexOf('신호') > -1) return 'fa-paper-plane';
        return 'fa-location-dot';
    }

    public isZenlySignalCard(card: any) {
        return !!card && (card.category === '신호' || !!card.message || !!card.remainingLabel);
    }

    public zenlySignalMeta(signal: any) {
        return [
            signal && signal.rangeLabel ? signal.rangeLabel : '주변',
            signal && signal.remainingLabel ? `${signal.remainingLabel} 남음` : '시간 제한',
            signal && signal.responseStatus === 'pending' ? '관심 보냄' : ''
        ].filter((value: string) => !!value).join(' · ');
    }

    public zenlySignalTags(signal: any) {
        return Array.isArray(signal && signal.moodTags) ? signal.moodTags.slice(0, 6) : [];
    }

    public zenlySignalResponses(signal: any) {
        return Array.isArray(signal && signal.responses) ? signal.responses : [];
    }

    public zenlySignalResponseStatusLabel(response: any) {
        let labels: any = { pending: '검토 중', accepted: '수락됨', declined: '거절됨' };
        return labels[String(response && response.status || '')] || '관심';
    }

    public canRespondZenlySignal(signal: any) {
        return !!signal
            && !signal.owned
            && signal.status !== 'matched'
            && ['pending', 'accepted'].indexOf(String(signal.responseStatus || '')) < 0;
    }

    public hasNearbyZenlyTraveler() {
        return this.zenlyNearbyTravelerCount > 0;
    }

    public async openZenlySummary(event?: any) {
        if (event) event.stopPropagation();
    }

    public async viewZenlyCardCourse(card: any, event?: any) {
        if (event) event.stopPropagation();
        this.query = card && card.name ? card.name : '';
        this.activeTab = 'home';
        this.homeContentTab = 'courses';
        this.persistAccessState();
        this.replaceAccessUrl();
        await this.service.render();
    }

    private seedSignalPosition(index: number) {
        let positions = [
            { x: 34, y: 42 },
            { x: 62, y: 58 },
            { x: 49, y: 74 },
            { x: 72, y: 36 }
        ];
        return positions[index % positions.length];
    }

    public async toggleZenlySignalComposer() {
        if (!this.isLoggedIn()) {
            await this.openAuthModal('login');
            return;
        }
        this.zenlySignalComposerOpen = !this.zenlySignalComposerOpen;
        await this.service.render();
    }

    public async setZenlySignalLocationMode(mode: string) {
        if (mode !== 'current') return;
        this.zenlySignalDraft.locationMode = mode;
        if (mode === 'current') {
            this.zenlySignalDraft.place = null;
            this.zenlyPlaceSearchResults = [];
        } else if (!this.zenlyPlaceSearchResults.length) {
            await this.searchZenlySignalPlaces(false);
        }
        await this.service.render();
    }

    public async setZenlySignalDuration(duration: number) {
        if (!this.zenlySignalDurations.some((item: any) => item.value === duration)) return;
        this.zenlySignalDraft.duration = duration;
        await this.service.render();
    }

    public isZenlySignalTagSelected(tag: string) {
        return this.zenlySignalDraft.tags.indexOf(tag) > -1;
    }

    public async toggleZenlySignalTag(tag: string) {
        let tags = this.zenlySignalDraft.tags || [];
        this.zenlySignalDraft.tags = tags.indexOf(tag) > -1
            ? tags.filter((item: string) => item !== tag)
            : [...tags, tag].slice(0, 6);
        await this.service.render();
    }

    public async onZenlyPlaceSearchInput() {
        await this.searchZenlySignalPlaces(false);
    }

    public async searchZenlySignalPlaces(showLoading: boolean = true) {
        if (showLoading) await this.service.render();
        try {
            let center = this.currentMapCenter();
            const response: any = await wiz.call('search_course_places', {
                lat: center.lat,
                lng: center.lng,
                keyword: this.zenlySignalDraft.placeQuery || '',
                region: this.zenlyRegionLabel(),
                limit: 6
            });
            if (response && response.code === 200) {
                this.zenlyPlaceSearchResults = (response.data && response.data.rows) || [];
            }
        } catch (e) { }
        if (showLoading) await this.service.render();
    }

    public async chooseZenlySignalPlace(place: any) {
        if (!place) return;
        this.zenlySignalDraft.place = place;
        this.zenlySignalDraft.placeQuery = place.name || place.title || '';
        this.zenlyPlaceSearchResults = [];
        await this.service.render();
    }

    public async submitZenlySignal(event?: any) {
        if (event && event.preventDefault) event.preventDefault();
        if (this.zenlySignalSubmitting) return;
        if (!this.isLoggedIn()) {
            await this.openAuthModal('login');
            return;
        }
        if (!this.isTogetherMapActive()) {
            await this.showSaveHint('여행을 시작한 뒤 합류 신호를 보낼 수 있어요.');
            return;
        }
        if (!this.hasNearbyZenlyTraveler()) return;
        let message = String(this.zenlySignalDraft.message || '').trim();
        if (!message) {
            await this.showSaveHint('신호 메시지를 입력해주세요.');
            return;
        }
        if (message.length > 50) {
            await this.showSaveHint('합류 신호는 50자 이내로 입력해주세요.');
            return;
        }
        this.zenlySignalSubmitting = true;
        await this.service.render();
        let origin = await this.requestTogetherGpsCoordinate();
        if (!origin || !isFinite(origin.accuracy) || origin.accuracy <= 0 || origin.accuracy > 100) {
            this.zenlySignalSubmitting = false;
            await this.showSaveHint('현재 위치를 확인하지 못했어요. 위치 권한을 켜고 다시 시도해주세요.');
            return;
        }
        let payload: any = {
            message,
            mood_tags: JSON.stringify(this.zenlySignalDraft.tags || []),
            duration_minutes: this.zenlySignalDraft.duration,
            lat: origin.lat,
            lng: origin.lng,
            accuracy: origin.accuracy
        };
        try {
            const response: any = await wiz.call('zenly_signal_create', payload);
            if (response && response.code === 200 && response.data && response.data.signal) {
                this.zenlySignals = [response.data.signal, ...this.zenlySignals.filter((item: any) => item.id !== response.data.signal.id)];
                this.selectedZenlySignalId = response.data.signal.id;
                this.zenlySignalComposerOpen = false;
                this.zenlySignalDraft.message = '';
                this.zenlySignalDraft.tags = [];
                await this.showSaveHint('신호를 보냈어요.');
            } else {
                await this.showSaveHint(this.responseMessage(response && response.data, '신호를 보내지 못했어요.'));
            }
        } catch (e) {
            await this.showSaveHint('신호를 보내지 못했어요.');
        }
        this.zenlySignalSubmitting = false;
        await this.service.render();
    }

    public async respondZenlySignal(signal: any) {
        if (!signal || !signal.id) return;
        if (!this.isLoggedIn()) {
            await this.openAuthModal('login');
            return;
        }
        try {
            const origin = await this.requestTogetherGpsCoordinate();
            if (!origin) {
                await this.showSaveHint('현재 위치를 확인해야 관심을 보낼 수 있어요.');
                return;
            }
            const response: any = await wiz.call('zenly_signal_respond', { signal_id: signal.id, ...origin });
            if (response && response.code === 200 && response.data && response.data.signal) {
                this.applyZenlySignalUpdate(response.data.signal);
                await this.showSaveHint(response.data.already ? '이미 관심을 표시했어요.' : '관심을 보냈어요.');
            } else {
                await this.showSaveHint(this.responseMessage(response && response.data, '관심 표시를 보내지 못했어요.'));
            }
        } catch (e) {
            await this.showSaveHint('관심 표시를 보내지 못했어요.');
        }
    }

    public async updateZenlySignalResponse(signal: any, response: any, status: string) {
        if (!signal || !response || !signal.id || !response.id) return;
        try {
            const result: any = await wiz.call('zenly_signal_response_update', {
                signal_id: signal.id,
                response_id: response.id,
                status
            });
            if (result && result.code === 200 && result.data) {
                if (result.data.signal) this.applyZenlySignalUpdate(result.data.signal);
                if (status === 'accepted') {
                    if (result.data.meeting) {
                        this.applyTogetherMeetingPayload(result.data.meeting, result.data.messages || []);
                    } else {
                        await this.loadTogetherMeeting(false, false);
                    }
                    this.activeTab = 'map';
                    this.mapContentTab = 'zenly';
                    this.togetherInfoFocus = 'meeting';
                    this.togetherMeetingChatOpen = this.hasActiveTogetherMeetingChat();
                    this.persistAccessState();
                    this.replaceAccessUrl();
                }
                await this.service.render();
                await this.showSaveHint(status === 'accepted' ? '약속과 약속 채팅을 만들었어요.' : '응답을 거절했어요.');
                if (status === 'accepted') this.scrollTogetherMeetingChat();
            } else {
                await this.showSaveHint(this.responseMessage(result && result.data, '응답을 처리하지 못했어요.'));
            }
        } catch (e) {
            await this.showSaveHint('응답을 처리하지 못했어요.');
        }
    }

    public async reportZenlySignal(signal: any) {
        if (!signal || !signal.id) return;
        if (!this.isLoggedIn()) {
            await this.openAuthModal('login');
            return;
        }
        try {
            const response: any = await wiz.call('zenly_signal_report', {
                signal_id: signal.id,
                reason: '부적절한 신호'
            });
            if (response && response.code === 200 && response.data && response.data.signal) {
                this.applyZenlySignalUpdate(response.data.signal);
                await this.showSaveHint('신고가 접수됐어요.');
            } else {
                await this.showSaveHint(this.responseMessage(response && response.data, '신고를 접수하지 못했어요.'));
            }
        } catch (e) {
            await this.showSaveHint('신고를 접수하지 못했어요.');
        }
    }

    public async toggleSave(course: any) {
        if (this.companionCourseIsMine(course)) {
            course.saved = false;
            await this.service.render();
            return;
        }
        if (!this.isLoggedIn()) {
            await this.showSaveHint();
            return;
        }

        await this.persistCourseSave(course, !course.saved);
    }

    public isLoggedIn() {
        return !!(this.service.auth && this.service.auth.isLoggedIn && this.service.auth.isLoggedIn());
    }

    public myDisplayName() {
        let auth = this.service && this.service.auth ? this.service.auth : null;
        let session = auth && auth.session ? auth.session : {};
        let names = [
            session.nickname,
            session.display_name,
            session.displayName,
            session.name,
            session.username
        ];
        for (let value of names) {
            let name = String(value || '').trim();
            if (name) return name;
        }

        let email = String(session.email || '').trim();
        if (email) return email.split('@')[0];

        return '여행자';
    }

    public profileDisplayName() {
        this.ensureMyProfileEditDefaults();
        let nickname = String(this.myProfileEdit.nickname || '').trim();
        let accountName = this.myDisplayName();
        let genericNames = ['여행자', '여행자님'];
        if (nickname && (genericNames.indexOf(nickname) === -1 || accountName === '여행자')) return nickname;
        return accountName;
    }

    public profileTravelerTitle() {
        return String(this.myProfileEdit && this.myProfileEdit.travelerTitle || '').trim().slice(0, 20) || '프로 여행러';
    }

    public profileIntro() {
        this.ensureMyProfileEditDefaults();
        let intro = String(this.myProfileEdit.intro || '').trim();
        return intro || '내 코스와 여행 팔로우 관계를 한곳에서 확인하고, 인증샷은 각 코스 상세에서 볼 수 있어요.';
    }

    public async openMyProfileEdit() {
        if (!this.isLoggedIn()) {
            this.goLogin();
            return;
        }

        this.loadMyProfileEdit();
        this.syncMyProfileNicknameFromAccount();
        this.myProfileEditSnapshot = { ...this.myProfileEdit };
        this.myProfileEditing = false;
        this.myProfileSaving = false;
        this.myProfileOpen = true;
        this.resetMyProfileSubscreens();
        this.myProfileEditOpen = true;
        await this.service.render();
    }

    public async closeMyProfileEdit() {
        if (this.myProfileEditing && this.myProfileEditSnapshot) {
            this.myProfileEdit = { ...this.myProfileEditSnapshot };
        }
        this.myProfileEditing = false;
        this.myProfileSaving = false;
        this.myProfileEditOpen = false;
        await this.service.render();
    }

    public async startMyProfileEditing() {
        this.ensureMyProfileEditDefaults();
        this.myProfileEditSnapshot = { ...this.myProfileEdit };
        this.myProfileEditing = true;
        await this.service.render();
    }

    public async cancelMyProfileEditing() {
        if (this.myProfileEditSnapshot) this.myProfileEdit = { ...this.myProfileEditSnapshot };
        this.myProfileEditing = false;
        this.myProfileSaving = false;
        await this.service.render();
    }

    public async saveMyProfileEdit() {
        if (!this.myProfileEditing || this.myProfileSaving) return;
        this.ensureMyProfileEditDefaults();
        this.myProfileEdit.nickname = String(this.myProfileEdit.nickname || '').trim();
        this.myProfileEdit.travelerTitle = this.profileTravelerTitle();
        this.myProfileEdit.region = String(this.myProfileEdit.region || '').trim();
        this.myProfileEdit.intro = String(this.myProfileEdit.intro || '').trim();
        if (!this.myProfileEdit.nickname) {
            await this.showSaveHint('닉네임을 입력해주세요.');
            return;
        }

        this.myProfileSaving = true;
        this.persistMyProfileEdit();
        await this.service.render();
        try {
            const response: any = await wiz.call('saved_courses', {
                profile_action: 'update',
                nickname: this.myProfileEdit.nickname
            });
            if (!response || response.code !== 200) {
                await this.finishMyProfileSave({}, '', true);
                return;
            }

            let data = response.data || {};
            await this.finishMyProfileSave(data.session || {}, data.token || '', false);
        } catch (e) {
            await this.finishMyProfileSave({}, '', true);
        }
    }

    private async finishMyProfileSave(account: any = {}, token: string = '', localOnly: boolean = false) {
        let auth = this.service && this.service.auth ? this.service.auth : null;
        let current = auth && auth.session && typeof auth.session === 'object' ? auth.session : {};
        let merged = {
            ...current,
            ...(account && typeof account === 'object' ? account : {}),
            name: this.myProfileEdit.nickname,
            nickname: this.myProfileEdit.nickname
        };
        let nextToken = String(token || (auth && auth.token) || '').trim();
        if (auth && auth.setLocalSession && (merged.id || merged.email)) {
            auth.setLocalSession(merged, nextToken);
        }
        this.persistMyProfileEdit();
        this.myProfileEditSnapshot = { ...this.myProfileEdit };
        this.myProfileEditing = false;
        this.myProfileSaving = false;
        this.myProfileEditOpen = false;
        this.myProfileOpen = true;
        this.myProfileTab = 'myCourses';
        await this.showSaveHint(localOnly ? '프로필이 이 기기에 저장됐어요.' : '프로필이 저장됐어요.');
    }

    public async handleMyProfilePhotoUpload(event: any) {
        if (!this.myProfileEditing || this.myProfileSaving) return;
        let input = event && event.target ? event.target : null;
        let file = input && input.files && input.files[0] ? input.files[0] : null;
        if (!file) return;

        if (file.type && !/^image\//.test(file.type)) {
            await this.showSaveHint('이미지 파일만 등록할 수 있어요.');
            if (input) input.value = '';
            return;
        }

        let reader = new FileReader();
        reader.onload = () => {
            this.myProfileEdit.photo = String(reader.result || '');
            this.service.render();
        };
        reader.readAsDataURL(file);
        if (input) input.value = '';
    }

    public async openTravelResume(returnToSettings: boolean = false) {
        if (!this.isLoggedIn()) {
            this.goLogin();
            return;
        }

        await this.syncTravelResumeFromServer(false);
        this.ensureTravelResumeDefaults();
        this.myProfileOpen = true;
        this.resetMyProfileSubscreens();
        this.travelResumeReturnToSettings = !!returnToSettings;
        this.myResumeOpen = true;
        this.myResumePreviewOpen = false;
        this.travelResumeStep = 1;
        this.travelIdentity.loading = true;
        this.travelIdentity.error = '';
        await this.service.render();
        this.resetTravelResumeScroll();
        await this.loadTravelIdentityStatus();
    }

    public async handleTravelResumeBack() {
        if (!this.myResumePreviewOpen && this.travelResumeStep === 2) {
            await this.setTravelResumeStep(1);
            return;
        }
        await this.closeTravelResume();
    }

    public async closeTravelResume() {
        let returnToSettings = this.travelResumeReturnToSettings;
        this.myResumeOpen = false;
        this.myResumePreviewOpen = false;
        this.travelResumeStep = 1;
        this.travelResumeReturnToSettings = false;
        if (returnToSettings) this.myProfileOpen = false;
        await this.service.render();
    }

    public async setTravelResumeStep(step: any) {
        let nextStep = Number(step) === 2 ? 2 : 1;
        if (nextStep === 2 && !(await this.validateTravelResumeStepOne())) return;
        this.travelResumeStep = nextStep;
        this.myResumePreviewOpen = false;
        await this.service.render();
        this.resetTravelResumeScroll();
    }

    public async toggleTravelResumePreview() {
        this.ensureTravelResumeDefaults();
        if (!this.myResumePreviewOpen && !(await this.validateCompleteTravelResume())) return;
        this.myResumePreviewOpen = !this.myResumePreviewOpen;
        await this.service.render();
        this.resetTravelResumeScroll();
    }

    public async saveTravelResume() {
        this.ensureTravelResumeDefaults();
        if (!(await this.validateCompleteTravelResume())) return;
        this.travelResume.fullName = String(this.travelResume.fullName || '').trim();
        this.travelResume.age = this.normalizePositiveNumber(this.travelResume.age);
        this.travelResume.companionUses = this.normalizePositiveNumber(this.travelResume.companionUses);
        this.persistTravelResume();
        let synced = await this.saveTravelResumeRemote();
        await this.showSaveHint(synced ? '여행 이력서가 계정에 저장됐어요.' : '서버 연결이 불안정해 이 기기에 먼저 저장했어요.');
    }

    public async handleResumePhotoUpload(event: any) {
        let input = event && event.target ? event.target : null;
        let file = input && input.files && input.files[0] ? input.files[0] : null;
        if (!file) return;

        if (file.type && !/^image\//.test(file.type)) {
            await this.showSaveHint('이미지 파일만 등록할 수 있어요.');
            if (input) input.value = '';
            return;
        }

        if (Number(file.size || 0) > 15 * 1024 * 1024) {
            await this.showSaveHint('15MB 이하 사진을 선택해주세요.');
            if (input) input.value = '';
            return;
        }

        try {
            this.travelResume.photo = await this.prepareTravelResumePhoto(file);
            this.persistTravelResume();
            await this.service.render();
        } catch (e) {
            await this.showSaveHint('사진을 불러오지 못했어요. 다른 사진을 선택해주세요.');
        }
        if (input) input.value = '';
    }

    public travelResumeCompanionUses() {
        let saved = Number(this.travelResume && this.travelResume.companionUses ? this.travelResume.companionUses : 0);
        return Math.max(saved, this.inferredCompanionUseCount());
    }

    public travelResumeCompletion() {
        this.ensureTravelResumeDefaults();
        let checks = [
            this.isTravelResumeSafetyReady(),
            String(this.travelResume.photo || '').trim().length > 0,
            String(this.travelResume.fullName || '').trim().length > 0,
            this.normalizePositiveNumber(this.travelResume.age) > 0,
            String(this.travelResume.gender || '').trim().length > 0,
            String(this.travelResume.region || '').trim().length > 0,
            String(this.travelResume.smoking || '').trim().length > 0,
            String(this.travelResume.drinking || '').trim().length > 0
        ];
        let done = checks.filter((item: boolean) => item).length;
        return Math.round((done / checks.length) * 100);
    }

    public travelResumeProgressLabel() {
        if (!this.isTravelResumeSafetyReady()) return '본인인증 또는 무료 안전 기록에 동의해주세요.';
        if (!String(this.travelResume.photo || '').trim()) return '사진 1장만 추가하면 기본 정보가 끝나요.';
        if (!String(this.travelResume.region || '').trim()) return '거주지역만 선택하면 기본 정보가 끝나요.';
        let remaining = [this.travelResume.smoking, this.travelResume.drinking]
            .filter((value: any) => !String(value || '').trim()).length;
        if (remaining > 0) return `${remaining}가지만 선택하면 이력서가 완성돼요.`;
        return '필수 입력을 모두 완료했어요.';
    }

    public resumeValue(value: any, fallback: string) {
        let text = String(value || '').trim();
        return text || fallback;
    }

    public isTravelResumeIdentityVerified() {
        return !!(this.travelIdentity && this.travelIdentity.verified);
    }

    public isTravelResumeSafetyReady() {
        return this.isTravelResumeIdentityVerified()
            || !!(this.travelResume && this.travelResume.safetyRecordAccepted);
    }

    public travelResumeIdentityLabel() {
        if (this.isTravelResumeIdentityVerified()) return '본인 인증 완료';
        if (this.isTravelResumeSafetyReady()) return '계정·접속 안전 기록 동의';
        return '안전 확인 전';
    }

    public isResumeInterestSelected(value: string) {
        let selected = String(this.travelResume.interests || '')
            .split(',')
            .map((item: string) => item.trim())
            .filter((item: string) => !!item);
        return selected.indexOf(value) > -1;
    }

    public async toggleResumeInterest(value: string) {
        let selected = String(this.travelResume.interests || '')
            .split(',')
            .map((item: string) => item.trim())
            .filter((item: string) => !!item);
        let index = selected.indexOf(value);
        if (index > -1) selected.splice(index, 1);
        else selected.push(value);
        this.travelResume.interests = selected.join(', ');
        this.persistTravelResume();
        await this.saveTravelResumeRemote();
        await this.service.render();
    }

    public async selectResumePreference(field: string, value: string) {
        if (['smoking', 'drinking'].indexOf(field) === -1) return;
        this.travelResume[field] = value;
        this.persistTravelResume();
        await this.service.render();
    }

    public async setTravelResumeRegion(region: string) {
        this.travelResume.region = String(region || '').trim();
        this.persistTravelResume();
        await this.service.render();
    }

    public canContinueTravelResumeStepOne() {
        return this.isTravelResumeSafetyReady()
            && !!String(this.travelResume.photo || '').trim()
            && !!String(this.travelResume.fullName || '').trim()
            && this.normalizePositiveNumber(this.travelResume.age) > 0
            && !!String(this.travelResume.gender || '').trim()
            && !!String(this.travelResume.region || '').trim();
    }

    public canSaveTravelResume() {
        return this.canContinueTravelResumeStepOne()
            && !!String(this.travelResume.smoking || '').trim()
            && !!String(this.travelResume.drinking || '').trim();
    }

    public async startIdentityVerification() {
        if (!this.isLoggedIn()) {
            this.goLogin();
            return;
        }
        if (this.travelIdentity.verifying) return;

        this.travelIdentity.verifying = true;
        this.travelIdentity.error = '';
        await this.service.render();
        try {
            const startResponse: any = await wiz.call('identity_verification_start', {});
            const startData: any = startResponse && startResponse.data ? startResponse.data : {};
            if (!startResponse || startResponse.code !== 200) {
                this.travelIdentity.configured = startData.configured !== false;
                this.travelIdentity.error = String(startData.message || '본인 인증을 시작하지 못했어요.');
                return;
            }

            this.travelIdentity.configured = true;
            const portOne: any = await this.loadPortOneIdentitySdk();
            const redirectUrl = `${window.location.origin}/access?tab=my&identityReturn=1`;
            const result: any = await portOne.requestIdentityVerification({
                storeId: startData.storeId,
                identityVerificationId: startData.identityVerificationId,
                channelKey: startData.channelKey,
                redirectUrl,
                bypass: {
                    inicisUnified: {
                        flgFixedUser: 'N'
                    }
                }
            });

            if (result && result.code) {
                this.travelIdentity.error = String(result.message || '본인 인증이 취소됐어요.');
                return;
            }
            const completedId = String((result && result.identityVerificationId) || startData.identityVerificationId || '');
            await this.completeIdentityVerification(completedId);
        } catch (e) {
            this.travelIdentity.error = '본인 인증 창을 열지 못했어요. 잠시 후 다시 시도해주세요.';
        } finally {
            this.travelIdentity.verifying = false;
            await this.service.render();
        }
    }

    public async startSafetyRecordFallback() {
        if (!this.isLoggedIn()) {
            this.goLogin();
            return;
        }
        let accepted = await this.service.modal.show({
            title: '무료 안전 기록 동의',
            message: '동행 신청 시 계정 ID, 신청 시각, 접속 IP·브라우저, 이력서 사본을 180일 보관하고 적법한 수사기관 요청이 있을 때만 제공할 수 있습니다. 이는 통신사·정부 본인인증이 아닙니다.',
            action: '동의하고 계속',
            actionBtn: 'primary',
            status: 'info'
        });
        if (!accepted) return;
        this.ensureTravelResumeDefaults();
        let acceptedAt = new Date().toISOString();
        this.travelResume.safetyRecordAccepted = true;
        this.travelResume.safetyRecordAcceptedAt = acceptedAt;
        this.travelResume.safetyRecordConsentVersion = 'safety-record-v1-180d';
        this.travelIdentity.safetyRecordAccepted = true;
        this.travelIdentity.safetyRecordAcceptedAt = acceptedAt;
        if (!String(this.travelResume.fullName || '').trim()) {
            this.travelResume.fullName = String(this.myDisplayName() || '').trim();
        }
        this.persistTravelResume();
        await this.saveTravelResumeRemote();
        await this.service.render();
    }

    public async loadTravelIdentityStatus() {
        if (!this.isLoggedIn()) return;
        this.travelIdentity.loading = true;
        this.travelIdentity.error = '';
        try {
            const response: any = await wiz.call('identity_verification_status', {});
            const data: any = response && response.data ? response.data : {};
            if (response && response.code === 200) {
                this.travelIdentity.configured = data.configured !== false;
                this.applyTravelIdentityProfile(data.identity || {});
            } else {
                this.travelIdentity.verified = false;
                this.travelIdentity.error = String(data.message || '본인 인증 상태를 확인하지 못했어요.');
            }
        } catch (e) {
            this.travelIdentity.verified = false;
            this.travelIdentity.error = '본인 인증 상태를 확인하지 못했어요.';
        } finally {
            this.travelIdentity.loading = false;
            await this.service.render();
        }
    }

    private async restoreIdentityReturn() {
        let identityVerificationId = String(this.identityReturnId || '').trim();
        if (!identityVerificationId || !this.isLoggedIn()) return;

        this.identityReturnId = '';
        this.activeTab = 'my';
        this.myProfileOpen = true;
        this.resetMyProfileSubscreens();
        this.myResumeOpen = true;
        this.travelResumeStep = 1;
        this.travelIdentity.verifying = true;
        this.travelIdentity.error = '';
        let completed = await this.completeIdentityVerification(identityVerificationId);
        this.travelIdentity.verifying = false;
        if (completed) await this.showSaveHint('본인 인증이 완료됐어요. 사진만 추가해주세요.');
    }

    private async completeIdentityVerification(identityVerificationId: string) {
        if (!identityVerificationId) {
            this.travelIdentity.error = '인증 요청 정보를 찾지 못했어요. 본인 인증을 다시 시작해주세요.';
            return false;
        }
        try {
            const response: any = await wiz.call('identity_verification_complete', {
                identity_verification_id: identityVerificationId
            });
            const data: any = response && response.data ? response.data : {};
            if (!response || response.code !== 200) {
                this.travelIdentity.configured = data.configured !== false;
                this.travelIdentity.error = String(data.message || '본인 인증 결과를 확인하지 못했어요.');
                return false;
            }
            this.travelIdentity.configured = true;
            this.applyTravelIdentityProfile(data.identity || {});
            this.travelIdentity.error = '';
            return this.isTravelResumeIdentityVerified();
        } catch (e) {
            this.travelIdentity.error = '본인 인증 결과를 확인하지 못했어요. 잠시 후 다시 시도해주세요.';
            return false;
        }
    }

    private applyTravelIdentityProfile(profile: any) {
        let verified = !!(profile && profile.verified);
        this.travelIdentity = {
            ...this.travelIdentity,
            verified,
            name: verified ? String(profile.name || '').trim() : '',
            age: verified ? this.normalizePositiveNumber(profile.age) : 0,
            gender: verified ? String(profile.gender || '').trim() : '',
            verifiedAt: verified ? String(profile.verifiedAt || '').trim() : ''
        };
        if (!verified) return;

        this.ensureTravelResumeDefaults();
        this.ensureMyProfileEditDefaults();
        this.travelResume.fullName = this.travelIdentity.name;
        this.travelResume.age = this.travelIdentity.age;
        this.travelResume.gender = this.travelIdentity.gender;
        if (!String(this.travelResume.region || '').trim()) {
            this.travelResume.region = String(this.myProfileEdit.region || '').trim();
        }
        this.persistTravelResume();
    }

    private async validateTravelResumeStepOne() {
        this.ensureTravelResumeDefaults();
        if (!this.isTravelResumeSafetyReady()) {
            this.travelResumeStep = 1;
            await this.showSaveHint('본인인증 또는 무료 안전 기록 동의를 먼저 완료해주세요.');
            return false;
        }
        if (!String(this.travelResume.photo || '').trim()) {
            this.travelResumeStep = 1;
            await this.service.render();
            this.focusTravelResumeRequirement('travelResumePhotoCard');
            await this.showSaveHint('여행 이력서 사진은 필수예요. 사진 1장을 추가해주세요.');
            return false;
        }
        if (
            !String(this.travelResume.fullName || '').trim()
            || this.normalizePositiveNumber(this.travelResume.age) <= 0
            || !String(this.travelResume.gender || '').trim()
        ) {
            this.travelResumeStep = 1;
            await this.showSaveHint(this.isTravelResumeIdentityVerified() ? '기본 정보를 불러오지 못했어요. 본인 인증을 다시 해주세요.' : '이름·나이·성별을 입력해주세요.');
            return false;
        }
        if (!String(this.travelResume.region || '').trim()) {
            this.travelResumeStep = 1;
            await this.service.render();
            this.focusTravelResumeRequirement('travelResumeRegion');
            await this.showSaveHint('거주지역을 한 번만 선택해주세요.');
            return false;
        }
        return true;
    }

    private async validateCompleteTravelResume() {
        if (!(await this.validateTravelResumeStepOne())) return false;
        if (!String(this.travelResume.smoking || '').trim() || !String(this.travelResume.drinking || '').trim()) {
            this.travelResumeStep = 2;
            this.myResumePreviewOpen = false;
            await this.service.render();
            this.focusTravelResumeRequirement('travelResumePreferences');
            await this.showSaveHint('흡연 여부와 음주 성향만 선택하면 완료돼요.');
            return false;
        }
        return true;
    }

    private focusTravelResumeRequirement(elementId: string) {
        if (typeof document === 'undefined') return;
        setTimeout(() => {
            let element: any = document.getElementById(elementId);
            if (!element) return;
            if (element.scrollIntoView) element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            if (element.focus) element.focus({ preventScroll: true });
        }, 0);
    }

    private loadPortOneIdentitySdk() {
        if (typeof window === 'undefined' || typeof document === 'undefined') {
            return Promise.reject(new Error('browser only'));
        }
        let portOne = (window as any).PortOne;
        if (portOne && portOne.requestIdentityVerification) return Promise.resolve(portOne);
        if (this.portOneSdkLoader) return this.portOneSdkLoader;

        this.portOneSdkLoader = new Promise((resolve, reject) => {
            let script: any = document.getElementById('portone-v2-browser-sdk');
            let onLoad = () => {
                let loaded = (window as any).PortOne;
                if (loaded && loaded.requestIdentityVerification) resolve(loaded);
                else reject(new Error('PortOne SDK unavailable'));
            };
            let onError = () => reject(new Error('PortOne SDK load failed'));
            if (!script) {
                script = document.createElement('script');
                script.id = 'portone-v2-browser-sdk';
                script.src = 'https://cdn.portone.io/v2/browser-sdk.js';
                script.async = true;
            }
            script.addEventListener('load', onLoad, { once: true });
            script.addEventListener('error', onError, { once: true });
            if (!script.parentNode) document.head.appendChild(script);
        }).catch((error: any) => {
            this.portOneSdkLoader = null;
            throw error;
        });
        return this.portOneSdkLoader;
    }

    private prepareTravelResumePhoto(file: any): Promise<string> {
        return new Promise((resolve, reject) => {
            let reader = new FileReader();
            reader.onerror = () => reject(new Error('read failed'));
            reader.onload = () => {
                let image = new Image();
                image.onerror = () => reject(new Error('image failed'));
                image.onload = () => {
                    let maxSize = 720;
                    let scale = Math.min(1, maxSize / Math.max(image.width || 1, image.height || 1));
                    let width = Math.max(1, Math.round(image.width * scale));
                    let height = Math.max(1, Math.round(image.height * scale));
                    let canvas = document.createElement('canvas');
                    canvas.width = width;
                    canvas.height = height;
                    let context = canvas.getContext('2d');
                    if (!context) {
                        reject(new Error('canvas unavailable'));
                        return;
                    }
                    context.fillStyle = '#ffffff';
                    context.fillRect(0, 0, width, height);
                    context.drawImage(image, 0, 0, width, height);
                    resolve(canvas.toDataURL('image/jpeg', 0.84));
                };
                image.src = String(reader.result || '');
            };
            reader.readAsDataURL(file);
        });
    }

    public recentPlaceTime(place: any) {
        let raw = place && place.viewedAt ? place.viewedAt : '';
        let date = new Date(raw);
        if (!raw || isNaN(date.getTime())) return '방금 전';

        let now = new Date();
        let time = `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
        let today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
        let viewedDay = new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();

        if (viewedDay === today) return `오늘 ${time}`;
        if (viewedDay === today - 86400000) return `어제 ${time}`;
        return `${date.getMonth() + 1}.${date.getDate()} ${time}`;
    }

    public async openRecentPlace(place: any) {
        if (!place || !place.id) return;
        if (!this.isLoggedIn()) {
            this.goMapLogin();
            return;
        }

        this.activeTab = 'map';
        this.activeFilterKey = '';
        this.filterOverviewOpen = false;
        if (place.location && place.location !== '국내') {
            this.selectedFilters.location = place.location;
        } else {
            this.selectedFilters.location = '';
        }
        this.syncLocationRegion();
        if (place.category && this.activeMapCategories.hasOwnProperty(place.category)) {
            this.activeMapCategories[place.category] = true;
        }
        this.selectedMapSpotId = place.id;
        this.recordRecentPlace(place);
        this.persistAccessState();
        this.replaceAccessUrl();
        await this.service.render();
        this.scheduleNaverMapRender();
    }

    public async clearRecentPlaces() {
        this.recentPlaces = [];
        this.persistRecentPlaces();
        await this.service.render();
    }

    private resetMyProfileSubscreens() {
        if (this.myProfileEditing && this.myProfileEditSnapshot) {
            this.myProfileEdit = { ...this.myProfileEditSnapshot };
        }
        this.myProfileEditing = false;
        this.myProfileSaving = false;
        this.myCompanionEditOpen = false;
        this.myCompanionEditSaving = false;
        this.selectedMyCompanionPost = null;
        this.myCompanionEditDraft = {};
        this.companionCoursePickerOpen = false;
        this.companionSelectedCourse = null;
        this.companionRecruitDraft = this.defaultCompanionRecruitDraft();
        this.myProfileEditOpen = false;
        this.myResumeOpen = false;
        this.myResumePreviewOpen = false;
        this.travelResumeStep = 1;
        this.travelResumeReturnToSettings = false;
        this.myFeedComposerOpen = false;
        this.myArchiveOpen = false;
        this.myActivityOpen = false;
        this.mySettingsDetail = '';
        this.myFeedDetailOpen = false;
        this.myCourseDetailOpen = false;
        this.myCourseDeleteConfirmOpen = false;
        this.selectedMyProfilePost = null;
        this.selectedMyProfileCourse = null;
        this.feedComposerReturnCourse = null;
        this.profileFeedDetailPhotoIndex = 0;
        this.profileCourseDetailPhotoIndex = 0;
        this.profileCourseDetailLoading = false;
    }

    private resetNewFeedPost() {
        this.newFeedStep = 'select';
        this.newFeedPhotoIndex = 0;
        this.newFeedPost = {
            photo: '',
            photos: [],
            courseId: '',
            courseTitle: '',
            title: '',
            location: '',
            tags: '',
            caption: '',
            icon: 'fa-camera',
            tone: 'feed-rose'
        };
    }

    public async openMyProfile() {
        if (!this.isLoggedIn()) {
            this.goLogin();
            return;
        }

        this.myProfileOpen = true;
        this.myProfileTab = 'myCourses';
        this.resetMyProfileSubscreens();
        await this.loadMyProfileFollowing(false);
        await this.refreshBehavioralSignals();
        await this.service.render();
    }

    public async closeMyProfile() {
        this.myProfileOpen = false;
        this.myProfileTab = 'myCourses';
        this.resetMyProfileSubscreens();
        await this.service.render();
    }

    public isMyProfileMainVisible() {
        return !this.myProfileEditOpen && !this.myResumeOpen && !this.myFeedComposerOpen && !this.myArchiveOpen && !this.myActivityOpen && !this.mySettingsDetail && !this.myFeedDetailOpen && !this.myCourseDetailOpen;
    }

    public async setMyProfileTab(tab: string) {
        if (['myCourses', 'companions', 'savedCourses', 'following', 'followers'].indexOf(tab) === -1) return;
        this.myCompanionEditOpen = false;
        this.myCompanionEditSaving = false;
        this.selectedMyCompanionPost = null;
        this.myProfileTab = tab;
        if (tab === 'following') await this.loadMyProfileFollowing(false);
        await this.service.render();
    }

    public myCompanionPosts() {
        return this.companionPosts.filter((post: any) => {
            return !!post && post.owned === true && this.isConfirmedCompanionPost(post);
        });
    }

    public myCompanionPostCount() {
        return this.myCompanionPosts().length;
    }

    public async openMyCompanionEdit(post: any, event: any = null) {
        if (event && event.stopPropagation) event.stopPropagation();
        if (!post || post.owned !== true) return;
        await this.restoreSavedCourses();
        let courseId = String(post.courseId || post.originalCourseId || '').trim();
        let linkedCourse = [...this.courses, ...this.savedCourses()].find((course: any) => {
            return course && String(course.id || '') === courseId;
        });
        if (!linkedCourse) {
            linkedCourse = {
                id: courseId,
                title: String(post.route || post.title || '연결된 코스').replace(/\s*동행 모집\s*$/, ''),
                region: String(post.location || ''),
                image: String(post.image || ''),
                source: String(post.courseSource || 'mine'),
                route: {
                    title: String(post.route || post.title || '연결된 코스'),
                    stops: (Array.isArray(post.routeStops) ? post.routeStops : []).map((name: string) => ({ name }))
                }
            };
        }
        this.selectedMyCompanionPost = post;
        this.companionSelectedCourse = linkedCourse;
        this.companionRecruitDraft = {
            capacity: Number(post.capacity || 1),
            cost: String(post.estimatedCost || '').trim(),
            pace: String(post.pace || '').trim(),
            budgetStyle: String(post.budgetStyle || '').trim(),
            smoking: String(post.smoking || '').trim(),
            drinking: String(post.drinking || '').trim(),
            mood: Array.isArray(post.moodTags) ? post.moodTags.join(', ') : String(post.moodTags || '').trim(),
            flexible: Array.isArray(post.flexibility) ? post.flexibility.join(', ') : String(post.flexibility || '').trim(),
            meetingPoint: String(post.meetingPoint || '').trim(),
            meetingCoordinate: post.meetingCoordinate ? { ...post.meetingCoordinate } : null,
            startDate: this.companionCalendarDate(post.startDate || post.date),
            endDate: this.companionCalendarDate(post.endDate),
            time: /^\d{2}:\d{2}$/.test(String(post.time || '')) ? post.time : '',
            intro: String(post.intro || '').trim()
        };
        this.resetCompanionMeetingSearch();
        this.myCompanionEditOpen = true;
        this.companionCoursePickerOpen = true;
        await this.service.render();
    }

    public async cancelMyCompanionEdit() {
        this.resetCompanionMeetingSearch();
        this.companionCoursePickerOpen = false;
        this.companionSelectedCourse = null;
        this.companionRecruitDraft = this.defaultCompanionRecruitDraft();
        this.myCompanionEditOpen = false;
        this.myCompanionEditSaving = false;
        this.selectedMyCompanionPost = null;
        this.myCompanionEditDraft = {};
        await this.service.render();
    }

    public async saveMyCompanionEdit() {
        if (this.myCompanionEditSaving || !this.selectedMyCompanionPost) return;
        if (!this.validateCompanionSchedule()) {
            await this.showSaveHint('시작일을 선택하고 종료일이 시작일 이후인지 확인해주세요.');
            return;
        }
        let capacity = Number(this.companionRecruitDraft.capacity);
        if (!Number.isInteger(capacity) || capacity < 1 || capacity > 8) {
            await this.showSaveHint('모집 인원은 1명 이상 8명 이하로 입력해주세요.');
            return;
        }

        this.myCompanionEditSaving = true;
        this.companionRecruitSubmitting = true;
        await this.service.render();
        let updatedPost = {
            ...this.selectedMyCompanionPost,
            ...this.companionSchedulePayload(this.selectedMyCompanionPost.date, this.selectedMyCompanionPost.time),
            capacity,
            estimatedCost: String(this.companionRecruitDraft.cost || '비용 협의').trim(),
            pace: String(this.companionRecruitDraft.pace || '').trim(),
            budgetStyle: String(this.companionRecruitDraft.budgetStyle || '').trim(),
            smoking: String(this.companionRecruitDraft.smoking || '').trim(),
            drinking: String(this.companionRecruitDraft.drinking || '').trim(),
            moodTags: this.splitList(this.companionRecruitDraft.mood || ''),
            flexibility: this.splitList(this.companionRecruitDraft.flexible || ''),
            meetingPoint: String(this.companionRecruitDraft.meetingPoint || '').trim(),
            meetingCoordinate: this.companionRecruitDraft.meetingPoint && this.companionRecruitDraft.meetingCoordinate
                ? { ...this.companionRecruitDraft.meetingCoordinate } : null,
            intro: String(this.companionRecruitDraft.intro || '').trim()
        };
        let savedPost = await this.saveCompanionPostRemote(updatedPost);
        if (!savedPost) {
            this.myCompanionEditSaving = false;
            this.companionRecruitSubmitting = false;
            await this.showSaveHint('동행 모집글을 서버에 저장하지 못했어요.');
            return;
        }
        updatedPost = savedPost;
        this.companionPosts = [
            updatedPost,
            ...this.companionPosts.filter((post: any) => post && post.id !== updatedPost.id)
        ];
        this.persistCompanionPostsCache();
        this.companionCoursePickerOpen = false;
        this.companionSelectedCourse = null;
        this.companionRecruitDraft = this.defaultCompanionRecruitDraft();
        this.myCompanionEditOpen = false;
        this.myCompanionEditSaving = false;
        this.companionRecruitSubmitting = false;
        this.selectedMyCompanionPost = null;
        this.myCompanionEditDraft = {};
        this.resetCompanionMeetingSearch();
        await this.showSaveHint('동행 모집글이 수정됐어요.');
    }

    public async setMyCourseViewMode(mode: string) {
        if (['list', 'grid', 'blog'].indexOf(mode) === -1) return;
        this.myCourseViewMode = mode;
        this.persistMyCourseViewMode();
        await this.service.render();
    }

    public activeMyProfilePosts() {
        return this.myProfilePosts.filter((post: any) => !post.archived);
    }

    public visibleMyProfilePosts() {
        let posts = this.activeMyProfilePosts();
        if (this.myProfileTab === 'regram') {
            return posts.filter((post: any) => post.regrammed);
        }
        return posts;
    }

    public myProfileRegramCount() {
        return this.activeMyProfilePosts().filter((post: any) => post.regrammed).length;
    }

    public myProfileCourses() {
        let draft = this.courseDraftProfileCard();
        let seen: any = {};
        let mine = this.courses.filter((course: any) => {
            if (!course || course.source !== 'mine' || course.archived || !course.id || seen[course.id]) return false;
            seen[course.id] = true;
            return true;
        });
        return [...(draft ? [draft] : []), ...mine];
    }

    public archivedMyProfileCourses() {
        let seen: any = {};
        return this.courses.filter((course: any) => {
            if (!course || course.source !== 'mine' || !course.archived || !course.id || seen[course.id]) return false;
            seen[course.id] = true;
            return true;
        });
    }

    private courseDraftProfileCard() {
        let saved = this.readCourseDraft();
        if (!saved) return null;
        let places = Array.isArray(saved.builderDays) && saved.builderDays.length > 0
            ? saved.builderDays.reduce((items: any[], day: any) => items.concat((day && day.places) || []), [])
            : (Array.isArray(saved.builderPlaces) ? saved.builderPlaces : []);
        let firstImage = places.find((place: any) => place && place.image);
        return {
            id: 'local-course-draft',
            title: saved.title || '임시저장한 새 코스',
            location: saved.region || '지역 미정',
            summary: saved.description || (places.length ? places.map((place: any) => place.name).filter((name: string) => !!name).join(' · ') : '작성 중인 코스입니다.'),
            duration: saved.schedule || '일정 미정',
            category: saved.category === '데이트' ? '데이트' : '여행',
            icon: 'fa-box-archive',
            image: firstImage ? firstImage.image : (saved.photo || ''),
            saved: false,
            mine: true,
            source: 'draft',
            isCourseDraft: true,
            places
        };
    }

    public visibleMyProfileCourses() {
        if (this.myProfileTab === 'savedCourses') return this.savedCourses();
        return this.myProfileCourses();
    }

    public myProfileCourseCount() {
        return this.myProfileCourses().length;
    }

    public myProfileSavedCourseCount() {
        return this.savedCourses().length;
    }

    public myProfileCertificationCount() {
        return this.myProfileCourses().reduce((total: number, course: any) => total + this.profileCourseCertificationCount(course), 0);
    }

    public activeMyProfileFollowing() {
        return this.myProfileFollowing.filter((person: any) => person && person.following !== false);
    }

    private profilePersonAuthorId(person: any) {
        return String((person && (person.authorId || person.author_id || person.id)) || '').trim();
    }

    private applyMyProfileFollowing(rows: any[] = []) {
        this.myProfileFollowing = (rows || []).map((row: any) => ({
            authorId: String((row && (row.authorId || row.author_id || row.id)) || '').trim(),
            name: String((row && (row.name || row.authorName || row.author_name)) || 'GACHI 크리에이터').trim(),
            handle: String((row && (row.handle || row.authorHandle || row.author_handle)) || '').trim(),
            initial: String((row && (row.initial || row.authorInitial || row.author_initial)) || '').trim(),
            icon: String((row && row.icon) || 'fa-user').trim(),
            note: String((row && row.note) || '피드에서 팔로우한 여행자').trim(),
            following: true
        })).filter((person: any) => !!person.authorId);
    }

    private async loadMyProfileFollowing(showLoading: boolean = false) {
        if (!this.isLoggedIn()) {
            this.myProfileFollowing = [];
            return false;
        }
        try {
            const response: any = await wiz.call('saved_courses', { community_action: 'author_follows' });
            if (!response || response.code !== 200 || !response.data || !Array.isArray(response.data.following_authors)) return false;
            this.applyMyProfileFollowing(response.data.following_authors);
            if (showLoading) await this.service.render();
            return true;
        } catch (e) { }
        return false;
    }

    public myProfileFollowingCount() {
        return this.activeMyProfileFollowing().length;
    }

    public myProfileFollowersCount() {
        return this.myProfileFollowers.length;
    }

    public isProfileFollowing(person: any) {
        if (!person) return false;
        let authorId = this.profilePersonAuthorId(person);
        if (authorId) {
            return this.myProfileFollowing.some((item: any) => item && item.following !== false && this.profilePersonAuthorId(item) === authorId);
        }
        let handle = String(person.handle || '').trim();
        if (handle) {
            return this.myProfileFollowing.some((item: any) => item && item.following !== false && String(item.handle || '').trim() === handle);
        }
        return !!person.following;
    }

    public async toggleProfileFollow(person: any, event?: any) {
        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }
        if (!person) return;

        if (!this.isLoggedIn()) {
            await this.openAuthModal('login');
            return;
        }
        let authorId = this.profilePersonAuthorId(person);
        if (!authorId) {
            await this.showSaveHint('팔로우할 작성자 정보를 찾지 못했어요.');
            return;
        }
        let following = !this.isProfileFollowing(person);
        let response: any = null;
        try {
            response = await wiz.call('saved_courses', {
                community_action: 'author_follow',
                author_id: authorId,
                following
            });
        } catch (e) { }
        if (!response || response.code !== 200) {
            await this.showSaveHint(response && response.data && response.data.message ? response.data.message : '팔로우 상태를 변경하지 못했어요.');
            return;
        }
        this.allCourses().forEach((course: any) => {
            if (this.courseAuthorId(course) === authorId) {
                course.authorFollowing = following;
                course.author_following = following;
            }
        });
        if (this.selectedFeaturedCourse && this.courseAuthorId(this.selectedFeaturedCourse) === authorId) {
            this.selectedFeaturedCourse.authorFollowing = following;
            this.selectedFeaturedCourse.author_following = following;
        }
        if (response.data && Array.isArray(response.data.following_authors)) {
            this.applyMyProfileFollowing(response.data.following_authors);
        } else {
            person.following = following;
        }
        await this.showSaveHint(following ? '팔로우했어요.' : '팔로잉을 해제했어요.');
    }

    public profileCourseRegion(course: any) {
        return String((course && course.location) || '지역 미정');
    }

    public profileCourseTitle(course: any) {
        return String((course && course.title) || '여행 코스');
    }

    public profileCourseSummary(course: any) {
        return String((course && course.summary) || '저장한 여행 동선을 확인해보세요.');
    }

    public profileCourseDuration(course: any) {
        return String((course && course.duration) || '소요시간 미정');
    }

    public profileCourseIcon(course: any) {
        return String((course && course.icon) || 'fa-route');
    }

    public profileCourseCategoryLabel(course: any) {
        let explicit = String((course && course.category) || '').trim();
        if (explicit) return explicit;
        let icon = this.profileCourseIcon(course);
        if (icon.indexOf('mug') > -1 || icon.indexOf('coffee') > -1 || icon.indexOf('cookie') > -1) return '카페';
        if (icon.indexOf('water') > -1 || icon.indexOf('fish') > -1) return '바다';
        if (icon.indexOf('bed') > -1) return '숙소';
        if (icon.indexOf('utensils') > -1) return '맛집';
        if (icon.indexOf('walk') > -1 || icon.indexOf('tree') > -1) return '산책';
        if (icon.indexOf('car') > -1) return '드라이브';
        if (icon.indexOf('landmark') > -1 || icon.indexOf('camera') > -1) return '명소';
        return '코스';
    }

    public profileCourseCover(course: any) {
        if (!course) return '';
        if (!course.image_broken) {
            let direct = String(course.cover_image || course.image || '').trim();
            if (direct) return direct;
            let stop = this.profileCourseStops(course).find((item: any) => !!item.image);
            if (stop) return stop.image;
        }
        let shot = this.profileCourseCertShots(course).find((item: any) => !!item.src);
        return shot ? shot.src : '';
    }

    public profileCourseTags(course: any) {
        let tags = Array.isArray(course && course.tags) ? course.tags : [];
        return this.uniqueTags([
            this.profileCourseCategoryLabel(course),
            this.profileCourseRegion(course),
            ...tags
        ]).slice(0, 6);
    }

    public profileCourseDistance(course: any) {
        let route = course && course.route && typeof course.route === 'object' ? course.route : {};
        return String((course && course.distance) || route.total_distance || '').trim();
    }

    public profileCourseStops(course: any) {
        if (!course) return [];
        let route = course.route && typeof course.route === 'object' ? course.route : {};
        let raw: any[] = Array.isArray(course.places) ? course.places : [];
        if (raw.length === 0 && Array.isArray(route.places)) raw = route.places;
        if (raw.length === 0 && Array.isArray(route.days)) {
            raw = route.days.reduce((items: any[], day: any, dayIndex: number) => {
                let stops = day && Array.isArray(day.stops) ? day.stops : [];
                return items.concat(stops.map((stop: any) => ({
                    ...stop,
                    day: stop.day || day.day || dayIndex + 1,
                    day_label: stop.day_label || day.label || `${dayIndex + 1}일차`
                })));
            }, []);
        }

        let defaultDayLabel = String(this.profileCourseDuration(course)).indexOf('박') > -1 ? '1일차' : '오늘의 코스';
        let stops = raw.map((place: any, index: number) => {
            place = place && typeof place === 'object' ? place : {};
            let storedMeta = this.profileCourseStoredPlaceMeta(place);
            let name = String(place.name || place.title || place.place_name || '').trim();
            if (!name) return null;
            let day = Number(storedMeta.day || place.day || place.day_index || 0);
            let explicitDayLabel = String(
                storedMeta.day_label || place.day_label || place.dayLabel || (day > 0 ? `${day}일차` : '')
            ).trim();
            let dayLabel = explicitDayLabel || defaultDayLabel;
            return {
                id: place.place_id || place.placeId || place.id || `course-stop-${index}`,
                order: Number(place.order_index || place.order || index + 1),
                name,
                dayLabel,
                time: String(place.visit_time || place.visitTime || place.time || '').trim(),
                category: String(place.category_label || place.tag || place.category || '').trim(),
                address: String(place.area || place.address || place.addr || place.location || '').trim(),
                memo: String(
                    Object.prototype.hasOwnProperty.call(storedMeta, 'memo')
                        ? storedMeta.memo
                        : (place.memo || place.description || place.overview_summary || place.overview || '')
                ).trim(),
                move: String(place.move || place.transport || place.travel_time || place.travelTime || '').trim(),
                distance: String(place.distance || place.distance_text || place.distanceText || '').trim(),
                image: String(place.image || place.first_image || place.firstimage || place.first_image2 || place.thumbnail || '').trim(),
                lat: this.profileCourseCoordinateValue(place, 'lat'),
                lng: this.profileCourseCoordinateValue(place, 'lng'),
                raw: place,
                dayExplicit: !!explicitDayLabel,
                showDay: false
            };
        }).filter((stop: any) => !!stop);

        let summaryStops = this.profileCourseSummaryStops(course);
        if (stops.length === 0) {
            stops = summaryStops;
        } else if (summaryStops.length > 0) {
            stops = stops.map((stop: any, index: number) => {
                let stopKey = String(stop.name || '').replace(/[^0-9a-zA-Z가-힣]/g, '').toLowerCase();
                let schedule = summaryStops.find((candidate: any) => {
                    let candidateKey = String(candidate.name || '').replace(/[^0-9a-zA-Z가-힣]/g, '').toLowerCase();
                    return stopKey.length >= 2 && candidateKey.length >= 2
                        && (stopKey.indexOf(candidateKey) > -1 || candidateKey.indexOf(stopKey) > -1);
                });
                if (!schedule && summaryStops.length === stops.length) schedule = summaryStops[index];
                if (!schedule) return stop;
                return {
                    ...stop,
                    dayLabel: stop.dayExplicit ? stop.dayLabel : schedule.dayLabel,
                    time: stop.time || schedule.time
                };
            });
        }
        let previousDay = '';
        return stops.map((stop: any) => {
            let showDay = stop.dayLabel !== previousDay;
            previousDay = stop.dayLabel;
            return { ...stop, showDay };
        });
    }

    private profileCourseStoredPlaceMeta(place: any) {
        let memo = String(place && place.memo || '');
        let prefix = '__gachi_item__';
        if (memo.indexOf(prefix) !== 0) return {};
        try {
            let parsed = JSON.parse(memo.slice(prefix.length));
            return parsed && typeof parsed === 'object' ? parsed : {};
        } catch (e) {
            return {};
        }
    }

    private profileCourseSummaryStops(course: any) {
        let summary = this.profileCourseSummary(course);
        let parts = summary.split(/\s*(?:→|·|\n)\s*/).filter((part: string) => !!part.trim());
        if (parts.length < 2 && summary.indexOf(',') > -1) {
            parts = summary.split(/\s*,\s*/).filter((part: string) => !!part.trim());
        }
        if (parts.length < 2) return [];

        let dayLabel = String(this.profileCourseDuration(course)).indexOf('박') > -1 ? '1일차' : '오늘의 코스';
        let stops: any[] = [];
        parts.forEach((raw: string, index: number) => {
            let text = String(raw || '').replace(/^[\-–—•]\s*/, '').trim();
            let dayMatch = text.match(/^(\d+일차)\s*(.*)$/);
            if (dayMatch) {
                dayLabel = dayMatch[1];
                text = String(dayMatch[2] || '').replace(/^[:\-–—]\s*/, '').trim();
                if (!text) return;
            }
            let time = '';
            let timeMatch = text.match(/^(\d{1,2}:\d{2})\s*(.*)$/);
            if (timeMatch) {
                time = timeMatch[1];
                text = String(timeMatch[2] || '').trim();
            }
            if (!text) return;
            stops.push({
                id: `summary-stop-${index}`,
                order: stops.length + 1,
                name: text,
                dayLabel,
                time,
                category: '',
                address: '',
                memo: '',
                move: '',
                distance: '',
                image: '',
                showDay: false
            });
        });
        return stops;
    }

    public profileCourseMoveLabel(stop: any) {
        let values = [String((stop && stop.move) || '').trim(), String((stop && stop.distance) || '').trim()]
            .filter((value: string, index: number, items: string[]) => !!value && items.indexOf(value) === index);
        return values.join(' · ') || '다음 장소로 이동';
    }

    public profileCourseStopIcon(stop: any) {
        let text = `${(stop && stop.category) || ''} ${(stop && stop.name) || ''}`;
        if (/카페|커피|디저트/.test(text)) return 'fa-mug-saucer';
        if (/맛집|음식|식당|요리/.test(text)) return 'fa-utensils';
        if (/숙소|호텔|스테이/.test(text)) return 'fa-bed';
        if (/산책|공원|숲|해변/.test(text)) return 'fa-person-walking';
        if (/쇼핑|시장|소품/.test(text)) return 'fa-bag-shopping';
        return 'fa-location-dot';
    }

    public hideCourseBlogImage(event: any) {
        let image = event && event.target ? event.target : null;
        if (image && image.style) image.style.display = 'none';
    }

    public profileCourseSaveCount(course: any) {
        let fixed = Number((course && (course.saves || course.savedCount || course.likes)) || 0);
        if (fixed > 0) return fixed;
        let seed = String((course && (course.id || course.title)) || 'course')
            .split('')
            .reduce((total: number, char: string) => total + char.charCodeAt(0), 0);
        return 42 + (seed % 87);
    }

    public profileCourseCertificationCount(course: any) {
        return this.profileCourseCertShots(course).length;
    }

    public profileCourseCertShots(course: any) {
        let related = this.activeMyProfilePosts()
            .filter((post: any) => this.isPostRelatedToCourse(post, course))
            .reduce((items: any[], post: any) => {
                let photos = Array.isArray(post.photos) && post.photos.length > 0 ? post.photos : (post.photo ? [{ src: post.photo, caption: post.caption || '' }] : []);
                photos.forEach((photo: any) => {
                    let src = String((photo && photo.src) || photo || '').trim();
                    if (!src) return;
                    items.push({
                        src,
                        icon: post.icon || this.profileCourseIcon(course),
                        caption: (photo && photo.caption) || post.caption || this.profileCourseSummary(course),
                        author: post.author || this.myDisplayName(),
                        location: post.location || this.profileCourseRegion(course)
                    });
                });
                return items;
            }, []);

        return related.slice(0, 24);
    }

    public selectedCourseCertShots() {
        return this.profileCourseCertShots(this.selectedMyProfileCourse);
    }

    public selectedCourseCertShot() {
        let shots = this.selectedCourseCertShots();
        if (shots.length === 0) return {};
        let index = Math.min(Math.max(this.profileCourseDetailPhotoIndex, 0), shots.length - 1);
        return shots[index] || {};
    }

    public courseShareUrl(course: any) {
        if (!course || !course.id || typeof window === 'undefined') return '';
        return `${window.location.origin}/access?course=${encodeURIComponent(String(course.id))}`;
    }

    public courseShareFallbackId = '';
    public courseShareFallbackUrl = '';

    public async shareCourse(course: any = null) {
        course = course || this.activeInlineCourse();
        if (!course || !course.id) return;
        if (course.is_public === false || course.archived) {
            await this.showSaveHint('공개 코스로 전환하면 읽기 전용 링크를 공유할 수 있어요.');
            return;
        }
        let url = this.courseShareUrl(course);
        this.courseShareFallbackId = '';
        this.courseShareFallbackUrl = '';
        if (typeof navigator !== 'undefined' && navigator.share) {
            try {
                await navigator.share({ title: this.profileCourseTitle(course), text: this.profileCourseSummary(course), url });
                return;
            } catch (error: any) {
                if (error && error.name === 'AbortError') return;
            }
        }
        try {
            if (typeof navigator === 'undefined' || !navigator.clipboard) throw new Error('Clipboard unavailable');
            await navigator.clipboard.writeText(url);
            await this.showSaveHint('코스 공유 링크를 복사했어요.');
        } catch (error) {
            this.courseShareFallbackId = String(course.id);
            this.courseShareFallbackUrl = url;
            await this.service.render();
            await this.showSaveHint('아래 링크를 선택해 직접 복사해주세요.');
        }
    }

    private calendarDateKey(course: any) {
        let value = String((course && (course.scheduleDate || course.schedule_date || course.start_date || course.date)) || '').trim();
        let match = value.match(/\d{4}-\d{2}-\d{2}/);
        if (match) return match[0].replace(/-/g, '');
        return new Date().toISOString().slice(0, 10).replace(/-/g, '');
    }

    private calendarEscape(value: any) {
        return String(value || '').replace(/\\/g, '\\\\').replace(/\n/g, '\\n').replace(/,/g, '\\,').replace(/;/g, '\\;');
    }

    public async exportCourseCalendar(course: any = null) {
        course = course || this.activeInlineCourse();
        if (!course || typeof window === 'undefined') return;
        let start = this.calendarDateKey(course);
        let date = new Date(`${start.slice(0, 4)}-${start.slice(4, 6)}-${start.slice(6, 8)}T00:00:00Z`);
        date.setUTCDate(date.getUTCDate() + 1);
        let end = date.toISOString().slice(0, 10).replace(/-/g, '');
        let stops = this.profileCourseStops(course).map((stop: any) => stop.name).join(' → ');
        let lines = [
            'BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//GACHI//Travel Course//KO',
            'BEGIN:VEVENT', `UID:${String(course.id || Date.now())}@gachi.travel`,
            `DTSTART;VALUE=DATE:${start}`, `DTEND;VALUE=DATE:${end}`,
            `SUMMARY:${this.calendarEscape(this.profileCourseTitle(course))}`,
            `DESCRIPTION:${this.calendarEscape(stops || this.profileCourseSummary(course))}`,
            `LOCATION:${this.calendarEscape(this.profileCourseRegion(course))}`,
            `URL:${this.calendarEscape(this.courseShareUrl(course))}`,
            'END:VEVENT', 'END:VCALENDAR'
        ];
        let blob = new Blob([lines.join('\r\n')], { type: 'text/calendar;charset=utf-8' });
        let url = URL.createObjectURL(blob);
        let anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = `gachi-${String(course.id || 'course')}.ics`;
        document.body.appendChild(anchor);
        try {
            anchor.click();
            await this.showSaveHint('캘린더 파일 다운로드를 요청했어요. 저장한 .ics 파일을 캘린더에서 열어주세요.');
        } catch (error) {
            await this.showSaveHint('캘린더 파일을 다운로드하지 못했어요. 브라우저의 다운로드 권한을 확인해주세요.');
        } finally {
            anchor.remove();
            window.setTimeout(() => URL.revokeObjectURL(url), 60000);
        }
    }

    public collaborationStatusLabel(item: any) {
        if (item && item.status === 'accepted') return '반영';
        if (item && item.status === 'rejected') return '보류';
        return '검토 중';
    }

    private resetCourseCollaboration() {
        this.courseCollaborationItems = [];
        this.courseCollaborationDraft = { title: '', detail: '' };
        this.courseCollaborationLoading = false;
        this.courseCollaborationSaving = false;
        this.courseCollaborationIsOwner = false;
    }

    private async loadCourseCollaboration(course: any, showLoading: boolean = true) {
        let courseId = String((course && course.id) || '').trim();
        if (!courseId) return false;
        this.courseCollaborationLoading = showLoading;
        try {
            const response: any = await wiz.call('course_collaboration', { action: 'load', course_id: courseId });
            if (response && response.code === 200 && response.data) {
                this.courseCollaborationItems = Array.isArray(response.data.items) ? response.data.items : [];
                this.courseCollaborationIsOwner = !!response.data.is_owner;
                this.courseCollaborationLoading = false;
                return true;
            }
        } catch (e) { }
        this.courseCollaborationLoading = false;
        return false;
    }

    private applyCourseCollaborationResponse(response: any) {
        if (!response || response.code !== 200 || !response.data) return false;
        this.courseCollaborationItems = Array.isArray(response.data.items) ? response.data.items : [];
        this.courseCollaborationIsOwner = !!response.data.is_owner;
        return true;
    }

    public async submitCourseCollaboration() {
        let course = this.activeInlineCourse();
        let title = String(this.courseCollaborationDraft.title || '').trim();
        if (!course || !title || this.courseCollaborationSaving) return;
        if (!this.isLoggedIn()) {
            await this.openAuthModal('login');
            return;
        }
        this.courseCollaborationSaving = true;
        let response: any = null;
        try {
            response = await wiz.call('course_collaboration', {
                action: 'add', course_id: course.id, title,
                detail: String(this.courseCollaborationDraft.detail || '').trim()
            });
        } catch (e) { }
        this.courseCollaborationSaving = false;
        if (!this.applyCourseCollaborationResponse(response)) {
            await this.showSaveHint('편집 제안을 저장하지 못했어요.');
            return;
        }
        this.courseCollaborationDraft = { title: '', detail: '' };
        await this.showSaveHint('공동 편집 제안을 등록했어요.');
    }

    public async toggleCourseCollaborationVote(item: any) {
        let course = this.activeInlineCourse();
        if (!course || !item) return;
        if (!this.isLoggedIn()) {
            await this.openAuthModal('login');
            return;
        }
        let response: any = null;
        try {
            response = await wiz.call('course_collaboration', { action: 'vote', course_id: course.id, item_id: item.id });
        } catch (e) { }
        if (!this.applyCourseCollaborationResponse(response)) await this.showSaveHint('투표를 반영하지 못했어요.');
        else await this.service.render();
    }

    public async setCourseCollaborationStatus(item: any, status: string) {
        let course = this.activeInlineCourse();
        if (!course || !item || !this.courseCollaborationIsOwner) return;
        let response: any = null;
        try {
            response = await wiz.call('course_collaboration', { action: 'status', course_id: course.id, item_id: item.id, status });
        } catch (e) { }
        if (!this.applyCourseCollaborationResponse(response)) await this.showSaveHint('제안 상태를 변경하지 못했어요.');
        else await this.service.render();
    }

    public async openMyCourseDetail(course: any, returnContext: string = '') {
        if (!course) return;
        if (course.isCourseDraft) {
            await this.loadSavedCourseDraft();
            return;
        }
        this.profileCourseResolvedCoordinates = {};
        this.profileCourseStaticMapUrl = '';
        this.profileCourseDynamicMapEnabled = false;
        this.courseDetailReturnContext = returnContext;
        this.selectedMyProfileCourse = course;
        this.profileCourseDetailPhotoIndex = 0;
        this.profileCourseDetailLoading = true;
        this.myCourseDetailOpen = true;
        this.myResumeOpen = false;
        this.myFeedComposerOpen = false;
        this.myArchiveOpen = false;
        this.myActivityOpen = false;
        this.myFeedDetailOpen = false;
        await this.service.render();
        await Promise.all([
            this.loadProfileCourseDetail(course),
            this.loadProfileCourseStories(course)
        ]);
        if (this.selectedMyProfileCourse && String(this.selectedMyProfileCourse.id || '') === String(course.id || '')) {
            this.profileCourseDetailLoading = false;
            await this.service.render();
            this.scheduleProfileCourseInlineMap();
        }
    }

    public async closeMyCourseDetail() {
        if (this.myCourseDeleteSubmittingId) return;
        let returnContext = this.courseDetailReturnContext;
        this.resetProfileCourseInlineMap();
        this.resetProfileCourseMap();
        this.profileCourseResolvedCoordinates = {};
        this.myCourseDeleteConfirmOpen = false;
        this.myCourseDetailOpen = false;
        this.profileCourseDetailPhotoIndex = 0;
        this.profileCourseDetailLoading = false;
        this.selectedMyProfileCourse = null;
        this.resetCourseCollaboration();
        this.courseDetailReturnContext = '';
        if (returnContext === 'companion') {
            this.activeTab = 'home';
            this.homeContentTab = 'companion';
            this.companionMode = 'pretrip';
            this.myProfileOpen = false;
            this.persistAccessState();
            this.replaceAccessUrl();
        }
        await this.service.render();
        if (returnContext === 'companion') this.resetHomeContentScroll();
    }

    private activeInlineCourse() {
        if (this.featuredCourseDetailOpen && this.selectedFeaturedCourse) return this.selectedFeaturedCourse;
        if (this.myCourseDetailOpen && this.selectedMyProfileCourse) return this.selectedMyProfileCourse;
        return null;
    }

    public profileCourseMapStops(course: any = this.selectedMyProfileCourse) {
        return this.profileCourseStops(course)
            .map((stop: any, index: number) => {
                let resolved = this.profileCourseResolvedCoordinates[this.profileCourseStopCoordinateKey(stop, index)] || {};
                return {
                    ...stop,
                    lat: this.isFiniteNumber(stop.lat) ? Number(stop.lat) : resolved.lat,
                    lng: this.isFiniteNumber(stop.lng) ? Number(stop.lng) : resolved.lng
                };
            })
            .filter((stop: any) => this.isFiniteNumber(stop.lat) && this.isFiniteNumber(stop.lng));
    }

    public async openProfileCourseStopDetail(stop: any, event?: any) {
        if (event && event.preventDefault) event.preventDefault();
        if (event && event.stopPropagation) event.stopPropagation();
        if (!stop) return;
        let raw = stop.raw && typeof stop.raw === 'object' ? stop.raw : {};
        await this.openCoursePlaceDetail({
            ...raw,
            ...stop,
            name: stop.name || raw.name || raw.title,
            address: stop.address || raw.address || raw.area || raw.location,
            category: stop.category || raw.category || raw.category_label,
            image: stop.image || raw.image || raw.first_image || raw.thumbnail,
            description: stop.memo || raw.description || raw.overview,
            time: stop.time || raw.visit_time || raw.time
        });
    }

    public async openProfileCourseMap(event?: any) {
        if (event && event.preventDefault) event.preventDefault();
        if (event && event.stopPropagation) event.stopPropagation();
        if (this.profileCourseMapOpen) return;
        this.profileCourseMapOpen = true;
        this.profileCourseMapLoading = true;
        this.profileCourseMapError = '';
        await this.service.render();
        setTimeout(() => this.renderProfileCourseMap(), 0);
    }

    public async closeProfileCourseMap() {
        this.resetProfileCourseMap();
        await this.service.render();
    }

    public async retryProfileCourseMap(event?: any) {
        if (event && event.preventDefault) event.preventDefault();
        if (event && event.stopPropagation) event.stopPropagation();
        this.profileCourseMapLoading = true;
        this.profileCourseMapError = '';
        await this.service.render();
        setTimeout(() => this.renderProfileCourseMap(), 0);
    }

    public async retryProfileCourseInlineMap(event?: any) {
        if (event && event.preventDefault) event.preventDefault();
        if (event && event.stopPropagation) event.stopPropagation();
        this.scheduleProfileCourseInlineMap();
    }

    private scheduleProfileCourseInlineMap() {
        if (this.profileCourseStops(this.activeInlineCourse()).length === 0) {
            this.resetProfileCourseInlineMap();
            return;
        }
        let token = ++this.profileCourseInlineMapRenderToken;
        this.profileCourseInlineMapLoading = true;
        this.profileCourseInlineMapError = '';
        this.service.render().then(() => setTimeout(() => this.renderProfileCourseInlineMap(token), 0));
    }

    private resetProfileCourseInlineMap() {
        ++this.profileCourseInlineMapRenderToken;
        this.profileCourseInlineNaverMarkers.forEach((marker: any) => marker && marker.setMap && marker.setMap(null));
        this.profileCourseInlineNaverMarkers = [];
        if (this.profileCourseInlineNaverRouteLine && this.profileCourseInlineNaverRouteLine.setMap) {
            this.profileCourseInlineNaverRouteLine.setMap(null);
        }
        this.profileCourseInlineNaverRouteLine = null;
        this.profileCourseInlineNaverMap = null;
        this.profileCourseInlineNaverMapElement = null;
        this.profileCourseInlineMapLoading = false;
        this.profileCourseInlineMapError = '';
    }

    private resetProfileCourseMap() {
        this.profileCourseNaverMarkers.forEach((marker: any) => marker && marker.setMap && marker.setMap(null));
        this.profileCourseNaverMarkers = [];
        this.profileCourseNaverInfoWindows.forEach((info: any) => info && info.close && info.close());
        this.profileCourseNaverInfoWindows = [];
        if (this.profileCourseNaverRouteLine && this.profileCourseNaverRouteLine.setMap) {
            this.profileCourseNaverRouteLine.setMap(null);
        }
        this.profileCourseNaverRouteLine = null;
        this.profileCourseNaverMap = null;
        this.profileCourseNaverMapElement = null;
        this.profileCourseMapOpen = false;
        this.profileCourseMapLoading = false;
        this.profileCourseMapError = '';
        this.profileCourseStaticMapUrl = '';
        this.profileCourseDynamicMapEnabled = false;
        this.coursePlaceDetailOpen = false;
        this.coursePlaceDetailLoading = false;
        this.coursePlaceDetailError = '';
    }

    private profileCourseCoordinateValue(place: any, axis: 'lat' | 'lng') {
        let source = place && typeof place === 'object' ? place : {};
        let coordinate = source.coordinate && typeof source.coordinate === 'object'
            ? source.coordinate
            : (source.coordinates && typeof source.coordinates === 'object' ? source.coordinates : {});
        let candidates = axis === 'lat'
            ? [source.lat, source.latitude, coordinate.lat, coordinate.latitude]
            : [source.lng, source.lon, source.longitude, coordinate.lng, coordinate.lon, coordinate.longitude];
        for (let value of candidates) {
            let parsed = this.safeNumber(value);
            if (parsed !== null) return parsed;
        }
        return null;
    }

    private async resolveProfileCourseMapStops(google: any, stops: any[], isActive: () => boolean = () => this.profileCourseMapOpen, course: any = this.selectedMyProfileCourse) {
        let unresolved = (stops || []).map((stop: any, index: number) => ({ stop, index })).filter((entry: any) => {
            let stop = entry.stop;
            if (this.isFiniteNumber(stop.lat) && this.isFiniteNumber(stop.lng)) return false;
            return !this.profileCourseResolvedCoordinates[this.profileCourseStopCoordinateKey(stop, entry.index)];
        });
        for (let entry of unresolved) {
            if (!isActive()) return;
            let stop = entry.stop;
            let coordinate = await this.resolveProfileCourseStopCoordinate(google, stop, course);
            if (coordinate) {
                this.profileCourseResolvedCoordinates[this.profileCourseStopCoordinateKey(stop, entry.index)] = coordinate;
            }
        }
    }

    private profileCourseStopCoordinateKey(stop: any, index: number) {
        let id = String(stop && stop.id || '').trim();
        if (id) return `id:${id}`;
        return `stop:${index}:${String(stop && stop.name || '').trim()}:${String(stop && stop.address || '').trim()}`;
    }

    private async renderProfileCourseInlineMap(token: number) {
        let course = this.activeInlineCourse();
        let featured = !!(this.featuredCourseDetailOpen && this.selectedFeaturedCourse === course);
        let selector = featured
            ? '.access-shell .featured-course-inline-route-map'
            : '.access-shell .profile-course-inline-route-map';
        let isActive = () => token === this.profileCourseInlineMapRenderToken && this.activeInlineCourse() === course;
        let allStops = this.profileCourseStops(course);
        if (!allStops.length || !isActive()) return;

        let google = await this.loadNaverMapsScript();
        if (!isActive()) return;
        if (!this.isNaverMapsReady(google)) {
            this.profileCourseInlineMapLoading = false;
            this.profileCourseInlineMapError = 'NAVER 지도를 불러오지 못했어요. 잠시 후 다시 시도해주세요.';
            await this.service.render();
            return;
        }

        await this.resolveProfileCourseMapStops(google, allStops, isActive, course);
        if (!isActive()) return;
        let stops = this.profileCourseMapStops(course);
        if (!stops.length) {
            this.profileCourseInlineMapLoading = false;
            this.profileCourseInlineMapError = 'NAVER 지도에서 장소 위치를 찾지 못했어요.';
            await this.service.render();
            return;
        }

        await new Promise((resolve) => {
            let root: any = typeof window !== 'undefined' ? window : null;
            let frame = root && root.requestAnimationFrame
                ? root.requestAnimationFrame.bind(root)
                : (callback: any) => setTimeout(callback, 16);
            frame(() => frame(resolve));
        });
        if (!isActive()) return;

        let element: any = document.querySelector(selector);
        if (!element || !element.offsetWidth || !element.offsetHeight) {
            this.profileCourseInlineMapLoading = false;
            this.profileCourseInlineMapError = '지도 영역을 준비하지 못했어요.';
            await this.service.render();
            return;
        }

        this.resetProfileCourseInlineMapObjects();
        while (element.firstChild) element.removeChild(element.firstChild);
        let points = stops.map((stop: any) => ({ lat: Number(stop.lat), lng: Number(stop.lng) }));
        try {
            this.profileCourseInlineNaverMap = new google.maps.Map(element, {
                center: points[0],
                zoom: points.length > 1 ? 13 : 15,
                clickableIcons: false,
                disableDefaultUI: true,
                gestureHandling: 'cooperative',
                keyboardShortcuts: true,
                zoomControl: true,
                streetViewControl: false,
                mapTypeControl: false,
                fullscreenControl: false
            });
            this.profileCourseInlineNaverMapElement = element;
            this.profileCourseInlineNaverMarkers = points.map((position: any, index: number) => {
                let stop = stops[index];
                let marker = new google.maps.Marker({
                    map: this.profileCourseInlineNaverMap,
                    position,
                    title: stop.name || `${index + 1}번 장소`,
                    label: { text: String(index + 1), color: '#ffffff', fontSize: '10px', fontWeight: '800' },
                    icon: {
                        path: google.maps.SymbolPath.CIRCLE,
                        scale: 10,
                        fillColor: '#F20D19',
                        fillOpacity: 1,
                        strokeColor: '#ffffff',
                        strokeWeight: 3
                    }
                });
                if (marker.addListener) marker.addListener('click', () => this.openProfileCourseStopDetail(stop));
                return marker;
            });
            if (points.length > 1) {
                this.profileCourseInlineNaverRouteLine = new google.maps.Polyline({
                    map: this.profileCourseInlineNaverMap,
                    path: points,
                    strokeColor: '#F20D19',
                    strokeOpacity: 0.88,
                    strokeWeight: 4,
                    geodesic: false
                });
            }
            let fitMap = () => {
                if (!this.profileCourseInlineNaverMap || !isActive()) return;
                if (points.length > 1) {
                    let bounds = new google.maps.LatLngBounds();
                    points.forEach((point: any) => bounds.extend(point));
                    this.profileCourseInlineNaverMap.fitBounds(bounds, 34);
                } else {
                    this.profileCourseInlineNaverMap.setCenter(points[0]);
                    this.profileCourseInlineNaverMap.setZoom(15);
                }
            };
            fitMap();
            setTimeout(() => {
                if (!this.profileCourseInlineNaverMap || !isActive()) return;
                if (google.maps.event && google.maps.event.trigger) google.maps.event.trigger(this.profileCourseInlineNaverMap, 'resize');
                fitMap();
            }, 120);
            this.profileCourseInlineMapLoading = false;
            this.profileCourseInlineMapError = '';
            await this.service.render();
        } catch (e) {
            this.resetProfileCourseInlineMapObjects();
            this.profileCourseInlineMapLoading = false;
            this.profileCourseInlineMapError = 'NAVER 지도 화면을 생성하지 못했어요.';
            await this.service.render();
        }
    }

    private resetProfileCourseInlineMapObjects() {
        this.profileCourseInlineNaverMarkers.forEach((marker: any) => marker && marker.setMap && marker.setMap(null));
        this.profileCourseInlineNaverMarkers = [];
        if (this.profileCourseInlineNaverRouteLine && this.profileCourseInlineNaverRouteLine.setMap) {
            this.profileCourseInlineNaverRouteLine.setMap(null);
        }
        this.profileCourseInlineNaverRouteLine = null;
        this.profileCourseInlineNaverMap = null;
        this.profileCourseInlineNaverMapElement = null;
    }

    private async resolveProfileCourseStopCoordinate(google: any, stop: any, course: any = this.selectedMyProfileCourse) {
        let address = String(stop && stop.address || '').trim();
        let region = this.profileCourseRegion(course);
        let query = address || [region, stop && stop.name].filter((value: string) => !!String(value || '').trim()).join(' ');
        if (google && google.maps && typeof google.maps.Geocoder === 'function' && query) {
            let coordinate: any = await new Promise((resolve) => {
                let geocoder = new google.maps.Geocoder();
                geocoder.geocode({ address: query }, (results: any[], status: string) => {
                    let location = status === 'OK' && results && results[0] && results[0].geometry
                        ? results[0].geometry.location
                        : null;
                    let lat = location && typeof location.lat === 'function' ? this.safeNumber(location.lat()) : null;
                    let lng = location && typeof location.lng === 'function' ? this.safeNumber(location.lng()) : null;
                    resolve(lat !== null && lng !== null ? { lat, lng } : null);
                });
            });
            if (coordinate) return coordinate;
        }

        try {
            let response: any = await wiz.call('search_course_places', {
                keyword: String(stop && stop.name || '').trim(),
                region,
                limit: 5
            });
            let rows = response && response.code === 200 && response.data && Array.isArray(response.data.rows)
                ? response.data.rows
                : [];
            let nameKey = String(stop && stop.name || '').replace(/[^0-9a-zA-Z가-힣]/g, '').toLowerCase();
            let row = rows.find((item: any) => {
                let itemKey = String(item && (item.name || item.title) || '').replace(/[^0-9a-zA-Z가-힣]/g, '').toLowerCase();
                return !!nameKey && !!itemKey && (itemKey === nameKey || itemKey.indexOf(nameKey) > -1 || nameKey.indexOf(itemKey) > -1);
            }) || rows[0];
            let lat = this.safeNumber(row && (row.lat !== undefined ? row.lat : row.latitude));
            let lng = this.safeNumber(row && (row.lng !== undefined ? row.lng : row.longitude));
            if (lat !== null && lng !== null) return { lat, lng };
        } catch (e) { }
        return null;
    }

    private async loadProfileCourseStaticMap(stops: any[]) {
        let points = (stops || []).map((stop: any, index: number) => ({
            lat: Number(stop.lat),
            lng: Number(stop.lng),
            label: String(index + 1)
        }));
        try {
            let body = new URLSearchParams();
            body.set('kind', 'course_static_map');
            body.set('points', JSON.stringify(points));
            let response = await fetch('/api/directions/segment', {
                method: 'POST',
                body
            });
            let payload = response.ok ? await response.json() : null;
            this.profileCourseStaticMapUrl = payload && payload.code === 200 && payload.data
                ? String(payload.data.image_data_url || '')
                : '';
        } catch (e) {
            this.profileCourseStaticMapUrl = '';
        }
    }

    private async renderProfileCourseMap() {
        let allStops = this.profileCourseStops(this.selectedMyProfileCourse);
        if (!allStops.length) {
            this.profileCourseMapLoading = false;
            this.profileCourseMapError = '지도에 표시할 여행 장소가 아직 없어요.';
            await this.service.render();
            return;
        }

        let google = await this.loadNaverMapsScript();
        if (!this.profileCourseMapOpen) return;
        if (!this.isNaverMapsReady(google)) {
            this.profileCourseMapLoading = false;
            this.profileCourseMapError = 'NAVER 지도를 불러오지 못했어요. 잠시 후 다시 시도해주세요.';
            await this.service.render();
            return;
        }

        await this.resolveProfileCourseMapStops(google, allStops);
        if (!this.profileCourseMapOpen) return;
        let stops = this.profileCourseMapStops();
        if (!stops.length) {
            this.profileCourseMapLoading = false;
            this.profileCourseMapError = 'NAVER 지도에서 장소 위치를 찾지 못했어요.';
            await this.service.render();
            return;
        }

        this.profileCourseDynamicMapEnabled = true;
        this.profileCourseMapError = '';
        await this.service.render();
        if (!this.profileCourseMapOpen) return;
        await new Promise((resolve) => {
            let root: any = typeof window !== 'undefined' ? window : null;
            let frame = root && root.requestAnimationFrame
                ? root.requestAnimationFrame.bind(root)
                : (callback: any) => setTimeout(callback, 16);
            frame(() => frame(resolve));
        });
        if (!this.profileCourseMapOpen) return;

        let element: any = document.querySelector('.access-shell .profile-course-route-map');
        if (!element || !element.offsetWidth || !element.offsetHeight) {
            this.profileCourseMapLoading = false;
            this.profileCourseMapError = '지도 영역을 준비하지 못했어요.';
            await this.service.render();
            return;
        }

        this.profileCourseNaverMarkers.forEach((marker: any) => marker && marker.setMap && marker.setMap(null));
        this.profileCourseNaverMarkers = [];
        this.profileCourseNaverInfoWindows.forEach((info: any) => info && info.close && info.close());
        this.profileCourseNaverInfoWindows = [];
        if (this.profileCourseNaverRouteLine && this.profileCourseNaverRouteLine.setMap) {
            this.profileCourseNaverRouteLine.setMap(null);
        }
        this.profileCourseNaverRouteLine = null;
        this.profileCourseNaverMap = null;
        this.profileCourseNaverMapElement = null;
        while (element.firstChild) element.removeChild(element.firstChild);

        let points = stops.map((stop: any) => ({ lat: Number(stop.lat), lng: Number(stop.lng) }));
        try {
            this.profileCourseNaverMap = new google.maps.Map(element, {
                center: points[0],
                zoom: points.length > 1 ? 13 : 15,
                clickableIcons: false,
                disableDefaultUI: true,
                gestureHandling: 'greedy',
                keyboardShortcuts: true,
                zoomControl: true,
                streetViewControl: false,
                mapTypeControl: false,
                fullscreenControl: false
            });
            this.profileCourseNaverMapElement = element;

            this.profileCourseNaverMarkers = points.map((position: any, index: number) => {
                let stop = stops[index];
                let marker = new google.maps.Marker({
                    map: this.profileCourseNaverMap,
                    position,
                    title: stop.name || `${index + 1}번 장소`,
                    label: {
                        text: String(index + 1),
                        color: '#ffffff',
                        fontSize: '11px',
                        fontWeight: '800'
                    },
                    icon: {
                        path: google.maps.SymbolPath.CIRCLE,
                        scale: 12,
                        fillColor: '#F20D19',
                        fillOpacity: 1,
                        strokeColor: '#ffffff',
                        strokeWeight: 3
                    }
                });
                if (marker.addListener) marker.addListener('click', () => this.openProfileCourseStopDetail(stop));
                return marker;
            });

            let fitProfileCourseMap = () => {
                if (!this.profileCourseNaverMap || !this.profileCourseMapOpen) return;
                if (points.length > 1) {
                    let bounds = new google.maps.LatLngBounds();
                    points.forEach((point: any) => bounds.extend(point));
                    this.profileCourseNaverMap.fitBounds(bounds, 48);
                } else {
                    this.profileCourseNaverMap.setCenter(points[0]);
                    this.profileCourseNaverMap.setZoom(15);
                }
            };

            if (points.length > 1) {
                this.profileCourseNaverRouteLine = new google.maps.Polyline({
                    map: this.profileCourseNaverMap,
                    path: points,
                    strokeColor: '#F20D19',
                    strokeOpacity: 0.9,
                    strokeWeight: 5,
                    geodesic: false
                });
            }
            fitProfileCourseMap();
            setTimeout(() => {
                if (!this.profileCourseNaverMap || !this.profileCourseMapOpen) return;
                if (google.maps.event && google.maps.event.trigger) {
                    google.maps.event.trigger(this.profileCourseNaverMap, 'resize');
                }
                fitProfileCourseMap();
            }, 120);
            this.profileCourseMapLoading = false;
            this.profileCourseMapError = '';
            await this.service.render();
        } catch (e) {
            this.profileCourseNaverMap = null;
            this.profileCourseNaverMapElement = null;
            this.profileCourseMapLoading = false;
            this.profileCourseMapError = 'NAVER 지도 화면을 생성하지 못했어요.';
            await this.service.render();
        }
    }

    public async openMyCourseDeleteConfirm() {
        if (!this.selectedMyProfileCourse || this.myCourseDeleteSubmittingId) return;
        this.myCourseDeleteConfirmOpen = true;
        await this.service.render();
    }

    public async closeMyCourseDeleteConfirm() {
        if (this.myCourseDeleteSubmittingId) return;
        this.myCourseDeleteConfirmOpen = false;
        await this.service.render();
    }

    public async deleteMyCourse() {
        let course = this.selectedMyProfileCourse;
        let courseId = String((course && course.id) || '').trim();
        if (!courseId || this.myCourseDeleteSubmittingId) return;
        if (!course.mine && course.source !== 'mine') {
            await this.showSaveHint('내가 만든 코스만 삭제할 수 있어요.');
            return;
        }
        this.myCourseDeleteConfirmOpen = false;
        this.myCourseDeleteSubmittingId = courseId;
        await this.service.render();
        let response: any = null;
        try {
            response = await wiz.call('delete_builder_course', { course_id: courseId });
        } catch (e) { }

        if (!response || response.code !== 200) {
            this.myCourseDeleteSubmittingId = '';
            let message = response && response.data && response.data.message
                ? response.data.message
                : '코스를 삭제하지 못했습니다. 다시 시도해주세요.';
            await this.showSaveHint(message);
            return;
        }

        this.courses = this.courses.filter((item: any) => String((item && item.id) || '') !== courseId);
        this.recommendations = this.recommendations.filter((item: any) => String((item && item.id) || '') !== courseId);
        this.ownedCourseRows = this.ownedCourseRows.filter((item: any) => String((item && item.id) || '') !== courseId);
        this.mapExecutableCourses = this.mapExecutableCourses.filter((item: any) => String((item && item.id) || '') !== courseId);
        this.savedCourseIds = this.savedCourseIds.filter((id: any) => String(id || '') !== courseId);
        if (this.activeExecutionCourseId === courseId) this.activeExecutionCourseId = '';
        if (this.stagedExecutionCourseId === courseId) this.stagedExecutionCourseId = '';
        this.applySavedCourseIds(response.data && response.data.course_ids ? response.data.course_ids : []);
        this.applySavedCourseRows(response.data && response.data.courses ? response.data.courses : []);
        this.myCourseDeleteSubmittingId = '';
        this.myCourseDetailOpen = false;
        this.profileCourseDetailPhotoIndex = 0;
        this.profileCourseDetailLoading = false;
        this.selectedMyProfileCourse = null;
        await this.showSaveHint('내 코스에서 삭제했어요.');
    }

    public async toggleMyCourseArchive(course: any = null, event: any = null) {
        if (event && event.stopPropagation) event.stopPropagation();
        course = course || this.selectedMyProfileCourse;
        let courseId = String((course && course.id) || '').trim();
        if (!courseId || this.myCourseArchiveSubmittingId) return;
        if (!course.mine && course.source !== 'mine') {
            await this.showSaveHint('내가 만든 코스만 보관할 수 있어요.');
            return;
        }

        let archived = !course.archived;
        this.myCourseArchiveSubmittingId = courseId;
        await this.service.render();
        let response: any = null;
        try {
            response = await wiz.call('update_builder_course', {
                course_id: courseId,
                data: JSON.stringify(this.myCourseArchivePayload(course, archived))
            });
        } catch (e) { }

        if (!response || response.code !== 200) {
            this.myCourseArchiveSubmittingId = '';
            let message = response && response.data && response.data.message
                ? response.data.message
                : '코스 보관 상태를 변경하지 못했습니다.';
            await this.showSaveHint(message);
            return;
        }

        let data = response.data || {};
        if (Array.isArray(data.owned_courses)) {
            this.applyOwnedCourseRows(data.owned_courses);
        } else if (data.row) {
            this.rememberOwnedCourseRow(data.row);
            this.applyOwnedCourseRows(this.ownedCourseRows);
        }
        if (Array.isArray(data.public_courses)) this.applyPublicCourseRows(data.public_courses);
        this.myCourseArchiveSubmittingId = '';
        if (archived && this.selectedMyProfileCourse && String(this.selectedMyProfileCourse.id || '') === courseId) {
            this.myCourseDetailOpen = false;
            this.selectedMyProfileCourse = null;
        }
        await this.showSaveHint(archived ? '코스를 보관함으로 옮겼어요.' : '코스를 내 코스로 되돌렸어요.');
    }

    private myCourseArchivePayload(course: any, archived: boolean) {
        let archiveTag = '__gachi_archived__';
        let tags = (Array.isArray(course && course.tags) ? course.tags : [])
            .filter((tag: any) => String(tag || '').trim() && tag !== archiveTag);
        if (archived) tags.push(archiveTag);
        let places = (Array.isArray(course && course.places) ? course.places : []).map((raw: any, index: number) => {
            let place = raw && typeof raw === 'object' ? raw : {};
            let stored = this.profileCourseStoredPlaceMeta(place);
            let day = Math.max(1, Number(stored.day || place.day || place.day_index || 1));
            let meta = {
                ...stored,
                item_type: stored.item_type || place.item_type || 'place',
                day,
                day_label: stored.day_label || place.day_label || `${day}일차`,
                date: stored.date || place.date || '',
                name: place.name || place.title || '',
                area: place.area || '',
                address: place.address || '',
                category: place.category || '',
                image: place.image || '',
                latitude: place.latitude || place.lat,
                longitude: place.longitude || place.lng,
                memo: Object.prototype.hasOwnProperty.call(stored, 'memo') ? stored.memo : String(place.memo || ''),
                source_order: index + 1
            };
            return {
                ...place,
                place_id: place.place_id || place.placeId || place.id,
                day,
                day_label: meta.day_label,
                date: meta.date,
                order_index: index + 1,
                memo: `__gachi_item__${JSON.stringify(meta)}`
            };
        });
        let duration = String(course && (course.duration_value || course.duration) || '').trim();
        return {
            title: this.profileCourseTitle(course),
            region: this.profileCourseRegion(course),
            category: String(course && course.category || '여행'),
            description: String(course && (course.description || course.summary) || ''),
            cover_image: this.profileCourseCover(course),
            image: this.profileCourseCover(course),
            duration_type: String(course && course.duration_type || (duration.indexOf('박') > -1 ? 'overnight' : 'hours')),
            duration_value: duration || '1',
            companion_type: String(course && course.companion_type || ''),
            is_public: !!(course && course.is_public),
            is_featured: !!(course && course.is_featured),
            is_hidden: archived ? true : !(course && course.is_public),
            places,
            tags
        };
    }

    private async loadProfileCourseDetail(course: any) {
        let courseId = String((course && course.id) || '').trim();
        if (!courseId) return;
        let detail: any = null;
        try {
            let response = await fetch(`/api/courses/${encodeURIComponent(courseId)}`, { method: 'GET' });
            if (response.ok) {
                let payload = await response.json();
                let data = payload && payload.data ? payload.data : payload;
                detail = data && data.row ? data.row : null;
            }
        } catch (e) { }

        if (!detail && this.isLoggedIn()) {
            try {
                let response: any = await wiz.call('course_execution', { course_id: courseId });
                if (response && response.code === 200 && response.data && response.data.execution) {
                    let execution = response.data.execution;
                    detail = {
                        ...(execution.course || {}),
                        places: Array.isArray(execution.places) ? execution.places : []
                    };
                }
            } catch (e) { }
        }

        if (!detail || !this.selectedMyProfileCourse || String(this.selectedMyProfileCourse.id || '') !== courseId) return;
        let places = Array.isArray(detail.places) && detail.places.length > 0
            ? detail.places
            : (Array.isArray(course.places) ? course.places : []);
        this.selectedMyProfileCourse = {
            ...course,
            ...detail,
            id: courseId,
            location: detail.region || detail.location || course.location || '',
            summary: detail.description || detail.summary || course.summary || '',
            duration: detail.duration || course.duration || '',
            image: detail.cover_image || detail.image || course.image || '',
            cover_image: detail.cover_image || detail.image || course.cover_image || course.image || '',
            places,
            route: detail.route || course.route || {}
        };
    }

    private async loadProfileCourseStories(course: any) {
        let courseId = String((course && course.id) || '').trim();
        if (!courseId) return;
        try {
            let response: any = await wiz.call('saved_courses', {
                community_action: 'course_story',
                course_id: courseId
            });
            if (!response || response.code !== 200 || !response.data || !Array.isArray(response.data.posts)) return;
            let posts = response.data.posts.map((post: any) => this.profileCourseStoryFromApi(post));
            let ids: any = {};
            posts.forEach((post: any) => {
                if (post && post.id) ids[post.id] = true;
            });
            this.myProfilePosts = [
                ...posts,
                ...this.myProfilePosts.filter((post: any) => !ids[post && post.id])
            ];
        } catch (e) { }
    }

    private profileCourseStoryFromApi(post: any) {
        post = post || {};
        let photo = String(post.photo || '').trim();
        let caption = String(post.summary || post.caption || '').trim();
        return {
            id: post.id || `course-story-${Date.now()}`,
            title: post.title || '여행 사진 기록',
            location: post.destination || post.location || '나의 여행',
            caption,
            photo,
            photos: photo ? [{ src: photo, caption }] : [],
            tags: Array.isArray(post.tags) ? post.tags : [],
            icon: 'fa-camera',
            tone: 'feed-rose',
            likes: Number(post.likes || 0),
            regrams: 0,
            regrammed: false,
            archived: !!post.archived,
            courseId: post.place || post.courseId || '',
            author: post.author || '여행자',
            mine: !!post.owned,
            serverCourseStory: true
        };
    }

    public async openCoursePhotoComposer() {
        if (!this.isLoggedIn()) {
            this.goLogin();
            return;
        }
        let course = this.selectedMyProfileCourse;
        if (!course) return;
        this.feedComposerReturnCourse = course;
        this.resetNewFeedPost();
        this.newFeedPost.courseId = String(course.id || '');
        this.newFeedPost.courseTitle = this.profileCourseTitle(course);
        this.newFeedPost.location = this.profileCourseRegion(course);
        this.myCourseDetailOpen = false;
        this.myFeedComposerOpen = true;
        await this.service.render();
    }

    public async moveCourseCertShot(step: number) {
        let shots = this.selectedCourseCertShots();
        if (shots.length === 0) return;
        this.profileCourseDetailPhotoIndex = Math.min(Math.max(this.profileCourseDetailPhotoIndex + step, 0), shots.length - 1);
        await this.service.render();
    }

    public async selectCourseCertShot(index: number) {
        let shots = this.selectedCourseCertShots();
        if (index < 0 || index >= shots.length) return;
        this.profileCourseDetailPhotoIndex = index;
        await this.service.render();
    }

    private isPostRelatedToCourse(post: any, course: any) {
        if (!post || !course) return false;
        if (post.courseId && course.id && post.courseId === course.id) return true;
        let courseLocation = String(course.location || '').trim();
        let postLocation = String(post.location || '').trim();
        if (courseLocation && postLocation && (courseLocation.indexOf(postLocation) > -1 || postLocation.indexOf(courseLocation) > -1)) return true;

        let courseWords = `${course.title || ''} ${course.location || ''}`.split(/\s+/).filter((word: string) => word.length >= 2);
        let postWords = `${post.title || ''} ${post.location || ''} ${post.caption || ''}`.split(/\s+/).filter((word: string) => word.length >= 2);
        return courseWords.some((word: string) => postWords.indexOf(word) > -1);
    }

    private findProfileCourseForLocation(location: string) {
        let normalized = String(location || '').trim();
        if (!normalized) return null;
        return this.myProfileCourses().find((course: any) => {
            let courseLocation = String((course && course.location) || '').trim();
            return courseLocation && (courseLocation.indexOf(normalized) > -1 || normalized.indexOf(courseLocation) > -1);
        }) || null;
    }

    public async openMyFeedDetail(post: any) {
        if (!post) return;
        this.selectedMyProfilePost = post;
        this.profileFeedDetailPhotoIndex = 0;
        this.myFeedDetailOpen = true;
        this.myResumeOpen = false;
        this.myFeedComposerOpen = false;
        this.myArchiveOpen = false;
        this.myActivityOpen = false;
        await this.service.render();
    }

    public async closeMyFeedDetail() {
        this.myFeedDetailOpen = false;
        this.profileFeedDetailPhotoIndex = 0;
        this.selectedMyProfilePost = null;
        await this.service.render();
    }

    public selectedFeedDetailPhotos() {
        let post = this.selectedMyProfilePost || {};
        if (Array.isArray(post.photos) && post.photos.length > 0) return post.photos;
        if (post.photo) return [{ src: post.photo, caption: post.caption || '' }];
        return [];
    }

    public selectedFeedDetailPhoto() {
        let photos = this.selectedFeedDetailPhotos();
        if (photos.length === 0) return {};
        let index = Math.min(Math.max(this.profileFeedDetailPhotoIndex, 0), photos.length - 1);
        return photos[index] || {};
    }

    public selectedFeedDetailCaption() {
        let photo: any = this.selectedFeedDetailPhoto();
        if (photo.caption) return String(photo.caption);
        let post = this.selectedMyProfilePost || {};
        return String(post.caption || '');
    }

    public selectedFeedDetailTags() {
        let post = this.selectedMyProfilePost || {};
        if (Array.isArray(post.tags)) return post.tags;
        return String(post.tags || '')
            .split(',')
            .map((tag: string) => tag.trim())
            .filter((tag: string) => !!tag);
    }

    public async moveFeedDetailPhoto(step: number) {
        let photos = this.selectedFeedDetailPhotos();
        if (photos.length === 0) return;
        this.profileFeedDetailPhotoIndex = Math.min(Math.max(this.profileFeedDetailPhotoIndex + step, 0), photos.length - 1);
        await this.service.render();
    }

    public async selectFeedDetailPhoto(index: number) {
        let photos = this.selectedFeedDetailPhotos();
        if (index < 0 || index >= photos.length) return;
        this.profileFeedDetailPhotoIndex = index;
        await this.service.render();
    }

    public async toggleProfileRegram(post: any, event?: any) {
        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }
        if (!post) return;

        post.regrammed = !post.regrammed;
        post.regrams = Math.max(0, Number(post.regrams || 0) + (post.regrammed ? 1 : -1));
        try {
            await wiz.call('profile_activity', {
                action: 'record',
                event_type: 'repost',
                target_type: 'feed',
                target_id: post.id,
                title: post.title || '여행 피드',
                active: post.regrammed
            });
        } catch (e) { }
        await this.service.render();
    }

    public async openFeedComposer() {
        if (!this.isLoggedIn()) {
            this.goLogin();
            return;
        }

        this.myProfileOpen = true;
        this.resetMyProfileSubscreens();
        this.feedComposerReturnCourse = null;
        this.resetNewFeedPost();
        this.myFeedComposerOpen = true;
        await this.service.render();
    }

    public async closeFeedComposer() {
        let returnCourse = this.feedComposerReturnCourse;
        this.myFeedComposerOpen = false;
        this.feedComposerReturnCourse = null;
        if (returnCourse) {
            this.selectedMyProfileCourse = returnCourse;
            this.myCourseDetailOpen = true;
        }
        await this.service.render();
    }

    public selectedFeedPhotos() {
        return Array.isArray(this.newFeedPost.photos) ? this.newFeedPost.photos : [];
    }

    public currentFeedPhoto() {
        let photos = this.selectedFeedPhotos();
        if (photos.length === 0) return {};
        let index = Math.min(Math.max(this.newFeedPhotoIndex, 0), photos.length - 1);
        return photos[index] || {};
    }

    public currentFeedPhotoCaption() {
        let photo: any = this.currentFeedPhoto();
        return String(photo.caption || '');
    }

    public async setCurrentFeedPhotoCaption(value: any) {
        let photos = this.selectedFeedPhotos();
        if (photos.length === 0) return;
        let index = Math.min(Math.max(this.newFeedPhotoIndex, 0), photos.length - 1);
        photos[index].caption = String(value || '');
    }

    public async selectNewFeedPhoto(index: number) {
        let photos = this.selectedFeedPhotos();
        if (index < 0 || index >= photos.length) return;
        this.newFeedPhotoIndex = index;
        await this.service.render();
    }

    public async moveNewFeedPhoto(step: number) {
        let photos = this.selectedFeedPhotos();
        if (photos.length === 0) return;
        this.newFeedPhotoIndex = Math.min(Math.max(this.newFeedPhotoIndex + step, 0), photos.length - 1);
        await this.service.render();
    }

    public async goFeedCaptionStep() {
        if (this.selectedFeedPhotos().length === 0) {
            await this.showSaveHint('먼저 피드 사진을 선택해주세요.');
            return;
        }

        this.newFeedStep = 'edit';
        this.newFeedPhotoIndex = 0;
        await this.service.render();
    }

    public async backToFeedPhotoSelect() {
        this.newFeedStep = 'select';
        await this.service.render();
    }

    public async removeNewFeedPhoto(index: number, event?: any) {
        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }
        let photos = this.selectedFeedPhotos();
        if (index < 0 || index >= photos.length) return;
        photos.splice(index, 1);
        this.newFeedPhotoIndex = Math.min(this.newFeedPhotoIndex, Math.max(photos.length - 1, 0));
        if (photos.length === 0) {
            this.newFeedPost.photo = '';
            this.newFeedStep = 'select';
        } else {
            this.newFeedPost.photo = photos[0].src;
        }
        await this.service.render();
    }

    public async handleFeedPhotoUpload(event: any) {
        let input = event && event.target ? event.target : null;
        let files = input && input.files ? Array.prototype.slice.call(input.files) : [];
        if (files.length === 0) return;

        let imageFiles = files.filter((file: any) => file && (!file.type || /^image\//.test(file.type)) && Number(file.size || 0) <= 15 * 1024 * 1024);
        if (imageFiles.length !== files.length) {
            await this.showSaveHint('15MB 이하 이미지 파일만 등록할 수 있어요.');
        }
        if (imageFiles.length === 0) {
            if (input) input.value = '';
            return;
        }

        if (!Array.isArray(this.newFeedPost.photos)) this.newFeedPost.photos = [];
        let available = Math.max(0, 8 - this.newFeedPost.photos.length);
        if (available === 0) {
            await this.showSaveHint('사진은 한 번에 최대 8장까지 올릴 수 있어요.');
            if (input) input.value = '';
            return;
        }
        if (imageFiles.length > available) {
            imageFiles = imageFiles.slice(0, available);
            await this.showSaveHint('사진은 한 번에 최대 8장까지 올릴 수 있어요.');
        }

        let startIndex = this.selectedFeedPhotos().length;
        let failed = 0;
        for (let file of imageFiles) {
            try {
                let src = await this.prepareFeedPhoto(file);
                this.newFeedPost.photos.push({ src, caption: '', name: file.name || '여행 사진' });
                if (!this.newFeedPost.photo) this.newFeedPost.photo = src;
            } catch (e) {
                failed += 1;
            }
        }
        this.newFeedPhotoIndex = Math.min(startIndex, Math.max(0, this.selectedFeedPhotos().length - 1));
        await this.service.render();
        if (failed > 0) await this.showSaveHint(`${failed}장의 사진을 불러오지 못했어요.`);

        if (input) input.value = '';
    }

    private prepareFeedPhoto(file: any): Promise<string> {
        return new Promise((resolve, reject) => {
            let reader = new FileReader();
            reader.onerror = () => reject(new Error('read_failed'));
            reader.onload = () => {
                let image = new Image();
                image.onerror = () => reject(new Error('decode_failed'));
                image.onload = () => {
                    let originalWidth = Math.max(1, Number(image.naturalWidth || image.width || 1));
                    let originalHeight = Math.max(1, Number(image.naturalHeight || image.height || 1));
                    let scale = Math.min(1, 1280 / Math.max(originalWidth, originalHeight));
                    let quality = 0.82;
                    let output = '';
                    for (let attempt = 0; attempt < 10; attempt += 1) {
                        let canvas = document.createElement('canvas');
                        canvas.width = Math.max(1, Math.round(originalWidth * scale));
                        canvas.height = Math.max(1, Math.round(originalHeight * scale));
                        let context = canvas.getContext('2d');
                        if (!context) {
                            reject(new Error('canvas_failed'));
                            return;
                        }
                        context.fillStyle = '#ffffff';
                        context.fillRect(0, 0, canvas.width, canvas.height);
                        context.drawImage(image, 0, 0, canvas.width, canvas.height);
                        output = canvas.toDataURL('image/jpeg', quality);
                        if (output.length <= 48000) break;
                        scale *= 0.82;
                        quality = Math.max(0.46, quality - 0.06);
                    }
                    if (!output || output.length > 62000) {
                        reject(new Error('image_too_large'));
                        return;
                    }
                    resolve(output);
                };
                image.src = String(reader.result || '');
            };
            reader.readAsDataURL(file);
        });
    }

    public async publishFeedPost() {
        let photos = this.selectedFeedPhotos();
        if (photos.length === 0) {
            await this.showSaveHint('피드 사진을 선택해주세요.');
            return;
        }

        let location = String(this.newFeedPost.location || '').trim() || '나의 여행';
        let tags = String(this.newFeedPost.tags || '')
            .split(',')
            .map((tag: string) => tag.trim())
            .filter((tag: string) => !!tag);
        let title = location === '나의 여행' ? '새 여행 피드' : `${location} 여행 피드`;
        let explicitCourseId = String(this.newFeedPost.courseId || '').trim();
        let relatedCourse = explicitCourseId
            ? this.myProfileCourses().find((course: any) => String((course && course.id) || '') === explicitCourseId) || this.feedComposerReturnCourse
            : (this.findProfileCourseForLocation(location) || this.myProfileCourses()[0] || null);
        let courseId = explicitCourseId || String((relatedCourse && relatedCourse.id) || '').trim();
        let createdAt = Date.now();
        let storyPosts = photos.map((photo: any, index: number) => {
            let photoCaption = String((photo && photo.caption) || '').trim() || '새 여행 사진을 공유했어요.';
            return {
                id: `course-story-${createdAt}-${index}`,
                title: this.newFeedPost.courseTitle || title,
                location,
                caption: photoCaption,
                photo: photo.src,
                photos: [{ src: photo.src, caption: photoCaption }],
                photoName: photo.name || `여행 사진 ${index + 1}`,
                tags,
                icon: 'fa-camera',
                tone: 'feed-rose',
                likes: 0,
                regrams: 0,
                regrammed: false,
                archived: false,
                courseId,
                author: this.myDisplayName(),
                mine: true
            };
        });
        this.myProfilePosts = [...storyPosts, ...this.myProfilePosts];
        let failed = 0;
        if (courseId) {
            let results = await Promise.all(storyPosts.map(async (post: any) => {
                try {
                    let response: any = await wiz.call('save_community_post', {
                        post: JSON.stringify({
                            id: post.id,
                            kind: 'course_story',
                            topic: 'travel_log',
                            title: post.title,
                            summary: post.caption,
                            category: 'course_story',
                            destination: post.location,
                            place: post.courseId,
                            photo: post.photo,
                            photoName: post.photoName,
                            author: post.author,
                            tags: post.tags
                        })
                    });
                    return !!(response && response.code === 200);
                } catch (e) {
                    return false;
                }
            }));
            failed = results.filter((saved: boolean) => !saved).length;
        }
        let returnCourse = this.feedComposerReturnCourse;
        this.resetNewFeedPost();
        this.myFeedComposerOpen = false;
        this.feedComposerReturnCourse = null;
        if (returnCourse) {
            this.selectedMyProfileCourse = returnCourse;
            this.profileCourseDetailLoading = false;
            this.myCourseDetailOpen = true;
        } else {
            this.myProfileTab = 'myCourses';
        }
        if (failed > 0) {
            await this.showSaveHint(`${storyPosts.length - failed}장은 저장했고, ${failed}장은 현재 화면에만 남겼어요.`);
        } else {
            await this.showSaveHint('코스에 여행 사진과 글을 올렸어요.');
        }
    }

    public async openMyArchive() {
        if (!this.isLoggedIn()) {
            this.goLogin();
            return;
        }

        this.myProfileOpen = true;
        this.resetMyProfileSubscreens();
        this.myArchiveOpen = true;
        await this.loadMyArchivePosts();
        await this.service.render();
    }

    public async closeMyArchive() {
        this.myArchiveOpen = false;
        this.myProfileOpen = false;
        await this.service.render();
    }

    public archivedMyProfilePosts() {
        return this.myProfilePosts.filter((post: any) => !!post.archived);
    }

    public async toggleMyPostArchive(post: any) {
        if (!post) return;
        let archived = !post.archived;
        try {
            const response: any = await wiz.call('profile_archive', {
                action: 'toggle_feed',
                post_id: post.id,
                archived
            });
            if (!response || response.code !== 200) {
                await this.showSaveHint(response && response.data && response.data.message ? response.data.message : '피드 보관 상태를 저장하지 못했어요.');
                return;
            }
            post.archived = archived;
            await this.showSaveHint(archived ? '피드를 계정 보관함으로 옮겼어요.' : '피드를 프로필로 되돌렸어요.');
        } catch (e) {
            await this.showSaveHint('피드 보관 상태를 저장하지 못했어요.');
        }
    }

    private async loadMyArchivePosts() {
        try {
            const response: any = await wiz.call('profile_archive', { action: 'load' });
            if (!response || response.code !== 200 || !response.data || !Array.isArray(response.data.posts)) return false;
            let posts = response.data.posts.map((post: any) => this.profileCourseStoryFromApi(post));
            let ids: any = {};
            posts.forEach((post: any) => { if (post && post.id) ids[post.id] = true; });
            this.myProfilePosts = [...posts, ...this.myProfilePosts.filter((post: any) => !ids[post && post.id])];
            return true;
        } catch (e) { }
        return false;
    }

    public async openMyActivity() {
        if (!this.isLoggedIn()) {
            this.goLogin();
            return;
        }

        this.myProfileOpen = true;
        this.resetMyProfileSubscreens();
        this.myActivityOpen = true;
        await this.loadMyActivity();
        await this.service.render();
    }

    public async closeMyActivity() {
        this.myActivityOpen = false;
        this.myProfileOpen = false;
        await this.service.render();
    }

    public async selectMyActivityFilter(key: string) {
        if (!this.myActivityGroups || !Array.isArray(this.myActivityGroups[key])) return;
        this.myActivityFilter = key;
        await this.service.render();
    }

    public visibleMyActivityRows() {
        let rows = this.myActivityGroups && Array.isArray(this.myActivityGroups[this.myActivityFilter])
            ? this.myActivityGroups[this.myActivityFilter]
            : [];
        return rows;
    }

    private async loadMyActivity() {
        try {
            const response: any = await wiz.call('profile_activity', { action: 'load' });
            if (!response || response.code !== 200 || !response.data) return false;
            let groups = response.data.groups || {};
            this.myActivityGroups = {
                likes: Array.isArray(groups.likes) ? groups.likes : [],
                comments: Array.isArray(groups.comments) ? groups.comments : [],
                reposts: Array.isArray(groups.reposts) ? groups.reposts : [],
                tags: Array.isArray(groups.tags) ? groups.tags : []
            };
            let counts = response.data.counts || {};
            this.myActivityItems = this.myActivityItems.map((item: any) => ({
                ...item,
                count: Math.max(0, Number(counts[item.key] || 0))
            }));
            if (!Array.isArray(this.myActivityGroups[this.myActivityFilter])) this.myActivityFilter = 'likes';
            return true;
        } catch (e) { }
        return false;
    }

    public async openAccountCenter() {
        await this.openSettingsDetail('account');
    }

    public async openProfileInsights() {
        await this.openSettingsDetail('insights');
    }

    public async openProfessionalSettings() {
        await this.openSettingsDetail('professional');
    }

    public async openProfileVerification() {
        await this.openSettingsDetail('verification');
    }

    public async openBillingSettings() {
        await this.openSettingsDetail('billing');
    }

    private async openSettingsDetail(detail: string) {
        if (!this.isLoggedIn()) {
            this.goLogin();
            return;
        }
        this.myProfileOpen = true;
        this.resetMyProfileSubscreens();
        this.mySettingsDetail = detail;
        this.settingsDetailLoading = true;
        await this.service.render();
        await this.loadAccountSettings();
        if (detail === 'insights') await this.loadProfileInsights();
        if (detail === 'verification') await this.loadTravelIdentityStatus();
        this.settingsDetailLoading = false;
        await this.service.render();
    }

    public async closeSettingsDetail() {
        this.mySettingsDetail = '';
        this.myProfileOpen = false;
        this.settingsDetailLoading = false;
        this.settingsDetailSaving = false;
        await this.service.render();
    }

    private applyAccountSettings(data: any) {
        data = data || {};
        this.accountCenterProfile = {
            ...this.accountCenterProfile,
            ...(data.profile || {})
        };
        this.accountPreferences = {
            ...this.accountPreferences,
            ...(data.account || {})
        };
        this.professionalSettings = {
            ...this.professionalSettings,
            ...(data.professional || {})
        };
        this.billingSettings = {
            ...this.billingSettings,
            ...(data.billing || {})
        };
        if (data.identity) this.applyTravelIdentityProfile(data.identity);
    }

    private async loadAccountSettings() {
        try {
            const response: any = await wiz.call('account_center', { action: 'load' });
            if (response && response.code === 200 && response.data && response.data.settings) {
                this.applyAccountSettings(response.data.settings);
                return true;
            }
        } catch (e) { }
        return false;
    }

    public async saveAccountCenter() {
        if (this.settingsDetailSaving) return;
        this.settingsDetailSaving = true;
        await this.service.render();
        try {
            const response: any = await wiz.call('account_center', {
                action: 'save_profile',
                nickname: this.accountCenterProfile.nickname,
                mobile: this.accountCenterProfile.mobile,
                account: JSON.stringify(this.accountPreferences || {})
            });
            if (!response || response.code !== 200) {
                await this.showSaveHint(response && response.data && response.data.message ? response.data.message : '계정 정보를 저장하지 못했어요.');
                return;
            }
            if (response.data && response.data.settings) this.applyAccountSettings(response.data.settings);
            let auth = this.service && this.service.auth ? this.service.auth : null;
            if (auth && auth.setLocalSession && response.data && response.data.session) {
                auth.setLocalSession(response.data.session, response.data.token || auth.token || '');
            }
            await this.refreshBehavioralSignals();
            await this.showSaveHint('계정 정보와 개인정보 설정을 저장했어요.');
        } catch (e) {
            await this.showSaveHint('계정 정보를 저장하지 못했어요.');
        } finally {
            this.settingsDetailSaving = false;
            await this.service.render();
        }
    }

    public async changeAccountPassword() {
        if (this.settingsDetailSaving) return;
        if (!this.accountPassword.current || !this.accountPassword.next) {
            await this.showSaveHint('현재 비밀번호와 새 비밀번호를 입력해주세요.');
            return;
        }
        if (this.accountPassword.next !== this.accountPassword.confirm) {
            await this.showSaveHint('새 비밀번호 확인이 일치하지 않아요.');
            return;
        }
        this.settingsDetailSaving = true;
        await this.service.render();
        try {
            const response: any = await wiz.call('account_center', {
                action: 'change_password',
                current_password: this.accountPassword.current,
                new_password: this.accountPassword.next
            });
            if (!response || response.code !== 200) {
                await this.showSaveHint(response && response.data && response.data.message ? response.data.message : '비밀번호를 변경하지 못했어요.');
                return;
            }
            this.accountPassword = { current: '', next: '', confirm: '' };
            await this.showSaveHint('비밀번호를 변경했어요.');
        } catch (e) {
            await this.showSaveHint('비밀번호를 변경하지 못했어요.');
        } finally {
            this.settingsDetailSaving = false;
            await this.service.render();
        }
    }

    public async saveProfessionalSettings() {
        if (this.settingsDetailSaving) return;
        this.settingsDetailSaving = true;
        await this.service.render();
        try {
            const response: any = await wiz.call('account_center', {
                action: 'save_professional',
                professional: JSON.stringify(this.professionalSettings || {})
            });
            if (!response || response.code !== 200) {
                await this.showSaveHint('계정 유형과 도구 설정을 저장하지 못했어요.');
                return;
            }
            if (response.data && response.data.settings) this.applyAccountSettings(response.data.settings);
            await this.showSaveHint('계정 유형과 도구 설정을 저장했어요.');
        } catch (e) {
            await this.showSaveHint('계정 유형과 도구 설정을 저장하지 못했어요.');
        } finally {
            this.settingsDetailSaving = false;
            await this.service.render();
        }
    }

    public async saveBillingSettings() {
        if (this.settingsDetailSaving) return;
        this.settingsDetailSaving = true;
        await this.service.render();
        try {
            const response: any = await wiz.call('account_center', {
                action: 'save_billing',
                billing: JSON.stringify(this.billingSettings || {})
            });
            if (!response || response.code !== 200) {
                await this.showSaveHint(response && response.data && response.data.message ? response.data.message : '광고 결제 정보를 저장하지 못했어요.');
                return;
            }
            if (response.data && response.data.settings) this.applyAccountSettings(response.data.settings);
            await this.showSaveHint('광고 정산 정보를 저장했어요. 실제 결제는 생성되지 않았습니다.');
        } catch (e) {
            await this.showSaveHint('광고 결제 정보를 저장하지 못했어요.');
        } finally {
            this.settingsDetailSaving = false;
            await this.service.render();
        }
    }

    private async loadProfileInsights() {
        try {
            const response: any = await wiz.call('account_center', { action: 'insights' });
            this.profileInsights = response && response.code === 200 && response.data && Array.isArray(response.data.insights)
                ? response.data.insights
                : [];
            return response && response.code === 200;
        } catch (e) { }
        this.profileInsights = [];
        return false;
    }

    public professionalAccountTypeLabel() {
        return { traveler: '여행자', creator: '크리에이터', business: '비즈니스' }[this.professionalSettings.accountType] || '여행자';
    }

    public billingSettingsLabel() {
        return this.billingSettings.paymentStatus === 'profile_saved' ? '정보 저장됨' : '미설정';
    }

    public settingsDateLabel(value: any) {
        let date = new Date(value || '');
        if (!value || isNaN(date.getTime())) return '';
        return `${date.getFullYear()}.${String(date.getMonth() + 1).padStart(2, '0')}.${String(date.getDate()).padStart(2, '0')} ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
    }

    public isAdmin() {
        let auth = this.service && this.service.auth ? this.service.auth : null;
        let session = auth && auth.session ? auth.session : {};
        return session.role === 'admin';
    }

    public goLogin() {
        this.service.href(`/login?returnTo=${encodeURIComponent('/access?tab=my')}`);
    }

    public goAdminDashboard() {
        this.service.href('/admin/dashboard');
    }

    public async logout() {
        this.behavioralSignals = { enabled: false, followed_authors: [], executed_courses: [], terms: [] };
        if (this.service && this.service.auth) this.service.auth.clearLocalSession();
        let target = `/auth/logout?returnTo=${encodeURIComponent('/access?tab=my')}`;
        let mobile: any = typeof window !== 'undefined' ? (window as any).GachiMobile : null;
        try {
            await fetch(mobile && mobile.resolveServerUrl ? mobile.resolveServerUrl(target) : target, {
                method: 'GET',
                credentials: 'include'
            });
        } catch (e) { }
        this.service.href('/access?tab=my');
    }

    public goMapLogin() {
        let returnTo = this.mapContentTab === 'zenly' ? '/access?tab=map&mapMode=together' : '/access?tab=map';
        this.service.href(`/login?returnTo=${encodeURIComponent(returnTo)}`);
    }

    public goChatLogin() {
        this.service.href(`/login?returnTo=${encodeURIComponent('/access?tab=chat')}`);
    }

    public async openAuthModal(mode: string = 'login') {
        this.authMode = mode;
        this.authModalOpen = true;
        this.authServerError = '';
        this.authErrors = {};
        await this.loadSocialProviders();
        await this.service.render();
    }

    public async closeAuthModal() {
        this.authModalOpen = false;
        this.pendingSaveCourse = null;
        this.authServerError = '';
        this.authErrors = {};
        await this.service.render();
    }

    public async switchAuthMode(mode: string) {
        this.authMode = mode;
        this.authServerError = '';
        this.authErrors = {};
        await this.service.render();
    }

    public socialAuthAvailable(provider: string) {
        return !!this.socialProviders[provider];
    }

    public socialAuthLabel(provider: string) {
        let labels: any = { kakao: '카카오', apple: 'Apple', google: 'Google' };
        let name = labels[provider] || '간편 로그인';
        return this.socialAuthAvailable(provider) ? `${name}로 계속하기` : `${name} 연결 준비 중`;
    }

    private async loadSocialProviders() {
        try {
            let response = await fetch('/auth/social/providers', {
                method: 'GET',
                credentials: 'same-origin',
                headers: { 'Accept': 'application/json' }
            });
            let result: any = await response.json();
            let payload = result && result.data ? result.data : result;
            let providers = payload && payload.providers ? payload.providers : {};
            ['kakao', 'apple', 'google'].forEach((provider: string) => {
                this.socialProviders[provider] = !!(providers[provider] && providers[provider].available);
            });
        } catch (error) {
            this.socialProviders = { kakao: false, apple: false, google: false };
        }
    }

    public startSocialAuth(provider: string) {
        if (this.authSubmitting || !this.socialAuthAvailable(provider)) return;
        let allowed = ['apple', 'google', 'kakao'];
        if (allowed.indexOf(provider) < 0) return;

        this.authSubmitting = true;
        this.authServerError = '';
        let returnTo = '/access';
        if (typeof window !== 'undefined') {
            let path = String(window.location.pathname || '/access');
            let search = String(window.location.search || '');
            returnTo = path.indexOf('/') === 0 && path.indexOf('//') !== 0 ? path + search : '/access';
        }
        let query = new URLSearchParams({
            returnTo,
            mode: this.authMode
        });
        window.location.href = `/auth/social/${provider}?${query.toString()}`;
    }

    public async submitAuth() {
        if (!this.validateAuthForm()) {
            await this.service.render();
            return;
        }

        this.authSubmitting = true;
        this.authServerError = '';
        await this.service.render();

        let payload: any = {
            email: String(this.authForm.email || '').trim(),
            password: this.authForm.password
        };
        if (this.authMode === 'register') {
            payload.name = String(this.authForm.nickname || '').trim();
            payload.password_confirm = this.authForm.passwordConfirm;
        }

        const { code, data } = await this.requestAuth(payload);
        if (code === 200) {
            let session = data && data.session ? data.session : {};
            this.service.auth.setLocalSession(session, data && data.token ? data.token : '');
            this.loadRecentPlaces();
            this.loadSavedPlaces();
            this.loadSavedCompanionPosts();
            this.loadMyProfileEdit();
            this.loadMyCourseViewMode();
            if (this.activeTab === 'my') this.myProfileOpen = true;
            if (session.role === 'admin') {
                this.service.href('/admin/dashboard');
                return;
            }
            let course = this.pendingSaveCourse;
            this.pendingSaveCourse = null;
            this.authModalOpen = false;
            this.resetAuthForm();
            if (course) {
                await this.persistCourseSave(course, true);
            } else {
                await this.restoreSavedCourses();
            }
            await this.loadChatThreads(false);
            await this.loadDirectChatRooms(true);
            this.startDirectChatRealtime();
        } else {
            this.authServerError = this.responseMessage(data, this.authMode === 'register' ? '회원가입에 실패했습니다.' : '로그인에 실패했습니다.');
        }

        this.authSubmitting = false;
        await this.service.render();
    }

    public authSubmitLabel() {
        if (this.authSubmitting) return '처리 중';
        return this.authMode === 'register' ? '가입하기' : '로그인';
    }

    public authModalTitle() {
        return this.authMode === 'register' ? '회원가입' : '로그인';
    }

    private async requestAuth(payload: any) {
        let endpoint = this.authMode === 'register' ? '/auth/signup' : '/auth/login';
        let result: any = await this.service.request.post(endpoint, payload);
        if (result && typeof result.code !== 'undefined') return result;
        return await wiz.call(this.authMode === 'register' ? 'register' : 'login', payload);
    }

    private async persistCourseSave(course: any, saved: boolean) {
        let previous = !!course.saved;
        course.saved = saved;
        await this.service.render();

        let isMine = this.companionCourseIsMine(course);
        let route = {
            ...((course && course.route && typeof course.route === 'object') ? course.route : {}),
            source: isMine ? 'mine' : 'saved',
            original_course_id: course.id,
            original_title: course.title || '',
            original_author: isMine ? this.myDisplayName() : this.companionCourseAuthor(course),
            region: course.location || '',
            schedule: course.duration || ''
        };

        let response: any = null;
        try {
            response = await wiz.call('save_course', {
                course_id: course.id,
                title: course.title,
                location: course.location,
                summary: course.summary,
                duration: course.duration,
                rating: course.rating || '',
                icon: course.icon || '',
                tone: course.tone || '',
                places_json: JSON.stringify(Array.isArray(course.places) ? course.places : []),
                route_json: JSON.stringify(route),
                saved: saved ? 'true' : 'false'
            });
        } catch (e) {
            course.saved = previous;
            await this.showSaveHint('찜 상태를 변경하지 못했어요. 잠시 후 다시 시도해주세요.');
            return;
        }

        let code = response && response.code;
        let data = response && response.data;
        if (code === 200) {
            let ids = data && data.course_ids ? data.course_ids : [];
            let rows = data && data.courses ? data.courses : [];
            this.applySavedCourseIds(ids);
            this.applySavedCourseRows(rows);
            this.persistOfflineSavedCourses(ids, rows);
            await this.service.render();
            return;
        }

        course.saved = previous;
        if (code === 401 && this.service.auth) this.service.auth.clearLocalSession();
        await this.showSaveHint(this.responseMessage(data, '찜 상태를 변경하지 못했어요.'));
    }

    private async showSaveHint(message: string = '로그인해야 코스를 저장할 수 있어요.') {
        this.saveHintMessage = message;
        this.saveHintVisible = true;
        if (this.saveHintTimer) clearTimeout(this.saveHintTimer);
        if (typeof window !== 'undefined') {
            this.saveHintTimer = window.setTimeout(async () => {
                this.saveHintVisible = false;
                await this.service.render();
            }, 1800);
        }
        await this.service.render();
    }

    private scheduleFilterPlaceLoad() {
        if (this.placeSearchTimer) clearTimeout(this.placeSearchTimer);
        if (typeof window === 'undefined') {
            this.loadFilterPlaces(false);
            return;
        }

        this.placeSearchTimer = window.setTimeout(() => {
            this.loadFilterPlaces();
        }, 260);
    }

    private refreshHomePlacesInBackground() {
        if (this.activeTab !== 'home') return;
        this.loadFilterPlaces(false)
            .then(async () => {
                if (this.activeTab === 'home') await this.service.render();
            })
            .catch(() => { });
    }

    private async loadFilterPlaces(showLoading: boolean = true) {
        let token = ++this.placeCoursesRequestToken;
        if (!this.shouldLoadFilterPlaces()) {
            this.placeCourses = [];
            this.placeThemeSections = [];
            this.activePlaceThemeKey = '';
            this.placeCoursesLoading = false;
            return;
        }

        this.placeCoursesLoading = showLoading;
        if (showLoading) await this.service.render();

        try {
            let params = new URLSearchParams();
            params.set('limit', this.selectedFilters.location ? '48' : '24');
            if (this.selectedFilters.location) params.set('location', this.selectedFilters.location);
            let apiSearch = this.placeApiSearchText();
            if (apiSearch) params.set('search', apiSearch);

            let response = await fetch(`/api/places/themes?${params.toString()}`, { method: 'GET' });
            let payload = await response.json();
            let data = payload && payload.data ? payload.data : payload;
            if (token !== this.placeCoursesRequestToken) return;

            this.placeThemeSections = this.themeSectionsToPlaceSections((data && data.rows) || []);
            this.placeCourses = this.flattenPlaceThemeSections(this.placeThemeSections);
            this.syncActivePlaceTheme();
            this.applySavedCourseIds(this.savedCourseIds);
        } catch (e) {
            if (token === this.placeCoursesRequestToken) {
                this.placeCourses = [];
                this.placeThemeSections = [];
                this.activePlaceThemeKey = '';
            }
        }

        if (token === this.placeCoursesRequestToken) {
            this.placeCoursesLoading = false;
            if (showLoading) await this.service.render();
        }
    }

    private shouldLoadFilterPlaces() {
        if (this.activeTab !== 'home') return false;
        return true;
    }

    private placeApiSearchText() {
        let themeTerms = [
            '여행',
            '데이트',
            '1박2일',
            '당일치기',
            '감성숙소',
            '숙소',
            '카페',
            '맛집',
            '음식점',
            '볼거리',
            '명소',
            '쇼핑',
            '미용',
            '뷰티'
        ];
        return String(this.query || '')
            .trim()
            .split(/\s+/)
            .filter((token: string) => themeTerms.indexOf(token) === -1)
            .join(' ');
    }

    private themeSectionsToPlaceSections(sections: any[] = []) {
        let seen: any = {};
        let rows: any[] = [];
        (sections || []).forEach((section: any, index: number) => {
            let theme: any = {
                key: String((section && section.key) || `theme-${index}`).trim(),
                label: String((section && section.label) || '장소').trim(),
                keyword: String((section && section.keyword) || (section && section.label) || '장소').trim(),
                icon: String((section && section.icon) || '').trim(),
                tone: String((section && section.tone) || '').trim(),
                count: Number((section && section.count) || 0),
                courses: []
            };
            ((section && section.places) || []).forEach((place: any) => {
                if (this.isLegacyExamplePlace(place)) return;
                let id = String((place && (place.id || place.tourapi_id)) || '').trim();
                if (!id || seen[id]) return;
                seen[id] = true;
                theme.courses.push(this.placeToCourse(place, theme));
            });
            if (theme.courses.length > 0) rows.push(theme);
        });
        return rows;
    }

    private isLegacyExamplePlace(place: any) {
        let externalId = String(place && place.tourapi_id || '').trim().toUpperCase();
        let description = String(place && (place.description || place.overview) || '').trim();
        return externalId.indexOf('DUMMY-') === 0 || description.indexOf('[더미]') === 0;
    }

    private flattenPlaceThemeSections(sections: any[] = []) {
        let rows: any[] = [];
        (sections || []).forEach((section: any) => {
            rows = rows.concat((section && section.courses) || []);
        });
        return rows;
    }

    private syncActivePlaceTheme() {
        let themes = this.visiblePlaceThemes();
        if (themes.length === 0) {
            this.activePlaceThemeKey = '';
            return;
        }
        if (!this.activePlaceThemeKey || !themes.some((theme: any) => theme.key === this.activePlaceThemeKey)) {
            this.activePlaceThemeKey = themes[0].key;
        }
    }

    private filteredThemeCourses(theme: any) {
        return ((theme && theme.courses) || []).filter((course: any) => this.matchesCourse(course));
    }

    private placeToCourse(place: any, theme: any) {
        let themeKey = String(place.theme_key || theme.key || '').trim();
        let themeLabel = String(place.theme_label || theme.label || place.category || '장소').trim();
        let themeKeyword = String(place.theme_keyword || theme.keyword || themeLabel).trim();
        let category = String(place.category || themeLabel).trim();
        let location = this.placeLocationLabel(place);
        let summary = String(place.summary || place.address || category || '방문하기 좋은 장소입니다.').trim();
        let tags = this.uniqueTags([
            '여행',
            '데이트',
            '연인',
            '친구',
            '가족',
            '혼자',
            '오늘',
            '이번주말',
            '당일치기',
            '1박2일',
            location,
            themeLabel,
            themeKeyword,
            category,
            place.name,
            place.address,
            summary
        ]);

        return {
            id: `place-${place.id || place.tourapi_id}`,
            source: 'place',
            theme_key: themeKey,
            theme_label: themeLabel,
            theme_keyword: themeKeyword,
            title: place.name || '추천 장소',
            location,
            summary,
            duration: place.usage_time || '방문지',
            distance: category || themeLabel,
            rating: this.placeRatingLabel(place),
            tone: this.placeTone(themeKey),
            icon: place.theme_icon || this.placeIcon(themeKey, category),
            image: place.image || '',
            image_broken: false,
            saved: false,
            tags
        };
    }

    private placeLocationLabel(place: any) {
        let area = String((place && place.area) || '').trim();
        if (area) return area;

        let address = String((place && place.address) || '').trim();
        if (!address) return '부산';

        let tokens = address
            .replace(/특별시|광역시|특별자치도|특별자치시/g, '')
            .split(/\s+/)
            .filter((item: string) => !!item);
        return tokens.slice(0, 2).join(' ') || address;
    }

    private placeRatingLabel(place: any) {
        if (!place || place.rating === null || typeof place.rating === 'undefined' || place.rating === '') return '-';
        let value = Number(place.rating);
        if (Number.isNaN(value)) return String(place.rating);
        return value.toFixed(1);
    }

    private placeTone(themeKey: string) {
        let tones: any = {
            sight: 'tone-blue',
            cafe: 'tone-rose',
            beauty: 'tone-green',
            stay: 'tone-sun',
            food: 'tone-orange',
            shopping: 'tone-slate'
        };
        return tones[themeKey] || 'tone-blue';
    }

    private placeIcon(themeKey: string, category: string) {
        let icons: any = {
            sight: 'fa-location-dot',
            cafe: 'fa-mug-saucer',
            beauty: 'fa-spa',
            stay: 'fa-bed',
            food: 'fa-utensils',
            shopping: 'fa-bag-shopping'
        };
        if (icons[themeKey]) return icons[themeKey];
        if (category.indexOf('음식') > -1) return 'fa-utensils';
        if (category.indexOf('숙') > -1) return 'fa-bed';
        return 'fa-location-dot';
    }

    private uniqueTags(values: any[] = []) {
        let seen: any = {};
        let tags: string[] = [];
        values.forEach((value: any) => {
            let text = String(value || '').trim();
            if (!text) return;
            [text, ...text.replace(/[·,/()]/g, ' ').split(/\s+/)].forEach((item: string) => {
                item = String(item || '').trim();
                if (!item || seen[item]) return;
                seen[item] = true;
                tags.push(item);
            });
        });
        return tags;
    }

    private async restoreSavedCourses() {
        if (!this.isLoggedIn()) return;
        let response: any = null;
        try {
            response = await wiz.call('saved_courses', {});
        } catch (e) { }
        const code = response && response.code;
        const data = response && response.data;
        if (code === 200) {
            let ids = data && data.course_ids ? data.course_ids : [];
            let rows = data && data.courses ? data.courses : [];
            let ownedRows = data && data.owned_courses ? data.owned_courses : [];
            let publicRows = data && data.public_courses ? data.public_courses : [];
            this.applySavedCourseIds(ids);
            this.applySavedCourseRows(rows);
            this.applyOwnedCourseRows(ownedRows);
            this.applyPublicCourseRows(publicRows);
            this.applyBehavioralSignals(data);
            if (data && Array.isArray(data.following_authors)) this.applyMyProfileFollowing(data.following_authors);
            this.persistOfflineSavedCourses(ids, rows, ownedRows, publicRows);
            return;
        }
        if (this.restoreOfflineSavedCourses()) return;
        if (code === 401 && this.service.auth) this.service.auth.clearLocalSession();
    }

    private async loadPublicCourses(showLoading: boolean = false) {
        try {
            let response: any = await wiz.call('saved_courses', { community_action: 'public_courses' });
            let rows = response && response.data && Array.isArray(response.data.public_courses) ? response.data.public_courses : [];
            if (!response || response.code !== 200) return false;
            this.applyPublicCourseRows(rows);
            this.applyBehavioralSignals(response.data);
            if (showLoading) await this.service.render();
            return true;
        } catch (e) { }
        return false;
    }

    private persistOfflineSavedCourses(ids: any[] = [], rows: any[] = [], ownedRows: any[] = this.ownedCourseRows, publicRows: any[] = []) {
        if (typeof window === 'undefined' || !window.localStorage) return;
        try {
            window.localStorage.setItem(this.savedCourseOfflineStorageKey, JSON.stringify({
                course_ids: ids || [],
                courses: rows || [],
                owned_courses: ownedRows || [],
                public_courses: publicRows || [],
                cached_at: new Date().toISOString()
            }));
        } catch (e) { }
    }

    private restoreOfflineSavedCourses() {
        if (typeof window === 'undefined' || !window.localStorage) return false;
        try {
            let raw = window.localStorage.getItem(this.savedCourseOfflineStorageKey) || '';
            let cached = raw ? JSON.parse(raw) : null;
            if (!cached || !Array.isArray(cached.courses)) return false;
            this.applySavedCourseIds(Array.isArray(cached.course_ids) ? cached.course_ids : []);
            this.applySavedCourseRows(cached.courses);
            this.applyOwnedCourseRows(Array.isArray(cached.owned_courses) ? cached.owned_courses : []);
            this.applyPublicCourseRows(Array.isArray(cached.public_courses) ? cached.public_courses : []);
            return true;
        } catch (e) {
            return false;
        }
    }

    private applySavedCourseIds(ids: any[] = []) {
        this.savedCourseIds = (ids || []).filter((id: any) => !this.isLegacyExampleCourseId(id));
        this.courses = this.courses.filter((course: any) => {
            return !course || !course.serverSavedCourse || this.savedCourseIds.indexOf(course.id) > -1;
        });
        let applySaved = (course: any) => {
            course.saved = !this.companionCourseIsMine(course) && this.savedCourseIds.indexOf(course.id) > -1;
        };
        this.allCourses().forEach((course: any) => {
            applySaved(course);
        });
        this.placeThemeSections.forEach((section: any) => {
            ((section && section.courses) || []).forEach((course: any) => applySaved(course));
        });
    }

    private applySavedCourseRows(rows: any[] = []) {
        if (!Array.isArray(rows) || rows.length === 0) return;
        let restored = rows
            .filter((row: any) => !this.isLegacyExampleCourseId(row && row.course_id))
            .map((row: any) => this.savedCourseRowToCourse(row))
            .filter((course: any) => !!course && !!course.id);
        if (restored.length === 0) return;

        let restoredMap: any = {};
        restored.forEach((course: any) => {
            restoredMap[course.id] = course;
        });

        this.courses = [
            ...restored,
            ...this.courses
                .filter((course: any) => course && !restoredMap[course.id])
        ];
        this.recommendations = this.recommendations.map((course: any) => {
            return course && restoredMap[course.id]
                ? { ...course, saved: !!restoredMap[course.id].saved }
                : course;
        });
    }

    private applyOwnedCourseRows(rows: any[] = []) {
        this.ownedCourseRows = Array.isArray(rows) ? rows : [];
        let restored = this.ownedCourseRows
            .map((row: any) => this.courseRowToCard(row, row))
            .filter((course: any) => !!course && !!course.id)
            .map((course: any) => ({ ...course, saved: false, mine: true, source: 'mine', serverOwnedCourse: true }));
        let restoredMap: any = {};
        restored.forEach((course: any) => restoredMap[course.id] = course);

        this.courses = [
            ...restored,
            ...this.courses.filter((course: any) => {
                if (!course || restoredMap[course.id]) return false;
                return !course.serverOwnedCourse;
            })
        ];
        this.recommendations = this.recommendations.map((course: any) => {
            return course && restoredMap[course.id] ? { ...course, ...restoredMap[course.id] } : course;
        });
    }

    private applyPublicCourseRows(rows: any[] = []) {
        let shared = (Array.isArray(rows) ? rows : [])
            .map((row: any) => this.courseRowToCard(row, row))
            .filter((course: any) => !!course && !!course.id)
            .map((course: any) => ({
                ...course,
                saved: this.savedCourseIds.indexOf(course.id) > -1,
                mine: false,
                source: 'shared',
                serverPublicCourse: true
            }));
        let sharedMap: any = {};
        shared.forEach((course: any) => sharedMap[course.id] = course);
        this.recommendations = [
            ...shared,
            ...this.recommendations.filter((course: any) => {
                if (!course || sharedMap[course.id]) return false;
                return !course.serverPublicCourse;
            })
        ];
        this.applySavedCourseIds(this.savedCourseIds);
    }

    private rememberOwnedCourseRow(row: any) {
        if (!row || !row.id) return;
        this.ownedCourseRows = [row, ...this.ownedCourseRows.filter((item: any) => item && item.id !== row.id)];
    }

    private isLegacyExampleCourseId(value: any) {
        let id = String(value || '').trim().toLowerCase();
        return id.indexOf('rec-') === 0
            || id.indexOf('list-') === 0
            || ['course-seongsu-date', 'course-busan-haeundae', 'course-jeju-aewol'].indexOf(id) > -1;
    }

    private savedCourseRowToCourse(row: any) {
        if (!row || !row.course_id) return null;
        let places = this.parseJsonArray(row.places_json);
        let route = this.parseJsonObject(row.route_json);
        let firstPlace = places.length > 0 ? places[0] : {};
        return {
            id: row.course_id,
            title: row.title || route.title || '저장한 AI 코스',
            location: row.location || route.region || '',
            summary: row.summary || this.savedCoursePlacesSummary(places),
            duration: row.duration || route.schedule || '',
            distance: route.total_distance || '',
            rating: row.rating || '',
            icon: row.icon || 'fa-route',
            tone: row.tone || 'tone-blue',
            image: firstPlace.image || '/assets/places/haeundae-beach.jpg',
            saved: route.source !== 'mine',
            mine: route.source === 'mine',
            source: route.source === 'mine' ? 'mine' : 'saved',
            author: route.original_author || route.author || '',
            originalAuthor: route.original_author || route.author || '',
            originalCourseId: route.original_course_id || row.course_id,
            serverSavedCourse: true,
            places,
            route,
            tags: this.uniqueTags(['AI플래너', row.location || route.region || '', row.duration || route.schedule || ''])
        };
    }

    private parseJsonArray(value: any) {
        try {
            let parsed = typeof value === 'string' ? JSON.parse(value || '[]') : value;
            return Array.isArray(parsed) ? parsed : [];
        } catch (e) {
            return [];
        }
    }

    private parseJsonObject(value: any) {
        try {
            let parsed = typeof value === 'string' ? JSON.parse(value || '{}') : value;
            return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
        } catch (e) {
            return {};
        }
    }

    private savedCoursePlacesSummary(places: any[] = []) {
        return places
            .map((place: any) => `${place.day_label || ''} ${place.time || ''} ${place.name || ''}`.trim())
            .filter((text: string) => !!text)
            .slice(0, 6)
            .join(' · ');
    }

    private validateAuthForm() {
        let errors: any = {};
        let email = String(this.authForm.email || '').trim();
        let password = String(this.authForm.password || '');
        let passwordConfirm = String(this.authForm.passwordConfirm || '');
        let nickname = String(this.authForm.nickname || '').trim();
        let isAdminLogin = this.authMode === 'login' && email === 'admin';
        let isEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

        if (!email) {
            errors.email = '이메일을 입력해주세요.';
        } else if (!isAdminLogin && !isEmail) {
            errors.email = '올바른 이메일 형식으로 입력해주세요.';
        }
        if (!password) {
            errors.password = '비밀번호를 입력해주세요.';
        }
        if (this.authMode === 'register') {
            if (!isEmail) errors.email = '올바른 이메일 형식으로 입력해주세요.';
            if (!nickname) errors.nickname = '닉네임을 입력해주세요.';
            if (password.length < 6) errors.password = '비밀번호는 6자 이상 입력해주세요.';
            if (password !== passwordConfirm) errors.passwordConfirm = '비밀번호가 일치하지 않습니다.';
        }

        this.authErrors = errors;
        this.authServerError = '';
        return Object.keys(errors).length === 0;
    }

    private resetAuthForm() {
        this.authForm = {
            email: '',
            password: '',
            passwordConfirm: '',
            nickname: ''
        };
        this.authErrors = {};
        this.authServerError = '';
    }

    private responseMessage(data: any, fallback: string) {
        if (typeof data === 'string' && data) return data;
        if (data && data.message) return data.message;
        return fallback;
    }

    private logFilterEvent(key: string, value: string) {
        if (!value) return;
        wiz.call('log_filter_event', {
            filter_key: key,
            filter_value: value
        });
    }

    public async changeCalendarMonth(direction: number) {
        this.calendarMonth = new Date(this.calendarMonth.getFullYear(), this.calendarMonth.getMonth() + direction, 1);
        this.buildCalendar();
        await this.service.render();
    }

    public async startScheduleDrag(day: any, event: any) {
        if (event) event.preventDefault();
        this.schedulePointerActive = true;
        this.scheduleDragStartKey = day.key;
        this.setDraftScheduleRange(day.key, day.key);
        await this.service.render();
    }

    public async previewSchedulePointer(event: any) {
        if (!this.schedulePointerActive) return;
        if (event) event.preventDefault();
        let key = this.dateKeyFromPointer(event);
        if (!key) return;

        let next = this.normalizeDateRange(this.scheduleDragStartKey, key);
        if (next.start === this.draftScheduleRange.start && next.end === this.draftScheduleRange.end) return;
        this.draftScheduleRange = next;
        await this.service.render();
    }

    public async finishSchedulePointer(event: any) {
        if (!this.schedulePointerActive) return;
        if (event) event.preventDefault();

        let key = this.dateKeyFromPointer(event) || this.draftScheduleRange.end || this.scheduleDragStartKey;
        this.schedulePointerActive = false;
        this.ignoreNextScheduleClick = true;
        this.setDraftScheduleRange(this.scheduleDragStartKey, key);
        this.applyScheduleRange(this.draftScheduleRange.start, this.draftScheduleRange.end);
        this.logFilterEvent('schedule', this.selectedFilters.schedule);
        this.activeFilterKey = '';
        this.persistAccessState();
        this.replaceAccessUrl();
        await this.loadFilterPlaces();
        await this.service.render();
    }

    public async cancelScheduleDrag() {
        this.schedulePointerActive = false;
        this.draftScheduleRange = { start: '', end: '' };
        await this.service.render();
    }

    public async selectScheduleDay(day: any) {
        if (this.ignoreNextScheduleClick) {
            this.ignoreNextScheduleClick = false;
            return;
        }

        this.applyScheduleRange(day.key, day.key);
        this.logFilterEvent('schedule', this.selectedFilters.schedule);
        this.activeFilterKey = '';
        this.persistAccessState();
        this.replaceAccessUrl();
        await this.loadFilterPlaces();
        await this.service.render();
    }

    public async clearSchedule() {
        this.scheduleRange = { start: '', end: '' };
        this.draftScheduleRange = { start: '', end: '' };
        this.selectedFilters.schedule = '';
        this.activeFilterKey = '';
        this.persistAccessState();
        this.replaceAccessUrl();
        await this.loadFilterPlaces();
        await this.service.render();
    }

    public isScheduleDayInRange(day: any) {
        let range = this.visibleScheduleRange();
        if (!range.start || !range.end) return false;
        return day.key >= range.start && day.key <= range.end;
    }

    public isScheduleRangeStart(day: any) {
        let range = this.visibleScheduleRange();
        return !!range.start && day.key === range.start;
    }

    public isScheduleRangeEnd(day: any) {
        let range = this.visibleScheduleRange();
        return !!range.end && day.key === range.end;
    }

    public isScheduleDaySelected(day: any) {
        return this.isScheduleRangeStart(day) && this.isScheduleRangeEnd(day);
    }

    public activeLocationAreas() {
        let group = this.locationGroups.find((item: any) => item.name === this.selectedLocationRegion);
        return group ? group.areas : [];
    }

    public async selectLocationRegion(region: any) {
        this.selectedLocationRegion = region.name;
        await this.service.render();
    }

    public async selectLocationArea(area: any) {
        this.selectedFilters.location = this.selectedFilters.location === area.value ? '' : area.value;
        this.logFilterEvent('location', this.selectedFilters.location);
        this.activeFilterKey = '';
        this.filterOverviewOpen = false;
        this.persistAccessState();
        this.replaceAccessUrl();
        await this.loadFilterPlaces();
        await this.service.render();
    }

    public isLocationAreaSelected(area: any) {
        return this.selectedFilters.location === area.value;
    }

    public activeFilter() {
        return this.conditionFilters.find((filter: any) => filter.key === this.activeFilterKey);
    }

    public isConditionSelected(key: string) {
        return !!this.selectedFilters[key];
    }

    public getConditionText(filter: any) {
        return this.selectedFilters[filter.key] || filter.defaultValue;
    }

    public getSheetTitle(filter: any) {
        if (filter.key === 'location') return '지역 선택';
        if (filter.key === 'schedule') return '날짜 선택';
        return '누구랑 갈까요?';
    }

    public getPreferenceValue(key: string) {
        return this.selectedFilters[key] || '미선택';
    }

    public getMapLocationLabel() {
        return this.selectedFilters.location || '국내';
    }

    public getLeadText() {
        let location = this.selectedFilters.location;
        let companion = this.getCompanionPhrase(this.selectedFilters.companion);

        if (location && companion) return `${location}에서 ${companion}`;
        if (location) return `${location}에서`;
        if (companion) return `동행 ${companion}`;
        return '오늘의 추천';
    }

    public filteredRecommendations() {
        let courses = this.recommendations.filter((course: any) => this.matchesCourse(course));
        let terms = this.recommendationPreferenceTerms();
        return courses.sort((left: any, right: any) => {
            if (terms.length > 0 || this.behavioralSignals.enabled) {
                let scoreGap = this.recommendationScore(right, terms) - this.recommendationScore(left, terms);
                if (scoreGap !== 0) return scoreGap;
            }
            return this.recommendationStableRank(left) - this.recommendationStableRank(right);
        });
    }

    private recommendationPreferenceTerms() {
        let values: any[] = [
            this.selectedFilters.location,
            this.selectedFilters.companion,
            this.selectedFilters.schedule
        ];
        if (!this.accountPreferences.personalizedRecommendations || !this.behavioralSignals.enabled) return this.uniqueTags(values).map((v: string) => v.toLowerCase());
        values.push(...(this.behavioralSignals.terms || []));
        this.allCourses()
            .filter((course: any) => course && (course.saved || course.mine))
            .slice(0, 12)
            .forEach((course: any) => values.push(course.location, course.category, ...(course.tags || [])));
        return this.uniqueTags(values).map((value: string) => value.toLowerCase());
    }

    private recommendationScore(course: any, terms: string[]) {
        let text = [
            course && course.title,
            course && course.location,
            course && course.summary,
            course && course.category,
            course && course.companion_type,
            ...(course && Array.isArray(course.tags) ? course.tags : [])
        ].map((value: any) => String(value || '').toLowerCase()).join(' ');
        return terms.reduce((score: number, term: string) => {
            if (!term || text.indexOf(term) < 0) return score;
            let location = String((course && course.location) || '').toLowerCase();
            return score + (location.indexOf(term) > -1 ? 8 : 2);
        }, 0) + Number((course && course.saved_count) || 0) * 0.01
            + (this.accountPreferences.personalizedRecommendations && this.behavioralSignals.enabled
                ? ((this.behavioralSignals.followed_authors || []).indexOf(this.courseAuthorId(course)) >= 0 ? 12 : 0)
                  + ((this.behavioralSignals.executed_courses || []).indexOf(String(course && course.id || '')) >= 0 ? 4 : 0)
                : 0);
    }

    private recommendationStableRank(course: any) {
        let value = `${this.communityActorKey()}:${String((course && course.id) || '')}`;
        let hash = 2166136261;
        for (let index = 0; index < value.length; index += 1) {
            hash ^= value.charCodeAt(index);
            hash = Math.imul(hash, 16777619);
        }
        return hash >>> 0;
    }

    public async openFeaturedCourseDetail(course: any) {
        if (!course) return;
        this.resetProfileCourseInlineMap();
        this.profileCourseResolvedCoordinates = {};
        this.selectedFeaturedCourse = course;
        this.featuredCourseDetailOpen = true;
        await this.service.render();
        this.scheduleProfileCourseInlineMap();
        if (typeof document !== 'undefined') {
            let content = document.querySelector('.app-content') as HTMLElement;
            if (content) content.scrollTo({ top: 0, behavior: 'smooth' });
        }
    }

    public async closeFeaturedCourseDetail() {
        this.resetProfileCourseInlineMap();
        this.profileCourseResolvedCoordinates = {};
        this.featuredCourseDetailOpen = false;
        this.selectedFeaturedCourse = null;
        this.resetCourseCollaboration();
        await this.service.render();
        this.resetHomeContentScroll();
    }

    public featuredCoursePlaces(course: any) {
        return course && Array.isArray(course.places) ? course.places : [];
    }

    public filteredCourses() {
        return [
            ...this.courses,
            ...this.placeCourses
        ].filter((course: any) => this.matchesCourse(course));
    }

    public savedCourses() {
        return this.allCourses().filter((course: any) => {
            if (!course || !course.saved) return false;
            return course.source !== 'mine' && course.source !== 'draft' && !course.isCourseDraft;
        });
    }

    public zenlyRegionLabel() {
        let label = this.selectedFilters.location || (this.executionCourse && this.executionCourse.region) || '성수';
        return String(label || '성수').trim() || '성수';
    }

    public zenlyRegionBanner() {
        return (this.zenlyHeatmap && this.zenlyHeatmap.banner) || `지금 ${this.zenlyRegionLabel()}에 0명이 있어요`;
    }

    public async loadZenlyData(showLoading: boolean = true) {
        await this.loadZenlyHeatmap(showLoading);
        await this.loadZenlySignals(false);
        this.touchZenlyPresence();
        this.bindZenlyMarkerDelegation();
    }

    public async loadZenlyHeatmap(showLoading: boolean = true) {
        this.zenlyHeatmapLoading = !!showLoading;
        if (showLoading) await this.service.render();
        let changed = false;
        try {
            const response: any = await wiz.call('zenly_presence_heatmap', {
                region: this.zenlyRegionLabel(),
                limit: 12
            });
            if (response && response.code === 200 && response.data && response.data.heatmap) {
                this.zenlyHeatmap = response.data.heatmap;
                changed = true;
                if (this.selectedPresencePlaceId) {
                    let selected = this.zenlyPresencePlaces().find((place: any) => place.place_id === this.selectedPresencePlaceId);
                    if (selected && selected.place_id) {
                        await this.loadPresenceHourly(selected.place_id, false);
                    } else {
                        this.selectedPresencePlaceId = '';
                    }
                }
            }
        } catch (e) { }
        this.zenlyHeatmapLoading = false;
        if (showLoading || changed) {
            await this.service.render();
            this.bindZenlyMarkerDelegation();
        }
    }

    public async loadPresenceHourly(placeId: string, showLoading: boolean = true) {
        if (!placeId || String(placeId).indexOf('seed-') === 0) {
            this.zenlyPresenceHourly = this.seedPresenceHourly();
            return;
        }
        try {
            const response: any = await wiz.call('zenly_presence_hourly', {
                place_id: placeId,
                hours: 12
            });
            if (response && response.code === 200 && response.data && response.data.hourly) {
                this.zenlyPresenceHourly = response.data.hourly.buckets || [];
            }
        } catch (e) {
            this.zenlyPresenceHourly = this.seedPresenceHourly();
        }
        if (showLoading) await this.service.render();
    }

    public seedPresenceHourly() {
        let labels = ['09시', '10시', '11시', '12시', '13시', '14시', '15시', '16시', '17시', '18시', '19시', '20시'];
        return labels.map((label: string, index: number) => ({
            label,
            count: [2, 4, 7, 11, 13, 9, 12, 18, 21, 17, 14, 8][index] || 0
        }));
    }

    public async loadZenlySignals(showLoading: boolean = true) {
        this.zenlySignalsLoading = !!showLoading;
        let eligibilityChanged = this.zenlyNearbyTravelerCount !== 0;
        this.zenlyNearbyTravelerCount = 0;
        if (showLoading) await this.service.render();
        let origin = await this.requestTogetherGpsCoordinate();
        if (!origin || origin.accuracy <= 0 || origin.accuracy > 100) {
            this.zenlySignals = [];
            this.zenlyNearbyTravelerCount = 0;
            this.zenlySignalsLoading = false;
            await this.service.render();
            return;
        }
        let changed = false;
        try {
            const response: any = await wiz.call('zenly_signals_nearby', {
                lat: origin.lat,
                lng: origin.lng,
                radius: 1000,
                accuracy: origin.accuracy
            });
            if (response && response.code === 200 && response.data && Array.isArray(response.data.signals)) {
                this.zenlySignals = response.data.signals;
                this.zenlyNearbyTravelerCount = Math.max(0, Number(response.data.nearbyTravelerCount || 0));
                changed = true;
                if (this.selectedZenlySignalId && !this.zenlySignals.find((signal: any) => signal.id === this.selectedZenlySignalId)) {
                    this.selectedZenlySignalId = '';
                }
            }
        } catch (e) { }
        this.zenlySignalsLoading = false;
        if (showLoading || changed || eligibilityChanged) {
            await this.service.render();
            this.bindZenlyMarkerDelegation();
        }
    }

    private touchZenlyPresence() {
        if (typeof navigator === 'undefined' || !navigator.geolocation) return;
        navigator.geolocation.getCurrentPosition(
            (position: any) => {
                try {
                    wiz.call('zenly_presence_touch', {
                        lat: position.coords.latitude,
                        lng: position.coords.longitude,
                        region: this.zenlyRegionLabel(),
                        accuracy: position.coords.accuracy,
                        radius: 180
                    });
                } catch (e) { }
            },
            () => { },
            { enableHighAccuracy: true, timeout: 10000, maximumAge: 15000 }
        );
    }

    private applyZenlySignalUpdate(signal: any) {
        if (!signal || !signal.id) return;
        this.zenlySignals = [signal, ...this.zenlySignals.filter((item: any) => item.id !== signal.id)];
        this.selectedZenlySignalId = signal.id;
    }

    public async prepareMapExecution() {
        await Promise.all([this.loadMapExecutionCourses(false), this.loadMyTrip(true)]);
        await this.service.render();
        this.scheduleNaverMapRender();
    }

    public async loadMapExecutionCourses(showLoading: boolean = true) {
        if (!this.isLoggedIn()) return;
        if (this.mapExecutableCourses.length > 0 && !showLoading) return;
        this.mapCoursesLoading = !!showLoading;
        if (showLoading) this.mapExecutableCourses = [];
        if (showLoading) await this.service.render();
        try {
            const response: any = await wiz.call('course_execution_courses', {});
            const code = response && response.code;
            const data = response && response.data;
            if (code === 200 && data && Array.isArray(data.courses)) {
                this.mapExecutableCourses = data.courses.filter((course: any) => {
                    return !!course && ['mine', 'saved', 'companion'].indexOf(String(course.source || '')) >= 0;
                });
                const clientId = String(data.naver_maps_client_id || data.naverMapsClientId || '').trim();
                if (clientId) this.naverMapsClientId = clientId;
            } else {
                this.mapExecutableCourses = [];
            }
        } catch (e) {
            this.mapExecutableCourses = [];
        }
        this.mapCoursesLoading = false;
        if (showLoading) await this.service.render();
    }

    public async toggleMapCoursePicker() {
        if (this.mapCoursePickerOpen) {
            await this.closeMapCoursePicker();
            return;
        }
        this.mapCoursePickerOpen = true;
        this.stagedExecutionCourseId = this.activeExecutionCourseId || '';
        await this.loadMapExecutionCourses(true);
        await this.service.render();
    }

    public async closeMapCoursePicker() {
        this.mapCoursePickerOpen = false;
        await this.service.render();
    }

    public filteredMapExecutableCourses() {
        let query = String(this.mapCourseSearchQuery || '').trim().toLowerCase();
        return (this.mapExecutableCourses || []).filter((course: any) => {
            if (!query) return true;
            let text = [
                course.title,
                course.location,
                course.summary,
                course.duration,
            ].join(' ').toLowerCase();
            return text.indexOf(query) > -1;
        });
    }

    public async stageExecutionCourse(course: any) {
        if (this.myTripBusy || this.mapRouteLoading) return;
        let nextCourseId = String(course && course.id ? course.id : '').trim();
        if (nextCourseId !== this.stagedExecutionCourseId) {
            this.mapStartAddress = '';
            this.mapStartCoordinate = null;
            this.mapStartRequiresGps = false;
            this.executionLiveOrigin = null;
            this.naverSearchCoordinate = null;
            this.mapStartStatus = '출발지 선택 전';
            this.travelModeChoice = '';
            this.companionSearchOffered = false;
            this.myTripError = '';
        }
        this.stagedExecutionCourseId = nextCourseId;
        await this.service.render();
    }

    public isStagedExecutionCourse(course: any) {
        return !!course && String(course.id || '') === this.stagedExecutionCourseId;
    }

    public hasMapStartCoordinate() {
        return !!this.mapStartCoordinate
            && this.isFiniteNumber(this.mapStartCoordinate.lat)
            && this.isFiniteNumber(this.mapStartCoordinate.lng);
    }

    public courseSourceLabel(course: any) {
        if (course && course.companion_post_ids && course.companion_post_ids.length) return '동행 코스';
        return String(course && course.source || '') === 'saved' ? '저장한 코스' : '내 코스';
    }

    public courseMetaLabel(course: any) {
        let location = String(course && course.location ? course.location : '지역 미정');
        return `${location} · ${this.courseSourceLabel(course)}`;
    }

    public courseDurationLabel(course: any) {
        let duration = String(course && course.duration ? course.duration : '').trim();
        if (duration) return duration;
        let count = Number(course && course.place_count ? course.place_count : 0);
        let hours = Math.max(1, Math.ceil((count || 2) * 45 / 60));
        return `약 ${hours}시간`;
    }

    public async selectExecutionCourse(course: any) {
        let courseId = String(course && course.id ? course.id : '').trim();
        if (!courseId) return;
        this.replanProposal = null;
        this.replanMessage = '';
        this.replanFixedIds = [];
        this.replanClosedIds = [];
        this.replanDelayMinutes = 0;
        this.activeExecutionCourseId = courseId;
        this.stagedExecutionCourseId = courseId;
        this.mapCoursePickerOpen = false;
        this.mapRouteSheetExpanded = false;
        this.mapRouteLoading = true;
        this.mapRouteOverviewActive = false;
        this.executionRouteSummary = '';
        this.executionRouteError = '';
        this.executionNavigationActive = false;
        this.executionNavigationUsingLiveLocation = false;
        this.executionNavigationStatus = '';
        this.executionNavigationSteps = [];
        this.executionNavigationLastSpokenKey = '';
        this.resetExecutionNavigationTracking();
        this.stopExecutionVoiceGuidance();
        this.releaseExecutionWakeLock();
        this.executionMapZoomAdjustment = 0;
        this.selectedMapSpotId = '';
        await this.service.render();
        try {
            const response: any = await wiz.call('course_execution', { course_id: courseId });
            const code = response && response.code;
            const data = response && response.data;
            if (code === 200 && data && data.execution) {
                this.applyExecutionCourse(data.execution);
                await this.recordExecutionStart(courseId);
                const solo = !!this.myTrip && String(this.myTrip.course_id || '') === courseId;
                this.togetherTripStarted = !solo;
                this.togetherTripEnded = false;
                this.togetherLocationSharingActive = false;
                this.togetherShareStartedAt = 0;
                this.clearTogetherShareTimer();
                if (!solo) await this.activateTogetherTripMeeting();
            }
        } catch (e) { }
        this.mapRouteLoading = false;
        if (this.isTogetherMapActive()) await this.loadTogetherMeeting(false, false);
        await this.service.render();
        this.scheduleNaverMapRender();
    }

    public async endExecutionCourse() {
        const soloTrip = this.myTrip && this.executionCourse
            && String(this.myTrip.course_id || '') === String(this.executionCourse.id || '');
        if (soloTrip) {
            try {
                const response: any = await wiz.call('personal_trip', { action: 'complete', trip_id: this.myTrip.id });
                if (response && response.code === 200) this.myTrip = response.data.trip;
            } catch (e) { }
        } else {
            await this.closeTogetherMeeting(true);
        }
        this.togetherTripStarted = false;
        this.togetherTripEnded = true;
        this.togetherLocationSharingActive = false;
        this.togetherShareStartedAt = 0;
        this.clearTogetherShareTimer();
        this.togetherSafetyOpen = false;
        this.zenlySignalComposerOpen = false;
        this.activeExecutionCourseId = '';
        this.stagedExecutionCourseId = '';
        this.executionCourse = null;
        this.executionPlaces = [];
        this.executionTotalMinutes = 0;
        this.executionTotalDistanceMeters = 0;
        this.executionRouteSummary = '';
        this.executionRouteError = '';
        this.executionNavigationActive = false;
        this.executionNavigationUsingLiveLocation = false;
        this.executionNavigationStatus = '';
        this.executionNavigationSteps = [];
        this.executionNavigationLastCoordinate = null;
        this.executionNavigationLastRefreshAt = 0;
        this.executionNavigationLastSpokenKey = '';
        this.resetExecutionNavigationTracking();
        this.stopExecutionVoiceGuidance();
        this.releaseExecutionWakeLock();
        this.mapRouteOverviewActive = false;
        this.mapRouteSheetExpanded = false;
        this.mapRouteLoading = false;
        this.mapDepartureLoading = false;
        this.selectedMapSpotId = '';
        if (this.mapGeoWatchId && typeof navigator !== 'undefined' && navigator.geolocation) {
            try { navigator.geolocation.clearWatch(this.mapGeoWatchId); } catch (e) { }
        }
        this.mapGeoWatchId = null;
        await this.service.render();
        this.scheduleNaverMapRender();
    }

    public async toggleMapRouteSheet() {
        this.mapRouteSheetExpanded = !this.mapRouteSheetExpanded;
        await this.service.render();
    }

    public visibleExecutionNavigationSteps() {
        let steps = Array.isArray(this.executionNavigationSteps) ? this.executionNavigationSteps : [];
        let index = Math.min(Math.max(0, this.executionNavigationStepIndex), Math.max(0, steps.length - 1));
        return this.mapRouteSheetExpanded ? steps.slice(index) : steps.slice(index, index + 1);
    }

    public executionCurrentNavigationStep() {
        let steps = Array.isArray(this.executionNavigationSteps) ? this.executionNavigationSteps : [];
        return steps[Math.min(Math.max(0, this.executionNavigationStepIndex), Math.max(0, steps.length - 1))] || null;
    }

    public executionNavigationInstructionLabel() {
        let step = this.executionCurrentNavigationStep();
        if (step && step.instruction) return step.instruction;
        let target = this.executionNextSpot();
        return target ? `${target.order}. ${target.name} 방향으로 이동` : '경로를 확인하는 중';
    }

    public executionNavigationInstructionIcon() {
        let step = this.executionCurrentNavigationStep();
        return step && step.icon ? step.icon : 'fa-arrow-up';
    }

    public executionNavigationDistanceLabel() {
        return this.executionNavigationNextDistance || '경로 확인 중';
    }

    public executionNavigationRemainingLabel() {
        if (this.executionNavigationRemainingMeters === null) return '거리 계산 중';
        return this.formatPlannerDistance(Math.max(0, this.executionNavigationRemainingMeters), false);
    }

    public executionNavigationEtaLabel() {
        if (this.executionNavigationRemainingSeconds <= 0) return '--:-- 도착';
        let eta = new Date(Date.now() + this.executionNavigationRemainingSeconds * 1000);
        return `${String(eta.getHours()).padStart(2, '0')}:${String(eta.getMinutes()).padStart(2, '0')} 도착`;
    }

    public executionNavigationHeadingDegrees() {
        return Number.isFinite(Number(this.executionNavigationHeading)) ? Number(this.executionNavigationHeading) : 0;
    }

    public recenterExecutionNavigation(event?: any) {
        if (event && event.preventDefault) event.preventDefault();
        if (event && event.stopPropagation) event.stopPropagation();
        let coordinate = this.naverUserCoordinate || this.executionNavigationFilteredCoordinate || this.executionLiveOrigin;
        if (!coordinate) return;
        this.naverCenterOnUser = true;
        this.followExecutionNavigationCamera(coordinate, Math.max(0, this.executionNavigationSpeedKmh / 3.6));
    }

    public resetExecutionNavigationCompass(event?: any) {
        if (event && event.preventDefault) event.preventDefault();
        if (event && event.stopPropagation) event.stopPropagation();
        this.executionNavigationHeading = 0;
        try {
            if (this.naverMap && this.naverMap.setHeading) this.naverMap.setHeading(0);
            if (this.naverMap && this.naverMap.setTilt) this.naverMap.setTilt(0);
        } catch (e) { }
        let coordinate = this.naverUserCoordinate || this.executionNavigationFilteredCoordinate;
        if (coordinate) this.renderNaverUserMarker(coordinate);
    }

    public mapRouteNextSummary() {
        let spots = this.executionRouteSpots();
        if (!spots || spots.length === 0) return `${this.executionCourseTitle()} · 모든 장소 도착`;
        if (this.mapRouteOverviewActive) return `전체 코스 한눈에 보기 · ${this.executionPlaces.length}곳`;
        let selected = this.selectedMapSpot();
        let target = this.executionNavigationActive ? this.executionNextSpot() : (selected || this.executionNextSpot());
        if (!target) return this.executionCourseTitle();
        return `${this.executionSegmentOriginLabel()} → ${target.order}. ${target.name}`;
    }

    public executionNextSpot() {
        return this.executionPlaces.find((place: any) => place && !place.visited) || null;
    }

    public executionSegmentOriginLabel() {
        if (this.executionNavigationActive && this.executionNavigationUsingLiveLocation) return '내 위치';
        let visited = this.executionPlaces
            .filter((place: any) => place && place.visited)
            .sort((left: any, right: any) => Number(right.order || 0) - Number(left.order || 0));
        if (visited.length > 0) return `${visited[0].order}. ${visited[0].name}`;
        return this.mapStartStatus === '현재 위치' ? '현재 위치' : (this.mapStartAddress || this.mapStartStatus || '출발지');
    }

    public executionRouteStatusLabel(spot: any) {
        if (!spot) return '모든 장소에 도착했어요.';
        if (this.mapRouteLoading) return `${this.getTravelModeLabel()} 경로를 찾는 중`;
        if (this.executionRouteSummary) return this.executionRouteSummary;
        return this.getSpotTravelTime(spot) || `${this.getTravelModeLabel()} 경로 확인`;
    }

    public executionNavigationButtonLabel() {
        return this.executionNavigationActive ? '안내 종료' : '이 화면에서 안내 시작';
    }

    public executionVoiceSupported() {
        return typeof window !== 'undefined'
            && !!(window as any).speechSynthesis
            && !!(window as any).SpeechSynthesisUtterance;
    }

    public executionVoiceLabel() {
        if (!this.executionVoiceSupported()) return '음성 미지원';
        return this.executionVoiceGuidanceEnabled ? '음성 켜짐' : '음성 꺼짐';
    }

    public async toggleExecutionVoice(event?: any) {
        if (event && event.preventDefault) event.preventDefault();
        if (event && event.stopPropagation) event.stopPropagation();
        if (!this.executionVoiceSupported()) return;
        this.executionVoiceGuidanceEnabled = !this.executionVoiceGuidanceEnabled;
        this.executionNavigationLastSpokenKey = '';
        if (!this.executionVoiceGuidanceEnabled) this.stopExecutionVoiceGuidance();
        await this.service.render();
        if (this.executionVoiceGuidanceEnabled) this.announceExecutionNavigationStep();
    }

    public async toggleExecutionNavigation(event?: any) {
        if (event && event.preventDefault) event.preventDefault();
        if (event && event.stopPropagation) event.stopPropagation();
        if (this.executionNavigationActive) {
            this.executionNavigationActive = false;
            this.executionNavigationUsingLiveLocation = false;
            this.executionNavigationStatus = '';
            this.executionNavigationLastCoordinate = null;
            this.executionNavigationLastRefreshAt = 0;
            this.executionNavigationLastSpokenKey = '';
            this.resetExecutionNavigationTracking();
            this.stopExecutionVoiceGuidance();
            this.releaseExecutionWakeLock();
            if (this.mapGeoWatchId && typeof navigator !== 'undefined' && navigator.geolocation) {
                try { navigator.geolocation.clearWatch(this.mapGeoWatchId); } catch (e) { }
            }
            this.mapGeoWatchId = null;
            await this.service.render();
            this.scheduleNaverMapRender();
            return;
        }
        await this.startExecutionNavigation();
    }

    public async startExecutionNavigationFromDetail(event?: any) {
        if (event && event.preventDefault) event.preventDefault();
        if (event && event.stopPropagation) event.stopPropagation();
        this.selectedMapSpotId = '';
        if (!this.executionNavigationActive) await this.startExecutionNavigation();
        else {
            await this.service.render();
            this.scheduleNaverMapRender();
        }
    }

    private async startExecutionNavigation() {
        let target = this.executionNextSpot();
        if (!target) return;
        let origin = this.executionRouteOrigin();
        if (!origin) {
            this.executionRouteError = '출발지를 입력한 뒤 안내를 시작해주세요.';
            await this.service.render();
            return;
        }
        this.mapRouteOverviewActive = false;
        this.mapRouteSheetExpanded = false;
        this.selectedMapSpotId = '';
        this.executionNavigationActive = true;
        this.executionNavigationUsingLiveLocation = false;
        this.executionNavigationStatus = `내 위치에서 ${target.order}. ${target.name} 경로 확인 중`;
        this.executionRouteError = '';
        this.naverCenterOnUser = true;
        this.executionLiveOrigin = origin;
        this.executionNavigationLastCoordinate = origin;
        this.executionNavigationLastRefreshAt = Date.now();
        this.executionNavigationLastSpokenKey = '';
        this.resetExecutionNavigationTracking();
        this.requestExecutionWakeLock();
        await this.service.render();
        this.scheduleNaverMapRender();
        this.announceExecutionNavigationStep();
        if (typeof navigator === 'undefined' || !navigator.geolocation) {
            this.mapGpsDenied = true;
            this.executionRouteError = 'GPS 갱신 없이 선택한 출발지 기준으로 안내합니다.';
            await this.service.render();
            return;
        }
        navigator.geolocation.getCurrentPosition(
            async (position: any) => {
                this.mapGpsDenied = false;
                this.mapStartRequiresGps = true;
                this.executionNavigationUsingLiveLocation = true;
                await this.handleExecutionPosition(position, true);
                if (!this.mapGeoWatchId) this.startExecutionGeofence();
            },
            async () => {
                this.mapGpsDenied = true;
                this.executionNavigationStatus = `${this.executionSegmentOriginLabel()} 기준 안내 중`;
                this.executionRouteError = 'GPS 권한 없이 선택한 출발지 기준으로 안내합니다.';
                await this.service.render();
            },
            { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
        );
    }

    private applyExecutionCourse(execution: any) {
        this.executionCourse = execution.course || null;
        this.executionPlaces = Array.isArray(execution.places)
            ? execution.places.map((place: any, index: number) => this.normalizeExecutionSpot(place, index))
            : [];
        this.mapRouteOverviewActive = this.executionPlaces.length > 0
            && this.executionPlaces.every((place: any) => !!place.visited);
        this.executionRouteSummary = '';
        this.executionRouteError = '';
        this.executionNavigationSteps = [];
        this.syncExecutionCategories();
        this.selectedMapSpotId = '';
        let first = this.executionPlaces.find((spot: any) => this.isFiniteNumber(spot.lat) && this.isFiniteNumber(spot.lng));
        if (first) {
            this.selectedFilters.location = this.executionCourse && this.executionCourse.region ? this.executionCourse.region : first.location;
        }
        this.refreshExecutionSpotProjection();
    }

    private normalizeExecutionSpot(place: any, index: number) {
        let category = place.category_key || this.savedPlaceTypeKey(place);
        let location = place.address || (this.executionCourse && this.executionCourse.region) || '';
        let lat = this.safeNumber(place.lat);
        let lng = this.safeNumber(place.lng);
        let projected = lat !== null && lng !== null ? this.projectMapPosition(lat, lng) : { x: 50, y: 50 };
        return {
            id: place.place_id || `execution-${index + 1}`,
            place_id: place.place_id || `execution-${index + 1}`,
            order: Number(place.order || index + 1),
            name: place.name || '장소',
            kind: place.category_label || place.category || '장소',
            category,
            categoryLabel: place.category_label || place.category || '장소',
            icon: place.icon || this.executionCategoryIcon(category),
            x: projected.x,
            y: projected.y,
            lat,
            lng,
            rating: place.rating || '',
            hours: place.hours || '확인 필요',
            visit_time: place.visit_time || '', day: place.day || 1, date: place.date || '', phone: place.phone || '',
            description: place.memo || place.address || '코스에 포함된 장소입니다.',
            times: { transit: '', walk: '', car: '' },
            photoClass: this.executionPhotoClass(category),
            visited: !!place.checked,
            checked_at: place.checked_at || '',
            checkin_method: place.checkin_method || '',
            location,
            address: place.address || ''
        };
    }

    private syncExecutionCategories() {
        if (this.executionPlaces.length === 0) return;
        let next: any = {};
        this.executionPlaces.forEach((spot: any) => {
            next[spot.category] = typeof this.activeMapCategories[spot.category] === 'boolean'
                ? this.activeMapCategories[spot.category]
                : true;
        });
        this.activeMapCategories = next;
    }

    private refreshExecutionSpotProjection() {
        if (this.executionPlaces.length === 0) return;
        this.executionPlaces = this.executionPlaces.map((spot: any) => {
            if (!this.isFiniteNumber(spot.lat) || !this.isFiniteNumber(spot.lng)) return spot;
            let projected = this.projectMapPosition(Number(spot.lat), Number(spot.lng));
            return {
                ...spot,
                x: projected.x,
                y: projected.y,
            };
        });
    }

    public mapCategoriesForExecution() {
        if (this.executionPlaces.length === 0) return this.mapCategories;
        let seen: any = {};
        return this.executionPlaces
            .filter((spot: any) => {
                if (!spot.category || seen[spot.category]) return false;
                seen[spot.category] = true;
                return true;
            })
            .map((spot: any) => ({
                key: spot.category,
                label: spot.categoryLabel || spot.kind || '장소',
                icon: spot.icon || this.executionCategoryIcon(spot.category),
            }));
    }

    public isMapSpotDimmed(spot: any) {
        if (!this.executionCourse) return false;
        return this.activeMapCategories[spot.category] === false;
    }

    public executionCourseTitle() {
        if (this.executionCourse && this.executionCourse.title) return this.executionCourse.title;
        return '코스를 선택해주세요';
    }

    public mapSummaryLine() {
        let base = this.executionCourse && this.executionCourse.region ? this.executionCourse.region : '국내';
        let minutes = this.executionTotalMinutes > 0 ? `총 ${this.executionTotalMinutes}분` : '시간 계산 중';
        let distance = this.executionTotalDistanceMeters > 0 ? ` · ${this.formatPlannerDistance(this.executionTotalDistanceMeters, false)}` : '';
        return `${base} 추천 동선 · ${this.getTravelModeLabel()} · ${this.mapProgressText()} · ${this.mapStartStatus} · ${minutes}${distance}`;
    }

    public mapRuntimeLine() {
        let google = this.naverMapReady
            ? '네이버 지도 연결됨'
            : (this.naverMapsClientId ? '네이버 지도 확인 중' : '네이버 지도 키 없음');
        let gps = this.mapGpsDenied
            ? 'GPS 권한 거부'
            : (this.naverUserCoordinate ? 'GPS 연결됨' : 'GPS 확인 중');
        let geofence = this.mapGeoWatchId
            ? '자동 체크인 감지 중'
            : (this.mapGpsDenied ? '수동 체크인 모드' : '자동 체크인 대기');
        return `${google} · ${gps} · ${geofence}`;
    }

    public openMapSearchSuggestions() {
        this.mapSearchFocused = true;
        this.scheduleMapSearchSuggestionLoad();
    }

    public onMapSearchInput() {
        this.mapSearchFocused = true;
        if (this.mapCoursePickerOpen && this.stagedExecutionCourseId && !this.executionCourse) {
            this.mapStartCoordinate = null;
            this.executionLiveOrigin = null;
            this.naverSearchCoordinate = null;
            this.mapStartStatus = '장소 선택 전';
        }
        this.scheduleMapSearchSuggestionLoad();
    }

    public deferCloseMapSearchSuggestions() {
        if (typeof window === 'undefined') {
            this.mapSearchFocused = false;
            return;
        }
        window.setTimeout(async () => {
            this.mapSearchFocused = false;
            await this.service.render();
        }, 120);
    }

    public async closeMapSearchSuggestions() {
        if (!this.mapSearchFocused) return;
        this.mapSearchFocused = false;
        await this.service.render();
    }

    public showMapSearchSuggestions() {
        return !this.executionCourse
            && !this.mapCoursePickerOpen
            && this.mapSearchFocused
            && this.mapSearchQuery().length > 0
            && this.mapSearchSuggestions().length > 0;
    }

    public showMapDepartureSuggestions() {
        return this.mapCoursePickerOpen
            && !!this.stagedExecutionCourseId
            && !this.executionCourse
            && this.mapSearchFocused
            && this.mapSearchQuery().length > 0
            && this.mapSearchSuggestions().length > 0;
    }

    public mapSearchSuggestions() {
        let query = this.mapSearchQuery();
        if (!query) return [];

        let candidates: any[] = [];
        let appendNaver = () => (this.mapSearchNaverSuggestions || [])
            .forEach((place: any) => candidates.push(this.mapSearchSuggestionFromPlace(place, '네이버')));
        let appendRemote = () => (this.mapSearchRemoteSuggestions || [])
            .forEach((place: any) => candidates.push(this.mapSearchSuggestionFromPlace(place, '장소')));
        let appendLocal = () => {
            this.savedPlaces().forEach((place: any) => candidates.push(this.mapSearchSuggestionFromPlace(place, '저장 장소')));
            this.mapSpotsForLocation().forEach((spot: any) => candidates.push(this.mapSearchSuggestionFromPlace(spot, '근처 장소')));
            Object.keys(this.mapSpotMap || {}).forEach((key: string) => {
                ((this.mapSpotMap[key] || []) as any[]).forEach((spot: any) => candidates.push(this.mapSearchSuggestionFromPlace(spot, '추천 장소')));
            });
        };
        appendNaver();
        appendRemote();
        appendLocal();

        let seen: any = {};
        return candidates
            .filter((item: any) => !!item && this.mapSearchSuggestionMatches(item, query))
            .filter((item: any) => {
                let key = `${item.name}|${item.location || ''}`.toLowerCase();
                if (seen[key]) return false;
                seen[key] = true;
                return true;
            })
            .slice(0, 5);
    }

    public async selectMapSearchSuggestion(suggestion: any, event?: any) {
        if (event && event.preventDefault) event.preventDefault();
        if (event && event.stopPropagation) event.stopPropagation();
        if (!suggestion) return;

        this.mapStartAddress = suggestion.name || '';
        this.pendingMapSearchSuggestion = suggestion;
        this.mapSearchFocused = false;
        this.mapSearchRemoteSuggestions = [];
        this.mapSearchNaverSuggestions = [];
        this.recordRecentPlace({
            id: suggestion.id || `search-${suggestion.name}`,
            name: suggestion.name,
            kind: suggestion.kind || '장소',
            category: suggestion.category || '',
            icon: suggestion.icon || 'fa-location-dot',
            location: suggestion.location || this.getMapLocationLabel(),
            address: suggestion.address || '',
            description: suggestion.address || suggestion.meta || '',
            coordinate: suggestion.coordinate || null,
            lat: suggestion.coordinate ? suggestion.coordinate.lat : '',
            lng: suggestion.coordinate ? suggestion.coordinate.lng : ''
        });

        if (suggestion.coordinate) {
            this.pendingMapSearchSuggestion = null;
            let status = this.mapCoursePickerOpen && this.stagedExecutionCourseId ? '출발지 설정됨' : '검색 위치';
            await this.applyMapSearchCoordinateForContext(suggestion.coordinate, status, suggestion);
            return;
        }

        await this.setStartFromAddress();
    }

    public async setStartFromAddress() {
        let address = String(this.mapStartAddress || '').trim();
        let suggestion = this.pendingMapSearchSuggestion;
        this.pendingMapSearchSuggestion = null;
        this.mapSearchFocused = false;
        if (!address) {
            this.requestMapGpsLocation(true);
            return;
        }
        let google = await this.loadNaverMapsScript();
        if (!google || !google.maps || !google.maps.Geocoder) {
            this.mapStartStatus = '주소 변환 실패';
            await this.service.render();
            return;
        }
        let geocoder = new google.maps.Geocoder();
        let placeId = String(suggestion && suggestion.place_id || '').trim();
        let lookupAddress = suggestion
            ? [suggestion.name, suggestion.address || suggestion.location].filter((value: string) => !!String(value || '').trim()).join(' ')
            : this.mapDepartureSearchText(address);
        let request = placeId
            ? { placeId }
            : { address: lookupAddress, componentRestrictions: { country: 'KR' } };
        let searchCenter = this.naverUserCoordinate || this.currentMapCenter();
        if (!placeId && google.maps.LatLngBounds && searchCenter
            && this.isFiniteNumber(searchCenter.lat) && this.isFiniteNumber(searchCenter.lng)) {
            request['bounds'] = new google.maps.LatLngBounds(
                { lat: Number(searchCenter.lat) - 0.5, lng: Number(searchCenter.lng) - 0.5 },
                { lat: Number(searchCenter.lat) + 0.5, lng: Number(searchCenter.lng) + 0.5 }
            );
            request['region'] = 'KR';
        }
        geocoder.geocode(request, async (results: any[], status: string) => {
            if (status === 'OK' && results && results[0]) {
                let location = results[0].geometry.location;
                let coordinate = { lat: location.lat(), lng: location.lng() };
                let formattedAddress = String(results[0].formatted_address || '').trim();
                let resolvedSuggestion = suggestion
                    ? {
                        ...suggestion,
                        place_id: String(results[0].place_id || placeId || '').trim(),
                        address: formattedAddress || suggestion.address || '',
                        location: formattedAddress || suggestion.location || '',
                        coordinate
                    }
                    : {
                        name: address,
                        place_id: String(results[0].place_id || '').trim(),
                        address: formattedAddress || address,
                        location: formattedAddress
                    };
                let startStatus = this.executionCourse || (this.mapCoursePickerOpen && this.stagedExecutionCourseId)
                    ? '출발지 설정됨'
                    : '검색 위치';
                await this.applyMapSearchCoordinateForContext(coordinate, startStatus, resolvedSuggestion);
                return;
            }
            this.mapStartStatus = '주소 변환 실패';
            await this.service.render();
        });
    }

    public async chooseCurrentMapStart() {
        if (this.mapDepartureLoading) return;
        this.mapDepartureLoading = true;
        await this.service.render();
        if (typeof navigator === 'undefined' || !navigator.geolocation) {
            this.mapDepartureLoading = false;
            this.mapGpsDenied = true;
            this.mapStartStatus = '현재 위치를 사용할 수 없어요';
            await this.service.render();
            return;
        }

        navigator.geolocation.getCurrentPosition(
            async (position: any) => {
                let coordinate = { lat: position.coords.latitude, lng: position.coords.longitude };
                this.naverUserCoordinate = coordinate;
                this.naverSearchCoordinate = null;
                this.mapStartAddress = '현재 위치';
                this.mapGpsDenied = false;
                this.mapDepartureLoading = false;
                await this.applyMapSearchCoordinate(coordinate, '현재 위치에서 출발', true);
            },
            async () => {
                this.mapDepartureLoading = false;
                this.mapGpsDenied = true;
                this.mapStartStatus = '위치 권한이 필요해요';
                await this.service.render();
            },
            { enableHighAccuracy: true, timeout: 7000, maximumAge: 120000 }
        );
    }

    private mapSearchQuery() {
        return String(this.mapStartAddress || '').trim().toLowerCase();
    }

    private mapDepartureSearchText(query: string) {
        return String(query || '').trim();
    }

    private scheduleMapSearchSuggestionLoad() {
        if (this.mapSearchTimer) clearTimeout(this.mapSearchTimer);
        let query = this.mapSearchQuery();
        if (!query) {
            this.mapSearchRemoteSuggestions = [];
            this.mapSearchNaverSuggestions = [];
            return;
        }
        if (typeof window === 'undefined') {
            this.loadMapSearchRemoteSuggestions(query);
            this.loadNaverMapSearchSuggestions(query);
            return;
        }
        this.mapSearchTimer = window.setTimeout(() => {
            this.loadMapSearchRemoteSuggestions(query);
            this.loadNaverMapSearchSuggestions(query);
        }, 220);
    }

    private async loadMapSearchRemoteSuggestions(query: string) {
        if (!query || query !== this.mapSearchQuery()) return;
        this.mapSearchSuggestionLoading = true;
        try {
            let selectedRegion = this.mapCoursePickerOpen ? '' : String(this.getMapLocationLabel() || '').trim();
            if (['국내', '전국', '나의 여행'].indexOf(selectedRegion) > -1) selectedRegion = '';
            let center = this.currentMapCenter();
            let parameters = {
                lat: center.lat,
                lng: center.lng,
                keyword: query,
                region: selectedRegion && selectedRegion !== '전국' ? selectedRegion : '',
                limit: 8
            };
            const response: any = await wiz.call('search_course_places', parameters);
            if (query === this.mapSearchQuery()) {
                this.mapSearchRemoteSuggestions = response && response.code === 200 && response.data && Array.isArray(response.data.rows)
                    ? response.data.rows
                    : [];
            }
        } catch (e) {
            if (query === this.mapSearchQuery()) this.mapSearchRemoteSuggestions = [];
        }
        this.mapSearchSuggestionLoading = false;
        if (this.mapSearchFocused) await this.service.render();
    }

    private async loadNaverMapSearchSuggestions(query: string) {
        if (!query || query !== this.mapSearchQuery()) return;
        let naverMaps = await this.loadNaverMapsScript();
        if (query !== this.mapSearchQuery()) return;
        await this.loadNaverGeocodeMapSearchSuggestions(query, naverMaps);
    }

    private async loadNaverGeocodeMapSearchSuggestions(query: string, naverMaps: any) {
        if (!naverMaps || !naverMaps.maps || typeof naverMaps.maps.Geocoder !== 'function') {
            if (query === this.mapSearchQuery()) this.mapSearchNaverSuggestions = [];
            return;
        }
        let geocoder = new naverMaps.maps.Geocoder();
        geocoder.geocode({
            address: this.mapDepartureSearchText(query)
        }, async (results: any[], status: string) => {
            if (query !== this.mapSearchQuery()) return;
            this.mapSearchNaverSuggestions = status === 'OK' && Array.isArray(results)
                ? results.slice(0, 5).map((place: any) => {
                    let location = place && place.geometry && place.geometry.location ? place.geometry.location : null;
                    return {
                        id: place.place_id || place.formatted_address,
                        place_id: place.place_id || '',
                        name: place.formatted_address || query,
                        title: place.formatted_address || query,
                        area: 'NAVER 지도',
                        category: '장소',
                        address: place.formatted_address || '',
                        lat: location && typeof location.lat === 'function' ? location.lat() : '',
                        lng: location && typeof location.lng === 'function' ? location.lng() : '',
                        icon: 'fa-location-dot'
                    };
                })
                : [];
            if (this.mapSearchFocused) await this.service.render();
        });
    }

    private mapSearchSuggestionFromPlace(place: any, sourceLabel: string) {
        let name = String(place && (place.name || place.title) || '').trim();
        if (!name) return null;
        let lat = this.safeNumber(place.lat || place.latitude);
        let lng = this.safeNumber(place.lng || place.longitude);
        let location = String(place.location || place.area || this.getMapLocationLabel() || '').trim();
        let kind = String(place.kind || place.category || '장소').trim();
        let address = String(place.address || place.summary || place.description || '').trim();
        let meta = [location, kind].filter((item: string) => !!item && item !== '국내').join(' · ');
        if (!meta) meta = sourceLabel;
        let providerPlaceId = String(
            place.provider_place_id || place.providerPlaceId || ''
        ).trim();
        return {
            id: String(place.id || place.place_id || name).trim(),
            place_id: providerPlaceId,
            name,
            kind,
            category: place.category || '',
            icon: place.icon || this.mapSearchSuggestionIcon(place),
            location,
            address,
            searchQuery: String(place.search_query || '').trim(),
            meta,
            coordinate: lat !== null && lng !== null ? { lat, lng } : null
        };
    }

    private mapSearchSuggestionMatches(suggestion: any, query: string) {
        let searchable = [
            suggestion.name,
            suggestion.location,
            suggestion.kind,
            suggestion.category,
            suggestion.address,
            suggestion.searchQuery,
            suggestion.meta
        ].join(' ').toLowerCase();
        return searchable.indexOf(query) > -1;
    }

    private mapSearchSuggestionIcon(place: any) {
        let key = this.savedPlaceTypeKey(place);
        let filter = this.savedPlaceFilters.find((item: any) => item.key === key);
        return filter ? filter.icon : 'fa-location-dot';
    }

    private async applyMapSearchCoordinateForContext(coordinate: any, status: string, suggestion?: any) {
        if (this.executionCourse || this.mapCoursePickerOpen) {
            await this.applyMapSearchCoordinate(coordinate, status);
            return;
        }

        let endpoint = this.mapPlaceEndpoint(suggestion, coordinate);
        if (this.mapPlaceRouteOpen && this.mapPlaceRouteSearchTarget === 'origin') {
            this.mapPlaceRouteOrigin = endpoint;
            await this.finishMapPlaceEndpointSearch();
            return;
        }
        if (this.mapPlaceRouteOpen && this.mapPlaceRouteSearchTarget === 'destination') {
            this.mapPlaceRouteDestination = endpoint;
            this.mapSelectedPlace = this.mapPlaceFromEndpoint(endpoint, suggestion);
            this.naverSearchCoordinate = endpoint.coordinate;
            await this.finishMapPlaceEndpointSearch();
            return;
        }

        this.mapSelectedPlace = this.mapPlaceFromEndpoint(endpoint, suggestion);
        this.mapPlaceRouteDestination = endpoint;
        this.mapPlaceRouteTravelMode = 'transit';
        this.mapPlaceNavigationActive = false;
        this.mapPlaceRouteOpen = false;
        this.mapPlaceRouteSearchTarget = '';
        this.mapPlaceRouteSummary = '';
        this.mapPlaceRouteSteps = [];
        this.mapPlaceRouteError = '';
        this.naverSearchCoordinate = endpoint.coordinate;
        this.mapStartCoordinate = null;
        this.mapStartStatus = '장소 선택됨';
        this.naverCenterOnUser = false;
        this.selectedMapSpotId = '';
        this.panMapToCoordinate(endpoint.coordinate);
        await this.service.render();
        this.scheduleNaverMapRender();
    }

    private mapPlaceEndpoint(suggestion: any, coordinate: any) {
        let label = String((suggestion && (suggestion.name || suggestion.title)) || this.mapStartAddress || '선택한 장소').trim();
        let address = String((suggestion && (suggestion.address || suggestion.location || suggestion.meta)) || '').trim();
        return {
            label: label || '선택한 장소',
            address,
            coordinate: { lat: Number(coordinate.lat), lng: Number(coordinate.lng) }
        };
    }

    private mapPlaceFromEndpoint(endpoint: any, suggestion?: any) {
        return {
            id: String((suggestion && suggestion.id) || `map-place-${endpoint.coordinate.lat}-${endpoint.coordinate.lng}`),
            name: endpoint.label,
            address: endpoint.address,
            location: String((suggestion && suggestion.location) || endpoint.address || ''),
            kind: String((suggestion && (suggestion.kind || suggestion.category)) || '장소'),
            icon: String((suggestion && suggestion.icon) || 'fa-location-dot'),
            coordinate: endpoint.coordinate
        };
    }

    public async openMapPlaceRoute() {
        if (!this.mapSelectedPlace || !this.mapSelectedPlace.coordinate) return;
        this.stopMapPlaceNavigationTracking();
        this.mapPlaceRouteOpen = true;
        this.mapPlaceRouteSheetCollapsed = false;
        this.mapPlaceRouteTravelMode = 'transit';
        this.mapPlaceRouteSearchTarget = '';
        this.mapPlaceRouteDestination = this.mapPlaceEndpoint(this.mapSelectedPlace, this.mapSelectedPlace.coordinate);
        this.mapPlaceRouteSummary = '';
        this.mapPlaceRouteSteps = [];
        this.mapPlaceRouteAlternatives = [];
        this.mapPlaceRouteSelectedIndex = 0;
        this.mapPlaceNaverRoutes = [];
        this.mapPlaceRouteError = '';
        this.mapPlaceGuidanceExpanded = false;
        this.mapPlaceRouteUpdatedAt = '';
        this.mapPlaceRouteProvider = 'estimate';
        let current = this.naverUserCoordinate;
        if (current && this.isFiniteNumber(current.lat) && this.isFiniteNumber(current.lng)) {
            this.mapPlaceRouteOrigin = {
                label: '내 위치',
                address: '현재 위치',
                coordinate: { lat: Number(current.lat), lng: Number(current.lng) }
            };
        } else {
            this.mapPlaceRouteOrigin = null;
            this.requestMapPlaceCurrentOrigin();
        }
        this.mapStartAddress = '';
        await this.service.render();
        this.scheduleNaverMapRender();
    }

    public async closeMapPlaceRoute() {
        if (this.mapPlaceRouteDestination && this.mapPlaceRouteDestination.coordinate) {
            this.mapSelectedPlace = this.mapPlaceFromEndpoint(this.mapPlaceRouteDestination);
            this.naverSearchCoordinate = this.mapPlaceRouteDestination.coordinate;
        }
        this.mapPlaceRouteOpen = false;
        this.mapPlaceRouteSheetCollapsed = false;
        this.resetMapPlaceRouteSheetDrag();
        this.mapPlaceNavigationActive = false;
        this.stopMapPlaceNavigationTracking();
        this.mapPlaceRouteSearchTarget = '';
        this.mapPlaceGuidanceExpanded = false;
        this.mapSearchFocused = false;
        this.mapStartAddress = this.mapSelectedPlace ? this.mapSelectedPlace.name : '';
        await this.service.render();
        this.scheduleNaverMapRender();
    }

    public async closeMapSelectedPlace() {
        this.mapSelectedPlace = null;
        this.mapPlaceRouteDestination = null;
        this.naverSearchCoordinate = null;
        this.mapStartAddress = '';
        await this.service.render();
        this.scheduleNaverMapRender();
    }

    public async startMapPlaceEndpointSearch(target: string) {
        if (target !== 'origin' && target !== 'destination') return;
        this.mapPlaceRouteSearchTarget = target;
        this.mapStartAddress = '';
        this.mapSearchRemoteSuggestions = [];
        this.mapSearchNaverSuggestions = [];
        this.mapSearchFocused = true;
        await this.service.render();
    }

    public mapPlaceEndpointSearchPlaceholder() {
        return this.mapPlaceRouteSearchTarget === 'origin' ? '출발 장소 검색' : '도착 장소 검색';
    }

    public async finishMapPlaceEndpointSearch() {
        this.mapPlaceNavigationActive = false;
        this.mapPlaceRouteTravelMode = 'transit';
        this.stopMapPlaceNavigationTracking();
        this.mapPlaceRouteSearchTarget = '';
        this.mapSearchFocused = false;
        this.mapSearchRemoteSuggestions = [];
        this.mapSearchNaverSuggestions = [];
        this.mapStartAddress = '';
        this.mapPlaceRouteSummary = '';
        this.mapPlaceRouteSteps = [];
        this.mapPlaceRouteAlternatives = [];
        this.mapPlaceRouteSelectedIndex = 0;
        this.mapPlaceNaverRoutes = [];
        this.mapPlaceRouteError = '';
        this.mapPlaceRouteUpdatedAt = '';
        this.mapPlaceRouteProvider = 'estimate';
        await this.service.render();
        this.scheduleNaverMapRender();
    }

    public async swapMapPlaceRouteEndpoints() {
        if (!this.mapPlaceRouteOrigin || !this.mapPlaceRouteDestination) return;
        this.mapPlaceNavigationActive = false;
        this.stopMapPlaceNavigationTracking();
        let previousOrigin = this.mapPlaceRouteOrigin;
        this.mapPlaceRouteOrigin = this.mapPlaceRouteDestination;
        this.mapPlaceRouteDestination = previousOrigin;
        this.naverSearchCoordinate = this.mapPlaceRouteDestination.coordinate;
        this.mapPlaceRouteSummary = '';
        this.mapPlaceRouteSteps = [];
        this.mapPlaceRouteAlternatives = [];
        this.mapPlaceRouteSelectedIndex = 0;
        this.mapPlaceNaverRoutes = [];
        this.mapPlaceRouteError = '';
        this.mapPlaceRouteUpdatedAt = '';
        this.mapPlaceRouteProvider = 'estimate';
        await this.service.render();
        this.scheduleNaverMapRender();
    }

    public async setMapPlaceTravelMode(mode: string) {
        if (!this.mapPlaceTravelModes.some((item: any) => item.key === mode)) return;
        this.mapPlaceNavigationActive = false;
        this.stopMapPlaceNavigationTracking();
        this.mapPlaceRouteTravelMode = mode;
        this.mapPlaceRouteSheetCollapsed = false;
        this.mapPlaceRouteSummary = '';
        this.mapPlaceRouteSteps = [];
        this.mapPlaceRouteAlternatives = [];
        this.mapPlaceRouteSelectedIndex = 0;
        this.mapPlaceNaverRoutes = [];
        this.mapPlaceRouteError = '';
        this.mapPlaceRouteUpdatedAt = '';
        this.mapPlaceRouteProvider = 'estimate';
        await this.service.render();
        this.scheduleNaverMapRender();
    }

    public async toggleMapPlaceGuidance() {
        this.mapPlaceGuidanceExpanded = !this.mapPlaceGuidanceExpanded;
        await this.service.render();
    }

    public async refreshMapPlaceRoute(event?: any) {
        if (event && event.preventDefault) event.preventDefault();
        if (this.mapPlaceRouteRefreshLoading || !this.mapPlaceRouteOrigin || !this.mapPlaceRouteDestination) return;
        this.mapPlaceRouteRefreshLoading = true;
        this.mapPlaceRouteLoading = true;
        this.mapPlaceRouteError = '';
        await this.service.render();
        this.scheduleNaverMapRender();
    }

    public startMapPlaceRouteSheetDrag(event: any) {
        let y = this.mapPlaceRoutePointerY(event);
        if (y === null) return;
        if (event && event.stopPropagation) event.stopPropagation();
        if (event && event.preventDefault) event.preventDefault();
        let handle = event && event.currentTarget;
        let sheet = handle && handle.parentElement;
        if (!sheet) return;
        if (this.mapPlaceRouteSheetSettleTimer) {
            clearTimeout(this.mapPlaceRouteSheetSettleTimer);
            this.mapPlaceRouteSheetSettleTimer = null;
        }
        this.mapPlaceRouteSheetElement = sheet;
        this.mapPlaceRouteSheetDragHeight = Math.max(0, Number(sheet && sheet.offsetHeight || 0));
        this.mapPlaceRouteSheetDragStartY = y;
        this.mapPlaceRouteSheetDragLastY = y;
        this.mapPlaceRouteSheetDragLastAt = Date.now();
        this.mapPlaceRouteSheetDragBaseY = this.mapPlaceRouteSheetCollapsed
            ? this.mapPlaceRouteSheetDragHeight
            : 0;
        this.mapPlaceRouteSheetDragOffsetY = this.mapPlaceRouteSheetDragBaseY;
        this.mapPlaceRouteSheetDragging = true;
        sheet.classList.add('dragging');
        sheet.style.animation = 'none';
        sheet.style.transition = 'none';
        sheet.style.transform = `translate3d(0, ${Math.round(this.mapPlaceRouteSheetDragBaseY)}px, 0)`;
        if (handle && handle.setPointerCapture && event.pointerId !== undefined) {
            try { handle.setPointerCapture(event.pointerId); } catch (e) { }
        }
    }

    public moveMapPlaceRouteSheetDrag(event: any) {
        if (!this.mapPlaceRouteSheetDragging) return;
        let y = this.mapPlaceRoutePointerY(event);
        if (y === null) return;
        if (event && event.stopPropagation) event.stopPropagation();
        if (event && event.preventDefault) event.preventDefault();
        let maximum = Math.max(0, this.mapPlaceRouteSheetDragHeight);
        let next = this.mapPlaceRouteSheetDragBaseY + (y - this.mapPlaceRouteSheetDragStartY);
        this.mapPlaceRouteSheetDragOffsetY = Math.max(0, Math.min(maximum, next));
        this.mapPlaceRouteSheetDragLastY = y;
        this.mapPlaceRouteSheetDragLastAt = Date.now();
        if (this.mapPlaceRouteSheetElement) {
            this.mapPlaceRouteSheetElement.style.transform = `translate3d(0, ${Math.round(this.mapPlaceRouteSheetDragOffsetY)}px, 0)`;
        }
    }

    public finishMapPlaceRouteSheetDrag(event?: any) {
        if (!this.mapPlaceRouteSheetDragging) return;
        if (event && event.stopPropagation) event.stopPropagation();
        let releasedY = this.mapPlaceRoutePointerY(event);
        let elapsed = Math.max(1, Date.now() - this.mapPlaceRouteSheetDragLastAt);
        let velocity = releasedY === null ? 0 : (releasedY - this.mapPlaceRouteSheetDragLastY) / elapsed;
        let moved = this.mapPlaceRouteSheetDragOffsetY - this.mapPlaceRouteSheetDragBaseY;
        let collapseBoundary = Math.max(56, this.mapPlaceRouteSheetDragHeight * 0.28);
        let collapsed = velocity > 0.45 || moved > 44
            ? true
            : velocity < -0.45 || moved < -36
                ? false
                : this.mapPlaceRouteSheetDragOffsetY >= collapseBoundary;
        this.settleMapPlaceRouteSheet(collapsed);
    }

    public expandMapPlaceRouteSheet() {
        if (!this.mapPlaceRouteSheetCollapsed) return;
        this.settleMapPlaceRouteSheet(false);
    }

    public toggleMapPlaceRouteSheetCollapsed(event?: any) {
        if (event && event.preventDefault) event.preventDefault();
        if (event && event.stopPropagation) event.stopPropagation();
        this.settleMapPlaceRouteSheet(!this.mapPlaceRouteSheetCollapsed);
    }

    private mapPlaceRoutePointerY(event: any): number | null {
        if (event && event.touches && event.touches[0]) return Number(event.touches[0].clientY);
        if (event && event.changedTouches && event.changedTouches[0]) return Number(event.changedTouches[0].clientY);
        if (event && Number.isFinite(Number(event.clientY))) return Number(event.clientY);
        return null;
    }

    private settleMapPlaceRouteSheet(collapsed: boolean) {
        let sheet = this.mapPlaceRouteSheetElement;
        let height = this.mapPlaceRouteSheetDragHeight || Math.max(0, Number(sheet && sheet.offsetHeight || 0));
        let current = this.mapPlaceRouteSheetDragging
            ? this.mapPlaceRouteSheetDragOffsetY
            : (this.mapPlaceRouteSheetCollapsed ? height : 0);
        let target = collapsed ? height : 0;
        this.mapPlaceRouteSheetCollapsed = collapsed;
        this.mapPlaceRouteSheetDragging = false;
        if (!sheet) {
            this.resetMapPlaceRouteSheetDrag(false);
            return;
        }
        sheet.classList.remove('dragging');
        sheet.classList.toggle('collapsed', collapsed);
        sheet.style.animation = 'none';
        sheet.style.transition = 'none';
        sheet.style.transform = `translate3d(0, ${Math.round(current)}px, 0)`;
        sheet.getBoundingClientRect();
        sheet.style.transition = 'transform 280ms cubic-bezier(0.22, 1, 0.36, 1)';
        sheet.style.transform = `translate3d(0, ${Math.round(target)}px, 0)`;
        this.mapPlaceRouteSheetSettleTimer = setTimeout(() => {
            if (sheet === this.mapPlaceRouteSheetElement) {
                sheet.style.animation = '';
                sheet.style.transition = '';
                sheet.style.transform = '';
            }
            this.mapPlaceRouteSheetElement = null;
            this.mapPlaceRouteSheetSettleTimer = null;
            this.resetMapPlaceRouteSheetDrag(false);
        }, 300);
    }

    private resetMapPlaceRouteSheetDrag(clearElement: boolean = true) {
        if (clearElement && this.mapPlaceRouteSheetSettleTimer) {
            clearTimeout(this.mapPlaceRouteSheetSettleTimer);
            this.mapPlaceRouteSheetSettleTimer = null;
        }
        if (clearElement && this.mapPlaceRouteSheetElement) {
            this.mapPlaceRouteSheetElement.classList.remove('dragging');
            this.mapPlaceRouteSheetElement.style.animation = '';
            this.mapPlaceRouteSheetElement.style.transition = '';
            this.mapPlaceRouteSheetElement.style.transform = '';
            this.mapPlaceRouteSheetElement = null;
        }
        this.mapPlaceRouteSheetDragging = false;
        this.mapPlaceRouteSheetDragStartY = 0;
        this.mapPlaceRouteSheetDragLastY = 0;
        this.mapPlaceRouteSheetDragLastAt = 0;
        this.mapPlaceRouteSheetDragBaseY = 0;
        this.mapPlaceRouteSheetDragOffsetY = 0;
        this.mapPlaceRouteSheetDragHeight = 0;
    }

    public async selectMapPlaceRouteAlternative(index: number) {
        let selectedIndex = Number(index);
        if (!Number.isFinite(selectedIndex) || selectedIndex < 0 || selectedIndex >= this.mapPlaceNaverRoutes.length) return;
        this.mapPlaceRouteSelectedIndex = selectedIndex;
        let google = await this.loadNaverMapsScript();
        let route = this.mapPlaceNaverRoutes[selectedIndex];
        if (!route || !this.isNaverMapsReady(google)) return;
        if (route.provider === 'odsay') {
            this.applyOdsayTransitRoute(google, route, selectedIndex);
        } else {
            this.applyNaverPlaceRoute(google, route, selectedIndex);
        }
        await this.service.render();
    }

    public mapPlaceRouteFreshnessLabel() {
        if (this.mapPlaceRouteTravelMode === 'transit' && this.mapPlaceRouteProvider === 'odsay') {
            let updatedAt = this.mapPlaceRouteUpdatedAt ? `${this.mapPlaceRouteUpdatedAt} 조회 · ` : '';
            return `${updatedAt}ODsay 대중교통 노선 · 실시간 도착정보 미포함`;
        }
        if (this.mapPlaceRouteTravelMode !== 'driving') {
            let updatedAt = this.mapPlaceRouteUpdatedAt ? `${this.mapPlaceRouteUpdatedAt} 계산 · ` : '';
            return `${updatedAt}현재 화면의 예상 경로 · 실시간 운행정보 미반영`;
        }
        if (this.mapPlaceRouteRefreshLoading || this.mapPlaceRouteLoading) return '최신 운행정보 확인 중';
        if (!this.mapPlaceRouteUpdatedAt) return '운행정보 조회 전';
        return `${this.mapPlaceRouteUpdatedAt} 조회 · 운영사 제공 범위에 따라 실시간 또는 시간표 기준`;
    }

    public mapPlaceStepVisibleDetails(step: any) {
        let details = Array.isArray(step && step.metaLines) ? step.metaLines : [];
        return step && step.detailsExpanded ? details : details.slice(0, 2);
    }

    public mapPlaceStepHasMoreDetails(step: any) {
        return Array.isArray(step && step.metaLines) && step.metaLines.length > 2;
    }

    public async toggleMapPlaceStepDetails(step: any, event?: any) {
        if (event && event.preventDefault) event.preventDefault();
        if (event && event.stopPropagation) event.stopPropagation();
        if (!step) return;
        step.detailsExpanded = !step.detailsExpanded;
        await this.service.render();
    }

    public mapPlaceRouteOriginLabel() {
        return this.mapPlaceRouteOrigin ? this.mapPlaceRouteOrigin.label : '내 위치 확인 중';
    }

    public mapPlaceRouteDestinationLabel() {
        return this.mapPlaceRouteDestination ? this.mapPlaceRouteDestination.label : '도착지 선택';
    }

    public async openMapSelectedPlaceNavigation(event?: any) {
        if (event && event.preventDefault) event.preventDefault();
        if (!this.mapSelectedPlace || !this.mapSelectedPlace.coordinate) return;
        this.mapPlaceRouteTravelMode = 'transit';
        await this.openMapPlaceRoute();
    }

    public async openMapPlaceNavigation(event?: any) {
        if (event && event.preventDefault) event.preventDefault();
        if (this.mapPlaceNavigationActive) {
            this.mapPlaceNavigationActive = false;
            this.mapPlaceGuidanceExpanded = false;
            this.stopMapPlaceNavigationTracking();
            await this.service.render();
            return;
        }
        if (!this.mapPlaceRouteOrigin || !this.mapPlaceRouteDestination) {
            this.mapPlaceRouteError = '출발지를 선택하면 이 화면에서 경로 안내를 시작할 수 있어요.';
            await this.service.render();
            return;
        }
        if (this.mapPlaceRouteLoading || this.mapPlaceRouteSteps.length === 0) {
            this.mapPlaceRouteError = '경로를 계산하고 있어요. 잠시 후 다시 눌러주세요.';
            await this.service.render();
            this.scheduleNaverMapRender();
            return;
        }
        this.mapPlaceNavigationActive = true;
        this.mapPlaceGuidanceExpanded = true;
        this.mapPlaceRouteError = '';
        this.startMapPlaceNavigationTracking();
        await this.service.render();
        this.scheduleNaverMapRender();
    }

    public mapPlaceNavigationButtonLabel() {
        if (this.mapPlaceNavigationActive) return '경로 상세 닫기';
        if (this.mapPlaceRouteTravelMode === 'walking') return '도보 예상 경로 보기';
        if (this.mapPlaceRouteTravelMode === 'driving') return '차량 안내 시작';
        return this.mapPlaceRouteProvider === 'odsay' ? '대중교통 상세 경로 보기' : '대중교통 예상 경로 보기';
    }

    public mapPlaceRouteStartHintLabel() {
        if (this.mapPlaceRouteTravelMode === 'driving') return '경로 시작을 누르면 이동 단계를 자세히 볼 수 있어요.';
        if (this.mapPlaceRouteTravelMode === 'transit' && this.mapPlaceRouteProvider === 'odsay') {
            return '버스 번호·승하차 정류장·환승 순서를 이 화면에서 확인할 수 있어요.';
        }
        return '버튼을 누르면 이 화면에서 예상 시간·거리와 이동 단계를 볼 수 있어요.';
    }

    public mapPlaceNavigationStatusLabel() {
        if (!this.mapPlaceNavigationActive) {
            return this.mapPlaceRouteLoading ? '경로를 찾는 중' : (this.mapPlaceRouteSummary || '출발지를 선택해주세요');
        }
        if (this.mapPlaceRouteTravelMode === 'walking') return '도보 예상 경로';
        if (this.mapPlaceRouteTravelMode === 'driving') return '차량 경로 안내 중';
        return this.mapPlaceRouteProvider === 'odsay' ? '대중교통 상세 경로' : '대중교통 예상 경로';
    }

    private startMapPlaceNavigationTracking() {
        this.stopMapPlaceNavigationTracking();
        if (typeof navigator === 'undefined' || !navigator.geolocation) return;
        this.mapPlaceNavigationLastRefreshAt = Date.now();
        this.mapPlaceNavigationLastCoordinate = this.naverUserCoordinate || null;
        this.mapPlaceNavigationWatchId = navigator.geolocation.watchPosition(
            async (position: any) => {
                if (!this.mapPlaceNavigationActive) return;
                let coordinate = { lat: position.coords.latitude, lng: position.coords.longitude };
                let previous = this.mapPlaceNavigationLastCoordinate;
                let moved = previous
                    ? (this.distanceKm(previous.lat, previous.lng, coordinate.lat, coordinate.lng) || 0)
                    : 1;
                this.naverUserCoordinate = coordinate;
                this.naverCenterOnUser = false;
                this.mapGpsDenied = false;
                if (this.isMapPlaceCurrentLocationOrigin()) {
                    this.mapPlaceRouteOrigin = { label: '내 위치', address: '현재 위치', coordinate };
                }
                let elapsed = Date.now() - this.mapPlaceNavigationLastRefreshAt;
                if (this.isMapPlaceCurrentLocationOrigin() && moved >= 0.03 && elapsed >= 12000) {
                    this.mapPlaceNavigationLastCoordinate = coordinate;
                    this.mapPlaceNavigationLastRefreshAt = Date.now();
                    this.mapPlaceRouteLoading = true;
                    this.scheduleNaverMapRender();
                } else {
                    this.renderNaverUserMarker(coordinate);
                }
                await this.service.render();
            },
            async () => {
                this.mapGpsDenied = true;
                this.mapPlaceRouteError = '현재 위치를 갱신할 수 없어 마지막 위치 기준으로 안내합니다.';
                await this.service.render();
            },
            { enableHighAccuracy: true, timeout: 10000, maximumAge: 5000 }
        );
    }

    private stopMapPlaceNavigationTracking() {
        if (this.mapPlaceNavigationWatchId && typeof navigator !== 'undefined' && navigator.geolocation) {
            try { navigator.geolocation.clearWatch(this.mapPlaceNavigationWatchId); } catch (e) { }
        }
        this.mapPlaceNavigationWatchId = null;
        this.mapPlaceNavigationLastCoordinate = null;
        this.mapPlaceNavigationLastRefreshAt = 0;
    }

    private isMapPlaceCurrentLocationOrigin() {
        if (!this.mapPlaceRouteOrigin) return false;
        return this.mapPlaceRouteOrigin.label === '내 위치' || this.mapPlaceRouteOrigin.address === '현재 위치';
    }

    private requestMapPlaceCurrentOrigin() {
        if (typeof navigator === 'undefined' || !navigator.geolocation) {
            this.mapGpsDenied = true;
            this.mapPlaceRouteError = '현재 위치를 사용할 수 없어요. 출발지를 직접 선택해주세요.';
            this.service.render();
            return;
        }
        navigator.geolocation.getCurrentPosition(
            async (position: any) => {
                let coordinate = { lat: position.coords.latitude, lng: position.coords.longitude };
                this.naverUserCoordinate = coordinate;
                this.mapPlaceRouteOrigin = { label: '내 위치', address: '현재 위치', coordinate };
                this.mapGpsDenied = false;
                this.mapPlaceRouteError = '';
                await this.service.render();
                this.scheduleNaverMapRender();
            },
            async () => {
                this.mapGpsDenied = true;
                this.mapPlaceRouteError = '위치 권한이 없어요. 위의 출발지 바를 눌러 장소를 선택해주세요.';
                await this.service.render();
            },
            { enableHighAccuracy: true, timeout: 7000, maximumAge: 120000 }
        );
    }

    private async applyMapSearchCoordinate(coordinate: any, status: string, requiresGps: boolean = false) {
        this.naverSearchCoordinate = coordinate;
        this.mapStartCoordinate = coordinate;
        this.mapStartRequiresGps = requiresGps;
        if (this.executionCourse) this.executionLiveOrigin = coordinate;
        this.mapStartStatus = status;
        this.naverCenterOnUser = true;
        this.selectedMapSpotId = '';
        this.userPosition = this.projectMapPosition(coordinate.lat, coordinate.lng);
        if (this.naverMap) {
            this.panMapToCoordinate(coordinate);
            try {
                this.naverMap.setZoom(Math.max(Number(this.naverMap.getZoom && this.naverMap.getZoom()) || 15, 15));
                this.renderNaverSearchMarker(coordinate);
            } catch (e) { }
        }
        await this.service.render();
        this.scheduleNaverMapRender();
    }

    private panMapToCoordinate(coordinate: any) {
        if (!coordinate || !this.naverMap || !this.naverMap.panTo) return;
        try {
            this.naverMap.panTo(coordinate);
        } catch (e) { }
    }

    private requestMapGpsLocation(forceRender: boolean) {
        if (typeof navigator === 'undefined' || !navigator.geolocation) {
            this.mapGpsDenied = true;
            this.mapStartStatus = '수동 체크인만 가능';
            return;
        }
        navigator.geolocation.getCurrentPosition(
            async (position: any) => {
                let coordinate = { lat: position.coords.latitude, lng: position.coords.longitude };
                this.naverSearchCoordinate = null;
                this.mapStartCoordinate = coordinate;
                this.executionLiveOrigin = coordinate;
                this.naverUserCoordinate = coordinate;
                this.mapStartRequiresGps = true;
                this.userPosition = this.projectMapPosition(coordinate.lat, coordinate.lng);
                this.mapStartStatus = '현재 위치';
                this.mapGpsDenied = false;
                this.naverCenterOnUser = true;
                if (forceRender) await this.service.render();
                this.scheduleNaverMapRender();
            },
            async () => {
                this.mapGpsDenied = true;
                this.mapStartStatus = '수동 체크인만 가능';
                if (forceRender) await this.service.render();
            },
            { enableHighAccuracy: true, timeout: 5000, maximumAge: 120000 }
        );
    }

    private startExecutionGeofence() {
        if (typeof navigator === 'undefined' || !navigator.geolocation || this.executionPlaces.length === 0) return;
        if (this.mapGeoWatchId) {
            try { navigator.geolocation.clearWatch(this.mapGeoWatchId); } catch (e) { }
        }
        this.mapGeoWatchId = navigator.geolocation.watchPosition(
            (position: any) => this.handleExecutionPosition(position),
            async () => {
                this.mapGpsDenied = true;
                this.mapStartStatus = '수동 체크인만 가능';
                await this.service.render();
            },
            { enableHighAccuracy: true, timeout: 10000, maximumAge: 30000 }
        );
    }

    private async handleExecutionPosition(position: any, forceRouteRefresh: boolean = false) {
        let rawCoordinate = { lat: position.coords.latitude, lng: position.coords.longitude };
        let accuracy = Math.max(0, Number(position && position.coords && position.coords.accuracy || 0));
        let coordinate = this.filterExecutionNavigationCoordinate(rawCoordinate, accuracy);
        let previous = this.executionNavigationLastCoordinate;
        this.executionLiveOrigin = coordinate;
        this.naverUserCoordinate = coordinate;
        this.mapStartRequiresGps = true;
        this.userPosition = this.projectMapPosition(coordinate.lat, coordinate.lng);
        let target = this.executionNextSpot();
        let targetDistance = target && this.isFiniteNumber(target.lat) && this.isFiniteNumber(target.lng)
            ? this.distanceKm(coordinate.lat, coordinate.lng, target.lat, target.lng)
            : null;
        if (this.executionNavigationActive) {
            this.executionNavigationUsingLiveLocation = true;
            let progress = this.updateExecutionNavigationProgress(coordinate, position);
            let remainingMeters = progress && this.executionNavigationPathMeters > 0
                ? Math.max(0, this.executionNavigationPathMeters - progress.alongMeters)
                : (targetDistance === null ? null : Math.max(0, targetDistance) * 1000);
            let remaining = remainingMeters === null
                ? '남은 거리 계산 중'
                : `남은 ${this.formatPlannerDistance(remainingMeters, false)}`;
            let speed = Math.max(0, Number(position && position.coords && position.coords.speed || 0));
            let positionAt = Number(position && position.timestamp || Date.now());
            if (!speed && this.executionNavigationLastPositionCoordinate && this.executionNavigationLastPositionAt > 0) {
                let seconds = Math.max(1, (positionAt - this.executionNavigationLastPositionAt) / 1000);
                speed = Math.min(55, ((this.distanceKm(
                    this.executionNavigationLastPositionCoordinate.lat,
                    this.executionNavigationLastPositionCoordinate.lng,
                    coordinate.lat,
                    coordinate.lng
                ) || 0) * 1000) / seconds);
            }
            this.executionNavigationLastPositionCoordinate = coordinate;
            this.executionNavigationLastPositionAt = positionAt;
            let eta = remainingMeters !== null && this.executionNavigationPathMeters > 0 && this.executionTotalMinutes > 0
                ? `약 ${this.formatPlannerLegDuration(Math.max(60, this.executionTotalMinutes * 60 * remainingMeters / this.executionNavigationPathMeters))}`
                : '';
            this.executionNavigationRemainingMeters = remainingMeters;
            this.executionNavigationRemainingSeconds = remainingMeters !== null && this.executionNavigationPathMeters > 0 && this.executionTotalMinutes > 0
                ? Math.max(60, this.executionTotalMinutes * 60 * remainingMeters / this.executionNavigationPathMeters)
                : 0;
            this.executionNavigationSpeedKmh = Math.max(0, Math.round(speed * 3.6));
            this.executionNavigationStatus = [
                target ? `${target.order}. ${target.name}` : '코스 완료',
                remaining,
                eta,
                speed > 0.8 ? `${Math.round(speed * 3.6)}km/h` : '',
                progress && progress.offRoute ? `경로 이탈 ${Math.round(progress.distanceMeters)}m` : '',
                accuracy > 0 ? `GPS ±${Math.round(accuracy)}m` : ''
            ].filter((value: string) => !!value).join(' · ');
            this.naverCenterOnUser = true;
            let displayCoordinate = progress && progress.snappedCoordinate && !progress.offRoute
                ? progress.snappedCoordinate
                : coordinate;
            this.naverUserCoordinate = displayCoordinate;
            this.renderNaverUserMarker(displayCoordinate);
            this.followExecutionNavigationCamera(displayCoordinate, speed);
            let reroute = !!(progress && progress.shouldReroute);
            if (forceRouteRefresh || reroute) {
                this.executionNavigationLastCoordinate = coordinate;
                this.executionNavigationLastRefreshAt = Date.now();
                if (reroute) this.executionNavigationLastRerouteAt = Date.now();
                this.mapRouteLoading = true;
                this.scheduleNaverMapRender();
            }
            if (!this.executionWakeLock && typeof document !== 'undefined' && !document.hidden) this.requestExecutionWakeLock();
        }
        if (target && targetDistance !== null && targetDistance <= 0.08) {
            await this.checkInSpot(target, 'auto', coordinate);
            return;
        }
        await this.service.render();
    }

    private resetExecutionNavigationTracking() {
        this.executionNavigationPath = [];
        this.executionNavigationPathMeters = 0;
        this.executionNavigationStepIndex = 0;
        this.executionNavigationNextDistance = '';
        this.executionNavigationRemainingMeters = null;
        this.executionNavigationRemainingSeconds = 0;
        this.executionNavigationSpeedKmh = 0;
        this.executionNavigationFilteredCoordinate = null;
        this.executionNavigationHeading = null;
        this.executionNavigationOffRouteSince = 0;
        this.executionNavigationLastRerouteAt = 0;
        this.executionNavigationLastPositionAt = 0;
        this.executionNavigationLastPositionCoordinate = null;
    }

    private filterExecutionNavigationCoordinate(coordinate: any, accuracy: number) {
        let previous = this.executionNavigationFilteredCoordinate;
        if (!previous) {
            this.executionNavigationFilteredCoordinate = coordinate;
            return coordinate;
        }
        let movedMeters = (this.distanceKm(previous.lat, previous.lng, coordinate.lat, coordinate.lng) || 0) * 1000;
        if (movedMeters > 400) {
            this.executionNavigationFilteredCoordinate = coordinate;
            return coordinate;
        }
        let alpha = accuracy <= 15 ? 0.72 : (accuracy <= 50 ? 0.5 : 0.32);
        let filtered = {
            lat: previous.lat + (coordinate.lat - previous.lat) * alpha,
            lng: previous.lng + (coordinate.lng - previous.lng) * alpha
        };
        this.executionNavigationFilteredCoordinate = filtered;
        return filtered;
    }

    private normalizeNavigationCoordinate(point: any) {
        if (!point) return null;
        let lat = typeof point.lat === 'function' ? Number(point.lat()) : Number(point.lat);
        let lng = typeof point.lng === 'function' ? Number(point.lng()) : Number(point.lng);
        return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null;
    }

    private setExecutionNavigationRoute(path: any[]) {
        this.executionNavigationPath = (path || [])
            .map((point: any) => this.normalizeNavigationCoordinate(point))
            .filter((point: any) => !!point);
        this.executionNavigationPathMeters = 0;
        for (let index = 1; index < this.executionNavigationPath.length; index++) {
            let previous = this.executionNavigationPath[index - 1];
            let current = this.executionNavigationPath[index];
            this.executionNavigationPathMeters += (this.distanceKm(previous.lat, previous.lng, current.lat, current.lng) || 0) * 1000;
        }
        let steps = Array.isArray(this.executionNavigationSteps) ? this.executionNavigationSteps : [];
        let declaredTotal = steps.reduce((sum: number, step: any) => sum + Math.max(0, Number(step.navigationDistanceMeters || 0)), 0);
        let fallback = steps.length > 0 ? Math.max(1, this.executionNavigationPathMeters / steps.length) : 0;
        let cursor = 0;
        steps.forEach((step: any) => {
            let distance = Math.max(0, Number(step.navigationDistanceMeters || 0));
            if (declaredTotal <= 0 || distance <= 0) distance = fallback;
            cursor += distance;
            step.navigationEndMeters = cursor;
        });
        if (cursor > 0 && this.executionNavigationPathMeters > 0) {
            steps.forEach((step: any) => step.navigationEndMeters = step.navigationEndMeters / cursor * this.executionNavigationPathMeters);
        }
        this.executionNavigationStepIndex = 0;
        if (this.executionNavigationActive) {
            this.executionNavigationRemainingMeters = this.executionNavigationPathMeters > 0
                ? this.executionNavigationPathMeters
                : null;
            this.executionNavigationRemainingSeconds = this.executionTotalMinutes > 0
                ? this.executionTotalMinutes * 60
                : 0;
            let firstStepDistance = steps[0] ? Number(steps[0].navigationEndMeters || 0) : 0;
            this.executionNavigationNextDistance = firstStepDistance > 0
                ? `${this.formatPlannerDistance(firstStepDistance, false)} 후`
                : '';
        }
        if (this.executionNavigationFilteredCoordinate) this.updateExecutionNavigationProgress(this.executionNavigationFilteredCoordinate, null);
    }

    private projectCoordinateToExecutionRoute(coordinate: any) {
        let path = this.executionNavigationPath;
        if (!coordinate || path.length < 2) return null;
        let best: any = null;
        let alongBefore = 0;
        let latFactor = 111320;
        let lngFactor = Math.max(1000, 111320 * Math.cos(coordinate.lat * Math.PI / 180));
        for (let index = 1; index < path.length; index++) {
            let start = path[index - 1];
            let end = path[index];
            let ax = (start.lng - coordinate.lng) * lngFactor;
            let ay = (start.lat - coordinate.lat) * latFactor;
            let bx = (end.lng - coordinate.lng) * lngFactor;
            let by = (end.lat - coordinate.lat) * latFactor;
            let dx = bx - ax;
            let dy = by - ay;
            let lengthSquared = dx * dx + dy * dy;
            let ratio = lengthSquared > 0 ? Math.max(0, Math.min(1, -(ax * dx + ay * dy) / lengthSquared)) : 0;
            let px = ax + dx * ratio;
            let py = ay + dy * ratio;
            let distanceMeters = Math.sqrt(px * px + py * py);
            let segmentMeters = Math.sqrt(lengthSquared);
            if (!best || distanceMeters < best.distanceMeters) {
                best = {
                    distanceMeters,
                    alongMeters: alongBefore + segmentMeters * ratio,
                    snappedCoordinate: {
                        lat: start.lat + (end.lat - start.lat) * ratio,
                        lng: start.lng + (end.lng - start.lng) * ratio
                    },
                    heading: (Math.atan2(dx, dy) * 180 / Math.PI + 360) % 360
                };
            }
            alongBefore += segmentMeters;
        }
        return best;
    }

    private updateExecutionNavigationProgress(coordinate: any, position: any) {
        let projected = this.projectCoordinateToExecutionRoute(coordinate);
        if (!projected) return null;
        let accuracy = Math.max(0, Number(position && position.coords && position.coords.accuracy || 0));
        let offRouteLimit = this.travelMode === 'walk' ? 45 : (this.travelMode === 'car' ? 75 : 100);
        offRouteLimit = Math.max(offRouteLimit, Math.min(140, accuracy * 1.4));
        let offRoute = projected.distanceMeters > offRouteLimit;
        if (offRoute) {
            if (!this.executionNavigationOffRouteSince) this.executionNavigationOffRouteSince = Date.now();
        } else {
            this.executionNavigationOffRouteSince = 0;
        }
        let steps = Array.isArray(this.executionNavigationSteps) ? this.executionNavigationSteps : [];
        let nextIndex = steps.findIndex((step: any) => projected.alongMeters < Number(step.navigationEndMeters || Infinity) - 18);
        if (nextIndex < 0 && steps.length > 0) nextIndex = steps.length - 1;
        let changed = nextIndex !== this.executionNavigationStepIndex;
        this.executionNavigationStepIndex = Math.max(0, nextIndex);
        this.executionNavigationHeading = Number.isFinite(Number(position && position.coords && position.coords.heading))
            && Number(position.coords.heading) >= 0
                ? Number(position.coords.heading)
                : projected.heading;
        let step = steps[this.executionNavigationStepIndex];
        let distanceToStep = step
            ? Math.max(0, Number(step.navigationEndMeters || projected.alongMeters) - projected.alongMeters)
            : 0;
        this.executionNavigationNextDistance = distanceToStep > 0
            ? `${this.formatPlannerDistance(distanceToStep, false)} 후`
            : '';
        this.announceExecutionNavigationStep(distanceToStep, changed);
        let shouldReroute = offRoute
            && Date.now() - this.executionNavigationOffRouteSince >= 4000
            && Date.now() - this.executionNavigationLastRerouteAt >= 12000;
        return { ...projected, offRoute, shouldReroute, distanceToStep };
    }

    private followExecutionNavigationCamera(coordinate: any, speedMetersPerSecond: number) {
        this.panMapToCoordinate(coordinate);
        if (!this.naverMap) return;
        let zoom = this.travelMode === 'walk' ? 18 : (speedMetersPerSecond > 22 ? 15 : (speedMetersPerSecond > 10 ? 16 : 17));
        try {
            if (this.naverMap.setZoom) this.naverMap.setZoom(zoom);
            if (this.naverMap.setHeading && this.executionNavigationHeading !== null) this.naverMap.setHeading(this.executionNavigationHeading);
            if (this.naverMap.setTilt && this.travelMode === 'car') this.naverMap.setTilt(35);
        } catch (e) { }
    }

    private async requestExecutionWakeLock() {
        let wakeLock = typeof navigator !== 'undefined' ? (navigator as any).wakeLock : null;
        if (!wakeLock || !wakeLock.request || this.executionWakeLock) return;
        try {
            this.executionWakeLock = await wakeLock.request('screen');
            this.executionWakeLock.addEventListener('release', () => this.executionWakeLock = null);
        } catch (e) { }
    }

    private releaseExecutionWakeLock() {
        let lock = this.executionWakeLock;
        this.executionWakeLock = null;
        if (lock && lock.release) lock.release().catch(() => undefined);
    }

    public async checkInSpot(spot: any, method: string = 'manual', coordinate?: any) {
        if (!spot || spot.visited || !this.activeExecutionCourseId) return;
        let origin = coordinate || this.executionLiveOrigin || this.mapStartCoordinate || {};
        let spotCoordinate = this.spotLatLng(spot);
        let liveNavigationCoordinate = this.executionNavigationActive
            && this.executionNavigationUsingLiveLocation
            && this.naverUserCoordinate
            && this.isFiniteNumber(this.naverUserCoordinate.lat)
            && this.isFiniteNumber(this.naverUserCoordinate.lng)
                ? { lat: Number(this.naverUserCoordinate.lat), lng: Number(this.naverUserCoordinate.lng) }
                : null;
        let arrivalCoordinate = coordinate && this.isFiniteNumber(coordinate.lat) && this.isFiniteNumber(coordinate.lng)
            ? { lat: Number(coordinate.lat), lng: Number(coordinate.lng) }
            : (liveNavigationCoordinate || (spotCoordinate && this.isFiniteNumber(spotCoordinate.lat) && this.isFiniteNumber(spotCoordinate.lng)
                ? { lat: Number(spotCoordinate.lat), lng: Number(spotCoordinate.lng) }
                : null));
        try {
            const response: any = await wiz.call('course_checkin', {
                course_id: this.activeExecutionCourseId,
                place_id: spot.place_id || spot.id,
                method,
                lat: origin.lat || '',
                lng: origin.lng || '',
            });
            const code = response && response.code;
            const data = response && response.data;
            if (code === 200 && data && data.execution) {
                this.applyExecutionCourse(data.execution);
            } else {
                spot.visited = true;
            }
        } catch (e) {
            spot.visited = true;
        }
        if (arrivalCoordinate) {
            this.executionLiveOrigin = arrivalCoordinate;
            this.mapStartCoordinate = arrivalCoordinate;
            this.mapStartRequiresGps = method === 'auto' || !!liveNavigationCoordinate;
            this.mapStartAddress = liveNavigationCoordinate ? '현재 위치' : `${spot.order}. ${spot.name}`;
            this.mapStartStatus = liveNavigationCoordinate ? '현재 위치에서 다음 장소 안내' : `${spot.order}번 장소 도착`;
        }
        this.mapRouteOverviewActive = !this.executionNextSpot();
        if (!this.executionNextSpot()) {
            this.executionNavigationActive = false;
            this.executionNavigationUsingLiveLocation = false;
            this.executionNavigationStatus = '';
            this.executionNavigationLastSpokenKey = '';
            this.resetExecutionNavigationTracking();
            this.stopExecutionVoiceGuidance();
            this.releaseExecutionWakeLock();
        }
        this.executionRouteSummary = '';
        this.executionRouteError = '';
        await this.service.render();
        this.scheduleNaverMapRender();
    }

    public mapSpotsForLocation() {
        if (this.executionPlaces.length > 0) return this.executionPlaces;
        let location = this.selectedFilters.location || '';
        let region = this.getRegionForLocation(location);
        if (location && this.mapSpotMap[location]) return this.mapSpotMap[location];
        if (region && this.mapSpotMap[region]) return this.mapSpotMap[region];
        return this.mapSpotMap.default;
    }

    public filteredMapSpots() {
        if (this.executionPlaces.length > 0) return this.mapSpotsForLocation();
        return this.mapSpotsForLocation().filter((spot: any) => this.activeMapCategories[spot.category]);
    }

    public isMapCategoryActive(key: string) {
        return !!this.activeMapCategories[key];
    }

    public async toggleMapCategory(key: string) {
        this.activeMapCategories[key] = !this.activeMapCategories[key];
        let activeKeys = Object.keys(this.activeMapCategories).filter((item: string) => this.activeMapCategories[item]);
        if (activeKeys.length === 0) this.activeMapCategories[key] = true;
        if (this.selectedMapSpotId && !this.filteredMapSpots().some((spot: any) => spot.id === this.selectedMapSpotId)) {
            this.selectedMapSpotId = '';
        }
        await this.service.render();
        this.scheduleNaverMapRender();
    }

    public async setTravelMode(mode: string) {
        if (!this.isSupportedTravelMode(mode)) return;
        this.travelMode = mode;
        if (this.executionCourse) {
            this.mapRouteOverviewActive = false;
            this.selectedMapSpotId = '';
            this.executionRouteSummary = '';
            this.executionRouteError = '';
            this.executionNavigationSteps = [];
            this.mapRouteLoading = true;
        }
        this.persistAccessState();
        this.replaceAccessUrl();
        await this.service.render();
        this.scheduleNaverMapRender();
    }

    public getSpotTravelTime(spot: any) {
        if (!spot) return '';
        if (spot.visited) return spot.checkin_method === 'auto' ? '자동 도착' : '도착 완료';
        if (!spot.times) return this.executionCourse ? '이동 계산 중' : '';
        return spot.times[this.travelMode] || (this.executionCourse ? '이동 계산 중' : '');
    }

    public getTravelModeLabel() {
        let mode = this.mapTravelModes.find((item: any) => item.key === this.travelMode);
        return mode ? mode.label : '';
    }

    public getMoveFilterLabel() {
        let mode = this.moveFilterOptions.find((item: any) => item.key === this.selectedMoveFilter);
        return mode ? mode.label : '전체';
    }

    public isTravelModeFiltered() {
        return !!this.selectedMoveFilter;
    }

    public selectedMapSpot() {
        if (!this.selectedMapSpotId) return null;
        return this.mapSpotsForLocation().find((spot: any) => spot.id === this.selectedMapSpotId) || null;
    }

    public async selectMapSpot(spot: any) {
        this.mapRouteOverviewActive = false;
        this.selectedMapSpotId = spot.id;
        this.recordRecentPlace(spot);
        await this.service.render();
        this.scheduleNaverMapRender();
    }

    public async showFullCourseOverview() {
        this.selectedMapSpotId = '';
        this.mapRouteOverviewActive = true;
        this.executionNavigationActive = false;
        this.executionNavigationUsingLiveLocation = false;
        this.executionNavigationStatus = '';
        this.executionNavigationLastSpokenKey = '';
        this.resetExecutionNavigationTracking();
        this.stopExecutionVoiceGuidance();
        this.releaseExecutionWakeLock();
        this.executionRouteSummary = '';
        this.executionRouteError = '';
        this.executionNavigationSteps = [];
        this.executionMapZoomAdjustment = 0;
        await this.service.render();
        this.scheduleNaverMapRender();
    }

    public async focusNextCourseLeg() {
        this.selectedMapSpotId = '';
        this.mapRouteOverviewActive = false;
        this.executionRouteSummary = '';
        this.executionRouteError = '';
        this.executionNavigationSteps = [];
        await this.service.render();
        this.scheduleNaverMapRender();
    }

    public async closeMapSpotSheet() {
        this.selectedMapSpotId = '';
        await this.service.render();
        this.scheduleNaverMapRender();
    }

    public async toggleVisit(spot: any, event?: any) {
        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }
        if (this.executionCourse) {
            await this.checkInSpot(spot, 'manual');
            return;
        }
        spot.visited = !spot.visited;
        await this.service.render();
        this.scheduleNaverMapRender();
    }

    public async zoomMap(direction: number) {
        if (this.naverMapReady && this.naverMap) {
            let step = direction > 0 ? 1 : -1;
            let currentZoom = Number(this.naverMap.getZoom && this.naverMap.getZoom());
            if (this.executionCourse) {
                this.executionMapZoomAdjustment = Math.min(4, Math.max(-2, this.executionMapZoomAdjustment + step));
            }
            this.naverBoundsToken++;
            if (Number.isFinite(currentZoom) && this.naverMap.setZoom) {
                this.naverMap.setZoom(Math.min(18, Math.max(5, currentZoom + step)));
            }
            await this.service.render();
            return;
        }
        this.mapZoom = Math.min(3, Math.max(1, this.mapZoom + direction));
        await this.service.render();
        this.scheduleNaverMapRender();
    }

    public async centerOnUser() {
        this.mapZoom = Math.max(2, this.mapZoom);
        if (typeof navigator !== 'undefined' && navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                async (position: any) => {
                    let coordinate = {
                        lat: position.coords.latitude,
                        lng: position.coords.longitude
                    };
                    this.naverSearchCoordinate = null;
                    this.naverUserCoordinate = coordinate;
                    if (this.executionCourse) {
                        this.mapStartCoordinate = coordinate;
                        this.executionLiveOrigin = coordinate;
                        this.mapStartRequiresGps = true;
                        this.mapStartStatus = '현재 위치';
                    }
                    this.naverCenterOnUser = true;
                    this.userPosition = this.projectMapPosition(position.coords.latitude, position.coords.longitude);
                    this.renderNaverUserMarker(coordinate);
                    this.panMapToCoordinate(coordinate);
                    await this.service.render();
                    this.scheduleNaverMapRender();
                },
                async () => {
                    await this.service.render();
                    this.scheduleNaverMapRender();
                },
                { enableHighAccuracy: false, timeout: 4000, maximumAge: 600000 }
            );
            return;
        }

        await this.service.render();
        this.scheduleNaverMapRender();
    }

    public rotateMap() {
        if (!this.naverMapReady || !this.naverMap || !this.naverMap.setHeading) return;
        let heading = Number(this.naverMap.getHeading && this.naverMap.getHeading()) || 0;
        this.naverMap.setHeading((heading + 45) % 360);
        if (this.naverMap.setTilt && Number(this.naverMap.getTilt && this.naverMap.getTilt()) === 0) {
            this.naverMap.setTilt(45);
        }
    }

    public async handleMapWheel(event: any) {
        if (!event) return;
        if (this.naverMapReady && this.naverMap) return;
        event.preventDefault();
        let direction = event.deltaY > 0 ? -1 : 1;
        await this.zoomMap(direction);
    }

    public startMapTouch(event: any) {
        if (this.naverMapReady && this.naverMap) return;
        if (!event || !event.touches || event.touches.length !== 2) return;
        this.pinchStartDistance = this.touchDistance(event.touches);
        this.pinchStartZoom = this.mapZoom;
    }

    public async moveMapTouch(event: any) {
        if (this.naverMapReady && this.naverMap) return;
        if (!event || !event.touches || event.touches.length !== 2 || !this.pinchStartDistance) return;
        event.preventDefault();
        let distance = this.touchDistance(event.touches);
        let diff = distance - this.pinchStartDistance;
        let targetZoom = this.pinchStartZoom;
        if (diff > 28) targetZoom = this.pinchStartZoom + 1;
        if (diff < -28) targetZoom = this.pinchStartZoom - 1;
        targetZoom = Math.min(3, Math.max(1, targetZoom));
        if (targetZoom === this.mapZoom) return;
        this.mapZoom = targetZoom;
        await this.service.render();
    }

    public endMapTouch() {
        this.pinchStartDistance = 0;
    }

    public mapRoutePoints() {
        let spots = this.filteredMapSpots();
        if (this.executionCourse) {
            if (this.mapRouteOverviewActive) {
                spots = this.executionPlaces;
            } else {
                let target = this.selectedMapSpot() || this.executionNextSpot();
                spots = target ? [target] : [];
            }
        }
        return this.routePoints(spots);
    }

    public mapProgressText() {
        let spots = this.mapSpotsForLocation();
        let visited = spots.filter((spot: any) => spot.visited).length;
        return `${visited}/${spots.length} 방문`;
    }

    public selectedMapTiles() {
        let center = this.currentMapCenter();
        let tile = this.lngLatToTile(center.lng, center.lat, center.zoom);
        let tiles = [];

        for (let row = -1; row <= 1; row++) {
            for (let col = -1; col <= 1; col++) {
                tiles.push({
                    src: `https://tile.openstreetmap.org/${center.zoom}/${tile.x + col}/${tile.y + row}.png`,
                    col: String(col + 2),
                    row: String(row + 2)
                });
            }
        }

        return tiles;
    }

    private scheduleCourseMapRender(retry: number = 0) {
        if (!this.courseComposerOpen || this.courseBuilderStep !== 'places') return;
        if (typeof window === 'undefined') return;
        let root: any = window as any;
        let frame = root.requestAnimationFrame || ((callback: any) => setTimeout(callback, 16));
        frame(() => this.renderCourseNaverMap(retry));
    }

    private async renderCourseNaverMap(retry: number = 0) {
        if (!this.courseComposerOpen || this.courseBuilderStep !== 'places' || typeof document === 'undefined') return;
        let element: any = document.querySelector('.course-naver-map');
        if (!element || (!element.offsetWidth || !element.offsetHeight)) {
            if (retry < 8) setTimeout(() => this.scheduleCourseMapRender(retry + 1), 80);
            return;
        }

        let google = await this.loadNaverMapsScript();
        if (!this.isNaverMapsReady(google)) {
            this.courseNaverReady = false;
            return;
        }

        let centerData = this.courseSearchCenter();
        let center = { lat: Number(centerData.lat), lng: Number(centerData.lng) };

        if (this.courseNaverMapElement !== element) {
            this.clearCourseNaverOverlays();
            if (this.courseNaverMapClickListener && this.courseNaverMapClickListener.remove) this.courseNaverMapClickListener.remove();
            this.courseNaverMapClickListener = null;
            this.courseNaverMap = null;
            this.courseNaverMapElement = element;
        }

        if (!this.courseNaverMap) {
            this.courseNaverMap = new google.maps.Map(element, {
                center,
                zoom: this.courseBuilderPlaces.length > 1 ? 14 : 15,
                clickableIcons: true,
                disableDefaultUI: true,
                gestureHandling: 'greedy',
                mapTypeControl: false,
                streetViewControl: false,
                fullscreenControl: false,
                zoomControl: false
            });
            this.courseNaverMapClickListener = this.courseNaverMap.addListener('click', (event: any) => {
                let point = event && (event.coord || event.latLng || event.coordinate);
                let lat = point && typeof point.lat === 'function' ? Number(point.lat()) : Number(point && point.lat);
                let lng = point && typeof point.lng === 'function' ? Number(point.lng()) : Number(point && point.lng);
                if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
                this.addCoursePlaceFromNaverMap({ lat, lng }, google);
            });
        } else {
            this.courseNaverMap.setCenter(center);
        }

        this.clearCourseNaverOverlays();

        let mapItems = this.courseBuilderPlaces
            .filter((place: any) => this.isFiniteNumber(place.lat) && this.isFiniteNumber(place.lng))
        let points = mapItems.map((place: any) => ({ lat: Number(place.lat), lng: Number(place.lng) }));

        points.forEach((point: any, index: number) => {
            let item = mapItems[index];
            let isFlight = item && String(item.itemType || item.item_type || '') === 'flight';
            let markerTitle = isFlight ? String(item.mapLabel || item.area || '공항') : String(item.name || '장소');
            let marker = new google.maps.Marker({
                map: this.courseNaverMap,
                position: point,
                title: markerTitle,
                label: {
                    text: isFlight ? '✈' : String(index + 1),
                    color: '#ffffff',
                    fontSize: '12px',
                    fontWeight: '900'
                },
                icon: {
                    path: google.maps.SymbolPath.CIRCLE,
                    scale: 15,
                    fillColor: '#e63946',
                    fillOpacity: 1,
                    strokeColor: '#ffffff',
                    strokeWeight: 3
                },
                zIndex: 20 + index
            });
            this.courseNaverMarkers.push(marker);
            if (isFlight && google.maps.InfoWindow) {
                let label = document.createElement('strong');
                label.textContent = markerTitle;
                label.style.cssText = 'color:#1f2937;font-size:11px;font-weight:900;white-space:nowrap;';
                let infoWindow = new google.maps.InfoWindow({ content: label, disableAutoPan: true });
                infoWindow.open({ map: this.courseNaverMap, anchor: marker, shouldFocus: false });
                this.courseNaverInfoWindows.push(infoWindow);
            }
        });

        if (points.length > 1) {
            this.courseNaverRouteLine = new google.maps.Polyline({
                map: this.courseNaverMap,
                path: points,
                strokeOpacity: 0,
                strokeWeight: 3,
                icons: [{
                    icon: {
                        path: 'M 0,-1 0,1',
                        strokeColor: '#e63946',
                        strokeOpacity: 0.9,
                        strokeWeight: 3,
                        scale: 3
                    },
                    offset: '0',
                    repeat: '14px'
                }]
            });

            let bounds = new google.maps.LatLngBounds();
            points.forEach((point: any) => bounds.extend(point));
            this.courseNaverMap.fitBounds(bounds, 24);
        } else {
            this.courseNaverMap.setCenter(center);
            this.courseNaverMap.setZoom(points.length === 1 ? 16 : 14);
        }

        if (google.maps.event && google.maps.event.trigger) {
            google.maps.event.trigger(this.courseNaverMap, 'resize');
        }
        this.courseNaverReady = true;
    }

    private addCoursePlaceFromNaverMap(coordinate: any, naverMaps: any) {
        if (!coordinate || !naverMaps || !naverMaps.maps || typeof naverMaps.maps.Geocoder !== 'function') return;
        let geocoder = new naverMaps.maps.Geocoder();
        geocoder.reverseGeocode({ location: coordinate }, async (results: any[], status: string) => {
            let result = status === 'OK' && Array.isArray(results) ? results[0] : null;
            let address = String(result && result.formatted_address || '').trim();
            let region = String(result && result.region || this.courseRegions()[0] || this.courseRegionLabel() || '').trim();
            let coordinateId = `${Number(coordinate.lng).toFixed(6)}-${Number(coordinate.lat).toFixed(6)}`;
            await this.addCoursePlace({
                id: `naver-map-${coordinateId}`,
                place_id: `naver-map-${coordinateId}`,
                name: address || `${region || '선택 지역'} 지도 위치`,
                title: address || `${region || '선택 지역'} 지도 위치`,
                area: region,
                address,
                category: '지도 위치',
                lat: Number(coordinate.lat),
                lng: Number(coordinate.lng),
                latitude: Number(coordinate.lat),
                longitude: Number(coordinate.lng),
                distance_km: null,
                source: 'naver-map'
            });
        });
    }

    private clearCourseNaverOverlays() {
        this.courseNaverInfoWindows.forEach((infoWindow: any) => {
            try { infoWindow.close(); } catch (e) { }
        });
        this.courseNaverInfoWindows = [];
        this.courseNaverMarkers.forEach((marker: any) => {
            try { marker.setMap(null); } catch (e) { }
        });
        this.courseNaverMarkers = [];
        this.courseNaverReady = false;
        if (this.courseNaverRouteLine) {
            try { this.courseNaverRouteLine.setMap(null); } catch (e) { }
            this.courseNaverRouteLine = null;
        }
    }

    private scheduleNaverMapRender(retry: number = 0) {
        if (this.activeTab !== 'map' || this.mapContentTab !== 'map') return;
        if (typeof window === 'undefined') return;
        let root: any = window as any;
        let frame = root.requestAnimationFrame || ((callback: any) => setTimeout(callback, 16));
        frame(() => this.renderNaverMap(retry));
    }

    private async renderNaverMap(retry: number = 0) {
        if (this.activeTab !== 'map' || this.mapContentTab !== 'map') return;
        if (typeof document === 'undefined') return;

        let element = document.getElementById('access-naver-map');
        if (!element) return;
        if ((!element.offsetWidth || !element.offsetHeight) && retry < 10) {
            setTimeout(() => this.scheduleNaverMapRender(retry + 1), 80);
            return;
        }

        let google = await this.loadNaverMapsScript();
        if (!this.isNaverMapsReady(google)) {
            this.naverMapReady = false;
            return;
        }

        let center = this.currentMapCenter();
        let focusCoordinate = !this.executionCourse && !this.mapPlaceRouteOpen
            ? (this.naverUserCoordinate || this.naverSearchCoordinate)
            : (this.naverSearchCoordinate || this.naverUserCoordinate);
        let centerPosition = this.naverCenterOnUser && focusCoordinate
            ? focusCoordinate
            : { lat: center.lat, lng: center.lng };
        let zoom = center.zoom + (this.mapZoom - 2);
        let fallback = element.parentElement ? element.parentElement.querySelector('.map-fallback') : null;

        if (this.naverMapElement !== element) {
            this.clearNaverMapOverlays();
            this.naverMap = null;
            this.naverMapElement = element;
        }

        let creatingMap = !this.naverMap;
        let shouldCenterOnUser = this.naverCenterOnUser && !!focusCoordinate;

        try {
            if (creatingMap) {
                this.naverMap = new google.maps.Map(element, {
                    center: centerPosition,
                    zoom,
                    clickableIcons: true,
                    disableDefaultUI: true,
                    gestureHandling: 'greedy',
                    mapTypeControl: false,
                    streetViewControl: false,
                    fullscreenControl: false,
                    zoomControl: false,
                    rotateControl: true,
                    keyboardShortcuts: true,
                    headingInteractionEnabled: true,
                    tiltInteractionEnabled: true
                });
                this.naverDirectionsService = new google.maps.DirectionsService();
            } else if (shouldCenterOnUser) {
                this.panMapToCoordinate(focusCoordinate);
            }
        } catch (e) {
            this.naverMapReady = false;
            this.naverMap = null;
            element.classList.remove('ready');
            return;
        }

        this.naverMapReady = true;
        element.classList.add('ready');
        if (fallback) fallback.classList.add('fallback-hidden');
        this.naverCenterOnUser = false;
        if (google.maps.event && google.maps.event.trigger) {
            google.maps.event.trigger(this.naverMap, 'resize');
            if (creatingMap) this.naverMap.setCenter(centerPosition);
        }

        this.renderNaverMarkers(google);
        this.renderNaverRoute(google);
        if (this.naverSearchCoordinate && !this.mapPlaceRouteOpen && !this.executionNavigationActive) {
            this.renderNaverSearchMarker(this.naverSearchCoordinate);
        } else if (this.naverSearchMarker) {
            this.naverSearchMarker.setMap(null);
            this.naverSearchMarker = null;
        }
        if (this.naverUserCoordinate && (!this.executionNavigationActive || this.executionNavigationUsingLiveLocation)) {
            this.renderNaverUserMarker(this.naverUserCoordinate);
        } else if (this.naverUserMarker) {
            this.naverUserMarker.setMap(null);
            this.naverUserMarker = null;
        }
    }

    private loadNaverMapsScript() {
        if (typeof window === 'undefined' || typeof document === 'undefined') return Promise.resolve(null);
        if (!this.naverMapsClientId) {
            return this.ensureNaverMapsClientId().then(() => {
                if (!this.naverMapsClientId) return null;
                return this.loadNaverMapsScript();
            });
        }

        let root: any = window as any;
        if (root.naver && root.naver.maps) return this.prepareNaverMaps(root.naver);
        if (this.naverMapsLoader) return this.naverMapsLoader;

        this.naverMapsLoader = new Promise((resolve) => {
            let settled = false;
            let callbackName = '';
            let script: any = null;
            let finish = (google: any, scriptElement: any = null) => {
                if (settled) return;
                settled = true;
                if (!google) {
                    this.naverMapsLoader = null;
                    if (scriptElement && scriptElement.parentNode) scriptElement.parentNode.removeChild(scriptElement);
                }
                if (callbackName) {
                    try {
                        delete root[callbackName];
                    } catch (e) {
                        root[callbackName] = null;
                    }
                }
                resolve(google);
            };
            let existing = document.getElementById('access-naver-maps-sdk');
            if (existing) {
                this.waitForNaverMapsReady(root).then((google: any) => finish(google, existing));
                existing.addEventListener('error', () => finish(null, existing), { once: true });
                return;
            }

            callbackName = '__tourOnNaverMapsReady';
            root[callbackName] = async () => {
                let google = await this.prepareNaverMaps(root.naver);
                finish(google, script);
            };
            root.navermap_authFailure = () => {
                this.naverMapsLoader = null;
                this.naverMapsAdapter = null;
                if (script && script.parentNode) script.parentNode.removeChild(script);
                if (this.profileCourseMapOpen) {
                    this.profileCourseNaverMap = null;
                    this.profileCourseNaverMapElement = null;
                    this.profileCourseMapLoading = false;
                    this.profileCourseMapError = 'NAVER 지도 인증에 실패했어요. 지도 서비스 URL 설정을 확인해주세요.';
                    Promise.resolve(this.service.render()).catch(() => null);
                }
                finish(null, script);
            };

            script = document.createElement('script');
            script.id = 'access-naver-maps-sdk';
            script.async = true;
            script.defer = true;
            script.src = `https://oapi.map.naver.com/openapi/v3/maps.js?ncpKeyId=${encodeURIComponent(this.naverMapsClientId)}&submodules=geocoder&callback=${callbackName}`;
            script.onerror = () => {
                finish(null, script);
            };
            document.head.appendChild(script);
            this.waitForNaverMapsReady(root).then((google: any) => finish(google, script));
        });

        return this.naverMapsLoader;
    }

    private ensureNaverMapsClientId() {
        if (this.naverMapsClientId) return Promise.resolve(this.naverMapsClientId);
        if (this.naverMapsConfigLoader) return this.naverMapsConfigLoader;
        this.naverMapsConfigLoader = wiz.call('naver_maps_config', {})
            .then((response: any) => {
                let data = response && response.data ? response.data : {};
                let apiKey = String(data.naver_maps_client_id || data.naverMapsClientId || '').trim();
                if (apiKey) this.naverMapsClientId = apiKey;
                return this.naverMapsClientId;
            })
            .catch(() => '')
            .then((apiKey: string) => {
                if (!apiKey) this.naverMapsConfigLoader = null;
                return apiKey;
            });
        return this.naverMapsConfigLoader;
    }

    private waitForNaverMapsReady(root: any) {
        return new Promise((resolve) => {
            let startedAt = Date.now();
            let poll = async () => {
                let google = await this.prepareNaverMaps(root.naver);
                if (google) {
                    resolve(google);
                    return;
                }
                if (Date.now() - startedAt > 8000) {
                    resolve(null);
                    return;
                }
                setTimeout(poll, 80);
            };
            poll();
        });
    }

    private async prepareNaverMaps(naver: any) {
        if (!naver || !naver.maps || typeof naver.maps.Map !== 'function') return null;
        if (!this.naverMapsAdapter) this.naverMapsAdapter = this.createNaverMapsAdapter(naver);
        return this.isNaverMapsReady(this.naverMapsAdapter) ? this.naverMapsAdapter : null;
    }

    private createNaverMapsAdapter(naver: any) {
        let maps = naver.maps;
        let coordinate = (value: any) => {
            if (!value) return null;
            let lat = typeof value.lat === 'function' ? Number(value.lat()) : Number(value.lat);
            let lng = typeof value.lng === 'function' ? Number(value.lng()) : Number(value.lng);
            if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
            return new maps.LatLng(lat, lng);
        };
        let listener = (target: any, name: string, callback: any) => {
            let handle = maps.Event.addListener(target, name, callback);
            return {
                remove: () => {
                    try { maps.Event.removeListener(handle); } catch (e) { }
                }
            };
        };
        let escapeHtml = (value: any) => String(value || '')
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#39;');
        let durationText = (milliseconds: number) => {
            let minutes = Math.max(1, Math.round(Number(milliseconds || 0) / 60000));
            if (minutes < 60) return `${minutes}분`;
            let hours = Math.floor(minutes / 60);
            let rest = minutes % 60;
            return rest ? `${hours}시간 ${rest}분` : `${hours}시간`;
        };
        let distanceText = (meters: number) => {
            let value = Math.max(0, Number(meters || 0));
            return value >= 1000 ? `${(value / 1000).toFixed(value >= 10000 ? 0 : 1)}km` : `${Math.round(value)}m`;
        };

        let MapAdapter: any = function (element: any, options: any = {}) {
            this.raw = new maps.Map(element, {
                center: coordinate(options.center),
                zoom: Number(options.zoom || 14),
                disableDoubleClickZoom: false,
                draggable: options.gestureHandling !== 'none',
                keyboardShortcuts: options.keyboardShortcuts !== false,
                zoomControl: options.zoomControl === true,
                mapTypeControl: options.mapTypeControl === true,
                scaleControl: false,
                logoControl: true,
                mapDataControl: false,
            });
        };
        MapAdapter.prototype.addListener = function (name: string, callback: any) { return listener(this.raw, name, callback); };
        MapAdapter.prototype.fitBounds = function (bounds: any, padding?: any) {
            let margin = typeof padding === 'number'
                ? padding
                : Math.max(0, ...Object.keys(padding || {}).map((key: string) => Number(padding[key] || 0)));
            this.raw.fitBounds(bounds && bounds.raw ? bounds.raw : bounds, margin);
        };
        MapAdapter.prototype.getZoom = function () { return this.raw.getZoom(); };
        MapAdapter.prototype.setZoom = function (value: number) { this.raw.setZoom(Number(value)); };
        MapAdapter.prototype.setCenter = function (value: any) { let point = coordinate(value); if (point) this.raw.setCenter(point); };
        MapAdapter.prototype.panTo = function (value: any) { let point = coordinate(value); if (point) this.raw.panTo(point); };
        MapAdapter.prototype.getHeading = function () { return 0; };
        MapAdapter.prototype.setHeading = function (value: any) {
            if (this.raw && typeof this.raw.setHeading === 'function') this.raw.setHeading(Number(value));
        };
        MapAdapter.prototype.getTilt = function () { return 0; };
        MapAdapter.prototype.setTilt = function (value: any) {
            if (this.raw && typeof this.raw.setTilt === 'function') this.raw.setTilt(Number(value));
        };

        let MarkerAdapter: any = function (options: any = {}) {
            let markerOptions: any = {
                map: options.map && options.map.raw ? options.map.raw : options.map,
                position: coordinate(options.position),
                title: options.title || '',
                zIndex: Number(options.zIndex || 0),
            };
            if (options.icon && options.icon.path === 'NAV_ARROW') {
                let diameter = Math.max(24, Number(options.icon.scale || 12) * 2);
                let rotation = Number(options.icon.rotation || 0);
                markerOptions.icon = {
                    content: `<div style="width:${diameter}px;height:${diameter}px;display:flex;align-items:center;justify-content:center;transform:rotate(${rotation}deg);filter:drop-shadow(0 2px 3px rgba(0,0,0,.28))"><div style="width:0;height:0;border-left:${diameter / 3}px solid transparent;border-right:${diameter / 3}px solid transparent;border-bottom:${diameter * .72}px solid ${escapeHtml(options.icon.fillColor || '#d94a58')};position:relative"><span style="position:absolute;left:-${Math.max(2, diameter / 10)}px;top:${diameter * .23}px;width:${Math.max(4, diameter / 5)}px;height:${Math.max(4, diameter / 5)}px;border-radius:50%;background:#fff"></span></div></div>`,
                    size: new maps.Size(diameter, diameter),
                    anchor: new maps.Point(diameter / 2, diameter / 2),
                };
            } else if (options.icon && options.icon.path === 'CIRCLE') {
                let diameter = Math.max(16, Number(options.icon.scale || 10) * 2);
                let label = options.label && typeof options.label === 'object' ? options.label.text : options.label;
                let fill = options.icon.fillColor || '#e63946';
                let border = Math.max(1, Number(options.icon.strokeWeight || 2));
                let borderColor = options.icon.strokeColor || '#ffffff';
                markerOptions.icon = {
                    content: `<div style="width:${diameter}px;height:${diameter}px;border-radius:50%;background:${escapeHtml(fill)};border:${border}px solid ${escapeHtml(borderColor)};box-sizing:border-box;color:${escapeHtml(options.label && options.label.color || '#fff')};display:flex;align-items:center;justify-content:center;font-size:${escapeHtml(options.label && options.label.fontSize || '11px')};font-weight:${escapeHtml(options.label && options.label.fontWeight || '800')};box-shadow:0 2px 7px rgba(0,0,0,.22)">${escapeHtml(label)}</div>`,
                    size: new maps.Size(diameter, diameter),
                    anchor: new maps.Point(diameter / 2, diameter / 2),
                };
            }
            this.raw = new maps.Marker(markerOptions);
        };
        MarkerAdapter.prototype.addListener = function (name: string, callback: any) { return listener(this.raw, name, callback); };
        MarkerAdapter.prototype.setMap = function (map: any) { this.raw.setMap(map && map.raw ? map.raw : map); };

        let PolylineAdapter: any = function (options: any = {}) {
            let dashed = Array.isArray(options.icons) && options.icons.length > 0;
            this.raw = new maps.Polyline({
                map: options.map && options.map.raw ? options.map.raw : options.map,
                path: (options.path || []).map((point: any) => coordinate(point)).filter((point: any) => !!point),
                strokeColor: options.strokeColor || '#e63946',
                strokeOpacity: dashed ? 0.9 : Number(options.strokeOpacity === undefined ? 1 : options.strokeOpacity),
                strokeWeight: Number(options.strokeWeight || 4),
                strokeStyle: dashed ? 'shortdash' : 'solid',
            });
        };
        PolylineAdapter.prototype.setMap = function (map: any) { this.raw.setMap(map && map.raw ? map.raw : map); };

        let LatLngBoundsAdapter: any = function (southWest?: any, northEast?: any) {
            this.raw = southWest && northEast
                ? new maps.LatLngBounds(coordinate(southWest), coordinate(northEast))
                : new maps.LatLngBounds();
        };
        LatLngBoundsAdapter.prototype.extend = function (value: any) {
            let point = coordinate(value);
            if (point) this.raw.extend(point);
            return this;
        };

        let InfoWindowAdapter: any = function (options: any = {}) {
            this.raw = new maps.InfoWindow({ content: options.content || '' });
        };
        InfoWindowAdapter.prototype.open = function (options: any, anchor?: any) {
            let map = options && options.map ? options.map : options;
            let marker = options && options.anchor ? options.anchor : anchor;
            this.raw.open(map && map.raw ? map.raw : map, marker && marker.raw ? marker.raw : marker);
        };
        InfoWindowAdapter.prototype.close = function () { this.raw.close(); };

        let GeocoderAdapter: any = function () { };
        GeocoderAdapter.prototype.geocode = function (request: any, callback: any) {
            let query = String(request && request.address || '').trim();
            if (!query || !maps.Service || typeof maps.Service.geocode !== 'function') {
                callback([], 'ZERO_RESULTS');
                return;
            }
            maps.Service.geocode({ query }, (status: any, response: any) => {
                let ok = status === maps.Service.Status.OK;
                let addresses = ok && response && response.v2 && Array.isArray(response.v2.addresses)
                    ? response.v2.addresses
                    : [];
                let rows = addresses.map((item: any) => {
                    let lat = Number(item.y);
                    let lng = Number(item.x);
                    return {
                        place_id: `naver-${lng}-${lat}`,
                        formatted_address: item.roadAddress || item.jibunAddress || item.englishAddress || query,
                        geometry: { location: { lat: () => lat, lng: () => lng } },
                    };
                });
                callback(rows, rows.length ? 'OK' : 'ZERO_RESULTS');
            });
        };
        GeocoderAdapter.prototype.reverseGeocode = function (request: any, callback: any) {
            let point = coordinate(request && request.location);
            if (!point || !maps.Service || typeof maps.Service.reverseGeocode !== 'function') {
                callback([], 'ZERO_RESULTS');
                return;
            }
            let orders = maps.Service.OrderType
                ? [maps.Service.OrderType.ROAD_ADDR, maps.Service.OrderType.ADDR].filter((value: any) => !!value).join(',')
                : 'roadaddr,addr';
            maps.Service.reverseGeocode({ coords: point, orders }, (status: any, response: any) => {
                let ok = status === maps.Service.Status.OK;
                let results = ok && response && response.v2 && Array.isArray(response.v2.results)
                    ? response.v2.results
                    : [];
                let rows = results.map((item: any) => {
                    let region = item && item.region ? item.region : {};
                    let land = item && item.land ? item.land : {};
                    let area = [region.area1 && region.area1.name, region.area2 && region.area2.name, region.area3 && region.area3.name]
                        .filter((value: any) => !!String(value || '').trim())
                        .join(' ');
                    let detail = [land.name, land.number1, land.number2]
                        .filter((value: any) => !!String(value || '').trim())
                        .join(' ');
                    let addition = land.addition0 && land.addition0.value ? String(land.addition0.value) : '';
                    return {
                        place_id: `naver-${point.lng()}-${point.lat()}`,
                        formatted_address: [area, detail, addition].filter((value: string) => !!value).join(' ').trim(),
                        region: area,
                        geometry: { location: { lat: () => point.lat(), lng: () => point.lng() } },
                    };
                });
                callback(rows, rows.length ? 'OK' : 'ZERO_RESULTS');
            });
        };

        let DirectionsServiceAdapter: any = function () { };
        DirectionsServiceAdapter.prototype.route = function (request: any, callback: any) {
            if (!request || request.travelMode !== 'DRIVING') {
                callback(null, 'ZERO_RESULTS');
                return;
            }
            let start = coordinate(request.origin);
            let goal = coordinate(request.destination);
            if (!start || !goal) {
                callback(null, 'ZERO_RESULTS');
                return;
            }
            wiz.call('naver_directions', {
                start_lat: start.lat(),
                start_lng: start.lng(),
                goal_lat: goal.lat(),
                goal_lng: goal.lng(),
            }).then((response: any) => {
                let data = response && response.data ? response.data : {};
                let routes = response && response.code === 200 && Array.isArray(data.routes) ? data.routes : [];
                if (!routes.length) {
                    callback(null, 'ZERO_RESULTS');
                    return;
                }
                callback({
                    routes: routes.map((route: any) => {
                        let path = (route.path || []).map((point: any) => ({
                            lat: () => Number(point[1]),
                            lng: () => Number(point[0]),
                        }));
                        let duration = { value: Math.round(Number(route.duration || 0) / 1000), text: durationText(route.duration) };
                        let distance = { value: Number(route.distance || 0), text: distanceText(route.distance) };
                        return {
                            overview_path: path,
                            summary: route.option || '네이버 추천 경로',
                            legs: [{
                                duration,
                                duration_in_traffic: duration,
                                distance,
                                steps: [{
                                    instructions: '네이버 지도 추천 자동차 경로',
                                    travel_mode: 'DRIVING',
                                    duration,
                                    distance,
                                    path,
                                }],
                            }],
                        };
                    }),
                }, 'OK');
            }).catch(() => callback(null, 'ZERO_RESULTS'));
        };

        return {
            maps: {
                Map: MapAdapter,
                Marker: MarkerAdapter,
                Polyline: PolylineAdapter,
                LatLngBounds: LatLngBoundsAdapter,
                LatLng: function (lat: number, lng: number) { return new maps.LatLng(Number(lat), Number(lng)); },
                InfoWindow: InfoWindowAdapter,
                Geocoder: GeocoderAdapter,
                DirectionsService: DirectionsServiceAdapter,
                SymbolPath: { CIRCLE: 'CIRCLE' },
                TravelMode: { DRIVING: 'DRIVING', TRANSIT: 'TRANSIT', WALKING: 'WALKING' },
                event: {
                    trigger: (target: any, name: string) => maps.Event.trigger(target && target.raw ? target.raw : target, name),
                    addListenerOnce: (target: any, name: string, callback: any) => {
                        let handle: any = null;
                        handle = maps.Event.addListener(target && target.raw ? target.raw : target, name, (...args: any[]) => {
                            try { maps.Event.removeListener(handle); } catch (e) { }
                            callback(...args);
                        });
                        return handle;
                    },
                },
            },
        };
    }

    private isNaverMapsReady(google: any) {
        return !!(
            google &&
            google.maps &&
            typeof google.maps.Map === 'function' &&
            typeof google.maps.Marker === 'function' &&
            typeof google.maps.DirectionsService === 'function' &&
            typeof google.maps.Polyline === 'function' &&
            google.maps.SymbolPath &&
            google.maps.TravelMode
        );
    }

    private clearNaverMapOverlays() {
        this.naverRouteToken++;
        this.naverMarkers.forEach((marker: any) => marker.setMap(null));
        this.naverMarkers = [];
        if (this.naverRouteLine) {
            this.naverRouteLine.setMap(null);
            this.naverRouteLine = null;
        }
        if (this.naverCompareLine) {
            this.naverCompareLine.setMap(null);
            this.naverCompareLine = null;
        }
        if (this.naverUserMarker) {
            this.naverUserMarker.setMap(null);
            this.naverUserMarker = null;
        }
        if (this.naverSearchMarker) {
            this.naverSearchMarker.setMap(null);
            this.naverSearchMarker = null;
        }
        if (this.naverDirectionsRenderer) {
            this.naverDirectionsRenderer.setMap(null);
            this.naverDirectionsRenderer = null;
        }
    }

    private resolveNaverMapsClientId() {
        if (typeof document !== 'undefined') {
            let meta = document.querySelector('meta[name="naver-maps-client-id"]') as HTMLMetaElement;
            if (meta && meta.content) return meta.content.trim();
        }

        let root: any = typeof window !== 'undefined' ? window as any : null;
        if (root && root.TOUR_ON_NAVER_MAPS_CLIENT_ID) {
            return String(root.TOUR_ON_NAVER_MAPS_CLIENT_ID).trim();
        }

        return '';
    }

    private executionRouteSpots() {
        if (!this.executionCourse) return this.filteredMapSpots();
        return this.executionPlaces.filter((spot: any) => !spot.visited);
    }

    private executionRouteOrigin() {
        if (!this.executionCourse) return null;
        if (this.executionNavigationActive && this.executionNavigationUsingLiveLocation) {
            let liveOrigin = this.naverUserCoordinate || this.executionLiveOrigin;
            if (liveOrigin && this.isFiniteNumber(liveOrigin.lat) && this.isFiniteNumber(liveOrigin.lng)) {
                return { lat: Number(liveOrigin.lat), lng: Number(liveOrigin.lng) };
            }
        }
        let origin = this.executionLiveOrigin || this.mapStartCoordinate || this.naverUserCoordinate;
        if (!origin || !this.isFiniteNumber(origin.lat) || !this.isFiniteNumber(origin.lng)) return null;
        return { lat: Number(origin.lat), lng: Number(origin.lng) };
    }

    private naverRoutePath(spots: any[], origin: any) {
        let path = (spots || [])
            .map((spot: any) => this.spotLatLng(spot))
            .filter((point: any) => point && this.isFiniteNumber(point.lat) && this.isFiniteNumber(point.lng));
        if (origin && path.length > 0) return [origin, ...path];
        return path;
    }

    private resetExecutionTravelTimes() {
        if (!this.executionCourse) return;
        this.executionPlaces.forEach((spot: any) => {
            if (!spot.times) spot.times = { transit: '', walk: '', car: '' };
            spot.times[this.travelMode] = '';
        });
    }

    private applyNaverRouteLegs(legs: any[], spots: any[], hasOrigin: boolean) {
        if (!this.executionCourse) return;
        let totalSeconds = 0;
        let totalMeters = 0;
        (legs || []).forEach((leg: any) => {
            totalSeconds += Number(leg && leg.duration && leg.duration.value ? leg.duration.value : 0);
            totalMeters += Number(leg && leg.distance && leg.distance.value ? leg.distance.value : 0);
        });
        this.executionTotalMinutes = totalSeconds > 0 ? Math.max(1, Math.round(totalSeconds / 60)) : 0;
        this.executionTotalDistanceMeters = Math.round(totalMeters);

        (spots || []).forEach((spot: any, index: number) => {
            if (!spot.times) spot.times = { transit: '', walk: '', car: '' };
            let legIndex = -1;
            if (hasOrigin && index === 0) {
                legIndex = 0;
            } else if (index < spots.length - 1) {
                legIndex = hasOrigin ? index + 1 : index;
            }
            let leg = legIndex > -1 && legs && legs[legIndex] ? legs[legIndex] : null;
            spot.times[this.travelMode] = this.routeLegText(leg, hasOrigin && index === 0);
        });
    }

    private applyEstimatedRouteLegs(spots: any[], origin: any) {
        if (!this.executionCourse) return;
        let points = this.naverRoutePath(spots, origin);
        let legs: any[] = [];
        let speedKmh = this.travelMode === 'car' ? 35 : (this.travelMode === 'transit' ? 26 : 4.5);
        let overheadMinutes = this.travelMode === 'transit' ? 10 : 0;
        let totalMeters = 0;
        let totalMinutes = 0;

        for (let index = 0; index < points.length - 1; index++) {
            let current = points[index];
            let next = points[index + 1];
            let distance = this.distanceKm(current.lat, current.lng, next.lat, next.lng);
            let meters = distance === null ? 0 : Math.round(distance * 1000);
            let minutes = distance === null ? 0 : Math.max(1, Math.round((distance / speedKmh) * 60 + overheadMinutes));
            totalMeters += meters;
            totalMinutes += minutes;
            legs.push({
                duration: { value: minutes * 60 },
                distance: { value: meters },
            });
        }

        this.executionTotalMinutes = totalMinutes;
        this.executionTotalDistanceMeters = totalMeters;
        (spots || []).forEach((spot: any, index: number) => {
            if (!spot.times) spot.times = { transit: '', walk: '', car: '' };
            let legIndex = -1;
            if (origin && index === 0) {
                legIndex = 0;
            } else if (index < spots.length - 1) {
                legIndex = origin ? index + 1 : index;
            }
            let leg = legIndex > -1 ? legs[legIndex] || null : null;
            spot.times[this.travelMode] = this.routeLegText(leg, !!origin && index === 0, true);
        });
    }

    private routeLegText(leg: any, fromStart: boolean = false, estimated: boolean = false) {
        if (!leg || !leg.duration) return fromStart ? '출발지 연결 중' : '마지막 장소';
        let minutes = Math.max(1, Math.round(Number(leg.duration.value || 0) / 60));
        let prefix = fromStart ? '현재 위치에서' : '다음 장소';
        let suffix = estimated ? ' 예상' : '';
        return `${prefix} 약 ${minutes}분${suffix}`;
    }

    private renderNaverMarkers(google: any) {
        this.naverMarkers.forEach((marker: any) => marker.setMap(null));
        this.naverMarkers = [];
        if (!this.executionCourse) {
            if (!this.mapPlaceRouteOpen) return;
            [
                { endpoint: this.mapPlaceRouteOrigin, label: '출발', color: '#2563eb' },
                { endpoint: this.mapPlaceRouteDestination, label: '도착', color: '#e63946' }
            ].forEach((item: any) => {
                let endpoint = item.endpoint;
                if (!endpoint || !endpoint.coordinate) return;
                let marker = new google.maps.Marker({
                    map: this.naverMap,
                    position: endpoint.coordinate,
                    title: `${item.label} · ${endpoint.label}`,
                    icon: {
                        path: google.maps.SymbolPath.CIRCLE,
                        scale: 10,
                        fillColor: item.color,
                        fillOpacity: 1,
                        strokeColor: '#ffffff',
                        strokeWeight: 3
                    },
                    zIndex: item.label === '도착' ? 15 : 14
                });
                this.naverMarkers.push(marker);
            });
            return;
        }

        let markerSpots = this.executionNavigationActive
            ? [this.executionNextSpot()].filter((spot: any) => !!spot)
            : this.filteredMapSpots();
        markerSpots.forEach((spot: any) => {
            let dimmed = this.isMapSpotDimmed(spot);
            let marker = new google.maps.Marker({
                map: this.naverMap,
                position: this.spotLatLng(spot),
                title: spot.name,
                label: {
                    text: String(spot.order),
                    color: '#ffffff',
                    fontSize: '12px',
                    fontWeight: '900'
                },
                icon: {
                    path: google.maps.SymbolPath.CIRCLE,
                    scale: 17,
                    fillColor: this.naverMarkerColor(spot),
                    fillOpacity: dimmed ? 0.28 : 1,
                    strokeColor: '#ffffff',
                    strokeOpacity: dimmed ? 0.5 : 1,
                    strokeWeight: 3
                },
                opacity: dimmed ? 0.55 : 1,
                zIndex: this.selectedMapSpotId === spot.id ? 20 : 10
            });

            marker.addListener('click', async () => {
                await this.selectMapSpot(spot);
            });

            this.naverMarkers.push(marker);
        });
    }

    private renderNaverRoute(google: any) {
        this.naverRouteToken++;
        let token = this.naverRouteToken;
        if (this.naverRouteLine) {
            this.naverRouteLine.setMap(null);
            this.naverRouteLine = null;
        }
        if (this.naverDirectionsRenderer) {
            this.naverDirectionsRenderer.setMap(null);
            this.naverDirectionsRenderer = null;
        }
        if (!this.executionCourse) {
            if (this.mapPlaceRouteOpen) {
                this.renderNaverPlaceRoute(google, token);
                return;
            }
            this.executionTotalMinutes = 0;
            this.executionTotalDistanceMeters = 0;
            this.mapRouteLoading = false;
            return;
        }

        let spots = this.executionRouteSpots();
        let origin = this.executionRouteOrigin();
        let selected = this.selectedMapSpot();
        let next = this.executionNavigationActive ? this.executionNextSpot() : (selected || this.executionNextSpot());
        let routeSpots = this.mapRouteOverviewActive
            ? this.executionPlaces.filter((spot: any) => this.spotLatLng(spot))
            : (next ? [next] : []);
        let path = this.naverRoutePath(routeSpots, this.mapRouteOverviewActive ? null : origin);
        this.resetExecutionTravelTimes();
        this.executionRouteSummary = '';
        this.executionRouteError = '';
        this.executionNavigationSteps = [];
        if (path.length < 2) {
            this.executionTotalMinutes = 0;
            this.executionTotalDistanceMeters = 0;
            if (!next) this.executionRouteSummary = '모든 장소에 도착했어요.';
            this.mapRouteLoading = false;
            if (this.mapRouteOverviewActive && this.executionPlaces.length > 0) {
                let overviewPath = this.naverRoutePath(this.executionPlaces, null);
                if (overviewPath.length > 0) this.fitExecutionMapBounds(google, overviewPath, 0);
            }
            this.service.render();
            return;
        }

        if (this.mapRouteOverviewActive) {
            this.drawEstimatedExecutionRoute(google, path, routeSpots, null, '', true);
            return;
        }

        if (!origin) {
            this.drawEstimatedExecutionRoute(google, path, routeSpots, origin, '출발 위치를 확인하면 다음 장소까지의 경로가 연결됩니다.');
            return;
        }

        if (this.travelMode === 'transit') {
            this.renderExecutionTransitRoute(google, token, origin, next);
            return;
        }

        if (this.travelMode === 'walk') {
            this.renderExecutionWalkingRoute(google, token, origin, next);
            return;
        }

        if (!this.naverDirectionsService) {
            this.drawEstimatedExecutionRoute(
                google,
                path,
                routeSpots,
                origin,
                '차량 경로를 불러오지 못해 거리 기반 예상 동선을 표시합니다.'
            );
            return;
        }

        this.mapRouteLoading = true;
        let drawTargetRoute = (result: any) => {
            let route = result && result.routes && result.routes[0] ? result.routes[0] : {};
            let routePath = Array.isArray(route.overview_path) && route.overview_path.length > 0
                ? route.overview_path
                : path;
            this.naverRouteLine = new google.maps.Polyline({
                map: this.naverMap,
                path: routePath,
                strokeColor: '#e63946',
                strokeOpacity: 0.92,
                strokeWeight: 5
            });
            this.applyNaverRouteLegs(route.legs || [], routeSpots, true);
            this.executionNavigationSteps = this.mapNaverRouteSteps(route.legs && route.legs[0] ? route.legs[0].steps || [] : []);
            this.setExecutionNavigationRoute(routePath);
            this.announceExecutionNavigationStep();
            this.updateExecutionRouteSummary(false);
            this.mapRouteLoading = false;
            if (this.executionNavigationActive && this.executionNavigationFilteredCoordinate) {
                this.followExecutionNavigationCamera(this.executionNavigationFilteredCoordinate, 0);
            } else {
                this.fitExecutionMapBounds(google, routePath, this.executionMapZoomAdjustment);
            }
            this.service.render();
        };
        let drawFallbackRoute = () => {
            this.drawEstimatedExecutionRoute(google, path, routeSpots, origin, '차량 경로를 불러오지 못해 거리 기반 예상 동선을 표시합니다.');
        };
        this.naverDirectionsService.route({
            origin: path[0],
            destination: path[path.length - 1],
            travelMode: google.maps.TravelMode.DRIVING
        }, (result: any, status: string) => {
            if (token !== this.naverRouteToken) return;
            if (status === 'OK') {
                drawTargetRoute(result);
                return;
            }
            drawFallbackRoute();
        });
    }

    private async renderExecutionWalkingRoute(google: any, token: number, origin: any, destinationSpot: any) {
        let destination = this.spotLatLng(destinationSpot);
        if (!destination) return;
        this.mapRouteLoading = true;
        try {
            let response: any = await wiz.call('free_walking_routes', {
                start_lat: origin.lat,
                start_lng: origin.lng,
                goal_lat: destination.lat,
                goal_lng: destination.lng
            });
            if (token !== this.naverRouteToken || this.travelMode !== 'walk' || this.mapRouteOverviewActive) return;
            let data = response && response.data ? response.data : {};
            let routes = response && response.code === 200 && Array.isArray(data.routes) ? data.routes : [];
            let route = routes[0];
            if (!route) {
                let protectedLimit = !!data.free_limit_protected;
                this.drawEstimatedExecutionRoute(
                    google,
                    [origin, destination],
                    [destinationSpot],
                    origin,
                    protectedLimit
                        ? '무료 호출 한도를 보호하기 위해 잠시 거리 기반 예상 동선을 표시합니다.'
                        : '무료 보행 경로를 불러오지 못해 거리 기반 예상 동선을 표시합니다.'
                );
                return;
            }
            let routePath = Array.isArray(route.path) && route.path.length > 1
                ? route.path
                : [origin, destination];
            this.naverRouteLine = new google.maps.Polyline({
                map: this.naverMap,
                path: routePath,
                strokeColor: '#e63946',
                strokeOpacity: 0.94,
                strokeWeight: 6
            });
            this.executionTotalMinutes = Math.max(1, Number(route.total_time || 0));
            this.executionTotalDistanceMeters = Math.max(0, Number(route.total_distance || 0));
            if (!destinationSpot.times) destinationSpot.times = { transit: '', walk: '', car: '' };
            destinationSpot.times.walk = `현재 위치에서 약 ${this.executionTotalMinutes}분`;
            let provider = String(route.provider || data.provider || 'openstreetmap');
            let providerLabel = provider === 'openrouteservice' ? 'openrouteservice 보행로' : 'OSM 보행로';
            this.executionRouteSummary = [
                this.formatPlannerLegDuration(this.executionTotalMinutes * 60),
                this.formatPlannerDistance(this.executionTotalDistanceMeters, false),
                providerLabel
            ].filter((value: any) => !!value).join(' · ');
            this.executionNavigationSteps = (Array.isArray(route.steps) ? route.steps : [])
                .map((step: any, index: number) => this.mapFreeWalkingStep(step, index));
            if (this.executionNavigationSteps.length === 0) {
                this.executionNavigationSteps = [{
                    instruction: `${destinationSpot.name || '목적지'}까지 보행로를 따라 이동`,
                    metaLines: [this.executionRouteSummary],
                    icon: 'fa-person-walking'
                }];
            }
            this.setExecutionNavigationRoute(routePath);
            this.executionRouteError = '';
            this.mapRouteLoading = false;
            if (this.executionNavigationActive && this.executionNavigationFilteredCoordinate) {
                this.followExecutionNavigationCamera(this.executionNavigationFilteredCoordinate, 0);
            } else {
                this.fitExecutionMapBounds(google, routePath, this.executionMapZoomAdjustment);
            }
            this.announceExecutionNavigationStep();
            await this.service.render();
        } catch (error) {
            if (token !== this.naverRouteToken || this.travelMode !== 'walk' || this.mapRouteOverviewActive) return;
            this.drawEstimatedExecutionRoute(
                google,
                [origin, destination],
                [destinationSpot],
                origin,
                '무료 보행 경로를 불러오지 못해 거리 기반 예상 동선을 표시합니다.'
            );
        }
    }

    private async renderExecutionTransitRoute(google: any, token: number, origin: any, destinationSpot: any) {
        let destination = this.spotLatLng(destinationSpot);
        if (!destination) return;
        this.mapRouteLoading = true;
        try {
            let response: any = await wiz.call('odsay_transit_routes', {
                start_lat: origin.lat,
                start_lng: origin.lng,
                goal_lat: destination.lat,
                goal_lng: destination.lng
            });
            if (token !== this.naverRouteToken || this.travelMode !== 'transit' || this.mapRouteOverviewActive) return;
            let routes = response && response.code === 200 && response.data && Array.isArray(response.data.routes)
                ? response.data.routes
                : [];
            let route = routes[0];
            if (!route) {
                let configured = !(response && response.data && response.data.configured === false);
                this.drawEstimatedExecutionRoute(
                    google,
                    [origin, destination],
                    [destinationSpot],
                    origin,
                    configured
                        ? '대중교통 노선을 불러오지 못해 거리 기반 예상 동선을 표시합니다.'
                        : '대중교통 API가 설정되지 않아 거리 기반 예상 동선을 표시합니다.'
                );
                return;
            }
            let routePath = Array.isArray(route.path) && route.path.length > 1
                ? route.path
                : [origin, destination];
            this.naverRouteLine = new google.maps.Polyline({
                map: this.naverMap,
                path: routePath,
                strokeColor: '#e63946',
                strokeOpacity: 0.94,
                strokeWeight: 6
            });
            this.executionTotalMinutes = Math.max(1, Number(route.total_time || 0));
            this.executionTotalDistanceMeters = Math.max(0, Number(route.total_distance || 0));
            if (!destinationSpot.times) destinationSpot.times = { transit: '', walk: '', car: '' };
            destinationSpot.times.transit = `현재 위치에서 약 ${this.executionTotalMinutes}분`;
            let lines = Array.isArray(route.line_labels)
                ? route.line_labels.map((value: any) => String(value || '').trim()).filter((value: string) => !!value)
                : [];
            this.executionRouteSummary = [
                this.formatPlannerLegDuration(this.executionTotalMinutes * 60),
                this.formatPlannerDistance(this.executionTotalDistanceMeters, false),
                lines.length > 0 ? lines.join(' → ') : '대중교통'
            ].filter((value: any) => !!value).join(' · ');
            this.executionNavigationSteps = (Array.isArray(route.steps) ? route.steps : [])
                .map((step: any, index: number) => this.mapOdsayTransitStep(step, index));
            this.setExecutionNavigationRoute(routePath);
            this.announceExecutionNavigationStep();
            this.executionRouteError = '';
            this.mapRouteLoading = false;
            if (this.executionNavigationActive && this.executionNavigationFilteredCoordinate) {
                this.followExecutionNavigationCamera(this.executionNavigationFilteredCoordinate, 0);
            } else {
                this.fitExecutionMapBounds(google, routePath, this.executionMapZoomAdjustment);
            }
            await this.service.render();
        } catch (error) {
            if (token !== this.naverRouteToken || this.travelMode !== 'transit' || this.mapRouteOverviewActive) return;
            this.drawEstimatedExecutionRoute(
                google,
                [origin, destination],
                [destinationSpot],
                origin,
                '대중교통 노선을 불러오지 못해 거리 기반 예상 동선을 표시합니다.'
            );
        }
    }

    private drawEstimatedExecutionRoute(google: any, path: any[], routeSpots: any[], origin: any, error: string = '', overview: boolean = false) {
        if (this.naverRouteLine) this.naverRouteLine.setMap(null);
        this.naverRouteLine = new google.maps.Polyline({
            map: this.naverMap,
            path,
            strokeColor: '#e63946',
            strokeOpacity: overview ? 0.72 : 0.9,
            strokeWeight: overview ? 5 : 6
        });
        this.applyEstimatedRouteLegs(routeSpots, origin);
        this.updateExecutionRouteSummary(true);
        if (!overview && routeSpots.length > 0) {
            let target = routeSpots[0];
            let modeLabel = this.getTravelModeLabel() || '선택한 이동수단';
            this.executionNavigationSteps = [
                {
                    instruction: `${target.name || '다음 장소'} 방향으로 ${modeLabel} 이동`,
                    metaLines: [this.executionRouteSummary, error || '지도에 표시된 경로와 현재 위치를 확인해주세요.'].filter((value: string) => !!value),
                    icon: this.travelMode === 'car' ? 'fa-car-side' : (this.travelMode === 'walk' ? 'fa-person-walking' : 'fa-bus-simple')
                },
                {
                    instruction: `${target.order}. ${target.name || '다음 장소'} 도착`,
                    metaLines: ['도착 반경 80m 안에 들어오면 자동으로 다음 구간으로 전환됩니다.'],
                    icon: 'fa-location-dot'
                }
            ];
            this.setExecutionNavigationRoute(path);
        }
        this.executionRouteError = error;
        this.mapRouteLoading = false;
        if (this.executionNavigationActive && this.executionNavigationFilteredCoordinate && !overview) {
            this.followExecutionNavigationCamera(this.executionNavigationFilteredCoordinate, 0);
        } else {
            this.fitExecutionMapBounds(google, path, (overview ? 0 : 1) + this.executionMapZoomAdjustment);
        }
        this.announceExecutionNavigationStep();
        this.service.render();
    }

    private updateExecutionRouteSummary(estimated: boolean) {
        if (this.executionTotalMinutes <= 0 && this.executionTotalDistanceMeters <= 0) {
            this.executionRouteSummary = '';
            return;
        }
        this.executionRouteSummary = [
            `${estimated ? '예상 ' : ''}${this.formatPlannerLegDuration(this.executionTotalMinutes * 60)}`,
            this.formatPlannerDistance(this.executionTotalDistanceMeters, false)
        ].filter((value: any) => !!value).join(' · ');
    }

    private renderNaverPlaceRoute(google: any, token: number) {
        let origin = this.mapPlaceRouteOrigin && this.mapPlaceRouteOrigin.coordinate;
        let destination = this.mapPlaceRouteDestination && this.mapPlaceRouteDestination.coordinate;
        if (!origin || !destination) {
            this.mapPlaceRouteLoading = false;
            this.mapPlaceRouteRefreshLoading = false;
            return;
        }

        if (this.mapPlaceRouteTravelMode === 'transit') {
            this.renderOdsayTransitRoute(google, token, origin, destination);
            return;
        }
        if (!this.naverDirectionsService) {
            this.mapPlaceRouteLoading = false;
            this.mapPlaceRouteRefreshLoading = false;
            return;
        }

        this.mapPlaceRouteLoading = true;
        this.mapPlaceRouteError = '';
        let modes: any = {
            transit: google.maps.TravelMode.TRANSIT,
            walking: google.maps.TravelMode.WALKING,
            driving: google.maps.TravelMode.DRIVING
        };
        let request: any = {
            origin,
            destination,
            travelMode: modes[this.mapPlaceRouteTravelMode] || google.maps.TravelMode.TRANSIT,
            provideRouteAlternatives: true
        };
        if (this.mapPlaceRouteTravelMode === 'driving') {
            request.drivingOptions = { departureTime: new Date(), trafficModel: 'bestguess' };
        }

        this.naverDirectionsService.route(request, (result: any, status: string) => {
            if (token !== this.naverRouteToken) return;
            let routes = status === 'OK' && result && Array.isArray(result.routes)
                ? result.routes.slice(0, 3)
                : [];
            if (routes.length === 0) {
                this.drawEstimatedMapPlaceRoute(google, origin, destination);
                return;
            }
            this.mapPlaceNaverRoutes = routes;
            this.mapPlaceRouteAlternatives = routes.map((item: any, index: number) => this.mapNaverRouteAlternative(item, index));
            if (this.mapPlaceRouteSelectedIndex >= routes.length) this.mapPlaceRouteSelectedIndex = 0;
            this.applyNaverPlaceRoute(google, routes[this.mapPlaceRouteSelectedIndex], this.mapPlaceRouteSelectedIndex);
        });
    }

    private async renderOdsayTransitRoute(google: any, token: number, origin: any, destination: any) {
        this.mapPlaceRouteLoading = true;
        this.mapPlaceRouteError = '';
        try {
            let response: any = await wiz.call('odsay_transit_routes', {
                start_lat: origin.lat,
                start_lng: origin.lng,
                goal_lat: destination.lat,
                goal_lng: destination.lng
            });
            if (token !== this.naverRouteToken || this.mapPlaceRouteTravelMode !== 'transit') return;
            let routes = response && response.code === 200 && response.data && Array.isArray(response.data.routes)
                ? response.data.routes.slice(0, 3)
                : [];
            if (routes.length > 0) {
                this.mapPlaceNaverRoutes = routes;
                this.mapPlaceRouteAlternatives = routes.map((item: any, index: number) => this.mapOdsayRouteAlternative(item, index));
                if (this.mapPlaceRouteSelectedIndex >= routes.length) this.mapPlaceRouteSelectedIndex = 0;
                this.applyOdsayTransitRoute(google, routes[this.mapPlaceRouteSelectedIndex], this.mapPlaceRouteSelectedIndex);
                return;
            }
            let configured = !(response && response.data && response.data.configured === false);
            this.drawEstimatedMapPlaceRoute(google, origin, destination);
            this.mapPlaceRouteError = configured
                ? '대중교통 노선 조회에 실패해 거리 기반 예상 경로를 표시합니다.'
                : '무료 ODsay API 키가 설정되지 않아 거리 기반 예상 경로를 표시합니다.';
            await this.service.render();
        } catch (error) {
            if (token !== this.naverRouteToken || this.mapPlaceRouteTravelMode !== 'transit') return;
            this.drawEstimatedMapPlaceRoute(google, origin, destination);
            this.mapPlaceRouteError = '대중교통 노선 조회에 실패해 거리 기반 예상 경로를 표시합니다.';
            await this.service.render();
        }
    }

    private applyOdsayTransitRoute(google: any, route: any, selectedIndex: number) {
        if (!route) return;
        if (this.naverRouteLine) {
            this.naverRouteLine.setMap(null);
            this.naverRouteLine = null;
        }
        let origin = this.mapPlaceRouteOrigin && this.mapPlaceRouteOrigin.coordinate;
        let destination = this.mapPlaceRouteDestination && this.mapPlaceRouteDestination.coordinate;
        let path = Array.isArray(route.path) && route.path.length > 1
            ? route.path
            : [origin, destination].filter((value: any) => !!value);
        this.naverRouteLine = new google.maps.Polyline({
            map: this.naverMap,
            path,
            strokeColor: '#e63946',
            strokeOpacity: 0.94,
            strokeWeight: 6
        });
        this.mapPlaceRouteSelectedIndex = selectedIndex;
        this.mapPlaceRouteProvider = 'odsay';
        this.mapPlaceRouteSummary = [
            this.formatPlannerLegDuration(Math.max(1, Number(route.total_time || 0)) * 60),
            this.formatPlannerDistance(Math.max(0, Number(route.total_distance || 0)), false)
        ].filter((value: any) => !!value).join(' · ');
        this.mapPlaceRouteSteps = (Array.isArray(route.steps) ? route.steps : [])
            .map((step: any, index: number) => this.mapOdsayTransitStep(step, index));
        this.mapPlaceRouteLoading = false;
        this.mapPlaceRouteRefreshLoading = false;
        this.mapPlaceRouteError = '';
        this.mapPlaceRouteUpdatedAt = this.mapRouteUpdatedTimeLabel();
        if (!this.mapPlaceNavigationActive) this.fitExecutionMapBounds(google, path, 0);
        this.service.render();
    }

    private mapOdsayTransitStep(step: any, index: number) {
        let trafficType = Number(step && step.traffic_type || 3);
        let lanes = Array.isArray(step && step.lanes) ? step.lanes : [];
        let lines = lanes.map((lane: any) => String(lane.bus_no || lane.name || '').trim()).filter((value: string) => !!value);
        let uniqueLines = lines.filter((value: string, lineIndex: number) => lines.indexOf(value) === lineIndex);
        let lineLabel = uniqueLines.join(' · ');
        let startName = String(step && step.start_name || '').trim();
        let endName = String(step && step.end_name || '').trim();
        let duration = Math.max(0, Number(step && step.duration || 0));
        let distance = Math.max(0, Number(step && step.distance || 0));
        let stationCount = Math.max(0, Number(step && step.station_count || 0));
        let direction = String(step && step.direction || '').trim();
        let instruction = '';
        let icon = 'fa-person-walking';
        if (trafficType === 2 || trafficType === 5 || trafficType === 6) {
            let buses = uniqueLines.map((value: string) => /번$/.test(value) ? value : `${value}번`).join(' · ');
            instruction = `${startName || '정류장'}에서 ${buses || '버스'} 탑승`;
            icon = 'fa-bus-simple';
        } else if (trafficType === 1) {
            instruction = `${startName || '역'}에서 ${lineLabel || '지하철'} 탑승`;
            icon = 'fa-train-subway';
        } else if (trafficType === 4) {
            instruction = `${startName || '역'}에서 ${lineLabel || '열차'} 탑승`;
            icon = 'fa-train';
        } else if (trafficType === 7) {
            instruction = `${startName || '출발지'}에서 ${lineLabel || '항공편'} 탑승`;
            icon = 'fa-plane';
        } else {
            instruction = `${endName || '다음 승차 지점'}까지 도보 이동`;
        }
        let metaLines = [
            startName && endName ? `${startName} → ${endName}` : (startName || endName),
            direction ? `${direction} 방면` : '',
            [duration > 0 ? this.formatPlannerLegDuration(duration * 60) : '', distance > 0 ? this.formatPlannerDistance(distance, false) : ''].filter((value: any) => !!value).join(' · '),
            stationCount > 0 ? `${stationCount}개 정류장·역 이동` : '',
            uniqueLines.length > 1 ? `탑승 가능 노선 · ${uniqueLines.join(', ')}` : ''
        ].filter((value: any) => !!value);
        return {
            order: index + 1,
            instruction,
            meta: metaLines.join(' · '),
            metaLines,
            detailsExpanded: false,
            navigationDistanceMeters: distance,
            icon
        };
    }

    private mapFreeWalkingStep(step: any, index: number) {
        let instruction = String(step && step.instruction || '보행로를 따라 이동').trim();
        let distance = Math.max(0, Number(step && step.distance || 0));
        let durationSeconds = Math.max(0, Number(step && step.duration_seconds || 0));
        let modifier = String(step && step.maneuver_modifier || '').toLowerCase();
        let maneuverType = String(step && step.maneuver_type || '').toLowerCase();
        let icon = 'fa-arrow-up';
        if (/left|좌회전|왼쪽/.test(`${modifier} ${instruction}`)) icon = 'fa-arrow-turn-up';
        else if (/right|우회전|오른쪽/.test(`${modifier} ${instruction}`)) icon = 'fa-arrow-turn-down';
        else if (/arrive|도착|ors-10/.test(`${maneuverType} ${instruction}`)) icon = 'fa-location-dot';
        else if (/depart|출발|ors-11/.test(`${maneuverType} ${instruction}`)) icon = 'fa-person-walking';
        let metaLines = [[
            durationSeconds > 0 ? this.formatPlannerLegDuration(durationSeconds) : '',
            distance > 0 ? this.formatPlannerDistance(distance, false) : ''
        ].filter((value: string) => !!value).join(' · ')].filter((value: string) => !!value);
        return {
            order: index + 1,
            instruction,
            meta: metaLines.join(' · '),
            metaLines,
            detailsExpanded: false,
            navigationDistanceMeters: distance,
            icon
        };
    }

    private stopExecutionVoiceGuidance() {
        if (!this.executionVoiceSupported()) return;
        try { (window as any).speechSynthesis.cancel(); } catch (e) { }
    }

    private announceExecutionNavigationStep(distanceMeters?: number, force: boolean = false) {
        if (!this.executionNavigationActive || !this.executionVoiceGuidanceEnabled || !this.executionVoiceSupported()) return;
        let step = this.executionNavigationSteps && this.executionNavigationSteps[this.executionNavigationStepIndex];
        let instruction = String(step && step.instruction || '').trim();
        if (!instruction) return;
        let target = this.executionNextSpot();
        let distance = Math.max(0, Number(distanceMeters || 0));
        let bucket = distance <= 35 ? 'now' : (distance <= 110 ? 'soon' : (distance <= 320 ? 'prepare' : 'start'));
        let key = `${target && target.id || ''}:${this.travelMode}:${this.executionNavigationStepIndex}:${bucket}`;
        if (key === this.executionNavigationLastSpokenKey) return;
        if (!force && bucket === 'start' && this.executionNavigationLastSpokenKey) return;
        this.executionNavigationLastSpokenKey = key;
        try {
            let speech = (window as any).speechSynthesis;
            let Utterance = (window as any).SpeechSynthesisUtterance;
            speech.cancel();
            let prefix = bucket === 'prepare' ? `${Math.max(100, Math.round(distance / 100) * 100)}미터 앞, `
                : (bucket === 'soon' ? `약 ${Math.max(30, Math.round(distance / 10) * 10)}미터 앞, ` : '');
            let utterance = new Utterance(`${prefix}${instruction}`);
            utterance.lang = 'ko-KR';
            utterance.rate = 1.02;
            utterance.pitch = 1;
            speech.speak(utterance);
        } catch (e) { }
    }

    private mapOdsayRouteAlternative(route: any, index: number) {
        let lines = Array.isArray(route && route.line_labels)
            ? route.line_labels.map((value: any) => String(value || '').trim()).filter((value: string) => !!value)
            : [];
        let totalTime = Math.max(1, Number(route && route.total_time || 0));
        let totalDistance = Math.max(0, Number(route && route.total_distance || 0));
        let totalWalk = Math.max(0, Number(route && route.total_walk || 0));
        let transferCount = Math.max(0, Number(route && route.transfer_count || 0));
        return {
            index,
            label: `${index + 1}안`,
            summary: [this.formatPlannerLegDuration(totalTime * 60), this.formatPlannerDistance(totalDistance, false)].filter((value: any) => !!value).join(' · '),
            detail: [
                lines.length > 0 ? lines.join(' → ') : '대중교통',
                totalWalk > 0 ? `도보 ${this.formatPlannerDistance(totalWalk, false)}` : '',
                transferCount > 0 ? `환승 ${transferCount}회` : '환승 없음'
            ].filter((value: any) => !!value).join(' · ')
        };
    }

    private applyNaverPlaceRoute(google: any, route: any, selectedIndex: number) {
        if (!route) return;
        if (this.naverRouteLine) {
            this.naverRouteLine.setMap(null);
            this.naverRouteLine = null;
        }
        let origin = this.mapPlaceRouteOrigin && this.mapPlaceRouteOrigin.coordinate;
        let destination = this.mapPlaceRouteDestination && this.mapPlaceRouteDestination.coordinate;
        let leg = route.legs && route.legs[0] ? route.legs[0] : {};
        let path = Array.isArray(route.overview_path) && route.overview_path.length > 0
            ? route.overview_path
            : [origin, destination].filter((value: any) => !!value);
        this.naverRouteLine = new google.maps.Polyline({
            map: this.naverMap,
            path,
            strokeColor: '#e63946',
            strokeOpacity: 0.94,
            strokeWeight: 6
        });
        let duration = leg.duration_in_traffic || leg.duration || {};
        this.mapPlaceRouteSelectedIndex = selectedIndex;
        this.mapPlaceRouteProvider = 'naver';
        this.mapPlaceRouteSummary = [duration.text, leg.distance && leg.distance.text]
            .filter((value: any) => !!value)
            .join(' · ');
        this.mapPlaceRouteSteps = this.mapNaverRouteSteps(leg.steps || []);
        this.mapPlaceRouteLoading = false;
        this.mapPlaceRouteRefreshLoading = false;
        this.mapPlaceRouteUpdatedAt = this.mapRouteUpdatedTimeLabel();
        if (!this.mapPlaceNavigationActive) {
            this.fitExecutionMapBounds(google, path, 0);
        }
        this.service.render();
    }

    private mapNaverRouteSteps(steps: any[]) {
        let flattened: any[] = [];
        let routeSteps = steps || [];
        routeSteps.forEach((step: any, topIndex: number) => {
            let previousTransit = routeSteps.slice(0, topIndex).reverse().find((item: any) => String(item && item.travel_mode || '').toUpperCase() === 'TRANSIT');
            let nextTransit = routeSteps.slice(topIndex + 1).find((item: any) => String(item && item.travel_mode || '').toUpperCase() === 'TRANSIT');
            let contextSource = previousTransit && previousTransit.transit && previousTransit.transit.arrival_stop
                ? previousTransit.transit.arrival_stop.name
                : this.mapPlaceRouteOriginLabel();
            let contextTarget = nextTransit && nextTransit.transit && nextTransit.transit.departure_stop
                ? nextTransit.transit.departure_stop.name
                : this.mapPlaceRouteDestinationLabel();
            let withContext = (item: any) => ({
                ...item,
                _contextSource: contextSource,
                _contextTarget: contextTarget
            });
            let nested = Array.isArray(step.steps) && step.steps.length > 0 ? step.steps : [];
            if (step.travel_mode === 'WALKING' && nested.length > 0) {
                nested.forEach((child: any) => flattened.push(withContext({ ...child, travel_mode: child.travel_mode || 'WALKING' })));
                return;
            }
            flattened.push(withContext(step));
        });
        return flattened.map((step: any, index: number) => this.mapNaverRouteStep(step, index));
    }

    private mapNaverRouteStep(step: any, index: number) {
        let transit = step.transit || {};
        let line = transit.line || {};
        let lineName = line.short_name || line.name || '';
        let vehicleName = line.vehicle && line.vehicle.name || '';
        let agencyName = line.agencies && line.agencies[0] && line.agencies[0].name || '';
        let departureStop = transit.departure_stop && transit.departure_stop.name || '';
        let arrivalStop = transit.arrival_stop && transit.arrival_stop.name || '';
        let departureText = transit.departure_time && transit.departure_time.text || '';
        let arrivalText = transit.arrival_time && transit.arrival_time.text || '';
        let headsign = transit.headsign || '';
        let stopCount = Number(transit.num_stops || 0);
        let countdown = this.mapTransitCountdown(transit.departure_time && transit.departure_time.value);
        let mode = String(step.travel_mode || '').toUpperCase();
        let isTransit = mode === 'TRANSIT';
        let modeLabel = mode === 'DRIVING' ? '차량' : (mode === 'WALKING' ? '도보' : '이동');
        let contextTarget = String(step._contextTarget || this.mapPlaceRouteDestinationLabel() || '').trim();
        let rawInstruction = this.stripMapDirectionHtml(step.instructions || '');
        let isGenericInstruction = !rawInstruction || /^(경로를 따라 이동|도보로 이동|걷기|이동)$/i.test(rawInstruction);
        let movementInstruction = isGenericInstruction
            ? (mode === 'WALKING'
                ? `${contextTarget || '다음 지점'}까지 도보 이동`
                : `${contextTarget || '목적지'}까지 ${modeLabel} 이동`)
            : rawInstruction;
        let metaLines = isTransit
            ? [
                departureStop ? `승차 · ${departureStop}` : '',
                arrivalStop ? `하차 · ${arrivalStop}` : '',
                [lineName ? `노선 ${lineName}` : '', headsign ? `${headsign} 방면` : '', vehicleName && vehicleName !== lineName ? vehicleName : ''].filter((value: any) => !!value).join(' · '),
                [countdown, departureText ? `${departureText} 출발` : ''].filter((value: any) => !!value).join(' · '),
                [departureStop && arrivalStop ? `${departureStop} → ${arrivalStop}` : '', stopCount > 0 ? `총 ${stopCount}개 정류장 구간` : ''].filter((value: any) => !!value).join(' · '),
                [arrivalText ? `${arrivalText} 도착 예정` : '', step.duration && step.duration.text].filter((value: any) => !!value).join(' · '),
                agencyName ? `운영 · ${agencyName}` : ''
            ].filter((value: any) => !!value)
            : [
                [`${index + 1}단계`, modeLabel, this.mapManeuverLabel(step.maneuver)].filter((value: any) => !!value).join(' · '),
                [step.duration && step.duration.text, step.distance && step.distance.text].filter((value: any) => !!value).join(' · ')
            ].filter((value: any) => !!value);
        return {
            order: index + 1,
            instruction: isTransit
                ? `${departureStop || '정류장'}에서 ${lineName || vehicleName || '대중교통'} 탑승`
                : movementInstruction,
            meta: metaLines.join(' · '),
            metaLines,
            detailsExpanded: false,
            navigationDistanceMeters: Number(step && step.distance && step.distance.value || 0),
            icon: isTransit ? this.mapTransitVehicleIcon(vehicleName)
                : this.mapDirectionStepIcon(mode, step.maneuver)
        };
    }

    private mapNaverRouteAlternative(route: any, index: number) {
        let leg = route && route.legs && route.legs[0] ? route.legs[0] : {};
        let steps = Array.isArray(leg.steps) ? leg.steps : [];
        let lines: string[] = [];
        let walkingMeters = 0;
        steps.forEach((step: any) => {
            if (step.travel_mode === 'WALKING') walkingMeters += Number(step.distance && step.distance.value || 0);
            let transit = step.transit || {};
            let line = transit.line || {};
            let name = line.short_name || line.name || '';
            if (name && lines.indexOf(name) < 0) lines.push(name);
        });
        let duration = leg.duration_in_traffic || leg.duration || {};
        let modeLabel = this.mapPlaceRouteTravelMode === 'transit' ? '대중교통'
            : (this.mapPlaceRouteTravelMode === 'walking' ? '도보' : '차량');
        let details = [
            lines.length > 0 ? lines.join(' → ') : modeLabel,
            walkingMeters > 0 ? `도보 ${walkingMeters >= 1000 ? `${(walkingMeters / 1000).toFixed(1)}km` : `${Math.round(walkingMeters)}m`}` : '',
            lines.length > 1 ? `환승 ${lines.length - 1}회` : ''
        ].filter((value: any) => !!value).join(' · ');
        return {
            index,
            label: `${index + 1}안`,
            summary: [duration.text, leg.distance && leg.distance.text].filter((value: any) => !!value).join(' · '),
            detail: details
        };
    }

    private mapTransitVehicleIcon(vehicleName: string) {
        let value = String(vehicleName || '').toLowerCase();
        if (/지하철|subway|metro/.test(value)) return 'fa-train-subway';
        if (/기차|열차|rail|train/.test(value)) return 'fa-train';
        if (/페리|배|ferry/.test(value)) return 'fa-ship';
        return 'fa-bus-simple';
    }

    private mapDirectionStepIcon(mode: string, maneuver: string) {
        let value = String(maneuver || '').toLowerCase();
        if (/uturn-left/.test(value)) return 'fa-rotate-left';
        if (/uturn-right/.test(value)) return 'fa-rotate-right';
        if (/left/.test(value)) return 'fa-arrow-left';
        if (/right/.test(value)) return 'fa-arrow-right';
        if (/straight/.test(value)) return 'fa-arrow-up';
        if (/merge|fork/.test(value)) return 'fa-code-merge';
        return mode === 'DRIVING' ? 'fa-car-side' : 'fa-person-walking';
    }

    private mapManeuverLabel(maneuver: string) {
        let value = String(maneuver || '').toLowerCase();
        if (!value) return '';
        if (/uturn/.test(value)) return '유턴';
        if (/slight-left/.test(value)) return '왼쪽 완만한 회전';
        if (/slight-right/.test(value)) return '오른쪽 완만한 회전';
        if (/left/.test(value)) return '왼쪽 회전';
        if (/right/.test(value)) return '오른쪽 회전';
        if (/straight/.test(value)) return '직진';
        if (/merge/.test(value)) return '도로 합류';
        if (/fork/.test(value)) return '갈림길';
        if (/roundabout/.test(value)) return '회전교차로';
        return '';
    }

    private drawEstimatedMapPlaceRoute(google: any, origin: any, destination: any) {
        let modeLabel = this.mapPlaceRouteTravelMode === 'walking' ? '도보'
            : (this.mapPlaceRouteTravelMode === 'driving' ? '차량' : '대중교통');
        this.mapPlaceNaverRoutes = [];
        this.mapPlaceRouteAlternatives = [];
        this.mapPlaceRouteSelectedIndex = 0;
        this.mapPlaceRouteProvider = 'estimate';
        if (this.mapPlaceRouteTravelMode === 'driving') {
            this.mapPlaceRouteSummary = '경로를 찾을 수 없음';
            this.mapPlaceRouteSteps = [];
            this.mapPlaceRouteError = `${modeLabel}로 이동할 수 있는 실제 경로를 찾지 못했어요. 출발지나 교통수단을 변경해주세요.`;
            this.mapPlaceRouteLoading = false;
            this.mapPlaceRouteRefreshLoading = false;
            this.mapPlaceRouteUpdatedAt = this.mapRouteUpdatedTimeLabel();
            this.fitExecutionMapBounds(google, [origin, destination], 0);
            this.service.render();
            return;
        }

        let straightDistance = this.distanceKm(origin.lat, origin.lng, destination.lat, destination.lng);
        let routeKilometers = Math.max(0.1, Number(straightDistance || 0.1))
            * (this.mapPlaceRouteTravelMode === 'walking' ? 1.15 : 1.25);
        let routeMeters = Math.max(100, Math.round(routeKilometers * 1000));
        let speed = this.mapPlaceRouteTravelMode === 'walking' ? 4.2 : 30;
        let overheadMinutes = this.mapPlaceRouteTravelMode === 'walking' ? 0 : 15;
        let totalMinutes = Math.max(2, Math.round((routeKilometers / speed) * 60 + overheadMinutes));
        let durationText = this.formatPlannerLegDuration(totalMinutes * 60);
        let distanceText = this.formatPlannerDistance(routeMeters);
        this.mapPlaceRouteSummary = `예상 ${durationText} · ${distanceText}`;
        this.mapPlaceRouteError = '';

        if (this.mapPlaceRouteTravelMode === 'walking') {
            this.mapPlaceRouteSteps = [
                {
                    order: 1,
                    instruction: `${this.mapPlaceRouteDestinationLabel()} 방향으로 도보 이동`,
                    metaLines: [`예상 ${durationText}`, distanceText, '지도에 표시된 연결선을 따라 이동 방향을 확인해주세요.'],
                    detailsExpanded: false,
                    icon: 'fa-person-walking'
                },
                {
                    order: 2,
                    instruction: `${this.mapPlaceRouteDestinationLabel()} 도착`,
                    metaLines: ['도착 전 주변 도로와 보행 진입로를 확인해주세요.'],
                    detailsExpanded: false,
                    icon: 'fa-location-dot'
                }
            ];
        } else {
            let accessMeters = Math.min(700, Math.max(150, Math.round(routeMeters * 0.04)));
            let transitMeters = Math.max(0, routeMeters - (accessMeters * 2));
            let transitMinutes = Math.max(2, totalMinutes - Math.max(4, Math.round((accessMeters * 2 / 1000 / 4.2) * 60)));
            this.mapPlaceRouteSteps = [
                {
                    order: 1,
                    instruction: `${this.mapPlaceRouteOriginLabel()} 인근 정류장·역까지 이동`,
                    metaLines: [`도보 약 ${this.formatPlannerDistance(accessMeters, false)}`, '이 화면은 추천 이동 흐름을 보여줍니다.'],
                    detailsExpanded: false,
                    icon: 'fa-person-walking'
                },
                {
                    order: 2,
                    instruction: `${this.mapPlaceRouteDestinationLabel()} 방면 대중교통 이용`,
                    metaLines: [`승차 구간 예상 ${this.formatPlannerLegDuration(transitMinutes * 60)}`, this.formatPlannerDistance(transitMeters), '노선·환승·실시간 도착 정보는 제공되지 않습니다.'],
                    detailsExpanded: false,
                    icon: 'fa-bus-simple'
                },
                {
                    order: 3,
                    instruction: `하차 후 ${this.mapPlaceRouteDestinationLabel()}까지 이동`,
                    metaLines: [`도보 약 ${this.formatPlannerDistance(accessMeters, false)}`, `전체 예상 ${durationText}`],
                    detailsExpanded: false,
                    icon: 'fa-location-dot'
                }
            ];
        }

        if (this.naverRouteLine) {
            this.naverRouteLine.setMap(null);
            this.naverRouteLine = null;
        }
        this.naverRouteLine = new google.maps.Polyline({
            map: this.naverMap,
            path: [origin, destination],
            strokeColor: '#e63946',
            strokeOpacity: 0.82,
            strokeWeight: 5
        });
        this.mapPlaceRouteLoading = false;
        this.mapPlaceRouteRefreshLoading = false;
        this.mapPlaceRouteUpdatedAt = this.mapRouteUpdatedTimeLabel();
        this.fitExecutionMapBounds(google, [origin, destination], 0);
        this.service.render();
    }

    private mapTransitCountdown(value: any) {
        if (!value) return '';
        let timestamp = value instanceof Date ? value.getTime() : new Date(value).getTime();
        if (!Number.isFinite(timestamp)) return '';
        let minutes = Math.ceil((timestamp - Date.now()) / 60000);
        if (minutes <= 1) return '곧 출발';
        return `${minutes}분 후 출발`;
    }

    private mapRouteUpdatedTimeLabel() {
        let now = new Date();
        return `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
    }

    private stripMapDirectionHtml(value: any) {
        let text = String(value || '').replace(/<[^>]*>/g, ' ').replace(/&nbsp;/g, ' ');
        return text.replace(/\s+/g, ' ').trim();
    }

    private fitExecutionMapBounds(google: any, path: any[], zoomBoost: number = 0) {
        if (!this.naverMap || !google || !google.maps || typeof google.maps.LatLngBounds !== 'function') return;
        let points = (path || []).filter((point: any) => point && this.isFiniteNumber(
            typeof point.lat === 'function' ? point.lat() : point.lat
        ) && this.isFiniteNumber(
            typeof point.lng === 'function' ? point.lng() : point.lng
        ));
        if (points.length === 0) return;
        let bounds = new google.maps.LatLngBounds();
        points.forEach((point: any) => bounds.extend(point));
        let boundsToken = ++this.naverBoundsToken;
        this.naverMap.fitBounds(bounds, { top: 94, right: 72, bottom: 220, left: 44 });
        if (points.length === 1 && this.naverMap.setZoom) {
            this.naverMap.setZoom(15);
            return;
        }
        if (!zoomBoost || !this.naverMap.setZoom) return;

        let applyZoomBoost = () => {
            if (boundsToken !== this.naverBoundsToken || !this.naverMap) return;
            let currentZoom = Number(this.naverMap.getZoom && this.naverMap.getZoom());
            if (!Number.isFinite(currentZoom)) return;
            this.naverMap.setZoom(Math.min(18, Math.max(5, currentZoom + zoomBoost)));
        };
        if (google.maps.event && typeof google.maps.event.addListenerOnce === 'function') {
            google.maps.event.addListenerOnce(this.naverMap, 'idle', applyZoomBoost);
            return;
        }
        if (typeof window !== 'undefined') window.setTimeout(applyZoomBoost, 0);
    }

    private renderNaverUserMarker(coordinate: any) {
        let google = this.naverMapsAdapter;
        if (!google || !google.maps || !this.naverMap) return;

        if (this.naverUserMarker) this.naverUserMarker.setMap(null);
        this.naverUserMarker = new google.maps.Marker({
            map: this.naverMap,
            position: coordinate,
            title: '내 위치',
            icon: this.executionNavigationActive && this.executionNavigationHeading !== null
                ? {
                    path: 'NAV_ARROW',
                    scale: 14,
                    rotation: this.executionNavigationHeading,
                    fillColor: '#d94a58'
                }
                : {
                    path: google.maps.SymbolPath.CIRCLE,
                    scale: 9,
                    fillColor: '#d94a58',
                    fillOpacity: 1,
                    strokeColor: '#ffffff',
                    strokeWeight: 4
                },
            zIndex: 30
        });
    }

    private renderNaverSearchMarker(coordinate: any) {
        let google = this.naverMapsAdapter;
        if (!google || !google.maps || !this.naverMap) return;

        if (this.naverSearchMarker) this.naverSearchMarker.setMap(null);
        this.naverSearchMarker = new google.maps.Marker({
            map: this.naverMap,
            position: coordinate,
            title: '검색 위치',
            icon: {
                path: google.maps.SymbolPath.CIRCLE,
                scale: 10,
                fillColor: '#e63946',
                fillOpacity: 1,
                strokeColor: '#ffffff',
                strokeWeight: 4
            },
            zIndex: 32
        });
    }

    private naverMarkerColor(spot: any) {
        if (this.executionCourse) return '#e63946';
        if (spot.visited) return '#56504e';
        if (this.selectedMapSpotId === spot.id) return '#e63946';
        let colors: any = {
            cafe: '#1f8a70',
            food: '#d58a1f',
            walk: '#d94a58',
            landmark: '#e63946'
        };
        return colors[spot.category] || '#1f8a70';
    }

    private spotLatLng(spot: any) {
        let lat = this.safeNumber(spot && spot.lat);
        let lng = this.safeNumber(spot && spot.lng);
        if (lat !== null && lng !== null) return { lat, lng };
        return this.canvasPointLatLng(spot.x, spot.y);
    }

    private canvasPointLatLng(x: number, y: number) {
        let center = this.currentMapCenter();
        let centerPoint = this.lngLatToTilePoint(center.lng, center.lat, center.zoom);
        let point = {
            x: centerPoint.x + ((x - 50) / 100),
            y: centerPoint.y + ((y - 50) / 100)
        };
        return this.tilePointToLngLat(point.x, point.y, center.zoom);
    }

    private allCourses() {
        let seen: any = {};
        return [...this.recommendations, ...this.courses, ...this.placeCourses].filter((course: any) => {
            let id = course && course.id ? course.id : '';
            if (!id) return true;
            if (seen[id]) return false;
            seen[id] = true;
            return true;
        });
    }

    private routePoints(spots: any[]) {
        if (!spots || spots.length < 2) return '';
        return spots.map((spot: any) => `${spot.x},${spot.y}`).join(' ');
    }

    private currentMapCenter() {
        if (!this.executionCourse && !this.mapPlaceRouteOpen && this.naverUserCoordinate) {
            return { lat: Number(this.naverUserCoordinate.lat), lng: Number(this.naverUserCoordinate.lng), zoom: 15 };
        }
        if (!this.executionCourse && this.naverSearchCoordinate) {
            return { lat: Number(this.naverSearchCoordinate.lat), lng: Number(this.naverSearchCoordinate.lng), zoom: 15 };
        }
        if (this.executionPlaces && this.executionPlaces.length > 0) {
            let first = this.executionPlaces.find((spot: any) => this.isFiniteNumber(spot.lat) && this.isFiniteNumber(spot.lng));
            if (first) return { lat: Number(first.lat), lng: Number(first.lng), zoom: 15 };
        }
        let location = this.selectedFilters.location || '';
        let region = this.getRegionForLocation(location);
        return this.mapCenters[location] || this.mapCenters[region] || this.mapCenters.default;
    }

    private lngLatToTile(lng: number, lat: number, zoom: number) {
        let latRad = lat * Math.PI / 180;
        let scale = Math.pow(2, zoom);
        let x = Math.floor((lng + 180) / 360 * scale);
        let y = Math.floor((1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2 * scale);
        return { x, y };
    }

    private projectMapPosition(lat: number, lng: number) {
        let center = this.currentMapCenter();
        let centerPoint = this.lngLatToTilePoint(center.lng, center.lat, center.zoom);
        let userPoint = this.lngLatToTilePoint(lng, lat, center.zoom);
        let x = 50 + ((userPoint.x - centerPoint.x) * 100);
        let y = 50 + ((userPoint.y - centerPoint.y) * 100);
        return {
            x: Math.min(92, Math.max(8, x)),
            y: Math.min(92, Math.max(8, y))
        };
    }

    private lngLatToTilePoint(lng: number, lat: number, zoom: number) {
        let latRad = lat * Math.PI / 180;
        let scale = Math.pow(2, zoom);
        let x = (lng + 180) / 360 * scale;
        let y = (1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2 * scale;
        return { x, y };
    }

    private tilePointToLngLat(x: number, y: number, zoom: number) {
        let scale = Math.pow(2, zoom);
        let lng = x / scale * 360 - 180;
        let n = Math.PI - (2 * Math.PI * y / scale);
        let lat = 180 / Math.PI * Math.atan(0.5 * (Math.exp(n) - Math.exp(-n)));
        return { lat, lng };
    }

    private touchDistance(touches: any) {
        let first = touches[0];
        let second = touches[1];
        let x = first.clientX - second.clientX;
        let y = first.clientY - second.clientY;
        return Math.sqrt((x * x) + (y * y));
    }

    private getCompanionPhrase(value: string) {
        let phraseMap: any = {
            '연인': '연인과',
            '친구': '친구와',
            '가족': '가족과',
            '혼자': '혼자'
        };
        return phraseMap[value] || '';
    }

    private prepareCalendar() {
        let today = new Date();
        this.todayKey = this.toDateKey(today);
        this.calendarMonth = new Date(today.getFullYear(), today.getMonth(), 1);
        this.buildCalendar();
    }

    private buildCalendar() {
        let year = this.calendarMonth.getFullYear();
        let month = this.calendarMonth.getMonth();
        let firstDate = new Date(year, month, 1);
        let startDate = new Date(year, month, 1 - firstDate.getDay());
        let weeks = [];
        let days = [];

        this.calendarMonthLabel = `${year}년 ${month + 1}월`;

        for (let weekIndex = 0; weekIndex < 6; weekIndex++) {
            let week = [];
            for (let dayIndex = 0; dayIndex < 7; dayIndex++) {
                let date = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate() + (weekIndex * 7) + dayIndex);
                let key = this.toDateKey(date);
                let item = {
                    key,
                    label: date.getDate(),
                    inMonth: date.getMonth() === month,
                    isToday: key === this.todayKey,
                    ariaLabel: `${date.getFullYear()}년 ${date.getMonth() + 1}월 ${date.getDate()}일`
                };
                week.push(item);
                days.push(item);
            }
            weeks.push(week);
        }

        this.calendarWeeks = weeks;
        this.calendarDays = days;
    }

    private setDraftScheduleRange(startKey: string, endKey: string) {
        this.draftScheduleRange = this.normalizeDateRange(startKey, endKey);
    }

    private applyScheduleRange(startKey: string, endKey: string) {
        let range = this.normalizeDateRange(startKey, endKey);
        let isSameRange = this.scheduleRange.start === range.start && this.scheduleRange.end === range.end;

        if (isSameRange) {
            this.scheduleRange = { start: '', end: '' };
            this.draftScheduleRange = { start: '', end: '' };
            this.selectedFilters.schedule = '';
            return;
        }

        this.scheduleRange = range;
        this.draftScheduleRange = range;
        this.selectedFilters.schedule = this.formatScheduleRangeLabel(range.start, range.end);
    }

    private visibleScheduleRange() {
        if (this.schedulePointerActive && this.draftScheduleRange.start) return this.draftScheduleRange;
        return this.scheduleRange;
    }

    private normalizeDateRange(startKey: string, endKey: string) {
        if (!startKey || !endKey) return { start: '', end: '' };
        if (startKey <= endKey) return { start: startKey, end: endKey };
        return { start: endKey, end: startKey };
    }

    private formatScheduleRangeLabel(startKey: string, endKey: string) {
        let startLabel = this.formatDateLabel(startKey);
        if (startKey === endKey) return startLabel;
        return `${startLabel}-${this.formatDateLabel(endKey)}`;
    }

    private formatDateLabel(key: string) {
        let date = this.fromDateKey(key);
        return `${date.getMonth() + 1}.${date.getDate()}`;
    }

    private toDateKey(date: Date) {
        let year = date.getFullYear();
        let month = String(date.getMonth() + 1).padStart(2, '0');
        let day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    }

    private fromDateKey(key: string) {
        let parts = key.split('-').map((part: string) => Number(part));
        return new Date(parts[0], parts[1] - 1, parts[2]);
    }

    private scheduleDurationToken() {
        if (!this.scheduleRange.start || !this.scheduleRange.end) return '';
        let startDate = this.fromDateKey(this.scheduleRange.start);
        let endDate = this.fromDateKey(this.scheduleRange.end);
        let diff = endDate.getTime() - startDate.getTime();
        let days = Math.round(diff / 86400000) + 1;

        if (days <= 1) return this.scheduleRange.start === this.todayKey ? '오늘' : '당일치기';
        if (days === 2) return '1박2일';
        return '여행';
    }

    private syncLocationRegion() {
        let location = this.selectedFilters.location || '';
        if (!location) {
            this.selectedLocationRegion = this.locationGroups[0].name;
            return;
        }

        let region = this.getRegionForLocation(location);
        this.selectedLocationRegion = region || this.locationGroups[0].name;
    }

    private getRegionForLocation(location: string) {
        for (let group of this.locationGroups) {
            if (group.name === location) return group.name;
            if (group.areas.some((area: any) => area.value === location)) return group.name;
        }
        return '';
    }

    private dateKeyFromPointer(event: any) {
        if (!event || typeof document === 'undefined') return '';
        let element: any = document.elementFromPoint(event.clientX, event.clientY);
        while (element && element !== document.body) {
            if (element.getAttribute) {
                let key = element.getAttribute('data-date-key');
                if (key) return key;
            }
            element = element.parentElement;
        }
        return '';
    }

    private restoreIncomingState() {
        let stored = this.readAccessState();
        if (stored) {
            if (stored.selectedFilters) {
                this.selectedFilters = { ...this.selectedFilters, ...stored.selectedFilters };
            }
            if (typeof stored.selectedKeyword === 'string') this.selectedKeyword = stored.selectedKeyword;
            if (typeof stored.query === 'string') this.query = stored.query;
            let storedCompanionMode = String(stored.companionMode || '');
            if (['pretrip', 'instant'].indexOf(storedCompanionMode) > -1) {
                this.companionMode = storedCompanionMode;
            } else if (storedCompanionMode === 'friend') {
                this.companionMode = 'pretrip';
            }
            this.applyIncomingAccessTab(stored.activeTab);
            if (this.isSupportedMapContentTab(stored.mapContentTab)) this.mapContentTab = this.normalizeMapContentTab(stored.mapContentTab);
            if (stored.scheduleRange) this.restoreScheduleRange(stored.scheduleRange);
            if (this.isSupportedTravelMode(stored.travelMode)) this.travelMode = String(stored.travelMode);
            if (this.isSupportedMoveFilter(stored.selectedMoveFilter)) this.selectedMoveFilter = String(stored.selectedMoveFilter || '');
        }

        let params = this.currentSearchParams();
        if (params.get('profileTab') === 'savedCourses') {
            this.activeTab = 'my';
            this.myProfileTab = 'savedCourses';
            this.myProfileOpen = true;
        }
        this.pendingMobileCourseId = String(params.get('course') || '').trim();
        let identityReturnId = params.get('identityVerificationId') || params.get('identity_verification_id') || '';
        if (identityReturnId) {
            this.identityReturnId = identityReturnId;
            this.activeTab = 'my';
        }
        let filterKeys = ['companion', 'schedule', 'location'];
        let hasFilterParams = filterKeys.some((key: string) => params.has(key));
        if (hasFilterParams) {
            this.selectedFilters = {
                companion: '',
                schedule: '',
                location: ''
            };
        }

        filterKeys.forEach((key: string) => {
            if (params.has(key)) this.selectedFilters[key] = params.get(key) || '';
        });

        if (params.has('keyword')) this.selectedKeyword = params.get('keyword') || '';
        if (params.has('q')) this.query = params.get('q') || '';
        this.applyIncomingAccessTab(params.get('tab'));
        let incomingCompanionMode = params.get('companionMode') || '';
        if (['pretrip', 'instant'].indexOf(incomingCompanionMode) > -1) {
            this.companionMode = incomingCompanionMode;
            this.activeTab = 'home';
            this.homeContentTab = 'companion';
        } else if (incomingCompanionMode === 'friend') {
            this.companionMode = 'pretrip';
            this.activeTab = 'home';
            this.homeContentTab = 'companion';
        }
        if (params.has('companionInvite')) {
            this.companionMode = 'pretrip';
            this.activeTab = 'home';
            this.homeContentTab = 'companion';
        }
        if (params.get('compose') === 'course') {
            this.courseComposerOpen = true;
            this.courseBuilderMode = 'manual';
            this.courseBuilderStep = 'info';
        }
        if (this.isSupportedMapContentTab(params.get('mapMode'))) this.mapContentTab = this.normalizeMapContentTab(params.get('mapMode'));
        let incomingTogetherFocus = String(params.get('focus') || '').trim();
        if (['companions', 'route', 'meeting', 'signals'].indexOf(incomingTogetherFocus) > -1) {
            this.togetherInfoFocus = incomingTogetherFocus;
            if (incomingTogetherFocus === 'signals') this.zenlyLayerTab = 'signals';
        }
        let incomingSignalId = String(params.get('signal') || '').trim();
        if (incomingSignalId) this.selectedZenlySignalId = incomingSignalId;
        let incomingTravelMode = params.get('move') || params.get('travelMode');
        if (this.isSupportedMoveFilter(incomingTravelMode)) {
            this.selectedMoveFilter = String(incomingTravelMode || '');
            if (this.selectedMoveFilter && this.isSupportedTravelMode(this.selectedMoveFilter)) this.travelMode = this.selectedMoveFilter;
        }
        let prompt = params.get('prompt') || '';
        if (prompt) {
            this.consumeStoredChatPrompt();
        } else {
            prompt = this.consumeStoredChatPrompt();
        }
        if (prompt) {
            this.activeTab = 'chat';
            this.chatContentTab = 'chat';
            this.pendingChatPrompt = prompt;
        }
        if (params.has('scheduleStart') && params.has('scheduleEnd')) {
            this.restoreScheduleRange({
                start: params.get('scheduleStart'),
                end: params.get('scheduleEnd')
            });
            if (!params.has('schedule') && this.scheduleRange.start) {
                this.selectedFilters.schedule = this.formatScheduleRangeLabel(this.scheduleRange.start, this.scheduleRange.end);
            }
        }

        this.syncLocationRegion();
        this.syncCalendarToSchedule();
        this.persistAccessState();
        this.replaceAccessUrl();
    }

    private bindMobileDeepLinks() {
        if (this.mobileDeepLinkListenerBound || typeof window === 'undefined') return;
        this.mobileDeepLinkListenerBound = true;
        window.addEventListener('gachi:deep-link', async (event: any) => {
            let route = String(event && event.detail && event.detail.route || '').trim();
            if (!route) return;
            if (event && event.preventDefault) event.preventDefault();
            await this.applyMobileDeepLink(route);
        });
    }

    private async applyMobileDeepLink(route: string) {
        try {
            let parsed = new URL(route, window.location.origin);
            let params = parsed.searchParams;
            if (params.get('tab') === 'chat') {
                this.activeTab = 'chat';
                this.chatContentTab = 'chat';
                let conversationId = String(params.get('conversation') || '').trim();
                if (conversationId) this.activeChatThreadId = conversationId;
                await this.service.render();
                return;
            }
            if (params.get('tab') === 'map') {
                this.mapContentTab = this.normalizeMapContentTab(params.get('mapMode') || 'zenly');
                let focus = String(params.get('focus') || '').trim();
                if (['companions', 'route', 'meeting', 'signals'].indexOf(focus) > -1) {
                    this.togetherInfoFocus = focus;
                }
                let signalId = String(params.get('signal') || '').trim();
                if (signalId) this.selectedZenlySignalId = signalId;
                await this.setTab(this.mapContentTab === 'zenly' ? 'zenly' : 'map');
                if (focus === 'meeting' && this.hasActiveTogetherMeetingChat()) {
                    await this.openTogetherMeetingChat();
                }
                return;
            }
            if (!params.get('course')) {
                this.applyIncomingAccessTab(params.get('tab'));
                await this.service.render();
                return;
            }
            this.activeTab = 'my';
            this.myProfileOpen = true;
            this.myProfileTab = 'savedCourses';
            this.pendingMobileCourseId = String(params.get('course') || '').trim();
            await this.openPendingMobileCourse();
            await this.service.render();
        } catch (e) { }
    }

    private async openPendingMobileCourse() {
        let courseId = String(this.pendingMobileCourseId || '').trim();
        if (!courseId) return;
        this.pendingMobileCourseId = '';
        let course = this.allCourses().find((item: any) => String(item && item.id || '') === courseId);
        if (!course) {
            course = { id: courseId, title: '여행 코스', saved: true, source: 'saved' };
        }
        if (course && course.serverPublicCourse === true && !course.mine && course.source !== 'mine') {
            this.activeTab = 'home';
            this.homeContentTab = 'feed';
            this.myProfileOpen = false;
            await this.openFeaturedCourseDetail(course);
            return;
        }
        this.activeTab = 'my';
        this.myProfileOpen = true;
        this.myProfileTab = 'savedCourses';
        await this.openMyCourseDetail(course);
    }

    private restoreScheduleRange(value: any) {
        if (!value || !value.start || !value.end) return;
        let range = this.normalizeDateRange(String(value.start), String(value.end));
        this.scheduleRange = range;
        this.draftScheduleRange = range;
    }

    private syncCalendarToSchedule() {
        if (!this.scheduleRange.start) return;
        let date = this.fromDateKey(this.scheduleRange.start);
        this.calendarMonth = new Date(date.getFullYear(), date.getMonth(), 1);
        this.buildCalendar();
    }

    private persistAccessState() {
        if (typeof window === 'undefined' || !window.sessionStorage) return;
        try {
            window.sessionStorage.setItem('tour-on-access-state', JSON.stringify({
                activeTab: this.activeTab,
                mapContentTab: this.mapContentTab,
                companionMode: this.companionMode,
                travelMode: this.travelMode,
                selectedMoveFilter: this.selectedMoveFilter,
                selectedFilters: { ...this.selectedFilters },
                selectedKeyword: this.selectedKeyword,
                query: this.query,
                scheduleRange: { ...this.scheduleRange }
            }));
        } catch (e) { }
    }

    private readAccessState() {
        if (typeof window === 'undefined' || !window.sessionStorage) return null;
        try {
            let raw = window.sessionStorage.getItem('tour-on-access-state');
            return raw ? JSON.parse(raw) : null;
        } catch (e) {
            return null;
        }
    }

    private clearStoredAccessState() {
        if (typeof window === 'undefined' || !window.sessionStorage) return;
        try {
            window.sessionStorage.removeItem('tour-on-access-state');
            window.sessionStorage.removeItem('tour-on-chat-prompt');
        } catch (e) { }
    }

    private loadRecentPlaces() {
        if (typeof window === 'undefined' || !window.localStorage) return;
        try {
            let raw = window.localStorage.getItem(this.recentPlacesStorageKey()) || '[]';
            let rows = JSON.parse(raw);
            this.recentPlaces = Array.isArray(rows)
                ? rows.filter((place: any) => !this.isLegacyExamplePlaceId(place && place.id)).slice(0, 6)
                : [];
            this.persistRecentPlaces();
        } catch (e) {
            this.recentPlaces = [];
        }
    }

    private persistRecentPlaces() {
        if (typeof window === 'undefined' || !window.localStorage) return;
        try {
            window.localStorage.setItem(this.recentPlacesStorageKey(), JSON.stringify(this.recentPlaces.slice(0, 6)));
        } catch (e) { }
    }

    private loadSavedPlaces() {
        if (typeof window === 'undefined' || !window.localStorage) return;
        try {
            let raw = window.localStorage.getItem(this.savedPlacesStorageKey()) || '[]';
            let rows = JSON.parse(raw);
            this.savedPlaceItems = Array.isArray(rows)
                ? rows.filter((place: any) => !!this.savedPlaceIdentity(place)).slice(0, 100)
                : [];
        } catch (e) {
            this.savedPlaceItems = [];
        }
    }

    private persistSavedPlaces() {
        if (typeof window === 'undefined' || !window.localStorage) return;
        try {
            window.localStorage.setItem(this.savedPlacesStorageKey(), JSON.stringify(this.savedPlaceItems.slice(0, 100)));
        } catch (e) { }
    }

    private savedPlaceIdentity(place: any) {
        if (!place) return '';
        let directId = String(place.savedPlaceId || place.place_id || place.placeId || place.id || place.provider_place_id || '').trim();
        if (directId) return directId;
        let name = String(place.name || place.title || '').trim().toLowerCase();
        let location = String(place.address || place.area || place.location || '').trim().toLowerCase();
        return name ? `place:${encodeURIComponent(`${name}|${location}`)}` : '';
    }

    private savedPlaceFromSource(place: any, placeId: string) {
        let coordinate = place.coordinate && typeof place.coordinate === 'object' ? place.coordinate : {};
        let lat = this.safeNumber(place.lat || place.latitude || place.mapy || coordinate.lat);
        let lng = this.safeNumber(place.lng || place.longitude || place.mapx || coordinate.lng);
        return {
            ...place,
            id: placeId,
            savedPlaceId: placeId,
            name: place.name || place.title || '여행 장소',
            kind: place.kind || place.category || '장소',
            category: place.category || place.kind || '',
            icon: place.icon || this.profileCourseStopIcon(place) || 'fa-location-dot',
            location: place.location || place.area || this.getMapLocationLabel() || '국내',
            address: place.address || place.addr || '',
            description: place.description || place.summary || place.memo || '',
            image: place.image || place.first_image || place.firstimage || '',
            lat: lat !== null ? lat : '',
            lng: lng !== null ? lng : '',
            savedAt: new Date().toISOString()
        };
    }

    private savedPlacesStorageKey() {
        return this.userScopedStorageKey(this.savedPlacesStorageBaseKey);
    }

    private isLegacyExamplePlaceId(value: any) {
        let id = String(value || '').trim().toLowerCase();
        return ['default-', 'seoul-', 'busan-', 'jeju-', 'gyeonggi-', 'gangwon-', 'chungcheong-', 'jeolla-', 'gyeongsang-', 'saved-place-']
            .some((prefix: string) => id.indexOf(prefix) === 0);
    }

    private recordRecentPlace(spot: any) {
        if (!spot || !spot.id) return;

        let location = String(spot.location || this.getMapLocationLabel() || '국내').trim() || '국내';
        let coordinate = spot.coordinate && typeof spot.coordinate === 'object' ? spot.coordinate : {};
        let lat = this.safeNumber(spot.lat || spot.latitude || coordinate.lat);
        let lng = this.safeNumber(spot.lng || spot.longitude || coordinate.lng);
        let place = {
            id: spot.id,
            name: spot.name || spot.title || '추천 장소',
            kind: spot.kind || spot.distance || '장소',
            category: spot.category || '',
            icon: spot.icon || 'fa-location-dot',
            location,
            address: spot.address || '',
            description: spot.description || spot.summary || '',
            lat: lat !== null ? lat : '',
            lng: lng !== null ? lng : '',
            viewedAt: new Date().toISOString()
        };

        this.recentPlaces = [
            place,
            ...this.recentPlaces.filter((item: any) => item && item.id !== place.id)
        ].slice(0, 6);
        this.persistRecentPlaces();
    }

    private recentPlacesStorageKey() {
        return this.userScopedStorageKey(this.recentPlacesStorageBaseKey);
    }

    private userScopedStorageKey(baseKey: string) {
        let auth = this.service && this.service.auth ? this.service.auth : null;
        let session = auth && auth.session ? auth.session : {};
        let owner = String(session.id || session.email || session.name || '').trim();
        if (!owner) return baseKey;
        return `${baseKey}:${encodeURIComponent(owner)}`;
    }

    private loadMyProfileEdit() {
        if (typeof window === 'undefined' || !window.localStorage) {
            this.myProfileEdit = this.defaultMyProfileEdit();
            this.syncMyProfileNicknameFromAccount();
            return;
        }

        try {
            let raw = window.localStorage.getItem(this.myProfileEditStorageKey());
            let saved = raw ? JSON.parse(raw) : {};
            this.myProfileEdit = { ...this.defaultMyProfileEdit(), ...(saved && typeof saved === 'object' ? saved : {}) };
            this.ensureMyProfileEditDefaults();
            this.syncMyProfileNicknameFromAccount();
        } catch (e) {
            this.myProfileEdit = this.defaultMyProfileEdit();
            this.syncMyProfileNicknameFromAccount();
        }
    }

    private persistMyProfileEdit() {
        if (typeof window === 'undefined' || !window.localStorage) return;
        try {
            window.localStorage.setItem(this.myProfileEditStorageKey(), JSON.stringify(this.myProfileEdit));
        } catch (e) { }
    }

    private myProfileEditStorageKey() {
        let auth = this.service && this.service.auth ? this.service.auth : null;
        let session = auth && auth.session ? auth.session : {};
        let owner = String(session.id || session.email || session.name || '').trim();
        if (!owner) return this.myProfileEditStorageBaseKey;
        return `${this.myProfileEditStorageBaseKey}:${encodeURIComponent(owner)}`;
    }

    private loadMyCourseViewMode() {
        if (typeof window === 'undefined' || !window.localStorage) return;
        try {
            let mode = String(window.localStorage.getItem(this.myCourseViewModeStorageKey()) || 'list');
            this.myCourseViewMode = ['list', 'grid', 'blog'].indexOf(mode) > -1 ? mode : 'list';
        } catch (e) {
            this.myCourseViewMode = 'list';
        }
    }

    private persistMyCourseViewMode() {
        if (typeof window === 'undefined' || !window.localStorage) return;
        try {
            window.localStorage.setItem(this.myCourseViewModeStorageKey(), this.myCourseViewMode);
        } catch (e) { }
    }

    private myCourseViewModeStorageKey() {
        let auth = this.service && this.service.auth ? this.service.auth : null;
        let session = auth && auth.session ? auth.session : {};
        let owner = String(session.id || session.email || session.nickname || session.name || '').trim();
        if (!owner) return this.myCourseViewModeStorageBaseKey;
        return `${this.myCourseViewModeStorageBaseKey}:${encodeURIComponent(owner)}`;
    }

    private defaultMyProfileEdit() {
        return {
            travelerTitle: '프로 여행러',
            photo: '',
            nickname: '',
            region: '',
            intro: ''
        };
    }

    private ensureMyProfileEditDefaults() {
        if (!this.myProfileEdit || typeof this.myProfileEdit !== 'object') this.myProfileEdit = this.defaultMyProfileEdit();
        if (typeof this.myProfileEdit.travelerTitle === 'undefined') this.myProfileEdit.travelerTitle = '프로 여행러';
        if (typeof this.myProfileEdit.photo === 'undefined') this.myProfileEdit.photo = '';
        if (typeof this.myProfileEdit.nickname === 'undefined') this.myProfileEdit.nickname = '';
        if (typeof this.myProfileEdit.region === 'undefined') this.myProfileEdit.region = '';
        if (typeof this.myProfileEdit.intro === 'undefined') this.myProfileEdit.intro = '';
    }

    private syncMyProfileNicknameFromAccount() {
        this.ensureMyProfileEditDefaults();
        let account = String(this.myDisplayName() || '').trim();
        let genericNames = ['', '여행자', '여행자님'];
        if (genericNames.indexOf(account) === -1) {
            this.myProfileEdit.nickname = account;
        }
    }

    private loadTravelResume() {
        if (typeof window === 'undefined' || !window.localStorage) {
            this.travelResume = this.defaultTravelResume();
            return;
        }

        try {
            let raw = window.localStorage.getItem(this.travelResumeStorageKey());
            let saved = raw ? JSON.parse(raw) : {};
            this.travelResume = { ...this.defaultTravelResume(), ...(saved && typeof saved === 'object' ? saved : {}) };
            this.migrateTravelResume(saved);
            this.ensureTravelResumeDefaults();
        } catch (e) {
            this.travelResume = this.defaultTravelResume();
        }
    }

    private persistTravelResume() {
        if (typeof window === 'undefined' || !window.localStorage) return;
        try {
            window.localStorage.setItem(this.travelResumeStorageKey(), JSON.stringify(this.travelResume));
        } catch (e) { }
    }

    private async syncTravelResumeFromServer(migrateLocal: boolean = false) {
        if (!this.isLoggedIn()) return false;
        try {
            const response: any = await wiz.call('travel_resume', { action: 'load' });
            const data: any = response && response.data ? response.data : {};
            const remote: any = data && data.resume && typeof data.resume === 'object' ? data.resume : {};
            if (response && response.code === 200 && Object.keys(remote).length > 0) {
                this.travelResume = { ...this.defaultTravelResume(), ...remote };
                this.ensureTravelResumeDefaults();
                this.persistTravelResume();
                return true;
            }
            if (response && response.code === 200 && migrateLocal && this.travelResumeCompletion() > 0) {
                return await this.saveTravelResumeRemote();
            }
        } catch (e) { }
        return false;
    }

    private async saveTravelResumeRemote() {
        if (!this.isLoggedIn()) return false;
        try {
            const response: any = await wiz.call('travel_resume', {
                action: 'save',
                resume: JSON.stringify(this.travelResume || {})
            });
            if (response && response.code === 200 && response.data && response.data.resume) {
                this.travelResume = { ...this.defaultTravelResume(), ...response.data.resume };
                this.ensureTravelResumeDefaults();
                this.persistTravelResume();
                return true;
            }
        } catch (e) { }
        return false;
    }

    private travelResumeStorageKey() {
        let auth = this.service && this.service.auth ? this.service.auth : null;
        let session = auth && auth.session ? auth.session : {};
        let owner = String(session.id || session.email || session.name || '').trim();
        if (!owner) return this.travelResumeStorageBaseKey;
        return `${this.travelResumeStorageBaseKey}:${encodeURIComponent(owner)}`;
    }

    private defaultTravelResume() {
        return {
            schemaVersion: 2,
            photo: '',
            fullName: '',
            age: '',
            gender: '',
            region: '',
            companionUses: this.inferredCompanionUseCount(),
            interests: '',
            smoking: '',
            drinking: '',
            travelExperience: '',
            intro: '',
            safetyRecordAccepted: false,
            safetyRecordAcceptedAt: '',
            safetyRecordConsentVersion: ''
        };
    }

    private migrateTravelResume(saved: any) {
        let source = saved && typeof saved === 'object' ? saved : {};
        if (typeof source.fullName === 'string') {
            this.travelResume.fullName = source.fullName;
        } else {
            let legacyName = String(source.name || '').trim();
            let accountNickname = String(this.myDisplayName() || '').trim();
            this.travelResume.fullName = legacyName && legacyName !== accountNickname ? legacyName : '';
        }
        if (String(this.travelResume.smoking || '') === 'flexible') this.travelResume.smoking = '';
        this.travelIdentity.safetyRecordAccepted = !!this.travelResume.safetyRecordAccepted;
        this.travelIdentity.safetyRecordAcceptedAt = String(this.travelResume.safetyRecordAcceptedAt || '');
        delete this.travelResume.name;
        delete this.travelResume.travelPace;
        delete this.travelResume.budgetStyle;
        this.travelResume.schemaVersion = 2;
    }

    private ensureTravelResumeDefaults() {
        if (!this.travelResume || typeof this.travelResume !== 'object') this.travelResume = this.defaultTravelResume();
        if (typeof this.travelResume.schemaVersion === 'undefined') this.travelResume.schemaVersion = 2;
        if (typeof this.travelResume.photo === 'undefined') this.travelResume.photo = '';
        if (typeof this.travelResume.fullName === 'undefined') this.travelResume.fullName = '';
        if (typeof this.travelResume.age === 'undefined') this.travelResume.age = '';
        if (typeof this.travelResume.gender === 'undefined') this.travelResume.gender = '';
        if (typeof this.travelResume.region === 'undefined') this.travelResume.region = '';
        if (typeof this.travelResume.companionUses === 'undefined') this.travelResume.companionUses = this.inferredCompanionUseCount();
        if (typeof this.travelResume.interests === 'undefined') this.travelResume.interests = '';
        if (typeof this.travelResume.smoking === 'undefined') this.travelResume.smoking = '';
        if (typeof this.travelResume.drinking === 'undefined') this.travelResume.drinking = '';
        if (typeof this.travelResume.travelExperience === 'undefined') this.travelResume.travelExperience = '';
        if (typeof this.travelResume.intro === 'undefined') this.travelResume.intro = '';
        if (typeof this.travelResume.safetyRecordAccepted === 'undefined') this.travelResume.safetyRecordAccepted = false;
        if (typeof this.travelResume.safetyRecordAcceptedAt === 'undefined') this.travelResume.safetyRecordAcceptedAt = '';
        if (typeof this.travelResume.safetyRecordConsentVersion === 'undefined') this.travelResume.safetyRecordConsentVersion = '';
        delete this.travelResume.name;
        delete this.travelResume.travelPace;
        delete this.travelResume.budgetStyle;
    }

    private hasCompletedTravelResume() {
        this.ensureTravelResumeDefaults();
        return this.travelResumeCompletion() === 100;
    }

    private async promptTravelResumeCompletion() {
        this.activeTab = 'my';
        this.myProfileOpen = true;
        this.travelResumeReturnToSettings = false;
        this.myResumeOpen = true;
        this.myResumePreviewOpen = false;
        this.travelResumeStep = 1;
        this.persistAccessState();
        this.replaceAccessUrl();
        await this.loadTravelIdentityStatus();
        await this.showSaveHint('여행 이력서를 완성한 후에 동행 신청해주세요.');
    }

    private createCompanionApplication(post: any) {
        this.ensureTravelResumeDefaults();
        this.travelResume.age = this.normalizePositiveNumber(this.travelResume.age);
        this.travelResume.companionUses = this.normalizePositiveNumber(this.travelResume.companionUses);
        this.persistTravelResume();

        let applicantKey = this.currentCompanionApplicantKey();
        let applicantNickname = this.profileDisplayName();
        return {
            id: `application-${post && post.id ? post.id : Date.now()}-${Date.now()}`,
            applicantKey,
            applicantNickname,
            appliedAt: '방금',
            status: 'pending',
            safetyRecordConsent: true,
            safetyRecordConsentVersion: 'safety-record-v1-180d',
            safetyRecordConsentAt: new Date().toISOString(),
            resume: {
                photo: String(this.travelResume.photo || '').trim(),
                fullName: String(this.travelResume.fullName || '').trim(),
                nickname: applicantNickname,
                age: this.normalizePositiveNumber(this.travelResume.age),
                gender: String(this.travelResume.gender || '').trim(),
                region: String(this.travelResume.region || '').trim(),
                companionUses: this.travelResumeCompanionUses(),
                interests: String(this.travelResume.interests || '').trim(),
                smoking: String(this.travelResume.smoking || '').trim(),
                drinking: String(this.travelResume.drinking || '').trim(),
                identityVerified: this.isTravelResumeIdentityVerified(),
                reviewScore: 0,
                availabilityConfirmed: true,
                travelExperience: String(this.travelResume.travelExperience || '').trim(),
                intro: String(this.travelResume.intro || '').trim()
            }
        };
    }

    private currentCompanionApplicantKey() {
        let auth = this.service && this.service.auth ? this.service.auth : null;
        let session = auth && auth.session ? auth.session : {};
        return String(session.id || session.email || session.name || this.myDisplayName()).trim();
    }

    private companionApplicationResume(application: any) {
        if (!application || !application.resume || typeof application.resume !== 'object') return {};
        return application.resume;
    }

    private inferredCompanionUseCount() {
        return this.companionPosts.filter((post: any) => post && (post.status === 'matched' || post.saved)).length;
    }

    private normalizePositiveNumber(value: any) {
        let number = Number(value || 0);
        if (!isFinite(number) || number < 0) return 0;
        return Math.floor(number);
    }

    private replaceAccessUrl() {
        if (typeof window === 'undefined' || !window.history) return;
        if (this.routeRootHomeIfEmptyFilters()) return;

        let params = new URLSearchParams();
        params.set('tab', this.activeTab);
        if (this.activeTab === 'map' && this.mapContentTab !== 'map') params.set('mapMode', this.mapContentTab === 'zenly' ? 'together' : this.mapContentTab);
        if (this.selectedMoveFilter) params.set('move', this.selectedMoveFilter);
        Object.keys(this.selectedFilters).forEach((key: string) => {
            if (this.selectedFilters[key]) params.set(key, this.selectedFilters[key]);
        });
        if (this.selectedKeyword) params.set('keyword', this.selectedKeyword);
        if (this.query) params.set('q', this.query);
        if (this.scheduleRange.start && this.scheduleRange.end) {
            params.set('scheduleStart', this.scheduleRange.start);
            params.set('scheduleEnd', this.scheduleRange.end);
        }

        let nextUrl = `/access?${params.toString()}`;
        if (window.location.pathname === '/access' && `${window.location.pathname}${window.location.search}` !== nextUrl) {
            window.history.replaceState(null, '', nextUrl);
        }
    }

    private routeRootHomeIfEmptyFilters() {
        return false;
    }

    private routeLoginForMap() {
        if (this.activeTab !== 'map') return false;
        if (this.isLoggedIn()) return false;

        this.goMapLogin();
        return true;
    }

    private shouldUseRootHome() {
        return false;
    }

    private currentSearchParams() {
        if (typeof window === 'undefined') return new URLSearchParams();
        return new URLSearchParams(window.location.search || '');
    }

    private applyIncomingAccessTab(tab: any) {
        let value = String(tab || '');
        if (value === 'zenly' || value === 'together') {
            this.activeTab = 'map';
            this.mapContentTab = 'zenly';
            return;
        }
        if (this.isSupportedAccessTab(value)) this.activeTab = value;
    }

    private isSupportedMapContentTab(tab: any) {
        return ['map', 'zenly', 'together'].indexOf(String(tab || '')) > -1;
    }

    private normalizeMapContentTab(tab: any) {
        let value = String(tab || 'map');
        return value === 'together' ? 'zenly' : value;
    }

    private isSupportedTravelMode(mode: any) {
        return this.mapTravelModes.some((item: any) => item.key === String(mode || ''));
    }

    private isSupportedMoveFilter(mode: any) {
        return this.moveFilterOptions.some((item: any) => item.key === String(mode || ''));
    }

    private isSupportedAccessTab(tab: any) {
        return ['home', 'chat', 'map', 'saved', 'my'].indexOf(String(tab || '')) > -1;
    }

    private defaultChatMessage() {
        return {
            role: 'assistant',
            text: '가고 싶은 지역과 날짜를 알려주세요. 출발 장소와 이동수단, 이용시간을 확인해 코스 초안을 준비할게요.',
            time: '오전 10:30',
            seed: true
        };
    }

    private defaultCourseDraft() {
        return {
            title: '',
            region: '',
            regions: [],
            schedule: '',
            scheduleDate: '',
            scheduleEndDate: '',
            places: '',
            photo: '',
            photoName: '',
            description: '',
            category: '여행',
            companionType: '',
            isPublic: true,
            companionEnabled: false,
            companionDate: '',
            companionTime: '',
            companionCapacity: 2,
            companionCost: '',
            companionBudgetStyle: '',
            companionPace: '',
            companionMood: '',
            companionFlexible: '',
            companionMeetingPoint: '',
            companionSmoking: '',
            companionDrinking: '',
            companionIntro: ''
        };
    }

    private resetCourseComposerDraftState() {
        if (this.courseDraftSavedToastTimer) clearTimeout(this.courseDraftSavedToastTimer);
        this.courseDraftSavedToastVisible = false;
        this.courseDraft = this.defaultCourseDraft();
        this.courseBuilderPlaces = [];
        this.courseComposerPlannerEdit = false;
        this.courseBuilderDayIndex = 0;
        this.courseBuilderDays = [];
        this.courseBuilderOriginalDays = [];
        this.courseScheduleFormOpen = false;
        this.courseScheduleDraft = { type: 'flight', title: '', time: '', detail: '' };
        this.courseScheduleDetailOpen = false;
        this.courseScheduleDetail = {};
        this.courseAiRebuilding = false;
        this.coursePlaceSearchQuery = '';
        this.coursePlaceSearchResults = [];
        this.coursePlaceSearchOpen = false;
        this.coursePlaceSearching = false;
        this.courseRegionSearchOpen = false;
        this.courseRegionInput = '';
        this.coursePlaceSearchSource = '';
        this.courseBuilderMode = '';
        this.courseBuilderStep = 'mode';
        this.courseBuilderError = '';
        this.coursePublishModalOpen = false;
        this.coursePlaceDetailOpen = false;
        this.coursePlaceDetailLoading = false;
        this.coursePlaceDetailError = '';
        this.coursePlaceDetail = {};
        this.coursePlaceDetailTab = 'overview';
        this.coursePublishSubmitting = false;
        this.courseMySaveSubmitting = false;
        this.courseDraftSavedAt = '';
        this.courseAiMood = '';
        this.courseDateDragActive = false;
        this.courseDateDragStartKey = '';
        this.courseDateCalendarMonth = new Date();
    }

    private persistCourseDraft() {
        if (typeof window === 'undefined' || !window.localStorage) return false;
        try {
            this.commitPendingCourseRegion();
            this.syncCurrentCourseBuilderDay();
            let savedAt = new Date().toISOString();
            window.localStorage.setItem(this.courseDraftStorageKey(), JSON.stringify({
                ...this.courseDraft,
                draftSchemaVersion: 2,
                builderPlaces: this.cloneCourseBuilderPlaces(this.courseBuilderPlaces),
                builderDays: this.cloneCourseBuilderDays(this.courseBuilderDays),
                builderDayIndex: this.courseBuilderDayIndex,
                builderMode: this.courseBuilderMode,
                builderStep: this.courseBuilderStep,
                aiMood: this.courseAiMood,
                regionInput: this.courseRegionInput,
                placeSearchQuery: this.coursePlaceSearchQuery,
                dateCalendarMonth: this.courseDateCalendarMonth.toISOString(),
                savedAt
            }));
            this.courseDraftSavedAt = this.formatCourseDraftSavedAt(savedAt);
            this.refreshCourseDraftArchiveSummary();
            return true;
        } catch (e) {
            return false;
        }
    }

    private restoreCourseDraft(force: boolean) {
        let saved = this.readCourseDraft();
        if (!saved) return false;
        this.courseDraftSavedAt = this.formatCourseDraftSavedAt(saved.savedAt);
        if (!force && this.hasCourseDraftContent()) return false;
        let draft = { ...saved };
        delete draft.builderPlaces;
        delete draft.builderDays;
        delete draft.builderDayIndex;
        delete draft.builderMode;
        delete draft.builderStep;
        delete draft.aiMood;
        delete draft.regionInput;
        delete draft.placeSearchQuery;
        delete draft.dateCalendarMonth;
        delete draft.draftSchemaVersion;
        delete draft.savedAt;
        this.courseDraft = {
            ...this.defaultCourseDraft(),
            ...draft
        };
        this.courseDraft.regions = Array.isArray(draft.regions)
            ? draft.regions
            : (String(this.courseDraft.region || '').trim() ? [String(this.courseDraft.region).trim()] : []);
        this.courseDraft.region = this.courseRegions().join(', ');
        this.courseAiMood = String(saved.aiMood || '');
        this.courseRegionInput = String(saved.regionInput || '');
        this.coursePlaceSearchQuery = String(saved.placeSearchQuery || '');
        this.courseBuilderMode = 'manual';
        let step = String(saved.builderStep || '').trim();
        this.courseBuilderStep = step === 'places' ? 'places' : 'info';
        this.courseBuilderPlaces = Array.isArray(saved.builderPlaces)
            ? this.cloneCourseBuilderPlaces(saved.builderPlaces)
            : this.splitList(saved.places).map((name: string, index: number) => ({
                placeId: `manual-${index}-${name}`,
                name,
                lat: null,
                lng: null,
                order: index + 1,
                visitTime: this.visitTimeForIndex(index),
                memo: '',
                area: saved.region || '',
                address: '',
                category: '',
                image: '',
                manualTime: false
            }));
        this.courseBuilderDays = Array.isArray(saved.builderDays)
            ? this.cloneCourseBuilderDays(saved.builderDays)
            : [];
        this.courseBuilderDayIndex = Math.max(0, Math.min(Number(saved.builderDayIndex || 0), Math.max(0, this.courseBuilderDays.length - 1)));
        if (this.courseBuilderDays[this.courseBuilderDayIndex]) {
            this.courseBuilderPlaces = this.cloneCourseBuilderPlaces(this.courseBuilderDays[this.courseBuilderDayIndex].places);
        } else if (this.courseBuilderStep === 'places') {
            this.ensureCourseBuilderDaysFromDates();
        }
        let calendarValue = String(saved.dateCalendarMonth || saved.scheduleDate || '').trim();
        let calendarDate = calendarValue ? new Date(calendarValue.length === 10 ? `${calendarValue}T00:00:00` : calendarValue) : null;
        if (calendarDate && !isNaN(calendarDate.getTime())) {
            this.courseDateCalendarMonth = new Date(calendarDate.getFullYear(), calendarDate.getMonth(), 1);
        }
        this.courseRegionSearchOpen = false;
        this.coursePlaceSearchOpen = false;
        this.coursePlaceSearchResults = [];
        this.courseBuilderError = '';
        this.normalizeCourseBuilderOrder(false);
        return true;
    }

    private readCourseDraft() {
        if (typeof window === 'undefined' || !window.localStorage) return null;
        try {
            let raw = window.localStorage.getItem(this.courseDraftStorageKey());
            let saved = raw ? JSON.parse(raw) : null;
            if (!saved || typeof saved !== 'object') return null;
            return saved;
        } catch (e) {
            return null;
        }
    }

    private clearCourseDraftStorage() {
        if (typeof window === 'undefined' || !window.localStorage) return;
        try {
            window.localStorage.removeItem(this.courseDraftStorageKey());
            this.refreshCourseDraftArchiveSummary();
        } catch (e) { }
    }

    private ensureCourseDraftArchiveSummary() {
        if (!this.courseDraftArchiveChecked) this.refreshCourseDraftArchiveSummary();
    }

    private refreshCourseDraftArchiveSummary() {
        let saved = this.readCourseDraft();
        let savedItems = saved && Array.isArray(saved.builderDays) && saved.builderDays.length > 0
            ? saved.builderDays.reduce((items: any[], day: any) => items.concat((day && day.places) || []), [])
            : (saved && Array.isArray(saved.builderPlaces) ? saved.builderPlaces : []);
        this.courseDraftArchiveSummary = saved ? {
            title: saved.title || '',
            region: saved.region || '',
            schedule: saved.schedule || '',
            savedAt: saved.savedAt || '',
            placeCount: savedItems.length || this.splitList(saved.places).length
        } : null;
        this.courseDraftArchiveChecked = true;
    }

    private hasCourseDraftContent() {
        let draft = this.courseDraft || {};
        if (this.courseBuilderPlaces.length > 0) return true;
        return ['title', 'region', 'schedule', 'places', 'photo', 'description'].some((key: string) => {
            return String(draft[key] || '').trim().length > 0;
        });
    }

    private courseDraftStorageKey() {
        let auth = this.service && this.service.auth ? this.service.auth : null;
        let session = auth && auth.session ? auth.session : {};
        let owner = String(session.id || session.email || session.name || '').trim();
        if (!owner) return this.courseDraftStorageBaseKey;
        return `${this.courseDraftStorageBaseKey}:${encodeURIComponent(owner)}`;
    }

    private formatCourseDraftSavedAt(value: any) {
        if (!value) return '';
        let date = new Date(value);
        if (Number.isNaN(date.getTime())) return '';
        return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
    }

    private mapSpotsForCourseDraftRegion() {
        let location = String(this.courseDraft.region || '').trim();
        let region = this.getRegionForLocation(location);
        if (location && this.mapSpotMap[location]) return this.mapSpotMap[location];
        if (region && this.mapSpotMap[region]) return this.mapSpotMap[region];
        return this.mapSpotMap.default;
    }

    private findCourseDraftSpotMatch(place: string, spots: any[]) {
        let normalized = String(place || '').replace(/\s+/g, '').toLowerCase();
        if (!normalized) return null;
        return spots.find((spot: any) => {
            let name = String(spot.name || '').replace(/\s+/g, '').toLowerCase();
            let kind = String(spot.kind || '').replace(/\s+/g, '').toLowerCase();
            return normalized.indexOf(name) > -1 || name.indexOf(normalized) > -1 || (kind && normalized.indexOf(kind) > -1);
        }) || null;
    }

    private fallbackCourseDraftSpot(place: string, index: number, total: number) {
        let radiusX = 28;
        let radiusY = 22;
        let angle = total <= 1 ? -Math.PI / 2 : (-Math.PI / 2) + (Math.PI * 2 * index / total);
        return {
            name: place,
            x: Math.round(50 + (Math.cos(angle) * radiusX)),
            y: Math.round(50 + (Math.sin(angle) * radiusY)),
            icon: 'fa-location-dot'
        };
    }

    private defaultCommunityDraft() {
        return {
            kind: 'post',
            topic: 'recommend',
            title: '',
            body: '',
            hasDestination: true,
            destination: '',
            photo: '',
            photoName: '',
            place: '',
            pollOptions: [],
            pollInput: '',
            tags: [],
            tagInput: ''
        };
    }

    private splitList(value: any) {
        if (Array.isArray(value)) {
            return value
                .map((item: any) => String(item || '').trim())
                .filter((item: string) => item.length > 0);
        }
        return String(value || '')
            .split(/[,\n]/)
            .map((item: string) => item.trim())
            .filter((item: string) => item.length > 0);
    }

    private normalizeDraftList(value: any) {
        return this.splitList(value);
    }

    private normalizeCommunityTag(value: any) {
        return String(value || '')
            .trim()
            .replace(/^#+/, '')
            .replace(/[#,]/g, '')
            .trim();
    }

    private normalizeCommunityDraftTags(value: any) {
        return this.splitList(value)
            .map((tag: string) => this.normalizeCommunityTag(tag))
            .filter((tag: string) => !!tag);
    }

    private ensureDirectChatForCompanion(post: any, application?: any) {
        if (!application) {
            application = this.companionApplications(post).find((item: any) => {
                return item && (item.status === 'accepted' || item.id === post.selectedApplicationId);
            });
        }
        let id = `dm-${post.id}`;
        let existing = this.directChats.find((chat: any) => chat.id === id || chat.companionPostId === post.id);
        let applicantName = this.companionApplicationName(application);
        let chatName = application ? applicantName : (post.host || '동행 작성자');
        let preparation = this.companionPreparationFromPost(post);
        if (!existing) {
            existing = {
                id,
                companionPostId: post.id,
                name: chatName,
                handle: post.route || post.title,
                avatar: String(chatName || '동').charAt(0),
                status: '동행 준비방',
                preview: '코스와 약속 장소, 준비물을 확인하고 채팅해보세요.',
                time: '방금',
                unread: 1,
                category: 'companion',
                preparation,
                muted: false,
                blocked: false,
                messages: [
                    { role: 'other', text: '동행 신청을 수락했어요. 준비방에서 코스와 약속 장소를 같이 확인해요.', time: '방금' }
                ]
            };
            this.directChats = [existing, ...this.directChats];
        } else {
            existing.companionPostId = post.id;
            existing.status = '동행 준비방';
            existing.category = 'companion';
            existing.handle = post.route || post.title;
            existing.preparation = {
                ...preparation,
                ...(existing.preparation || {}),
                packingItems: existing.preparation && Array.isArray(existing.preparation.packingItems)
                    ? existing.preparation.packingItems
                    : preparation.packingItems
            };
        }
        this.activeDirectChatId = existing.id;
        return existing;
    }

    private hydrateCompanionPreparationRooms() {
        this.companionPosts.filter((post: any) => post && post.status === 'matched').forEach((post: any) => {
            this.ensureDirectChatForCompanion(post);
        });
    }

    private companionPreparationFromPost(post: any) {
        let packingItems = Array.isArray(post && post.packingItems) ? post.packingItems : this.splitList(post && post.packingItems ? post.packingItems : '');
        return {
            collapsed: true,
            courseId: String(post && post.courseId ? post.courseId : ''),
            courseConfirmed: !!(post && post.courseConfirmed),
            courseTitle: String(post && post.route ? post.route : post && post.title ? post.title : '여행 코스'),
            routeStops: this.companionRouteStops(post),
            date: String(post && post.date ? post.date : '일정 협의'),
            time: String(post && post.time ? post.time : '시간 협의'),
            meetingPoint: String(post && post.meetingPoint ? post.meetingPoint : '준비방에서 약속 장소를 정해주세요.'),
            estimatedCost: String(post && post.estimatedCost ? post.estimatedCost : '비용 협의'),
            pace: String(post && post.pace ? post.pace : ''),
            moodTags: this.companionMoodTags(post),
            flexibility: this.companionFlexibility(post),
            packingItems: packingItems.map((item: any) => {
                if (item && typeof item === 'object') return { label: String(item.label || '준비물'), done: !!item.done };
                return { label: String(item || '준비물'), done: false };
            })
        };
    }

    private directChatMatchesFilter(chat: any, key: string) {
        if (key === 'unread') return Number(chat && chat.unread ? chat.unread : 0) > 0;
        if (key === 'companion') return (chat && chat.category === 'companion') || String(chat && chat.status || '').indexOf('동행') > -1;
        return true;
    }

    private async loadChatThreads(showLoading: boolean = true) {
        if (!this.isLoggedIn()) {
            this.chatThreads = [];
            this.activeChatThreadId = '';
            return;
        }

        if (showLoading) {
            this.chatHistoryLoading = true;
            await this.service.render();
        }

        const { code, data } = await wiz.call('chat_threads', {});
        if (code === 200) {
            this.chatThreads = data && data.threads ? data.threads : [];
        } else if (code === 401 && this.service.auth) {
            this.service.auth.clearLocalSession();
            this.chatThreads = [];
            this.activeChatThreadId = '';
        }

        this.chatHistoryLoading = false;
        await this.service.render();
    }

    private async sendChatPrompt(prompt: string, providedClientMessageId: string = '') {
        let clientMessageId = providedClientMessageId || this.createChatMessageId('client');
        this.chatSendInvocationCount += 1;
        if (this.isChatSending || this.chatInFlightMessageIds[clientMessageId]) {
            this.logChatMessageEvent('send_blocked_duplicate', {
                client_message_id: clientMessageId,
                invocation_count: this.chatSendInvocationCount
            });
            return;
        }
        this.chatInFlightMessageIds[clientMessageId] = true;
        this.isChatSending = true;
        this.plannerRequestState = { status: 'loading', lastPrompt: prompt, errorMessage: '' };
        let requestTime = this.currentChatTimeLabel();
        let userMessageId = `user-${clientMessageId}`;
        this.logChatMessageEvent('send_started', {
            client_message_id: clientMessageId,
            user_message_id: userMessageId,
            invocation_count: this.chatSendInvocationCount,
            before_length: this.messages.length
        });
        this.appendChatMessageOnce({
            id: userMessageId,
            clientMessageId,
            role: 'user',
            text: prompt,
            time: requestTime
        }, 'local');
        let canGenerateCourse = this.plannerConversationCanGenerateCourse(prompt);
        this.isPlannerGenerating = canGenerateCourse;
        if (canGenerateCourse) {
            this.plannerStage = 'checking_feasibility';
            this.plannerSuggestedReplies = [];
            this.startPlannerProgress();
        }

        await this.service.render();
        this.scrollToLatest();

        let reply: any = {
            id: `pending-assistant-${clientMessageId}`,
            clientMessageId,
            role: 'assistant',
            text: canGenerateCourse
                ? '입력 조건을 확인했어요. 실제 장소 후보와 이동 동선을 검증하고 있어요.'
                : '말씀해주신 내용에서 다음 여행 조건을 확인하고 있어요.',
            time: requestTime,
            loading: true
        };
        this.appendChatMessageOnce(reply, 'local');
        await this.service.render();
        this.scrollToLatest();

        try {
            let history = this.messages
                .filter((message: any) => !message.loading && !message.seed)
                .slice(0, -1)
                .slice(-12)
                .map((message: any) => ({
                    role: message.role,
                    text: String(message.text || '')
                }));
            let response: any = await wiz.call('chat_send', {
                prompt,
                history: JSON.stringify(history),
                thread_id: this.activeChatThreadId,
                client_message_id: clientMessageId,
                travel_state: JSON.stringify(this.plannerTravelState || {})
            }, { timeout: 45000 });
            let code = Number(response && response.code ? response.code : 0);
            let data = response && response.data ? response.data : response;
            if (code === 200 && data) {
                this.applyPlannerContract(data);
                reply.text = data.message || data.reply || 'AI가 여행 조건을 정리했어요.';
                reply.id = data.response_message_id || reply.id;
                reply.requestId = data.request_id || '';
                this.activeChatThreadId = data.thread_id || this.activeChatThreadId;
                this.plannerRequestState = { status: 'ready', lastPrompt: prompt, errorMessage: '' };
                this.logChatMessageEvent('http_response_applied', {
                    client_message_id: clientMessageId,
                    request_id: data.request_id || '',
                    conversation_id: data.conversation_id || data.thread_id || this.activeChatThreadId,
                    user_message_id: data.user_message_id || userMessageId,
                    response_message_id: data.response_message_id || reply.id,
                    append_path: 'http',
                    before_length: this.messages.length,
                    after_length: this.messages.length
                });
            } else {
                let fallbackMessage = canGenerateCourse
                    ? '코스 생성이 잠시 지연됐어요. 입력한 조건은 유지했으니 한 번 더 요청해주세요.'
                    : '답변이 잠시 지연됐어요. 입력한 내용은 유지했으니 한 번 더 보내주세요.';
                reply.text = this.responseMessage(data || response, fallbackMessage);
                this.plannerRequestState = { status: 'error', lastPrompt: prompt, errorMessage: reply.text };
            }
        } catch (e) {
            reply.text = canGenerateCourse
                ? '코스 생성이 잠시 지연됐어요. 입력한 조건은 유지했으니 한 번 더 요청해주세요.'
                : '답변이 잠시 지연됐어요. 입력한 내용은 유지했으니 한 번 더 보내주세요.';
            this.plannerRequestState = { status: 'error', lastPrompt: prompt, errorMessage: reply.text };
        }
        reply.loading = false;
        this.isChatSending = false;
        delete this.chatInFlightMessageIds[clientMessageId];
        this.isPlannerGenerating = false;
        this.stopPlannerProgress();

        await this.service.render();
        this.scrollToLatest();
        if (this.plannerCourseReady) this.scrollToPlannerPreview();
    }

    private currentChatTimeLabel() {
        let date = new Date();
        let hours = date.getHours();
        let minutes = String(date.getMinutes()).padStart(2, '0');
        let period = hours < 12 ? '오전' : '오후';
        return `${period} ${hours % 12 || 12}:${minutes}`;
    }

    private consumeStoredChatPrompt() {
        if (typeof window === 'undefined' || !window.sessionStorage) return '';
        try {
            let prompt = window.sessionStorage.getItem('tour-on-chat-prompt') || '';
            window.sessionStorage.removeItem('tour-on-chat-prompt');
            return prompt.trim();
        } catch (e) {
            return '';
        }
    }

    private scrollToLatest() {
        if (typeof window === 'undefined') return;
        window.setTimeout(() => {
            let scroller: any = document.querySelector('.access-shell .ai-planner-layout');
            if (!scroller) return;
            scroller.scrollTop = scroller.scrollHeight;
            window.requestAnimationFrame(() => {
                scroller.scrollTop = scroller.scrollHeight;
            });
        }, 0);
    }

    public scrollToPlannerPreview() {
        if (typeof window === 'undefined') return;
        window.setTimeout(() => {
            let preview: any = document.querySelector('.access-shell .ai-planner-preview');
            if (preview && preview.scrollIntoView) {
                preview.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        }, 0);
    }

    private resetTravelResumeScroll() {
        if (typeof window === 'undefined') return;
        window.setTimeout(() => {
            let content: any = document.querySelector('.access-shell .app-content');
            if (content) content.scrollTop = 0;
        }, 0);
    }

    private resetHomeContentScroll() {
        if (typeof window === 'undefined') return;
        window.setTimeout(() => {
            let content: any = document.querySelector('.access-shell .home-tab-content');
            let tabs: any = document.querySelector('.access-shell .home-panel > .home-content-tabs:not(.home-community-tabs)');
            if (content) content.scrollTop = 0;
            if (tabs && tabs.scrollIntoView) {
                tabs.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        }, 0);
    }

    private resetChatContentScroll() {
        if (typeof window === 'undefined') return;
        window.setTimeout(() => {
            let content = document.querySelector('.access-shell .chat-tab-content');
            let appContent = document.querySelector('.access-shell .app-content');
            if (content) content.scrollTop = 0;
            if (appContent) appContent.scrollTop = 0;
            this.scrollDirectMessages();
        }, 0);
    }

    private scrollDirectMessages() {
        if (typeof window === 'undefined') return;
        window.setTimeout(() => {
            let list = document.querySelector('.access-shell .direct-message-list');
            if (list) list.scrollTop = list.scrollHeight;
        }, 0);
    }

    private courseFilterValue(key: string) {
        if (key === 'schedule') return this.scheduleDurationToken() || this.selectedFilters.schedule;
        return this.selectedFilters[key];
    }

    private matchesCourse(course: any) {
        let conditionValues = Object.keys(this.selectedFilters)
            .map((key: string) => this.courseFilterValue(key))
            .filter((value: string) => !!value);
        let filterMatched = conditionValues.every((value: string) => this.courseContains(course, value));
        if (!filterMatched) return false;

        let moveKeyword = this.moveFilterCourseKeyword();
        if (moveKeyword && !this.courseContains(course, moveKeyword)) return false;

        if (this.selectedKeyword && !this.courseContains(course, this.selectedKeyword)) return false;

        let text = String(this.query || '').trim().toLowerCase();
        if (!text) return true;

        let tokens = text.split(/\s+/).filter((token: string) => token.length > 0);
        return tokens.every((token: string) => this.courseContains(course, token));
    }

    private moveFilterCourseKeyword() {
        if (this.selectedMoveFilter === 'walk') return '산책';
        if (this.selectedMoveFilter === 'car') return '드라이브';
        return '';
    }

    private matchesSelectedLocation(item: any) {
        let location = String(this.selectedFilters.location || '').trim();
        if (!location) return true;
        return this.itemContains(item, location);
    }

    private expandSearchKeywords(keyword: string) {
        let aliases: any = {
            서울: ['서울', '성수', '종로', '익선동', '한강', '홍대', '강남', '잠실', '이태원', '북촌'],
            부산: ['부산', '해운대', '광안리', '영도', '서면', '기장', '송정', '남포동'],
            제주: ['제주', '제주시', '애월', '협재', '서귀포', '성산', '중문', '우도'],
            경기: ['경기', '수원', '가평', '양평', '파주', '용인', '고양', '성남', '화성', '남양주', '포천'],
            충청: ['충청', '충남', '충북', '대전', '세종', '천안', '아산', '공주', '부여', '태안', '청주', '충주', '제천', '단양', '보령', '서산', '당진'],
            전라: ['전라', '전북', '전남', '광주', '전주', '군산', '목포', '여수', '순천', '담양', '남원', '고흥'],
            경상: ['경상', '경북', '경남', '대구', '경주', '포항', '안동', '울산', '통영', '거제', '진주', '창원', '남해', '부산'],
            강원: ['강원', '강릉', '속초', '춘천', '양양', '평창', '원주', '동해', '삼척', '홍천'],
            인천: ['인천', '송도', '월미도', '강화', '영종', '을왕리'],
            '이번 주말': ['이번 주말', '이번주말', '주말'],
            '다음 달': ['다음 달', '다음달', '여행']
        };
        return aliases[keyword] || [keyword];
    }

    private courseContains(course: any, value: string) {
        let keyword = String(value || '').trim().toLowerCase();
        if (!keyword) return true;

        let searchable = [
            course.title,
            course.location,
            course.summary,
            course.duration,
            course.distance,
            course.category,
            course.source,
            ...(course.tags || [])
        ].join(' ').toLowerCase();

        return this.expandSearchKeywords(keyword).some((item: string) => searchable.indexOf(String(item).toLowerCase()) > -1);
    }

    private itemContains(item: any, value: string) {
        let keyword = String(value || '').trim().toLowerCase();
        if (!keyword) return true;

        let searchable = [
            item.title,
            item.route,
            item.course,
            item.location,
            item.date,
            item.intro,
            item.summary,
            item.author,
            item.host,
            ...(item.tags || [])
        ].join(' ').toLowerCase();

        return this.expandSearchKeywords(keyword).some((token: string) => searchable.indexOf(String(token).toLowerCase()) > -1);
    }
}

export default PageAccessComponent;